import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase, serverMode, backendMode, setBackendMode } from "./supabaseClient";
import { chemAnalysis, chemSummaryText, parseGallons } from "./lib/chemistry";
import { startTrail, trailNav, trailSummary } from "./lib/trail";
import { APPLE_CSS } from "./appleTheme";

const STORE_KEY = "pooldesk:data:v5";
/* Sign-in is parked while the app is being built. Flip this to true to bring the
   4 digit code screen back — nothing else needs changing. */
const REQUIRE_SIGN_IN = true;
const PHOTO_KEY = (id) => "pooldesk:photos:" + id;
const VISIT_KEY = (id) => "pooldesk:visits:" + id;
const TEST_KEY = (id) => "pooldesk:tests:" + id;
const QUOTE_KEY = (id) => "pooldesk:quotes:" + id;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_FULL = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
const FREQS = [
  { v: "twice", label: "Twice weekly", want: 2 },
  { v: "weekly", label: "Weekly", want: 1 },
  { v: "biweekly", label: "Bi-weekly", want: 1 },
  { v: "monthly", label: "Monthly", want: 1 },
  { v: "short", label: "Short term service", want: 0 },
];
const FREQ_LABEL = Object.fromEntries(FREQS.map((f) => [f.v, f.label]));
const freqWant = (v) => (FREQS.find((f) => f.v === v) || {}).want ?? 1;
const capServiceDays = (days, frequency) => {
  const list = (Array.isArray(days) ? days : []).filter((d) => DAYS.includes(d))
    .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
  if (frequency === "twice" || freqWant(frequency) === 2) return list.slice(0, 2);
  return list;
};
const canPickServiceDay = (days, frequency, day, turningOff) => {
  if (turningOff) return true;
  if (frequency !== "twice" && freqWant(frequency) !== 2) return true;
  const cur = (Array.isArray(days) ? days : []).filter((d) => DAYS.includes(d));
  if (cur.includes(day)) return true;
  return cur.length < 2;
};
const showDayFilter = (fType) => fType !== "retail";
const clientDays = (c) => (Array.isArray(c.days) ? c.days : c.day ? [c.day] : []).filter((d) => DAYS.includes(d))
  .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
const NTHS = [
  { v: 1, label: "1st" }, { v: 2, label: "2nd" }, { v: 3, label: "3rd" }, { v: 4, label: "4th" }, { v: 5, label: "Last" },
];
const dayOfYear = (d) => Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
const dateFromDoy = (year, n) => new Date(year, 0, n);
const daysInYear = (y) => ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365);
const isoWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + 3 - ((x.getDay() + 6) % 7));
  const first = new Date(x.getFullYear(), 0, 4);
  return 1 + Math.round(((x - first) / 86400000 - 3 + ((first.getDay() + 6) % 7)) / 7);
};
const weekRange = (year, wk) => {
  const jan4 = new Date(year, 0, 4);
  const start = startOfWeek(jan4);
  const s2 = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (wk - 1) * 7);
  return [s2, new Date(s2.getFullYear(), s2.getMonth(), s2.getDate() + 6)];
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const money0 = (n) => "$" + Math.round(n).toLocaleString();

const parseDate = (str) => {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(str || "").trim());
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtShort = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const localDay = (iso) => (iso ? fmtDate(new Date(iso)) : "");
const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const weeksBetween = (a, b) => Math.round((startOfWeek(b) - startOfWeek(a)) / 604800000);
const nthWeekdayOfMonth = (d) => Math.floor((d.getDate() - 1) / 7) + 1;
const isLastWeekdayOfMonth = (d) => {
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7);
  return t.getMonth() !== d.getMonth();
};
const dayKeyOf = (d) => DAYS[(d.getDay() + 6) % 7];

function dueOnDate(c, d) {
  const until = parseDate(c.shortTermUntil);
  if (c.frequency === "short" && until && d > until) return false;
  if (c.frequency === "monthly" && c.monthlyMode === "date") {
    const target = Number(c.monthlyDate) || 1;
    if (d.getDate() === target) return true;
    if (dayKeyOf(d) === "Mon") {
      const yst = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
      return yst.getDay() === 0 && yst.getDate() === target;
    }
    return false;
  }
  const key = dayKeyOf(d);
  if (!key || !clientDays(c).includes(key)) return false;
  if (c.frequency === "biweekly") {
    const a = parseDate(c.anchorDate);
    return a ? weeksBetween(a, d) % 2 === 0 : true;
  }
  if (c.frequency === "monthly") {
    const n = Number(c.monthlyNth) || 1;
    return n === 5 ? isLastWeekdayOfMonth(d) : nthWeekdayOfMonth(d) === n;
  }
  return true;
}

function nextDue(c, from) {
  const start = from || new Date();
  for (let i = 0; i < 400; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    if (dueOnDate(c, d)) return d;
  }
  return null;
}

function cadenceLine(c) {
  const days = clientDays(c);
  if (c.frequency === "monthly" && c.monthlyMode === "date") return `Day ${Number(c.monthlyDate) || 1} of every month`;
  if (!days.length) return "No service day set";
  if (c.frequency === "monthly") {
    const n = Number(c.monthlyNth) || 1;
    return `${(NTHS.find((x) => x.v === n) || NTHS[0]).label} ${daysSentence(c).replace(/s$/, "")} of every month`;
  }
  if (c.frequency === "biweekly") return `Every other week · ${daysSentence(c)}`;
  return `${days.length} day${days.length === 1 ? "" : "s"} a week · ${daysSentence(c)}`;
}

function daysSentence(c) {
  const d = clientDays(c);
  if (!d.length) return "";
  const full = d.map((x) => DAY_FULL[x]);
  if (full.length === 1) return full[0] + "s";
  return full.slice(0, -1).join(", ") + " and " + full[full.length - 1];
}
const SANITIZERS = ["Chlorine tabs", "Liquid chlorine", "Salt", "Bromine", "Mineral system", "UV / ozone assisted"];
const TYPE_LABEL = { retail: "Retail", service: "Service", both: "Retail + Service" };
const PHOTO_TAGS = [
  { v: "house", label: "Property", add: "Add property photos" },
  { v: "pad", label: "Equipment pad", add: "Add equipment pad photos" },
  { v: "water", label: "Body of water", add: "Add Pool / Spa photos" },
  // Visit photos belong to the visit that took them (Shaun's field test: a
  // weekly account would pile up hundreds on the property). Read-only here —
  // they're captured in the field, never added from the office.
  { v: "visit", label: "Visit photos", add: null },
];
const PHOTO_TAG_LABEL = Object.fromEntries(PHOTO_TAGS.map((t) => [t.v, t.label]));
const normalizeTag = (t) => {
  if (PHOTO_TAG_LABEL[t]) return t;
  const m = { "Pool": "water", "Problem area": "water", "Equipment pad": "pad", "Access": "house", "Other": "house" };
  return m[t] || "house";
};
const CHANNELS = [
  { v: "none", label: "Don't send" }, { v: "email", label: "Email" },
  { v: "text", label: "Text message" }, { v: "both", label: "Email + text" },
];
const CHANNEL_LABEL = { none: "—", email: "Email", text: "Text", both: "Email + text" };
// Communication categories. Key "general" is kept for old data — it now means
// Retail / Water testing (special-order and water-test notices go through it).
const COMMS = [
  { k: "invoices", label: "Invoices" },
  { k: "service", label: "Service" },
  { k: "general", label: "Retail / Water Testing" },
  { k: "repairs", label: "Repairs" },
];
const STRUCTURES = [
  { v: "pool", label: "Pool" },
  { v: "pool_spa", label: "Pool with spa" },
  { v: "pool_spa_features", label: "Pool with spa and features" },
  { v: "spa", label: "Spa" },
  { v: "spa_features", label: "Spa with features" },
  { v: "other", label: "Other" },
];
const STRUCTURE_LABEL = Object.fromEntries(STRUCTURES.map((x) => [x.v, x.label]));
const COVERS = [
  { v: "safety", label: "Safety cover" }, { v: "tarp", label: "Tarp cover" },
  { v: "snapin", label: "Snap-in cover" }, { v: "leafnet", label: "Leaf net" }, { v: "none", label: "No cover" },
];
const COVER_LABEL = Object.fromEntries(COVERS.map((x) => [x.v, x.label]));
const canAddLeafNet = (v) => v === "tarp" || v === "snapin";
const hasSpa = (v) => v !== "pool";
const canSplitWater = (v) => v === "pool_spa" || v === "pool_spa_features";
const hasExtras = (v) => v === "pool_spa_features" || v === "spa_features";
const EQUIP_KINDS = [
  { v: "pump", label: "Pump" }, { v: "filter", label: "Filter" }, { v: "heater", label: "Heater" },
  { v: "chlorinator", label: "Chlorinator" }, { v: "alt", label: "Alternate sanitizer" },
  { v: "automation", label: "Automation" }, { v: "cleaner", label: "Cleaner" },
  { v: "booster", label: "Booster pump" }, { v: "blower", label: "Spa blower" },
  { v: "light", label: "Lighting control" }, { v: "other", label: "Other" },
];
const EQUIP_LABEL = Object.fromEntries(EQUIP_KINDS.map((x) => [x.v, x.label]));
const EQUIP_HINT = {
  pump: "Make, model, HP, single or variable speed",
  filter: "Sand, cartridge or DE — model and last media change",
  heater: "Make, model, BTU — or None",
  chlorinator: "Salt cell, inline feeder or peristaltic pump — model and install date",
  alt: "UV, ozone, mineral cartridge — model and last change",
  automation: "Panel model and where it's mounted",
  cleaner: "Robot, suction or pressure — model",
  booster: "Make, model, what it drives",
  blower: "Make, HP",
  light: "Transformer, controller, colour system",
  other: "What it is and anything a tech needs to know",
};
const CORE_EQUIP = ["pump", "filter", "heater", "chlorinator", "alt"];
const REPAIR_TYPES = [
  "Softub tub", "Softub Hydromate", "Robotic cleaner", "Pressure cleaner", "Suction cleaner",
];
const visitSide = (v) => (v.repairType ? "service" : v.side || "service");
const channelOf = (c, v) => {
  if (v.repairType) return "repair";
  if (v.delivery) return "delivery";
  if (visitSide(v) === "service") return "service";
  return c && c.trade ? "trade" : "retail";
};
const CHANNELS_SALES = [
  { k: "service", label: "Service sales" }, { k: "retail", label: "Retail sales" },
  { k: "trade", label: "Trade sales" }, { k: "delivery", label: "Delivery sales" },
  { k: "repair", label: "Repair sales" },
];
const WATER_PARAMS = [
  { k: "fc", label: "Free chlorine", unit: "ppm", lo: 1, hi: 4, aliases: ["free chlorine", "fc", "free cl"] },
  { k: "tc", label: "Total chlorine", unit: "ppm", lo: 1, hi: 5, aliases: ["total chlorine", "tc"] },
  { k: "ph", label: "pH", unit: "", lo: 7.2, hi: 7.8, aliases: ["ph"] },
  { k: "ta", label: "Total alkalinity", unit: "ppm", lo: 80, hi: 120, aliases: ["total alkalinity", "ta", "alkalinity"] },
  { k: "ch", label: "Calcium hardness", unit: "ppm", lo: 200, hi: 400, aliases: ["calcium hardness", "ch", "hardness"] },
  { k: "cya", label: "Cyanuric acid", unit: "ppm", lo: 30, hi: 50, aliases: ["cyanuric acid", "cya", "stabilizer"] },
  { k: "salt", label: "Salt", unit: "ppm", lo: 2700, hi: 3400, aliases: ["salt", "nacl"] },
  { k: "phos", label: "Phosphate", unit: "ppb", lo: 0, hi: 300, aliases: ["phosphate", "phosphates", "po4"] },
  { k: "copper", label: "Copper", unit: "ppm", lo: 0, hi: 0.2, aliases: ["copper", "cu"] },
  { k: "iron", label: "Iron", unit: "ppm", lo: 0, hi: 0.2, aliases: ["iron", "fe"] },
  { k: "temp", label: "Water temp", unit: "°F", lo: 45, hi: 95, aliases: ["temp", "temperature", "water temp"] },
];
const TEST_PLACES = [{ v: "store", label: "In store" }, { v: "onsite", label: "At the pool" }];
const TEST_PLACE_LABEL = { store: "In store", onsite: "At the pool" };
const paramByAlias = (txt) => {
  const t = String(txt).trim().toLowerCase().replace(/[^a-z0-9 ]/g, "");
  return WATER_PARAMS.find((p) => p.aliases.includes(t));
};
function testAsText(client, t) {
  if (!t) return "";
  const lines = WATER_PARAMS.filter((p) => t.results[p.k]).map((p) => {
    const st = readingState(p, t.results[p.k]);
    const tag = st === "ok" ? "in range" : st === "low" ? "LOW" : st === "high" ? "HIGH" : "";
    return `${p.label}: ${t.results[p.k]}${p.unit ? " " + p.unit : ""}${tag ? " (" + tag + ")" : ""}`;
  });
  const chem = chemSummaryText(t.results || {}, client.gallons, { saltPool: isSaltPool(client) });
  return [
    `Water test for ${client.name}`,
    `Tested ${t.date}${t.where === "onsite" ? " at the pool" : " in store"}${t.source === "spinlab" ? " (LaMotte Spin Lab)" : ""}`,
    "",
    ...lines,
    chem ? "\n" + chem : "",
    t.notes ? "\n" + t.notes : "",
  ].filter(Boolean).join("\n");
}
const isSaltPool = (c) => String(c.sanitizerType || "").toLowerCase().includes("salt");

function readingState(p, v) {
  const n = parseFloat(v);
  if (!isFinite(n)) return "none";
  if (n < p.lo) return "low";
  if (n > p.hi) return "high";
  return "ok";
}
function parseSpinLab(text) {
  const out = {};
  String(text || "").split(/\r?\n/).forEach((line) => {
    const m = /^\s*([A-Za-z][A-Za-z0-9 ()\/]*?)\s*[,:\t]\s*(-?[0-9]*\.?[0-9]+)/.exec(line);
    if (!m) return;
    const p = paramByAlias(m[1]);
    if (p) out[p.k] = m[2];
  });
  return out;
}
const FLAG_SCOPES = [
  { v: "both", label: "Both sides" }, { v: "service", label: "Service only" }, { v: "retail", label: "Retail only" },
];
const FLAG_SCOPE_LABEL = { both: "Service and retail", service: "Service only", retail: "Retail only" };
const defFlag = () => ({ on: false, message: "", scope: "both", updated: "", until: "" });
function autoFlags(c, visits) {
  // Ordered by severity — the worst billing problem always lands first in the banner.
  const out = [];
  const b = { ...defBilling(), ...(c.billing || {}) };
  const bal = accountBalances(b, visits || []);
  const cs = hasCard(b) ? cardStatus(b.card) : {};
  const wantsCard = b.autoCharge || b.payMethod === "credit";
  const cardBroken = !hasCard(b) || cs.expired;

  // 1. Past due with no way to charge — catch it while the customer is on the line.
  if (bal.dueNow > 0 && wantsCard && cardBroken) {
    out.push({
      key: "pastdue-nocard", auto: true, scope: "both", source: "Past due — cannot charge", act: hasCard(b) ? "updatecard" : "addcard",
      message: `$${bal.dueNow.toFixed(2)} past due and ${!hasCard(b) ? "there is no card on file" : `the ${b.card.brand || "card"} on file expired ${b.card.exp}`} — get a card while they're on the phone.`,
    });
  } else if (wantsCard && !hasCard(b)) {
    // 2. Auto-pay / pays-by-card with nothing to charge.
    out.push({
      key: "nocard", auto: true, scope: "both", source: "No card on file", act: "addcard",
      message: b.autoCharge
        ? "Auto charge is on but there's no card on file — every charge will fail until one is added."
        : "Set to pay by credit card but there's no card on file.",
    });
  }
  const unpaid = (visits || []).filter((v) => v.paid === false);
  if (unpaid.length) {
    const sides = new Set(unpaid.map((v) => v.side || "service"));
    const total = unpaid.reduce((sum, v) => sum + (parseFloat(String(v.amount || "").replace(/[^0-9.]/g, "")) || 0), 0);
    const oldest = unpaid.map((v) => v.date).filter(Boolean).sort()[0];
    const scope = sides.size > 1 ? "both" : sides.has("retail") ? "retail" : "service";
    out.push({
      key: "unpaid", auto: true, scope,
      source: sides.size > 1 ? "Outstanding — retail and service" : sides.has("retail") ? "Outstanding — retail" : "Outstanding — service",
      act: bal.dueNow > 0 && hasCard(b) && !cs.expired ? "chargenow" : undefined,
      message: `${unpaid.length} unpaid invoice${unpaid.length === 1 ? "" : "s"}${total ? " totalling $" + total.toFixed(2) : ""}${oldest ? ", oldest " + oldest : ""}. Collect before more work goes on the account.`,
    });
  }
  if (b.noService) {
    out.push({
      key: "noservice", auto: true, scope: "service", source: "Do not service", stop: true,
      message: `Do not service — ${b.noServiceReason || "see private notes"}.${b.noServiceNote ? " " + b.noServiceNote : ""}`,
    });
  }
  if (hasCard(b) && cs.expired && !(bal.dueNow > 0 && wantsCard)) {
    out.push({
      key: "card", auto: true, scope: "both", source: "Card expired", act: "updatecard",
      message: `The ${b.card.brand} ending ${b.card.last4 || "····"} expired ${b.card.exp}. Get a new card before the next scheduled service${b.autoCharge ? " — auto charge will fail." : "."}`,
    });
  } else if (hasCard(b) && cs.soon) {
    // 3. Expiring soon — amber, not red.
    out.push({
      key: "cardsoon", auto: true, scope: "both", amber: true, source: "Card expiring soon", act: "updatecard",
      message: `The ${b.card.brand} ending ${b.card.last4 || "····"} expires ${b.card.exp} — get the new card before charges start failing.`,
    });
  }
  return out;
}

function allFlags(c, visits) {
  const list = autoFlags(c, visits);
  if (flagShows(c)) {
    const f = c.flag || {};
    list.push({ key: "manual", auto: false, scope: f.scope || "both", source: FLAG_SCOPE_LABEL[f.scope || "both"], message: f.message });
  }
  return list;
}

function flagShows(c) {
  const f = c.flag || {};
  if (!f.on || !String(f.message || "").trim()) return false;
  // Auto-expiry: a flag with an until date stops showing after that day (the record stays for history).
  if (f.until && String(f.until) < new Date().toISOString().slice(0, 10)) return false;
  if (f.scope === "service") return c.type !== "retail";
  if (f.scope === "retail") return c.type !== "service";
  return true;
}
const eqDetail = (c, kind) => ((c.equipmentList || []).find((e) => e.kind === kind) || {}).detail || "";
const FEATURES = [
  { k: "skimmers", label: "Skimmers" },
  { k: "drains", label: "Main drains" },
  { k: "returns", label: "Returns" },
  { k: "lights", label: "Lights" },
  { k: "waterfalls", label: "Waterfalls" },
  { k: "deckJets", label: "Deck jets" },
  { k: "sideSuctions", label: "Side suctions" },
];
const MAX_PHOTO_BYTES = 4200000;
const CYCLES = [{ v: "monthly", label: "Monthly" }, { v: "per_visit", label: "Per visit" }];
const CYCLE_LABEL = { monthly: "Monthly", per_visit: "Per visit" };
const PAY_METHODS = [
  { v: "", label: "Ask customer — preferred payment?" },
  { v: "cash", label: "Cash" }, { v: "cheque", label: "Cheque" }, { v: "credit", label: "Credit card" },
  { v: "debit", label: "Debit" }, { v: "etransfer", label: "E-transfer" }, { v: "wire", label: "Wire" },
];
const PAY_LABEL = Object.fromEntries(PAY_METHODS.map((m) => [m.v, m.label]));
const CATS = [
  { k: "labour", label: "Labour" }, { k: "chemical", label: "Chemicals" }, { k: "material", label: "Materials" },
];
const CARD_BRANDS = ["Visa", "Mastercard", "Amex", "Discover", "Other"];
const TERMS = [
  { v: "net10", label: "Net 10 days · 3% interest" },
  { v: "receipt", label: "Due on receipt" }, { v: "net15", label: "Net 15" },
  { v: "net30", label: "Net 30" }, { v: "net60", label: "Net 60" },
];
const TERM_LABEL = Object.fromEntries(TERMS.map((t) => [t.v, t.label]));
const DEFAULT_LEVELS = [
  { id: "pushy", rank: 1, name: "Regular account", labour: 0, chemical: 0, material: 0, note: "List price — no discount." },
  { id: "tier2", rank: 2, name: "Tier 2", labour: 0, chemical: 10, material: 10, note: "10% off chemicals and materials. Labour at list." },
  { id: "tier3", rank: 3, name: "Tier 3", labour: 0, chemical: 15, material: 15, note: "15% off chemicals and materials. Labour at list." },
  { id: "tier4", rank: 4, name: "Tier 4", labour: 0, chemical: 20, material: 20, note: "20% off chemicals and materials. Labour at list." },
];
// One-shot stamp on businessProfile so this reset runs once; staff can reassign in testing after that.
const DISCOUNT_LADDER = "regular-t2-t3-t4-v1";

const cid = () => "x" + Math.random().toString(36).slice(2, 9);
const defComms = (on) => ({ invoices: on ? "email" : "none", service: on ? "email" : "none", general: on ? "email" : "none", repairs: on ? "email" : "none" });
const defBilling = () => ({
  acctNo: "", cycle: "monthly", terms: "net10", payMethod: "", zone: "z1", lateFeePct: "3",
  autoCharge: false, chargeDay: 1, paperInvoice: false,
  card: { holder: "", brand: "Visa", last4: "", exp: "", ref: "", updated: "" },
  noService: false,
  discountMode: "level", levelId: "pushy",
  discounts: { labour: 0, chemical: 0, material: 0 },
  specialPrices: [], // per-item prices for THIS customer only — beat every other discount

  monthlyRate: "", hourlyLabour: "", tripCharge: "",
  seasonStart: "", seasonEnd: "", contractTotal: "", instalments: "",
  taxable: true, taxExemptNo: "",
  poRequired: false, poNumber: "", creditLimit: "", lateFeePct: "",
  onHold: false, holdReason: "", billingNotes: "",
});
function cardStatus(card) {
  const m = /^(\d{1,2})\s*\/\s*(\d{2,4})$/.exec((card && card.exp) || "");
  if (!m) return {};
  const mo = Number(m[1]); let yr = Number(m[2]);
  if (yr < 100) yr += 2000;
  if (mo < 1 || mo > 12) return {};
  const end = new Date(yr, mo, 1);
  const now = new Date();
  const days = (end - now) / 86400000;
  return { expired: days <= 0, soon: days > 0 && days < 75 };
}
const money = (v) => (String(v || "").trim() ? "$" + String(v).trim().replace(/^\$/, "") : "");
const levelOf = (levels, id) => (levels || DEFAULT_LEVELS).find((l) => l.id === id) || (levels || DEFAULT_LEVELS).find((l) => l.id === "pushy") || (levels || DEFAULT_LEVELS)[0];
function effDiscounts(billing, levels) {
  const b = { ...defBilling(), ...(billing || {}) };
  if (b.discountMode === "custom") {
    return { labour: Number(b.discounts.labour) || 0, chemical: Number(b.discounts.chemical) || 0, material: Number(b.discounts.material) || 0 };
  }
  const l = levelOf(levels, b.levelId);
  return { labour: Number(l.labour) || 0, chemical: Number(l.chemical) || 0, material: Number(l.material) || 0 };
}
const hasCard = (b) => !!(b && b.card && (b.card.last4 || b.card.ref));
const defFeatures = () => ({ skimmers: 1, drains: 1, returns: 2, lights: 1, waterfalls: 0, deckJets: 0, sideSuctions: 0 });

/* ---------- complexity model ---------- */
function scoreParts(c) {
  const f = { ...defFeatures(), ...(c.features || {}) };
  const n = (k) => Math.max(0, Number(f[k]) || 0);
  const gal = parseInt(String(c.gallons || "").replace(/[^0-9]/g, ""), 10) || 0;
  const parts = [{ label: "Base stop", pts: 1.5 }];
  const push = (label, pts) => { if (pts > 0) parts.push({ label, pts: Math.round(pts * 10) / 10 }); };

  if (gal >= 40000) push("Large volume (40k+ gal)", 2);
  else if (gal >= 25000) push("Above-average volume", 1);

  push(`${n("skimmers")} skimmers`, Math.max(0, n("skimmers") - 1) * 0.5);
  push(`${n("drains")} main drains`, Math.max(0, n("drains") - 1) * 0.5);
  push(`${n("returns")} returns`, Math.max(0, n("returns") - 4) * 0.25);
  push(`${n("lights")} lights`, n("lights") * 0.3);
  push(`${n("waterfalls")} waterfalls`, n("waterfalls") * 0.8);
  push(`${n("deckJets")} deck jets`, n("deckJets") * 0.4);
  push(`${n("sideSuctions")} side suctions`, n("sideSuctions") * 0.5);

  const st = c.structure || "pool";
  if (hasSpa(st)) push("Spa on site", st === "spa" ? 0.5 : 1);
  if (canSplitWater(st) && c.spaSeparate) push("Spa on its own water", 1);
  if (hasExtras(st)) push("Built-in features", 0.5);
  const cov = c.coverType || "none";
  if (cov === "tarp") push("Tarp cover", 0.5);
  else if (cov === "safety" || cov === "snapin") push(COVER_LABEL[cov], 0.3);
  else if (cov === "leafnet") push("Leaf net", 0.2);
  if (c.coverLeafNet && canAddLeafNet(cov)) push("Leaf net as well", 0.3);

  const heater = eqDetail(c, "heater").trim().toLowerCase();
  if (heater && heater !== "none" && heater !== "n/a") push("Heater in service", 1);
  const s = c.sanitizerType || "";
  if (s === "Salt") push("Salt system", 0.5);
  if (s === "Liquid chlorine") push("Liquid chlorine feed", 1);
  if (s === "Mineral system" || s === "UV / ozone assisted") push("Supplemental sanitizer", 0.5);
  if (/\bde\b|diatom/i.test(eqDetail(c, "filter"))) push("DE filter", 0.5);
  if (eqDetail(c, "alt").trim()) push("Alternate sanitizer", 0.5);

  const raw = parts.reduce((a, b) => a + b.pts, 0);
  return { parts, raw: Math.round(raw * 10) / 10, score: Math.max(1, Math.min(10, Math.round(raw))) };
}
const effectiveCx = (c) => (c.complexityMode === "manual" ? Number(c.complexity) || 5 : scoreParts(c).score);

const PERMS = [
  { k: "sales", label: "Sales dashboard", note: "Company-wide gross sales — owner level" },
  { k: "staff", label: "Staff and timesheet", note: "Employee profiles, codes and everyone's hours" },
  { k: "rating", label: "Account rating", note: "Profitability meter and its breakdown" },
  { k: "margins", label: "Job margins", note: "Materials cost, price and labour on each visit" },
  { k: "pricing", label: "Pricing and billing", note: "Card on file, levels, discounts, terms" },
  { k: "standing", label: "Account standing", note: "Refunds, exchanges, complaints" },
  { k: "flags", label: "Flag messages", note: "Read and set account flags" },
  { k: "water", label: "Water tests", note: "Test history and Spin Lab results" },
  { k: "photos", label: "Reference photos", note: "Pool, pad and access shots" },
  { k: "equipment", label: "Equipment", note: "Pump, filter, heater and the rest" },
  { k: "edit", label: "Edit clients", note: "Change any client record" },
];
const ALL_PERMS = PERMS.map((p) => p.k);
const TECH_ROWS = 11;
const DEFAULT_TECHS = Array.from({ length: TECH_ROWS }, (_, i) => ({ id: "t" + (i + 1), name: "Technician " + (i + 1), staffId: "" }));
const defSchedule = () => ({ perTech: 8, amberAt: 0.7 });
const dayLoadBand = (count, cap, amberAt) => {
  if (!cap) return "green";
  if (count >= cap) return "red";
  if (count >= Math.round(cap * (amberAt || 0.7))) return "amber";
  return "green";
};
const BAND_WORD = { green: "Room to book", amber: "Ask before booking", red: "No room" };

const DEFAULT_ROLES = [
  { id: "owner", name: "Owner", perms: ALL_PERMS.slice() },
  { id: "manager", name: "Manager", perms: ALL_PERMS.filter((k) => k !== "sales") },
  { id: "retail", name: "Retail", perms: ["pricing", "flags", "water", "photos", "equipment", "edit"] },
  { id: "senior", name: "Senior technician", perms: ["flags", "water", "photos", "equipment", "edit"] },
  { id: "tech", name: "Technician", perms: ["flags", "water", "photos", "equipment"] },
  { id: "vac", name: "Vac technician", perms: ["flags", "water", "photos"] },
];
const DEFAULT_STAFF = [
  { id: "s1", name: "Owner", roleId: "owner", pin: "1234", clock: "100000001", active: true },
  { id: "s2", name: "Manager", roleId: "manager", pin: "2345", clock: "100000002", active: true },
  { id: "s3", name: "Retail counter", roleId: "retail", pin: "3456", clock: "100000003", active: true },
  { id: "s4", name: "Senior tech", roleId: "senior", pin: "4567", clock: "100000004", active: true },
  { id: "s5", name: "Technician", roleId: "tech", pin: "5678", clock: "100000005", active: true },
  { id: "s6", name: "Vac tech", roleId: "vac", pin: "6789", clock: "100000006", active: true },
];
// Parts catalogue doubles as inventory (qty on hand). Services/rates are non-inventory priced lines.
// The standard retail quick keys — the most-sold counter items. These become favourited
// till buttons; prices and photos are filled in through Inventory.
const QUICK_KEYS = [
  { code: "CHL-FILL-10", name: "10L Chlorine Fill", category: "Chlorine" },
  { code: "CHL-FILL-20", name: "20L Chlorine Fill", category: "Chlorine" },
  { code: "CHL-TAB-6", name: "Multi-Purpose Chlorine Tablets 6kg", category: "Chlorine" },
  { code: "SALT-20", name: "Salt 20kg", category: "Balancers" },
  { code: "STAB-1.8", name: "Stabilizer 1.8kg", category: "Balancers" },
  { code: "ACID-4", name: "Muriatic Acid 4L", category: "Balancers" },
  { code: "PH-MINUS-1.5", name: "pH Minus 1.5kg", category: "Balancers" },
  { code: "ALK-8", name: "Alkalinity 8kg", category: "Balancers" },
  { code: "CAL-8", name: "Calcium 8kg", category: "Balancers" },
  { code: "CLAR-1", name: "Clarifier 1L", category: "Water care" },
  { code: "PHOS-1", name: "Phos-Free 1L", category: "Water care" },
  { code: "ALGAE-1", name: "Algaecide 60% 1L", category: "Water care" },
];
const quickKeyPart = (qk) => ({ id: cid(), code: qk.code, name: qk.name, price: "", cost: "", qty: "", lowAt: "", category: qk.category, image: "", favorite: true, active: true });
const DEFAULT_PARTS = [
  { id: "p5", code: "WATER", name: "Water — bulk fill (no tax)", price: "0.00", cost: "0.00", qty: "0", lowAt: "", category: "Water", noTax: true, favorite: true, active: true },
  ...QUICK_KEYS.map(quickKeyPart),
];
const DEFAULT_SERVICES = [
  { id: "s1", name: "Standard service rate — per visit", rate: "89.00", kind: "service", active: true },
  { id: "s2", name: "Labour — per hour", rate: "95.00", kind: "labour", active: true },
  { id: "s3", name: "Pool opening", rate: "349.00", kind: "service", active: true },
  { id: "s4", name: "Pool closing", rate: "379.00", kind: "service", active: true },
  { id: "s5", name: "Emergency call-out", rate: "150.00", kind: "other", active: true },
];
const SERVICE_KINDS = [["service", "Service"], ["labour", "Labour"], ["other", "Other"]];

// Revenue centres — which part of the business a POS sale is attributed to, for reporting.
const SERVICE_SUBS = ["Pool openings", "Pool closings", "Weekly & bi-weekly vacuuming", "Weekly & bi-weekly pool checks", "Weekly & bi-weekly pool checks (vacuum if necessary)", "Spring vacuuming", "Portable vacuuming", "Short term pool checks", "Short term vacuuming", "Filter installation", "Heater installation", "Pump installation", "Salt system installation", "UV installation", "Mineral cartridge installation", "In-line chlorinator", "Off-line chlorinator", "Heat pump installation"];
const REPAIR_SUBS = ["Robotic repairs", "Pressure cleaner repairs", "Suction cleaner repairs", "Hydromate repairs", "Softub re-skins", "Softub jet plumbing manifold replacements", "Softub suction manifold replacements", "Hydromate re-skins", "Pump repairs"];
const DEFAULT_REVCENTRES = [
  { id: "rc1", name: "Retail", active: true, subs: [] },
  { id: "rc2", name: "Service", active: true, subs: SERVICE_SUBS },
  { id: "rc3", name: "Repairs", active: true, subs: REPAIR_SUBS },
  { id: "rc4", name: "Delivery", active: true, subs: [] },
  { id: "rc5", name: "Trades", active: true, subs: [] },
];
const OLD_REVCENTRES = ["Retail counter", "Chemicals", "Parts & equipment", "Service department"];

// Service types for work orders — each carries default instructions + a colour for the schedule.
const DEFAULT_SERVICETYPES = [
  { id: "st1", name: "Pool openings", color: "#1FA84C", instructions: "Remove and store winter cover. Reinstall baskets, returns, ladders. Fill to level, start up pump and filter, shock and balance. Inspect for winter damage and report.", active: true },
  { id: "st2", name: "Pool closings", color: "#D42B2B", instructions: "Balance water. Lower level below returns. Blow out and plug the lines, drain all equipment. Add winterizing chemicals. Install the winter cover.", active: true },
  { id: "st3", name: "Weekly & bi-weekly vacuuming", color: "#1E40AF", instructions: "", active: true },
  { id: "st4", name: "Weekly & bi-weekly pool checks", color: "#38A9E0", instructions: "", active: true },
  { id: "st5", name: "Weekly & bi-weekly pool checks (vacuum if necessary)", color: "#F2C200", instructions: "", active: true },
  { id: "st6", name: "Spring vacuuming", color: "#0D9488", instructions: "", active: true },
  { id: "st7", name: "Portable vacuuming", color: "#F5820B", instructions: "", active: true },
  { id: "st8", name: "Short term pool checks", color: "#7C3AED", instructions: "", active: true },
  { id: "st9", name: "Short term vacuuming", color: "#9BA3F0", instructions: "", active: true },
  { id: "st10", name: "Filter installation", color: "#8A6D52", instructions: "", active: true },
  { id: "st11", name: "Heater installation", color: "#7E1236", instructions: "", active: true },
  { id: "st12", name: "Pump installation", color: "#12245E", instructions: "", active: true },
  { id: "st13", name: "Salt system installation", color: "#6B7280", instructions: "", active: true },
  { id: "st14", name: "UV installation", color: "#C0208F", instructions: "", active: true },
  { id: "st15", name: "Mineral cartridge installation", color: "#6E8B23", instructions: "", active: true },
  { id: "st16", name: "In-line chlorinator", color: "#FB6F5A", instructions: "", active: true },
  { id: "st17", name: "Off-line chlorinator", color: "#F4A9C4", instructions: "", active: true },
  { id: "st18", name: "Heat pump installation", color: "#D9A425", instructions: "", active: true },
  { id: "st19", name: "Quotes", color: "#5A6472", instructions: "", active: true },
];
const OLD_SERVICETYPES = ["Weekly service", "Pool opening", "Pool closing", "Repair", "Green pool recovery", "Quotes"];
// Pick readable text (dark/light) for a coloured badge.
const textOn = (hex) => {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? "#1a2230" : "#fff";
};
// map a service type name to a scheduling kind (colour/label), best effort
const deriveKind = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("quote")) return "quote";
  if (n.includes("opening")) return "opening";
  if (n.includes("closing")) return "closing";
  if (n.includes("repair")) return "repair";
  if (n.includes("install")) return "install";
  if (n.includes("deliver")) return "delivery";
  return "service";
};

// Counter catalog: one list, driven off Inventory labour/service rates.
// Service-type instructions fill "what it includes" when the rate has no desc.
// Never a second rate table — prices always come from data.services.
const catalogNormName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const catalogNamesMatch = (a, b) => {
  const x = catalogNormName(a), y = catalogNormName(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const stem = (s) => s.replace(/s\b/g, "").replace(/ +/g, " ").trim();
  return stem(x) === stem(y);
};
const CATALOG_KIND_INK = { service: "#0B4F8A", labour: "#0E7A3C", other: "#7A4FB6" };
function serviceCatalogRows(services = [], serviceTypes = []) {
  const types = (serviceTypes || []).filter((t) => t && t.active !== false);
  return (services || []).filter((s) => s && s.active !== false).map((s) => {
    const st = types.find((t) => catalogNamesMatch(t.name, s.name));
    const desc = String(s.desc || "").trim() || (st ? String(st.instructions || "").trim() : "");
    const z1 = String(s.rate ?? "").trim();
    const z2raw = String(s.rateZone2 ?? "").trim();
    const kind = s.kind || "service";
    const kindLabel = (SERVICE_KINDS.find(([k]) => k === kind) || ["", "Service"])[1];
    return {
      id: s.id,
      name: s.name || "Service",
      desc,
      kind,
      kindLabel,
      rate: z1,
      rateZone2: z2raw || z1,
      hay: [s.name, desc, kindLabel, kind].join(" ").toLowerCase(),
    };
  });
}
function filterServiceCatalog(rows, q) {
  const t = String(q || "").trim().toLowerCase();
  if (!t) return rows || [];
  return (rows || []).filter((r) => (r.hay || "").includes(t));
}
const CATALOG_DESC_PLACEHOLDER = "No description yet — add what this service includes under Inventory → Labour & services. Don't invent copy here.";
function catalogDescPoints(desc) {
  const raw = String(desc || "").trim();
  if (!raw) return [];
  const lines = raw.split(/\n+/).map((s) => s.replace(/^[\s]*[-•*]\s*/, "").trim()).filter(Boolean);
  if (lines.length > 1) return lines;
  const sents = raw.split(/(?<=\.)\s+/).map((s) => s.trim()).filter(Boolean);
  return sents.length ? sents : [raw];
}

// Work types a job/quote can be, used to tag which terms & conditions apply.
const WORK_TYPES = [["service", "Service"], ["repair", "Repair"], ["opening", "Opening"], ["closing", "Closing"], ["install", "Install"], ["quote", "General quote"], ["rental", "Rental"]];
const TERM_TAGS = [["any", "Any work"], ...WORK_TYPES];
// Pre-set terms & conditions, tagged by the work they apply to.
const DEFAULT_TERMS = [
  { id: "t1", name: "Standard service terms", tag: "any", active: true,
    body: "Payment is due on receipt of invoice. Accounts on file are charged to the card on record. A card on file is required for recurring service." },
  { id: "t2", name: "Repair warranty", tag: "repair", active: true,
    body: "Parts and labour on repairs are warrantied for 30 days. Warranty does not cover damage from misuse, water chemistry, or freezing." },
  { id: "t3", name: "Pool opening", tag: "opening", active: true,
    body: "Opening covers removal and storage of the winter cover, reconnection and start-up of equipment, and initial balancing. Cover storage, extra labour for repairs found at opening, and chemicals are billed separately." },
  { id: "t4", name: "Pool closing", tag: "closing", active: true,
    body: "Closing covers lowering the water, blowing out and plugging the lines, draining equipment, and installing the winter cover. Damage from an improperly winterized pool the customer opened themselves is not covered." },
  { id: "t5", name: "Quote validity", tag: "quote", active: true,
    body: "This quote is valid for 30 days. Prices are estimates and may change if site conditions differ from what was described. A deposit may be required before ordering parts." },
  { id: "t6", name: "Cleaner rental", tag: "rental", active: true,
    body: "The renter is responsible for the unit while it is out. A valid card on file is required. Late return is charged at the posted daily late fee. Damage beyond normal wear is billed at cost of repair or replacement. The unit must come back with the accessories it went out with." },
];
// Keep owner-edited blocks; only add a factory term when this shop has none for that tag (e.g. rental on older files).
function mergeDefaultTerms(saved) {
  const list = Array.isArray(saved) ? saved.slice() : [];
  const tags = new Set(list.map((t) => t.tag || "any"));
  const ids = new Set(list.map((t) => t.id));
  DEFAULT_TERMS.forEach((t) => { if (!ids.has(t.id) && !tags.has(t.tag)) list.push({ ...t }); });
  return list;
}

const JOB_KINDS = [
  { k: "service", label: "Service call" },
  { k: "repair", label: "Repair" },
  { k: "opening", label: "Opening" },
  { k: "closing", label: "Closing" },
  { k: "install", label: "Install" },
  { k: "delivery", label: "Delivery" },
  { k: "quote", label: "Quote / look at" },
];
const JOB_LABEL = Object.fromEntries(JOB_KINDS.map((j) => [j.k, j.label]));
const blankJob = () => ({ id: cid(), clientId: "", kind: "service", what: "", note: "", date: "", techId: "", weight: 1, at: new Date().toISOString() });

/* ---------- Guided work-order workflows: a step ENGINE, job types are data ---------- */
// Step types (the only seven): photo, ack, note (conditional), media (conditional),
// gate, external, line_items. Steps may carry:
//   showIf/skipIf {step, is}  — visibility / deferral conditions on earlier answers
//   unit                      — cluster tag; a whole unit defers together
//   anchor "start"|"end"      — this photo's timestamp stamps the work order
// Job types may carry deferUnits (what spawns a follow-up and when) and
// requiresCompleted (job types that must be finished on the property first).
const WCR_LOW_WATER_FLAG = "System startup outstanding — cover type opens on low water; startup deferred until the pool is filled.";
// Shared tail for the always-low-water cover types (tube & tarp, snap-in):
// plugs still pull and the pad still de-winterizes, but startup NEVER runs on
// site — the return visit is automatic, and dosing is always at reduced volume.
const wcrLowWaterSteps = (coverOffLabel, coverOffHelp) => [
  { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo", help: "Closed pool, cover in full view. This photo stamps the start time." },
  { id: "coveroff", type: "ack", label: coverOffLabel, help: coverOffHelp },
  { id: "cover_note", type: "note", showIf: { step: "coveroff", is: false }, label: "What's the problem with the cover?", help: "So the office can quote a fix or replacement." },
  { id: "plugs", type: "ack", label: "Winter plugs pulled, equipment pad de-winterized" },
  { id: "cust_home", type: "gate", label: "Is the customer home?", help: "Water is always low with this cover — filling has to start." },
  { id: "fill_ack", type: "ack", showIf: { step: "cust_home", is: true }, label: "Filling started — customer knows to stop it", help: "Hose in, customer aware." },
  { id: "wtest", type: "external", label: "Water test", help: "Run the spin lab and enter the readings — saves as a SERVICE test on this account." },
  { id: "chems", type: "line_items", label: "Chemistry adjustment", help: "Doses calculate at the reduced volume automatically. Mark truck stock vs the customer's own." },
  { id: "final", type: "photo", anchor: "end", label: "Final photo — cover off, pool in view", help: "This photo stamps the completion time." },
];
// Pool winterizing (closing): identical procedure for every cover type except
// the cover-on step itself — balance the water, winterize the pad, blow and
// plug the lines, then the type-specific cover-on, then the final photo.
const winterizingSteps = (coverOnLabel, coverOnHelp) => [
  { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo", help: "Open pool, full pool in view. This photo stamps the start time." },
  { id: "wtest", type: "external", label: "Water test", help: "Last test of the season — enter the readings; saves as a SERVICE test." },
  { id: "chems", type: "line_items", label: "Winterizing chemicals", help: "Closing kit + anything the test calls for. Mark truck stock vs the customer's own." },
  { id: "pad", type: "ack", label: "Equipment pad winterized", help: "Pump, filter and heater drained." },
  { id: "pad_note", type: "note", showIf: { step: "pad", is: false }, label: "What's the problem at the pad?", help: "So the office can quote the fix before spring." },
  { id: "lines", type: "ack", label: "Lines blown out and plugged", help: "Returns and skimmer blown, plugs in, antifreeze as needed." },
  { id: "coveron", type: "ack", label: coverOnLabel, help: coverOnHelp },
  { id: "coveron_note", type: "note", showIf: { step: "coveron", is: false }, label: "What's the problem with the cover?", help: "Rips, missing anchors, short straps — the office quotes it." },
  { id: "final", type: "photo", anchor: "end", label: "Final photo — pool covered, cover in full view", help: "This photo stamps the completion time." },
];
const DEFAULT_JOB_TYPES = [
  {
    id: "wcl_safety", name: "Pool Winterizing — Safety Cover", active: true,
    steps: winterizingSteps("Cover on — all straps secured, all safety anchor pins raised", "Every strap tensioned, every anchor pin up."),
  },
  {
    id: "wcl_tube_tarp", name: "Pool Winterizing — Tube and Tarp", active: true,
    steps: winterizingSteps("Cover on — water bags filled and placed neatly on top", "Bags filled (not overfilled), laid flat along the edges."),
  },
  {
    id: "wcl_snap_in", name: "Pool Winterizing — Snap-In Cover", active: true,
    steps: winterizingSteps("Cover secured into the tracks — liner lock added as needed", "Snapped in all the way around; liner lock where the track is loose."),
  },
  {
    id: "wcr_tube_tarp", name: "Winter Cover Removal — Tube and Tarp", active: true, alwaysLowWater: true,
    deferUnits: [{ unit: "startup", when: { always: true }, followupJobType: "system_startup", flag: WCR_LOW_WATER_FLAG }],
    steps: wcrLowWaterSteps("Cover off — water tubes drained and stored", "Tubes emptied, tarp folded and put away."),
  },
  {
    id: "wcr_snap_in", name: "Winter Cover Removal — Snap-In Cover", active: true, alwaysLowWater: true,
    deferUnits: [{ unit: "startup", when: { always: true }, followupJobType: "system_startup", flag: WCR_LOW_WATER_FLAG }],
    steps: wcrLowWaterSteps("Cover unsnapped and stored", "Every snap released clean, cover put away."),
  },
  {
    id: "pool_opening", name: "Winter Cover Removal — Safety Cover", active: true,
    deferUnits: [{ unit: "startup", when: { step: "water", is: false }, followupJobType: "system_startup",
      flag: "System startup outstanding — pool was opened on low water; startup deferred until the pool is filled." }],
    steps: [
      { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo", help: "Closed pool, cover in full view. This photo stamps the start time." },
      { id: "coveroff", type: "ack", label: "Cover off — all pins down", help: "Confirm every pin went down clean." },
      { id: "pins_note", type: "note", showIf: { step: "coveroff", is: false }, label: "Which pins are stuck?", help: "So the office can quote a fix." },
      { id: "plugs", type: "ack", label: "Winter plugs pulled, system summerized" },
      { id: "water", type: "gate", label: "Is the water level high enough to run the system?" },
      { id: "cust_home", type: "gate", showIf: { step: "water", is: false }, label: "Is the customer home?" },
      { id: "fill_ack", type: "ack", showIf: { step: "cust_home", is: true }, label: "Filling started — customer knows to stop it", help: "Hose in, customer aware." },
      { id: "sys_on", type: "ack", unit: "startup", skipIf: { step: "water", is: false }, label: "System started" },
      { id: "no_leaks", type: "ack", unit: "startup", skipIf: { step: "water", is: false }, label: "No leaks at the equipment pad" },
      { id: "leak_media", type: "media", unit: "startup", showIf: { step: "no_leaks", is: false }, label: "Photo or short video of the leak" },
      { id: "wtest", type: "external", label: "Water test", help: "Run the spin lab and enter the readings — saves as a SERVICE test on this account." },
      { id: "chems", type: "line_items", label: "Chemistry adjustment", help: "Pre-filled from the test recommendation. Mark truck stock vs the customer's own." },
      { id: "final", type: "photo", anchor: "end", label: "Final photo — pool open, full pool in view", help: "This photo stamps the completion time." },
    ],
  },
  {
    id: "equip_install", name: "Equipment Installation", active: true,
    steps: [
      { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo — old equipment or pad", help: "The pad as found. This photo stamps the start time." },
      { id: "removed", type: "photo", label: "Old equipment removed", help: "The old unit out, pad clear." },
      { id: "installed", type: "photo", label: "New equipment installed", help: "The new unit plumbed and mounted." },
      { id: "serial", type: "photo", label: "New equipment serial number", help: "Close-up — the serial must be readable for the warranty record." },
      { id: "running", type: "media", anchor: "end", label: "Short video — running under pressure, no leaks", help: "System on, joints in frame. This stamps the completion time." },
      { id: "tech_notes", type: "note", optional: true, max: 1300, label: "Technician notes (optional)", help: "Anything the office or the next tech should know — or leave it blank." },
    ],
  },
  {
    id: "site_quote", name: "Site Visit — Quote", active: true,
    steps: [
      { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo — the area we're quoting", help: "Wide shot of the space as found. Stamps the start time." },
      { id: "photos", type: "photo", multi: true, label: "Photos of the problem / project", help: "As many angles as it takes — equipment, access, damage. Keep adding until the office could quote it blind." },
      { id: "measure", type: "note", max: 1300, anchor: "end", label: "Measurements & scope", help: "Dimensions, materials, access notes — everything the quote needs. Saving this stamps the visit's end time." },
    ],
  },
  {
    id: "system_startup", name: "System startup (return visit)", active: true,
    steps: [
      { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo" },
      { id: "sys_on", type: "ack", label: "System started" },
      { id: "no_leaks", type: "ack", label: "No leaks at the equipment pad" },
      { id: "leak_media", type: "media", showIf: { step: "no_leaks", is: false }, label: "Photo or short video of the leak" },
      { id: "final", type: "photo", anchor: "end", label: "Final photo — system running" },
    ],
  },
  {
    id: "spring_vac", name: "Spring vacuuming", active: true, requiresCompleted: ["system_startup"],
    steps: [
      { id: "arrive", type: "photo", anchor: "start", label: "Arrival photo" },
      { id: "vac", type: "ack", label: "Pool vacuumed" },
      { id: "wtest", type: "external", label: "Water test" },
      { id: "chems", type: "line_items", label: "Chemistry adjustment" },
      { id: "final", type: "photo", anchor: "end", label: "Final photo" },
    ],
  },
];

// A condition against the answers so far. Unanswered → condition not met.
const wfCond = (cond, results) => {
  if (!cond) return false;
  if (cond.always) return true; // e.g. tube&tarp / snap-in: startup ALWAYS defers
  const r = results && results[cond.step];
  return !!r && r.v === cond.is;
};
// Resolve every step's state for a job type given the captured results.
function wfResolve(jobType, results = {}) {
  const out = [];
  for (const st of (jobType && jobType.steps) || []) {
    const skipped = wfCond(st.skipIf, results);                 // deferred with its unit
    const hidden = st.showIf ? !wfCond(st.showIf, results) : false; // conditional not (yet) triggered
    const r = results[st.id];
    let satisfied = false;
    if (r) {
      if (st.type === "photo" || st.type === "media") satisfied = !!(r.media && r.media.length);
      else if (st.type === "note") satisfied = st.optional ? r.note !== undefined : !!(r.note || "").trim();
      else if (st.type === "ack" || st.type === "gate") satisfied = r.v === true || r.v === false;
      else if (st.type === "external") satisfied = !!r.testId;
      else if (st.type === "line_items") satisfied = Array.isArray(r.items);
    }
    out.push({ ...st, skipped, hidden, satisfied });
  }
  return out;
}
// The step the technician is on: first visible, unskipped, unsatisfied step.
function wfCurrent(jobType, results) {
  return wfResolve(jobType, results).find((s) => !s.skipped && !s.hidden && !s.satisfied) || null;
}
function wfComplete(jobType, results) { return !wfCurrent(jobType, results); }
// Units deferred by their conditions — each spawns a follow-up work order at close.
function wfDeferred(jobType, results) {
  return ((jobType && jobType.deferUnits) || []).filter((d) => wfCond(d.when, results));
}
// Duration in whole minutes between the anchor photos; null when either is missing.
function wfDuration(jobType, results) {
  const at = (anchor) => {
    const st = ((jobType && jobType.steps) || []).find((s) => s.anchor === anchor);
    const r = st && results && results[st.id];
    return r && r.at ? new Date(r.at).getTime() : null;
  };
  const a = at("start"), b = at("end");
  return a && b && b >= a ? Math.round((b - a) / 60000) : null;
}
// "Requires completed X": booking jobType on this client is blocked while an
// unfinished job of a required type exists (e.g. deferred system startup).
function wfPrereqBlock(jobTypes, jobs, clientId, jobTypeId) {
  const jt = (jobTypes || []).find((t) => t.id === jobTypeId);
  if (!jt || !Array.isArray(jt.requiresCompleted) || !jt.requiresCompleted.length) return null;
  return (jobs || []).find((j) => j.clientId === clientId && !j.cancelled
    && j.wf && jt.requiresCompleted.includes(j.wf.jobTypeId) && !j.wf.completedAt) || null;
}
// Guided WO close (Software reminder): notes + a follow-up reason before the
// order can close. AR reasons (`arFollowReason`) are collector-only (no card /
// expired / auto-charge off / charge failed) — not shop language for a service
// stop, so this is a small service list. "No follow-up needed" still counts as
// a chosen reason; a blank reason never does.
const SERVICE_FOLLOW_NONE = "No follow-up needed";
const SERVICE_FOLLOW_REASONS = ["Callback", "Parts", "Warranty", "Water", "Other", SERVICE_FOLLOW_NONE];
function guidedCloseDraft(job) {
  const wf = (job && job.wf) || {};
  return { closeNotes: wf.closeNotes || "", followReason: wf.followReason || "" };
}
function guidedCloseGate(draft) {
  const d = draft || {};
  const notes = String(d.closeNotes != null ? d.closeNotes : (d.notes || "")).trim();
  const reason = String(d.followReason != null ? d.followReason : (d.reason || "")).trim();
  const missing = [];
  if (!notes) missing.push({ k: "notes", label: "notes" });
  if (!reason) missing.push({ k: "reason", label: "follow-up reason" });
  else if (!SERVICE_FOLLOW_REASONS.includes(reason)) missing.push({ k: "reason", label: "follow-up reason" });
  return { ok: missing.length === 0, missing };
}
function applyGuidedClose(job, draft) {
  const notes = String((draft && (draft.closeNotes != null ? draft.closeNotes : draft.notes)) || "").trim();
  const reason = String((draft && (draft.followReason != null ? draft.followReason : draft.reason)) || "").trim();
  const prev = String((job && job.note) || "").trim();
  let note = prev;
  if (notes && !prev) note = notes;
  else if (notes && prev && !prev.includes(notes)) note = prev + "\n" + notes;
  return { ...(job || {}), note, wf: { ...((job && job.wf) || {}), closeNotes: notes, followReason: reason } };
}
function guidedCloseFollowup(job, draft, meta = {}) {
  if (!guidedCloseGate(draft).ok) return null;
  const reason = String((draft && (draft.followReason != null ? draft.followReason : draft.reason)) || "").trim();
  if (reason === SERVICE_FOLLOW_NONE) return null;
  const notes = String((draft && (draft.closeNotes != null ? draft.closeNotes : draft.notes)) || "").trim();
  const wo = (job && job.workOrder) || "";
  return {
    id: meta.id || "",
    note: `${wo ? "WO " + wo + " — " : ""}${reason}: ${notes}`,
    reason,
    customerId: (job && job.clientId) || "",
    forStaffId: "",
    dueDate: meta.today || "",
    createdBy: meta.meName || "",
    createdById: meta.meId || "",
    at: meta.at || "",
    done: false,
    source: "guided-wo",
    jobId: (job && job.id) || "",
  };
}
// DIY vs professionally serviced — derived, never guessed (owner's rule: 6 tests, 80%).
// Tests already record where they were taken: "onsite" = service, "store" = shop.
const testSide = (t) => (t && (t.where === "onsite" || t.source === "service") ? "service"
  : t && (t.where === "store" || t.source === "shop") ? "shop" : null);
function diyVerdict(tests, min = 6, pct = 0.8) {
  const labelled = (tests || []).map(testSide).filter(Boolean).map((s) => ({ source: s }));
  if (labelled.length < min) return { verdict: "unknown", label: "Not enough history", n: labelled.length };
  const svc = labelled.filter((t) => t.source === "service").length;
  const share = svc / labelled.length;
  if (share >= pct) return { verdict: "pro", label: "Professionally serviced", n: labelled.length };
  if (share <= 1 - pct) return { verdict: "diy", label: "DIY — customer-managed", n: labelled.length };
  return { verdict: "both", label: "Both — shop and service", n: labelled.length };
}
function waterTestSaveGate(test, client) {
  const id = String((test && (test.clientId || test.customerId)) || (client && (client.id || client.clientId)) || "").trim();
  if (!id || /^(anonymous|rush|walk-?in|none)$/i.test(id)) {
    return { ok: false, missing: [{ k: "client", label: "named customer profile" }] };
  }
  return { ok: true, clientId: id, missing: [] };
}

// ——— Combined 14 Software DRAFT helpers (2026-09-06) — unit-testable ———
const WATER_TEST_REMINDER_NOTE = "Reminder: please test your water.";
function makeWaterTestReminder(clientId, meta = {}) {
  const gate = waterTestSaveGate({ clientId }, { id: clientId });
  if (!gate.ok) return { ok: false, missing: gate.missing };
  return {
    ok: true,
    followup: {
      id: meta.id || ("wtr-" + String(clientId || "").slice(0, 8)),
      note: meta.note || WATER_TEST_REMINDER_NOTE,
      customerId: gate.clientId,
      forStaffId: meta.forStaffId || "",
      dueDate: meta.dueDate || "",
      createdBy: meta.createdBy || "",
      at: meta.at || new Date().toISOString(),
      done: false,
      kind: "water_test_reminder",
    },
  };
}

const SALT_SYSTEM_TYPES = [
  { v: "pentair_ic", label: "Pentair IntelliChlor" },
  { v: "hayward_aquarite", label: "Hayward AquaRite" },
  { v: "jandy_aquapure", label: "Jandy AquaPure" },
  { v: "autopilot", label: "AutoPilot" },
  { v: "chloromatic", label: "Chloromatic" },
  { v: "other", label: "Other" },
];
function saltSystemTypeOptions() { return SALT_SYSTEM_TYPES.slice(); }
function needsSaltSystemType(sanitizerType) {
  return String(sanitizerType || "").toLowerCase().includes("salt");
}

const VOLUME_PRESETS = [
  { v: "10000", label: "Small — 10,000 gal", gallons: "10000" },
  { v: "15000", label: "Medium — 15,000 gal", gallons: "15000" },
  { v: "20000", label: "Standard — 20,000 gal", gallons: "20000" },
  { v: "25000", label: "Large — 25,000 gal", gallons: "25000" },
  { v: "30000", label: "XL — 30,000 gal", gallons: "30000" },
  { v: "40000", label: "Commercial — 40,000 gal", gallons: "40000" },
  { v: "custom", label: "Custom volume calculator…", gallons: "" },
];
function volumePresetOptions() { return VOLUME_PRESETS.slice(); }
function gallonsFromVolumePreset(v) {
  const hit = VOLUME_PRESETS.find((p) => p.v === String(v));
  if (!hit || hit.v === "custom") return { custom: true, gallons: "" };
  return { custom: false, gallons: hit.gallons };
}
function volumeCalcGallons({ shape = "rect", lengthFt = 0, widthFt = 0, diameterFt = 0, avgDepthFt = 0 } = {}) {
  const d = Math.max(0, Number(avgDepthFt) || 0);
  if (d <= 0) return { ok: false, gallons: 0, reason: "depth" };
  if (shape === "round") {
    const dia = Math.max(0, Number(diameterFt) || 0);
    if (dia <= 0) return { ok: false, gallons: 0, reason: "diameter" };
    const r = dia / 2;
    const gal = Math.round(Math.PI * r * r * d * 7.48);
    return { ok: gal > 0, gallons: gal };
  }
  const L = Math.max(0, Number(lengthFt) || 0);
  const W = Math.max(0, Number(widthFt) || 0);
  if (L <= 0 || W <= 0) return { ok: false, gallons: 0, reason: "size" };
  const gal = Math.round(L * W * d * 7.48);
  return { ok: gal > 0, gallons: gal };
}
function equipmentTimerChangeNote(prev, next, when = "") {
  const a = equipmentTimerFields(prev);
  const b = equipmentTimerFields(next);
  if (a.timerOn === b.timerOn && a.timerOff === b.timerOff) return b.timerNote || "";
  const stamp = when || new Date().toISOString().slice(0, 10);
  const base = `Times changed ${stamp}: ${a.timerOn || "—"}–${a.timerOff || "—"} → ${b.timerOn || "—"}–${b.timerOff || "—"}`;
  return b.timerNote && b.timerNote !== a.timerNote ? b.timerNote : base;
}

function addEquipmentOfKind(list, kind, detail = "") {
  const k = String(kind || "other");
  const row = { id: "eq-" + Math.random().toString(36).slice(2, 9), kind: k, detail: detail || "",
    serial: "", installed: "", timerOn: "", timerOff: "", timerNote: "" };
  return [...(Array.isArray(list) ? list : []), row];
}
function equipmentTimerFields(e) {
  const x = e || {};
  return { timerOn: x.timerOn || "", timerOff: x.timerOff || "", timerNote: x.timerNote || "" };
}

const COMPLAINT_VALIDITY = [
  { v: "valid", label: "Valid" },
  { v: "not_valid", label: "Not valid" },
];
const COMPLAINT_SOURCE = [
  { v: "customer", label: "Customer-created issue" },
  { v: "service", label: "Service issue" },
];
function blankComplaint(src = {}) {
  return {
    id: src.id || ("cmp-" + Math.random().toString(36).slice(2, 9)),
    text: String(src.text || src.note || "").trim(),
    validity: COMPLAINT_VALIDITY.some((x) => x.v === src.validity) ? src.validity : "valid",
    source: COMPLAINT_SOURCE.some((x) => x.v === src.source) ? src.source : "customer",
    at: src.at || new Date().toISOString(),
    by: src.by || "",
  };
}
function storeComplaint(list, entry) {
  const row = blankComplaint(entry);
  if (!row.text) return { ok: false, list: Array.isArray(list) ? list.slice() : [], missing: [{ k: "text", label: "complaint text" }] };
  return { ok: true, list: [...(Array.isArray(list) ? list : []), row], complaint: row };
}
function complaintCount(list, standingComplaints) {
  if (Array.isArray(list) && list.length) return list.length;
  return Math.max(0, Number(standingComplaints) || 0);
}

const VIDEO_DURATION_MIN = 8;
const VIDEO_DURATION_MAX = 11;
function videoDurationGate(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return { ok: false, reason: "missing", min: VIDEO_DURATION_MIN, max: VIDEO_DURATION_MAX };
  if (n < VIDEO_DURATION_MIN) return { ok: false, reason: "short", seconds: n, min: VIDEO_DURATION_MIN, max: VIDEO_DURATION_MAX };
  if (n > VIDEO_DURATION_MAX) return { ok: false, reason: "long", seconds: n, min: VIDEO_DURATION_MIN, max: VIDEO_DURATION_MAX };
  return { ok: true, seconds: n, min: VIDEO_DURATION_MIN, max: VIDEO_DURATION_MAX };
}
function readVideoDurationSeconds(file) {
  return new Promise((resolve) => {
    if (!file || !(file.type || "").startsWith("video/")) { resolve(null); return; }
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(url); resolve(Number.isFinite(d) ? d : null); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      v.src = url;
    } catch { resolve(null); }
  });
}

const PO_VENDOR_MINIMUMS = {
  Central: 250,
  Lawrasons: 200,
  Mursatt: 150,
};
function poVendorMinimum(vendor) {
  const name = String(vendor || "").trim();
  if (!name) return null;
  if (PO_VENDOR_MINIMUMS[name] != null) return { vendor: name, minimum: PO_VENDOR_MINIMUMS[name] };
  const key = Object.keys(PO_VENDOR_MINIMUMS).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? { vendor: key, minimum: PO_VENDOR_MINIMUMS[key] } : null;
}
function poLineTotal(items) {
  return (items || []).reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.cost) || 0), 0);
}
function poMinimumGap(vendor, itemsOrTotal) {
  const info = poVendorMinimum(vendor);
  if (!info) return { flagged: false, vendor: String(vendor || ""), minimum: 0, total: 0, shortfall: 0 };
  const total = typeof itemsOrTotal === "number" ? itemsOrTotal : poLineTotal(itemsOrTotal);
  const shortfall = Math.max(0, Math.round((info.minimum - total) * 100) / 100);
  return { flagged: shortfall > 0, vendor: info.vendor, minimum: info.minimum, total, shortfall };
}
function suggestItemsToMinimum(parts, vendor, shortfall, excludeCodes = []) {
  const gap = Number(shortfall) || 0;
  if (gap <= 0) return [];
  const ex = new Set((excludeCodes || []).map((c) => String(c || "").trim().toLowerCase()).filter(Boolean));
  const v = String(vendor || "").trim().toLowerCase();
  const cand = (parts || []).filter((p) => {
    if (!p || p.active === false) return false;
    const code = String(p.code || "").trim().toLowerCase();
    if (code && ex.has(code)) return false;
    const sups = [p.supplier1, p.supplier2, p.supplier3].map((x) => String(x || "").trim().toLowerCase());
    if (v && !sups.includes(v)) return false;
    const cost = Number(p.cost) || Number(p.cost2) || Number(p.cost3) || 0;
    return cost > 0;
  }).map((p) => {
    const cost = Number(p.cost) || Number(p.cost2) || Number(p.cost3) || 0;
    const qty = Math.max(1, Math.ceil(gap / cost));
    return { code: p.code || "", name: p.name || "", qty, cost, cover: Math.round(qty * cost * 100) / 100 };
  }).sort((a, b) => a.cover - b.cover);
  return cand.slice(0, 6);
}

function specialOrderToPoItem(o) {
  return {
    code: (o && o.code) || "",
    name: (o && (o.item || o.name)) || "Special order item",
    qty: Math.max(1, Number(o && o.qty) || 1),
    cost: (o && (o.cost || o.unitCost)) || "",
    soId: (o && o.id) || "",
  };
}
function upsertSpecialOrderPo(poList, so, meta = {}) {
  const item = specialOrderToPoItem(so);
  const sup = String((so && so.supplier) || meta.supplier || "").trim();
  let list = Array.isArray(poList) ? poList.slice() : [];
  const holding = list.find((x) => x.status === "holding" && String(x.supplier || "").trim().toLowerCase() === sup.toLowerCase());
  if (!holding) {
    const po = {
      id: meta.id || ("po-so-" + String((so && so.id) || Math.random().toString(36).slice(2, 8))),
      poNo: "", status: "holding", supplier: sup, notes: meta.notes || `From special order${so && so.customerName ? " — " + so.customerName : ""}`,
      items: [item], createdBy: meta.createdBy || "Special order", createdDate: meta.createdDate || "",
      fromSpecialOrderId: (so && so.id) || "",
    };
    return { list: [...list, po], po, created: true };
  }
  const has = (holding.items || []).some((it) => (it.soId && so && it.soId === so.id)
    || (it.code && item.code && String(it.code).toLowerCase() === String(item.code).toLowerCase()));
  if (has) return { list, po: holding, created: false };
  const po = { ...holding, items: [...(holding.items || []), item] };
  list = list.map((x) => (x.id === holding.id ? po : x));
  return { list, po, created: false };
}

function specialOrderEmailGate(o, client) {
  const missing = [];
  if (!o) missing.push({ k: "order", label: "special order" });
  const contacts = (client && client.contacts) || [];
  const withEmail = contacts.find((k) => String(k.email || "").trim());
  const pref = contacts.find((k) => k.comms && k.comms.general && k.comms.general !== "none" && String(k.email || "").trim()
    && (k.comms.general === "email" || k.comms.general === "both"));
  const to = (pref && pref.email) || (withEmail && withEmail.email) || "";
  if (!String(to).trim()) missing.push({ k: "email", label: "customer email on file" });
  return { ok: missing.length === 0, missing, to: String(to || "").trim(), contact: pref || withEmail || null };
}
function specialOrderEmailPayload(o, client, opts = {}) {
  const gate = specialOrderEmailGate(o, client);
  if (!gate.ok) return { ok: false, missing: gate.missing };
  const body = opts.body || specialOrderMsg(o);
  const subject = opts.subject || (o && o.status === "received"
    ? "Your special order has arrived — Pool Master"
    : "Pool Master — your special order");
  // emailCat was previously omitted on markReceived/sendReminder — retail mailbox never got the send.
  return {
    ok: true,
    payload: {
      kind: "mail",
      client,
      to: gate.to,
      emailCat: opts.emailCat || "retail",
      subject,
      body,
    },
    send: false, // never auto-send from helpers — ComposeSheet / staff confirm
  };
}

const SMS_SENDER_LABEL = "Pool Master";
function smsSenderIdentity(businessProfile) {
  const n = String((businessProfile && businessProfile.name) || "").trim();
  if (!n) return SMS_SENDER_LABEL;
  if (/pool\s*master/i.test(n)) return "Pool Master";
  return n;
}
function ensureSmsBrand(body, label = SMS_SENDER_LABEL) {
  const text = String(body || "");
  if (!text.trim()) return text;
  if (new RegExp("^\\s*" + label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b", "i").test(text)) return text;
  if (/^\s*pool\s*master\b/i.test(text)) return text;
  return `${label}: ${text}`;
}

function searchClientsForEmail(clients, q, limit = 8) {
  const t = String(q || "").trim().toLowerCase();
  if (t.length < 1) return [];
  return (clients || []).filter((c) => {
    const hay = [c.name, c.address, ...((c.contacts || []).map((k) => `${k.name || ""} ${k.email || ""} ${k.phone || ""}`))].join(" ").toLowerCase();
    return hay.includes(t);
  }).slice(0, limit).map((c) => {
    const k = ((c.contacts || []).find((x) => String(x.email || "").trim()) || (c.contacts || [])[0] || {});
    return { id: c.id, name: c.name || "", email: String(k.email || "").trim(), client: c };
  }).filter((r) => r.email);
}

function displayTitleCase(s) {
  const raw = String(s ?? "");
  if (!raw.trim()) return raw;
  return raw.replace(/[A-Za-z0-9][A-Za-z0-9']*/g, (w) => {
    if (/^[A-Z0-9]{2,}$/.test(w) && w.length <= 5) return w; // keep acronyms like PO, SMS, HST
    if (/^\d/.test(w)) return w;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  });
}

// Low water: inches below normal → dose at reduced volume, never rated volume.
// Assumes ~54" average depth when the structure can't say better; never cuts past half.
function lowWaterFactor(inchesDown, avgDepthIn = 54) {
  const n = Number(inchesDown) || 0;
  if (n <= 0) return 1;
  return Math.max(0.5, Math.round((1 - n / avgDepthIn) * 100) / 100);
}
// Truck-first stock consumption for chemicals used on a guided work order:
// drains the tech's truck count first, spills the remainder onto shop stock.
function partsAfterTruckUse(parts, techId, items) {
  return (parts || []).map((p) => {
    const mine = (items || []).filter((it) => (p.code || "").trim().toLowerCase() === String(it.code || "").trim().toLowerCase());
    const used = mine.reduce((a, it) => a + (Math.max(0, Number(it.qty)) || 1), 0);
    if (!used) return p;
    // serials consumed on the job leave the truck's serial list with the unit
    if (partHasWarranty(p) && p.truckSerials && p.truckSerials[techId]) {
      const consumed = mine.flatMap((it) => serialList(it.serial));
      if (consumed.length) {
        const pool = (p.truckSerials[techId] || []).filter((x) => !consumed.includes(x));
        const ts = { ...p.truckSerials };
        if (pool.length) ts[techId] = pool; else delete ts[techId];
        p = { ...p, truckSerials: ts };
      }
    }
    const tq = Number((p.trucks || {})[techId]) || 0;
    if (tq > 0) {
      const fromTruck = Math.min(tq, used), spill = used - fromTruck;
      const trucks = { ...(p.trucks || {}) };
      if (tq - fromTruck > 0) trucks[techId] = tq - fromTruck; else delete trucks[techId];
      return { ...p, trucks, qty: String(Math.max(0, (Number(p.qty) || 0) - spill)) };
    }
    return { ...p, qty: String(Math.max(0, (Number(p.qty) || 0) - used)) };
  });
}
// Running numbers for invoices and work orders (single shared series).
const defNumbering = () => ({ inv: 1001, wo: 1001, po: 2001, rp: 3001 });
const fmtInvoice = (n) => "INV-" + n;
const fmtWO = (n) => "WO-" + n;
const fmtPO = (n) => "PO-" + n;
// Download rows as a CSV the accountant can open in Excel.
function csvDownload(filename, rows) {
  const esc = (v) => { const t = String(v ?? ""); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
  const blob = new Blob(["\ufeff" + rows.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click(); URL.revokeObjectURL(a.href);
}

// Parse a CSV (the format csvDownload writes): BOM, quoted commas/newlines, "" escapes.
function parseCsv(text) {
  const src = String(text || "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [], field = "", i = 0, inQ = false;
  const pushRow = () => { if (row.some((x) => String(x).trim() !== "")) rows.push(row); row = []; };
  while (i < src.length) {
    const c = src[i];
    if (inQ) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQ = true; i++; continue; }
    if (c === ",") { row.push(field); field = ""; i++; continue; }
    if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(field); field = ""; pushRow(); i++; continue;
    }
    field += c; i++;
  }
  if (inQ || field !== "" || row.length) { row.push(field); pushRow(); }
  return rows;
}
const csvKey = (h) => String(h || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const csvMoney = (v) => String(v ?? "").trim().replace(/^\$/, "").replace(/,/g, "");
function csvYesNo(v, fallback = true) {
  const t = String(v ?? "").trim().toLowerCase();
  if (!t) return fallback;
  if (["no", "false", "0", "n", "inactive", "off"].includes(t)) return false;
  if (["yes", "true", "1", "y", "active", "on"].includes(t)) return true;
  return fallback;
}
function csvServiceKind(v) {
  const t = String(v || "").trim().toLowerCase();
  if (t === "labour" || t === "labor" || t.startsWith("lab")) return "labour";
  if (t === "other") return "other";
  return "service";
}
// Merge a service-rate CSV into data.services (Inventory → Labour & services).
// Match by id, then by name. New names are added. Missing columns leave existing fields.
function applyServiceRatesCsv(existing, text, newId) {
  const rows = parseCsv(text);
  const list = (existing || []).map((x) => ({ ...x }));
  if (!rows.length) return { list, updated: 0, added: 0, skipped: 0, error: "That file is empty." };
  const headers = rows[0].map(csvKey);
  const col = (...aliases) => headers.findIndex((h) => aliases.map(csvKey).includes(h));
  const iName = col("name", "description", "service");
  const iDesc = col("desc", "includes");
  const iRate = col("rate", "zone1", "zone1rate", "price");
  const iZ2 = col("ratezone2", "zone2", "zone2rate");
  const iKind = col("kind", "type");
  const iActive = col("active");
  const iId = col("id");
  if (iName < 0) return { list, updated: 0, added: 0, skipped: 0, error: "Need a name column (name, description, or service)." };
  let updated = 0, added = 0, skipped = 0;
  const taken = new Set();
  const cell = (r, i) => (i >= 0 && r[i] != null ? String(r[i]) : "");
  rows.slice(1).forEach((r) => {
    const name = cell(r, iName).trim();
    if (!name) { skipped++; return; }
    const idIn = cell(r, iId).trim();
    let idx = -1;
    if (idIn) idx = list.findIndex((s, n) => !taken.has(n) && s.id === idIn);
    if (idx < 0) idx = list.findIndex((s, n) => !taken.has(n) && (s.name || "").trim().toLowerCase() === name.toLowerCase());
    const patch = { name };
    if (iDesc >= 0) patch.desc = cell(r, iDesc);
    if (iRate >= 0) patch.rate = csvMoney(cell(r, iRate));
    if (iZ2 >= 0) patch.rateZone2 = csvMoney(cell(r, iZ2));
    if (iKind >= 0) patch.kind = csvServiceKind(cell(r, iKind));
    if (iActive >= 0) patch.active = csvYesNo(cell(r, iActive), true);
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...patch };
      taken.add(idx);
      updated++;
    } else {
      list.push({
        id: (typeof newId === "function" ? newId() : cid()),
        name,
        desc: iDesc >= 0 ? cell(r, iDesc) : "",
        rate: iRate >= 0 ? csvMoney(cell(r, iRate)) : "",
        rateZone2: iZ2 >= 0 ? csvMoney(cell(r, iZ2)) : "",
        kind: iKind >= 0 ? csvServiceKind(cell(r, iKind)) : "service",
        active: iActive >= 0 ? csvYesNo(cell(r, iActive), true) : true,
      });
      taken.add(list.length - 1);
      added++;
    }
  });
  return { list, updated, added, skipped, error: null };
}

// Printable purchase order — Pool Master logo at the top, numbered, itemized.
function poHtml(po) {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const money = (v) => "$" + (parseFloat(v) || 0).toFixed(2);
  const lines = (po.items || []).map((it) => {
    const qty = parseFloat(it.qty) || 0;
    const cost = parseFloat(it.cost) || 0;
    return `<tr><td class="mono">${esc(it.code || "—")}</td><td>${esc(it.name || "")}</td><td class="r">${qty}</td><td class="r">${cost ? money(cost) : "—"}</td><td class="r">${cost ? money(qty * cost) : "—"}</td></tr>`;
  }).join("");
  const total = (po.items || []).reduce((a, it) => a + (parseFloat(it.qty) || 0) * (parseFloat(it.cost) || 0), 0);
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(po.poNo || "Purchase order")}</title>
<style>
  body{font:13.5px/1.55 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;max-width:720px;margin:28px auto;padding:0 18px;}
  .head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;border-bottom:3px solid #17356E;padding-bottom:14px;}
  .title{text-align:right;} .title h1{margin:0;font-size:22px;color:#17356E;letter-spacing:2px;} .title .no{font-size:16px;font-weight:800;color:#E41F24;}
  .muted{color:#5a6472;} .mono{font-family:ui-monospace,Menlo,monospace;}
  .meta{display:flex;gap:40px;margin:16px 0;} .meta b{display:block;font-size:10.5px;text-transform:uppercase;letter-spacing:1px;color:#5a6472;}
  table{width:100%;border-collapse:collapse;margin:10px 0 6px;} th{font-size:10.5px;text-transform:uppercase;letter-spacing:.8px;color:#5a6472;text-align:left;border-bottom:2px solid #17356E;padding:6px 6px;} td{padding:7px 6px;border-bottom:1px solid #dbe4ee;} .r{text-align:right;}
  .tot td{border-top:2px solid #17356E;border-bottom:none;font-weight:800;font-size:15px;}
  .notes{margin-top:14px;background:#F2F7FC;border:1px solid #dbe4ee;border-radius:8px;padding:10px 13px;}
  .sig{margin-top:34px;display:flex;gap:40px;} .sig div{flex:1;border-top:1px solid #12233b;padding-top:5px;font-size:11px;color:#5a6472;}
  @media print{body{margin:10px auto;}}
</style></head><body onload="window.print()">
  <div class="head">
    <div>${LOGO_SVG}<div class="muted" style="margin-top:6px">Pool Master Canada</div></div>
    <div class="title"><h1>PURCHASE ORDER</h1><div class="no">${esc(po.poNo || "DRAFT — not yet numbered")}</div><div class="muted">${esc(po.orderedDate || po.createdDate || "")}</div></div>
  </div>
  <div class="meta">
    <div style="max-width:46%"><b>Vendor</b>${esc(po.supplier || "—")}${po.supplierContact ? `<br>${esc(po.supplierContact)}` : ""}${po.supplierAddress ? `<br>${esc(po.supplierAddress)}` : ""}${po.supplierPhone ? `<br>${esc(po.supplierPhone)}` : ""}</div>
    <div style="max-width:40%"><b>Ship to</b>${esc((po.shipTo || {}).name || "Pool Master Canada")}${(po.shipTo || {}).address ? `<br>${esc(po.shipTo.address)}` : ""}${(po.shipTo || {}).phone ? `<br>${esc(po.shipTo.phone)}` : ""}</div>
    <div><b>Created by</b>${esc(po.createdBy || "—")}</div>
    <div><b>Sent by</b>${esc(po.orderedBy || "—")}</div>
  </div>
  <table>
    <thead><tr><th>Part #</th><th>Description</th><th class="r">Qty</th><th class="r">Unit cost</th><th class="r">Line total</th></tr></thead>
    <tbody>${lines}</tbody>
    <tfoot><tr class="tot"><td colspan="4">Total</td><td class="r">${total ? money(total) : "—"}</td></tr></tfoot>
  </table>
  ${po.notes ? `<div class="notes">${esc(po.notes)}</div>` : ""}
  <div class="sig"><div>Authorized by</div><div>Date</div></div>
</body></html>`;
}
function printPO(po) { const w = window.open("", "_blank"); if (!w) return; w.document.write(poHtml(po)); w.document.close(); }

const daysInRequest = (r) => {
  const a = parseDate(r.from), b = parseDate(r.to);
  if (!a || !b) return 0;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
};
const TO_STATUS = {
  pending: { label: "Waiting on your manager", pct: 55, colour: "#E8B203", step: 1 },
  approved: { label: "Approved", pct: 100, colour: "#12A650", step: 2 },
  declined: { label: "Declined", pct: 100, colour: "#DC1515", step: 2 },
  cancelled: { label: "Withdrawn", pct: 100, colour: "#93A4B8", step: 2 },
};
const TO_STEPS = ["Sent", "With manager", "Decision"];

const offOnDate = (staffId, d, timeOff) => {
  if (!staffId) return null;
  const day = fmtDate(d);
  return (timeOff || []).find((r) => r.status === "approved" && r.staffId === staffId
    && r.from <= day && day <= r.to) || null;
};
const hoursBetween = (a, b) => Math.max(0, (new Date(b) - new Date(a)) / 3600000);
const hhmm = (h) => `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, "0")}m`;
const clockTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const defStanding = () => ({ refunds: 0, exchanges: 0, complaints: 0 });
const num = (v) => parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")) || 0;
// Shared totals for materials and labour line items (used by both the visit form and the quote builder).
const sumMaterials = (arr) => (arr || []).reduce((a, m) => a + (num(m.qty) || 1) * num(m.price), 0);
const sumLabour = (arr) => (arr || []).reduce((a, l) => a + num(l.rate), 0);

function ratingParts(c, visits) {
  const v = visits || [];
  const st = { ...defStanding(), ...(c.standing || {}) };
  const matCost = v.reduce((a, x) => a + num(x.matCost), 0);
  const matPrice = v.reduce((a, x) => a + num(x.matPrice), 0);
  const labour = v.reduce((a, x) => a + num(x.labour), 0);
  const gross = matPrice - matCost + labour;
  const margin = matPrice > 0 ? (matPrice - matCost) / matPrice : null;
  const early = v.filter((x) => x.bookedEarly).length;

  const parts = [{ label: "Baseline", pts: 4 }];
  const add = (label, pts) => { if (Math.abs(pts) >= 0.05) parts.push({ label, pts: Math.round(pts * 10) / 10 }); };

  if (gross > 0) add(`$${gross.toFixed(0)} gross profit`, Math.min(3, gross / 500));
  if (margin !== null) add(`${(margin * 100).toFixed(0)}% margin on materials`, Math.max(-1, Math.min(2, (margin / 0.4) * 2)));
  if (labour > 0) add(`$${labour.toFixed(0)} labour invoiced`, Math.min(1, labour / 800));
  if (early) add(`${early} booked early`, Math.min(1.5, early * 0.5));
  if (st.refunds) add(`${st.refunds} refund${st.refunds === 1 ? "" : "s"}`, -Math.min(2, st.refunds * 0.5));
  if (st.exchanges) add(`${st.exchanges} exchange${st.exchanges === 1 ? "" : "s"}`, -Math.min(1.5, st.exchanges * 0.3));
  if (st.complaints) add(`${st.complaints} documented complaint${st.complaints === 1 ? "" : "s"}`, -Math.min(3, st.complaints * 1));

  const raw = parts.reduce((a, b) => a + b.pts, 0);
  const thin = matPrice === 0 && labour === 0;
  return { parts, raw: Math.round(raw * 10) / 10, score: Math.max(1, Math.min(10, Math.round(raw))), thin, gross, margin };
}
const RATING_SCALE = [
  { k: "bad", word: "Bad", colour: "#E01B1B" },
  { k: "ok", word: "Ok", colour: "#F5761A" },
  { k: "normal", word: "Normal", colour: "#FFC400" },
  { k: "good", word: "Good", colour: "#9ACD32" },
  { k: "great", word: "Great", colour: "#12A650" },
];
const RATING_STOPS = RATING_SCALE.map((x) => x.colour);
const ratingBand = (n) => RATING_SCALE[Math.max(0, Math.min(4, Math.ceil(Number(n) / 2) - 1))].k;
const ratingWord = Object.fromEntries(RATING_SCALE.map((x) => [x.k, x.word]));
const ratingColour = (n) => (RATING_SCALE.find((x) => x.k === ratingBand(n)) || RATING_SCALE[0]).colour;

/* ---------- seed ---------- */
const SEED = {
  recent: [],
  staff: DEFAULT_STAFF,
  punches: [],
  messages: [],
  timeOff: [],
  jobs: [],
  techs: DEFAULT_TECHS,
  schedule: defSchedule(),
  roles: DEFAULT_ROLES,
  currentRole: "owner",
  levels: DEFAULT_LEVELS,
  parts: DEFAULT_PARTS,
  services: DEFAULT_SERVICES,
  terms: DEFAULT_TERMS,
  revCentres: DEFAULT_REVCENTRES,
  serviceTypes: DEFAULT_SERVICETYPES,
  numbering: defNumbering(),
  posSales: [],
  posHeld: [],
  specialOrders: [],
  clients: [
    {
      id: "c1", name: "Halvorsen Residence", address: "88 Rosedale Heights Dr, Toronto",
      billingSame: false, billingAddress: "c/o A. Halvorsen, 410-77 Bay St, Toronto M5J 2T3",
      type: "both", days: ["Tue"], frequency: "weekly", shortTermUntil: "", order: 1, complexity: 7, complexityMode: "auto",
      billing: { cycle: "monthly", payMethod: "credit", autoCharge: true,
        card: { holder: "A. Halvorsen", brand: "Visa", last4: "4412", exp: "03/26", ref: "cus_9F2K71", updated: "2024-04-02" },
        discountMode: "level", levelId: "loyal", discounts: { labour: 0, chemical: 0, material: 0 },
        acctNo: "H-1042", terms: "receipt", chargeDay: 1, monthlyRate: "185", hourlyLabour: "95", tripCharge: "0",
        seasonStart: "2026-05-01", seasonEnd: "2026-10-15", taxable: true,
        billingNotes: "Prefers one monthly statement, not per-visit invoices." },
      contacts: [
        { id: "k1", name: "Ann Halvorsen", role: "Owner", phone: "416-555-0142", email: "ann.h@example.com",
          comms: { invoices: "email", service: "none", general: "email" } },
        { id: "k2", name: "Paul Halvorsen", role: "Spouse", phone: "416-555-0188", email: "",
          comms: { invoices: "none", service: "text", general: "text" } },
      ],
      structure: "pool_spa_features", spaSeparate: true, spaGallons: "800",
      spaNotes: "Spillover spa on its own pump and blower. Balanced separately.",
      coverType: "safety", coverLeafNet: false,
      surface: "Gunite / plaster", gallons: "22,000",
      features: { skimmers: 2, drains: 2, returns: 6, lights: 3, waterfalls: 1, deckJets: 4, sideSuctions: 1 },
      sanitizerType: "Salt",
      equipmentList: [
        { id: "e1", kind: "pump", detail: "Pentair IntelliFlo VSF, variable speed" },
        { id: "e2", kind: "filter", detail: "Hayward S244T sand, media changed 2024" },
        { id: "e3", kind: "heater", detail: "Raypak 406A — bypassed, not in service" },
        { id: "e4", kind: "chlorinator", detail: "Hayward AquaRite T-Cell-15, cell replaced 2025" },
        { id: "e5", kind: "alt", detail: "" },
        { id: "e6", kind: "blower", detail: "Spa blower, 1.5HP — noisy on start" },
      ],
      gateCode: "4417#", dog: true, keyLocation: "Lockbox on north fence post",
      accessNotes: "Side gate only — do not use the house. Dog is friendly but bolts; close gate behind you.",
      notes: "Buys chemicals in-store every spring. Wants a heater quote this fall.", lastService: "2026-07-14",
      waterTesting: true, lastWaterTest: "2026-07-14", standing: { refunds: 0, exchanges: 1, complaints: 0 },
      flag: { on: true, scope: "service", updated: "2026-06-30",
        message: "Never enter through the house. Owner works nights — no arrivals before 10am." },
    },
    {
      id: "c2", name: "Kemper Pool", address: "12 Larkspur Ln, Etobicoke",
      billingSame: true, billingAddress: "",
      type: "service", days: ["Tue"], frequency: "biweekly", shortTermUntil: "", anchorDate: "2026-07-14",
      monthlyMode: "nth", monthlyNth: 1, monthlyDate: 1, order: 2, complexity: 3, complexityMode: "auto",
      billing: { cycle: "per_visit", payMethod: "etransfer", autoCharge: false,
        card: { holder: "", brand: "Visa", last4: "", exp: "", ref: "", updated: "" },
        discountMode: "level", levelId: "pushy", discounts: { labour: 0, chemical: 0, material: 0 },
        acctNo: "K-0887", terms: "receipt", hourlyLabour: "95", taxable: true, billingNotes: "" },
      contacts: [{ id: "k3", name: "Ray Kemper", role: "Owner", phone: "647-555-0199", email: "",
        comms: { invoices: "text", service: "text", general: "text" } }],
      structure: "pool", spaSeparate: false, spaGallons: "", spaNotes: "",
      coverType: "tarp", coverLeafNet: true,
      surface: "Vinyl liner", gallons: "16,500",
      features: { skimmers: 1, drains: 1, returns: 2, lights: 1, waterfalls: 0, deckJets: 0, sideSuctions: 0 },
      sanitizerType: "Chlorine tabs",
      equipmentList: [
        { id: "e7", kind: "pump", detail: "Jandy 1.5HP single speed" },
        { id: "e8", kind: "filter", detail: "Cartridge, C4025 element" },
        { id: "e9", kind: "heater", detail: "None" },
        { id: "e10", kind: "chlorinator", detail: "Inline chlorinator, Rainbow 320" },
        { id: "e11", kind: "alt", detail: "" },
      ],
      gateCode: "", dog: false, keyLocation: "Gate unlocked Tues only",
      accessNotes: "Park on the street, driveway is tight.",
      notes: "Liner seam lifting near the shallow-end step — watch it.", lastService: "2026-07-14",
      waterTesting: true, lastWaterTest: "2026-06-28", standing: { refunds: 1, exchanges: 0, complaints: 1 },
    },
    {
      id: "c3", name: "Bright Star Motel", address: "2200 Lakeshore Rd W, Mississauga",
      billingSame: false, billingAddress: "Accounts Payable, Brightstar Hospitality, 40 King St W Suite 1900, Toronto M5H 3Y2",
      type: "service", days: ["Mon", "Thu"], frequency: "twice", shortTermUntil: "", order: 1, complexity: 9, complexityMode: "auto",
      billing: { cycle: "monthly", payMethod: "cheque", autoCharge: false,
        card: { holder: "", brand: "Visa", last4: "", exp: "", ref: "", updated: "" },
        discountMode: "custom", levelId: "regular", discounts: { labour: 15, chemical: 8, material: 20 },
        acctNo: "BSM-2200", terms: "net30", paperInvoice: true, monthlyRate: "1450", hourlyLabour: "110", tripCharge: "45",
        poRequired: true, poNumber: "PO-88213", creditLimit: "5000", lateFeePct: "2", taxable: true,
        billingNotes: "Statement to head office. PO number required on every invoice." },
      contacts: [
        { id: "k4", name: "Front desk", role: "Site contact", phone: "905-555-0110", email: "ops@brightstar.example",
          comms: { invoices: "none", service: "email", general: "email" } },
        { id: "k5", name: "T. Nadeau", role: "Property manager", phone: "905-555-0177", email: "tn@brightstar.example",
          comms: { invoices: "email", service: "both", general: "email" } },
      ],
      structure: "pool_spa", spaSeparate: false, spaGallons: "", spaNotes: "",
      coverType: "none", coverLeafNet: false,
      surface: "Gunite / tile", gallons: "48,000",
      features: { skimmers: 4, drains: 2, returns: 10, lights: 4, waterfalls: 0, deckJets: 0, sideSuctions: 2 },
      sanitizerType: "Liquid chlorine",
      equipmentList: [
        { id: "e12", kind: "pump", detail: "Two 3HP commercial, staged" },
        { id: "e13", kind: "filter", detail: "DE, 60 sq ft — backwash log required" },
        { id: "e14", kind: "heater", detail: "Lochinvar gas, 400k BTU" },
        { id: "e15", kind: "chlorinator", detail: "Stenner peristaltic feed pump" },
        { id: "e16", kind: "alt", detail: "UV, Delta 35 — lamp due 2027" },
        { id: "e17", kind: "automation", detail: "Pentair EasyTouch, panel in mechanical room" },
      ],
      gateCode: "0913", dog: false, keyLocation: "Mechanical room key at front desk",
      accessNotes: "Commercial log book must be signed at every visit.",
      notes: "Health inspection each June. Keep chem logs current.", lastService: "2026-07-13",
      trade: true, waterTesting: false, lastWaterTest: "", lastRetail: "2026-06-20", standing: { refunds: 0, exchanges: 0, complaints: 0 },
      flag: { on: true, scope: "retail", updated: "2026-05-14",
        message: "Purchase order number required before anything leaves the counter. Head office won't pay without it." },
    },
  ],
};

const SEED_VISITS = {
  c1: [
    { id: "v0", date: "2026-07-02", workOrder: "WO-4712", invoiceNo: "INV-2210", amount: "240.00", by: "Shop bench",
      side: "service", paid: true, repairType: "Robotic cleaner", matCost: "62.00", matPrice: "140.00", labour: "100.00",
      notes: "Dolphin drive motor seized. Replaced motor block and impeller, tested two cycles." },
    { id: "v1", date: "2026-07-14", workOrder: "WO-4821", invoiceNo: "INV-2291", amount: "185.00", by: "Marco R.", matCost: "38.00", matPrice: "96.00", labour: "89.00", bookedEarly: true,
      notes: "Weekly service. Salt cell cleaned, chlorine output back to 60%. Skimmer basket cracked — replaced. Deck jets 3 and 4 running weak, likely partial blockage, will chase next visit." },
    { id: "v2", date: "2026-07-07", workOrder: "WO-4770", invoiceNo: "INV-2244", amount: "185.00", by: "Marco R.", paid: false, side: "service", matCost: "22.00", matPrice: "61.00", labour: "124.00",
      notes: "Weekly service. Water balanced, brushed waterline. Owner asked about heater repair vs replace." },
  ],
  c3: [
    { id: "v4", date: "2026-06-20", workOrder: "", invoiceNo: "INV-2190", amount: "312.40", by: "Counter", paid: false, side: "retail", delivery: true, matCost: "196.00", matPrice: "312.40", labour: "0",
      notes: "Counter sale — DE, tabs, two skimmer baskets. Picked up by maintenance staff." },
    { id: "v3", date: "2026-07-13", workOrder: "WO-4815", invoiceNo: "INV-2288", amount: "420.00", by: "Dee W.", paid: false, side: "service", matCost: "140.00", matPrice: "265.00", labour: "155.00", bookedEarly: true,
      notes: "Commercial service. DE filter backwashed and recharged, log book signed. Stenner tube showing wear — schedule replacement before August." },
  ],
};

const SEED_TESTS = {
  c1: [
    { id: "t1", date: "2026-07-14", source: "spinlab", where: "store", notes: "Counter test, sample brought in by owner.",
      results: { fc: "2.4", tc: "2.8", ph: "7.6", ta: "104", ch: "310", cya: "44", salt: "3100", phos: "180", copper: "0.1", iron: "0" } },
    { id: "t2", date: "2026-06-16", source: "spinlab", where: "onsite", notes: "",
      results: { fc: "0.6", tc: "1.4", ph: "8.0", ta: "128", ch: "295", cya: "41", salt: "2600", phos: "420", copper: "0", iron: "0" } },
  ],
  c2: [
    { id: "t3", date: "2026-06-28", source: "manual", where: "onsite", notes: "Strip test at the pool, spin lab sample to follow.",
      results: { fc: "1.8", ph: "7.4", ta: "90", ch: "220", cya: "38" } },
  ],
};

function migrate(d) {
  if (!d || !Array.isArray(d.clients)) return SEED;
  const ladderDone = (d.businessProfile || {}).discountLadder === DISCOUNT_LADDER;
  // Upgrade the old factory revenue centres to the new set, but keep any the owner customised.
  const rcStored = Array.isArray(d.revCentres) ? d.revCentres : null;
  const rcIsFactory = rcStored && rcStored.length === OLD_REVCENTRES.length && rcStored.every((r, i) => r.name === OLD_REVCENTRES[i]);
  let revCentres = !rcStored || rcIsFactory ? DEFAULT_REVCENTRES
    : rcStored
        .filter((r) => r.name !== "Tax Exempt") // retired: tax-exemption is now per-product (water), not a centre
        .map((r) => { // backfill sub-categories by name for centres saved before subs existed
          if (Array.isArray(r.subs)) return r;
          const def = DEFAULT_REVCENTRES.find((x) => x.name === r.name);
          return { ...r, subs: def ? def.subs : [] };
        });
  // Ensure a Water part exists so water can be sold tax-free at the till.
  let parts = Array.isArray(d.parts) ? d.parts : DEFAULT_PARTS;
  if (!parts.some((p) => p.noTax)) {
    parts = [...parts, { id: cid(), code: "WATER", name: "Water — bulk fill (no tax)", price: "0.00", cost: "0.00", qty: "0", lowAt: "", category: "Water", noTax: true, favorite: true, active: true }];
  }
  // Ensure the standard retail quick-key items exist (added once, matched by code).
  QUICK_KEYS.forEach((qk) => {
    if (!parts.some((p) => (p.code || "").trim().toLowerCase() === qk.code.toLowerCase())) {
      parts = [...parts, quickKeyPart(qk)];
    }
  });
  // Retire the old demo favourites so the quick-keys tab shows the real counter items.
  parts = parts.map((p) => (["CL-TAB-3", "SHK-CAL"].includes((p.code || "").toUpperCase()) ? { ...p, favorite: false } : p));
  const stStored = Array.isArray(d.serviceTypes) ? d.serviceTypes : null;
  const stIsFactory = stStored && stStored.length === OLD_SERVICETYPES.length && stStored.every((s, i) => s.name === OLD_SERVICETYPES[i]);
  const serviceTypes = !stStored || stIsFactory ? DEFAULT_SERVICETYPES : stStored;
  return {
    recent: Array.isArray(d.recent) ? d.recent.slice(0, 8) : [],
    techs: Array.isArray(d.techs) && d.techs.length ? d.techs : DEFAULT_TECHS,
    staff: Array.isArray(d.staff) && d.staff.length ? d.staff : DEFAULT_STAFF,
    punches: Array.isArray(d.punches) ? d.punches.slice(-600) : [],
    messages: Array.isArray(d.messages) ? d.messages.slice(-400) : [],
    timeOff: Array.isArray(d.timeOff) ? d.timeOff.slice(-300) : [],
    jobs: Array.isArray(d.jobs) ? d.jobs : [],
    schedule: { ...defSchedule(), ...(d.schedule || {}) },
    roles: Array.isArray(d.roles) && d.roles.length ? d.roles : DEFAULT_ROLES,
    currentRole: d.currentRole || "owner",
    levels: ladderDone
      ? (Array.isArray(d.levels) && d.levels.length ? d.levels : DEFAULT_LEVELS).map((l) => (l && l.name === "Pushy" ? { ...l, name: "Regular account" } : l))
      : DEFAULT_LEVELS,
    parts,
    services: Array.isArray(d.services) ? d.services : DEFAULT_SERVICES,
    terms: mergeDefaultTerms(Array.isArray(d.terms) ? d.terms : DEFAULT_TERMS),
    revCentres,
    serviceTypes,
    numbering: { ...defNumbering(), ...(d.numbering || {}) },
    posSales: Array.isArray(d.posSales) ? d.posSales.slice(-2000) : [],
    posHeld: Array.isArray(d.posHeld) ? d.posHeld : [],
    specialOrders: Array.isArray(d.specialOrders) ? d.specialOrders : [],
    purchaseOrders: Array.isArray(d.purchaseOrders) ? d.purchaseOrders : [],
    // Every collection the storage adapter syncs MUST be carried through here —
    // a field missing from this return is dropped on load, and the next save
    // then deletes its server rows. (Bit us 08/06: repairs + followups.)
    repairs: (Array.isArray(d.repairs) ? d.repairs : []).map((t) =>
      (t && t.stage === "Assigned to technician" && !t.techId ? { ...t, stage: "In queue — awaiting diagnosis" } : t)),
    followups: Array.isArray(d.followups) ? d.followups : [],
    jobTypes: Array.isArray(d.jobTypes) && d.jobTypes.length ? d.jobTypes : DEFAULT_JOB_TYPES,
    rentalUnits: Array.isArray(d.rentalUnits) ? d.rentalUnits : [],
    tools: Array.isArray(d.tools) ? d.tools : [],
    campaigns: Array.isArray(d.campaigns) ? d.campaigns : [],
    // Company vehicles/trailers (prompt #12). Singleton in app_settings — not rentalUnits.
    fleetUnits: Array.isArray(d.fleetUnits) ? d.fleetUnits : [],
    businessProfile: { ...(d.businessProfile || {}), discountLadder: DISCOUNT_LADDER },
    assistantSettings: d.assistantSettings || {},
    supplierInfo: d.supplierInfo || {},
    clients: d.clients.map((c) => {
      const out = { ...c };
      if (!Array.isArray(out.contacts)) {
        out.contacts = [{ id: cid(), name: c.contact || "", role: "Primary", phone: c.phone || "", email: c.email || "" }];
      }
      out.contacts = out.contacts.map((k, i) => {
        const base = { ...k, comms: k.comms || defComms(i === 0) };
        if (!base.firstName && String(base.name || "").trim()) {
          const w = String(base.name).trim().split(/\s+/);
          base.firstName = w[0]; base.lastName = w.slice(1).join(" ");
        }
        return base;
      });
      delete out.contact; delete out.phone; delete out.email; delete out.tech;
      if (typeof out.complexity !== "number") out.complexity = 5;
      if (!out.complexityMode) out.complexityMode = "manual";
      out.flag = { ...defFlag(), ...(out.flag || {}) };
      out.standing = { ...defStanding(), ...(out.standing || {}) };
      if (typeof out.trade !== "boolean") out.trade = false;
      out.techId = out.techId || "";
      if (typeof out.waterTesting !== "boolean") out.waterTesting = false;
      if (!Array.isArray(out.days)) out.days = out.day ? [out.day] : [];
      if (!out.frequency) out.frequency = "weekly";
      out.shortTermUntil = out.shortTermUntil || "";
      out.anchorDate = out.anchorDate || "";
      out.monthlyMode = out.monthlyMode || "nth";
      out.monthlyNth = out.monthlyNth || 1;
      out.monthlyDate = out.monthlyDate || 1;
      delete out.day;
      out.lastWaterTest = out.lastWaterTest || "";
      out.lastRetail = out.lastRetail || "";
      if (!out.structure) out.structure = "pool";
      if (typeof out.spaSeparate !== "boolean") out.spaSeparate = false;
      out.spaGallons = out.spaGallons || ""; out.spaNotes = out.spaNotes || "";
      if (!out.coverType) out.coverType = "none";
      if (typeof out.coverLeafNet !== "boolean") out.coverLeafNet = false;
      if (!out.features) out.features = defFeatures();
      out.billing = { ...defBilling(), ...(out.billing || {}),
        card: { ...defBilling().card, ...((out.billing || {}).card || {}) },
        discounts: { ...defBilling().discounts, ...((out.billing || {}).discounts || {}) } };
      const ob = out.billing;
      if (ob.tierId || ob.discounts.chemicals !== undefined || ob.discounts.materials !== undefined) {
        const acct = Number(ob.discounts.account) || 0;
        ob.discounts = {
          labour: Math.max(acct, Number(ob.discounts.labour) || 0),
          chemical: Math.max(acct, Number(ob.discounts.chemicals ?? ob.discounts.chemical) || 0),
          material: Math.max(acct, Number(ob.discounts.materials ?? ob.discounts.material) || 0),
        };
        ob.discountMode = "custom";
        delete ob.tierId;
      }
      if (!ladderDone) {
        out.billing = { ...out.billing, discountMode: "level", levelId: "pushy", discounts: { labour: 0, chemical: 0, material: 0 } };
      }
      if (typeof out.billingSame !== "boolean") out.billingSame = true;
      out.billingAddress = out.billingAddress || "";
      if (c.equipment && !out.pump) out.pump = c.equipment;
      delete out.equipment;
      if (!out.sanitizerType) out.sanitizerType = c.sanitizer || "";
      delete out.sanitizer;
      if (!Array.isArray(out.equipmentList)) {
        const legacy = { pump: out.pump, filter: out.filter, heater: out.heater,
          chlorinator: out.chlorinator || out.saltOrChlorinator, alt: out.altSanitizer };
        out.equipmentList = CORE_EQUIP.map((k) => ({ id: cid(), kind: k, detail: legacy[k] || "", timerOn: "", timerOff: "", timerNote: "" }));
      } else {
        out.equipmentList = out.equipmentList.map((e) => ({
          ...e, timerOn: e.timerOn || "", timerOff: e.timerOff || "", timerNote: e.timerNote || "",
        }));
      }
      if (!out.saltSystemType) out.saltSystemType = "";
      if (!Array.isArray(out.complaints)) out.complaints = [];
      delete out.pump; delete out.filter; delete out.heater;
      delete out.chlorinator; delete out.altSanitizer; delete out.saltOrChlorinator;
      return out;
    }),
  };
}

function blank() {
  return {
    id: cid(), name: "", address: "", billingSame: true, billingAddress: "",
    type: "service", days: ["Mon"], frequency: "weekly", shortTermUntil: "",
    anchorDate: "", monthlyMode: "nth", monthlyNth: 1, monthlyDate: 1, order: 99, complexity: 5, complexityMode: "auto",
    contacts: [{ id: cid(), name: "", role: "Owner", phone: "", email: "", comms: defComms(true) }],
    flag: defFlag(), standing: defStanding(), trade: false, giftedService: false, techId: "",
    waterTesting: false, lastWaterTest: "", lastRetail: "",
    structure: "pool", spaSeparate: false, spaGallons: "", spaNotes: "",
    coverType: "safety", coverLeafNet: false,
    surface: "", gallons: "", features: defFeatures(), billing: defBilling(),
    sanitizerType: "", saltSystemType: "",
    complaints: [],
    equipmentList: CORE_EQUIP.map((k) => ({ id: cid(), kind: k, detail: "", timerOn: "", timerOff: "", timerNote: "" })),
    gateCode: "", dog: false, keyLocation: "", accessNotes: "", notes: "", lastService: "",
  };
}

const cxBand = (n) => (n <= 3 ? "low" : n <= 7 ? "mid" : "high");
const cxWord = { low: "Routine", mid: "Moderate", high: "Complex" };
const CX_SCALE = [
  { k: "easy", word: "Easy", colour: "#12A650" },
  { k: "routine", word: "Routine", colour: "#9ACD32" },
  { k: "moderate", word: "Moderate", colour: "#FFC400" },
  { k: "heavy", word: "Heavy", colour: "#F5761A" },
  { k: "complex", word: "Complex", colour: "#E01B1B" },
];
const CX_STOPS = CX_SCALE.map((x) => x.colour);
const cxStep = (n) => CX_SCALE[Math.max(0, Math.min(4, Math.ceil(Number(n) / 2) - 1))];

function shrink(file, maxEdge = 1400, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode"));
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const cv = document.createElement("canvas");
        cv.width = w; cv.height = h;
        cv.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(cv.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const CSS = `
.pd, .pd * { box-sizing: border-box; }
/* Owner 08/03: everything 8% larger. Zoom MUST sit on html, not .pd —
   zoom on .pd creates a containing block the size of the page, so
   position:sticky on the schedule holding bin never pins while staff
   scroll a long truck board. html is the window scrollport, so sticky works. */
html { zoom: 1.08; }
.pd {
  --ink: #17356E;        /* Pool Master navy */
  --brand: #22509E;      /* logo blue */
  --plaster: #F2F7FC;    /* page white-blue */
  --card: #FFFFFF;       /* white */
  --tile: #0887C8;       /* logo mid blue */
  --tile-lit: #1AA2DC;   /* logo bright blue */
  --sky: #65C5EF;        /* logo light blue */
  --sun: #DC1515;        /* logo red */
  --clay: #DC1515;       /* logo red */
  --line: #D8E4F1; --muted: #5A6E86;
  font-family: ui-sans-serif, system-ui, "Segoe UI", Roboto, sans-serif;
  color: var(--ink); background: var(--plaster); min-height: 100vh; padding: 18px;
}
.pd .mono { font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace; }
.pd .brandbar { display:flex; align-items:center; gap:17px; }
.pd .brandbar > div { border-left:1px solid var(--line); padding-left:17px; }
.pd .brandbar h1 { font-size:23px; }
.pd .brandbar .sub { font-size:13px; }
@media (max-width: 560px) { .pd .brandbar { gap:10px; } .pd .brandbar > div { border-left:0; padding-left:0; } .pd .brandbar svg { height:30px; } }
.pd h1 { font-size: 19px; text-transform:uppercase; letter-spacing:1.4px; color:var(--brand); letter-spacing: -0.4px; margin: 0; font-weight: 700; }
.pd .sub { color: var(--muted); font-size: 12px; margin-top: 2px; letter-spacing: 0.3px; }
.pd .topbar { display:flex; align-items:flex-end; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
.pd .tabs { display:flex; gap:2px; background: var(--card); border:1px solid var(--line); border-radius:8px; padding:3px; }
.pd .tabs button { border:0; background:transparent; padding:7px 14px; border-radius:6px; cursor:pointer; font: inherit; font-size:13px; font-weight:600; color: var(--muted); }
.pd .tabs button[data-on="1"] { background: var(--ink); color:#fff; }
.pd[data-nav="right"] { padding-right:262px; }
.pd[data-nav="right"] .mainnav { position:fixed; top:18px; right:18px; bottom:18px; width:228px; flex-direction:column; align-items:stretch; overflow-y:auto; z-index:60; }
.pd[data-nav="right"] .mainnav button { justify-content:flex-start; margin-left:0 !important; }
@media (max-width: 980px) {
  .pd[data-nav="right"] { padding-right:18px; }
  .pd[data-nav="right"] .mainnav { position:static; width:auto; flex-direction:row; align-items:center; bottom:auto; }
}
.pd .mainnav { display:flex; gap:3px; background:var(--card); border:1px solid var(--line); border-radius:10px; padding:4px; margin-bottom:14px; flex-wrap:wrap; }
.pd .mainnav button { display:inline-flex; align-items:center; gap:7px; border:0; background:transparent; padding:9px 18px; border-radius:7px; cursor:pointer; font:inherit; font-size:13.5px; font-weight:650; color:var(--muted); white-space:nowrap; transition: background .12s ease, color .12s ease; }
.pd .mainnav button:hover { color:var(--ink); }
.pd .mainnav button[data-on="1"] { background:var(--ink); color:#fff; }
.pd .phonemenu-btn { display:none; }
.pd .phonenav-head { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 16px; border-bottom:1px solid var(--line); flex:none; }
.pd .phonenav-head h2 { margin:0; font-size:16px; font-weight:800; letter-spacing:.2px; }
.pd .phonenav { display:flex; flex-direction:column; gap:2px; padding:10px 10px 24px; }
.pd .phonenav button { display:flex; align-items:center; gap:10px; width:100%; border:0; background:transparent; padding:12px 14px; border-radius:8px; cursor:pointer; font:inherit; font-size:15px; font-weight:650; color:var(--muted); text-align:left; }
.pd .phonenav button:hover { color:var(--ink); background:var(--card); }
.pd .phonenav button[data-on="1"] { background:var(--ink); color:#fff; }
@media (max-width: 820px) {
  .pd .mainnav { display:none !important; }
  .pd .phonemenu-btn { display:inline-flex; align-items:center; gap:6px; font-weight:700; }
  .pd .navflip { display:none !important; }
}
.pd .posgrid { display:grid; grid-template-columns: minmax(230px, 1fr) minmax(320px, 1.35fr); gap:14px; align-items:start; }
@media (max-width: 820px) { .pd .posgrid { grid-template-columns:1fr; } }
.pd .posproducts { border:1px solid var(--line); border-radius:10px; padding:10px; background:#fff; }
.pd .poscats { display:flex; flex-wrap:wrap; gap:6px; margin:9px 0; }
.pd .poschip { border:1px solid var(--line); background:#fff; color:var(--muted); border-radius:999px; padding:5px 11px; font:inherit; font-size:12px; font-weight:650; cursor:pointer; }
.pd .poschip[data-on="1"] { background:var(--ink); color:#fff; border-color:var(--ink); }
.pd .postiles { display:grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap:10px; }
.pd .postile { position:relative; display:flex; flex-direction:column; gap:7px; text-align:left; border:1px solid var(--line); border-left:5px solid var(--line); border-radius:11px; background:#fff; padding:11px 12px 12px; cursor:pointer; min-height:96px; font:inherit; transition: box-shadow .12s ease, transform .04s ease; }
.pd .postile:hover { box-shadow:0 5px 16px rgba(16,32,55,.14); }
.pd .postile:active { transform: translateY(1px); }
.pd .postile[data-out="1"] { opacity:.6; }
.pd .postile .pt-img { width:100%; height:84px; object-fit:cover; border-radius:8px; background:var(--card); }
.pd .postile .pt-cat { font-size:9.5px; font-weight:800; letter-spacing:.7px; text-transform:uppercase; }
.pd .postile .pt-name { font-size:13.5px; font-weight:700; color:var(--ink); line-height:1.28; flex:1; }
.pd .postile .pt-foot { display:flex; justify-content:space-between; align-items:center; gap:8px; }
.pd .postile .pt-price { font-weight:800; font-size:13px; color:var(--ink); background:var(--card); border-radius:999px; padding:3px 11px; white-space:nowrap; }
.pd .postile .pt-stk { font-size:11px; color:var(--muted); }
.pd .postile .pt-out { font-size:11px; color:var(--clay); font-weight:800; }
.pd .poscart { min-width:0; }
.pd .posproducts2 { border:1px solid var(--line); border-radius:12px; padding:12px; background:#fff; }
.pd .posprodhead { display:flex; align-items:flex-start; gap:12px; flex-wrap:wrap; margin-bottom:10px; }
.pd .posprodhead .poscats { margin:0; flex:1; }
.pd .posprodsearch { position:relative; width:min(280px,100%); }
.pd .posprodsearch input { font-size:12.5px; padding:7px 10px; }
.pd .poscheckout { position:sticky; bottom:0; z-index:6; margin-top:14px; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; background:#fff; border:1px solid var(--line); border-radius:13px; padding:11px 12px; box-shadow:0 -6px 20px rgba(16,32,55,.10); }
.pd .poscheckout-cart { display:flex; align-items:center; gap:11px; border:0; background:transparent; cursor:pointer; font:inherit; text-align:left; padding:4px; border-radius:9px; min-width:0; }
.pd .poscheckout-cart:disabled { cursor:default; opacity:.85; }
.pd .posbag { flex:none; width:34px; height:34px; border-radius:9px; background:var(--ink); color:#fff; font-weight:800; font-size:14px; display:flex; align-items:center; justify-content:center; }
.pd .poscheckout-lbl { display:flex; flex-direction:column; line-height:1.3; font-weight:700; font-size:13.5px; color:var(--ink); min-width:0; }
.pd .poscheckout-lbl em { font-style:normal; font-weight:500; font-size:11.5px; color:var(--muted); }
.pd .poscheckout-pay { display:flex; align-items:center; gap:14px; }
.pd .poscheckout-total { text-align:right; line-height:1.15; }
.pd .poscheckout-total span { display:block; font-size:10.5px; text-transform:uppercase; letter-spacing:.5px; font-weight:700; color:var(--muted); }
.pd .poscheckout-total b { font-size:20px; }
.pd .poscheckout-pay .btn { font-size:15px; padding:12px 20px; }
@media (max-width:620px){ .pd .poscheckout-cart { flex:1; } .pd .poscheckout-pay { flex:1; justify-content:space-between; } }
.pd .posdrawer-wrap { position:fixed; inset:0; z-index:70; background:rgba(16,32,55,.36); display:flex; justify-content:flex-end; animation:posfade .12s ease; }
.pd .posdrawer { width:min(468px,100%); height:100%; background:#fff; box-shadow:-10px 0 34px rgba(16,32,55,.24); display:flex; flex-direction:column; overflow-y:auto; }
@keyframes posfade { from{opacity:0} to{opacity:1} }
.pd .possplit { display:grid; grid-template-columns: 30fr 70fr; gap:16px; align-items:start; }
@media (max-width: 860px){ .pd .possplit { grid-template-columns:1fr; } }
.pd .poscartcol { position:sticky; top:12px; border:1px solid var(--line); border-radius:14px; background:#fff; padding:16px; max-height:calc(100vh - 24px); overflow-y:auto; box-shadow:0 2px 12px rgba(16,32,55,.06); }
@media (max-width: 860px){ .pd .poscartcol { position:static; max-height:none; } }
.pd .poscartline { border:1px solid var(--line); border-radius:10px; padding:11px 12px; margin-bottom:9px; background:#fff; }
.pd .poscartline .pcl-top { display:grid; grid-template-columns: 130px 1fr 30px; gap:9px; align-items:center; }
.pd .poscartline .pcl-top input:nth-child(2) { font-size:14.5px; font-weight:600; }
.pd .poscartline .pcl-bot { display:grid; grid-template-columns: 1fr 1fr 1fr 52px; gap:9px; align-items:end; margin-top:10px; }
.pd .poscartline .pcl-bot label { font-size:10px; color:var(--muted); font-weight:800; text-transform:uppercase; letter-spacing:.5px; display:flex; flex-direction:column; gap:4px; margin:0; }
.pd .poscartline input, .pd .poscartline select { padding:9px 10px; font-size:14px; }
.pd .poscartline .pcl-tot { text-align:right; font-weight:800; font-size:16px; margin-top:9px; }
.pd .posstart { display:grid; grid-template-columns: 1fr 1fr; gap:18px; max-width:800px; margin:6vh auto 0; }
@media (max-width:640px){ .pd .posstart { grid-template-columns:1fr; margin-top:3vh; } }
.pd .posstart-btn { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; text-align:center; min-height:220px; border:1px solid var(--line); border-radius:16px; background:#fff; cursor:pointer; font:inherit; padding:30px 24px; transition: box-shadow .12s ease, transform .05s ease, border-color .12s; }
.pd .posstart-btn:hover { box-shadow:0 8px 26px rgba(16,32,55,.14); border-color:var(--tile); }
.pd .posstart-btn:active { transform:translateY(1px); }
.pd .posstart-btn svg { color:var(--brand); }
.pd .posstart-lbl { font-size:23px; font-weight:800; color:var(--ink); }
.pd .posstart-sub { font-size:13.5px; color:var(--muted); max-width:240px; }
.pd .poscust { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:12px; }
.pd .poscust .poscust-btn { flex:1; min-width:150px; font-size:15px; font-weight:750; padding:13px 16px; justify-content:center; }
.pd .poscust .btn.ghost[data-on="1"] { background:var(--ink); color:#fff; border-color:var(--ink); }
.pd .poslookup { border:1px solid var(--line); border-radius:8px; margin-top:6px; overflow:hidden; max-height:236px; overflow-y:auto; background:#fff; box-shadow:0 4px 12px rgba(16,32,55,.1); }
.pd .poslookupitem { display:block; width:100%; text-align:left; border:0; border-bottom:1px solid var(--line); background:#fff; padding:8px 11px; cursor:pointer; font:inherit; }
.pd .poslookupitem:last-child { border-bottom:0; }
.pd .poslookupitem:hover { background:var(--card); }
.pd .poslookupitem b { font-size:13px; color:var(--ink); display:block; }
.pd .posline { display:grid; grid-template-columns: 92px 1fr 46px 74px 52px 44px 66px 26px; gap:5px; margin-bottom:6px; align-items:center; }
.pd .posline input, .pd .posline select { padding:6px 7px; font-size:12.5px; }
.pd .poslinetot { text-align:right; font-weight:700; font-size:12.5px; }
@media (max-width: 620px) { .pd .posline { grid-template-columns: 1fr 1fr 26px; } .pd .poslinetot { text-align:left; } }
.pd .grid { display:grid; grid-template-columns: 340px 1fr; gap:16px; align-items:start; }
.pd .grid[data-solo="1"] { grid-template-columns: 1fr; }
@media (max-width: 820px) { .pd .grid { grid-template-columns: 1fr; } .pd { padding:12px; } }
.pd .mobileback { display:none; }
@media (max-width: 820px) {
  .pd .mobileback { display:inline-flex; margin-bottom:10px; }
  .pd .grid[data-detail="1"] > .panel:first-child { display:none; }
}
.pd .panel { background: var(--card); border:1px solid var(--line); border-radius:10px; }
.pd .pad { padding:14px; }
.pd input, .pd select, .pd textarea { width:100%; font: inherit; font-size:13px; color: var(--ink); border:1px solid var(--line); border-radius:7px; padding:8px 10px; background:#fff; }
.pd textarea { resize: vertical; min-height:58px; line-height:1.45; }
.pd input:focus, .pd select:focus, .pd textarea:focus, .pd button:focus-visible { outline: 2px solid var(--tile-lit); outline-offset: 1px; }
.pd .filters { display:flex; gap:6px; margin-top:8px; }
.pd .filters select { flex:1; font-size:12px; padding:6px 8px; }
.pd .grid > .panel:first-child { position:sticky; top:12px; }
@media (max-width: 820px) { .pd .grid > .panel:first-child { position:static; } }
.pd .iconbtn { position:relative; padding:7px 9px; }
.pd .ackbox { border:1px solid var(--line); border-left:4px solid var(--brand); border-radius:9px; background:var(--plaster); padding:14px 16px; color:var(--ink); }
.pd .ackbox svg { color:var(--brand); margin-bottom:8px; }
.pd .ackbox p { margin:0 0 9px; font-size:13.5px; line-height:1.6; }
.pd .ackbox p:first-of-type { font-weight:750; }
.pd .ackbox p:last-child { margin-bottom:0; }
.pd .tostat { font-size:10px; letter-spacing:0.9px; text-transform:uppercase; font-weight:800; padding:2px 7px; border-radius:5px; }
.pd .tostat[data-s="pending"] { background:#FDF4E0; color:#8A6316; }
.pd .tostat[data-s="approved"] { background:#E6F6EC; color:#0E7A3C; }
.pd .tostat[data-s="declined"] { background:#FDECEC; color:var(--clay); }
.pd .tostat[data-s="cancelled"] { background:#EFF2F6; color:#5A6E86; }
.pd .torow { border-left:4px solid var(--line); }
.pd .statuswrap { margin-top:10px; }
.pd .statusbar { height:7px; border-radius:4px; background:#E7EDF6; overflow:hidden; }
.pd .statusbar span { display:block; height:100%; border-radius:4px; transition:width .3s; }
.pd .statussteps { display:flex; justify-content:space-between; margin-top:5px; font-size:9.5px; letter-spacing:0.7px;
  text-transform:uppercase; font-weight:800; color:#A8B6C8; }
.pd .statussteps span[data-on="1"] { color:var(--muted); }
.pd .statusline { margin-top:4px; font-size:11.5px; font-weight:750; }
.pd .statusline em { font-style:normal; font-weight:600; color:var(--muted); }
.pd .iconbtn .badge { position:absolute; top:-5px; right:-5px; min-width:16px; height:16px; padding:0 4px; border-radius:9px;
  background:var(--clay); color:#fff; font-size:10px; font-weight:800; display:flex; align-items:center; justify-content:center; }
.pd .picklist { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:7px; margin-top:6px; }
.pd .pick { display:flex; flex-direction:column; align-items:flex-start; gap:2px; border:1px solid var(--line); background:#fff;
  border-radius:8px; padding:8px 10px; font:inherit; cursor:pointer; text-align:left; }
.pd .pick[data-on="1"] { border-color:var(--tile); background:#EDF7FD; box-shadow:inset 0 0 0 1px var(--tile); }
.pd .pick .pnm { font-size:13px; font-weight:700; color:var(--ink); }
.pd .pick .prole { font-size:10px; letter-spacing:0.8px; text-transform:uppercase; font-weight:700; color:var(--muted); }
.pd .bstages { display:flex; overflow-x:auto; padding:4px 0 2px; }
.pd .bstage { flex:1 1 0; min-width:76px; background:none; border:0; padding:0; cursor:pointer; font:inherit;
  display:flex; flex-direction:column; align-items:center; gap:4px; }
.pd .bstage .bdot { position:relative; width:100%; height:22px; display:flex; align-items:center; justify-content:center; }
.pd .bstage .bdot::before, .pd .bstage .bdot::after { content:""; position:absolute; top:50%; transform:translateY(-50%);
  height:4px; width:50%; background:var(--line); border-radius:2px; }
.pd .bstage .bdot::before { left:0; }
.pd .bstage .bdot::after { right:0; }
.pd .bstage:first-child .bdot::before, .pd .bstage:last-child .bdot::after { display:none; }
.pd .bstage[data-done="1"] .bdot::before, .pd .bstage[data-done="1"] .bdot::after,
.pd .bstage[data-cur="1"] .bdot::before { background:#12A650; }
.pd .bstage .bknob { position:relative; z-index:1; width:18px; height:18px; border-radius:50%; background:#fff;
  border:2px solid var(--line); color:var(--muted); font-size:10px; font-weight:800;
  display:flex; align-items:center; justify-content:center; transition:background .25s, border-color .25s; }
.pd .bstage[data-done="1"] .bknob { background:#12A650; border-color:#12A650; color:#fff; }
.pd .bstage[data-cur="1"] .bknob { width:22px; height:22px; background:var(--tile); border-color:var(--tile); color:#fff; font-size:11px; }
.pd .bstage .blbl { font-size:10px; line-height:1.25; text-align:center; font-weight:600; color:var(--muted); max-width:96px; }
.pd .bstage[data-done="1"] .blbl { color:#0E7A3C; }
.pd .bstage[data-cur="1"] .blbl { color:var(--ink); font-weight:800; }
.pd .msgrow { border:1px solid var(--line); border-radius:9px; padding:10px 12px; margin-bottom:8px; background:#fff; }
.pd .msgrow[data-unread="1"] { border-color:var(--tile); background:#EDF7FD; cursor:pointer; }
.pd .msgrow .mtop { display:flex; justify-content:space-between; gap:10px; align-items:baseline; }
.pd .msgrow .mbody { margin-top:5px; font-size:13px; line-height:1.5; color:var(--ink); white-space:pre-wrap; }
.pd .newtag { display:inline-block; margin-top:7px; font-size:10px; letter-spacing:0.8px; text-transform:uppercase; font-weight:800; color:var(--tile); }
.pd .keypad { max-width:280px; margin:0 auto; }
.pd .pindots { display:flex; justify-content:center; gap:10px; margin:14px 0 16px; }
.pd .pindots span { width:13px; height:13px; border-radius:50%; border:2px solid var(--line); background:#fff; }
.pd .pindots span[data-on="1"] { background:var(--brand); border-color:var(--brand); }
.pd .keys { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; }
.pd .keys button { font:inherit; font-size:19px; font-weight:700; color:var(--ink); background:#fff; border:1px solid var(--line);
  border-radius:10px; padding:14px 0; cursor:pointer; }
.pd .keys button:hover { border-color:var(--tile); background:#EDF7FD; }
.pd .keys button.alt { font-size:16px; color:var(--muted); }
.pd .keys button.go { background:var(--ink); border-color:var(--ink); color:#fff; font-size:15px; }
.pd .keys button.go:disabled { opacity:0.35; cursor:not-allowed; }
.pd .punchbtn { display:flex; align-items:center; justify-content:center; gap:12px; width:100%; margin:16px 0 12px;
  padding:26px 18px; border:0; border-radius:14px; background:#12A650; color:#fff; font:inherit; font-size:22px; font-weight:800;
  letter-spacing:0.5px; cursor:pointer; box-shadow:0 3px 0 rgba(0,0,0,0.14); }
.pd .punchbtn.out { background:var(--clay); }
.pd .punchbtn:active { transform:translateY(2px); box-shadow:0 1px 0 rgba(0,0,0,0.14); }
.pd .punchbtn .pb-ic { font-size:26px; line-height:1; }
.pd .punchdone { text-align:center; padding:14px 0 4px; }
.pd .punchdone .big { font-size:24px; font-weight:800; color:#12A650; }
.pd .punchdone[data-dir="out"] .big { color:var(--clay); }
.pd .punchdone .who4 { font-weight:700; color:var(--ink); margin-top:4px; }
.pd .punchdone .stamp { font-family: ui-monospace, Menlo, monospace; font-size:13px; color:var(--muted); margin-top:3px; }
.pd .whobar { display:inline-flex; flex-direction:column; line-height:1.25; margin-right:4px; }
.pd .whobar b { font-size:13px; color:var(--ink); }
.pd .whobar em { font-style:normal; font-size:10.5px; letter-spacing:0.8px; text-transform:uppercase; color:var(--muted); font-weight:700; }
.pd .onclock { display:inline-block; padding:2px 7px; border-radius:5px; background:#E6F6EC; color:#0E7A3C; font-size:11px; font-weight:700; }
.pd .commrow { display:flex; gap:6px; margin-top:6px; }
.pd .commbtn { display:inline-flex; align-items:center; gap:5px; border:1px solid var(--line); background:#fff; border-radius:7px;
  padding:5px 9px; font:inherit; font-size:11px; font-weight:700; color:var(--tile); cursor:pointer; text-decoration:none; }
.pd .commbtn:hover { border-color:var(--tile); background:#EDF7FD; }
.pd .commbtn svg { color:var(--tile); }
.pd .listhead { padding:9px 13px 6px; font-size:10px; letter-spacing:1.3px; text-transform:uppercase; font-weight:800;
  color:var(--muted); background:var(--plaster); border-bottom:1px solid var(--line); position:sticky; top:0; z-index:1; }
.pd .clientrow { width:100%; text-align:left; border:0; border-top:1px solid var(--line); background:transparent; padding:11px 14px; cursor:pointer; font: inherit; display:block; }
.pd .clientrow:hover { background:#F5FAFD; }
.pd .clientrow[data-on="1"] { background:#E4F2FC; box-shadow: inset 3px 0 0 var(--tile); }
.pd .cr-name { font-weight:650; font-size:14px; letter-spacing:-0.2px; }
.pd .cr-meta { font-size:11.5px; color: var(--muted); margin-top:3px; display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.pd .pill { display:inline-block; font-size:10px; font-weight:700; letter-spacing:0.6px; text-transform:uppercase; padding:2px 7px; border-radius:20px; border:1px solid var(--line); color: var(--muted); background:#fff; }
.pd .pill[data-t="both"] { color:#0E7A3C; border-color:#CDEBD8; background:#EFF9F3; }
.pd .pill[data-t="service"] { color:#086EB6; border-color:#B9E0F5; background:#EAF4FC; }
.pd .pill[data-t="retail"] { color:#3D4E63; border-color:#D8E4F1; background:#F4F9FC; }
.pd .cx { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:700; }
.pd .cx b { font-family: ui-monospace, Menlo, monospace; font-size:12px; }
.pd .cx[data-b="low"] { color:#086EB6; } .pd .cx[data-b="mid"] { color:#B31212; } .pd .cx[data-b="high"] { color:#B31212; }
.pd .cx[data-dark="1"] { color:#EFF7FD; }
.pd .cx.rating[data-dark="1"] b, .pd .cx.rating[data-dark="1"] span { filter: brightness(1.35) saturate(1.1); }
.pd .gatecard .cxblock { margin-left:auto; text-align:right; }
.pd .gatecard .cxblock .cx { justify-content:flex-end; margin-top:4px; }
.pd .gatecard { background: var(--ink); color:#fff; border-radius:10px; padding:13px 15px; margin-bottom:13px; }
.pd .gatecard .lbl { font-size:10px; letter-spacing:1.4px; text-transform:uppercase; color:#8FCFF0; }
.pd .gatecode { font-size:25px; font-weight:700; letter-spacing:1.5px; line-height:1.15; margin-top:2px; }
.pd .gaterow { display:flex; gap:20px; flex-wrap:wrap; align-items:flex-start; }
.pd .meterwrap { margin-left:auto; display:flex; gap:20px; }
.pd .meterwrap .mcell { text-align:center; background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.16);
  border-radius:9px; padding:8px 12px 9px; min-width:104px; }
.pd .meterwrap .cx { justify-content:center; margin-top:3px; font-size:10.5px; }
.pd .flag { display:inline-flex; align-items:center; gap:6px; background:#3A0D0D; color:var(--sun); border:1px solid #7A1414; border-radius:6px; padding:5px 9px; font-size:12px; font-weight:650; }
.pd .gatenote { color:#DAEFFB; font-size:13px; font-weight:700; line-height:1.5; margin-top:10px; border-top:1px solid #2A4E92; padding-top:9px; }
.pd .detailroot[data-stop="1"] { border:3px solid #DC1515; border-radius:13px; padding:11px; margin:2px; animation: stopflash 1.3s ease-in-out infinite; }
@keyframes stopflash {
  0%, 100% { border-color:#DC1515; box-shadow:0 0 0 0 rgba(220,21,21,.5); }
  50% { border-color:#FF8A80; box-shadow:0 0 0 7px rgba(220,21,21,0); }
}
@media (prefers-reduced-motion: reduce) { .pd .detailroot[data-stop="1"] { animation:none; box-shadow:0 0 0 3px rgba(220,21,21,.3); } }
.pd .bannerflag { background:rgba(220,21,21,.22); border:1px solid rgba(255,120,110,.45); border-radius:8px; padding:8px 11px; color:#FFE1DF; border-top:1px solid rgba(255,120,110,.45); }
.pd .bannerflag[data-stop="1"] { background:rgba(220,21,21,.4); }
.pd .doc.unpaidflash { background:#B31212; animation: unpaidblink 1.1s ease-in-out infinite; }
@keyframes unpaidblink { 50% { opacity:.3; } }
@media (prefers-reduced-motion: reduce) { .pd .doc.unpaidflash { animation:none; } }
.pd .bf-scope { display:inline-block; font-size:9.5px; letter-spacing:.8px; text-transform:uppercase; font-weight:800; background:#DC1515; color:#fff; border-radius:5px; padding:2px 7px; margin-right:8px; vertical-align:1px; }
.pd .sechead { font-size:10.5px; letter-spacing:1.4px; text-transform:uppercase; color:var(--muted); font-weight:700; margin:13px 0 7px; padding-bottom:5px; border-bottom:1px solid var(--line); display:flex; justify-content:space-between; align-items:center; gap:8px; }
.pd .sechead:first-child { margin-top:0; }
.pd details.rfold { margin: 13px 0 4px; }
.pd details.rfold > summary { cursor: pointer; font-size: 10.5px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--muted); font-weight: 700;
  padding-bottom: 5px; border-bottom: 1px solid var(--line); display: flex; justify-content: space-between; align-items: center; gap: 8px; list-style: none; }
.pd details.rfold > summary::-webkit-details-marker { display: none; }
.pd details.rfold > summary::before { content: "▸ "; letter-spacing: 0; }
.pd details.rfold[open] > summary::before { content: "▾ "; }
.pd .fields { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
@media (max-width: 560px) { .pd .fields { grid-template-columns:1fr; } }
.pd .f-wide { grid-column: 1 / -1; }
.pd label { display:block; font-size:11px; font-weight:650; color:var(--muted); margin-bottom:4px; letter-spacing:0.2px; }
.pd .kv { font-size:13.5px; line-height:1.5; }
@keyframes notepulse {
  0%, 100% { color: var(--clay); }
  50% { color: #7A1414; opacity: 0.55; }
}
.pd .lrow .paypick { width:auto; min-width:132px; padding:5px 8px; font-size:12.5px; font-weight:650; text-align:right;
  border:1px solid var(--line); border-radius:7px; background:#fff; color:var(--ink); }
.pd .lrow .paypick:hover { border-color:var(--tile); }
.pd .stoptag { display:inline-block; margin-left:6px; padding:1px 6px; border-radius:4px; background:var(--clay); color:#fff; font-size:9px; letter-spacing:0.8px; text-transform:uppercase; font-weight:800; vertical-align:middle; }
.pd .stopwork[data-on="1"] { border-color:#DC1515; background:#FDECEC; }
.pd .switch.stop { letter-spacing:1.2px; }
.pd .switch.stop[data-on="1"] { background:var(--clay); border-color:var(--clay); color:#fff; font-weight:800; }
.pd .switch.stop[data-on="1"] .dot { background:#fff; }
.pd .flagpop .msg2.stopmsg { font-weight:800; color:var(--clay); text-transform:uppercase; letter-spacing:0.4px; }
.pd .notealert { font-weight: 800; font-size: 14px; color: var(--clay); border-color: #F6C7C7; background: #FDECEC;
  animation: notepulse 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .pd .notealert { animation: none; } }
.pd .kv em { font-style:normal; color:var(--muted); }
.pd .btn { font: inherit; font-size:13px; font-weight:650; padding:8px 14px; border-radius:8px; cursor:pointer; border:1px solid var(--ink); background:var(--ink); color:#fff; transition: background .12s ease, border-color .12s ease, box-shadow .12s ease, transform .04s ease; }
.pd .btn.ghost { background:#fff; color:var(--ink); border-color:var(--line); }
.pd .btn.warn { background:#fff; color:var(--clay); border-color:#F6C7C7; }
.pd .btn.mini { font-size:11.5px; padding:4px 9px; border-radius:6px; }
.pd .btn.icon { display:inline-flex; align-items:center; gap:7px; }
.pd .btn:hover { box-shadow:0 2px 7px rgba(16,32,55,.16); }
.pd .btn.ghost:hover { background:var(--card); border-color:var(--muted); }
.pd .btn:active { transform:translateY(1px); box-shadow:none; }
/* PREVIEW: navy (--ink) solid buttons & accents recoloured to the logo's lighter blue (--tile). Text stays navy for readability. */
.pd .btn:not(.ghost):not(.warn) { background:var(--tile); border-color:var(--tile); }
.pd .btn:not(.ghost):not(.warn):hover { background:var(--tile-lit); border-color:var(--tile-lit); }
.pd .btn:disabled, .pd .btn:disabled:hover, .pd .btn:disabled:active { opacity:0.45; cursor:not-allowed; box-shadow:none; transform:none; }
.pd .mainnav button[data-on="1"],
.pd .tabs button[data-on="1"],
.pd .poschip[data-on="1"],
.pd .posbag,
.pd .keys button.go,
.pd .poscust .btn.ghost[data-on="1"] { background:var(--tile); border-color:var(--tile); color:#fff; }
.pd .actions { display:flex; align-items:center; gap:8px; padding-left:12px; border-left:1px solid var(--line); }
@media (max-width: 720px) { .pd .actions { padding-left:0; border-left:0; } }
.pd .btnrow { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
@keyframes quotepulse { 0%,100% { background:#E88A00; box-shadow:0 0 0 0 rgba(232,138,0,.55); } 50% { background:#FBB43D; box-shadow:0 0 0 7px rgba(232,138,0,0); } }
.pd .quoteflash { display:inline-flex; align-items:center; gap:8px; border:0; cursor:pointer; font:inherit; font-weight:750; font-size:12.5px; color:#3a2600; padding:7px 12px 7px 11px; border-radius:999px; animation:quotepulse 1.25s ease-in-out infinite; }
.pd .quoteflash:hover { filter:brightness(1.05); }
.pd .quoteflash .qcount { display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 5px; border-radius:10px; background:#3a2600; color:#fff; font-size:12px; font-weight:800; }
@media (prefers-reduced-motion: reduce) { .pd .quoteflash { animation:none; background:#E88A00; } }
.pd .glance { display:grid; grid-template-columns:repeat(auto-fit, minmax(108px, 1fr)); gap:7px; margin:11px 0 2px; }
.pd .gcell { border:1px solid var(--line); border-radius:9px; background:#FAFDFF; padding:7px 9px; text-align:center; }
.pd .gcell .gv { font-family:ui-monospace,Menlo,monospace; font-size:14.5px; font-weight:700; color:var(--ink); white-space:nowrap; }
.pd .gcell[data-hot="1"] .gv { color:var(--clay); }
.pd .gcell .gl { font-size:9.5px; letter-spacing:.8px; text-transform:uppercase; color:var(--muted); font-weight:700; margin-top:1px; }
.pd .eqmore { display:block; width:100%; text-align:left; border:1px dashed var(--line); background:#FAFDFF; color:var(--muted); font:inherit; font-size:12.5px; font-weight:650; border-radius:9px; padding:8px 12px; cursor:pointer; }
.pd .eqmore:hover { color:var(--tile); border-color:var(--tile); }
.pd .eqmissing { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.pd .eqmchip { font-size:11.5px; font-weight:650; color:var(--muted); border:1px solid var(--line); border-radius:999px; padding:4px 10px; background:#FAFDFF; }
.pd .flagsticky { position:sticky; top:8px; z-index:40; display:flex; flex-direction:column; gap:8px; margin-bottom:11px; }
.pd .flagsticky .bannerflag { background:#B31212; border-color:#8F0E0E; color:#fff; font-weight:600; box-shadow:0 6px 18px rgba(179,18,18,.28); }
.pd .flagsticky .bannerflag[data-stop="1"] { background:#7E0B0B; }
.pd .flagsticky .bannerflag[data-amber="1"] { background:#B7791F; border-color:#8F5A12; box-shadow:0 6px 18px rgba(183,121,31,.28); }
/* Written flag messages + special orders: soft coral — calmer than the hard billing red. */
.pd .flagsticky .bannerflag[data-tone="soft"] { background:#E97A66; border-color:#CE5B47; box-shadow:0 6px 18px rgba(233,122,102,.3); }
.pd .flagsticky .bannerflag[data-tone="soft"] .bf-scope { background:rgba(255,255,255,.28); }
.pd .flagsticky .bannerflag[data-tone="soft"] .flagact { color:#CE5B47; }
.pd .flagsticky .bf-scope { background:rgba(255,255,255,.22); }
.pd .flagact { border:0; border-radius:7px; background:#fff; color:#B31212; font:inherit; font-size:12px; font-weight:800; padding:5px 11px; cursor:pointer; margin-left:10px; vertical-align:1px; white-space:nowrap; }
.pd .flagact:hover { filter:brightness(.94); }
.pd .bannerflag[data-amber="1"] .flagact { color:#8F5A12; }
.pd .gcell[data-warn="1"] .gv { color:#B7791F; }
.pd .gcell .gv.txt { font-family:inherit; font-size:12px; white-space:normal; line-height:1.3; }
.pd .flagchip { align-self:flex-start; display:inline-flex; align-items:center; gap:7px; border:0; cursor:pointer; font:inherit; font-weight:750; font-size:12.5px; color:#fff; background:var(--clay); padding:8px 14px; border-radius:999px; box-shadow:0 4px 14px rgba(179,18,18,.3); }
.pd .flagchip:hover { filter:brightness(1.07); }
.pd .flagmini { align-self:flex-end; border:0; background:transparent; cursor:pointer; font:inherit; font-size:12px; font-weight:700; color:var(--clay); text-decoration:underline; text-underline-offset:3px; padding:2px 4px; }
@keyframes livepulse { 0%,100% { box-shadow:0 0 0 0 rgba(34,197,94,.55); } 50% { box-shadow:0 0 0 6px rgba(34,197,94,0); } }
.pd .livedot { width:10px; height:10px; min-width:10px; border-radius:50%; background:#22C55E; display:inline-block; animation:livepulse 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .pd .livedot { animation:none; box-shadow:0 0 0 3px rgba(34,197,94,.35); } }
@keyframes addcustpulse { 0%,100% { box-shadow:0 0 6px 1px rgba(8,135,200,.18); } 50% { box-shadow:0 0 18px 5px rgba(8,135,200,.5); } }
.pd .addpulse { animation:addcustpulse 3s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .pd .addpulse { animation:none; box-shadow:0 0 0 3px rgba(8,135,200,.45); } }
.pd .link { border:0; background:none; padding:0; font:inherit; font-size:13.5px; color:var(--tile); font-weight:700; cursor:pointer; text-decoration:underline; text-underline-offset:3px; font-family: ui-monospace, Menlo, monospace; }
.pd .contact { border:1px solid var(--line); border-radius:11px; padding:13px 14px; margin-bottom:10px; background:#fff; }
.pd .contact.primary { border-color:#B9E0F5; background:#F5FAFE; box-shadow:0 1px 5px rgba(16,32,55,.05); }
.pd .ctop { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; gap:8px; }
.pd .ctag { font-size:10px; letter-spacing:1px; text-transform:uppercase; font-weight:700; color:var(--tile); }
.pd .chead { display:flex; align-items:center; gap:11px; margin-bottom:6px; }
.pd .cavatar { width:36px; height:36px; border-radius:10px; flex:none; display:grid; place-items:center;
  background:linear-gradient(150deg,var(--ink),var(--tile)); color:#fff; font-weight:800; font-size:12.5px; letter-spacing:.5px; }
.pd .cname { font-size:14.5px; font-weight:750; color:var(--ink); line-height:1.2; }
.pd .detail-name { font-size:28px; font-weight:850; letter-spacing:-0.45px; line-height:1.12; color:var(--ink); }
.pd .crole { font-size:11.5px; color:var(--muted); font-weight:600; margin-top:1px; }
.pd .cbadge { flex:none; font-size:9.5px; letter-spacing:.8px; text-transform:uppercase; font-weight:800; color:var(--tile);
  background:#EAF6FD; border:1px solid #C7E6F7; border-radius:999px; padding:3px 9px; }
.pd .crow { display:flex; align-items:center; gap:10px; padding:8px 0; }
.pd .crow + .crow { border-top:1px solid var(--line); }
.pd .crow > svg { color:var(--muted); flex:none; }
.pd .crow .cval { flex:1; min-width:0; font-size:13.5px; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd .crow .cacts { display:flex; gap:6px; flex:none; }
/* Catalogue: tabs down the left, scannable part rows on the right */
.pd .catwrap { display:flex; gap:16px; align-items:flex-start; }
.pd .catrail { display:flex; flex-direction:column; gap:3px; flex:0 0 172px; position:sticky; top:4px; }
.pd .catrail button { text-align:left; border:0; background:transparent; padding:10px 13px; border-radius:9px; font:inherit; font-size:13px; font-weight:700; color:var(--muted); cursor:pointer; transition: background .12s ease, color .12s ease; }
.pd .catrail button:hover { color:var(--ink); background:var(--card); }
.pd .catrail button[data-on="1"] { background:var(--ink); color:#fff; }
.pd .catbody { flex:1; min-width:0; }
@media (max-width:700px){
  .pd .catwrap { display:block; }
  .pd .catrail { flex-direction:row; flex-wrap:wrap; position:static; margin-bottom:12px; }
}
/* RB-style workstation: search + record on the left, big grid on the right */
.pd .rbwrap { display:flex; gap:14px; align-items:flex-start; }
.pd .rbleft { flex:0 0 330px; min-width:0; }
.pd .rbright { flex:1; min-width:0; }
.pd .rbbox { background:#fff; border:1px solid var(--line); border-radius:10px; padding:11px 12px; }
.pd .rbrow { display:flex; align-items:center; gap:8px; margin-bottom:7px; }
.pd .rbrow > label:first-child { flex:0 0 92px; font-size:11px; font-weight:700; color:var(--muted); text-align:right; }
.pd .rbrow > input, .pd .rbrow > select { flex:1; min-width:0; }
@media (max-width:920px){ .pd .rbwrap { display:block; } .pd .rbleft { margin-bottom:12px; } }
/* RB-style inventory grid: dense, sortable, colour-coded rows; detail panel below */
.pd .invgridwrap { border:1px solid var(--line); border-radius:10px; background:#fff; overflow:auto; max-height:44vh; }
.pd .invgrid { width:100%; border-collapse:collapse; font-size:12.5px; }
.pd .invgrid th { position:sticky; top:0; z-index:2; background:var(--ink); color:#fff; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.7px; padding:8px 10px; cursor:pointer; white-space:nowrap; user-select:none; }
.pd .invgrid th:hover { background:var(--brand); }
.pd .invgrid td { padding:6px 10px; border-top:1px solid var(--line); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:250px; }
.pd .invgrid tbody tr { cursor:pointer; }
.pd .invgrid tbody tr:hover { background:var(--plaster); }
.pd .invgrid tbody tr[data-s="low"] { background:#FFF7E8; }
.pd .invgrid tbody tr[data-s="out"] { background:#FDEDEB; }
.pd .invgrid tbody tr[data-sel="1"] { background:#D8E9FB; box-shadow: inset 3px 0 0 var(--tile); }
.pd .invgrid tbody tr[data-inactive="1"] td { color:var(--muted); font-style:italic; }
.pd .routing { display:flex; flex-wrap:wrap; gap:6px; margin-top:9px; padding-top:9px; border-top:1px dashed var(--line); }
.pd .commtiles { display:grid; grid-template-columns:repeat(4,1fr); gap:9px; margin-bottom:6px; }
@media (max-width:760px){ .pd .commtiles { grid-template-columns:repeat(2,1fr); } }
@media (max-width:420px){ .pd .commtiles { grid-template-columns:1fr; } }
.pd .commtile { display:flex; flex-direction:column; align-items:center; text-align:center; gap:3px; padding:10px 8px;
  border:1px solid var(--line); border-radius:12px; background:#fff; cursor:pointer; font:inherit; transition:box-shadow .12s ease, border-color .12s ease, transform .04s ease; }
.pd .commtile:hover { border-color:var(--tile); box-shadow:0 4px 12px rgba(16,32,55,.10); }
.pd .commtile:active { transform:translateY(1px); }
.pd .commtile .ct-ic { width:44px; height:44px; border-radius:12px; display:grid; place-items:center; background:var(--card); color:var(--tile); margin-bottom:3px; }
.pd .commtile .ct-ic svg { color:var(--tile); }
.pd .commtile b { font-size:13.5px; color:var(--ink); }
.pd .commtile span { font-size:11px; color:var(--muted); line-height:1.35; }
.pd .commtile .ct-pref { font-size:11.5px; font-weight:800; color:var(--tile); letter-spacing:.2px; }
.pd .commtile .ct-pref[data-set="0"] { color:var(--muted); font-weight:700; }
.pd .commlog-add { display:flex; gap:9px; align-items:stretch; }
.pd .commlog-add textarea { flex:1; }
.pd .commlog-list { margin-top:14px; }
.pd .commlog-entry { display:grid; grid-template-columns:66px 1fr auto; gap:12px; align-items:start; padding:11px 0; border-top:1px solid var(--line); }
.pd .commlog-entry:first-child { border-top:0; }
.pd .cle-when { font-size:11.5px; color:var(--muted); font-weight:700; line-height:1.4; white-space:nowrap; }
.pd .cle-note { font-size:13.5px; color:var(--ink); line-height:1.5; }
.pd .chip { font-size:11px; font-weight:650; border-radius:6px; padding:3px 8px; background:#EAF4FC; color:#086EB6; border:1px solid #BFE2F6; }
.pd .chip[data-ch="text"] { background:#FDECEC; color:#B31212; border-color:#F6C7C7; }
.pd .chip[data-ch="both"] { background:#EAF4FC; color:#086EB6; border-color:#BFE2F6; }
.pd .alert { background:#FDECEC; border:1px solid #F6C7C7; color:#9B1010; border-radius:8px; padding:9px 11px; font-size:12.5px; line-height:1.5; margin-bottom:10px; }
.pd .counts { display:grid; grid-template-columns: repeat(auto-fit, minmax(104px, 1fr)); gap:8px; }
.pd .count-cell { border:1px solid var(--line); border-radius:8px; padding:6px 8px; text-align:center; background:#FAFDFF; }
.pd .count-cell .n { font-size:18px; font-weight:700; font-family: ui-monospace, Menlo, monospace; line-height:1.2; }
.pd .count-cell .l { font-size:10.5px; color:var(--muted); letter-spacing:0.4px; }
.pd .count-cell[data-zero="1"] .n, .pd .count-cell[data-zero="1"] .t { color:#A8B6C8; }
.pd .count-cell .t { font-size:13.5px; font-weight:650; line-height:1.3; padding:3px 0 2px; overflow-wrap:anywhere; }
.pd .breakdown { border:1px solid var(--line); border-radius:8px; background:#FAFDFF; padding:10px 12px; }
.pd .brow { display:flex; justify-content:space-between; font-size:12px; padding:3px 0; border-bottom:1px dotted #E2EAF4; }
.pd .brow:last-child { border-bottom:0; }
.pd .brow b { font-family: ui-monospace, Menlo, monospace; }
.pd .photos { display:grid; grid-template-columns: repeat(auto-fill, minmax(132px, 1fr)); gap:10px; }
.pd .shot { border:1px solid var(--line); border-radius:8px; overflow:hidden; background:#fff; }
.pd .shot img { width:100%; height:104px; object-fit:cover; display:block; cursor:zoom-in; background:#DCE5F1; }
.pd .shot .cap { padding:7px 8px; }
.pd .shot .tag { display:flex; align-items:center; gap:5px; font-size:9.5px; letter-spacing:1px; text-transform:uppercase; font-weight:700; color:var(--tile); }
.pd .phototags { display:grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap:8px; margin-bottom:11px; }
.pd .phototag { display:flex; flex-direction:column; gap:6px; min-width:0; }
.pd .phototag .opt { flex:1; position:relative; }
.pd .phototag .tcount { position:absolute; top:6px; right:8px; font-size:10.5px; font-weight:700; color:var(--muted); }
.pd .phototag .opt[data-on="1"] .tcount { color:var(--tile); }
.pd .photoadd { justify-content:center; width:100%; text-align:center; line-height:1.3; margin-top:0; }
.pd .photoadd .plus { width:16px; height:16px; font-size:12px; flex:0 0 auto; }
@media (max-width: 560px) { .pd .phototags { grid-template-columns:1fr; } .pd .phototag { flex-direction:row; align-items:stretch; } .pd .phototag .opt { flex:0 0 96px; } }
.pd .shot input { font-size:11.5px; padding:4px 6px; margin-top:4px; }
.pd .overlay { position:fixed; inset:0; background:rgba(8,20,25,.72); z-index:60; display:flex; align-items:flex-start; justify-content:center; padding:24px 14px; overflow:auto; }
.pd .sheet { background:#fff; border-radius:12px; width:100%; max-width:640px; }
.pd .lightbox { position:fixed; inset:0; background:rgba(8,20,25,.92); z-index:70; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:20px; }
.pd .lightbox img { max-width:100%; max-height:80vh; border-radius:8px; }
.pd .lightbox .cap2 { color:#CBE8F8; font-size:13px; margin-top:12px; text-align:center; }
.pd .visit { border:1px solid var(--line); border-radius:9px; padding:12px; margin-bottom:10px; }
.pd .visit.latest { border-color:#B9E0F5; background:#F5FAFE; }
.pd .vhead { display:flex; justify-content:space-between; gap:10px; flex-wrap:wrap; align-items:baseline; }
.pd .vdate { font-size:15px; font-weight:700; font-family: ui-monospace, Menlo, monospace; }
.pd .vdocs { display:flex; gap:6px; flex-wrap:wrap; }
.pd .doc { font-size:11px; font-weight:700; font-family: ui-monospace, Menlo, monospace; background:#17356E; color:#fff; border-radius:5px; padding:3px 7px; }
.pd .doc.inv { background:#0887C8; }
.pd .vnotes { font-size:13px; line-height:1.55; margin-top:9px; white-space:pre-wrap; }
.pd .daycol { border-top:3px solid var(--brand); }
.pd .daycol h3 { margin:0; padding:10px 12px 6px; font-size:13px; letter-spacing:0.2px; }
.pd .daycol .count { color:var(--muted); font-weight:500; font-size:11.5px; }
.pd .binwrap { border:1px solid var(--line); border-radius:11px; background:var(--card); padding:10px 12px 11px; margin-bottom:12px;
  border-left:4px solid var(--tile); }
.pd .binhead { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.pd .bintoggle { display:inline-flex; align-items:center; gap:7px; border:0; background:none; font:inherit; font-size:13.5px;
  font-weight:750; color:var(--ink); cursor:pointer; padding:0; }
.pd .bincount { display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 6px;
  border-radius:10px; background:var(--tile); color:#fff; font-size:11px; font-weight:800; }
.pd .bincount[data-zero="1"] { background:#C9D6E5; }
.pd .binrow { display:grid; grid-template-columns:repeat(auto-fill, minmax(210px, 1fr)); gap:8px; margin-top:10px; }
.pd .stop.job { border-left:3px solid var(--sky); }
.pd .stop.job[data-bin="1"] { border-left-color:var(--tile); background:#F7FBFE; }
.pd .jobtop { display:flex; align-items:center; justify-content:space-between; gap:6px; margin-bottom:3px; }
.pd .jobkind { font-size:9.5px; letter-spacing:0.9px; text-transform:uppercase; font-weight:800; color:var(--tile); }
.pd .jobw { font-size:10px; font-weight:800; color:var(--muted); }
.pd .jobwhat { font-size:12px; color:var(--muted); margin-top:2px; line-height:1.4; }
/* Morning Pre-Flight pull sheet on My Day. */
.pd .preflight { border:2px solid var(--sun); border-radius:11px; background:#FFFBF2; padding:11px 13px; margin-bottom:12px; }
.pd .preflight[data-ok="1"] { border-color:#12A650; background:#F2FBF5; }
.pd .pfhead { display:flex; align-items:baseline; justify-content:space-between; gap:10px; flex-wrap:wrap; margin-bottom:4px; }
.pd .pfhead b { font-size:14.5px; color:var(--ink); }
.pd .preflight[data-ok="1"] .pfhead b { color:#0E7A3C; }
.pd .pfsec { font-size:10.5px; letter-spacing:1.1px; text-transform:uppercase; color:var(--muted); font-weight:800; margin:9px 0 5px; }
.pd .pfrow { display:flex; align-items:center; gap:10px; border:1px solid var(--line); border-radius:9px; background:#fff; padding:8px 10px; margin-bottom:6px; width:100%; }
.pd .pfrow[data-miss="1"] { border-color:#F0C9C9; background:#FFF7F7; }
.pd .pfimg { width:40px; height:40px; border-radius:7px; object-fit:cover; border:1px solid var(--line); flex:0 0 auto; }
.pd .pfimg-blank { display:inline-block; background:var(--card); }
.pd .pfcheck { font:inherit; cursor:pointer; }
.pd .pfcheck[data-on="1"] { border-color:#12A650; background:#F2FBF5; }
.pd .pfbox { flex:0 0 auto; width:30px; height:30px; border-radius:8px; border:2px solid #B9C7D8; background:#fff; display:inline-flex; align-items:center; justify-content:center; font-weight:900; font-size:17px; color:#12A650; }
.pd .pfcheck[data-on="1"] .pfbox { border-color:#12A650; background:#E7F8EE; }
.pd .pfrow[data-special="1"]:not([data-on="1"]):not([data-miss="1"]) { border-color:#E0A21B; background:#FFF8E6; }
.pd .pftag { display:inline-block; margin-right:6px; padding:1px 6px; border-radius:4px; background:#E0A21B; color:#1a2230; font-size:9px; letter-spacing:0.6px; text-transform:uppercase; font-weight:800; vertical-align:middle; }
/* Day board — vehicles as vertical tiles; calls stacked under each truck (no sideways columns). */
.pd .techrows { display:flex; flex-direction:column; gap:10px; }
.pd .techrow { display:flex; flex-direction:column; gap:8px; align-items:stretch; background:var(--card); border:1px solid var(--line); border-radius:10px; padding:10px 12px; }
.pd .techrow .rowhead { flex:0 0 auto; width:100%; min-width:0; }
.pd .techrow .rowhead .tchead { margin:0; border:0; padding:0 0 2px; }
.pd .techrow .rowmeta { display:flex; align-items:center; gap:6px; padding-left:25px; }
.pd .techrow .rowstops { flex:0 0 auto; width:100%; min-width:0; display:flex; flex-direction:column; gap:6px; }
.pd .techrow .rowstops .stop { border:1px solid var(--line); border-radius:8px; margin:0; padding:8px 10px; background:#fff; width:100%; }
.pd .techrow[data-full="1"] { border-color:#F6C7C7; background:#FDF9F9; }
.pd .techrow[data-warn="1"] { border-color:var(--clay); background:#FDECEC; }
.pd .techrow[data-off="1"] { background:#EFF2F6; border-style:dashed; }
.pd .techrow[data-off="1"] .tnm, .pd .techrow[data-off="1"] .tcnt { color:#93A4B8; }
.pd .techrow[data-off="1"] .tnum { background:#93A4B8; }
.pd .techrow[data-over="1"] { border-color:var(--tile); background:#F0F9FF; box-shadow:0 0 0 2px rgba(8,135,200,.28); }
.pd .techrow[data-over="1"][data-full="1"] { border-color:var(--clay); background:#FDF3F3; box-shadow:0 0 0 2px rgba(179,18,18,.3); }
.pd .droptag { display:inline-block; margin-top:6px; font-size:10.5px; font-weight:800; letter-spacing:.6px; text-transform:uppercase; color:#fff; background:var(--tile); border-radius:999px; padding:3px 9px; }
.pd .techrow[data-over="1"][data-full="1"] .droptag { background:var(--clay); }
.pd .stop.job { cursor:grab; }
.pd .stop.job[data-drag="1"] { opacity:.45; }
/* Schedule holding bin: pin Daily/Overview/Tools + bin in .sched-pin, and let the
   truck/call list scroll in .sched-board-scroll. Do NOT use position:sticky + a high
   z-index — that painted the bin over Confirm-with-customer / closed-WO / first calls.
   confirm/billing .binwrap stay in flow via inline static. max-height keeps a full
   bin from eating the pin (desktop + phone ≤820px). Zoom stays on html, not .pd. */
.pd .sched-board { display:flex; flex-direction:column; min-height:0;
  height:calc(100vh - 6.5rem); max-height:calc(100vh - 6.5rem); }
.pd .sched-pin { flex:0 0 auto; background:var(--plaster); position:relative; z-index:1; }
.pd .sched-board-scroll { flex:1 1 auto; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.pd .binwrap.sched-bin { position:relative; background:var(--card);
  box-shadow:0 8px 22px rgba(16,32,55,.12); max-height:min(46vh, 28rem); overflow-y:auto; }
.pd .binwrap[data-over="1"] { border-color:var(--tile); box-shadow:0 0 0 2px rgba(8,135,200,.28), 0 8px 22px rgba(16,32,55,.12); }
@media (max-width: 820px) {
  .pd .sched-board { height:calc(100vh - 5.5rem); max-height:calc(100vh - 5.5rem); }
  .pd .binwrap.sched-bin { max-height:42vh; -webkit-overflow-scrolling:touch; }
  .pd .techrow { gap:7px; padding:9px 10px; }
  .pd .techrow .rowstops .stop { padding:7px 9px; }
}
.pd .offtag { border:1px solid #C3D9F2; background:#fff; border-radius:7px; padding:6px 8px; margin-bottom:8px;
  font-size:10.5px; font-weight:700; letter-spacing:0.4px; text-transform:uppercase; color:var(--muted); }
.pd .offtag b { display:block; margin-top:4px; text-transform:none; letter-spacing:0; font-size:11.5px; color:var(--clay); }
.pd .awaydot { font-style:normal; margin-left:4px; padding:0 4px; border-radius:6px; background:#93A4B8; color:#fff; font-size:9px; font-weight:800; }
.pd .tchead { display:flex; align-items:center; gap:6px; padding:2px 2px 8px; border-bottom:1px solid var(--line); margin-bottom:7px; }
.pd .tnum { flex:0 0 auto; display:inline-flex; align-items:center; justify-content:center; width:19px; height:19px; border-radius:50%;
  background:var(--ink); color:#fff; font-size:10.5px; font-weight:800; }
.pd .techrow[data-warn="1"] .tnum { background:var(--clay); }
.pd .tnm { flex:1; min-width:0; font-weight:750; font-size:12.5px; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd .tcnt { flex:0 0 auto; font-family: ui-monospace, Menlo, monospace; font-size:11px; font-weight:800; color:var(--muted); }
.pd .techrow .rowstops .stop .cr-meta { gap:5px 7px; font-size:10.5px; }
.pd .techrow .rowstops .stop-n { font-size:10px; }
.pd .band { display:inline-block; width:9px; height:9px; border-radius:50%; margin-right:6px; vertical-align:middle; }
.pd .band[data-b="green"] { background:#1E9E56; }
.pd .band[data-b="amber"] { background:#E0A21B; }
.pd .band[data-b="red"] { background:var(--clay); }
.pd .monthgrid { display:grid; grid-template-columns:repeat(7, 1fr); gap:5px; }
.pd .mhead { text-align:center; font-size:10px; letter-spacing:1.1px; text-transform:uppercase; font-weight:800; color:var(--muted); padding:4px 0; }
.pd .calcell { position:relative; aspect-ratio:1/0.82; border:1px solid var(--line); border-radius:8px; background:#fff; cursor:pointer; font:inherit;
  display:flex; flex-direction:column; align-items:flex-start; justify-content:space-between; padding:6px 7px; }
.pd .calcell[data-out="1"] { opacity:0.38; }
.pd .calcell[data-today="1"] { box-shadow: inset 0 0 0 2px var(--brand); }
.pd .calcell[data-b="green"] { background:#EAF7EF; border-color:#B6DEC5; }
.pd .calcell[data-b="amber"] { background:#FDF4E0; border-color:#EBD3A8; }
.pd .calcell[data-b="red"] { background:#FDECEC; border-color:#F6C7C7; }
.pd .calcell .dnum { font-size:12px; font-weight:700; color:var(--ink); }
.pd .calcell .dcount { align-self:flex-end; font-size:16px; font-weight:800; color:var(--ink); font-family: ui-monospace, Menlo, monospace; }
.pd .monthkey { display:flex; gap:16px; flex-wrap:wrap; margin-top:11px; font-size:11.5px; color:var(--muted); font-weight:650; }
.pd .weekgrid { display:grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap:10px; }
.pd .stop { border-top:1px solid var(--line); padding:9px 12px; cursor:pointer; }
.pd .stop:hover { background:#F5FAFD; }
.pd .stop-n { color:var(--tile); font-weight:700; font-size:11px; }
.pd .empty { padding:26px 16px; text-align:center; color:var(--muted); font-size:13px; line-height:1.6; }
.pd .bar { display:flex; justify-content:space-between; align-items:center; gap:10px; padding:12px 14px; border-bottom:1px solid var(--line); flex-wrap:wrap; }
.pd .autocharge { display:flex; align-items:center; gap:12px; flex-wrap:wrap; border:1px solid var(--line); border-radius:9px; padding:12px; background:#FAFDFF; margin-bottom:11px; }
.pd .autocharge[data-on="1"] { border-color:#B9E0F5; background:#EDF7FD; }
.pd .switch { display:inline-flex; align-items:center; gap:8px; border:1px solid var(--ink); background:#fff; color:var(--ink); border-radius:20px; padding:8px 16px; font:inherit; font-size:12.5px; font-weight:700; cursor:pointer; white-space:nowrap; transition: background .12s, color .12s; }
.pd .switch .dot { width:9px; height:9px; border-radius:50%; background:#A8B6C8; box-shadow: inset 0 0 0 1px rgba(0,0,0,.08); }
.pd .switch[data-on="1"] .dot { background:#B7E4F8; }
.pd .switch:hover:not(:disabled) { background:#17356E; color:#fff; }
.pd .switch[data-on="1"]:hover:not(:disabled) { background:#086EB6; }
.pd .switch[data-on="1"] { background:var(--tile); border-color:var(--tile); color:#fff; }
.pd .switch:disabled { opacity:.45; cursor:not-allowed; }
.pd .cardbox { border:1px solid var(--line); border-radius:9px; padding:12px; background:#fff; }
.pd .cardface { background:linear-gradient(140deg,#17356E,#22509E); color:#fff; border-radius:9px; padding:13px 15px; min-width:190px; }
.pd .cardface .num { font-family: ui-monospace, Menlo, monospace; font-size:17px; letter-spacing:2px; margin-top:5px; }
.pd .cardface .who { font-size:11.5px; color:#9CD3F0; margin-top:6px; display:flex; justify-content:space-between; gap:12px; }
.pd .notice { background:#F7FBFD; border:1px solid var(--line); border-left:3px solid var(--sun); border-radius:7px; padding:9px 11px; font-size:12px; line-height:1.5; color:#42546A; margin-top:9px; }
.pd .picker { display:grid; grid-template-columns: repeat(auto-fit, minmax(104px, 1fr)); gap:8px; }
.pd .opt { display:flex; flex-direction:column; align-items:center; gap:8px; border:1px solid var(--line); background:#fff; border-radius:10px; padding:11px 7px 9px; cursor:pointer; font:inherit; color:var(--muted); transition: border-color .12s, background .12s, color .12s; }
.pd .opt:hover:not([data-on="1"]) { border-color:#8FCDEE; background:#FAFDFF; }
.pd .opt[data-on="1"] { border-color:var(--tile); background:#EAF5FD; color:var(--ink); box-shadow: inset 0 0 0 1px var(--tile); }
.pd .opt .cap { font-size:11.5px; font-weight:650; line-height:1.25; text-align:center; }
.pd .opt svg { display:block; }
.pd .stackpanel { border:1px solid var(--line); border-radius:10px; background:#FAFDFF; padding:13px; margin-top:11px; }
.pd .stackpanel .ptitle { font-size:11px; letter-spacing:1.2px; text-transform:uppercase; font-weight:700; color:var(--muted); margin-bottom:10px; }
.pd .seg { display:inline-flex; border:1px solid var(--line); border-radius:8px; overflow:hidden; background:#fff; margin-bottom:11px; }
.pd .seg button { border:0; border-right:1px solid var(--line); background:#fff; font:inherit; font-size:12.5px; font-weight:650; padding:8px 15px; cursor:pointer; color:var(--muted); }
.pd .seg button:last-child { border-right:0; }
.pd .seg button[data-on="1"] { background:var(--ink); color:#fff; }
.pd .leafrow { margin-top:11px; padding-top:11px; border-top:1px dashed var(--line); }
.pd .leafrow label { color:var(--ink); font-size:13px; font-weight:650; }
.pd .schedcard { border:1px solid var(--line); border-left:4px solid var(--brand); border-radius:9px; padding:12px 14px; background:#FAFDFF; margin-bottom:13px; }
.pd .schedcard .freq { font-size:16px; font-weight:700; letter-spacing:-0.2px; }
.pd .schedcard .when { font-size:13px; color:var(--muted); margin-top:3px; }
.pd .schedcard .until { font-size:12px; font-weight:650; color:#B31212; margin-top:6px; }
.pd .schedcard .nextup { font-size:12.5px; font-weight:700; color:var(--tile); margin-top:6px; font-family: ui-monospace, Menlo, monospace; }
.pd .dayline { display:flex; gap:6px; flex-wrap:wrap; }
.pd .daypill.pick { cursor:pointer; font:inherit; }
.pd .daypill.pick:hover:not([data-on="1"]) { border-color:#8FCDEE; background:#FAFDFF; }
.pd .freqrow { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:11px; }
.pd .freqrow button { flex:1 1 auto; border:1px solid var(--line); background:#fff; border-radius:8px; padding:9px 13px; font:inherit; font-size:12.5px; font-weight:650; color:var(--muted); cursor:pointer; white-space:nowrap; }
.pd .freqrow button[data-on="1"] { background:var(--ink); border-color:var(--ink); color:#fff; }
.pd .daypill { flex:1 1 0; min-width:46px; text-align:center; border:1px solid var(--line); border-radius:8px; padding:9px 4px 8px; background:#fff; }
.pd .daypill .dd { font-size:12px; font-weight:700; letter-spacing:0.4px; color:#93A4B8; }
.pd .daypill[data-on="1"] { background:var(--tile); border-color:var(--tile); box-shadow:0 0 0 3px rgba(8,135,200,.12); }
.pd .daypill[data-on="1"] .dd { color:#fff; }
.pd .daycap { font-size:12.5px; color:var(--muted); margin-top:8px; }
.pd .daycap b { color:var(--ink); font-weight:700; }
.pd .permgrid { display:grid; grid-template-columns: repeat(auto-fit, minmax(215px, 1fr)); gap:7px; }
.pd .perm { display:flex; align-items:flex-start; border:1px solid var(--line); border-radius:8px; padding:8px 10px; background:#fff; cursor:pointer; margin:0; }
.pd .perm[data-on="1"] { border-color:var(--tile); background:#EDF7FD; }
.pd .perm b { display:block; font-size:12.5px; color:var(--ink); font-weight:650; }
.pd .perm em { display:block; font-style:normal; font-size:11px; color:var(--muted); margin-top:2px; line-height:1.35; }
.pd .topright { display:flex; align-items:center; gap:7px; flex-wrap:wrap; justify-content:flex-end; }
.pd .usermenu { position:relative; }
.pd .whobtn { border:1px solid transparent; background:transparent; cursor:pointer; padding:5px 9px; border-radius:9px; text-align:right; align-items:flex-end; margin:0; }
.pd .whobtn:hover { background:var(--card); border-color:var(--line); }
.pd .whobtn[aria-expanded="1"] { background:var(--card); border-color:var(--line); }
.pd .whocaret { font-size:9px; color:var(--muted); transition:transform .12s ease; display:inline-block; }
.pd .whocaret[data-open="1"] { transform:rotate(180deg); }
.pd .menu-backdrop { position:fixed; inset:0; z-index:40; }
.pd .usermenu-pop { position:absolute; top:calc(100% + 6px); right:0; z-index:41; min-width:170px;
  background:#fff; border:1px solid var(--line); border-radius:10px; box-shadow:0 10px 28px rgba(16,32,55,.18); padding:6px; }
.pd .usermenu-pop button { display:block; width:100%; text-align:left; border:0; background:#fff; color:var(--ink);
  font:inherit; font-size:13px; font-weight:600; padding:9px 11px; border-radius:7px; cursor:pointer; }
.pd .usermenu-pop button:hover { background:var(--card); }
.pd .rolepick { display:flex; align-items:center; gap:7px; }
.pd .rolepick select { width:auto; font-size:12.5px; font-weight:650; padding:7px 9px; }
.pd .recpair { display:grid; grid-template-columns:1fr 1fr; gap:9px; }
@media (max-width: 560px) { .pd .recpair { grid-template-columns:1fr; } }
.pd .recpair .recrow { margin-bottom:7px; padding:10px 12px; }
.pd .showall { display:flex; width:fit-content; align-items:center; gap:7px; margin-top:11px; clear:both; border:1px solid var(--line); background:#fff; border-radius:7px; padding:6px 10px; font:inherit; font-size:11.5px; font-weight:650; color:var(--muted); cursor:pointer; }
.pd .showall:hover { border-color:var(--tile); color:var(--tile); }
.pd .recrow { display:flex; align-items:flex-start; gap:11px; border:1px solid var(--line); border-radius:9px; padding:10px 11px; margin-bottom:7px; background:#FAFDFF; }
.pd .recrow .ico { color:var(--tile); flex:0 0 auto; margin-top:1px; }
.pd .recrow .body { flex:1; min-width:0; }
.pd .recrow .lbl2 { font-size:10px; letter-spacing:1.1px; text-transform:uppercase; font-weight:700; color:var(--muted); display:flex; align-items:center; gap:7px; flex-wrap:wrap; }
.pd .recrow .bigdate { display:block; font-family: ui-monospace, Menlo, monospace; font-size:17px; font-weight:700; color:var(--tile); background:none; border:0; padding:2px 0 0; cursor:pointer; text-decoration:underline; text-underline-offset:3px; }
.pd .recrow .bigdate:hover { color:#086EB6; }
.pd .recrow .none2 { font-size:13px; color:#93A4B8; margin-top:3px; }
.pd .addr { display:grid; grid-template-columns:1fr 1fr; gap:9px; }
@media (max-width: 560px) { .pd .addr { grid-template-columns:1fr; } }
.pd .addrcard { border:1px solid var(--line); border-radius:9px; padding:9px 11px; background:#fff; }
.pd .addrcard .ctag { display:block; margin-bottom:4px; }
.pd .addrcard .txt { font-size:13.5px; line-height:1.5; }
.pd .addrcard .txt.muted2 { color:var(--muted); }
.pd .myday { max-width:640px; margin:0 auto; }
.pd .mdprog { height:8px; border-radius:5px; background:var(--line); overflow:hidden; margin-bottom:13px; }
.pd .mdprog i { display:block; height:100%; background:linear-gradient(90deg, var(--tile), #12A650); border-radius:5px; transition:width .25s ease; }
.pd .mdcard { display:flex; align-items:center; gap:12px; width:100%; text-align:left; font:inherit; color:inherit; cursor:pointer;
  border:1px solid var(--line); border-left:5px solid var(--line); border-radius:11px; background:#fff; padding:13px 14px; margin-bottom:9px; }
.pd .mdcard:hover { box-shadow:0 3px 10px rgba(16,32,55,.10); }
.pd .mdcard[data-st="done"] { opacity:.62; } .pd .mdcard[data-st="skipped"] { opacity:.62; border-left-color:#D9822B; }
.pd .mdst { flex:none; width:30px; height:30px; border-radius:9px; display:grid; place-items:center; font-weight:800; font-size:13px;
  background:var(--card); border:1px solid var(--line); color:var(--ink-soft, var(--muted)); }
.pd .mdst[data-st="done"] { background:#12A650; border-color:#12A650; color:#fff; }
.pd .mdst[data-st="skipped"] { background:#D9822B; border-color:#D9822B; color:#fff; }
.pd .mdbody { flex:1; min-width:0; display:flex; flex-direction:column; }
.pd .mdbody b { font-size:14.5px; }
.pd .mdbody span { font-size:12px; color:var(--muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd .mdmeta { flex:none; display:flex; gap:6px; font-size:15px; }
.pd .mdinfo { border:1px solid var(--line); border-radius:11px; background:#F7FBFE; padding:12px 14px; margin-bottom:4px; }
.pd .mdaddr { display:block; font-size:14px; font-weight:700; color:var(--tile); text-decoration:none; margin-bottom:7px; }
.pd .mdchips { display:flex; flex-wrap:wrap; gap:7px; }
.pd .mdchip { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:650; color:var(--ink);
  background:#fff; border:1px solid var(--line); border-radius:999px; padding:4px 11px; text-decoration:none; }
.pd .mdchip.warn { border-color:#F0C9A0; background:#FFF6EB; color:#8A5A16; }
.pd .mdcheck { display:flex; align-items:center; gap:11px; width:100%; text-align:left; font:inherit; font-size:14px; color:inherit;
  border:1px solid var(--line); border-radius:10px; background:#fff; padding:12px 13px; margin-bottom:7px; cursor:pointer; }
.pd .mdcheck[data-on="1"] { background:#F0FAF4; border-color:#B9E4C8; color:var(--muted); text-decoration:line-through; }
.pd .mdbox { flex:none; width:22px; height:22px; border-radius:7px; border:2px solid var(--line); display:grid; place-items:center;
  background:#fff; color:#fff; font-weight:900; font-size:13px; }
.pd .mdcheck[data-on="1"] .mdbox { background:#12A650; border-color:#12A650; }
.pd .mdshots { display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; }
.pd .mdshots img { width:74px; height:74px; object-fit:cover; border-radius:9px; border:1px solid var(--line); }
.pd .mdread { display:grid; grid-template-columns:repeat(auto-fill, minmax(96px, 1fr)); gap:8px; }
.pd .mdread label { font-size:10px; }
.pd .mdfinish { display:flex; gap:9px; margin-top:18px; position:sticky; bottom:10px; }
.pd .stop.job[data-done="1"] { opacity:.55; }
.pd .chempanel { margin-top:11px; border:1px solid var(--line); border-radius:10px; padding:11px 13px; background:#F7FBFE; }
.pd .chemtop { display:flex; justify-content:space-between; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:8px; }
.pd .lsichip { font-weight:800; font-size:12.5px; padding:4px 12px; border-radius:999px; color:#fff; letter-spacing:.2px; }
.pd .lsichip[data-b="balanced"] { background:#12A650; }
.pd .lsichip[data-b="corrosive"] { background:#D9822B; }
.pd .lsichip[data-b="scaling"] { background:#7C4CE0; }
.pd .chemvol { font-size:11px; color:var(--muted); font-weight:650; }
.pd .chemok { font-size:13px; font-weight:750; color:#0E7A3C; }
.pd .chemrec { display:flex; flex-direction:column; gap:2px; padding:8px 0; border-top:1px dashed var(--line); }
.pd .chemrec:first-child { border-top:0; padding-top:2px; }
.pd .chemrec b { font-size:13px; }
.pd .chemrec[data-sev="action"] b { color:#B3261E; }
.pd .chemrec[data-sev="watch"] b { color:#8A6316; }
.pd .chemrec .dose { font-size:13.5px; font-weight:750; }
.pd .chemrec .note { font-size:11.5px; color:var(--muted); }
.pd .trendgrid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:10px; }
.pd .trendcell { border:1px solid var(--line); border-radius:9px; padding:8px 11px; background:#fff; }
.pd .trendcell .tl { font-size:10px; text-transform:uppercase; letter-spacing:.6px; color:var(--muted); font-weight:800; }
.pd .trendcell .tv { font-size:15px; font-weight:800; margin:1px 0 3px; }
.pd .reading { display:inline-flex; align-items:baseline; gap:5px; border:1px solid var(--line); border-radius:7px; padding:5px 9px; background:#fff; font-size:12px; }
.pd .reading .rl { color:var(--muted); font-weight:650; }
.pd .reading .rv { font-family: ui-monospace, Menlo, monospace; font-weight:700; font-size:13px; }
.pd .reading[data-s="ok"] { border-color:#B9E0F5; background:#EDF7FD; }
.pd .reading[data-s="ok"] .rv { color:#086EB6; }
.pd .reading[data-s="low"] { border-color:#BFE2F6; background:#EDF7FD; }
.pd .reading[data-s="low"] .rv { color:#086EB6; }
.pd .reading[data-s="high"] { border-color:#F6C7C7; background:#FDECEC; }
.pd .reading[data-s="high"] .rv { color:#B31212; }
.pd .readings { display:flex; flex-wrap:wrap; gap:6px; margin-top:9px; }
.pd .testcard { border:1px solid var(--line); border-radius:9px; padding:12px; margin-bottom:10px; }
.pd .testcard.latest { border-color:#B9E0F5; background:#F5FAFE; }
.pd .srcchip { font-size:10px; letter-spacing:1px; text-transform:uppercase; font-weight:700; color:var(--tile); border:1px solid #BFE2F6; background:#EAF4FC; border-radius:5px; padding:2px 7px; }
.pd .srcchip[data-s="manual"] { color:var(--muted); border-color:var(--line); background:#F7FBFD; }
.pd .flagbtn { display:inline-flex; align-items:center; gap:8px; font:inherit; font-size:13.5px; font-weight:700; padding:9px 16px; border-radius:8px; cursor:pointer; border:1.5px solid var(--clay); background:#fff; color:var(--clay); letter-spacing:0.2px; }
.pd .flagbtn:hover { background:#FDECEC; }
.pd .flagbtn[data-on="1"] { background:var(--clay); border-color:var(--clay); color:#fff; box-shadow:0 0 0 3px rgba(220,21,21,.18); }
.pd .flagbtn[data-on="1"]:hover { background:#B31212; }
@keyframes flagflash {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220,21,21,.55); }
  55% { box-shadow: 0 0 0 7px rgba(220,21,21,0); }
}
.pd .flagbtn[data-alert="1"] { background:var(--clay); border-color:var(--clay); color:#fff; animation: flagflash 1.7s ease-out infinite; }
.pd .flagbtn[data-alert="1"]:hover { background:#B31212; }
@media (prefers-reduced-motion: reduce) {
  .pd .flagbtn[data-alert="1"] { animation:none; box-shadow:0 0 0 3px rgba(220,21,21,.3); }
}
.pd .flagbanner.auto { background:#FDEFEF; border-color:#F6C7C7; border-left-color:var(--sun); }
.pd .flagbanner.auto .who { color:#B31212; }
.pd .flagbanner.auto .msg { color:#9B1010; }
.pd .doc.owe { background:#DC1515; }
.pd .flagbanner { display:flex; gap:12px; align-items:flex-start; background:#FDECEC; border:1px solid #F6C7C7; border-left:4px solid var(--clay); border-radius:9px; padding:12px 14px; margin-bottom:13px; }
.pd .flagbanner .msg { font-size:13.5px; line-height:1.5; color:#9B1010; font-weight:600; }
.pd .flagbanner .who { font-size:10.5px; letter-spacing:1.1px; text-transform:uppercase; font-weight:700; color:#C86A6A; margin-bottom:3px; }
.pd .flagpop { background:#fff; border-radius:12px; width:100%; max-width:460px; overflow:hidden; }
.pd .flagpop .top { background:var(--clay); color:#fff; padding:13px 16px; font-size:11px; letter-spacing:1.4px; text-transform:uppercase; font-weight:700; display:flex; justify-content:space-between; gap:10px; }
.pd .flagpop .who2 { color:#FBDDDD; }
.pd .flagpop .msg2 { padding:18px 16px; font-size:16px; line-height:1.55; font-weight:600; }
.pd .flagpop .acts { padding:0 16px 16px; display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; }
.pd .dotflag { display:inline-block; width:7px; height:7px; border-radius:50%; background:var(--clay); }
.pd .eqrow { display:flex; align-items:flex-start; gap:11px; border:1px solid var(--line); border-radius:9px; padding:8px 11px; margin-bottom:6px; background:#FAFDFF; }
.pd .eqrow .ico { color:var(--tile); flex:0 0 auto; margin-top:1px; }
.pd .eqrow .body { flex:1; min-width:0; }
.pd .eqrow .kind { font-size:10px; letter-spacing:1.1px; text-transform:uppercase; font-weight:700; color:var(--muted); }
.pd .eqrow .val { font-size:13.5px; line-height:1.45; margin-top:2px; overflow-wrap:anywhere; }
.pd .eqrow .val.none { color:#93A4B8; }
.pd .eqrow.edit { background:#fff; align-items:center; }
.pd .eqrow .grow { display:grid; grid-template-columns: 150px 1fr; gap:8px; flex:1; min-width:0; }
@media (max-width: 560px) { .pd .eqrow .grow { grid-template-columns: 1fr; } }
.pd .addrow { display:flex; align-items:center; justify-content:center; gap:9px; width:100%; border:1px dashed var(--line); background:#fff; border-radius:9px; padding:11px; cursor:pointer; font:inherit; font-size:13px; font-weight:650; color:var(--muted); }
.pd .addrow:hover { border-color:var(--tile); color:var(--tile); background:#F8FCFE; }
.pd .plus { display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:50%; border:1.5px solid currentColor; font-size:14px; line-height:1; font-weight:700; }
.pd .structhero { display:flex; align-items:center; gap:12px; border:1px solid var(--line); border-radius:10px; padding:9px 12px; background:#FAFDFF; margin-bottom:8px; }
.pd .structrow { display:flex; gap:11px; flex-wrap:wrap; }
.pd .structrow .structhero { flex:1 1 240px; min-width:0; }
.pd .lrow[data-flash="1"] { animation: payflash 1.4s ease-in-out infinite; border-radius:7px; }
.pd .lrow[data-flash="1"] > span:first-child { color:#B3261E; font-weight:800; }
.pd .payexp { display:block; color:#B3261E; font-size:11.5px; font-weight:800; margin-top:2px; line-height:1.4; }
@keyframes payflash { 0%,100% { background:transparent; } 50% { background:#FDECEC; } }
@media (prefers-reduced-motion: reduce) { .pd .lrow[data-flash="1"] { animation:none; background:#FDECEC; } }
.pd .switch.acsmall { font-size:11px; padding:5px 10px; }
.pd .switch.acsmall[data-on="1"] { background:#12A650; border-color:#12A650; color:#fff; }
.pd .switch.acsmall[data-on="1"] .dot { background:#fff; }
.pd .chargeopt { display:flex; flex-direction:column; align-items:flex-start; gap:3px; width:100%; text-align:left; padding:13px 15px; margin-bottom:9px; }
.pd .chargeopt b { font-size:14.5px; }
.pd .chargeopt span { font-size:11.5px; font-weight:500; opacity:.85; }
.pd .structhero .nm { font-size:15px; font-weight:700; letter-spacing:-0.2px; }
.pd .ledger { border:1px solid var(--line); border-radius:9px; overflow:hidden; }
.pd .lrow { display:flex; justify-content:space-between; gap:14px; padding:8px 12px; font-size:13px; border-bottom:1px solid #E7EDF6; }
.pd .lrow:last-child { border-bottom:0; }
.pd .lrow:nth-child(odd) { background:#FAFDFF; }
.pd .lrow span { color:var(--muted); }
.pd .lrow b { text-align:right; }
.pd .dashwrap { border-bottom:1px solid var(--line); padding:4px 0 10px; }
.pd .dashwrap:last-child { border-bottom:0; }
.pd .dashwrap[data-strong="1"] { background:var(--plaster); border-radius:9px; padding:6px 10px 12px; margin-top:6px; border-bottom:0; }
.pd .dashwrap[data-strong="1"] + .dashwrap[data-strong="1"] { margin-top:8px; }
.pd .chev { display:inline-block; margin-right:8px; transition:transform .15s; color:var(--muted); font-size:15px; }
.pd .chev[data-open="1"] { transform:rotate(90deg); color:var(--tile); }
.pd .salesgrid { display:grid; grid-template-columns:repeat(auto-fit, minmax(118px, 1fr)); gap:7px; margin-top:2px; }
.pd .salesgrid.open { margin-top:7px; grid-template-columns:repeat(auto-fit, minmax(180px, 1fr)); }
.pd .salecell { display:flex; flex-direction:column; align-items:flex-start; gap:2px; border:1px solid var(--line); border-radius:8px; background:#fff; padding:8px 9px; }
.pd .salecell[data-zero="1"] { opacity:0.45; }
.pd .salecell .ic { color:var(--tile); }
.pd .salecell .n { font-family: ui-monospace, Menlo, monospace; font-size:14px; font-weight:800; color:var(--ink); }
.pd .salecell .n.big { font-size:21px; }
.pd .salecell .l { font-size:9.5px; letter-spacing:0.9px; text-transform:uppercase; font-weight:700; color:var(--muted); }
.pd .trend { margin-top: 10px; border:1px solid var(--line); border-radius:9px; background:#fff; padding:9px 10px 7px; }
.pd .trend svg { display:block; }
.pd .tlabels { display:flex; margin-top:2px; }
.pd .tlabels span { flex:1; text-align:center; font-size:9.5px; font-weight:700; color:var(--muted); overflow:hidden; white-space:nowrap; }
.pd .tlabels span[data-on="1"] { color:var(--brand); }
.pd .tkey { display:flex; gap:14px; align-items:center; flex-wrap:wrap; margin-top:7px; font-size:11px; font-weight:650; color:var(--muted); }
.pd .tkey .sw { display:inline-block; width:10px; height:10px; border-radius:2px; margin-right:5px; vertical-align:middle; }
.pd .salecell.wide { border-color:var(--tile); background:#EDF7FD; }
.pd .dashline { width:100%; text-align:left; font:inherit; background:none; border:0; cursor:pointer; display:grid; grid-template-columns: 190px 1fr auto; gap:12px; align-items:center; padding:11px 4px; border-bottom:1px solid var(--line); }
.pd .dl-label { font-size:11px; letter-spacing:1.2px; text-transform:uppercase; font-weight:800; color:var(--muted); }
.pd .dashwrap[data-strong="1"] .dl-label { color:var(--ink); font-size:12px; }
.pd .dl-amt { text-align:right; font-family: ui-monospace, Menlo, monospace; font-size:19px; font-weight:800; color:var(--ink); white-space:nowrap; }
.pd .dashwrap[data-strong="1"] .dl-amt { font-size:24px; color:var(--brand); }
.pd .dl-prev { display:block; margin-top:3px; font-size:10.5px; letter-spacing:0.3px; text-transform:none; font-weight:600; color:var(--muted); }
.pd .dl-prev b { margin-left:6px; font-weight:800; }
.pd .dl-prev b[data-dir="up"] { color:#1E9E56; }
.pd .dl-prev b[data-dir="down"] { color:var(--clay); }
.pd .dl-amt[data-dir="up"] { color:#1E9E56; }
.pd .dl-amt[data-dir="down"] { color:var(--clay); }
.pd .dashwrap[data-strong="1"] .dl-amt[data-dir="up"] { color:#17864A; }
.pd .dashwrap[data-strong="1"] .dl-amt[data-dir="down"] { color:var(--clay); }
.pd .dl-sub { display:block; font-family:inherit; font-size:10.5px; font-weight:600; color:var(--muted); letter-spacing:0.3px; }
.pd .stepper { display:inline-flex; align-items:center; gap:8px; }
.pd .stepper button { border:1px solid var(--line); background:#fff; border-radius:7px; width:26px; height:26px; cursor:pointer; font-size:14px; color:var(--ink); line-height:1; }
.pd .stepper button:hover { border-color:var(--tile); color:var(--tile); }
.pd .stepper .val { font-size:13px; font-weight:700; color:var(--ink); }
.pd .stepper .val em { font-style:normal; font-weight:600; color:var(--muted); margin-left:6px; font-size:12px; }
@media (max-width: 700px) { .pd .dashline { grid-template-columns:1fr auto; } .pd .dl-label { grid-column:1 / -1; } }
.pd .salest { width:100%; border-collapse:collapse; font-size:13px; }
.pd .salest th { text-align:right; font-size:10px; letter-spacing:1.1px; text-transform:uppercase; color:var(--muted); padding:9px 12px; border-bottom:1px solid var(--line); white-space:nowrap; }
.pd .salest th:first-child { text-align:left; }
.pd .salest td { padding:10px 12px; border-bottom:1px solid #E7EDF6; text-align:right; white-space:nowrap; }
.pd .salest td:first-child { text-align:left; font-weight:650; white-space:normal; }
.pd .salest tbody tr { cursor:pointer; }
.pd .salest tbody tr:hover { background:#F5FAFD; }
.pd .lvlpick { display:grid; grid-template-columns: repeat(auto-fit, minmax(142px, 1fr)); gap:8px; }
.pd .lvl { display:flex; flex-direction:column; align-items:flex-start; gap:5px; border:1px solid var(--line); background:#fff; border-radius:9px; padding:10px 11px; cursor:pointer; font:inherit; text-align:left; }
.pd .lvl:hover:not([data-on="1"]) { border-color:#8FCDEE; background:#FAFDFF; }
.pd .lvl[data-on="1"] { border-color:var(--tile); background:#EAF5FD; box-shadow: inset 0 0 0 1px var(--tile); }
.pd .lvl .lvlname { font-size:13.5px; font-weight:700; color:var(--ink); }
.pd .lvl .lvlcols { display:grid; grid-template-columns:repeat(3, 1fr); gap:5px; width:100%; margin-top:3px; }
.pd .lvl .lvlcol { display:flex; flex-direction:column; align-items:center; border-top:1px solid var(--line); padding-top:5px; }
.pd .lvl .lvlpct { font-family: ui-monospace, Menlo, monospace; font-size:13.5px; font-weight:700; color:var(--ink); }
.pd .lvl .lvlcap { font-size:9px; letter-spacing:0.7px; text-transform:uppercase; font-weight:700; color:var(--muted); margin-top:2px; }
.pd .lvl[data-on="1"] .lvlpct { color:#086EB6; }
.pd .lvl[data-on="1"] .lvlcol { border-top-color:#BCE0F5; }
.pd .lvlrank { display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px; border-radius:50%; background:var(--ink); color:#fff; font-size:11.5px; font-weight:700; flex:0 0 auto; }
.pd .tier { display:inline-flex; align-items:center; gap:7px; border:1px solid #BFE2F6; background:#EAF4FC; color:#086EB6; border-radius:7px; padding:6px 10px; font-size:12.5px; font-weight:700; }
.pd .tier b { font-family: ui-monospace, Menlo, monospace; }
.pd .tierrow { display:grid; grid-template-columns: 1.1fr 0.5fr 1.6fr auto; gap:8px; align-items:center; margin-bottom:8px; }
@media (max-width: 560px) { .pd .tierrow { grid-template-columns: 1fr 1fr; } }
` + APPLE_CSS; // Apple visual pass — look only, see src/appleTheme.js

function PoolMasterLogo({ height = 34 }) {
  return (
    <svg height={height} viewBox="0 0 850.394 226.772" xmlns="http://www.w3.org/2000/svg"
      role="img" aria-label="Pool Master" style={{ display: "block", width: "auto" }}>
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 61.031 45.886 C 61.031 42.378 59.390 40.621 55.75 40.621 L 50.835 40.621 L 50.835 87.496 L 55.75 87.496 C 59.390 87.496 61.031 85.914 61.031 82.406 Z M 57.207 103.648 L 51.015 103.648 L 51.015 144.550 L 28.441 144.550 L 28.441 25.171 L 57.386 25.171 C 77.050 25.171 83.421 32.195 83.421 46.238 L 83.421 82.578 C 83.421 96.625 76.867 103.648 57.207 103.648 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 132.164 45.210 C 132.164 41.812 131.460 39.265 127.234 39.265 C 123.007 39.265 122.480 41.644 122.480 45.039 L 122.480 124.175 C 122.480 127.570 123.007 129.945 127.234 129.945 C 131.460 129.945 132.164 127.570 132.164 124.175 Z M 127.234 144.550 C 103.992 144.550 100.644 133.851 100.644 118.570 L 100.644 51.152 C 100.644 35.871 103.992 25.171 127.234 25.171 C 150.832 25.171 154 35.871 154 51.152 L 154 118.570 C 154 133.851 150.832 144.550 127.234 144.550 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 203.285 45.210 C 203.285 41.812 202.582 39.265 198.355 39.265 C 194.128 39.265 193.597 41.644 193.597 45.039 L 193.597 124.175 C 193.597 127.570 194.128 129.945 198.355 129.945 C 202.582 129.945 203.285 127.570 203.285 124.175 Z M 198.355 144.550 C 175.109 144.550 171.765 133.851 171.765 118.570 L 171.765 51.152 C 171.765 35.871 175.109 25.171 198.355 25.171 C 221.949 25.171 225.121 35.871 225.121 51.152 L 225.121 118.570 C 225.121 133.851 221.949 144.550 198.355 144.550 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 242.890 144.550 L 242.890 25.171 L 265.464 25.171 L 265.464 127.875 L 283.125 127.875 L 283.125 144.550 Z M 242.890 144.550 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 487.199 144.550 C 464.304 144.550 460.960 133.171 460.960 118.398 L 460.960 97.511 L 482.621 97.511 L 482.621 124.683 C 482.621 128.078 483.148 130.625 487.375 130.625 C 491.601 130.625 491.953 128.078 491.953 124.683 L 491.953 108.550 C 491.953 101.757 488.429 99.210 485.437 96.324 L 471.703 83.078 C 462.195 73.910 461.312 66.945 461.312 53.531 C 461.312 34.683 465.187 25.171 487.550 25.171 C 509.914 25.171 513.437 36.039 513.437 50.644 L 513.437 69.324 L 491.777 69.324 L 491.777 44.531 C 491.777 41.136 491.425 38.585 487.375 38.585 C 483.324 38.585 482.796 40.964 482.796 44.359 L 482.796 58.117 C 482.796 65.757 486.492 68.136 489.312 70.851 L 503.398 84.437 C 512.734 93.437 513.613 100.058 513.613 114.496 C 513.613 134.023 510.089 144.550 487.199 144.550 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 565.597 41.148 L 565.597 144.550 L 543.023 144.550 L 543.023 41.148 L 527.730 41.148 L 527.730 25.171 L 580.710 25.171 L 580.710 41.148 Z M 565.597 41.148 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 595.753 144.550 L 595.753 25.171 L 638.539 25.171 L 638.539 41.148 L 618.332 41.148 L 618.332 74.679 L 637.808 74.679 L 637.808 91.531 L 618.332 91.531 L 618.332 127.875 L 638.539 127.875 L 638.539 144.550 Z M 595.753 144.550 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 687.082 72.398 C 687.082 75.031 685.261 77.136 681.621 77.136 L 676.886 77.136 L 676.886 40.621 L 681.621 40.621 C 685.261 40.621 687.082 42.726 687.082 45.359 Z M 694.730 86.441 L 692.179 86.441 C 700.917 85.562 709.476 81 709.476 65.375 L 709.476 47.117 C 709.476 32.195 703.101 25.171 683.441 25.171 L 654.496 25.171 L 654.496 144.550 L 677.070 144.550 L 677.070 94.867 L 681.984 94.867 C 685.625 94.867 687.265 96.976 687.265 99.785 L 687.265 144.550 L 709.839 144.550 L 709.839 105.929 C 709.839 93.289 706.015 86.441 694.730 86.441 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 346.671 25.171 L 336.558 106.632 L 326.445 25.171 L 299.082 25.171 L 299.082 144.550 L 317.648 144.550 L 317.648 69.285 L 328.835 144.550 L 344.281 144.550 L 355.468 69.285 L 355.468 144.550 L 374.039 144.550 L 374.039 25.171 Z M 346.671 25.171 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 417.988 99.785 L 412.898 99.785 L 417.988 44.019 L 423.078 99.785 Z M 433.191 25.171 L 402.789 25.171 L 389.5 144.550 L 408.996 144.550 L 411.808 117.339 L 424.167 117.339 L 426.980 144.550 L 446.480 144.550 Z M 433.191 25.171 " />
      <path fillRule="nonzero" fill="#2E55AB" fillOpacity="1" d="M 796.335 187.660 C 786.570 187.660 777.730 184.316 771.328 178.910 C 764.925 184.316 756.082 187.660 746.316 187.660 C 736.550 187.660 727.710 184.316 721.308 178.910 C 714.906 184.316 706.066 187.660 696.296 187.660 C 686.535 187.660 677.691 184.316 671.289 178.910 C 664.886 184.316 656.046 187.660 646.281 187.660 C 636.515 187.660 627.671 184.316 621.269 178.910 C 614.867 184.316 606.027 187.660 596.261 187.660 C 586.496 187.660 577.656 184.316 571.253 178.910 C 564.851 184.316 556.007 187.660 546.242 187.660 C 536.476 187.660 527.636 184.316 521.234 178.910 C 514.832 184.316 505.988 187.660 496.222 187.660 C 486.457 187.660 477.617 184.316 471.214 178.910 C 464.812 184.316 455.968 187.660 446.207 187.660 C 436.441 187.660 427.597 184.316 421.195 178.910 C 414.792 184.316 405.953 187.660 396.187 187.660 C 386.421 187.660 377.582 184.316 371.175 178.910 C 364.773 184.316 355.933 187.660 346.167 187.660 C 336.402 187.660 327.562 184.316 321.160 178.910 C 314.757 184.316 305.914 187.660 296.148 187.660 C 286.382 187.660 277.542 184.316 271.140 178.910 C 264.738 184.316 255.894 187.660 246.128 187.660 C 236.363 187.660 227.523 184.316 221.121 178.910 C 214.718 184.316 205.878 187.660 196.113 187.660 C 186.347 187.660 177.503 184.316 171.101 178.910 C 164.699 184.316 155.859 187.660 146.093 187.660 C 136.328 187.660 127.488 184.316 121.085 178.910 C 114.679 184.316 105.839 187.660 96.074 187.660 C 86.308 187.660 77.468 184.316 71.066 178.910 C 64.664 184.316 55.820 187.660 46.054 187.660 C 39.640 187.660 33.628 186.210 28.441 183.687 L 28.441 197.398 C 33.628 199.921 39.640 201.371 46.054 201.371 C 55.820 201.371 64.664 198.027 71.066 192.621 C 77.468 198.027 86.308 201.371 96.074 201.371 C 105.839 201.371 114.679 198.027 121.085 192.621 C 127.488 198.027 136.328 201.371 146.093 201.371 C 155.859 201.371 164.699 198.027 171.101 192.621 C 177.503 198.027 186.347 201.371 196.113 201.371 C 205.878 201.371 214.718 198.027 221.121 192.621 C 227.523 198.027 236.363 201.371 246.128 201.371 C 255.894 201.371 264.738 198.027 271.140 192.621 C 277.542 198.027 286.382 201.371 296.148 201.371 C 305.914 201.371 314.757 198.027 321.160 192.621 C 327.562 198.027 336.402 201.371 346.167 201.371 C 355.933 201.371 364.773 198.027 371.175 192.621 C 377.582 198.027 386.421 201.371 396.187 201.371 C 405.953 201.371 414.792 198.027 421.195 192.621 C 427.597 198.027 436.441 201.371 446.207 201.371 C 455.968 201.371 464.812 198.027 471.214 192.621 C 477.617 198.027 486.457 201.371 496.222 201.371 C 505.988 201.371 514.832 198.027 521.234 192.621 C 527.636 198.027 536.476 201.371 546.242 201.371 C 556.007 201.371 564.851 198.027 571.253 192.621 C 577.656 198.027 586.496 201.371 596.261 201.371 C 606.027 201.371 614.867 198.027 621.269 192.621 C 627.671 198.027 636.515 201.371 646.281 201.371 C 656.046 201.371 664.886 198.027 671.289 192.621 C 677.691 198.027 686.535 201.371 696.296 201.371 C 706.066 201.371 714.906 198.027 721.308 192.621 C 727.710 198.027 736.550 201.371 746.316 201.371 C 756.082 201.371 764.925 198.027 771.328 192.621 C 777.730 198.027 786.570 201.371 796.335 201.371 C 806.101 201.371 814.941 198.027 821.347 192.621 L 821.347 178.910 C 814.941 184.316 806.101 187.660 796.335 187.660 " />
      <path fillRule="nonzero" fill="#E41F24" fillOpacity="1" d="M 761.109 144.281 L 760.441 131.574 C 760.406 130.800 761.007 130.144 761.785 130.109 C 761.886 130.105 761.988 130.113 762.089 130.128 L 774.839 132.351 L 773.117 127.640 C 772.976 127.253 773.093 126.824 773.414 126.562 L 787.382 115.339 L 784.238 113.882 C 783.796 113.675 783.582 113.179 783.734 112.718 L 786.492 104.296 L 778.449 105.988 C 778 106.082 777.542 105.847 777.363 105.429 L 775.804 101.792 L 769.527 108.480 C 769.160 108.863 768.546 108.878 768.160 108.515 C 767.921 108.289 767.816 107.960 767.878 107.640 L 770.906 92.144 L 766.050 94.929 C 765.593 95.195 765 95.042 764.734 94.585 L 764.703 94.531 L 759.773 84.929 L 754.843 94.531 C 754.605 95.003 754.027 95.195 753.550 94.960 L 753.492 94.929 L 748.640 92.144 L 751.667 107.640 C 751.769 108.156 751.425 108.660 750.902 108.761 C 750.578 108.820 750.246 108.714 750.019 108.480 L 743.742 101.792 L 742.183 105.429 C 742 105.847 741.550 106.082 741.097 105.988 L 733.054 104.296 L 735.816 112.718 C 735.964 113.179 735.75 113.675 735.308 113.882 L 732.164 115.339 L 746.132 126.562 C 746.453 126.824 746.570 127.253 746.429 127.640 L 744.707 132.351 L 757.457 130.128 C 758.226 129.996 758.953 130.507 759.085 131.269 C 759.105 131.371 759.109 131.472 759.105 131.574 L 758.437 144.281 Z M 761.109 144.281 " />
      <path fillRule="nonzero" fill="#2E55AB" fillOpacity="1" d="M 735.800 26.578 C 730.355 26.578 725.921 30.980 725.921 36.390 C 725.921 41.800 730.355 46.203 735.800 46.203 C 741.25 46.203 745.679 41.800 745.679 36.390 C 745.679 30.980 741.25 26.578 735.800 26.578 M 735.800 47.605 C 729.574 47.605 724.507 42.574 724.507 36.390 C 724.507 30.203 729.574 25.171 735.800 25.171 C 742.027 25.171 747.093 30.203 747.093 36.390 C 747.093 42.574 742.027 47.605 735.800 47.605 " />
      <path fillRule="nonzero" fill="#2E55AB" fillOpacity="1" d="M 732.828 31.656 L 732.828 36.218 L 736.015 36.218 C 737.621 36.218 738.566 35.441 738.566 34.007 C 738.566 32.574 737.664 31.656 735.695 31.656 Z M 730.902 42.597 L 730.902 30.183 L 735.718 30.183 C 738.945 30.183 740.531 31.835 740.531 33.988 C 740.531 35.203 739.730 36.417 738.324 36.796 C 739.589 37.117 740.171 38.332 740.351 39.546 L 740.550 41.042 C 740.652 41.601 740.832 42.097 741.171 42.597 L 739.148 42.597 C 738.925 42.359 738.726 41.820 738.605 41.101 L 738.386 39.589 C 738.203 38.273 737.863 37.675 736.179 37.675 L 732.828 37.675 L 732.828 42.597 Z M 730.902 42.597 " />
      <path fillRule="nonzero" fill="#6BCCF2" fillOpacity="1" d="M 753.128 25.171 C 755.070 28.136 756.261 31.625 756.433 35.371 L 811.066 35.371 L 811.066 158.832 C 806.578 160.570 801.593 161.546 796.335 161.546 C 786.570 161.546 777.730 158.203 771.328 152.792 C 764.925 158.203 756.082 161.546 746.316 161.546 C 736.550 161.546 727.710 158.203 721.308 152.792 C 714.906 158.203 706.066 161.546 696.296 161.546 C 686.535 161.546 677.691 158.203 671.289 152.792 C 664.886 158.203 656.046 161.546 646.281 161.546 C 636.515 161.546 627.671 158.203 621.269 152.792 C 614.867 158.203 606.027 161.546 596.261 161.546 C 586.496 161.546 577.656 158.203 571.253 152.792 C 564.851 158.203 556.007 161.546 546.242 161.546 C 536.476 161.546 527.636 158.203 521.234 152.792 C 514.832 158.203 505.988 161.546 496.222 161.546 C 486.457 161.546 477.617 158.203 471.214 152.792 C 464.812 158.203 455.972 161.546 446.207 161.546 C 436.441 161.546 427.597 158.203 421.195 152.792 C 414.792 158.203 405.953 161.546 396.187 161.546 C 386.421 161.546 377.578 158.203 371.175 152.792 C 364.773 158.203 355.933 161.546 346.167 161.546 C 336.402 161.546 327.562 158.203 321.160 152.792 C 314.757 158.203 305.914 161.546 296.148 161.546 C 286.382 161.546 277.542 158.203 271.140 152.792 C 264.738 158.203 255.894 161.546 246.128 161.546 C 236.363 161.546 227.523 158.203 221.121 152.792 C 214.718 158.203 205.878 161.546 196.113 161.546 C 186.347 161.546 177.503 158.203 171.101 152.792 C 164.699 158.203 155.859 161.546 146.093 161.546 C 136.328 161.546 127.488 158.203 121.082 152.792 C 114.679 158.203 105.839 161.546 96.074 161.546 C 86.308 161.546 77.468 158.203 71.066 152.792 C 64.664 158.203 55.820 161.546 46.054 161.546 C 39.640 161.546 33.628 160.097 28.441 157.574 L 28.441 171.285 C 33.628 173.808 39.640 175.257 46.054 175.257 C 55.820 175.257 64.664 171.914 71.066 166.503 C 77.468 171.914 86.308 175.257 96.074 175.257 C 105.839 175.257 114.679 171.914 121.082 166.503 C 127.488 171.914 136.328 175.257 146.093 175.257 C 155.859 175.257 164.699 171.914 171.101 166.503 C 177.503 171.914 186.347 175.257 196.113 175.257 C 205.878 175.257 214.718 171.914 221.121 166.503 C 227.523 171.914 236.363 175.257 246.128 175.257 C 255.894 175.257 264.738 171.914 271.140 166.503 C 277.542 171.914 286.382 175.257 296.148 175.257 C 305.914 175.257 314.757 171.914 321.160 166.503 C 327.562 171.914 336.402 175.257 346.167 175.257 C 355.933 175.257 364.773 171.914 371.175 166.503 C 377.578 171.914 386.421 175.257 396.187 175.257 C 405.953 175.257 414.792 171.914 421.195 166.503 C 427.597 171.914 436.441 175.257 446.207 175.257 C 455.972 175.257 464.812 171.914 471.214 166.503 C 477.617 171.914 486.457 175.257 496.222 175.257 C 505.988 175.257 514.832 171.914 521.234 166.503 C 527.636 171.914 536.476 175.257 546.242 175.257 C 556.007 175.257 564.851 171.914 571.253 166.503 C 577.656 171.914 586.496 175.257 596.261 175.257 C 606.027 175.257 614.867 171.914 621.269 166.503 C 627.671 171.914 636.515 175.257 646.281 175.257 C 656.046 175.257 664.886 171.914 671.289 166.503 C 677.691 171.914 686.535 175.257 696.296 175.257 C 706.066 175.257 714.906 171.914 721.308 166.503 C 727.710 171.914 736.550 175.257 746.316 175.257 C 756.082 175.257 764.925 171.914 771.328 166.503 C 777.730 171.914 786.570 175.257 796.335 175.257 C 806.101 175.257 814.941 171.914 821.347 166.503 L 821.347 25.171 Z M 753.128 25.171 " />
    </svg>
  );
}

// Plain-HTML copy of the logo for print windows (purchase orders etc.).
const LOGO_SVG = `<svg height="56" viewBox="0 0 850.394 226.772" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 61.031 45.886 C 61.031 42.378 59.390 40.621 55.75 40.621 L 50.835 40.621 L 50.835 87.496 L 55.75 87.496 C 59.390 87.496 61.031 85.914 61.031 82.406 Z M 57.207 103.648 L 51.015 103.648 L 51.015 144.550 L 28.441 144.550 L 28.441 25.171 L 57.386 25.171 C 77.050 25.171 83.421 32.195 83.421 46.238 L 83.421 82.578 C 83.421 96.625 76.867 103.648 57.207 103.648 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 132.164 45.210 C 132.164 41.812 131.460 39.265 127.234 39.265 C 123.007 39.265 122.480 41.644 122.480 45.039 L 122.480 124.175 C 122.480 127.570 123.007 129.945 127.234 129.945 C 131.460 129.945 132.164 127.570 132.164 124.175 Z M 127.234 144.550 C 103.992 144.550 100.644 133.851 100.644 118.570 L 100.644 51.152 C 100.644 35.871 103.992 25.171 127.234 25.171 C 150.832 25.171 154 35.871 154 51.152 L 154 118.570 C 154 133.851 150.832 144.550 127.234 144.550 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 203.285 45.210 C 203.285 41.812 202.582 39.265 198.355 39.265 C 194.128 39.265 193.597 41.644 193.597 45.039 L 193.597 124.175 C 193.597 127.570 194.128 129.945 198.355 129.945 C 202.582 129.945 203.285 127.570 203.285 124.175 Z M 198.355 144.550 C 175.109 144.550 171.765 133.851 171.765 118.570 L 171.765 51.152 C 171.765 35.871 175.109 25.171 198.355 25.171 C 221.949 25.171 225.121 35.871 225.121 51.152 L 225.121 118.570 C 225.121 133.851 221.949 144.550 198.355 144.550 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 242.890 144.550 L 242.890 25.171 L 265.464 25.171 L 265.464 127.875 L 283.125 127.875 L 283.125 144.550 Z M 242.890 144.550 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 487.199 144.550 C 464.304 144.550 460.960 133.171 460.960 118.398 L 460.960 97.511 L 482.621 97.511 L 482.621 124.683 C 482.621 128.078 483.148 130.625 487.375 130.625 C 491.601 130.625 491.953 128.078 491.953 124.683 L 491.953 108.550 C 491.953 101.757 488.429 99.210 485.437 96.324 L 471.703 83.078 C 462.195 73.910 461.312 66.945 461.312 53.531 C 461.312 34.683 465.187 25.171 487.550 25.171 C 509.914 25.171 513.437 36.039 513.437 50.644 L 513.437 69.324 L 491.777 69.324 L 491.777 44.531 C 491.777 41.136 491.425 38.585 487.375 38.585 C 483.324 38.585 482.796 40.964 482.796 44.359 L 482.796 58.117 C 482.796 65.757 486.492 68.136 489.312 70.851 L 503.398 84.437 C 512.734 93.437 513.613 100.058 513.613 114.496 C 513.613 134.023 510.089 144.550 487.199 144.550 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 565.597 41.148 L 565.597 144.550 L 543.023 144.550 L 543.023 41.148 L 527.730 41.148 L 527.730 25.171 L 580.710 25.171 L 580.710 41.148 Z M 565.597 41.148 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 595.753 144.550 L 595.753 25.171 L 638.539 25.171 L 638.539 41.148 L 618.332 41.148 L 618.332 74.679 L 637.808 74.679 L 637.808 91.531 L 618.332 91.531 L 618.332 127.875 L 638.539 127.875 L 638.539 144.550 Z M 595.753 144.550 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 687.082 72.398 C 687.082 75.031 685.261 77.136 681.621 77.136 L 676.886 77.136 L 676.886 40.621 L 681.621 40.621 C 685.261 40.621 687.082 42.726 687.082 45.359 Z M 694.730 86.441 L 692.179 86.441 C 700.917 85.562 709.476 81 709.476 65.375 L 709.476 47.117 C 709.476 32.195 703.101 25.171 683.441 25.171 L 654.496 25.171 L 654.496 144.550 L 677.070 144.550 L 677.070 94.867 L 681.984 94.867 C 685.625 94.867 687.265 96.976 687.265 99.785 L 687.265 144.550 L 709.839 144.550 L 709.839 105.929 C 709.839 93.289 706.015 86.441 694.730 86.441 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 346.671 25.171 L 336.558 106.632 L 326.445 25.171 L 299.082 25.171 L 299.082 144.550 L 317.648 144.550 L 317.648 69.285 L 328.835 144.550 L 344.281 144.550 L 355.468 69.285 L 355.468 144.550 L 374.039 144.550 L 374.039 25.171 Z M 346.671 25.171 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 417.988 99.785 L 412.898 99.785 L 417.988 44.019 L 423.078 99.785 Z M 433.191 25.171 L 402.789 25.171 L 389.5 144.550 L 408.996 144.550 L 411.808 117.339 L 424.167 117.339 L 426.980 144.550 L 446.480 144.550 Z M 433.191 25.171 " /> <path fill-rule="nonzero" fill="#2E55AB" fill-opacity="1" d="M 796.335 187.660 C 786.570 187.660 777.730 184.316 771.328 178.910 C 764.925 184.316 756.082 187.660 746.316 187.660 C 736.550 187.660 727.710 184.316 721.308 178.910 C 714.906 184.316 706.066 187.660 696.296 187.660 C 686.535 187.660 677.691 184.316 671.289 178.910 C 664.886 184.316 656.046 187.660 646.281 187.660 C 636.515 187.660 627.671 184.316 621.269 178.910 C 614.867 184.316 606.027 187.660 596.261 187.660 C 586.496 187.660 577.656 184.316 571.253 178.910 C 564.851 184.316 556.007 187.660 546.242 187.660 C 536.476 187.660 527.636 184.316 521.234 178.910 C 514.832 184.316 505.988 187.660 496.222 187.660 C 486.457 187.660 477.617 184.316 471.214 178.910 C 464.812 184.316 455.968 187.660 446.207 187.660 C 436.441 187.660 427.597 184.316 421.195 178.910 C 414.792 184.316 405.953 187.660 396.187 187.660 C 386.421 187.660 377.582 184.316 371.175 178.910 C 364.773 184.316 355.933 187.660 346.167 187.660 C 336.402 187.660 327.562 184.316 321.160 178.910 C 314.757 184.316 305.914 187.660 296.148 187.660 C 286.382 187.660 277.542 184.316 271.140 178.910 C 264.738 184.316 255.894 187.660 246.128 187.660 C 236.363 187.660 227.523 184.316 221.121 178.910 C 214.718 184.316 205.878 187.660 196.113 187.660 C 186.347 187.660 177.503 184.316 171.101 178.910 C 164.699 184.316 155.859 187.660 146.093 187.660 C 136.328 187.660 127.488 184.316 121.085 178.910 C 114.679 184.316 105.839 187.660 96.074 187.660 C 86.308 187.660 77.468 184.316 71.066 178.910 C 64.664 184.316 55.820 187.660 46.054 187.660 C 39.640 187.660 33.628 186.210 28.441 183.687 L 28.441 197.398 C 33.628 199.921 39.640 201.371 46.054 201.371 C 55.820 201.371 64.664 198.027 71.066 192.621 C 77.468 198.027 86.308 201.371 96.074 201.371 C 105.839 201.371 114.679 198.027 121.085 192.621 C 127.488 198.027 136.328 201.371 146.093 201.371 C 155.859 201.371 164.699 198.027 171.101 192.621 C 177.503 198.027 186.347 201.371 196.113 201.371 C 205.878 201.371 214.718 198.027 221.121 192.621 C 227.523 198.027 236.363 201.371 246.128 201.371 C 255.894 201.371 264.738 198.027 271.140 192.621 C 277.542 198.027 286.382 201.371 296.148 201.371 C 305.914 201.371 314.757 198.027 321.160 192.621 C 327.562 198.027 336.402 201.371 346.167 201.371 C 355.933 201.371 364.773 198.027 371.175 192.621 C 377.582 198.027 386.421 201.371 396.187 201.371 C 405.953 201.371 414.792 198.027 421.195 192.621 C 427.597 198.027 436.441 201.371 446.207 201.371 C 455.968 201.371 464.812 198.027 471.214 192.621 C 477.617 198.027 486.457 201.371 496.222 201.371 C 505.988 201.371 514.832 198.027 521.234 192.621 C 527.636 198.027 536.476 201.371 546.242 201.371 C 556.007 201.371 564.851 198.027 571.253 192.621 C 577.656 198.027 586.496 201.371 596.261 201.371 C 606.027 201.371 614.867 198.027 621.269 192.621 C 627.671 198.027 636.515 201.371 646.281 201.371 C 656.046 201.371 664.886 198.027 671.289 192.621 C 677.691 198.027 686.535 201.371 696.296 201.371 C 706.066 201.371 714.906 198.027 721.308 192.621 C 727.710 198.027 736.550 201.371 746.316 201.371 C 756.082 201.371 764.925 198.027 771.328 192.621 C 777.730 198.027 786.570 201.371 796.335 201.371 C 806.101 201.371 814.941 198.027 821.347 192.621 L 821.347 178.910 C 814.941 184.316 806.101 187.660 796.335 187.660 " /> <path fill-rule="nonzero" fill="#E41F24" fill-opacity="1" d="M 761.109 144.281 L 760.441 131.574 C 760.406 130.800 761.007 130.144 761.785 130.109 C 761.886 130.105 761.988 130.113 762.089 130.128 L 774.839 132.351 L 773.117 127.640 C 772.976 127.253 773.093 126.824 773.414 126.562 L 787.382 115.339 L 784.238 113.882 C 783.796 113.675 783.582 113.179 783.734 112.718 L 786.492 104.296 L 778.449 105.988 C 778 106.082 777.542 105.847 777.363 105.429 L 775.804 101.792 L 769.527 108.480 C 769.160 108.863 768.546 108.878 768.160 108.515 C 767.921 108.289 767.816 107.960 767.878 107.640 L 770.906 92.144 L 766.050 94.929 C 765.593 95.195 765 95.042 764.734 94.585 L 764.703 94.531 L 759.773 84.929 L 754.843 94.531 C 754.605 95.003 754.027 95.195 753.550 94.960 L 753.492 94.929 L 748.640 92.144 L 751.667 107.640 C 751.769 108.156 751.425 108.660 750.902 108.761 C 750.578 108.820 750.246 108.714 750.019 108.480 L 743.742 101.792 L 742.183 105.429 C 742 105.847 741.550 106.082 741.097 105.988 L 733.054 104.296 L 735.816 112.718 C 735.964 113.179 735.75 113.675 735.308 113.882 L 732.164 115.339 L 746.132 126.562 C 746.453 126.824 746.570 127.253 746.429 127.640 L 744.707 132.351 L 757.457 130.128 C 758.226 129.996 758.953 130.507 759.085 131.269 C 759.105 131.371 759.109 131.472 759.105 131.574 L 758.437 144.281 Z M 761.109 144.281 " /> <path fill-rule="nonzero" fill="#2E55AB" fill-opacity="1" d="M 735.800 26.578 C 730.355 26.578 725.921 30.980 725.921 36.390 C 725.921 41.800 730.355 46.203 735.800 46.203 C 741.25 46.203 745.679 41.800 745.679 36.390 C 745.679 30.980 741.25 26.578 735.800 26.578 M 735.800 47.605 C 729.574 47.605 724.507 42.574 724.507 36.390 C 724.507 30.203 729.574 25.171 735.800 25.171 C 742.027 25.171 747.093 30.203 747.093 36.390 C 747.093 42.574 742.027 47.605 735.800 47.605 " /> <path fill-rule="nonzero" fill="#2E55AB" fill-opacity="1" d="M 732.828 31.656 L 732.828 36.218 L 736.015 36.218 C 737.621 36.218 738.566 35.441 738.566 34.007 C 738.566 32.574 737.664 31.656 735.695 31.656 Z M 730.902 42.597 L 730.902 30.183 L 735.718 30.183 C 738.945 30.183 740.531 31.835 740.531 33.988 C 740.531 35.203 739.730 36.417 738.324 36.796 C 739.589 37.117 740.171 38.332 740.351 39.546 L 740.550 41.042 C 740.652 41.601 740.832 42.097 741.171 42.597 L 739.148 42.597 C 738.925 42.359 738.726 41.820 738.605 41.101 L 738.386 39.589 C 738.203 38.273 737.863 37.675 736.179 37.675 L 732.828 37.675 L 732.828 42.597 Z M 730.902 42.597 " /> <path fill-rule="nonzero" fill="#6BCCF2" fill-opacity="1" d="M 753.128 25.171 C 755.070 28.136 756.261 31.625 756.433 35.371 L 811.066 35.371 L 811.066 158.832 C 806.578 160.570 801.593 161.546 796.335 161.546 C 786.570 161.546 777.730 158.203 771.328 152.792 C 764.925 158.203 756.082 161.546 746.316 161.546 C 736.550 161.546 727.710 158.203 721.308 152.792 C 714.906 158.203 706.066 161.546 696.296 161.546 C 686.535 161.546 677.691 158.203 671.289 152.792 C 664.886 158.203 656.046 161.546 646.281 161.546 C 636.515 161.546 627.671 158.203 621.269 152.792 C 614.867 158.203 606.027 161.546 596.261 161.546 C 586.496 161.546 577.656 158.203 571.253 152.792 C 564.851 158.203 556.007 161.546 546.242 161.546 C 536.476 161.546 527.636 158.203 521.234 152.792 C 514.832 158.203 505.988 161.546 496.222 161.546 C 486.457 161.546 477.617 158.203 471.214 152.792 C 464.812 158.203 455.972 161.546 446.207 161.546 C 436.441 161.546 427.597 158.203 421.195 152.792 C 414.792 158.203 405.953 161.546 396.187 161.546 C 386.421 161.546 377.578 158.203 371.175 152.792 C 364.773 158.203 355.933 161.546 346.167 161.546 C 336.402 161.546 327.562 158.203 321.160 152.792 C 314.757 158.203 305.914 161.546 296.148 161.546 C 286.382 161.546 277.542 158.203 271.140 152.792 C 264.738 158.203 255.894 161.546 246.128 161.546 C 236.363 161.546 227.523 158.203 221.121 152.792 C 214.718 158.203 205.878 161.546 196.113 161.546 C 186.347 161.546 177.503 158.203 171.101 152.792 C 164.699 158.203 155.859 161.546 146.093 161.546 C 136.328 161.546 127.488 158.203 121.082 152.792 C 114.679 158.203 105.839 161.546 96.074 161.546 C 86.308 161.546 77.468 158.203 71.066 152.792 C 64.664 158.203 55.820 161.546 46.054 161.546 C 39.640 161.546 33.628 160.097 28.441 157.574 L 28.441 171.285 C 33.628 173.808 39.640 175.257 46.054 175.257 C 55.820 175.257 64.664 171.914 71.066 166.503 C 77.468 171.914 86.308 175.257 96.074 175.257 C 105.839 175.257 114.679 171.914 121.082 166.503 C 127.488 171.914 136.328 175.257 146.093 175.257 C 155.859 175.257 164.699 171.914 171.101 166.503 C 177.503 171.914 186.347 175.257 196.113 175.257 C 205.878 175.257 214.718 171.914 221.121 166.503 C 227.523 171.914 236.363 175.257 246.128 175.257 C 255.894 175.257 264.738 171.914 271.140 166.503 C 277.542 171.914 286.382 175.257 296.148 175.257 C 305.914 175.257 314.757 171.914 321.160 166.503 C 327.562 171.914 336.402 175.257 346.167 175.257 C 355.933 175.257 364.773 171.914 371.175 166.503 C 377.578 171.914 386.421 175.257 396.187 175.257 C 405.953 175.257 414.792 171.914 421.195 166.503 C 427.597 171.914 436.441 175.257 446.207 175.257 C 455.972 175.257 464.812 171.914 471.214 166.503 C 477.617 171.914 486.457 175.257 496.222 175.257 C 505.988 175.257 514.832 171.914 521.234 166.503 C 527.636 171.914 536.476 175.257 546.242 175.257 C 556.007 175.257 564.851 171.914 571.253 166.503 C 577.656 171.914 586.496 175.257 596.261 175.257 C 606.027 175.257 614.867 171.914 621.269 166.503 C 627.671 171.914 636.515 175.257 646.281 175.257 C 656.046 175.257 664.886 171.914 671.289 166.503 C 677.691 171.914 686.535 175.257 696.296 175.257 C 706.066 175.257 714.906 171.914 721.308 166.503 C 727.710 171.914 736.550 175.257 746.316 175.257 C 756.082 175.257 764.925 171.914 771.328 166.503 C 777.730 171.914 786.570 175.257 796.335 175.257 C 806.101 175.257 814.941 171.914 821.347 166.503 L 821.347 25.171 Z M 753.128 25.171 " /> </svg>`;

function Meter({ n, size = 34, dark, good, stops }) {
  const ramp = stops || (good ? RATING_STOPS : CX_STOPS);
  const v = Math.max(1, Math.min(10, Number(n) || 1));
  const pct = (v - 1) / 9;
  const cx = 20, cy = 21, r = 15;
  const a = Math.PI * (1 - pct);
  const nx = cx + (r - 4.5) * Math.cos(a), ny = cy - (r - 4.5) * Math.sin(a);
  const pt = (deg) => ({ x: +(cx + r * Math.cos((deg * Math.PI) / 180)).toFixed(2), y: +(cy - r * Math.sin((deg * Math.PI) / 180)).toFixed(2) });
  return (
    <svg width={size} height={size * 0.65} viewBox="0 0 40 26" aria-hidden="true" style={{ display: "block" }}>
      {ramp.map((col, i) => {
        const a0 = 180 - i * (180 / ramp.length), a1 = 180 - (i + 1) * (180 / ramp.length);
        const p0 = pt(a0 - 1.4), p1 = pt(a1 + 1.4);
        const live = i === Math.min(ramp.length - 1, Math.floor(pct * ramp.length + 0.0001));
        return (
          <path key={i} d={`M${p0.x} ${p0.y} A15 15 0 0 1 ${p1.x} ${p1.y}`} fill="none" stroke={col}
            strokeWidth={live ? 7.6 : 5.4} strokeLinecap="butt" />
        );
      })}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={dark ? "#0B2545" : "#FFFFFF"} strokeWidth="3.4" strokeLinecap="round" opacity="0.9" />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={dark ? "#FFFFFF" : "#17356E"} strokeWidth="1.9" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="2.6" fill={dark ? "#FFFFFF" : "#17356E"} />
    </svg>
  );
}

function Rating({ n, showWord, size, dark }) {
  const b = ratingBand(n);
  const col = ratingColour(n);
  return (
    <span className="cx rating" data-dark={dark ? 1 : 0}>
      <Meter n={n} size={size || (showWord ? 48 : 32)} dark={dark} good stops={RATING_STOPS} />
      <b style={{ color: col }}>{n}/10</b>
      {showWord && <span style={{ fontWeight: 700, color: col }}>{ratingWord[b]}</span>}
    </span>
  );
}

function Complexity({ n, showWord, size, dark }) {
  const step = cxStep(n);
  return (
    <span className="cx rating" data-dark={dark ? 1 : 0}>
      <Meter n={n} size={size || (showWord ? 48 : 32)} dark={dark} stops={CX_STOPS} />
      <b style={{ color: step.colour }}>{n}/10</b>
      {showWord && <span style={{ fontWeight: 700, color: step.colour }}>{step.word}</span>}
    </span>
  );
}

const SERVER = serverMode();


// Password input with a tap-to-reveal eye — used on every password field.
function EyeIcon({ off, size = 18 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <path {...P} d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle {...P} cx="12" cy="12" r="3" />
      {off && <path {...P} d="M4.5 19.5 19.5 4.5" />}
    </svg>
  );
}

function PwInput({ value, onChange, autoComplete, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input type={show ? "text" : "password"} autoComplete={autoComplete} autoFocus={autoFocus}
        value={value} onChange={onChange} style={{ paddingRight: 44 }} />
      <button type="button" aria-label={show ? "Hide password" : "Show password"} title={show ? "Hide password" : "Show password"}
        onClick={() => setShow(!show)}
        style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: 0, background: "transparent", cursor: "pointer", padding: 6, color: "var(--muted)", lineHeight: 1 }}>
        <EyeIcon off={show} />
      </button>
    </div>
  );
}

function SupabaseSignIn() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin"); // signin | forgot | sent
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: pw });
    setBusy(false);
    if (error) setErr(error.message || "Sign in failed.");
  };
  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin });
    setBusy(false);
    if (error) setErr(error.message || "Couldn't send the reset email — try again in a minute.");
    else setMode("sent");
  };
  return (
    <div className="pd">
      <style>{CSS}</style>
      <form className="panel" style={{ maxWidth: 400, margin: "60px auto", padding: 24, textAlign: "center" }}
        onSubmit={mode === "forgot" ? sendReset : submit}>
        <div style={{ display: "inline-block", marginBottom: 16 }}><PoolMasterLogo height={40} /></div>
        <div style={{ fontWeight: 750, fontSize: 15, color: "var(--ink)", marginBottom: 2 }}>
          {mode === "signin" ? "Sign in" : "Reset your password"}
        </div>
        <div className="sub" style={{ marginBottom: 16 }}>Poolmaster Canada Service Desk</div>

        {mode === "sent" ? (
          <>
            <div className="notice" style={{ textAlign: "left", marginBottom: 14 }}>
              Check your email — a password-reset link was sent to <b>{email.trim()}</b>. Open it on this device, pick a new password, and you'll be signed straight in. (No email? Check junk, or wait a minute and try again.)
            </div>
            <button type="button" className="link" onClick={() => { setMode("signin"); setErr(""); }}>‹ Back to sign in</button>
          </>
        ) : (
          <>
            <div style={{ textAlign: "left", marginBottom: 10 }}>
              <label>Email</label>
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {mode === "signin" && (
              <div style={{ textAlign: "left", marginBottom: 14 }}>
                <label>Password</label>
                <PwInput autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} />
              </div>
            )}
            {err && <div className="alert" style={{ marginBottom: 12 }}>{err}</div>}
            <button className="btn" type="submit" disabled={busy || !email || (mode === "signin" && !pw)} style={{ width: "100%", justifyContent: "center" }}>
              {busy ? (mode === "forgot" ? "Sending…" : "Signing in…") : (mode === "forgot" ? "Email me a reset link" : "Sign in")}
            </button>
            <div style={{ marginTop: 12 }}>
              {mode === "signin"
                ? <button type="button" className="link" onClick={() => { setMode("forgot"); setErr(""); }}>Forgot password?</button>
                : <button type="button" className="link" onClick={() => { setMode("signin"); setErr(""); }}>‹ Back to sign in</button>}
            </div>
          </>
        )}
      </form>
    </div>
  );
}

// Shown from a password-reset email, and ONCE on first sign-in with a temporary
// password (welcome mode) — the employee picks their own password to finish setup.
function SetNewPassword({ onDone, welcome }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (p1.length < 8) { setErr("Use at least 8 characters."); return; }
    if (p1 !== p2) { setErr("The two passwords don't match."); return; }
    setBusy(true); setErr("");
    const { error } = await supabase.auth.updateUser({ password: p1, data: { must_set_password: false } });
    setBusy(false);
    if (error) setErr(error.message || "Couldn't set the password — try again.");
    else onDone();
  };
  return (
    <div className="pd">
      <style>{CSS}</style>
      <form className="panel" style={{ maxWidth: 400, margin: "60px auto", padding: 24, textAlign: "center" }} onSubmit={submit}>
        <div style={{ display: "inline-block", marginBottom: 16 }}><PoolMasterLogo height={40} /></div>
        <div style={{ fontWeight: 750, fontSize: 15, color: "var(--ink)", marginBottom: 2 }}>{welcome ? "Welcome! Set your password" : "Choose a new password"}</div>
        <div className="sub" style={{ marginBottom: 16 }}>{welcome ? "One-time setup — pick the password you'll sign in with from now on" : "You're almost back in"}</div>
        <div style={{ textAlign: "left", marginBottom: 10 }}>
          <label>New password (8+ characters)</label>
          <PwInput autoComplete="new-password" value={p1} onChange={(e) => setP1(e.target.value)} />
        </div>
        <div style={{ textAlign: "left", marginBottom: 14 }}>
          <label>New password again</label>
          <PwInput autoComplete="new-password" value={p2} onChange={(e) => setP2(e.target.value)} />
        </div>
        {err && <div className="alert" style={{ marginBottom: 12 }}>{err}</div>}
        <button className="btn" type="submit" disabled={busy || !p1 || !p2} style={{ width: "100%", justifyContent: "center" }}>
          {busy ? "Saving…" : "Save password and sign in"}
        </button>
      </form>
    </div>
  );
}

function ServerGate() {
  const [session, setSession] = useState(undefined); // undefined = still checking
  const [recovery, setRecovery] = useState(false); // arrived from a password-reset email
  useEffect(() => {
    let sub;
    supabase.auth.getSession().then(({ data }) => setSession(data.session || null));
    sub = supabase.auth.onAuthStateChange((ev, s) => {
      if (ev === "PASSWORD_RECOVERY") setRecovery(true);
      setSession(s);
    }).data.subscription;
    return () => { if (sub) sub.unsubscribe(); };
  }, []);

  // 88 minutes with no interaction → signed out (owner's rule, 08/19 — long
  // enough to cover a real service stop, short enough that a forgotten screen
  // locks itself within the afternoon). Any tap,
  // keystroke, or scroll resets the clock; active people are never kicked.
  // Keyed on !!session so hourly token refreshes don't restart the timer.
  const signedIn = !!session;
  useEffect(() => {
    if (!signedIn) return;
    const IDLE_MS = 88 * 60 * 1000;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const check = () => { if (Date.now() - last > IDLE_MS) supabase.auth.signOut(); };
    const evs = ["pointerdown", "keydown", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    document.addEventListener("visibilitychange", check); // phone woken after a long sit → out immediately
    const t = setInterval(check, 30000);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener("visibilitychange", check);
      clearInterval(t);
    };
  }, [signedIn]);

  if (session === undefined) return (
    <div className="pd"><style>{CSS}</style>
      <div className="panel" style={{ maxWidth: 360, margin: "80px auto", padding: 24, textAlign: "center" }}>Connecting to the server…</div>
    </div>
  );
  if (!session) return <SupabaseSignIn />;
  if (recovery) return <SetNewPassword onDone={() => setRecovery(false)} />;
  if (session.user && session.user.user_metadata && session.user.user_metadata.must_set_password) {
    return <SetNewPassword welcome onDone={() => setSession({ ...session, user: { ...session.user, user_metadata: { ...session.user.user_metadata, must_set_password: false } } })} />;
  }
  return <PoolDesk serverEmail={session.user.email} serverUserId={session.user.id} />;
}

// While the app is open, quietly stamp "I'm here" once a minute so the owner's
// Employee Profiles screen can show who is in the app right now.
function usePresenceHeartbeat(userId, email) {
  useEffect(() => {
    if (!userId || !supabase) return;
    const beat = () => {
      if (document.visibilityState === "hidden") return;
      supabase.from("presence").upsert({ user_id: userId, email: (email || "").toLowerCase(), last_seen: new Date().toISOString() }).then(() => {});
    };
    beat();
    const t = setInterval(beat, 60 * 1000);
    document.addEventListener("visibilitychange", beat);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", beat); };
  }, [userId, email]);
}

export default function App() {
  return SERVER ? <ServerGate /> : <PoolDesk />;
}

function PoolDesk({ serverEmail, serverUserId }) {
  usePresenceHeartbeat(serverUserId, serverEmail);
  // Activity breadcrumb: screens + tagged clicks only — never what anyone types.
  useEffect(() => { startTrail(serverUserId); }, [serverUserId]);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [view, setView] = useState(() => (parseRepairTicketFromLocation() ? "repairs" : "clients"));
  // Global back: remembers the pages you came through (top-left button below the menu).
  const [navHist, setNavHist] = useState([]);
  const prevViewRef = useRef(parseRepairTicketFromLocation() ? "repairs" : "clients");
  useEffect(() => {
    if (prevViewRef.current !== view) {
      const from = prevViewRef.current; // capture BEFORE advancing — the updater runs later
      prevViewRef.current = view;
      setNavHist((h) => [...h, from].slice(-20));
    }
  }, [view]);
  const [selId, setSelId] = useState(null);
  useEffect(() => { trailNav(view === "clients" && selId ? "clients/#" + selId : view); }, [view, selId]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [photos, setPhotos] = useState({});
  const [visits, setVisits] = useState({});
  const [tests, setTests] = useState({});
  const [quotes, setQuotes] = useState({});
  const [testsOpen, setTestsOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [visitsOpen, setVisitsOpen] = useState(false);
  const [visitAutoNew, setVisitAutoNew] = useState(false);
  const [bookSeed, setBookSeed] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [woAreaAsk, setWoAreaAsk] = useState(null); // service-perimeter gate on the work-order path
  const [mydayJump, setMydayJump] = useState(null); // open this job's guided runner when My Day mounts
  const [repairJump, setRepairJump] = useState(() => parseRepairTicketFromLocation() || null); // printed QR #repair=RP-12 → open that ticket
  const repairQrConsumed = useRef(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [retailTab, setRetailTab] = useState("pos");
  const [approvedOpen, setApprovedOpen] = useState(false);
  const [quotesListOpen, setQuotesListOpen] = useState(false);
  const [quoteFocusId, setQuoteFocusId] = useState(null);
  const [quoteAutoNew, setQuoteAutoNew] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [tiersOpen, setTiersOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [salesLoading, setSalesLoading] = useState(false);
  const [cardOpen, setCardOpen] = useState(null);
  const [schedMode, setSchedMode] = useState("day");
  const [compose, setCompose] = useState(null);
  const [staffId, setStaffId] = useState(null);
  const [clockOpen, setClockOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [msgsOpen, setMsgsOpen] = useState(false);
  const [fuReminders, setFuReminders] = useState(null); // null = not checked yet this sign-in
  const [textsOpen, setTextsOpen] = useState(false);
  const [smsInbox, setSmsInbox] = useState([]);
  const [smsSent, setSmsSent] = useState([]);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  const [flagEdit, setFlagEdit] = useState(false);
  const [navSide, setNavSide] = useState(() => { try { return localStorage.getItem("pm_navside") || "top"; } catch { return "top"; } });
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);
  const flipNav = () => { const v = navSide === "right" ? "top" : "right"; setNavSide(v); try { localStorage.setItem("pm_navside", v); } catch {} };
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("all");
  const [fCx, setFCx] = useState("all");
  const [fDay, setFDay] = useState("all");

  const [bootTries, setBootTries] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      // "No such key" = the store is genuinely empty. Anything else is a network /
      // server hiccup — retry forever rather than fall back to demo data, because a
      // session running on the demo seed could save junk over the real database.
      const storeEmpty = (e) => String((e && e.message) || e).startsWith("No such key");
      let loaded = null;
      for (let attempt = 0; alive && !loaded; attempt++) {
        try {
          loaded = migrate(JSON.parse((await window.storage.get(STORE_KEY)).value));
        } catch (e) {
          if (storeEmpty(e)) {
            for (const key of ["pooldesk:data:v4", "pooldesk:data:v3", "pooldesk:data:v2", "pooldesk:data:v1"]) {
              try { loaded = migrate(JSON.parse((await window.storage.get(key)).value)); break; } catch {}
            }
            if (!loaded) { loaded = SEED; setVisits(SEED_VISITS); setTests(SEED_TESTS); }
            break;
          }
          if (!alive) return;
          setBootTries(attempt + 1);
          await new Promise((r) => setTimeout(r, Math.min(1500 * (attempt + 1), 6000)));
        }
      }
      if (!alive || !loaded) return;
      setData(loaded);
      setSelId(null); // land on the clean search screen — no customer auto-opened
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!selId) return;
    if (photos[selId] === undefined) {
      (async () => {
        let list = [];
        try { list = JSON.parse((await window.storage.get(PHOTO_KEY(selId))).value); } catch { list = []; }
        setPhotos((p) => ({ ...p, [selId]: list }));
      })();
    }
    if (visits[selId] === undefined) {
      (async () => {
        let list = [];
        try { list = JSON.parse((await window.storage.get(VISIT_KEY(selId))).value); } catch { list = []; }
        setVisits((v) => ({ ...v, [selId]: list }));
      })();
    }
    if (tests[selId] === undefined) {
      (async () => {
        let list = [];
        try { list = JSON.parse((await window.storage.get(TEST_KEY(selId))).value); } catch { list = []; }
        setTests((t) => ({ ...t, [selId]: list }));
      })();
    }
    if (quotes[selId] === undefined) {
      (async () => {
        let list = [];
        try { list = JSON.parse((await window.storage.get(QUOTE_KEY(selId))).value); } catch { list = []; }
        setQuotes((qm) => ({ ...qm, [selId]: list }));
      })();
    }
  }, [selId]);

  useEffect(() => {
    if (!(view === "sales" || view === "ar" || view === "offbooks") || !data) return;
    const missing = (data.clients || []).filter((c) => visits[c.id] === undefined);
    if (!missing.length) { setSalesLoading(false); return; }
    setSalesLoading(true);
    (async () => {
      const found = {};
      for (const c of missing) {
        try { found[c.id] = JSON.parse((await window.storage.get(VISIT_KEY(c.id))).value); } catch { found[c.id] = []; }
      }
      setVisits((v) => ({ ...v, ...found }));
      setSalesLoading(false);
    })();
  }, [view, data]);

  // Load every client's quotes once, so the manager's "approved quotes" count is live.
  useEffect(() => {
    if (!data) return;
    const missing = (data.clients || []).filter((c) => quotes[c.id] === undefined);
    if (!missing.length) return;
    (async () => {
      const found = {};
      for (const c of missing) {
        try { found[c.id] = JSON.parse((await window.storage.get(QUOTE_KEY(c.id))).value); } catch { found[c.id] = []; }
      }
      setQuotes((qm) => ({ ...qm, ...found }));
    })();
  }, [data]);

  const save = async (next) => {
    setData(next);
    try { await window.storage.set(STORE_KEY, JSON.stringify(next)); setStatus(""); }
    catch { setStatus("Changes are showing here but didn't save. Try again in a moment."); }
  };

  // Download every stored key as one JSON file — a restore point before any migration.
  const downloadBackup = async () => {
    try {
      const { keys } = await window.storage.list("");
      const dump = {};
      for (const k of keys) { try { dump[k] = (await window.storage.get(k)).value; } catch {} }
      const payload = JSON.stringify({ app: "poolmaster", version: STORE_KEY, at: new Date().toISOString(), data: dump }, null, 2);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
      a.download = `poolmaster-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(a.href);
      setStatus("");
    } catch { setStatus("Couldn't build the backup. Try again in a moment."); }
  };
  const [serverOpen, setServerOpen] = useState(false);
  // One-time copy of this browser's data up to the shared server (needs a signed-in session).
  const importToServer = async () => {
    if (!supabase) return;
    try {
      const rows = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("pooldesk:") && k !== "pooldesk:backend") rows.push({ key: k, val: localStorage.getItem(k), updated_at: new Date().toISOString() });
      }
      for (let i = 0; i < rows.length; i += 100) {
        const { error } = await supabase.from("kv_store").upsert(rows.slice(i, i + 100), { onConflict: "key" });
        if (error) throw error;
      }
      window.alert(`Sent ${rows.length} items to the server. The app will reload.`);
      window.location.reload();
    } catch (e) { setStatus("Import failed: " + (e.message || e)); }
  };
  const goShared = () => { setBackendMode("supabase"); window.location.reload(); };
  const goLocal = () => { setBackendMode("local"); window.location.reload(); };

  const restoreRef = useRef(null);
  const restoreBackup = async (file) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const dump = parsed.data || parsed;
      if (!dump || typeof dump !== "object") throw new Error("shape");
      if (!window.confirm(`Restore this backup? It overwrites the data in THIS browser with ${Object.keys(dump).length} saved items.`)) return;
      for (const [k, v] of Object.entries(dump)) { await window.storage.set(k, v); }
      window.location.reload();
    } catch { setStatus("That file didn't look like a Pool Master backup."); }
  };

  // In shared mode, photos live as FILES in the private storage bucket — the database
  // keeps only the path. Uploads happen here; failures keep the base64 so nothing is lost.
  const uploadPhotoIfNeeded = async (clientId, p) => {
    if (!SERVER || !supabase || !p.dataUrl || !String(p.dataUrl).startsWith("data:")) return p;
    try {
      const blob = await (await fetch(p.dataUrl)).blob();
      const path = `${clientId}/${p.id}.jpg`;
      const { error } = await supabase.storage.from("photos").upload(path, blob, { contentType: blob.type || "image/jpeg", upsert: true });
      if (error) return p;
      const { dataUrl, ...rest } = p;
      return { ...rest, path };
    } catch { return p; }
  };
  const savePhotos = async (clientId, list) => {
    const next = [];
    for (const p of list) next.push(await uploadPhotoIfNeeded(clientId, p));
    const stored = next.map(({ signedUrl, ...rest }) => rest); // signed links are never persisted
    const payload = JSON.stringify(stored);
    if (payload.length > MAX_PHOTO_BYTES) {
      setStatus("That's more photos than this client can hold. Delete a few older ones first."); return false;
    }
    setPhotos((p) => ({ ...p, [clientId]: next }));
    try { await window.storage.set(PHOTO_KEY(clientId), payload); setStatus(""); return true; }
    catch { setStatus("The photo is showing here but didn't save. Try again in a moment."); return false; }
  };
  // Resolve private-bucket paths into short-lived viewing links (kept out of storage).
  useEffect(() => {
    if (!SERVER || !supabase) return;
    const need = [];
    for (const list of Object.values(photos)) for (const p of list || []) { if (p.path && !p.signedUrl) need.push(p.path); }
    if (!need.length) return;
    (async () => {
      const { data, error } = await supabase.storage.from("photos").createSignedUrls([...new Set(need)], 60 * 60 * 24 * 6);
      if (error || !data) return;
      const urlFor = {};
      data.forEach((d) => { if (d.signedUrl) urlFor[d.path] = d.signedUrl; });
      if (!Object.keys(urlFor).length) return;
      setPhotos((m) => {
        const out = {};
        for (const [k, list] of Object.entries(m)) out[k] = (list || []).map((p) => (p.path && urlFor[p.path] && !p.signedUrl ? { ...p, signedUrl: urlFor[p.path] } : p));
        return out;
      });
    })();
  }, [photos]); // eslint-disable-line

  const saveVisits = async (clientId, list) => {
    // Assign invoice numbers to new billed invoices (single shared series). Gifted visits
    // have amount 0, so they get no invoice number — they're documented, not billed.
    let nb = { ...defNumbering(), ...(data?.numbering || {}) };
    let numberingChanged = false;
    const numbered = list.map((v) => {
      if ((v.invoiceNo && String(v.invoiceNo).trim()) || !(num(v.amount) > 0)) return v;
      const n = nb.inv; nb = { ...nb, inv: n + 1 }; numberingChanged = true;
      return { ...v, invoiceNo: fmtInvoice(n) };
    });
    const sorted = [...numbered].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    setVisits((v) => ({ ...v, [clientId]: sorted }));
    try { await window.storage.set(VISIT_KEY(clientId), JSON.stringify(sorted)); }
    catch { setStatus("That visit is showing here but didn't save. Try again in a moment."); return; }
    const lastService = (sorted.find((v) => visitSide(v) === "service") || {}).date || "";
    const lastRetail = (sorted.find((v) => visitSide(v) === "retail") || {}).date || "";
    // Move inventory: compare catalogue-part quantities used across this client's visits, before vs after.
    // Idempotent — re-saving an unchanged visit moves nothing; deleting a visit restores its stock.
    const committed = (arr) => {
      const map = {};
      (arr || []).forEach((v) => (v.materials || []).forEach((m) => {
        const code = (m.code || "").trim().toLowerCase();
        if (code) map[code] = (map[code] || 0) + (num(m.qty) || 0);
      }));
      return map;
    };
    const before = committed(visits[clientId]);
    const after = committed(sorted);
    const codes = new Set([...Object.keys(before), ...Object.keys(after)]);
    const deltaItems = [...codes].map((code) => ({ code, qty: (after[code] || 0) - (before[code] || 0) })).filter((x) => x.qty);
    const eff = bulkRoute(data?.parts, deltaItems); // bulk-linked items drain their container part
    let stockChanged = false;
    const nextParts = (data?.parts || []).map((p) => {
      const code = (p.code || "").trim().toLowerCase();
      if (!code || !eff[code]) return p;
      stockChanged = true;
      return { ...p, qty: String(num(p.qty) - eff[code]) };
    });
    const c = (data?.clients || []).find((x) => x.id === clientId);
    const dateChanged = c && (c.lastService !== lastService || c.lastRetail !== lastRetail);
    // A service invoice on a retail-only account (or a retail invoice on a service-only account)
    // upgrades the customer to Retail + Service.
    const hasService = sorted.some((v) => visitSide(v) === "service");
    const hasRetail = sorted.some((v) => visitSide(v) === "retail");
    const newType = c ? (c.type === "retail" && hasService ? "both" : c.type === "service" && hasRetail ? "both" : c.type) : undefined;
    const typeChanged = c && newType !== c.type;
    if (dateChanged || stockChanged || numberingChanged || typeChanged) {
      save({
        ...data,
        numbering: nb,
        parts: stockChanged ? nextParts : data.parts,
        clients: (dateChanged || typeChanged)
          ? data.clients.map((x) => (x.id === clientId ? { ...x, lastService, lastRetail, type: newType } : x))
          : data.clients,
      });
    }
  };

  const saveTests = async (clientId, list) => {
    if (!waterTestSaveGate({ clientId }, { id: clientId }).ok) {
      setStatus("Water tests need a named customer — pick an account first, even on rush days.");
      return;
    }
    // New tests are stamped with the full name of whoever recorded them.
    const prev = tests[clientId] || [];
    const stamped = list.map((t) => (prev.some((p) => p.id === t.id) ? t : { ...t, by: t.by || (me ? me.name : "") }));
    const sorted = [...stamped].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    setTests((t) => ({ ...t, [clientId]: sorted }));
    try { await window.storage.set(TEST_KEY(clientId), JSON.stringify(sorted)); }
    catch { setStatus("That test is showing here but didn't save. Try again in a moment."); return; }
    const latest = sorted[0]?.date || "";
    const c = (data?.clients || []).find((x) => x.id === clientId);
    // Algae on a test that didn't have it before alerts service management and the owner.
    const algaeIn = (t) => !!t.algae || /algae/i.test(t.notes || "");
    const fresh = sorted.filter((t) => algaeIn(t) && !prev.some((p) => p.id === t.id && algaeIn(p)));
    let messages = data?.messages;
    if (fresh.length && c) {
      const to = staff.filter((s) => s.active !== false && ["owner", "manager"].includes(s.roleId) && (!me || s.id !== me.id)).map((s) => s.id);
      if (to.length) {
        messages = [...(data.messages || []), {
          id: cid(), from: me ? me.id : "", to,
          body: `⚠ Algae reported at ${c.name} — water test ${fresh[0].date}${me ? ", entered by " + me.name : ""}. Stay on top of this property and track why.`,
          at: new Date().toISOString(), read: [],
        }].slice(-400);
      }
      setStatus("Algae flagged — service management and owner notified.");
    }
    if (c && (c.lastWaterTest !== latest || messages !== data?.messages)) {
      save({ ...data, messages, clients: data.clients.map((x) => (x.id === clientId ? { ...x, lastWaterTest: latest } : x)) });
    }
  };

  const saveQuotes = async (clientId, list) => {
    const sorted = [...list].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    setQuotes((qm) => ({ ...qm, [clientId]: sorted }));
    try { await window.storage.set(QUOTE_KEY(clientId), JSON.stringify(sorted)); }
    catch { setStatus("That quote is showing here but didn't save. Try again in a moment."); }
  };

  // Move catalogue stock for a set of sale items. dir -1 removes (a sale), +1 adds back (a return).
  // Bulk-linked items (10L/20L fills, LC per litre) come off their container part instead.
  const partsAfterMove = (baseParts, items, dir) => {
    const eff = bulkRoute(baseParts, items);
    return (baseParts || []).map((p) => {
      const code = (p.code || "").trim().toLowerCase();
      if (!code || !eff[code]) return p;
      return { ...p, qty: String(num(p.qty) + dir * eff[code]) };
    });
  };

  // Record a completed counter sale or return: move stock, log it, post to account if needed — one save.
  // Auto-order: any part flagged autoOrder that just crossed down to its reorder level
  // gets its approved amount added to the holding purchase order ("to be ordered").
  const withAutoOrders = (prevParts, nextParts, poList) => {
    let pos = poList;
    for (const p of nextParts) {
      const prev = (prevParts || []).find((x) => x.id === p.id);
      if (!prev || !p.autoOrder || p.active === false) continue;
      if (p.lowAt === "" || p.lowAt == null) continue;
      if (!(num(prev.qty) > num(p.lowAt) && num(p.qty) <= num(p.lowAt))) continue; // crossed just now
      const item = { code: p.code || "", name: p.name || "", qty: num(p.reorderQty) > 0 ? num(p.reorderQty) : 1, cost: p.cost || "" };
      // Route to the open PO for this part's Supplier #1 — or start one for that supplier.
      const sup = (p.supplier1 || "").trim();
      const holding = pos.find((x) => x.status === "holding" && (x.supplier || "").trim().toLowerCase() === sup.toLowerCase());
      if (!holding) {
        pos = [...pos, { id: cid(), poNo: "", status: "holding", supplier: sup, notes: "",
          items: [item], createdBy: "Auto — low stock", createdDate: fmtDate(new Date()) }];
      } else if (!holding.items.some((it) => it.code && item.code && it.code === item.code)) {
        pos = pos.map((x) => (x.id === holding.id ? { ...x, items: [...x.items, item] } : x));
      }
    }
    return pos;
  };

  const recordPosSale = (sale) => {
    const dir = sale.type === "return" ? +1 : -1;
    const parts = partsAfterMove(data.parts, sale.items, dir);
    const purchaseOrders = withAutoOrders(data.parts, parts, data.purchaseOrders || []);
    // A retail sale on a service-only account upgrades them to Retail + Service.
    let clients = sale.type !== "return" && sale.customerId
      ? (data.clients || []).map((c) => (c.id === sale.customerId && c.type === "service" ? { ...c, type: "both" } : c))
      : data.clients;
    // In-store credit returns land on the customer's credit balance; store-credit sales spend it.
    if (sale.type === "return" && sale.returnType === "credit" && sale.customerId) {
      clients = clients.map((c) => (c.id === sale.customerId
        ? withStoreCredit(c, num(sale.total), { id: cid(), date: sale.date, amount: num(sale.total), by: sale.by || "", note: `In-store credit issued — return${sale.refFrom ? " · " + sale.refFrom : ""}` })
        : c));
    }
    if (sale.type !== "return" && sale.method === "storecredit" && sale.customerId) {
      clients = clients.map((c) => (c.id === sale.customerId
        ? withStoreCredit(c, -num(sale.total), { id: cid(), date: sale.date, amount: -num(sale.total), by: sale.by || "", note: "Store credit spent at the till" })
        : c));
    }
    let posSales = [...(data.posSales || []), sale].slice(-2000);
    // A refund is stamped onto the original sale too — who processed it, how, and who authorized it.
    if (sale.type === "return" && sale.refSaleId) {
      posSales = posSales.map((s) => (s.id === sale.refSaleId
        ? { ...s, refundLog: [...(s.refundLog || []), { id: cid(), date: sale.date, by: sale.by || "", total: sale.total, returnType: sale.returnType || "refund", manager: (sale.override || {}).managerName || "" }] } : s));
    }
    // Completing a recalled held ticket settles any special order that was waiting on it.
    let specialOrders = data.specialOrders;
    if (sale.type !== "return" && sale.fromHeldId) {
      const now = new Date();
      const hhmm = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
      specialOrders = (data.specialOrders || []).map((o) => (o.heldSaleId === sale.fromHeldId && !o.invoicePaid
        ? { ...o, invoicePaid: true, invoiceDate: o.invoiceDate || sale.date,
            changes: [...(o.changes || []), { id: cid(), date: sale.date, time: hhmm, by: sale.by || "", note: "Paid at the till — held sale completed" }] }
        : o));
    }
    save({ ...data, clients, parts, posSales, specialOrders, purchaseOrders });
    if (sale.type === "return" && sale.refClientId && sale.refVisitId) stampVisitRefund(sale);
    if (sale.method === "account" && sale.customerId) postAccountCharge(sale.customerId, sale);
    // Sold something we don't have on the shelf, to a known customer → offer a special order.
    if (sale.type !== "return" && sale.customerId) {
      const short = [];
      for (const it of sale.items || []) {
        const p = (data.parts || []).find((x) => x.code && it.code && x.code.trim().toLowerCase() === it.code.trim().toLowerCase());
        if (!p || !posTracked(p)) continue;
        const gap = (num(it.qty) || 1) - Math.max(0, bulkOnHand(p, data.parts));
        if (gap > 0) short.push({ code: p.code, item: p.name || it.desc || p.code, qty: gap });
      }
      if (short.length) {
        const first = short[0];
        const rest = short.slice(1);
        if (window.confirm(`${first.item} isn't in stock${rest.length ? ` (+${rest.length} more item${rest.length === 1 ? "" : "s"})` : ""}. Open a special order for ${sale.customerName}?`)) {
          setSoSeed({
            customerId: sale.customerId, customerName: sale.customerName,
            item: first.item, code: first.code, qty: String(first.qty),
            invoiceDate: sale.date, invoicePaid: sale.method !== "account",
            notes: rest.length ? "Also short from this sale: " + rest.map((r) => `${r.qty}× ${r.item}`).join("; ") : "",
            createdBy: sale.by || (me ? me.name : ""),
          });
          setView("special");
        }
      }
    }
  };
  const [soSeed, setSoSeed] = useState(null);
  const [wrefSeed, setWrefSeed] = useState(null); // Warranty Reference opened with a serial/date preloaded
  // Field chemical usage: stock comes off the tech's truck first, then the shop shelf.
  // Bulk-linked items (10L/20L fills) drain the container part (LC) at the shop instead.
  // ---- Robotic cleaner rentals: every transition is ONE save ----
  const rentalSave = (unitsNext) => save({ ...data, rentalUnits: unitsNext });
  const rentalCheckout = (unitId, current) => {
    const now = new Date().toISOString();
    const units = (data.rentalUnits || []).map((u) => (u.id === unitId
      ? { ...u, status: "rented", current: { ...current, notices: [] },
          log: [...(u.log || []), { at: now, by: me ? me.name : "", note: `Checked out to ${current.customerName} — due ${current.dueDate}` }] }
      : u));
    const u = (data.rentalUnits || []).find((x) => x.id === unitId);
    const entry = { id: cid(), cat: "general", date: fmtDate(new Date()),
      time: now.slice(11, 16), by: me ? me.name : "",
      note: `Rental checked out: ${u ? (u.tag || u.id) : unitId} (${u ? [u.make, u.model].filter(Boolean).join(" ") : ""}) — due back ${current.dueDate}` };
    save({ ...data, rentalUnits: units,
      clients: (data.clients || []).map((c) => (c.id === current.customerId ? { ...c, commLog: [...(c.commLog || []), entry] } : c)) });
  };
  const rentalCheckIn = (unitId, hist, held) => {
    const now = new Date().toISOString();
    const u = (data.rentalUnits || []).find((x) => x.id === unitId);
    if (!u) return;
    const tagText = "Rental overdue — " + (u.tag || u.id);
    const units = (data.rentalUnits || []).map((x) => (x.id === unitId
      ? { ...x, status: "inspection", current: null, history: [hist, ...(x.history || [])],
          log: [...(x.log || []), { at: now, by: me ? me.name : "", note: `Checked in — ${hist.daysLate ? hist.daysLate + " day(s) late, " : "on time, "}$${hist.revenue} charged` }] }
      : x));
    // clear the overdue flag line for THIS unit, keep any other flag text
    const clientsNext = (data.clients || []).map((c) => {
      if (c.id !== hist.customerId) return c;
      const f = c.flag || defFlag();
      const parts = String(f.message || "").split("·").map((x) => x.trim()).filter((x) => x && !x.startsWith(tagText));
      const msg = parts.join(" · ");
      return { ...c, flag: { ...f, message: msg, on: msg ? f.on : false, scope: msg ? f.scope : "both" } };
    });
    save({ ...data, rentalUnits: units, clients: clientsNext, posHeld: held ? [...(data.posHeld || []), held] : (data.posHeld || []) });
  };


  // Closing a guided work order — ONE save carries everything: the completed job,
  // any deferred-startup follow-up (to the holding bin), the service-scope flag,
  // and truck-stock depletion for chems used. No stacked saves, no clobbering.
  const closeWorkOrder = (job) => {
    const jts = (data.jobTypes && data.jobTypes.length ? data.jobTypes : DEFAULT_JOB_TYPES);
    const jt = jts.find((t) => t.id === job.wfTypeId);
    if (!jt || !job.wf) return;
    const closeDraft = guidedCloseDraft(job);
    if (!guidedCloseGate(closeDraft).ok) return; // notes + follow-up reason required — hiding the button is not the boundary
    const closed = applyGuidedClose(job, closeDraft);
    const res = closed.wf.results || {};
    const completedAt = (res.final && res.final.at) || new Date().toISOString();
    const deferred = wfDeferred(jt, res);
    const followups = deferred.map((d) => ({
      ...blankJob(), clientId: closed.clientId, kind: "service", wfTypeId: d.followupJobType,
      what: `${(jts.find((t) => t.id === d.followupJobType) || {}).name || "Return visit"} — deferred from WO ${closed.workOrder || ""}`,
      parentJobId: closed.id,
      note: res.cust_home && res.cust_home.v === false
        ? "Customer wasn't home — call them to start filling, then book this once the level is up."
        : "Filling was started on site — book once the level is up.",
    }));
    const nowIso = new Date().toISOString();
    const closeFu = guidedCloseFollowup(closed, closeDraft, {
      id: cid(), meName: me ? me.name : "", meId: me ? me.id : "", today: fmtDate(new Date()), at: nowIso,
    });
    const chems = (res.chems && res.chems.items) || [];
    const truckUsed = chems.filter((x) => x.src === "truck" && String(x.code || "").trim());
    const partsNext = truckUsed.length && closed.techId ? partsAfterTruckUse(data.parts, closed.techId, truckUsed) : data.parts;
    const clientsNext = deferred.length
      ? (data.clients || []).map((c) => (c.id === closed.clientId
          ? { ...c, flag: { ...defFlag(), ...(c.flag || {}), on: true, scope: "service", updated: fmtDate(new Date()),
              message: [((c.flag || {}).message || "").trim(), deferred[0].flag].filter(Boolean).join(" · ") } }
          : c))
      : data.clients;
    const jobsNext = [
      ...(data.jobs || []).map((j) => (j.id === closed.id
        ? { ...j, ...closed, done: true, doneAt: completedAt,
            wf: { ...closed.wf, completedAt, startedAt: closed.wf.startedAt || (res.arrive && res.arrive.at) || "" } }
        : j)),
      ...followups,
    ];
    save({ ...data, parts: partsNext, clients: clientsNext, jobs: stampJobs(jobsNext),
      followups: closeFu ? [...(data.followups || []), closeFu] : (data.followups || []) });
    // Stamp what was dosed onto today's test record — the 3-test panel shows doses, not just numbers.
    const dosedSummary = chems.map((x) => `${x.desc}${(Number(x.qty) || 1) > 1 ? ` ×${x.qty}` : ""}${x.src === "customer" ? " (customer's own)" : ""}`).join("; ");
    if (res.wtest && res.wtest.testId && dosedSummary) {
      (async () => {
        try {
          let list = [];
          try { list = JSON.parse((await window.storage.get(TEST_KEY(job.clientId))).value); } catch { list = []; }
          const next = list.map((t) => (t.id === res.wtest.testId ? { ...t, dosedSummary } : t));
          await window.storage.set(TEST_KEY(job.clientId), JSON.stringify(next));
          setTests((m) => ({ ...m, [job.clientId]: next }));
        } catch { /* cosmetic — the work order itself holds the dose list */ }
      })();
    }
  };

  // Office review: a closed work order becomes an invoice only on explicit confirmation.
  // Automatic customer messages (5.3/5.4/8.3). Master switches live on the
  // business profile and ship OFF — nothing auto-sends until go-live arming.
  const autoComms = (data && data.businessProfile && data.businessProfile.autoComms) || {};
  const sendReportFor = async (clientId, v) => {
    if (!supabase) return { ok: false, reason: "offline mode" };
    const cl = (data.clients || []).find((c) => c.id === clientId);
    const contact = cl && serviceEmailContact(cl);
    if (!contact) return { ok: false, reason: "no contact set to receive Service emails" };
    const t = (tests[clientId] || []).find((x) => x.date === v.date) || null;
    const rpt = afterServiceReport(cl, contact, v, t, data.businessProfile || {});
    const { data: r, error } = await supabase.functions.invoke("send-email", {
      body: { to: contact.email, subject: rpt.subject, body: rpt.body, category: "service", clientId } });
    if (error || (r && r.error)) return { ok: false, reason: (r && r.error) || (error && error.message) || "send failed" };
    return { ok: true, to: contact.email };
  };
  const onMyWayText = async (c) => {
    if (!supabase) return;
    const k = ((c && c.contacts) || []).find((x) => String(x.phone || "").trim()
      && x.comms && (x.comms.service === "text" || x.comms.service === "both")) || ((c && c.contacts) || []).find((x) => String(x.phone || "").trim());
    if (!k) { setStatus(`${c.name}: no phone number on file for the on-my-way text.`); return; }
    const bpn = (data.businessProfile || {}).name || "Pool Master Canada";
    const body = `${bpn}: ${contactFirst(k) ? `Hi ${contactFirst(k)} — ` : ""}${me ? me.name : "your technician"} is on the way to your pool now.`;
    const { data: r, error } = await supabase.functions.invoke("send-sms", { body: { to: k.phone, body } });
    if (error || (r && r.error)) setStatus(`On-my-way text didn't send: ${(r && r.error) || (error && error.message) || "unknown error"}`);
    else setStatus(`On-my-way text sent to ${c.name} (${k.phone}).`);
  };
  // Customer portal (8.1): one shared sign-in page for every customer — they
  // sign in with the email and mobile number on their file (owner's rule) and
  // see a read-only page: balance, invoices, service history, latest water
  // test. This button emails the invite; the customer needs an email AND a
  // mobile number on file or the sign-in can't work.
  const portalLinkFor = (c) => {
    if (!supabase) { setStatus("The customer portal needs the shared backend — offline mode has no sign-in."); return; }
    const url = "https://app.poolmaster.ca/portal.html";
    const k = (c.contacts || []).find((x) => String(x.email || "").trim() && String(x.phone || "").trim() && x.comms && x.comms.invoices && x.comms.invoices !== "none")
      || (c.contacts || []).find((x) => String(x.email || "").trim() && String(x.phone || "").trim());
    if (!k) { setStatus(`${c.name}: the portal invite needs a contact with BOTH an email and a mobile number on file — that pair is their sign-in.`); return; }
    const bpn = (data.businessProfile || {}).name || "Pool Master Canada";
    const f = contactFirst(k);
    setCompose({ kind: "mail", client: c, to: k.email, emailCat: "invoices",
      subject: `${bpn} — your account page`,
      body: `${f ? `Hi ${f},` : "Hello,"}\n\nYou can now check your account with ${bpn} any time — your balance, invoices and recent service visits, always up to date:\n\n${url}\n\nSign in with this email address and your mobile number (${k.phone}) as the password. Questions? Just reply to this email.\n\n${bpn}` });
  };
  const confirmWoBilling = async (jobId, mode) => {
    const job = (data.jobs || []).find((j) => j.id === jobId);
    if (!job || !job.wf) return;
    if (mode === "bill") {
      if (!confirmBillingCanProceed(job)) {
        setStatus(CONFIRM_BILLING_EMPTY_MSG);
        return;
      }
      const woItems = (((job.wf.results || {}).chems || {}).items || []).filter((x) => x.src === "truck");
      const gaps = saleSerialGaps(woItems, data.parts || []);
      if (gaps.length) { setStatus(`Can't confirm ${job.workOrder || "this work order"} for billing — serial missing on: ${gaps.map((g) => g.desc).join("; ")}. Add it on the work-order line first.`); return; }
    }
    const jts = (data.jobTypes && data.jobTypes.length ? data.jobTypes : DEFAULT_JOB_TYPES);
    const jt = jts.find((t) => t.id === job.wfTypeId);
    const chems = ((job.wf.results || {}).chems || {}).items || [];
    const partOf = (code) => (data.parts || []).find((p) => (p.code || "").trim().toLowerCase() === String(code || "").trim().toLowerCase());
    const materials = chems.filter((x) => x.src === "truck").map((x) => {
      const p = partOf(x.code);
      return { desc: x.desc, qty: Number(x.qty) || 1, price: p ? Number(p.price) || 0 : 0 };
    });
    const matTotal = materials.reduce((a, m) => a + m.price * m.qty, 0);
    const dur = wfDuration(jt, job.wf.results || {});
    let visitId = "";
    if (mode === "bill") {
      // Safe append to the client's invoices — same no-clobber pattern as account charges.
      const v = { id: cid(), date: (job.wf.completedAt || "").slice(0, 10) || fmtDate(new Date()), side: "service",
        amount: (matTotal * hstFactorFor((data.clients || []).find((c) => c.id === job.clientId))).toFixed(2),
        paid: false, workOrder: job.workOrder || "",
        termIds: job.termIds || [], termsNote: job.termsNote || "",
        desc: `${(jt || {}).name || "Work order"}${dur ? ` · ${dur} min on site` : ""}${materials.length ? ` · materials: ${materials.map((m) => m.desc).join("; ")}` : ""}` };
      visitId = v.id;
      try {
        let list = [];
        try { list = JSON.parse((await window.storage.get(VISIT_KEY(job.clientId))).value); } catch { list = []; }
        const next = [v, ...list];
        await window.storage.set(VISIT_KEY(job.clientId), JSON.stringify(next));
        setVisits((m) => ({ ...m, [job.clientId]: next }));
      } catch { setStatus("Couldn't write the invoice — try again in a moment."); return; }
    }
    save({ ...data, jobs: (data.jobs || []).map((j) => (j.id === jobId
      ? { ...j, wf: { ...j.wf, billing: { mode, by: me ? me.name : "", at: new Date().toISOString(), visitId, amount: mode === "bill" ? matTotal : 0 } } } : j)) });
    if (autoComms.reports) {
      const vRpt = { date: (job.wf.completedAt || "").slice(0, 10) || fmtDate(new Date()), minutes: dur || "",
        desc: `${(jt || {}).name || "Work order"}${materials.length ? ` — materials: ${materials.map((m) => m.desc).join("; ")}` : ""}`,
        materials: materials.map((m) => ({ desc: m.desc, qty: m.qty })), amount: mode === "bill" ? String(matTotal) : "" };
      sendReportFor(job.clientId, vRpt).then((r) => { if (r.ok) setStatus(`Service report emailed to ${r.to}.`); });
    }
  };

  const consumeParts = (techId, items) => {
    const now = new Date();
    const hhmm = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
    const findCode = (c) => (data.parts || []).find((x) => (x.code || "").trim().toLowerCase() === c);
    const direct = [];
    const bulkUsed = {};
    for (const it of items || []) {
      const c = String(it.code || "").trim().toLowerCase(); if (!c) continue;
      const p = findCode(c);
      const bc = p && p.bulkCode ? String(p.bulkCode).trim().toLowerCase() : "";
      if (bc && bc !== c && findCode(bc)) bulkUsed[bc] = (bulkUsed[bc] || 0) + num(it.qty) * (num(p.bulkQty) > 0 ? num(p.bulkQty) : 1);
      else direct.push(it);
    }
    save({ ...data, parts: (data.parts || []).map((p) => {
      const code = (p.code || "").trim().toLowerCase();
      let next = p;
      const it = direct.find((i) => i.code && p.code && String(i.code).toLowerCase() === p.code.toLowerCase());
      if (it) {
        const q = num(it.qty);
        const trucks = { ...(next.trucks || {}) };
        const fromTruck = Math.min(num(trucks[techId]), q);
        if (fromTruck > 0) { trucks[techId] = num(trucks[techId]) - fromTruck; if (!(trucks[techId] > 0)) delete trucks[techId]; }
        const fromShop = q - fromTruck;
        const entry = { id: cid(), date: fmtDate(now), time: hhmm, by: me ? me.name : "", from: fromTruck > 0 ? techId : "shop", to: "used", qty: q };
        next = { ...next, qty: String(num(next.qty) - fromShop), trucks, moves: [...(next.moves || []), entry].slice(-60) };
      }
      if (code && bulkUsed[code]) {
        const q = bulkUsed[code];
        const entry = { id: cid(), date: fmtDate(now), time: hhmm, by: me ? me.name : "", from: "shop", to: "used", qty: q };
        next = { ...next, qty: String(num(next.qty) - q), moves: [...(next.moves || []), entry].slice(-60) };
      }
      return next;
    }) });
  };

  // Warehouse transfer: shop shelf ↔ a technician's truck. Saves immediately.
  const transferStock = (partId, from, to, qtyN, serials) => {
    const p = (data.parts || []).find((x) => x.id === partId);
    if (!p) return false;
    const res = applyTransfer(p, from, to, qtyN, me ? me.name : "", serials);
    if (!res.ok) { setStatus(res.reason); return false; }
    save({ ...data, parts: data.parts.map((x) => (x.id === partId ? res.part : x)) });
    const locName = (l) => { if (l === "shop") return "the shop"; const t = (data.techs || DEFAULT_TECHS).find((x) => x.id === l) || {}; return t.vehicle || t.name || "truck"; };
    setStatus(`Moved ${qtyN} × ${p.name || p.code || "item"} from ${locName(from)} to ${locName(to)}.`);
    return true;
  };
  // Refund against a service invoice: stamp who processed it onto the original invoice record.
  const stampVisitRefund = async (sale) => {
    try {
      let list = [];
      try { list = JSON.parse((await window.storage.get(VISIT_KEY(sale.refClientId))).value) || []; } catch { list = []; }
      const next = list.map((v) => (v.id === sale.refVisitId
        ? { ...v, refundLog: [...(v.refundLog || []), { id: cid(), date: sale.date, by: sale.by || "", total: sale.total }] } : v));
      await window.storage.set(VISIT_KEY(sale.refClientId), JSON.stringify(next));
      setVisits((m) => (m[sale.refClientId] ? { ...m, [sale.refClientId]: next } : m));
    } catch { /* refund itself already saved; the stamp is best-effort */ }
  };
  // Every booking carries who made it; every change to a booked service logs who changed it and what.
  const JOB_CHANGE_FIELDS = [["date", "Date"], ["serviceType", "Service"], ["kind", "Type"], ["what", "Work"], ["techId", "Technician"], ["weight", "Slots"], ["note", "Notes"]];
  const stampJobs = (next) => {
    const now = new Date();
    const time = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
    const techName = (id) => ((data.techs || DEFAULT_TECHS).find((t) => t.id === id) || {}).name || (id ? "other tech" : "unassigned");
    return next.map((j) => {
      const old = (data.jobs || []).find((x) => x.id === j.id);
      // Leaving the holding bin (or born scheduled) queues a customer confirmation.
      const withConfirm = (job) => (needsConfirm(old, job) ? { ...job, confirmPending: true } : job);
      if (!old) return withConfirm({ ...j, bookedBy: j.bookedBy || (me ? me.name : ""), bookedDate: j.bookedDate || fmtDate(now) });
      const diffs = JOB_CHANGE_FIELDS.filter(([k]) => String(old[k] ?? "") !== String(j[k] ?? ""))
        .map(([k, label]) => (k === "techId" ? `Technician: ${techName(old[k])} → ${techName(j[k])}` : `${label}: ${String(old[k] ?? "") || "—"} → ${String(j[k] ?? "") || "—"}`));
      if (!!old.done !== !!j.done) diffs.push(j.done ? "Marked done" : "Reopened");
      if (!diffs.length) return withConfirm(j);
      return withConfirm({ ...j, changes: [...(old.changes || []), { id: cid(), date: fmtDate(now), time, by: me ? me.name : "", note: diffs.join(" · ") }] });
    });
  };

  // Void a logged sale/return: reverse its stock effect and flag it out of the totals.
  const voidPosSale = (saleId) => {
    const s = (data.posSales || []).find((x) => x.id === saleId);
    if (!s || s.voided) return;
    const dir = s.type === "return" ? -1 : +1; // reverse the original move
    const parts = partsAfterMove(data.parts, s.items, dir);
    save({ ...data, parts, posSales: (data.posSales || []).map((x) => (x.id === saleId ? { ...x, voided: true } : x)) });
  };

  const holdSale = (held) => save({ ...data, posHeld: [...(data.posHeld || []), held] });
  const dropHeld = (id) => save({ ...data, posHeld: (data.posHeld || []).filter((h) => h.id !== id) });

  // Post an on-account counter sale to the customer's invoices as outstanding (safe append — no clobber).
  const postAccountCharge = async (customerId, sale) => {
    let list = [];
    try { list = JSON.parse((await window.storage.get(VISIT_KEY(customerId))).value) || []; } catch { list = []; }
    const v = { id: cid(), date: sale.date, side: "retail", amount: sale.total, paid: false, by: sale.by,
      notes: "Counter sale on account: " + (sale.items || []).map((it) => `${it.desc || it.code || "item"} ×${num(it.qty) || 1}`).join(", ") };
    const next = [v, ...list];
    try { await window.storage.set(VISIT_KEY(customerId), JSON.stringify(next)); setVisits((vm) => ({ ...vm, [customerId]: next })); } catch {}
  };

  const roles = data?.roles ?? DEFAULT_ROLES;
  const staff = data?.staff ?? DEFAULT_STAFF;
  // In server mode the Supabase gate already authenticated the user; map their email to a
  // staff member for role gating. A sign-in with no matching profile email gets a
  // least-privilege stand-in — NEVER the owner (that fallback let unlinked accounts see
  // owner-only screens). Otherwise use the 4-digit code flow.
  const me = SERVER
    ? (staff.find((x) => x.email && serverEmail && x.email.toLowerCase() === serverEmail.toLowerCase())
        || { id: "unlinked", name: serverEmail || "Unlinked sign-in", roleId: "tech", active: true, unlinked: true })
    : (staff.find((x) => x.id === staffId)
        || (!REQUIRE_SIGN_IN ? staff.find((x) => x.roleId === "owner") || staff[0] || null : null));
  const role = me ? roles.find((r) => r.id === me.roleId) || roles[roles.length - 1] : null;
  const can = (k) => !!role && role.perms.includes(k);
  const isOwner = !!(role && role.id === "owner");
  const isMgr = !!(role && ["owner", "manager"].includes(role.id));

  // Overdue sweep — once per app load: persist rented→overdue and raise the
  // both-scopes customer flag (shows at the retail counter too, by design).
  const rentalSweepDone = useRef(false);
  useEffect(() => {
    if (rentalSweepDone.current || !data || !me) return;
    rentalSweepDone.current = true;
    const today = fmtDate(new Date());
    let changed = false;
    const units = (data.rentalUnits || []).map((u) => {
      if (u.status === "rented" && u.current && rentalLateDays(u.current.dueDate, today) > 0) {
        changed = true;
        return { ...u, status: "overdue", log: [...(u.log || []), { at: new Date().toISOString(), by: "system", note: "Auto: overdue — was due " + u.current.dueDate }] };
      }
      return u;
    });
    const perDay = (u) => (num(u.lateFee) || num((data.businessProfile || {}).rentalLateFee) || 0).toFixed(2);
    const want = {};
    units.forEach((u) => { if (u.status === "overdue" && u.current && u.current.customerId) want[u.current.customerId] = u; });
    const clientsNext = (data.clients || []).map((c) => {
      const u = want[c.id];
      if (!u) return c;
      const line = `Rental overdue — ${u.tag || u.id} due ${u.current.dueDate}. Late fee accruing at $${perDay(u)}/day. Request return.`;
      const f = { ...defFlag(), ...(c.flag || {}) };
      if (String(f.message || "").includes(`Rental overdue — ${u.tag || u.id}`)) return c;
      changed = true;
      return { ...c, flag: { ...f, on: true, scope: "both", updated: today, message: [String(f.message || "").trim(), line].filter(Boolean).join(" · ") } };
    });
    if (changed) save({ ...data, rentalUnits: units, clients: clientsNext });
  }, [data ? 1 : 0, me ? 1 : 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Field technicians live in My Day (and My Truck) only — their whole workload is
  // the day's booked services on their phone. Everything else stays office-side.
  const fieldOnly = !!(role && ["tech", "senior", "vac"].includes(role.id));
  const unreadMsgs = me ? (data?.messages || []).filter((m) => m.to.includes(me.id) && !m.read.includes(me.id)).length : 0;

  // Once per sign-in, after the data lands: pop follow-up reminders that are due.
  useEffect(() => {
    if (fuReminders !== null || !data || !me) return;
    setFuReminders(followupReminders(data.followups, me.id, isOwner, fmtDate(new Date())));
  }, [data, me]); // eslint-disable-line react-hooks/exhaustive-deps

  // Customer texts (two-way SMS on the business line). Office-side roles only —
  // RLS enforces the same boundary server-side, this just decides what renders.
  const canTexts = SERVER && !!supabase && !!(role && ["owner", "manager", "retail"].includes(role.id));
  useEffect(() => {
    if (!canTexts) return;
    let dead = false;
    const pull = async () => {
      const [a, b] = await Promise.all([
        supabase.from("sms_inbound").select("*").order("received_at", { ascending: false }).limit(400),
        supabase.from("sms_log").select("*").order("created_at", { ascending: false }).limit(400),
      ]);
      if (dead) return;
      if (!a.error) setSmsInbox(a.data || []);
      if (!b.error) setSmsSent(b.data || []);
    };
    pull();
    const t = setInterval(pull, 60000);
    return () => { dead = true; clearInterval(t); };
  }, [canTexts, textsOpen]);
  const unreadTexts = smsInbox.filter((m) => !m.read).length;
  const markTextsRead = async (num) => {
    setSmsInbox((rows) => rows.map((r) => (r.from_number === num ? { ...r, read: true } : r)));
    try { await supabase.from("sms_inbound").update({ read: true }).eq("from_number", num).eq("read", false); } catch { /* next poll corrects */ }
  };
  // Owner-only: wipe a whole conversation from the app's inbox + send audit.
  // (Twilio keeps its own copy on their side — this cleans the app, not the carrier.)
  const deleteTextThread = async (num) => {
    const n10 = normPhone10(num);
    const inNums = [...new Set(smsInbox.filter((r) => normPhone10(r.from_number) === n10).map((r) => r.from_number))];
    const outNums = [...new Set(smsSent.filter((r) => normPhone10(r.to_number) === n10).map((r) => r.to_number))];
    if (inNums.length) await supabase.from("sms_inbound").delete().in("from_number", inNums);
    if (outNums.length) await supabase.from("sms_log").delete().in("to_number", outNums);
    setSmsInbox((rows) => rows.filter((r) => normPhone10(r.from_number) !== n10));
    setSmsSent((rows) => rows.filter((r) => normPhone10(r.to_number) !== n10));
  };
  const replyText = async (dest, text, cl) => {
    const { data: r, error } = await supabase.functions.invoke("send-sms", {
      body: { to: dest, body: text, clientId: cl ? cl.id : null } });
    if (error || (r && r.error)) throw new Error((r && r.error) || (error && error.message) || "Couldn't send the text.");
    const now = new Date();
    setSmsSent((rows) => [{ id: "tmp" + now.getTime(), to_number: dest, body: text, created_at: now.toISOString() }, ...rows]);
    if (cl) {
      const entry = { id: cid(), cat: "general", date: fmtDate(now),
        time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"),
        note: `Text reply sent from business line to ${dest}: ${text}`, by: me ? me.name : "" };
      save({ ...data, clients: (data.clients || []).map((x) => (x.id === cl.id ? { ...x, commLog: [...(x.commLog || []), entry] } : x)) });
    }
  };
  const pendingTimeOff = me && role && role.perms.includes("staff")
    ? (data?.timeOff || []).filter((r) => r.status === "pending").length : 0;
  const approvedQuotes = Object.values(quotes).flat().filter((q) => q && q.status === "accepted").length;
  const activeSpecialCount = (data?.specialOrders || []).filter((o) => o.status !== "pickedup" && !o.archived).length;
  const followupOpenCount = (data?.followups || []).filter((f) => !f.done && visibleFollowup(f, me ? me.id : "", isOwner)).length;
  const repairOpenCount = (data?.repairs || []).filter((t) => !t.archived && t.status !== "COMPLETED").length;
  // Optional second arg: a held POS sale created alongside the order (unpaid items).
  // Both land in ONE save so neither write clobbers the other.
  const saveSpecialOrder = (o, held) => save({ ...data,
    posHeld: held ? [...(data.posHeld || []), held] : data.posHeld,
    specialOrders: (data.specialOrders || []).some((x) => x.id === o.id)
      ? data.specialOrders.map((x) => (x.id === o.id ? o : x)) : [...(data.specialOrders || []), o] });
  const deleteSpecialOrder = (id) => save({ ...data, specialOrders: (data.specialOrders || []).filter((x) => x.id !== id) });

  // Book an approved quote straight into a service (from the approved-quotes list).
  // Stays in the approved queue until the service is actually saved (see markQuoteBooked).
  const bookFromQuote = (clientId, q) => {
    setSelId(clientId);
    setBookSeed({
      side: "service",
      fromQuote: { clientId, quoteId: q.id },
      materials: (q.materials || []).map((m) => ({ ...m, id: cid() })),
      labourLines: (q.labourLines || []).map((l) => ({ ...l, id: cid() })),
      termIds: q.termIds || [], termsNote: q.termsNote || "",
      notes: q.forWhom ? `From accepted quote: ${q.forWhom}` : "From accepted quote",
    });
    setApprovedOpen(false); setVisitAutoNew(true); setVisitsOpen("service");
  };
  const markQuoteBooked = (clientId, quoteId) => {
    saveQuotes(clientId, (quotes[clientId] || []).map((x) => (x.id === quoteId ? { ...x, status: "booked" } : x)));
  };

  const clients = data?.clients ?? [];

  const openClient = (id) => {
    setSelId(id);
    setEditing(false);
    setData((d) => {
      if (!d) return d;
      const already = (d.recent || [])[0] === id;
      if (already) return d;
      const next = { ...d, recent: [id, ...(d.recent || []).filter((x) => x !== id)].slice(0, 8) };
      window.storage.set(STORE_KEY, JSON.stringify(next)).catch(() => {});
      return next;
    });
  };

  // Charge-now from the customer profile: pay everything owing or just what's due,
  // mark those invoices paid, and send the receipt the way the customer receives invoices.
  // Once a customer's account is fully settled, their account-invoiced special orders
  // count as paid — which unlocks the Picked up button on those orders.
  const ordersAfterAccountPayment = (clientId, remainingUnpaid) => {
    if (remainingUnpaid > 0) return data.specialOrders;
    const now = new Date();
    const hhmm = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
    let touched = false;
    const next = (data.specialOrders || []).map((o) => {
      if (o.customerId !== clientId || o.invoicePaid || !o.invoiceDate || o.heldSaleId) return o;
      touched = true;
      return { ...o, invoicePaid: true, changes: [...(o.changes || []), { id: cid(), date: fmtDate(now), time: hhmm, by: me ? me.name : "", note: "Payment applied from account — invoice settled" }] };
    });
    return touched ? next : data.specialOrders;
  };

  const chargeCustomer = async (clientId, scope) => {
    const cl = clients.find((x) => x.id === clientId);
    if (!cl) return;
    const list = visits[clientId] || [];
    const bal = accountBalances(cl.billing, list);
    const toPay = scope === "due" ? bal.due : bal.unpaid;
    if (!toPay.length) { setStatus("Nothing to charge on that account."); return; }
    const total = toPay.reduce((a, v) => a + num(v.amount), 0);
    const ids = new Set(toPay.map((v) => v.id));
    const today = fmtDate(new Date());
    // Real money now moves through Helcim — no token, no charge, no pretending.
    let charge = null;
    if (serverMode() && supabase) {
      const hm = /^helcim:([^:]+):(.+)$/.exec(String((((cl.billing || {}).card) || {}).ref || "").trim());
      if (!hm) { setStatus("This card isn't in the Helcim vault yet — open the card on file and re-enter it through the secure form, then charge."); return; }
      setStatus(`Charging ${posMoney(total)} to ${cl.name}'s card…`);
      try {
        const { data: r, error } = await supabase.functions.invoke("payments", {
          body: { action: "charge", customerCode: hm[1], cardToken: hm[2], amount: Number(total.toFixed(2)) },
        });
        if (error || !r || r.error) throw new Error((r && r.error) || "the payment call failed");
        charge = r;
      } catch (e) {
        setStatus("Charge did NOT go through — " + String(e.message || e) + ". Nothing was marked paid.");
        return;
      }
      if (charge.status !== "APPROVED") {
        setStatus(`Card DECLINED by Helcim — nothing was marked paid. Ask the customer for another card.`);
        return;
      }
    }
    const paidList = list.map((v) => (ids.has(v.id) ? { ...v, paid: true, paidDate: today, paidVia: "card" } : v));
    await saveVisits(clientId, paidList);
    const remainingUnpaid = paidList.filter((v) => v.paid === false).length;

    // Receipt content
    const card = ((cl.billing || {}).card) || {};
    const fmt$ = (n) => "$" + num(n).toFixed(2);
    const lines = toPay.map((v) => `  ${v.invoiceNo || v.workOrder || "Invoice"} · ${v.date || ""} — ${fmt$(v.amount)}`);
    const receipt = [
      `Pool Master Canada — Payment receipt`,
      `${today} · ${cl.name}`,
      "",
      `Paid invoices:`,
      ...lines,
      "",
      `Total charged: ${fmt$(total)} to ${card.brand || "card"} ending ${card.last4 || "····"}`,
      ...(charge ? [`Approval ${charge.approvalCode || ""} · Ref ${charge.transactionId || ""}`] : []),
      "",
      "Thank you for your business!",
    ].join("\n");

    // Deliver per the customer's invoice preference: text / email / mail (print).
    const invContact = (cl.contacts || []).find((k) => k.comms && k.comms.invoices && k.comms.invoices !== "none");
    const ch = invContact ? invContact.comms.invoices : "mail";
    let sentBy;
    if ((ch === "email" || ch === "both") && invContact && invContact.email) {
      sentBy = "email";
      setCompose({ kind: "mail", client: cl, to: invContact.email, subject: `Pool Master — payment receipt ${today}`, body: receipt, emailCat: "invoices" });
    } else if ((ch === "text" || ch === "both") && invContact && invContact.phone) {
      sentBy = "text";
      setCompose({ kind: "text", client: cl, to: invContact.phone, body: `${contactFirst(invContact) ? `Hi ${contactFirst(invContact)}, ` : ""}Pool Master: payment received — ${fmt$(total)} charged to your ${card.brand || "card"} ending ${card.last4 || ""}. Thank you!` });
    } else {
      sentBy = "mail (printed)";
      const w = window.open("", "_blank");
      if (w) {
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Payment receipt</title>
<style>body{font:14px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;max-width:520px;margin:40px auto;}h1{font-size:20px;color:#0B4F8A;}pre{font:inherit;white-space:pre-wrap;}@media print{body{margin:16px;}}</style>
</head><body onload="window.print()"><h1>Pool Master Canada</h1><pre>${posEsc(receipt)}</pre></body></html>`);
        w.document.close();
      }
    }
    // Log it on the account so there's a record of the receipt going out.
    const now = new Date();
    const entry = { id: cid(), cat: "general", date: today, time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"), note: `Payment receipt sent by ${sentBy} — ${fmt$(total)} charged to card ending ${card.last4 || "····"} (${toPay.length} invoice${toPay.length === 1 ? "" : "s"}${scope === "due" ? ", due-now amount" : ", full balance"})${charge ? ` · Helcim approval ${charge.approvalCode || ""} ref ${charge.transactionId || ""}` : ""}`, by: me ? me.name : "" };
    save({ ...data, clients: clients.map((x) => (x.id === clientId ? { ...x, commLog: [...(x.commLog || []), entry] } : x)),
      specialOrders: ordersAfterAccountPayment(clientId, remainingUnpaid) });
    setStatus(`Charged ${fmt$(total)} to ${cl.name}'s card — receipt by ${sentBy}.`);
  };

  // Settle an account's outstanding balance — mark unpaid invoices paid to the card on file.
  const chargeAccount = async (clientId) => {
    const list = visits[clientId] || [];
    const unpaid = list.filter((v) => v.paid === false);
    if (!unpaid.length) { setStatus("Nothing outstanding on that account."); return; }
    const total = unpaid.reduce((a, v) => a + num(v.amount), 0);
    const cl = clients.find((c) => c.id === clientId);
    const card = ((cl && cl.billing) || {}).card || {};
    const cardTxt = card.last4 ? `${card.brand || "card"} ending ${card.last4}` : "the card on file";
    if (!window.confirm(`Charge ${money0(total)} to ${cardTxt} and mark ${unpaid.length} invoice${unpaid.length === 1 ? "" : "s"} as paid?`)) return;
    const today = fmtDate(new Date());
    const next = list.map((v) => (v.paid === false ? { ...v, paid: true, paidDate: today, paidVia: "charge" } : v));
    await saveVisits(clientId, next);
    const so = ordersAfterAccountPayment(clientId, 0);
    if (so !== data.specialOrders) save({ ...data, specialOrders: so });
    setStatus(`Recorded ${money0(total)} charged to ${cl ? cl.name : "account"}.`);
  };

  // Monthly billing run: invoice every monthly-cycle account its rate + HST, once per month.
  const runMonthlyBilling = async (monthKey) => {
    const label = new Date(monthKey + "-15").toLocaleDateString(undefined, { month: "long", year: "numeric" });
    const targets = clients.filter((c) => {
      const b = { ...defBilling(), ...(c.billing || {}) };
      return c.type !== "retail" && !c.trade && b.cycle === "monthly" && num(b.monthlyRate) > 0 && !b.onHold;
    });
    let billed = 0, skipped = 0, total = 0;
    for (const c of targets) {
      let list = visits[c.id];
      if (list === undefined) { try { list = JSON.parse((await window.storage.get(VISIT_KEY(c.id))).value) || []; } catch { list = []; } }
      const markr = `Monthly service — ${label}`;
      if (list.some((v) => (v.notes || "").startsWith(markr))) { skipped++; continue; }
      const rate = num((c.billing || {}).monthlyRate);
      const amount = (rate * hstFactorFor(c)).toFixed(2);
      const v = { id: cid(), date: fmtDate(new Date()), workOrder: "", invoiceNo: "", amount,
        by: me ? me.name : "Billing run", notes: `${markr} — $${rate.toFixed(2)} + ${hstLabelFor(c)}`, paid: false, side: "service", repairType: "" };
      await saveVisits(c.id, [...list, v]);
      billed++; total += num(amount);
    }
    setStatus(`Monthly billing for ${label}: ${billed} account${billed === 1 ? "" : "s"} invoiced (${money0(total)})${skipped ? `, ${skipped} already billed` : ""}.`);
    return { billed, skipped, total };
  };

  // Log a phone/email/text touch on a customer's account (used by the A/R follow-up page).
  const logClientComm = (clientId, note, cat = "general") => {
    const now = new Date();
    const entry = { id: cid(), cat, date: fmtDate(now), time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"), note, by: me ? me.name : "" };
    save({ ...data, clients: (data.clients || []).map((c) => (c.id === clientId ? { ...c, commLog: [...(c.commLog || []), entry] } : c)) });
  };

  // ---- A/R day-11 auto-charge sweep ----
  // An invoice 10 days old or less is new. On day 11 it is due, and the first time the app
  // is open at/after 8:00 AM it is charged to the card on file (auto charge on, card valid).
  // The customer gets a thank-you receipt per their invoice preference, logged on the account.
  // (True unattended 8:00 AM charging arrives with the card-processor integration — until
  // then the sweep runs when an office/owner user has the app open.)
  const arSweepRan = useRef(false);
  useEffect(() => {
    if (!AR_AUTO_CHARGE_ENABLED) return; // armed at go-live, per the owner
    if (arSweepRan.current || !data || !me || !(isOwner || can("margins"))) return;
    if (new Date().getHours() < 8) return;
    arSweepRan.current = true;
    (async () => {
      const map = {};
      for (const c of data.clients || []) {
        if (visits[c.id] !== undefined) { map[c.id] = visits[c.id]; continue; }
        try { map[c.id] = JSON.parse((await window.storage.get(VISIT_KEY(c.id))).value) || []; } catch { map[c.id] = []; }
      }
      setVisits((v) => ({ ...map, ...v }));
      const todayS = fmtDate(new Date());
      const hhmm = () => { const n = new Date(); return String(n.getHours()).padStart(2, "0") + ":" + String(n.getMinutes()).padStart(2, "0"); };
      const commAdds = {}; // clientId -> log entries, folded into ONE save at the end
      let accounts = 0, collected = 0;
      for (const c of data.clients || []) {
        const b = { ...defBilling(), ...(c.billing || {}) };
        if (!b.autoCharge || !hasCard(b) || cardStatus(b.card).expired) continue;
        const list = map[c.id] || [];
        const duePay = list.filter((v) => v.paid === false && num(v.amount) > 0 && arAge(v.date) > AR_GRACE_DAYS);
        if (!duePay.length) continue;
        const total = duePay.reduce((a, v) => a + num(v.amount), 0);
        const card = b.card || {};
        // Only cards vaulted with Helcim can actually be charged; the rest stay in Follow-up.
        const hm = /^helcim:([^:]+):(.+)$/.exec(String(card.ref || "").trim());
        if (!hm || !supabase) continue;
        let chg = null;
        try {
          const { data: r, error } = await supabase.functions.invoke("payments", {
            body: { action: "charge", customerCode: hm[1], cardToken: hm[2], amount: Number(total.toFixed(2)) },
          });
          if (error || !r || r.error || r.status !== "APPROVED") throw new Error((r && (r.error || r.status)) || "call failed");
          chg = r;
        } catch (e) {
          commAdds[c.id] = [{ id: cid(), cat: "general", date: todayS, time: hhmm(), by: "Auto charge", note: `Auto-charge attempt for $${total.toFixed(2)} did NOT go through (${String(e.message || e)}) — invoices left unpaid for follow-up.` }];
          continue;
        }
        const ids = new Set(duePay.map((v) => v.id));
        const nextList = list.map((v) => (ids.has(v.id) ? { ...v, paid: true, paidDate: todayS, paidVia: "card" } : v));
        await saveVisits(c.id, nextList);
        map[c.id] = nextList;
        const invContact = (c.contacts || []).find((k) => k.comms && k.comms.invoices && k.comms.invoices !== "none");
        const ch = invContact ? invContact.comms.invoices : "mail";
        const thanks = `Pool Master: Thank you for your payment — $${total.toFixed(2)} was charged to your ${card.brand || "card"} ending ${card.last4 || "····"}. No reply required.`;
        commAdds[c.id] = [
          { id: cid(), cat: "general", date: todayS, time: hhmm(), by: "Auto charge", note: `Auto-charged $${total.toFixed(2)} to card ending ${card.last4 || "····"} (${duePay.length} invoice${duePay.length === 1 ? "" : "s"} past the 10-day grace) · Helcim approval ${chg.approvalCode || ""} ref ${chg.transactionId || ""}.` },
          { id: cid(), cat: "general", date: todayS, time: hhmm(), by: "Auto charge", note: `Thank-you receipt by ${ch}: "${thanks}"` },
        ];
        accounts++; collected += total;
      }
      if (accounts) {
        save({ ...data, clients: (data.clients || []).map((c) => (commAdds[c.id] ? { ...c, commLog: [...(c.commLog || []), ...commAdds[c.id]] } : c)) });
        setStatus(`Auto-charge sweep: ${accounts} account${accounts === 1 ? "" : "s"} charged, ${money0(collected)} collected — thank-you receipts logged.`);
      }
    })();
  }, [data, me]); // eslint-disable-line

  const browsing = !q.trim() && fType === "all" && fDay === "all" && fCx === "all";
  // Views that render full-width with no customer sidebar (no search bar / "Last viewed" list).
  // The schedule (view "week") is full-width in every mode — day, weekly, and monthly.
  useEffect(() => {
    // Wait for real data: while it loads, `me` is a placeholder that reads as
    // field-only, and redirecting on it stranded office staff on "Talon's day".
    if (data && fieldOnly && !["myday", "truck", "assist"].includes(view)) setView("myday");
    if (data && view === "fleet" && !isMgr) setView("clients");
  }, [data, fieldOnly, view, isMgr]); // eslint-disable-line
  // Drop-off receipt / work-order QR: #repair=RP-12 (also ?repair=). Field techs
  // stay on My Day — the workshop is office-side. Do NOT wait for `data` and do
  // NOT key this on repairJump: Quotes list Open already uses that same hook, and
  // clearing it after WorkshopView jumped must not re-parse the hash forever.
  useEffect(() => {
    if (fieldOnly) return;
    const apply = (ticket) => {
      if (!ticket) return;
      setRepairJump(ticket);
      setView("repairs");
      stripRepairTicketFromLocation();
      repairQrConsumed.current = true;
    };
    if (!repairQrConsumed.current) apply(parseRepairTicketFromLocation());
    const onHash = () => {
      const ticket = parseRepairTicketFromLocation();
      if (!ticket) return;
      repairQrConsumed.current = false;
      apply(ticket);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [fieldOnly]);

  const goBack = () => {
    if (view === "clients" && (selected || editing)) { setEditing(false); setSelId(null); return; }
    const h = [...navHist];
    const prev = h.pop();
    if (prev !== undefined) { setNavHist(h); prevViewRef.current = prev; setView(prev); }
  };
  // Back never leaves the app (Shaun's field test: Back from My Day dumped the
  // tech back to sign-in). A sentinel history entry is armed once; every pop is
  // answered with an IN-APP back step — the open sub-screen's own handler if
  // one is registered, the normal view history otherwise — and the sentinel is
  // re-armed, so hardware back / swipe-back can never walk past the app.
  const goBackRef = useRef(null);
  goBackRef.current = goBack;
  useEffect(() => {
    try { window.history.pushState({ pm: 1 }, ""); } catch { /* sandboxed */ }
    const onPop = () => {
      const h = BACK_HANDLERS[BACK_HANDLERS.length - 1];
      if (h) h(); else if (goBackRef.current) goBackRef.current();
      try { window.history.pushState({ pm: 1 }, ""); } catch { /* sandboxed */ }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const soloBoard = view === "week"
    || ["sales", "ar", "special", "retail", "offbooks", "myday", "truck", "ai", "inventory", "assist", "repairs", "followups", "fleet"].includes(view);
  const recentRows = ((data && data.recent) || [])
    .map((id) => clients.find((c) => c.id === id)).filter(Boolean).slice(0, 8);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return clients
      .filter((c) => (fType === "all" ? true : fType === "watertest" ? !!c.waterTesting : fType === "trade" ? !!c.trade : c.type === fType || c.type === "both"))
      .filter((c) => (fCx === "all" ? true : cxBand(effectiveCx(c)) === fCx))
      .filter((c) => (fDay === "all" ? true : clientDays(c).includes(fDay)))
      .filter((c) => !t ? true :
        [c.name, c.address, c.billingAddress, c.notes, ...(c.contacts || []).map((k) => k.name + " " + k.phone + " " + k.email)]
          .join(" ").toLowerCase().includes(t))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, q, fType, fCx, fDay]);

  const selected = clients.find((c) => c.id === selId) || null;
  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const commitClient = (finished, deactivate) => {
    finished = { ...finished, days: capServiceDays(clientDays(finished), finished.frequency) };
    const exists = clients.some((c) => c.id === finished.id);
    const links = new Set(finished.linkedIds || []);
    // Linked properties stay symmetric: linking A→B also links B→A, and unlinking removes both sides.
    let withLinks = (exists ? clients.map((c) => (c.id === finished.id ? finished : c)) : [...clients, finished])
      .map((c) => {
        if (c.id === finished.id) return c;
        const has = (c.linkedIds || []).includes(finished.id);
        if (links.has(c.id) && !has) return { ...c, linkedIds: [...(c.linkedIds || []), finished.id] };
        if (!links.has(c.id) && has) return { ...c, linkedIds: (c.linkedIds || []).filter((x) => x !== finished.id) };
        return c;
      });
    if (deactivate) {
      withLinks = withLinks.map((c) => (c.id === deactivate.id
        ? { ...c, deactivated: { date: fmtDate(new Date()), by: me ? me.name : "", reason: deactivate.reason } } : c));
    }
    save({ ...data, clients: withLinks });
    setSelId(finished.id); setEditing(false);
  };
  const normAddr = (a) => String(a || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const commit = () => {
    if (!draft.name.trim()) { setStatus("Give the client a name before saving."); return; }
    // Only the owner may apply the Indian Status exemption without the card photo on file.
    if (draft.statusExempt && !draft.statusCardImage && !isOwner) {
      setStatus("The First Nations HST Rebate can't be applied without a Status Card photo — add the photo, or ask the owner to override.");
      return;
    }
    const finished0 = stripServiceFields({ ...draft, complexity: effectiveCx(draft) });
    const existing = clients.find((c) => c.id === finished0.id);
    const finished = applyGiftedAccountFlag(existing, finished0, role);
    // A brand-new profile at an address we already have gets flagged before it saves —
    // could be a duplicate, or the house changed hands.
    if (!clients.some((c) => c.id === finished.id) && normAddr(finished.address)) {
      const twin = clients.find((c) => c.id !== finished.id && !c.deactivated && normAddr(c.address) === normAddr(finished.address));
      if (twin) { setDupPop({ finished, existing: twin, reason: "" }); return; }
    }
    commitClient(finished);
  };
  const [dupPop, setDupPop] = useState(null); // { finished, existing, reason }

  const saveFlag = (clientId, flag) => {
    save({ ...data, clients: clients.map((c) => {
      if (c.id !== clientId) return c;
      // Keep a trail: a cleared or replaced flag message lands in the history with who ended it.
      const old = c.flag || {};
      const ended = old.on && String(old.message || "").trim() && (!flag.on || flag.message !== old.message);
      const flagHistory = ended
        ? [...(c.flagHistory || []), {
            id: cid(), message: old.message, scope: old.scope || "both",
            endedAt: new Date().toISOString().slice(0, 10), endedBy: me ? me.name : "",
            how: !flag.on ? "cleared" : "replaced",
          }].slice(-20)
        : c.flagHistory;
      return { ...c, flag, flagHistory };
    }) });
  };

  const sendMessage = (m) => save({ ...data, messages: [...(data.messages || []), m].slice(-400) });
  const submitTimeOff = (r) => save({ ...data, timeOff: [...(data.timeOff || []), r].slice(-300) });
  // Back-and-forth on a request before deciding ("who's covering your shift?").
  // The reply lands on the request thread AND pings the other side in Messages.
  const replyTimeOff = (id, note) => {
    const r = (data.timeOff || []).find((x) => x.id === id);
    if (!r || !note.trim()) return;
    const entry = { id: cid(), byId: me ? me.id : "", by: me ? me.name : "", note: note.trim(), at: new Date().toISOString() };
    const timeOff = (data.timeOff || []).map((x) => (x.id === id ? { ...x, replies: [...(x.replies || []), entry] } : x));
    const iAmRequester = me && r.staffId === me.id;
    const to = iAmRequester
      ? staff.filter((s) => s.active !== false && ["owner", "manager"].includes(s.roleId) && s.id !== me.id).map((s) => s.id)
      : [r.staffId];
    const messages = to.length
      ? [...(data.messages || []), { id: cid(), from: me ? me.id : "", to, body: `Time off ${r.from} → ${r.to}: ${note.trim()}`, at: new Date().toISOString(), read: [] }].slice(-400)
      : data.messages;
    save({ ...data, timeOff, messages });
  };
  const decideTimeOff = (id, status) => {
    const req = (data.timeOff || []).find((r) => r.id === id);
    // The requester hears the verdict as a staff message — no more wondering
    // whether a reply meant yes or no.
    const verdict = req && req.staffId && req.staffId !== (me ? me.id : "")
      ? [{ id: cid(), threadId: cid(), from: me ? me.id : "", to: [req.staffId],
          body: `Your time off ${req.from}${req.to !== req.from ? " → " + req.to : ""} was ${status === "approved" ? "APPROVED ✅" : "DECLINED ❌"} by ${me ? me.name : "management"}.`,
          at: new Date().toISOString(), read: [] }]
      : [];
    save({
      ...data,
      timeOff: (data.timeOff || []).map((r) => (r.id === id ? { ...r, status, decidedBy: me ? me.id : "", decidedAt: new Date().toISOString() } : r)),
      messages: [...(data.messages || []), ...verdict].slice(-400),
    });
  };
  const markRead = (id) => save({
    ...data,
    messages: (data.messages || []).map((m) => (m.id === id && me && !m.read.includes(me.id)
      ? { ...m, read: [...m.read, me.id] } : m)),
  });
  const markReadMany = (ids) => save({
    ...data,
    messages: (data.messages || []).map((m) => (ids.includes(m.id) && me && !m.read.includes(me.id)
      ? { ...m, read: [...m.read, me.id] } : m)),
  });

  const punch = (sid, goingIn) => {
    const now = new Date().toISOString();
    const list = [...(data.punches || [])];
    if (goingIn) list.push({ id: cid(), staffId: sid, inAt: now, outAt: "" });
    else {
      const i = list.map((p) => p.staffId === sid && !p.outAt).lastIndexOf(true);
      if (i >= 0) list[i] = { ...list[i], outAt: now };
    }
    save({ ...data, punches: list.slice(-600) });
  };

  const setPayMethod = (clientId, v) => {
    const c = (data.clients || []).find((x) => x.id === clientId);
    const b = { ...defBilling(), ...((c || {}).billing || {}) };
    save({
      ...data,
      clients: data.clients.map((x) => (x.id === clientId
        ? { ...x, billing: { ...defBilling(), ...(x.billing || {}), payMethod: v } } : x)),
    });
    if (v === "credit" && !hasCard(b)) setCardOpen(clientId);
    else setStatus(v ? "Pays by " + PAY_LABEL[v].toLowerCase() : "No preference set — ask the customer their preferred payment.");
  };

  const saveCard = (clientId, card) => {
    save({
      ...data,
      clients: data.clients.map((x) => (x.id === clientId
        ? { ...x, billing: { ...defBilling(), ...(x.billing || {}), card: { ...card, updated: new Date().toISOString().slice(0, 10) } } }
        : x)),
    });
    setCardOpen(null);
    setStatus("Card saved");
  };

  const setNoService = (clientId, on, reason = "", note = "") => {
    save({
      ...data,
      clients: data.clients.map((c) => (c.id === clientId
        ? { ...c, billing: { ...defBilling(), ...(c.billing || {}), noService: on, noServiceReason: on ? reason : "", noServiceNote: on ? note : "" } } : c)),
    });
    setStatus(on ? `Do not service — flagged (${reason || "no reason"})` : "Service allowed again");
  };

  const setAutoCharge = (clientId, on) => {
    save({ ...data, clients: clients.map((c) => (c.id === clientId
      ? { ...c, billing: { ...defBilling(), ...(c.billing || {}), autoCharge: on } } : c)) });
  };

  const remove = async (id) => {
    const next = { ...data, clients: clients.filter((c) => c.id !== id) };
    await save(next);
    try { await window.storage.delete(PHOTO_KEY(id)); } catch {}
    try { await window.storage.delete(VISIT_KEY(id)); } catch {}
    try { await window.storage.delete(TEST_KEY(id)); } catch {}
    try { await window.storage.delete(QUOTE_KEY(id)); } catch {}
    setSelId(next.clients[0]?.id ?? null); setEditing(false);
  };

  if (!data) return (
    <div className="pd">
      <style>{CSS}</style>
      <div className="panel" style={{ padding: "38px 20px", textAlign: "center" }}>
        <div style={{ display: "inline-block" }}><PoolMasterLogo height={44} /></div>
        <div className="sub" style={{ marginTop: 12 }}>
          {bootTries === 0 ? "Loading your client list…" : `Trouble reaching the server — retrying (attempt ${bootTries + 1})…`}
        </div>
      </div>
    </div>
  );


  const onRoute = clients.filter((c) => c.type !== "retail" && !c.trade);
  const avgCx = onRoute.length ? (onRoute.reduce((s, c) => s + effectiveCx(c), 0) / onRoute.length).toFixed(1) : "—";

  return (
    <div className="pd" data-nav={navSide} data-shell={me && !fieldOnly ? "side" : "plain"}>
      <style>{CSS}</style>
      <div className="topbar">
        <div className="brandbar">
          {me && !fieldOnly && (
            <button type="button" className="btn ghost iconbtn phonemenu-btn" data-cobrowse="nav.phone-menu"
              title="Open menu" aria-label="Open menu" aria-expanded={phoneNavOpen ? 1 : 0}
              onClick={() => setPhoneNavOpen(true)}>
              <MenuIcon size={18} /> Menu
            </button>
          )}
          <PoolMasterLogo height={47} />
          <div>
            <h1>Service desk</h1>
            <div className="sub">{clients.length} customers · {onRoute.length} on route · avg complexity {avgCx}</div>
          </div>
        </div>
        {me ? <div className="topright">
          {can("edit") && approvedQuotes > 0 && (
            <button className="quoteflash" title="Approved quotes waiting to be booked" onClick={() => setApprovedOpen(true)}>
              <QuoteIcon size={16} />
              <span className="qcount">{approvedQuotes}</span>
            </button>
          )}
          <button className="btn ghost iconbtn" title="Book time off" onClick={() => setTimeOffOpen(true)}>
            <TimeOffIcon size={16} />
            {pendingTimeOff > 0 && <span className="badge">{pendingTimeOff}</span>}
          </button>
          {canTexts && (
            <button className="btn ghost iconbtn" title="Customer texts — the business texting line" onClick={() => setTextsOpen(true)}>
              <CommIcon v="sms" size={16} />
              {unreadTexts > 0 && <span className="badge">{unreadTexts}</span>}
            </button>
          )}
          <button className="btn ghost iconbtn" title="Staff messages" onClick={() => setMsgsOpen(true)}>
            <CommIcon v="text" size={16} />
            {unreadMsgs > 0 && <span className="badge">{unreadMsgs}</span>}
          </button>
          <button className="btn ghost iconbtn" title="Send an email" onClick={() => setCompose({ kind: "mail", to: "" })}>
            <CommIcon v="mail" size={16} />
          </button>
          <div className="usermenu">
            <button className="whobar whobtn" title="Account menu" aria-haspopup="true" aria-expanded={userMenu ? 1 : 0}
              onClick={() => setUserMenu((v) => !v)}>
              <b>{me.name} <span className="whocaret" data-open={userMenu ? 1 : 0}>▾</span></b>
              <em>{role ? role.name : ""}{REQUIRE_SIGN_IN ? "" : " · codes off"}</em>
            </button>
            <input ref={restoreRef} type="file" accept="application/json" style={{ display: "none" }}
              onChange={(e) => { restoreBackup(e.target.files[0]); e.target.value = ""; }} />
            {userMenu && (<>
              <div className="menu-backdrop" onClick={() => setUserMenu(false)} />
              <div className="usermenu-pop">
                <button onClick={() => { setUserMenu(false); setView("myday"); }}>My Day</button>
                <button onClick={() => { setUserMenu(false); setView("truck"); }}>My Truck</button>
                <button onClick={() => { setUserMenu(false); setView("assist"); }}>Ask the Poolmaster</button>
                <button onClick={() => { setUserMenu(false); setClockOpen(true); }}>Time clock</button>
                <button onClick={() => { setUserMenu(false); setTimeOffOpen(true); }}>Book time off{pendingTimeOff > 0 ? ` (${pendingTimeOff})` : ""}</button>
                {role && role.id === "owner" && (<>
                  <button onClick={() => { setUserMenu(false); downloadBackup(); }}>Backup</button>
                  <button onClick={() => { setUserMenu(false); restoreRef.current && restoreRef.current.click(); }}>Restore</button>
                  <button onClick={() => { setUserMenu(false); setServerOpen(true); }}>Server</button>
                </>)}
                {(SERVER || REQUIRE_SIGN_IN) && (
                  <button onClick={() => { setUserMenu(false); if (SERVER) supabase.auth.signOut(); else { setStaffId(null); setView("clients"); } }}>Sign out</button>
                )}
              </div>
            </>)}
          </div>
        </div> : (
          <div className="btnrow">
            <button className="btn ghost" onClick={() => setClockOpen(true)}>Time clock</button>
          </div>
        )}
      </div>

      {status && <div className="panel pad" style={{ marginBottom: 12, color: "#DC1515", fontSize: 13 }}>{status}</div>}

      {!me && REQUIRE_SIGN_IN && (
        <>
          <SignIn staff={staff} roles={roles} onSignIn={setStaffId} onTimeClock={() => setClockOpen(true)} />
          {clockOpen && (
            <TimeClockSheet staff={staff} roles={roles} punches={data.punches || []} canSeeAll={false}
              onPunch={punch} onClose={() => setClockOpen(false)} />
          )}
        </>
      )}

      {me && (<>

      {SERVER && me.unlinked && (
        <div className="notice" style={{ margin: "0 0 10px", borderColor: "#F2C063", background: "#FDF6E7" }}>
          This sign-in isn't linked to an employee profile yet, so it has technician-level access only.
          Ask the owner to open Employee Profiles and put <b>{serverEmail}</b> on your profile — access fixes itself on the next sign-in.
        </div>
      )}

      {!fieldOnly && (() => {
        const navItems = officeNavItems({
          can, isOwner, isMgr, isOwnerRole: !!(role && role.id === "owner"), view,
          activeSpecialCount, followupOpenCount, repairOpenCount,
        });
        const goNav = (item) => {
          setPhoneNavOpen(false);
          if (item.kind === "quotes") setQuotesListOpen(true);
          else if (item.kind === "staff") setStaffOpen(true);
          else if (item.view) setView(item.view);
        };
        const navIcon = (item, size = 16) => {
          switch (item.icon) {
            case "customers": return <CustomersIcon size={size} />;
            case "calendar": return <CalendarIcon size={size} />;
            case "retail": return <RetailIcon size={size} />;
            case "special": return <SpecialOrderIcon size={size} />;
            case "tick": return <TickIcon size={size} />;
            case "quote": return <QuoteIcon size={size} />;
            case "wrench": return <WrenchIcon size={size} />;
            case "fleet": return <FleetIcon size={size} />;
            case "dashboard": return <DashboardIcon size={size} />;
            case "phone": return <CommIcon v="phone" size={size} />;
            case "ask": return <AskIcon size={size} />;
            case "staff": return <StaffIcon size={size} />;
            default: return null;
          }
        };
        return (<>
      <div className="mainnav">
        {NAV_GROUPS.map((g) => {
          const inGroup = navItems.filter((item) => (item.group || "daily") === g.id);
          if (!inGroup.length) return null;
          return (
            <React.Fragment key={g.id}>
              <div className="navgroup" data-g={g.id}>{g.label}</div>
              {inGroup.map((item) => (
                <button key={item.id} data-g={g.id} data-cobrowse={item.cobrowse || undefined} data-on={item.on ? 1 : 0}
                  title={item.title || undefined} onClick={() => goNav(item)}>
                  {navIcon(item)} {item.label}
                </button>
              ))}
            </React.Fragment>
          );
        })}
        {isOwner && <button className="navflip" title="Move the menu to the other side of the screen" onClick={flipNav}>{navSide === "right" ? "⇤ Menu on left" : "⇥ Menu on right"}</button>}
      </div>
      {phoneNavOpen && (
        <div className="posdrawer-wrap" role="dialog" aria-modal="true" aria-label="Menu" onClick={() => setPhoneNavOpen(false)}>
          <div className="posdrawer" onClick={(e) => e.stopPropagation()}>
            <div className="phonenav-head">
              <h2>Menu</h2>
              <button type="button" className="btn ghost mini" onClick={() => setPhoneNavOpen(false)}>Close</button>
            </div>
            <div className="phonenav">
              {NAV_GROUPS.map((g) => {
                const inGroup = navItems.filter((item) => (item.group || "daily") === g.id);
                if (!inGroup.length) return null;
                return (
                  <React.Fragment key={g.id}>
                    <div className="navgroup" data-g={g.id}>{g.label}</div>
                    {inGroup.map((item) => (
                      <button key={item.id} data-g={g.id} data-cobrowse={item.cobrowse || undefined} data-on={item.on ? 1 : 0}
                        title={item.title || undefined} onClick={() => goNav(item)}>
                        {navIcon(item, 18)} {item.label}
                      </button>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
        </>);
      })()}
      {!fieldOnly && (navHist.length > 0 || (view === "clients" && (selected || editing))) && (
        <div style={{ margin: "8px 0 2px" }}>
          <button className="btn ghost mini" data-cobrowse="nav.back" onClick={goBack}>
            ‹ Back{view === "clients" && (selected || editing) ? " to customer search" : ""}
          </button>
        </div>
      )}

      <div className="grid" data-solo={(soloBoard || (!selected && !editing)) ? 1 : 0} data-detail={(!soloBoard && (selected || editing)) ? 1 : 0}>
        {!soloBoard && (selected || editing) && (
          <div className="panel">
            <div className="pad" style={{ borderBottom: "1px solid var(--line)" }}>
              {can("edit") && (
                <button className="btn icon" style={{ width: "100%", justifyContent: "center", marginBottom: 10, padding: "11px 16px" }}
                  title="Add a new customer"
                  onClick={() => { const b = blank(); setDraft(b); setEditing(true); setSelId(b.id); setView("clients"); }}>
                  <PlusIcon size={14} /> Add New Customer
                </button>
              )}
              {can("edit") && (
                <button className="btn ghost icon" style={{ width: "100%", justifyContent: "center", marginBottom: 10, padding: "11px 16px" }}
                  title="Build and send a quote for the selected customer"
                  onClick={() => {
                    if (!selected) { setStatus("Pick a customer first, then Send a quote."); return; }
                    setView("clients"); setQuoteFocusId(null); setQuoteAutoNew(false); setQuoteOpen(true);
                  }}>
                  <QuoteIcon size={15} /> Send a quote
                </button>
              )}
              <input placeholder="Search name, address, contact, phone, notes" value={q} onChange={(e) => setQ(e.target.value)} style={{ padding: "11px 10px" }} />
              <div className="filters">
                <select value={fType} onChange={(e) => { const v = e.target.value; setFType(v); if (v === "retail") setFDay("all"); }}>
                  <option value="all">All types</option><option value="service">Service</option>
                  <option value="retail">Retail</option><option value="both">Both</option>
                  <option value="trade">Trade</option>
                  <option value="watertest">Water testing</option>
                </select>
                {showDayFilter(fType) && (
                <select value={fDay} onChange={(e) => setFDay(e.target.value)}>
                  <option value="all">Any day</option>{DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                )}
                <select value={fCx} onChange={(e) => setFCx(e.target.value)} title="Property structure — skimmers, returns, water features, extras. Used to choose which tech to schedule.">
                  <option value="all">Any property complexity</option><option value="low">Routine (1–3)</option>
                  <option value="mid">Moderate (4–7)</option><option value="high">Complex (8–10)</option>
                </select>
              </div>
            </div>
            {browsing && recentRows.length === 0 ? (
              <div className="empty">Search a name, address, phone or contact to pull up an account.</div>
            ) : filtered.length === 0 ? (
              <div className="empty">No clients match. Clear the filters, or add a client.</div>
            ) : (browsing
              ? [{ key: "recent", title: "Last viewed", rows: recentRows }]
              : [{ key: "results", title: `${filtered.length} match${filtered.length === 1 ? "" : "es"}`, rows: filtered }])
              .filter((g) => g.rows.length)
              .map((g) => (
              <div key={g.key}>
                <div className="listhead">{g.title}</div>
                {g.rows.map((c) => (
              <button key={c.id} className="clientrow" data-on={c.id === selId ? 1 : 0}
                onClick={() => openClient(c.id)}>
                <div className="cr-name">{c.name}</div>
                <div className="cr-meta">
                  <span className="pill" data-t={c.type}>{TYPE_LABEL[c.type]}</span>
                  {c.trade && <span className="pill" data-t="both">Trade</span>}
                  {c.deactivated && <span className="stoptag">Inactive</span>}
                  {(c.billing || {}).noService && <span className="stoptag">Do not service</span>}
                  {c.type !== "retail" && !c.trade && <>
                    <span>{clientDays(c).length ? FREQ_LABEL[c.frequency] + " · " + clientDays(c).join(", ") : "Unscheduled"}</span>
                    <Complexity n={effectiveCx(c)} />
                  </>}
                  {c.dog && <span style={{ color: "var(--sun)", fontWeight: 700 }}>Dog</span>}
                  {(flagShows(c) || autoFlags(c, null).length > 0) && (
                    <span style={{ color: "var(--clay)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <FlagIcon size={12} filled />Flagged
                    </span>
                  )}
                  {(() => {
                    const so = (data.specialOrders || []).filter((o) => o.customerId === c.id && o.status !== "pickedup" && !o.archived);
                    if (!so.length) return null;
                    const ready = so.some((o) => o.status === "received");
                    return (
                      <span style={{ color: ready ? "#0B7A3B" : "var(--sun)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <SpecialOrderIcon size={12} />{ready ? "Order ready" : "Order in"}
                      </span>
                    );
                  })()}
                </div>
              </button>
                ))}
              </div>
            ))}
          </div>

        )}

      {view === "myday" ? (
        <div className="panel" style={{ padding: 13 }}>
          <MyDayView me={me} clients={clients} techs={data.techs || DEFAULT_TECHS} jobs={data.jobs || []}
            fieldOnly={fieldOnly} specialOrders={data.specialOrders || []}
            parts={data.parts || []} onConsumeParts={consumeParts} onTransfer={transferStock}
            serviceTypes={data.serviceTypes || []} visits={visits} tests={tests} photos={photos}
            saveVisits={saveVisits} saveTests={saveTests} savePhotos={savePhotos}
            onSaveJobs={(next) => save({ ...data, jobs: stampJobs(next) })}
            jobTypes={data.jobTypes && data.jobTypes.length ? data.jobTypes : DEFAULT_JOB_TYPES}
            onWoClose={closeWorkOrder}
            onOnMyWay={onMyWayText}
            onAutoReport={autoComms.reports ? (clientId, v) => { sendReportFor(clientId, v).then((r) => { if (r.ok) setStatus(`Service report emailed to ${r.to}.`); }); } : undefined}
            jumpJobId={mydayJump} onJumpDone={() => setMydayJump(null)}
            onOpen={(id) => openClient(id)}
            onAddFollowup={(clientId, note) => save({ ...data, followups: [...(data.followups || []), { id: cid(), note, customerId: clientId, forStaffId: "", dueDate: fmtDate(new Date()), createdBy: me ? me.name : "", at: new Date().toISOString(), done: false }] })}
            onDamageFlag={(clientId, msg) => save({ ...data, clients: (data.clients || []).map((c) => {
              if (c.id !== clientId) return c;
              const f = c.flag || {};
              const message = f.on && f.message ? f.message + "\n" + msg : msg;
              return { ...c, flag: { ...f, on: true, scope: "both", message, updated: fmtDate(new Date()) } };
            }) })}
            onPick={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : view === "truck" ? (
        <div className="panel" style={{ padding: 13 }}>
          <TruckView me={me} parts={data.parts || []} techs={data.techs || DEFAULT_TECHS} onTransfer={transferStock} tools={data.tools || []} staff={staff} onToolsSave={(next) => save({ ...data, tools: next })} />
        </div>
      ) : view === "ai" ? (
        <div className="panel" style={{ padding: 13 }}>
          <AIReceptionistView data={data} save={save} me={me} clients={clients}
            onPick={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : view === "assist" ? (
        <div className="panel" style={{ padding: 13 }}>
          <AssistantView me={me} data={data} save={save} canTeach={isOwner || (role && role.id === "manager")} />
        </div>
      ) : view === "inventory" ? (
        <div className="panel" style={{ padding: 0 }}>
          <CatalogueSheet inline parts={data.parts || []} services={data.services || []} terms={data.terms || []} revCentres={data.revCentres || []} serviceTypes={data.serviceTypes || []} can={can} isOwner={isOwner}
            purchaseOrders={data.purchaseOrders || []} me={me} techs={data.techs || DEFAULT_TECHS} staff={staff} fleetUnits={data.fleetUnits || []} onTransfer={transferStock}
            onSavePOs={(list) => save({ ...data, purchaseOrders: list })}
            onFinalizePO={(po) => {
              const nb = { ...defNumbering(), ...(data.numbering || {}) };
              const n = nb.po || 2001;
              const bp = data.businessProfile || {};
              const done = { ...po, poNo: fmtPO(n), status: "ordered", orderedBy: me ? me.name : "", orderedDate: fmtDate(new Date()),
                shipTo: { name: bp.name || "Pool Master Canada", address: bp.address || "", phone: bp.phone || "" } };
              save({ ...data, purchaseOrders: (data.purchaseOrders || []).map((x) => (x.id === po.id ? done : x)), numbering: { ...nb, po: n + 1 } });
              printPO(done);
            }}
            supplierInfo={data.supplierInfo || {}}
            onSaveSupplierInfo={(name, info) => save({ ...data, supplierInfo: { ...(data.supplierInfo || {}), [name]: info } })}
            nextPoNo={fmtPO(({ ...defNumbering(), ...(data.numbering || {}) }).po || 2001)}
            onReceivePO={(po) => {
              const parts = (data.parts || []).map((p) => {
                const it = (po.items || []).find((i) => i.code && p.code && i.code.trim().toLowerCase() === p.code.trim().toLowerCase());
                return it ? { ...p, qty: String(num(p.qty) + num(it.qty)) } : p;
              });
              save({ ...data, parts,
                purchaseOrders: (data.purchaseOrders || []).map((x) => (x.id === po.id ? { ...x, status: "received", receivedBy: me ? me.name : "", receivedDate: fmtDate(new Date()) } : x)) });
            }}
            onSave={(nextParts, nextServices, nextTerms, nextCentres, nextTypes) => { save({ ...data, parts: nextParts, services: nextServices, terms: nextTerms, revCentres: nextCentres, serviceTypes: nextTypes }); setStatus("Inventory saved."); }}
            onClose={() => {}} />
        </div>
      ) : view === "sales" ? (
        <div className="panel" style={{ padding: 13 }}>
          <SalesView mode="dashboard" clients={clients} allVisits={visits} posSales={data.posSales || []} loading={salesLoading} isOwner={isOwner}
            onPick={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : view === "ar" ? (
        <div className="panel" style={{ padding: 13 }}>
          <ARView clients={clients} allVisits={visits} posSales={data.posSales || []} loading={salesLoading}
            onCharge={chargeAccount} onCompose={(x) => setCompose(x)} onLogComm={logClientComm}
            onRunBilling={runMonthlyBilling} terms={data.terms || []} me={me}
            onPatchVisit={(clientId, visitId, next) => saveVisits(clientId, (visits[clientId] || []).map((v) => (v.id === visitId ? { ...v, ...next } : v)))}
            onPick={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : view === "retail" ? (
        <div className="panel" style={{ padding: 13 }}>
          <PosView parts={data.parts || []} services={data.services || []} serviceTypes={data.serviceTypes || []} clients={clients} me={me} isOwner={isOwner} revCentres={data.revCentres || []} staff={staff}
            posSales={data.posSales || []} posHeld={data.posHeld || []}
            onSale={recordPosSale} onVoid={voidPosSale} onHold={holdSale} onDropHeld={dropHeld}
            onEmail={(payload) => setCompose(payload)}
            onAddCustomer={(name) => { const b = blank(); if (name) b.name = name; setDraft(b); setEditing(true); setSelId(b.id); setView("clients"); }} />
        </div>
      ) : view === "followups" ? (
        <div className="panel" style={{ padding: 13 }}>
          <FollowupsView followups={data.followups || []} clients={clients} staff={staff} me={me} isOwner={isOwner}
            onSave={(followups) => save({ ...data, followups })}
            onPick={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : view === "repairs" ? (
        <div className="panel" style={{ padding: 13 }}>
          <WorkshopView repairs={data.repairs || []} clients={clients} staff={staff} me={me}
            terms={data.terms || []}
            allSales={data.posSales || []} allJobs={data.jobs || []} onHoldSale={(h) => save({ ...data, posHeld: [...(data.posHeld || []), h] })}
            rentalUnits={data.rentalUnits || []} onRentalSave={rentalSave} onRentalCheckout={rentalCheckout} onRentalCheckIn={rentalCheckIn}
            isMgr={!!(role && ["owner", "manager"].includes(role.id))} onOpenCard={(id) => setCardOpen(id)}
            businessProfile={data.businessProfile || {}} parts={data.parts || []}
            onSaveProfile={(bp) => save({ ...data, businessProfile: bp })}
            onCompleteTicket={(repairsNext, fu) => save({ ...data, repairs: repairsNext, followups: fu ? [...(data.followups || []), fu] : (data.followups || []) })}
            onWarrantyRef={(seed) => { setWrefSeed(seed); setView("warrantyref"); }}
            onUseParts={(items, dir) => {
              const parts = partsAfterMove(data.parts, items, dir);
              save({ ...data, parts, purchaseOrders: dir < 0 ? withAutoOrders(data.parts, parts, data.purchaseOrders || []) : (data.purchaseOrders || []) });
            }}
            onSaveRepairs={(repairs) => save({ ...data, repairs })}
            onCreateTicket={(t) => {
              const nb = { ...defNumbering(), ...(data.numbering || {}) };
              let ticket = { ...t, ticketNo: "RP-" + nb.rp, uploadToken: (crypto.randomUUID ? crypto.randomUUID() : cid() + cid()) };
              // A drop-off under a brand-new name creates a real customer profile in the same
              // save, so they show up in customer look-up right away. Retail-only — a repair
              // never puts anyone on the service schedule.
              let nextClients = clients;
              if (!ticket.customerId && String(ticket.customerName || "").trim() && ticket.customerName !== "Walk-in") {
                const name = ticket.customerName.trim();
                const cust = stripServiceFields({ ...blank(), type: "retail", name, address: ticket.address || "",
                  contacts: [{ id: cid(), name, role: "Owner", phone: ticket.phone || "", email: ticket.email || "", comms: defComms(true) }] });
                nextClients = [...clients, cust];
                ticket = { ...ticket, customerId: cust.id };
              } else if (ticket.customerId && String(ticket.email || "").trim()) {
                // An email captured at drop-off lands back on a profile that was missing one.
                nextClients = clients.map((c) => {
                  if (c.id !== ticket.customerId) return c;
                  const k0 = (c.contacts || [])[0];
                  if (!k0 || String(k0.email || "").trim()) return c;
                  return { ...c, contacts: c.contacts.map((k, i) => (i === 0 ? { ...k, email: ticket.email.trim() } : k)) };
                });
              }
              save({ ...data, clients: nextClients, numbering: { ...nb, rp: nb.rp + 1 }, repairs: [...(data.repairs || []), ticket] });
              return ticket;
            }}
            onCompose={(x) => setCompose(x)}
            onPick={(id) => { openClient(id); setView("clients"); }}
            openTicketNo={repairJump} onOpenTicketDone={() => setRepairJump(null)} />
        </div>
      ) : view === "special" ? (
        <div className="panel" style={{ padding: 13 }}>
          <SpecialOrdersView specialOrders={data.specialOrders || []} clients={clients} parts={data.parts || []} me={me}
            seed={soSeed} onSeedDone={() => setSoSeed(null)} onHoldSale={holdSale}
            purchaseOrders={data.purchaseOrders || []}
            onSavePOs={(list) => save({ ...data, purchaseOrders: list })}
            onSave={saveSpecialOrder} onDelete={deleteSpecialOrder} onCompose={(x) => setCompose(x)}
            onOpenClient={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : view === "warrantyref" ? (
        <div className="panel" style={{ padding: 13 }}>
          <WarrantyRefView parts={data.parts || []} clients={clients} allSales={data.posSales || []} allJobs={data.jobs || []}
            seed={wrefSeed} onSeedDone={() => setWrefSeed(null)} />
        </div>
      ) : view === "promo" ? (
        <div className="panel" style={{ padding: 13 }}>
          <PromoView campaigns={data.campaigns || []} clients={clients} sales={data.posSales || []} repairs={data.repairs || []}
            me={me} businessProfile={data.businessProfile || {}}
            onSave={(next) => save({ ...data, campaigns: next })} />
        </div>
      ) : view === "fleet" && isMgr ? (
        <div className="panel" style={{ padding: 13 }}>
          <FleetView units={data.fleetUnits || []} staff={staff} me={me}
            onSave={(fleetUnits) => save({ ...data, fleetUnits })} />
        </div>
      ) : view === "week" ? (
        <div className="panel" style={{ padding: 13, minWidth: 0 }}>
          <ScheduleView clients={clients} techs={data.techs || DEFAULT_TECHS} schedule={data.schedule} parts={data.parts || []} followups={data.followups || []} repairs={data.repairs || []}
            jobTypes={data.jobTypes && data.jobTypes.length ? data.jobTypes : DEFAULT_JOB_TYPES} onWoBilling={confirmWoBilling}
            onOpenGuided={(j) => { setMydayJump(j.id); setView("myday"); }}
            onBuildQuote={(clientId) => { openClient(clientId); setView("clients"); setQuoteFocusId(null); setQuoteAutoNew(false); setQuoteOpen(true); }}
            onCompose={(x) => setCompose(x)} onLogComm={logClientComm} meName={me ? me.name : ""} isOwner={isOwner} canAdjust={isMgr}
            shopAddress={(data.businessProfile || {}).address || ""}
            onGeo={(id, geo) => save({ ...data, clients: (data.clients || []).map((c) => (c.id === id ? { ...c, geo } : c)) })}
            staff={staff} timeOff={data.timeOff || []} serviceTypes={data.serviceTypes || []} services={data.services || []}
            jobs={data.jobs || []} onSaveJobs={(next) => save({ ...data, jobs: stampJobs(next) })}
            mode={schedMode} setMode={setSchedMode}
            tools={data.tools || []} meId={me ? me.id : ""} onToolsSave={(next) => save({ ...data, tools: next })}
            businessProfile={data.businessProfile || {}} onSaveProfile={(bp) => save({ ...data, businessProfile: bp })}
            onSaveTechs={(techs) => save({ ...data, techs })}
            onSaveSchedule={(schedule) => save({ ...data, schedule })}
            terms={data.terms || []}
            onBookService={isMgr ? (() => setWorkOrder({ ...blankJob(), kind: "service" })) : undefined}
            onPick={(id) => { openClient(id); setView("clients"); }} />
        </div>
      ) : (
        <>
          <div className="panel">
            {(selected || editing) && (
              <button type="button" className="btn ghost mini mobileback" onClick={() => { setEditing(false); setSelId(null); }}>‹ All customers</button>
            )}
            {editing ? (
              <EditForm draft={draft} change={change} setDraft={setDraft} onSave={commit} can={can} isOwner={isOwner} isMgr={isMgr}
                allClients={clients} parts={data.parts || []} me={me}
                tiers={data.levels} techs={data.techs || DEFAULT_TECHS} onManageTiers={() => setTiersOpen(true)}
                onCancel={() => setEditing(false)} onDelete={() => remove(draft.id)}
                isNew={!clients.some((c) => c.id === draft.id)} />
            ) : !selected ? (
              /* Clean landing — nothing open until a customer is searched. */
              (() => {
                const matches = q.trim() ? clients.filter((c) =>
                  [c.name, c.address, c.billingAddress, c.notes, ...(c.contacts || []).map((k) => `${k.name} ${k.phone} ${k.email}`)]
                    .join(" ").toLowerCase().includes(q.trim().toLowerCase())) : [];
                const noMatch = !!q.trim() && matches.length === 0;
                return (
              <div style={{ maxWidth: 620, margin: "48px auto", textAlign: "center", padding: "0 16px" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: "var(--ink)", marginBottom: 14, letterSpacing: 0.2 }}>Search a customer</div>
                <input data-cobrowse="customers.search" autoFocus placeholder="name, address, contact, phone, email" value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ padding: "17px 18px", fontSize: 18, borderRadius: 12, boxShadow: "0 6px 20px rgba(16,32,55,.08)" }} />
                {noMatch ? (
                  <div className="sub" style={{ marginTop: 14, color: "var(--ink)", fontWeight: 650 }}>No customer found matching “{q.trim()}”. They are not in our system yet.</div>
                ) : q.trim() ? (
                  <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 10, maxHeight: 340, textAlign: "left" }}>
                    {matches.slice(0, 10).map((c) => (
                      <button key={c.id} type="button" className="poslookupitem" onClick={() => openClient(c.id)}>
                        <b>{c.name}{c.trade ? " · Trade" : ""}{c.deactivated ? " · INACTIVE" : ""}</b>
                        <span className="sub" style={{ marginTop: 0 }}>{c.address || ((c.contacts || [])[0] || {}).phone || "No address on file"}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="btnrow" style={{ justifyContent: "center", marginTop: 18, gap: 10 }}>
                  {can("edit") && (
                    <button className={`btn icon${noMatch ? " addpulse" : ""}`}
                      style={noMatch
                        ? { padding: "17px 34px", fontSize: 17.5, fontWeight: 750, transition: "padding .3s ease, font-size .3s ease" }
                        : { padding: "13px 20px", fontSize: 15, transition: "padding .3s ease, font-size .3s ease" }}
                      data-cobrowse="customers.add" onClick={() => { const b = blank(); if (noMatch) b.name = q.trim(); setDraft(b); setEditing(true); setSelId(b.id); }}>
                      <PlusIcon size={noMatch ? 17 : 14} /> Add New Customer
                    </button>
                  )}
                </div>
              </div>
                );
              })()
            ) : (
              <Detail c={selected} tiers={data.levels} can={can} photos={photos[selected.id]} visits={visits[selected.id]}
                onOpenLinked={(id) => openClient(id)} allClientsForLinks={clients}
                specialOrders={data.specialOrders || []}
                savePhotos={savePhotos} setStatus={setStatus} onZoom={setLightbox}
                onOpenVisits={(side) => setVisitsOpen(side || "all")}
                tests={tests[selected.id]}
                onToggleAutoCharge={(v) => setAutoCharge(selected.id, v)}
                onAddCard={() => setCardOpen(selected.id)}
                followupsOpen={(data.followups || []).filter((f) => f.customerId === selected.id && !f.done && visibleFollowup(f, me ? me.id : "", isOwner))}
                repairsOpen={(data.repairs || []).filter((t) => t.customerId === selected.id && !t.archived && t.status !== "COMPLETED")}
                repairHistoryRows={repairHistory(data.repairs || [], selected.id, staff, data.businessProfile || {}, fmtDate(new Date()))}
                quotes={quotes[selected.id] || []}
                repairsForQuotes={(data.repairs || []).filter((t) => t.customerId === selected.id)}
                onOpenQuote={(id) => { setQuoteFocusId(id); setQuoteAutoNew(false); setQuoteOpen(true); }}
                onNewQuote={() => { setQuoteFocusId(null); setQuoteAutoNew(true); setQuoteOpen(true); }}
                onOpenRepairQuote={(ticketNo) => { setRepairJump(ticketNo); setView("repairs"); }}
                businessProfileForDocs={data.businessProfile || {}}
                termsForDocs={data.terms || []}
                onWarrantyRef={(seed) => { setWrefSeed(seed); setView("warrantyref"); }}
                onAddFollowup={(note) => save({ ...data, followups: [...(data.followups || []), { id: cid(), note, customerId: selected.id, forStaffId: "", dueDate: "", createdBy: me ? me.name : "", at: new Date().toISOString(), done: false }] })}
                onDoneFollowup={(fid) => save({ ...data, followups: (data.followups || []).map((f) => (f.id === fid ? { ...f, done: true, doneBy: me ? me.name : "", doneAt: new Date().toISOString() } : f)) })}
                onToggleNoService={(v, reason, note) => setNoService(selected.id, v, reason, note)}
                onSetPayMethod={(v) => setPayMethod(selected.id, v)}
                onEditFlag={() => setFlagEdit(true)}
                onCompose={(x) => setCompose(x)}
                onOpenTests={() => setTestsOpen(true)}
                onLogComm={(cat, note) => {
                  const now = new Date();
                  const entry = { id: cid(), cat, date: fmtDate(now), time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"), note, by: me ? me.name : "" };
                  save({ ...data, clients: clients.map((x) => (x.id === selected.id ? { ...x, commLog: [...(x.commLog || []), entry] } : x)) });
                }}
                onDeleteComm={(id) => save({ ...data, clients: clients.map((x) => (x.id === selected.id ? { ...x, commLog: (x.commLog || []).filter((e) => e.id !== id) } : x)) })}
                onChargeNow={(scope) => chargeCustomer(selected.id, scope)}
                onPortalLink={portalLinkFor}
                onEdit={() => { setDraft(JSON.parse(JSON.stringify(selected))); setEditing(true); }} />
            )}
          </div>
        </>
      )}
      </div>

      </>)}

      {me && clockOpen && (
        <TimeClockSheet staff={staff} roles={roles} punches={data.punches || []} canSeeAll={can("staff")}
          onPunch={punch} onClose={() => setClockOpen(false)} />
      )}

      {timeOffOpen && me && (
        <TimeOffSheet me={me} staff={staff} roles={roles} requests={data.timeOff || []} canApprove={can("staff")}
          onSubmit={submitTimeOff} onDecide={decideTimeOff} onReply={replyTimeOff} onClose={() => setTimeOffOpen(false)} />
      )}

      {msgsOpen && me && (
        <MessagesSheet me={me} staff={staff} roles={roles} messages={data.messages || []}
          onSend={sendMessage} onReadMany={markReadMany} onClose={() => setMsgsOpen(false)} />
      )}

      {Array.isArray(fuReminders) && fuReminders.length > 0 && (
        <div className="overlay">
          <div className="sheet" style={{ maxWidth: 540 }}>
            <div className="bar">
              <div>
                <strong style={{ fontSize: 16 }}>⏰ Follow-up reminder{fuReminders.length === 1 ? "" : "s"}</strong>
                <div className="sub">{fuReminders.length === 1 ? "One person is" : fuReminders.length + " people are"} waiting on a call back</div>
              </div>
              <div className="btnrow">
                {can("edit") && <button className="btn" onClick={() => { setFuReminders([]); setView("followups"); }}>Open Follow-ups</button>}
                <button className="btn ghost" onClick={() => setFuReminders([])}>Later</button>
              </div>
            </div>
            <div className="pad">
              {fuReminders.slice(0, 8).map((f) => {
                const cust = f.customerId ? (clients.find((c) => c.id === f.customerId) || {}).name : "";
                const name = f.contactName || cust || "";
                const od = f.dueDate < fmtDate(new Date());
                return (
                  <div key={f.id} className="visit" style={{ borderLeft: `4px solid ${od ? "#DC1515" : "#0887C8"}` }}>
                    <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 180, fontWeight: 650 }}>{name || "Follow up"}{f.contactPhone ? " · " : ""}
                        {f.contactPhone && <a className="link" href={"tel:" + String(f.contactPhone).replace(/[^0-9+]/g, "")}>{f.contactPhone}</a>}</span>
                      <span className="doc" style={{ background: od ? "#DC1515" : "#5A6E86" }}>{od ? "OVERDUE · " : "due "}{f.dueDate}</span>
                    </div>
                    <div className="sub" style={{ marginTop: 4 }}>{f.note}</div>
                  </div>
                );
              })}
              {fuReminders.length > 8 && <div className="sub">…and {fuReminders.length - 8} more in Follow-ups.</div>}
            </div>
          </div>
        </div>
      )}

      {textsOpen && canTexts && (
        <TextsInbox inbound={smsInbox} sent={smsSent} clients={clients}
          onReply={replyText} onMarkRead={markTextsRead}
          onDeleteThread={isOwner ? deleteTextThread : null}
          onOpenClient={(id) => { setTextsOpen(false); openClient(id); }}
          onClose={() => setTextsOpen(false)} />
      )}

      {staffOpen && (
        <StaffSheet staff={staff} roles={roles} punches={data.punches || []} onOpenRoles={() => setRolesOpen(true)}
          onSave={(next) => {
            // Position changed on someone with a sign-in → sync their server access level.
            (data.staff || []).forEach((before) => {
              const after = next.find((x) => x.id === before.id);
              if (after && after.email && before.roleId !== after.roleId) {
                supabase.functions.invoke("manage-logins", { body: { action: "set-role", email: after.email, position: after.roleId } }).catch(() => {});
              }
            });
            // A licensed employee automatically gets a technician column on the schedule + a truck.
            let techsNext = data.techs || DEFAULT_TECHS;
            const adds = next.filter((s) => s.active !== false && s.hasLicense && !techsNext.some((t) => t.staffId === s.id));
            if (adds.length) techsNext = [...techsNext, ...adds.map((s) => ({ id: cid(), name: s.name || "New tech", staffId: s.id, vehicle: s.vehicle || "" }))];
            techsNext = techsNext.map((t) => { const s = next.find((x) => x.id === t.staffId); return s && s.vehicle && s.vehicle !== t.vehicle ? { ...t, vehicle: s.vehicle } : t; });
            save({ ...data, staff: next, techs: techsNext });
          }} onClose={() => setStaffOpen(false)} />
      )}

      {workOrder && (
        <WorkOrderSheet draft={workOrder} clients={clients} serviceTypes={data.serviceTypes || []} parts={data.parts || []}
          terms={data.terms || []}
          jobTypes={data.jobTypes && data.jobTypes.length ? data.jobTypes : DEFAULT_JOB_TYPES}
          onSave={(j) => {
            if (!isMgr) { setStatus("Only a manager or the owner can book a service call onto the schedule."); return; }
            // Prerequisite rule: e.g. spring vacuuming can't be booked while a
            // deferred system startup is still open on that property.
            const jts = data.jobTypes && data.jobTypes.length ? data.jobTypes : DEFAULT_JOB_TYPES;
            const blocker = j.wfTypeId ? wfPrereqBlock(jts, data.jobs || [], j.clientId, j.wfTypeId) : null;
            if (blocker) {
              const bt = jts.find((t) => t.id === (blocker.wf || {}).jobTypeId) || jts.find((t) => t.id === blocker.wfTypeId);
              setStatus(`Can't book ${(jts.find((t) => t.id === j.wfTypeId) || {}).name || "this"} yet — "${(bt || {}).name || "a required job"}" is still open on this property (${blocker.workOrder || "in the holding bin"}). Complete it first.`);
              return;
            }
            // Same service-perimeter gate as the schedule — this path books too.
            const gcl = clients.find((c) => c.id === j.clientId);
            const gb = gcl && gcl.geo ? serviceBand(gcl.geo.lat, gcl.geo.lng) : null;
            if (!j.areaOkBy && gb && (gb.band === "ask" || gb.band === "never")) { setWoAreaAsk({ j, band: gb, cl: gcl }); return; }
            const nb = { ...defNumbering(), ...(data.numbering || {}) };
            const n = nb.wo;
            // Booking a service call on a retail-only account upgrades them to Retail + Service.
            const nextClients = (data.clients || []).map((c) => (c.id === j.clientId && c.type === "retail" ? { ...c, type: "both" } : c));
            save({ ...data, clients: nextClients, jobs: [...(data.jobs || []), { ...j, workOrder: fmtWO(n), bookedBy: me ? me.name : "", bookedDate: fmtDate(new Date()) }], numbering: { ...nb, wo: n + 1 } });
            setWorkOrder(null); setStatus(""); setView("week");
          }}
          onClose={() => setWorkOrder(null)} />
      )}

      {woAreaAsk && (
        <AreaGateSheet info={woAreaAsk} isOwner={isOwner} meName={me ? me.name : ""}
          onApprove={(j) => {
            if (!isMgr) { setWoAreaAsk(null); setStatus("Only a manager or the owner can book a service call onto the schedule."); return; }
            setWoAreaAsk(null);
            const nb = { ...defNumbering(), ...(data.numbering || {}) };
            const n = nb.wo;
            const nextClients = (data.clients || []).map((c) => (c.id === j.clientId && c.type === "retail" ? { ...c, type: "both" } : c));
            save({ ...data, clients: nextClients, jobs: [...(data.jobs || []), { ...j, workOrder: fmtWO(n), bookedBy: me ? me.name : "", bookedDate: fmtDate(new Date()) }], numbering: { ...nb, wo: n + 1 } });
            setWorkOrder(null); setStatus(""); setView("week");
          }}
          onClose={() => setWoAreaAsk(null)} />
      )}

      {serverOpen && (
        <ServerSheet serverEmail={serverEmail} onImport={importToServer} onGoShared={goShared} onGoLocal={goLocal}
          onBackup={downloadBackup} onClose={() => setServerOpen(false)} />
      )}

      {catalogueOpen && (
        <CatalogueSheet parts={data.parts || []} services={data.services || []} terms={data.terms || []} revCentres={data.revCentres || []} serviceTypes={data.serviceTypes || []} can={can} isOwner={isOwner}
          purchaseOrders={data.purchaseOrders || []} me={me} techs={data.techs || DEFAULT_TECHS} staff={staff} fleetUnits={data.fleetUnits || []} onTransfer={transferStock}
          onSavePOs={(list) => save({ ...data, purchaseOrders: list })}
          onFinalizePO={(po) => {
            const nb = { ...defNumbering(), ...(data.numbering || {}) };
            const n = nb.po || 2001;
            const done = { ...po, poNo: fmtPO(n), status: "ordered", orderedBy: me ? me.name : "", orderedDate: fmtDate(new Date()) };
            save({ ...data, purchaseOrders: (data.purchaseOrders || []).map((x) => (x.id === po.id ? done : x)), numbering: { ...nb, po: n + 1 } });
            printPO(done);
          }}
          onSave={(nextParts, nextServices, nextTerms, nextCentres, nextTypes) => { save({ ...data, parts: nextParts, services: nextServices, terms: nextTerms, revCentres: nextCentres, serviceTypes: nextTypes }); setCatalogueOpen(false); }}
          onClose={() => setCatalogueOpen(false)} />
      )}

      {visitsOpen && selected && (
        <VisitSheet client={selected} visits={visits[selected.id] || []} can={can} isOwner={isOwner} revCentres={data.revCentres || []} initialSide={visitsOpen}
          onSendReport={(v) => sendReportFor(selected.id, v).then((r) => setStatus(r.ok ? `Service report emailed to ${r.to}.` : `Couldn't send the report — ${r.reason}.`))}
          autoStart={visitAutoNew} seed={bookSeed} parts={data.parts || []} services={data.services || []} terms={data.terms || []}
          onBooked={({ clientId, quoteId }) => markQuoteBooked(clientId, quoteId)}
          saveVisits={saveVisits} onClose={() => { setVisitsOpen(false); setVisitAutoNew(false); setBookSeed(null); }} />
      )}

      {quoteOpen && selected && (
        <QuoteSheet client={selected} quotes={quotes[selected.id] || []}
          autoStart={quoteAutoNew || ((quotes[selected.id] || []).length === 0 && !quoteFocusId)}
          focusId={quoteFocusId} meName={me ? me.name : ""}
          parts={data.parts || []} services={data.services || []} terms={data.terms || []}
          saveQuotes={saveQuotes} onEmail={(payload) => setCompose(payload)}
          onBook={(s) => { setQuoteOpen(false); setQuoteFocusId(null); setQuoteAutoNew(false); setBookSeed(s); setVisitAutoNew(true); setVisitsOpen("service"); }}
          onClose={() => { setQuoteOpen(false); setQuoteFocusId(null); setQuoteAutoNew(false); }} />
      )}

      {quotesListOpen && (
        <QuotesListSheet
          rows={quoteListRows({ quotesMap: quotes, repairs: data.repairs || [], clients, today: fmtDate(new Date()) })}
          onOpen={(r) => {
            setQuotesListOpen(false);
            if (r.kind === "repair") { setRepairJump(r.ticketNo || r.ticketId); setView("repairs"); return; }
            if (r.clientId) { setSelId(r.clientId); setQuoteFocusId(r.quoteId); setQuoteAutoNew(false); setView("clients"); setQuoteOpen(true); }
          }}
          onClose={() => setQuotesListOpen(false)} />
      )}

      {approvedOpen && (
        <ApprovedQuotesSheet clients={clients} quotesMap={quotes} onBookQuote={bookFromQuote}
          onOpen={(clientId) => { setSelId(clientId); setApprovedOpen(false); setQuoteOpen(true); }}
          onClose={() => setApprovedOpen(false)} />
      )}

      {compose && (
        <ComposeSheet client={compose.client || selected} kind={compose.kind} to={compose.to} emailCat={compose.emailCat}
          clients={clients} businessProfile={(data && data.businessProfile) || {}}
          subject={compose.subject} body={compose.body} onClose={() => setCompose(null)}
          onSendText={!supabase ? undefined : async (dest, text) => {
            const cl = compose.client || selected;
            const { data: r, error } = await supabase.functions.invoke("send-sms", {
              body: { to: dest, body: text, clientId: cl ? cl.id : null } });
            if (error || (r && r.error)) throw new Error((r && r.error) || (error && error.message) || "Couldn't send the text.");
            if (cl) {
              const now = new Date();
              const entry = { id: cid(), cat: "general", date: fmtDate(now),
                time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"),
                note: `Text sent from business line to ${dest}: ${text}`, by: me ? me.name : "" };
              save({ ...data, clients: clients.map((x) => (x.id === cl.id ? { ...x, commLog: [...(x.commLog || []), entry] } : x)) });
            }
          }}
          onSendMail={!supabase ? undefined : async (dest, subj, text, cat) => {
            const cl = compose.client || selected;
            const { data: r, error } = await supabase.functions.invoke("send-email", {
              body: { to: dest, subject: subj, body: text, category: cat || compose.emailCat || "service", clientId: cl ? cl.id : null } });
            if (error || (r && r.error)) throw new Error((r && r.error) || (error && error.message) || "Couldn't send the email.");
            if (cl) {
              const now = new Date();
              const entry = { id: cid(), cat: "general", date: fmtDate(now),
                time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"),
                note: `Email sent from ${(r && r.from) || "the company address"} to ${dest}: ${subj}`, by: me ? me.name : "" };
              save({ ...data, clients: clients.map((x) => (x.id === cl.id ? { ...x, commLog: [...(x.commLog || []), entry] } : x)) });
            }
          }} />
      )}

      {cardOpen && (() => {
        const cc = clients.find((x) => x.id === cardOpen);
        return cc ? <CardSheet client={cc} onSave={(card) => saveCard(cc.id, card)} onClose={() => setCardOpen(null)} /> : null;
      })()}

      {rolesOpen && (
        <RoleSheet roles={roles} currentRole={role ? role.id : ""} staff={staff}
          onSave={(next) => save({ ...data, roles: next })} onClose={() => setRolesOpen(false)} />
      )}

      {testsOpen && selected && (
        <WaterTestSheet client={selected} tests={tests[selected.id] || []} can={can}
          saveTests={saveTests} onClose={() => setTestsOpen(false)} />
      )}

      {flagEdit && selected && (
        <FlagSheet client={selected}
          onSave={(flag) => saveFlag(selected.id, flag)} onClose={() => setFlagEdit(false)} />
      )}

      {tiersOpen && (
        <LevelSheet levels={data.levels || DEFAULT_LEVELS} clients={clients}
          onSave={(levels) => save({ ...data, levels })} onClose={() => setTiersOpen(false)} />
      )}

      {dupPop && (
        <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setDupPop(null); }}>
          <div className="sheet" style={{ maxWidth: 520 }}>
            <div className="bar">
              <div>
                <strong style={{ fontSize: 16 }}>This address is already in the system</strong>
                <div className="sub">{dupPop.finished.address}</div>
              </div>
              <button className="btn ghost" onClick={() => setDupPop(null)}>Back</button>
            </div>
            <div className="pad">
              <div className="notice" style={{ marginBottom: 12 }}>
                <b>{dupPop.existing.name}</b> already has a profile at this address. It could be a duplicate —
                or the home sold and a new owner is standing at the counter.
              </div>
              <button className="btn" style={{ width: "100%", justifyContent: "center", marginBottom: 14, padding: "13px 16px" }}
                onClick={() => { const id = dupPop.existing.id; setDupPop(null); setEditing(false); openClient(id); }}>
                Don't add — open {dupPop.existing.name} and work under the original profile
              </button>
              <div className="sechead" style={{ marginTop: 0 }}>Or replace the old profile</div>
              <label>Reason for deactivating {dupPop.existing.name} (required)</label>
              <select value={dupPop.reason} onChange={(e) => setDupPop({ ...dupPop, reason: e.target.value })} style={{ marginBottom: 10 }}>
                <option value="">&mdash; pick a reason &mdash;</option>
                {["Home sold — new owner", "Duplicate profile", "Customer moved away", "Business closed / changed hands", "Other"]
                  .map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button className="btn warn" style={{ width: "100%", justifyContent: "center" }} disabled={!dupPop.reason}
                onClick={() => { commitClient(dupPop.finished, { id: dupPop.existing.id, reason: dupPop.reason }); setDupPop(null); }}>
                Deactivate {dupPop.existing.name} and save the new customer
              </button>
              <div className="sub" style={{ marginTop: 8 }}>
                The deactivation is logged with the reason, the date, and your name{me ? ` (${me.name})` : ""}. The old
                profile keeps its history and stays searchable, marked Inactive.
              </div>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox.signedUrl || lightbox.dataUrl} alt={lightbox.caption || lightbox.tag} />
          <div className="cap2">{lightbox.tag}{lightbox.caption ? " — " + lightbox.caption : ""} · tap anywhere to close</div>
        </div>
      )}
    </div>
  );
}

/* ---------- who sees what ---------- */
function Keypad({ length, value, onChange, onSubmit, label }) {
  const press = (d) => { if (value.length < length) onChange(value + d); };
  return (
    <div className="keypad">
      <div className="pindots" aria-label={label}>
        {Array.from({ length }, (_, i) => <span key={i} data-on={i < value.length ? 1 : 0} />)}
      </div>
      <div className="keys">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" onClick={() => press(d)}>{d}</button>
        ))}
        <button type="button" className="alt" onClick={() => onChange(value.slice(0, -1))}>&larr;</button>
        <button type="button" onClick={() => press("0")}>0</button>
        <button type="button" className="go" onClick={onSubmit} disabled={value.length !== length}>OK</button>
      </div>
    </div>
  );
}

function SignIn({ staff, roles, onSignIn, onTimeClock }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const submit = () => {
    const who = staff.find((x) => x.active !== false && x.pin === pin);
    if (!who) { setErr("That code doesn't match an employee."); setPin(""); return; }
    setErr(""); setPin(""); onSignIn(who.id);
  };
  return (
    <div className="panel" style={{ maxWidth: 420, margin: "40px auto", padding: 22, textAlign: "center" }}>
      <div style={{ display: "inline-block", marginBottom: 16 }}><PoolMasterLogo height={40} /></div>
      <div style={{ fontWeight: 750, fontSize: 15, color: "var(--ink)" }}>Enter your 4 digit code</div>
      <div className="sub" style={{ marginBottom: 14 }}>Every page needs a code. Punching the clock doesn't.</div>
      <Keypad length={4} value={pin} onChange={(v) => { setPin(v); setErr(""); }} onSubmit={submit} label="Access code" />
      {err && <div className="alert" style={{ marginTop: 12 }}>{err}</div>}
      <button className="btn ghost" style={{ marginTop: 14, width: "100%", justifyContent: "center" }} onClick={onTimeClock}>
        Time clock
      </button>
    </div>
  );
}

function StatusBar({ status, decidedBy, decidedAt, nameOf }) {
  const st = TO_STATUS[status] || TO_STATUS.pending;
  return (
    <div className="statuswrap">
      <div className="statusbar"><span style={{ width: st.pct + "%", background: st.colour }} /></div>
      <div className="statussteps">
        {TO_STEPS.map((lab, i) => (
          <span key={lab} data-on={i <= st.step ? 1 : 0} style={i === st.step ? { color: st.colour } : undefined}>{lab}</span>
        ))}
      </div>
      <div className="statusline" style={{ color: st.colour }}>
        {st.label}
        {decidedBy && (status === "approved" || status === "declined") && (
          <em> · {nameOf(decidedBy)}{decidedAt ? " on " + new Date(decidedAt).toLocaleDateString() : ""}</em>
        )}
      </div>
    </div>
  );
}

function TimeOffSheet({ me, staff, roles, requests, canApprove, onSubmit, onDecide, onReply, onClose }) {
  const [ack, setAck] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("new");
  const [replyText, setReplyText] = useState({}); // request id → draft reply

  const nameOf = (id) => (staff.find((x) => x.id === id) || {}).name || "Someone";
  const mine = requests.filter((r) => r.staffId === me.id).slice().reverse();
  const pending = requests.filter((r) => r.status === "pending").slice().reverse();
  const thisYear = String(new Date().getFullYear());
  const daysApproved = requests.filter((r) => r.staffId === me.id && r.status === "approved" && r.from.startsWith(thisYear))
    .reduce((a, r) => a + daysInRequest(r), 0);
  const daysPending = mine.filter((r) => r.status === "pending").reduce((a, r) => a + daysInRequest(r), 0);
  const everyone = requests.slice().reverse();
  const daysOut = from ? Math.round((parseDate(from) - new Date()) / 86400000) : null;
  const shortNotice = daysOut !== null && daysOut < 14;
  const valid = parseDate(from) && parseDate(to) && parseDate(to) >= parseDate(from);

  const submit = () => {
    onSubmit({ id: cid(), staffId: me.id, from, to, note: note.trim(), status: "pending", at: new Date().toISOString() });
    setFrom(""); setTo(""); setNote(""); setTab("mine");
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 580 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Book time off</strong>
            <div className="sub">{me.name}</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          {!ack ? (
            <>
              <div className="ackbox">
                <TimeOffIcon size={26} />
                <p>All time off requests must be approved by your manager.</p>
                <p>
                  I understand I need 2 weeks notice to ensure Pool Master Canada can allow for scheduling
                  adjustments. We understand that things come up.
                </p>
                <p>Please add the time off you're requesting and send it for approval.</p>
              </div>
              <div className="btnrow" style={{ marginTop: 12 }}>
                <button className="btn" onClick={() => setAck(true)}>I understand</button>
                {canApprove && pending.length > 0 && (
                  <button className="btn ghost" onClick={() => { setAck(true); setTab("approve"); }}>
                    {pending.length} waiting on approval
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="seg" style={{ marginBottom: 13 }}>
                {[["new", "New request"], ["mine", `My requests${mine.length ? " (" + mine.length + ")" : ""}`]]
                  .concat(canApprove ? [["approve", `To approve${pending.length ? " (" + pending.length + ")" : ""}`], ["all", "Everyone"]] : [])
                  .map(([v, l]) => (
                    <button key={v} type="button" data-on={tab === v ? 1 : 0} onClick={() => setTab(v)}>{l}</button>
                  ))}
              </div>

              {tab === "new" && (
                <>
                  <div className="fields">
                    <div><label>First day off</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
                    <div><label>Last day off</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
                    <div className="f-wide">
                      <label>Reason or notes for your manager</label>
                      <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="Family trip — happy to swap my Thursday route." />
                    </div>
                  </div>
                  {valid && (
                    <div className="sub" style={{ marginTop: 8 }}>
                      That's {daysInRequest({ from, to })} day{daysInRequest({ from, to }) === 1 ? "" : "s"} off
                      {daysOut >= 0 ? `, ${daysOut} days from today.` : "."}
                    </div>
                  )}
                  {shortNotice && (
                    <div className="notice" style={{ marginTop: 10 }}>
                      That's {daysOut < 0 ? "in the past" : daysOut + " days out"} — less than the two weeks notice.
                      Send it anyway and say why; your manager decides.
                    </div>
                  )}
                  <div className="btnrow" style={{ marginTop: 12 }}>
                    <button className="btn" disabled={!valid} onClick={submit}>Send for approval</button>
                  </div>
                </>
              )}

              {tab === "mine" && (
                <div className="counts" style={{ marginBottom: 12 }}>
                  <div className="count-cell" data-zero={daysApproved ? 0 : 1}>
                    <div className="n">{daysApproved}</div><div className="l">Days approved {thisYear}</div>
                  </div>
                  <div className="count-cell" data-zero={daysPending ? 0 : 1}>
                    <div className="n">{daysPending}</div><div className="l">Days awaiting</div>
                  </div>
                  <div className="count-cell" data-zero={mine.length ? 0 : 1}>
                    <div className="n">{mine.length}</div><div className="l">Requests</div>
                  </div>
                </div>
              )}

              {tab !== "new" && (
                (tab === "mine" ? mine : tab === "all" ? everyone : pending).length === 0 ? (
                  <div className="empty">
                    {tab === "mine" ? "You haven't asked for any time off."
                      : tab === "all" ? "No requests on file." : "Nothing waiting on you."}
                  </div>
                ) : (tab === "mine" ? mine : tab === "all" ? everyone : pending).map((r) => {
                  const days = daysInRequest(r);
                  const notice = Math.round((parseDate(r.from) - new Date(r.at)) / 86400000);
                  return (
                    <div key={r.id} className="msgrow torow">
                      <div className="mtop">
                        <strong>{tab === "mine" ? `${r.from} → ${r.to}` : nameOf(r.staffId)}</strong>
                        <span className="tostat" data-s={r.status}>{r.status}</span>
                      </div>
                      <div className="sub" style={{ marginTop: 2 }}>
                        {tab !== "mine" && `${r.from} → ${r.to} · `}
                        {days} day{days === 1 ? "" : "s"} · asked {new Date(r.at).toLocaleDateString()}
                        {notice < 14 && <b style={{ color: "var(--clay)" }}> · {notice} days notice</b>}
                      </div>
                      {r.note && <div className="mbody">{r.note}</div>}
                      {(r.replies || []).length > 0 && (
                        <div style={{ marginTop: 8, borderLeft: "3px solid var(--tile)", paddingLeft: 10 }}>
                          {(r.replies || []).map((p) => (
                            <div key={p.id} style={{ marginTop: 5 }}>
                              <span className="sub" style={{ marginTop: 0 }}><b>{p.byId === me.id ? "You" : p.by}</b> · {new Date(p.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                              <div style={{ fontSize: 13 }}>{p.note}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {onReply && r.status === "pending" && (tab === "approve" || (tab === "mine" && r.staffId === me.id)) && (
                        <div style={{ marginTop: 8 }}>
                          {tab === "approve" && !(replyText[r.id] || "").trim() && (
                            <button className="btn ghost mini" style={{ marginBottom: 6 }}
                              onClick={() => setReplyText((m) => ({ ...m, [r.id]: "Who is covering your shift?" }))}>
                              Ask: who's covering your shift?
                            </button>
                          )}
                          <div style={{ display: "flex", gap: 7 }}>
                            <input placeholder={tab === "approve" ? "Reply before deciding — they'll get a message" : "Reply to your manager"} value={replyText[r.id] || ""}
                              style={{ flex: 1 }} onChange={(e) => setReplyText((m) => ({ ...m, [r.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter" && (replyText[r.id] || "").trim()) { onReply(r.id, replyText[r.id]); setReplyText((m) => ({ ...m, [r.id]: "" })); } }} />
                            <button className="btn ghost mini" disabled={!(replyText[r.id] || "").trim()}
                              onClick={() => { onReply(r.id, replyText[r.id]); setReplyText((m) => ({ ...m, [r.id]: "" })); }}>Reply</button>
                          </div>
                        </div>
                      )}
                      <StatusBar status={r.status} decidedBy={r.decidedBy} decidedAt={r.decidedAt} nameOf={nameOf} />
                      {tab === "approve" && (
                        <div className="btnrow" style={{ marginTop: 9 }}>
                          <button className="btn mini" onClick={() => onDecide(r.id, "approved")}>Approve</button>
                          <button className="btn warn mini" onClick={() => onDecide(r.id, "declined")}>Decline</button>
                        </div>
                      )}
                      {tab === "mine" && r.status === "pending" && (
                        <div className="btnrow" style={{ marginTop: 9 }}>
                          <button className="btn ghost mini" onClick={() => onDecide(r.id, "cancelled")}>Withdraw</button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Staff messaging as real conversations: messages carry a threadId, replies stay
// in the thread, and anyone in a conversation can add another staff member —
// who then sees the whole history (employee ↔ manager ↔ owner sharing).
export function msgThreads(messages, meId) {
  const map = new Map();
  for (const m of messages || []) {
    const tid = m.threadId || m.id; // pre-thread messages: each is its own conversation
    if (!map.has(tid)) map.set(tid, { id: tid, msgs: [] });
    map.get(tid).msgs.push(m);
  }
  const out = [];
  for (const t of map.values()) {
    t.msgs.sort((a, b) => String(a.at).localeCompare(String(b.at)));
    const people = new Set();
    t.msgs.forEach((m) => { people.add(m.from); (m.to || []).forEach((x) => people.add(x)); });
    if (!people.has(meId)) continue; // not my conversation — invisible
    t.participants = [...people];
    t.unread = t.msgs.filter((m) => (m.to || []).includes(meId) && !(m.read || []).includes(meId)).length;
    t.last = t.msgs[t.msgs.length - 1];
    out.push(t);
  }
  out.sort((a, b) => String(b.last.at).localeCompare(String(a.last.at)));
  return out;
}

function MessagesSheet({ me, staff, roles, messages, onSend, onReadMany, onClose }) {
  const [tab, setTab] = useState("in");
  const [openId, setOpenId] = useState(null);
  const [to, setTo] = useState([]);
  const [body, setBody] = useState("");
  const [reply, setReply] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const nameOf = (id) => (staff.find((x) => x.id === id) || {}).name || "Someone";
  const roleOf = (id) => {
    const st = staff.find((x) => x.id === id);
    return st ? (roles.find((r) => r.id === st.roleId) || {}).name || "" : "";
  };
  const threads = msgThreads(messages, me.id);
  const cur = openId ? threads.find((t) => t.id === openId) : null;
  const others = staff.filter((x) => x.active !== false && x.id !== me.id);
  const unreadTotal = threads.reduce((n, t) => n + t.unread, 0);
  const toggle = (id) => setTo((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const when = (iso) => `${new Date(iso).toLocaleDateString()} ${clockTime(iso)}`;

  const openThread = (t) => {
    setOpenId(t.id); setReply(""); setAddOpen(false);
    if (t.unread) onReadMany(t.msgs.filter((m) => (m.to || []).includes(me.id) && !(m.read || []).includes(me.id)).map((m) => m.id));
  };
  const send = () => {
    if (!to.length || !body.trim()) return;
    const tid = cid();
    onSend({ id: cid(), threadId: tid, from: me.id, to, body: body.trim(), at: new Date().toISOString(), read: [] });
    setBody(""); setTo([]); setTab("in"); setOpenId(tid);
  };
  const sendReply = () => {
    if (!cur || !reply.trim()) return;
    const dest = cur.participants.filter((x) => x !== me.id);
    onSend({ id: cid(), threadId: cur.id, from: me.id, to: dest, body: reply.trim(), at: new Date().toISOString(), read: [] });
    setReply("");
  };
  const addPerson = (x) => {
    if (!cur) return;
    const dest = [...cur.participants.filter((p) => p !== me.id), x.id];
    onSend({ id: cid(), threadId: cur.id, from: me.id, to: dest,
      body: `— ${me.name} added ${x.name} to this conversation —`, at: new Date().toISOString(), read: [] });
    setAddOpen(false);
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 640 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>{cur ? cur.participants.filter((x) => x !== me.id).map(nameOf).join(", ") || "Conversation" : "Messages"}</strong>
            <div className="sub">{cur ? `${cur.participants.length} in this conversation` : "Between staff — stays inside this app"}</div>
          </div>
          <div className="btnrow">
            {cur && <button className="btn ghost" onClick={() => setAddOpen((v) => !v)}>+ Add person</button>}
            {cur && <button className="btn ghost" onClick={() => setOpenId(null)}>All conversations</button>}
            <button className="btn ghost" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="pad">
          {!cur && (
            <div className="seg" style={{ marginBottom: 13 }}>
              {[["in", `Conversations${unreadTotal ? " (" + unreadTotal + ")" : ""}`], ["new", "New message"]].map(([v, l]) => (
                <button key={v} type="button" data-on={tab === v ? 1 : 0} onClick={() => setTab(v)}>{l}</button>
              ))}
            </div>
          )}

          {!cur && tab === "new" && (
            <>
              <label>Send to</label>
              <div className="picklist">
                {others.map((x) => (
                  <button key={x.id} type="button" className="pick" data-on={to.includes(x.id) ? 1 : 0} onClick={() => toggle(x.id)}>
                    <span className="pnm">{x.name}</span>
                    <span className="prole">{roleOf(x.id)}</span>
                  </button>
                ))}
              </div>
              <div className="btnrow" style={{ marginTop: 8 }}>
                <button className="btn ghost mini" onClick={() => setTo(others.map((x) => x.id))}>Everyone</button>
                {to.length > 0 && <button className="btn ghost mini" onClick={() => setTo([])}>Clear</button>}
              </div>
              <div style={{ marginTop: 12 }}>
                <label>Message</label>
                <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
                  placeholder="Pump for the Kemper job is on the bench, ready for pickup." />
              </div>
              <div className="btnrow" style={{ marginTop: 10 }}>
                <button className="btn" disabled={!to.length || !body.trim()} onClick={send}>
                  Send{to.length ? ` to ${to.length}` : ""}
                </button>
              </div>
            </>
          )}

          {!cur && tab === "in" && (
            threads.length === 0 ? <div className="empty">No conversations yet — start one with New message.</div>
              : threads.map((t) => (
                <div key={t.id} className="msgrow" data-unread={t.unread ? 1 : 0} onClick={() => openThread(t)} style={{ cursor: "pointer" }}>
                  <div className="mtop">
                    <strong>{t.participants.filter((x) => x !== me.id).map(nameOf).join(", ") || "Just you"}
                      {t.unread > 0 && <span className="badge" style={{ position: "static", marginLeft: 8 }}>{t.unread}</span>}</strong>
                    <span className="sub" style={{ marginTop: 0 }}>{when(t.last.at)}</span>
                  </div>
                  <div className="mbody" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {t.last.from === me.id ? "You: " : `${nameOf(t.last.from)}: `}{t.last.body}
                  </div>
                  {t.unread > 0 && <span className="newtag">New — tap to open</span>}
                </div>
              ))
          )}

          {cur && (<>
            {addOpen && (
              <div style={{ marginBottom: 10 }}>
                <label>Add to this conversation — they'll see everything above</label>
                <div className="picklist">
                  {others.filter((x) => !cur.participants.includes(x.id)).map((x) => (
                    <button key={x.id} type="button" className="pick" onClick={() => addPerson(x)}>
                      <span className="pnm">{x.name}</span>
                      <span className="prole">{roleOf(x.id)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ maxHeight: "46vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, padding: "4px 2px" }}>
              {cur.msgs.map((m) => (
                <div key={m.id} style={{ alignSelf: m.from === me.id ? "flex-end" : "flex-start", maxWidth: "82%" }}>
                  {m.from !== me.id && <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6E86", marginBottom: 2 }}>{nameOf(m.from)}</div>}
                  <div style={{
                    padding: "8px 12px", borderRadius: 14, whiteSpace: "pre-wrap", fontSize: 14,
                    background: m.from === me.id ? "#0B6FBE" : "#EEF3F9",
                    color: m.from === me.id ? "#fff" : "#12233B",
                    borderBottomRightRadius: m.from === me.id ? 4 : 14,
                    borderBottomLeftRadius: m.from !== me.id ? 4 : 14 }}>
                    {m.body}
                  </div>
                  <div style={{ fontSize: 11, color: "#7C8DA5", textAlign: m.from === me.id ? "right" : "left", marginTop: 2 }}>{when(m.at)}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} style={{ flex: 1 }}
                placeholder="Reply…" />
              <button className="btn" onClick={sendReply} disabled={!reply.trim()}>Send</button>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}


function TimeClockSheet({ staff, roles, punches, canSeeAll, onPunch, onClose }) {
  const [code, setCode] = useState("");
  const [who, setWho] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const [span, setSpan] = useState("day");

  const openPunch = (id) => punches.find((p) => p.staffId === id && !p.outAt);
  const submit = () => {
    const found = staff.find((x) => x.active !== false && x.clock === code);
    if (!found) { setErr("That 9 digit code doesn't match an employee."); setCode(""); return; }
    setErr(""); setWho(found);
  };
  const punch = () => {
    const open = openPunch(who.id);
    onPunch(who.id, !open);
    setDone(open ? "out" : "in");
  };

  const now = new Date();
  const weekStart = startOfWeek(now);
  const inSpan = (p) => {
    const d = new Date(p.inAt);
    return span === "day" ? fmtDate(d) === fmtDate(now) : d >= weekStart;
  };
  const totalFor = (id) => punches.filter((p) => p.staffId === id && p.outAt && inSpan(p))
    .reduce((a, p) => a + hoursBetween(p.inAt, p.outAt), 0);

  const rows = (canSeeAll ? staff : staff.filter((x) => who && x.id === who.id)).filter((x) => x.active !== false);
  const roleName = (id) => (roles.find((r) => r.id === id) || {}).name || "";

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 560 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Time clock</strong>
            <div className="sub">{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          {!who ? (
            <>
              <div style={{ textAlign: "center", fontWeight: 700, color: "var(--ink)", marginBottom: 4 }}>Enter your 9 digit code</div>
              <Keypad length={9} value={code} onChange={(v) => { setCode(v); setErr(""); }} onSubmit={submit} label="Clock code" />
              {err && <div className="alert" style={{ marginTop: 12 }}>{err}</div>}
            </>
          ) : done ? (
            <div className="punchdone" data-dir={done}>
              <div className="big">{done === "in" ? "Punched in" : "Punched out"}</div>
              <div className="who4">{who.name} · {roleName(who.roleId)}</div>
              <div className="stamp">{now.toLocaleDateString()} at {now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</div>
              <div className="sub" style={{ marginTop: 10 }}>
                {span === "day" ? "Today" : "This week"} so far: {hhmm(totalFor(who.id))}
              </div>
              <button className="btn" style={{ marginTop: 14 }} onClick={onClose}>Done</button>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontWeight: 750, fontSize: 15, color: "var(--ink)" }}>{who.name}</div>
              <div className="sub" style={{ marginBottom: 4 }}>{roleName(who.roleId)}</div>
              {openPunch(who.id) && (
                <div className="sub">On the clock since {clockTime(openPunch(who.id).inAt)}</div>
              )}
              <button className={"punchbtn" + (openPunch(who.id) ? " out" : "")} onClick={punch}>
                <span className="pb-ic">{openPunch(who.id) ? "\u23fb" : "\u25b6"}</span>
                {openPunch(who.id) ? "Punch Out" : "Punch In"}
              </button>
              <button className="btn ghost" onClick={() => { setWho(null); setCode(""); }}>Not you?</button>
            </div>
          )}

          {(canSeeAll || who) && (
            <>
              <div className="sechead" style={{ marginTop: 18 }}>
                Timesheet
                <div className="seg" style={{ margin: 0 }}>
                  <button type="button" data-on={span === "day" ? 1 : 0} onClick={() => setSpan("day")}>Day</button>
                  <button type="button" data-on={span === "week" ? 1 : 0} onClick={() => setSpan("week")}>Week</button>
                </div>
              </div>
              <table className="salest">
                <thead><tr><th>Employee</th><th>Status</th><th>{span === "day" ? "Today" : "This week"}</th></tr></thead>
                <tbody>
                  {rows.map((x) => {
                    const open = openPunch(x.id);
                    return (
                      <tr key={x.id}>
                        <td>{x.name}<span className="sub" style={{ marginTop: 0 }}>{roleName(x.roleId)}</span></td>
                        <td>{open ? <span className="onclock">In since {clockTime(open.inAt)}</span> : <span className="sub" style={{ marginTop: 0 }}>Out</span>}</td>
                        <td className="mono">{hhmm(totalFor(x.id))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {canSeeAll && (
                <div className="sub" style={{ marginTop: 8 }}>
                  Week runs Monday to Sunday. Open punches count once they're closed.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Inventory category tree: category → sub categories. Chemicals also carry a
// Pool / Spa marker; everything flows through to the till buttons and filters.
const PART_TREE = {
  "Chemicals": ["Sanitizers", "Oxidizers / Shock", "Balancers", "Algaecides", "Specialty chemicals"],
  "Pumps": ["Pumps", "Pump parts"],
  "Filters": ["Filters", "Filter parts"],
  "Heaters": ["Heaters", "Heater parts"],
  "Salt systems": ["Salt systems", "Salt system parts"],
  "Chlorinators": ["Chlorinators", "Chlorinator parts"],
  "Robotic cleaners": ["Corded", "Cordless"],
  "Softubs": ["Softubs", "Tub parts", "Hydromate parts"],
};
// Every piece of equipment carries a manufacturer; chemicals have their own maker list.
// Megna and AquaMate are swimming-pool liner manufacturers.
const MANUFACTURERS = ["Hayward", "Pentair", "Jandy / Fluidra", "Jacuzzi", "Softub", "Raypak / Rheem", "AquaMate", "Megna"];
const CHEM_MANUFACTURERS = ["Regal", "Life", "HTH", "Mursatt", "Lawrasons", "Anchem", "Spa Marvel", "SCP"];
// Supplier pick list for every item (typing a new name still works).
const SUPPLIERS = ["SCP", "POOLCORP", "Mursatt", "Lawrasons", "Anchem", "Aquiform", "Central", "AquaMate", "Spa Marvel"];

// Move stock between locations. p.qty is the SHOP shelf; p.trucks = { techId: qty } is
// what each technician carries. Every move is logged on the part so the shop always
// knows where stock went. Returns the updated part, or null if the source is short.
/* ---------- Warranty, serials & deductibles ---------- */
const WARRANTY_SCOPES = ["Complete unit", "Parts", "Labour", "Heat exchanger", "Shell", "Pump"];
// Add whole months to an ISO date; the anniversary DAY is still inside the period.
function addMonthsIso(iso, months) {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
  const day = d.getDate();
  d.setMonth(d.getMonth() + (Number(months) || 0));
  if (d.getDate() < day) d.setDate(0); // clamp Feb 30 → Feb 28 style overflows
  return fmtDate(d);
}
// One coverage term's live status against a purchase date.
function warrantyTermStatus(term, purchaseDate, today) {
  const end = addMonthsIso(purchaseDate, term && term.months);
  return { scope: (term && term.scope) || "", end, covered: !!end && today <= end };
}
// "Year N of M" for a coverage term. A year-only purchase date ("2021") is
// treated as Jan 1 and flagged approximate — never presented as certain.
function warrantyYearOf(term, purchaseDate, today) {
  const months = Number(term && term.months) || 0;
  const pd = String(purchaseDate || "").trim();
  if (!months || !pd) return null;
  const approx = /^\d{4}$/.test(pd);
  const a = new Date((approx ? pd + "-01-01" : pd.slice(0, 10)) + "T12:00:00");
  const b = new Date(String(today).slice(0, 10) + "T12:00:00");
  if (isNaN(a) || isNaN(b) || b < a) return null;
  let m = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) m--;
  const of = Math.ceil(months / 12);
  return { year: Math.min(of, Math.floor(m / 12) + 1), of, approx };
}
// The deductible window: free through the freeMonths anniversary, owed on EVERY
// repair after that, through the end of the longest covering term.
function deductibleFor(warranty, purchaseDate, repairDate) {
  const d = warranty && warranty.deductible;
  if (!d || !(num(d.amount) > 0) || !purchaseDate) return { applies: false };
  const freeUntil = addMonthsIso(purchaseDate, d.freeMonths || 0);
  const maxMonths = Math.max(0, ...((warranty.terms || []).map((t) => Number(t.months) || 0)));
  const coverEnd = addMonthsIso(purchaseDate, maxMonths);
  if (repairDate <= freeUntil) return { applies: false, freeUntil };
  if (coverEnd && repairDate > coverEnd) return { applies: false, freeUntil, expired: true };
  return { applies: true, amount: num(d.amount).toFixed(2), dueFrom: freeUntil, freeUntil };
}
// A repair ticket's outstanding deductible: owed unless paid, fully waived, or disposal.
function deductibleOutstanding(t) {
  const d = t && t.deductible;
  if (!d || !d.applies || t.disposal) return 0;
  if (d.status === "paid") return 0;
  const owed = num(d.amount) - (d.status === "waived" || d.waivedAmount ? num(d.waivedAmount) : 0);
  return Math.max(0, Math.round(owed * 100) / 100);
}
// Serial helpers: warranty items carry per-unit identity next to the counts.
const partHasWarranty = (p) => !!(p && p.warranty && p.warranty.applies);
const serialList = (v) => String(v || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
// Which sale/visit lines still need serials — the shared gate for every close point.
function saleSerialGaps(items, parts) {
  const out = [];
  for (const it of items || []) {
    const code = String(it.code || "").trim().toLowerCase();
    if (!code) continue; // free-typed lines match nothing — accepted
    const p = (parts || []).find((x) => (x.code || "").trim().toLowerCase() === code);
    if (!p || !partHasWarranty(p)) continue;
    const need = Math.max(1, Math.round(num(it.qty) || 1));
    const have = serialList(it.serial).length;
    if (have < need) out.push({ desc: it.desc || p.name || p.code, need, have });
  }
  return out;
}
function applyTransfer(p, from, to, qty, by, serials) {
  const trucks = { ...(p.trucks || {}) };
  const have = (loc) => (loc === "shop" ? num(p.qty) : num(trucks[loc]));
  if (!(qty > 0) || from === to || have(from) < qty) return { ok: false, reason: "Not enough stock at the source location for that transfer." };
  const sers = (serials || []).map((x) => String(x).trim()).filter(Boolean);
  const truckSerials = Object.fromEntries(Object.entries(p.truckSerials || {}).map(([k, v]) => [k, [...(v || [])]]));
  let shelfSerials = [...(p.shelfSerials || [])];
  if (partHasWarranty(p)) {
    if (to !== "shop") {
      // Owner's rule: equipment does not get on a truck without its serial documented.
      if (sers.length !== qty) return { ok: false, reason: `Serial required — ${qty > 1 ? `one serial for each of the ${qty} units` : "this unit's serial"} must be recorded before it leaves the shop.` };
      if (from === "shop") shelfSerials = shelfSerials.filter((x) => !sers.includes(x));
      else {
        const pool = truckSerials[from] || [];
        // moving between trucks: the serials must actually be on the source truck (when tracked)
        if (pool.length && !sers.every((x) => pool.includes(x))) return { ok: false, reason: "Those serials aren't on the source truck." };
        truckSerials[from] = pool.filter((x) => !sers.includes(x));
      }
      truckSerials[to] = [...(truckSerials[to] || []), ...sers];
    } else {
      // give-back to the shelf: return the serials, never orphan them. Legacy truck
      // stock that predates tracking has no serials — that must never block.
      const pool = truckSerials[from] || [];
      const back = sers.length ? sers : pool.slice(0, Math.min(qty, pool.length));
      truckSerials[from] = pool.filter((x) => !back.includes(x));
      shelfSerials = [...shelfSerials, ...back];
    }
    Object.keys(truckSerials).forEach((k) => { if (!(truckSerials[k] || []).length) delete truckSerials[k]; });
  }
  let shopQty = num(p.qty);
  if (from === "shop") shopQty -= qty; else trucks[from] = num(trucks[from]) - qty;
  if (to === "shop") shopQty += qty; else trucks[to] = num(trucks[to]) + qty;
  Object.keys(trucks).forEach((k) => { if (!(num(trucks[k]) > 0)) delete trucks[k]; });
  const now = new Date();
  const entry = { id: cid(), date: fmtDate(now), time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"), by: by || "", from, to, qty, ...(sers.length ? { serials: sers } : {}) };
  const next = { ...p, qty: String(shopQty), trucks, moves: [...(p.moves || []), entry].slice(-60) };
  if (partHasWarranty(p)) { next.truckSerials = truckSerials; next.shelfSerials = shelfSerials; }
  return { ok: true, part: next };
}
const truckTotalOf = (p) => Object.values(p.trucks || {}).reduce((a, v) => a + num(v), 0);

// Warehouse locations = Main warehouse (Shop) + named Fleet trucks.
// Assigned fleet truck → techId (p.trucks[techId]). Unlinked named fleet truck →
// fleet unit id (still p.trucks[id] — extra keys, not a second stock system).
// Empty "Technician N" slots with no vehicle, no fleet truck, and no qty are omitted.
const WAREHOUSE_QTY_HELP = "Click a part in the list to see how many are at the shop and on each truck.";
function isPlaceholderTechName(name) {
  return /^Technician\s+\d+$/i.test(String(name || "").trim());
}
function isWarehouseTruckKind(k) {
  const x = String(k || "").toLowerCase();
  return x === "vehicle" || x === "truck";
}
function warehouseFleetUnitLabel(u) {
  if (!u) return "";
  return String(u.name || [u.year, u.make, u.model].filter(Boolean).join(" ") || u.plate || "").trim();
}
function warehouseFleetAssignedTechId(u, techs, data) {
  const assigned = String((u && u.assignedTo) || "").trim();
  if (!assigned) return "";
  const staff = (data && (data.staff || data.employees)) || [];
  for (const t of techs || []) {
    if (!t || !t.id) continue;
    const staffId = t.staffId || "";
    const emp = staff.find((s) => s && (s.id === staffId || s.id === t.id)) || {};
    if (assigned === t.id || (staffId && assigned === staffId) || (emp.id && assigned === emp.id)) return t.id;
  }
  return "";
}
function warehouseVehicleName(tech, data) {
  const d = data || {};
  const staff = d.staff || d.employees || [];
  const fleetUnits = d.fleetUnits || [];
  const t = tech || {};
  const staffId = t.staffId || "";
  const emp = staff.find((s) => s && (s.id === staffId || s.id === t.id)) || {};
  const linked = (fleetUnits || []).filter((u) => u && !u.retired && isWarehouseTruckKind(u.kind)
    && (u.assignedTo === t.id || (staffId && u.assignedTo === staffId) || (emp.id && u.assignedTo === emp.id)));
  const unit = linked[0];
  const fleetName = warehouseFleetUnitLabel(unit);
  if (fleetName) return fleetName;
  const empVeh = String(emp.vehicle || "").trim();
  if (empVeh) return empVeh;
  return String(t.vehicle || "").trim();
}
function warehouseTechDisplayName(tech, data) {
  const d = data || {};
  const staff = d.staff || d.employees || [];
  const t = tech || {};
  const emp = staff.find((s) => s && (s.id === t.staffId || s.id === t.id));
  const empName = emp ? String(emp.name || "").trim() : "";
  if (empName && !isPlaceholderTechName(empName)) return empName;
  const tName = String(t.name || "").trim();
  if (tName && !isPlaceholderTechName(tName)) return tName;
  if (empName) return empName;
  return tName;
}
function warehouseStockLocations(data) {
  const d = data || {};
  const techs = Array.isArray(d.techs) ? d.techs : [];
  const parts = Array.isArray(d.parts) ? d.parts : [];
  const fleetUnits = Array.isArray(d.fleetUnits) ? d.fleetUnits : [];
  const qtyIds = new Set();
  for (const part of parts) {
    const trucks = (part && part.trucks) || {};
    for (const [id, q] of Object.entries(trucks)) {
      if (num(q) > 0) qtyIds.add(id);
    }
  }
  const shop = { id: "shop", kind: "shop", label: "Main warehouse (Shop)", secondary: "" };
  const rows = [];
  const seen = new Set();
  for (const t of techs) {
    if (!t || !t.id) continue;
    seen.add(t.id);
    const vehicle = warehouseVehicleName(t, d);
    const hasQty = qtyIds.has(t.id);
    if (!vehicle && !hasQty) continue;
    const techName = warehouseTechDisplayName(t, d);
    if (vehicle) {
      const secondary = techName && !isPlaceholderTechName(techName) ? techName : "";
      rows.push({ id: t.id, kind: "truck", label: vehicle, secondary });
    } else {
      rows.push({ id: t.id, kind: "truck", label: techName || t.name || "Truck", secondary: "" });
    }
  }
  // Named Fleet trucks with no linked tech still get a location. Stock key = unit id.
  for (const u of fleetUnits) {
    if (!u || !u.id || u.retired) continue;
    if (!isWarehouseTruckKind(u.kind)) continue;
    if (warehouseFleetAssignedTechId(u, techs, d)) continue;
    const label = warehouseFleetUnitLabel(u);
    if (!label && !qtyIds.has(u.id)) continue;
    if (seen.has(u.id)) continue;
    seen.add(u.id);
    rows.push({ id: u.id, kind: "truck", label: label || u.id, secondary: "" });
  }
  for (const id of qtyIds) {
    if (seen.has(id) || id === "shop") continue;
    rows.push({ id, kind: "truck", label: id, secondary: "" });
  }
  return [shop, ...rows];
}
const warehouseTransferLocations = warehouseStockLocations;

function CatalogueSheet({ parts, services, terms, revCentres, serviceTypes, can, isOwner = false, onSave, onClose, purchaseOrders = [], me, onSavePOs, onFinalizePO, onReceivePO, techs = [], staff = [], fleetUnits = [], onTransfer, inline, supplierInfo = {}, onSaveSupplierInfo, nextPoNo }) {
  const [tab, setTab] = useState("start") // start = tile menu;
  // Parts filter bar: text (part # / description), category, sub category, supplier.
  const [fq, setFq] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fSub, setFSub] = useState("all");
  const [fSup, setFSup] = useState("all");
  const [fMfr, setFMfr] = useState("all");
  const [openId, setOpenId] = useState(null); // selected part (record panel, RB-style)
  const [sortKey, setSortKey] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const [fPart, setFPart] = useState("");
  const [fShowInactive, setFShowInactive] = useState(true);
  const [dTab, setDTab] = useState("pricing"); // record sub-tab: pricing | stock | suppliers | image
  const clearPartFilters = () => { setFq(""); setFPart(""); setFCat("all"); setFSub("all"); setFSup("all"); setFMfr("all"); };
  // Warehouse transfer form (shop ↔ trucks); mirrors the parent save into local pList.
  const [wQty, setWQty] = useState("1");
  const [wFrom, setWFrom] = useState("shop");
  const [wTo, setWTo] = useState("");
  const [wSer, setWSer] = useState("");
  const doTransfer = (p) => {
    const n = num(wQty);
    if (!(n > 0) || !wTo || wFrom === wTo) return;
    const sers = serialList(wSer);
    if (onTransfer && onTransfer(p.id, wFrom, wTo, n, sers)) {
      setPList((L) => L.map((x) => (x.id === p.id ? ((applyTransfer(x, wFrom, wTo, n, me ? me.name : "", sers) || {}).part || x) : x)));
      setWQty("1"); setWSer("");
    }
  };
  // Warehouse from/to labels come from warehouseStockLocations (shop + trucks).
  const [poMsg, setPoMsg] = useState("");
  // Open "holding" POs — one per supplier. Order-more and the add line route items
  // to the right supplier's PO; a mismatch offers to start a separate one.
  const holdings = purchaseOrders.filter((x) => x.status === "holding");
  const [poEditId, setPoEditId] = useState(null);
  const holding = holdings.find((x) => x.id === poEditId) || holdings[0] || null;
  const holdingItemsTotal = holdings.reduce((a, h) => a + (h.items || []).length, 0);
  const newHolding = (supplier, item, by) => ({ id: cid(), poNo: "", status: "holding", supplier: (supplier || "").trim(),
    supplierContact: "", supplierAddress: "", supplierPhone: "", notes: "",
    items: [item], createdBy: by || (me ? me.name : ""), createdDate: fmtDate(new Date()) });
  const setHoldingFor = (h, patch) => onSavePOs(purchaseOrders.map((x) => (x.id === h.id ? { ...x, ...patch } : x)));
  const mergeItem = (h, item, qtyN) => (h.items.some((it) => it.code && item.code && it.code === item.code)
    ? h.items.map((it) => (it.code === item.code ? { ...it, qty: num(it.qty) + qtyN } : it))
    : [...h.items, item]);
  const pastPOs = purchaseOrders.filter((x) => x.status !== "holding").slice().reverse();
  const orderedPOs = purchaseOrders.filter((x) => x.status === "ordered").slice().reverse();
  const [poScreen, setPoScreen] = useState("start"); // start | create | receive | history
  const [poQ, setPoQ] = useState("");
  const [poQty, setPoQty] = useState("1");
  const [poSupPick, setPoSupPick] = useState({}); // matchId -> chosen supplier
  const poMatches = poQ.trim()
    ? (parts || []).filter((p) => p.active !== false).filter((p) => {
        const t = poQ.trim().toLowerCase();
        return (p.code || "").toLowerCase().includes(t) || (p.name || "").toLowerCase().includes(t);
      }).slice(0, 8)
    : [];
  const addPoItem = (part, supplierPick) => {
    const qtyN = num(poQty) > 0 ? num(poQty) : 1;
    const cost = supplierPick === part.supplier2 ? (part.cost2 || part.cost || "") : supplierPick === part.supplier3 ? (part.cost3 || part.cost || "") : (part.cost || "");
    const item = { code: part.code || "", name: part.name || "", qty: qtyN, cost };
    const sup = (supplierPick || part.supplier1 || "").trim();
    if (!holding) {
      onSavePOs([...purchaseOrders, newHolding(sup, item)]);
      setPoQ(""); setPoQty("1");
      return;
    }
    const poSup = (holding.supplier || "").trim();
    if (poSup && sup && poSup.toLowerCase() !== sup.toLowerCase()) {
      // Wrong supplier for this PO — offer the alternate purchase order.
      const other = holdings.find((h) => h.id !== holding.id && (h.supplier || "").trim().toLowerCase() === sup.toLowerCase());
      const msg = other
        ? `${part.name || "This item"} is from ${sup} — it can't be ordered on the ${poSup} purchase order.

Add it to the open ${sup} purchase order instead?`
        : `${part.name || "This item"} is from ${sup} — it can't be ordered on the ${poSup} purchase order.

Start a separate ${sup} purchase order? It will be held here until it's reviewed, adjusted and sent.`;
      if (!window.confirm(msg)) return;
      if (other) { setHoldingFor(other, { items: mergeItem(other, item, qtyN) }); setPoMsg(`Added to the open ${sup} purchase order — flip between POs with the supplier chips above the form.`); }
      else { onSavePOs([...purchaseOrders, newHolding(sup, item)]); setPoMsg(`Started a separate ${sup} purchase order — held until it's reviewed and sent.`); }
      setPoQ(""); setPoQty("1");
      return;
    }
    setHoldingFor(holding, { items: mergeItem(holding, item, qtyN), ...(poSup ? {} : { supplier: sup }) });
    setPoQ(""); setPoQty("1");
  };
  // Goods arrived: every line goes into shop stock; the PO is marked received.
  const receivePO = (po) => {
    if (onReceivePO) onReceivePO(po);
    setPList((L) => L.map((p) => {
      const it = (po.items || []).find((i) => i.code && p.code && i.code.trim().toLowerCase() === p.code.trim().toLowerCase());
      return it ? { ...p, qty: String(num(p.qty) + num(it.qty)) } : p;
    }));
    setPoMsg(`${po.poNo} received — ${po.items.length} line${po.items.length === 1 ? "" : "s"} added to shop stock.`);
  };
  const orderMore = (p) => {
    const qty = num(p.reorderQty) > 0 ? num(p.reorderQty) : 1;
    const item = { code: p.code || "", name: p.name || "", qty, cost: p.cost || "" };
    const sup = (p.supplier1 || "").trim();
    const target = holdings.find((h) => (h.supplier || "").trim() === sup);
    if (target) setHoldingFor(target, { items: mergeItem(target, item, qty) });
    else onSavePOs([...purchaseOrders, newHolding(sup, item)]);
    setPoMsg(`Added ${qty} × ${p.name || p.code || "item"} to the ${sup || "new"} purchase order.`);
  };
  const setHolding = (patch) => onSavePOs(purchaseOrders.map((x) => (x.id === holding.id ? { ...x, ...patch } : x)));
  const setPoItem = (i, k, v) => setHolding({ items: holding.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const dropPoItem = (i) => setHolding({ items: holding.items.filter((_, j) => j !== i) });
  const poTotal = (po) => (po.items || []).reduce((a, it) => a + (num(it.qty) || 0) * (num(it.cost) || 0), 0);
  const [pList, setPList] = useState((parts || []).map((x) => ({ ...x })));
  const warehouseLocs = warehouseStockLocations({ techs, staff, fleetUnits, parts: pList });
  const warehouseTrucks = warehouseLocs.filter((l) => l.kind === "truck");
  const locName = (l) => {
    if (l === "used") return "Used at a stop";
    const hit = warehouseLocs.find((x) => x.id === l);
    if (hit) return hit.secondary ? `${hit.label} (${hit.secondary})` : hit.label;
    if (l === "shop") return "Main warehouse (Shop)";
    const t = (techs || []).find((x) => x.id === l) || {};
    const veh = warehouseVehicleName(t, { techs, staff, fleetUnits, parts: pList });
    const nm = warehouseTechDisplayName(t, { techs, staff, fleetUnits, parts: pList });
    return veh ? (nm ? `${veh} (${nm})` : veh) : (nm || t.name || "Truck");
  };
  const [sList, setSList] = useState((services || []).map((x) => ({ ...x })));
  const [tList, setTList] = useState((terms || []).map((x) => ({ ...x })));
  const [rcList, setRcList] = useState((revCentres || []).map((x) => ({ ...x })));
  const [styList, setStyList] = useState((serviceTypes || []).map((x) => ({ ...x })));
  const [svcMsg, setSvcMsg] = useState("");
  const svcCsvRef = useRef(null);
  const setP = (id, k, v) => setPList((L) => L.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const setS = (id, k, v) => setSList((L) => L.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const setT = (id, k, v) => setTList((L) => L.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const setRc = (id, k, v) => setRcList((L) => L.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const setSty = (id, k, v) => setStyList((L) => L.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const addP = () => { const id = cid(); setPList((L) => [...L, { id, code: "", name: "New part", price: "", cost: "", qty: "0", lowAt: "", active: true }]); setOpenId(id); clearPartFilters(); };
  const addS = () => setSList((L) => [...L, { id: cid(), name: "New service", rate: "", kind: "service", active: true }]);
  const addT = () => setTList((L) => [...L, { id: cid(), name: "New terms", tag: "any", body: "", active: true }]);
  const importT = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const body = String(reader.result || "");
      const name = String(file.name || "Uploaded terms").replace(/\.[^.]+$/, "") || "Uploaded terms";
      setTList((L) => [...L, { id: cid(), name, tag: "any", body, active: true }]);
    };
    reader.readAsText(file);
  };
  const uploadTBody = (id, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const body = String(reader.result || "");
      const nameFromFile = String(file.name || "").replace(/\.[^.]+$/, "");
      setTList((L) => L.map((x) => (x.id === id ? { ...x, body, name: (!x.name || x.name === "New terms") && nameFromFile ? nameFromFile : x.name } : x)));
    };
    reader.readAsText(file);
  };
  const addRc = () => setRcList((L) => [...L, { id: cid(), name: "New revenue centre", active: true }]);
  const addSty = () => setStyList((L) => [...L, { id: cid(), name: "New service type", instructions: "", active: true }]);
  const dropP = (id) => setPList((L) => L.filter((x) => x.id !== id));
  const dropS = (id) => setSList((L) => L.filter((x) => x.id !== id));
  const dropT = (id) => setTList((L) => L.filter((x) => x.id !== id));
  const dropRc = (id) => setRcList((L) => L.filter((x) => x.id !== id));
  const dropSty = (id) => setStyList((L) => L.filter((x) => x.id !== id));
  const exportServiceCsv = () => csvDownload(`service-rates-${new Date().toISOString().slice(0, 10)}.csv`,
    [["id", "name", "desc", "rate", "rateZone2", "kind", "active"],
      ...sList.map((s) => [s.id || "", s.name || "", s.desc || "", s.rate ?? "", s.rateZone2 ?? "", s.kind || "service", s.active === false ? "no" : "yes"])]);
  const importServiceCsv = (file) => {
    if (!file || !isOwner) return;
    const reader = new FileReader();
    reader.onload = () => {
      const res = applyServiceRatesCsv(sList, String(reader.result || ""));
      if (res.error) { setSvcMsg(res.error); return; }
      setSList(res.list);
      onSave(pList, res.list, tList, rcList, styList);
      const n = res.updated + res.added;
      setSvcMsg(`Loaded ${n} service rate${n === 1 ? "" : "s"} (${res.updated} updated, ${res.added} added${res.skipped ? `, ${res.skipped} skipped` : ""}). They now show on the Service Guide and on quotes.`);
    };
    reader.onerror = () => setSvcMsg("Couldn't read that file.");
    reader.readAsText(file);
  };
  const dupCode = (x) => x.code && pList.filter((y) => y.id !== x.id && (y.code || "").trim().toLowerCase() === x.code.trim().toLowerCase()).length > 0;
  // Filtering: text over part # + description; category / sub category / supplier dropdowns.
  const partSuppliers = (p) => [p.supplier1, p.supplier2, p.supplier3].map((s) => (s || "").trim()).filter(Boolean);
  const allCats = [...new Set([...Object.keys(PART_TREE), ...pList.map((p) => (p.category || "").trim()).filter(Boolean)])];
  const allSubs = fCat !== "all"
    ? [...new Set([...(PART_TREE[fCat] || []), ...pList.filter((p) => (p.category || "") === fCat).map((p) => (p.subcategory || "").trim()).filter(Boolean)])]
    : [...new Set(pList.map((p) => (p.subcategory || "").trim()).filter(Boolean))];
  const allSups = [...new Set([...SUPPLIERS, ...pList.flatMap(partSuppliers)])].sort((a, b) => a.localeCompare(b));
  const allMfrs = [...new Set([...MANUFACTURERS, ...CHEM_MANUFACTURERS, ...pList.map((p) => (p.manufacturer || "").trim()).filter(Boolean)])];
  const shownParts = pList.filter((p) => {
    if (!fShowInactive && p.active === false) return false;
    const tp = fPart.trim().toLowerCase();
    if (tp && !(p.code || "").toLowerCase().includes(tp)) return false;
    const t = fq.trim().toLowerCase();
    if (t && !((p.code || "").toLowerCase().includes(t) || (p.name || "").toLowerCase().includes(t))) return false;
    if (fCat !== "all" && (p.category || "") !== fCat) return false;
    if (fSub !== "all" && (p.subcategory || "") !== fSub) return false;
    if (fSup !== "all" && !partSuppliers(p).includes(fSup)) return false;
    if (fMfr !== "all" && (p.manufacturer || "") !== fMfr) return false;
    return true;
  });
  const selPart = pList.find((x) => x.id === openId) || null;
  const showMargins = can ? can("margins") : true;
  const isLow = (p) => p.active !== false && p.lowAt !== "" && p.lowAt != null && num(p.qty) <= num(p.lowAt);
  const lowCount = pList.filter(isLow).length;

  const content = (
    <>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Inventory</strong>
            <div className="sub">Parts double as inventory · services &amp; rates are reusable priced lines</div>
          </div>
          <div className="btnrow">
            {tab !== "start" && tab !== "po" && (
              <button className="btn ghost" onClick={tab === "parts" ? addP : tab === "services" ? addS : tab === "terms" ? addT : tab === "centres" ? addRc : addSty}>{tab === "parts" ? "Add part" : tab === "services" ? "Add service" : tab === "terms" ? "Add terms" : tab === "centres" ? "Add centre" : "Add service type"}</button>
            )}
            <button className="btn" onClick={() => onSave(pList, sList, tList, rcList, styList)}>Save</button>
            {!inline && <button className="btn ghost" onClick={onClose}>Close</button>}
          </div>
        </div>
        <div className="pad">
          {tab === "start" && (
            <div className="posstart" style={{ maxWidth: "none", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
              <button type="button" className="posstart-btn" onClick={() => setTab("parts")}>
                <RetailIcon size={34} />
                <span className="posstart-lbl">Parts &amp; inventory</span>
                <span className="posstart-sub">{pList.length} items · stock, pricing, suppliers</span>
              </button>
              <button type="button" className="posstart-btn" onClick={() => setTab("services")}>
                <StaffIcon size={34} />
                <span className="posstart-lbl">Labour &amp; services</span>
                <span className="posstart-sub">{sList.length} services · Zone 1 &amp; Zone 2 rates</span>
              </button>
              <button type="button" className="posstart-btn" onClick={() => setTab("terms")}>
                <QuoteIcon size={34} />
                <span className="posstart-lbl">Terms &amp; conditions</span>
                <span className="posstart-sub">{tList.length} reusable term block{tList.length === 1 ? "" : "s"}</span>
              </button>
              <button type="button" className="posstart-btn" onClick={() => setTab("centres")}>
                <DashboardIcon size={34} />
                <span className="posstart-lbl">Revenue centres</span>
                <span className="posstart-sub">{rcList.length} centre{rcList.length === 1 ? "" : "s"} for reporting</span>
              </button>
              <button type="button" className="posstart-btn" onClick={() => setTab("servicetypes")}>
                <CalendarIcon size={34} />
                <span className="posstart-lbl">Service types</span>
                <span className="posstart-sub">{styList.length} type{styList.length === 1 ? "" : "s"} with tech checklists</span>
              </button>
            </div>
          )}
          {tab === "start" && (
            <div className="posstart" style={{ margin: "18px auto 0", maxWidth: 420, gridTemplateColumns: "1fr" }}>
              <button type="button" className="posstart-btn" onClick={() => setTab("po")}>
                <SpecialOrderIcon size={34} />
                <span className="posstart-lbl">Purchase orders{holdingItemsTotal ? ` (${holdingItemsTotal})` : ""}</span>
                <span className="posstart-sub">{holdingItemsTotal ? `${holdingItemsTotal} item${holdingItemsTotal === 1 ? "" : "s"} waiting to be ordered` : "holding order + past POs"}</span>
              </button>
            </div>
          )}
          {tab !== "start" && (
          <div className="catbody">
          <div className="btnrow" style={{ marginBottom: 12 }}>
            <button className="btn ghost mini" onClick={() => setTab("start")}>‹ All inventory</button>
          </div>
          {poMsg && <div className="notice" style={{ marginBottom: 12, background: "#E6F6EC", border: "1px solid #A9D9BC", color: "#0E7A3C", fontWeight: 600 }}>{poMsg}</div>}

          {tab === "parts" && (<>
            {lowCount > 0 && <div className="notice" style={{ marginBottom: 10 }}>{lowCount} {lowCount > 1 ? "items are" : "item is"} at or below the reorder level.</div>}
            <datalist id="pd-suppliers">{SUPPLIERS.map((s) => <option key={s} value={s} />)}</datalist>
            <datalist id="pd-bulkparts">{pList.filter((x) => (x.code || "").trim()).map((x) => <option key={x.id} value={x.code}>{x.name}</option>)}</datalist>
            <div className="rbwrap">
              <div className="rbleft">
                {/* search panel — RB top-left */}
                <div className="rbbox">
                  <div className="rbrow"><label>Part No</label><input value={fPart} onChange={(e) => setFPart(e.target.value)} /></div>
                  <div className="rbrow"><label>Description</label><input value={fq} onChange={(e) => setFq(e.target.value)} /></div>
                  <div className="rbrow"><label>Category</label>
                    <select value={fCat} onChange={(e) => { setFCat(e.target.value); setFSub("all"); }}>
                      <option value="all">All</option>{allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select></div>
                  <div className="rbrow"><label>Sub category</label>
                    <select value={fSub} onChange={(e) => setFSub(e.target.value)}>
                      <option value="all">All</option>{allSubs.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div className="rbrow"><label>Manufacturer</label>
                    <select value={fMfr} onChange={(e) => setFMfr(e.target.value)}>
                      <option value="all">All</option>{allMfrs.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select></div>
                  <div className="rbrow"><label>Supplier</label>
                    <select value={fSup} onChange={(e) => setFSup(e.target.value)}>
                      <option value="all">All</option>{allSups.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div className="rbrow"><label></label>
                    <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 650, color: "var(--muted)" }}>
                      <input type="checkbox" checked={fShowInactive} style={{ width: "auto" }} onChange={(e) => setFShowInactive(e.target.checked)} />
                      Show non-active parts
                    </label>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                    <span>
                      <button type="button" className="btn ghost mini" onClick={clearPartFilters}>Clear</button>
                      <button type="button" className="btn ghost mini" style={{ marginLeft: 6 }} onClick={() => csvDownload(`inventory-${new Date().toISOString().slice(0, 10)}.csv`,
                        [["Part #", "Description", "Category", "Sub category", "Manufacturer", "On hand", "Reorder at", "Order amount", "Price", "Cost #1", "Supplier #1", "Cost #2", "Supplier #2", "Cost #3", "Supplier #3", "Active"],
                          ...shownParts.map((p) => [p.code || "", p.name || "", p.category || "", p.subcategory || "", p.manufacturer || "",
                            p.qty ?? "", p.lowAt ?? "", p.reorderQty ?? "", p.price ?? "", p.cost ?? "", p.supplier1 || "", p.cost2 ?? "", p.supplier2 || "", p.cost3 ?? "", p.supplier3 || "", p.active === false ? "no" : "yes"])])}>CSV</button>
                    </span>
                    <b style={{ fontSize: 15 }}>Items&nbsp;&nbsp;{shownParts.length}{shownParts.length !== pList.length ? ` / ${pList.length}` : ""}</b>
                  </div>
                  <button type="button" className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 9 }} onClick={addP}>Add New Part</button>
                </div>

                {/* selected-part record — RB bottom-left */}
                <div className="rbbox" style={{ marginTop: 10 }}>
                  {!selPart ? (
                    <div className="sub" style={{ marginTop: 0 }}>Click a part in the list — its full record opens here.</div>
                  ) : (() => {
                    const p = selPart;
                    const low = isLow(p);
                    const isChem = p.category === "Chemicals";
                    const mfrBase = isChem ? CHEM_MANUFACTURERS : MANUFACTURERS;
                    const mfrMissing = !!p.category && !(p.manufacturer || "");
                    return (<>
                      <div className="rbrow"><label>Part #</label><input value={p.code} onChange={(e) => setP(p.id, "code", e.target.value)} /></div>
                      {dupCode(p) && <div className="sub" style={{ color: "var(--clay)", margin: "0 0 6px 100px" }}>Same code as another part</div>}
                      <div className="rbrow"><label>Description</label><input value={p.name} onChange={(e) => setP(p.id, "name", e.target.value)} /></div>
                      <div className="rbrow"><label>Category</label>
                        <select value={p.category || ""} onChange={(e) => {
                          setP(p.id, "category", e.target.value); setP(p.id, "subcategory", "");
                          // Owner 08/16: equipment defaults to a 1-year warranty at entry —
                          // editable right here on the Warranty tab (Softubs get their real
                          // 5-year + $250 deductible terms by hand). Chemicals stay bare.
                          if (e.target.value && e.target.value !== "Chemicals" && e.target.value !== "Water care" && !(p.warranty && (p.warranty.terms || []).length)) {
                            setP(p.id, "warranty", { ...(p.warranty || {}), applies: true, terms: [{ id: cid(), scope: "Complete unit", months: "12" }] });
                          }
                        }}>
                          <option value="">— pick —</option>
                          {[...new Set([...Object.keys(PART_TREE), ...((p.category || "") && !PART_TREE[p.category] ? [p.category] : [])])].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select></div>
                      <div className="rbrow"><label>Sub category</label>
                        <select value={p.subcategory || ""} onChange={(e) => setP(p.id, "subcategory", e.target.value)}>
                          <option value="">— pick —</option>
                          {[...new Set([...(PART_TREE[p.category] || []), ...((p.subcategory || "") ? [p.subcategory] : [])])].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select></div>
                      {isChem && (
                        <div className="rbrow"><label>Pool / spa</label>
                          <select value={p.chemFor || "pool"} onChange={(e) => setP(p.id, "chemFor", e.target.value)}>
                            <option value="pool">Pool</option><option value="spa">Spa</option><option value="both">Pool + spa</option>
                          </select></div>
                      )}
                      {p.category && (
                        <div className="rbrow"><label style={mfrMissing ? { color: "var(--clay)" } : undefined}>Manufacturer</label>
                          <select value={p.manufacturer || ""} style={mfrMissing ? { borderColor: "var(--clay)" } : undefined}
                            onChange={(e) => setP(p.id, "manufacturer", e.target.value)}>
                            <option value="">— pick —</option>
                            {[...new Set([...mfrBase, ...((p.manufacturer || "") && !mfrBase.includes(p.manufacturer) ? [p.manufacturer] : [])])].map((m) => <option key={m} value={m}>{m}</option>)}
                          </select></div>
                      )}

                      <div className="seg" style={{ margin: "10px 0", flexWrap: "wrap" }}>
                        <button type="button" data-on={dTab === "pricing" ? 1 : 0} onClick={() => setDTab("pricing")}>Retail Pricing</button>
                        <button type="button" data-on={dTab === "stock" ? 1 : 0} onClick={() => setDTab("stock")}>Stock</button>
                        <button type="button" data-on={dTab === "suppliers" ? 1 : 0} onClick={() => setDTab("suppliers")}>Suppliers</button>
                        <button type="button" data-on={dTab === "image" ? 1 : 0} onClick={() => setDTab("image")}>Image</button>
                        <button type="button" data-on={dTab === "warranty" ? 1 : 0} onClick={() => setDTab("warranty")}>Warranty{partHasWarranty(p) ? " ✓" : ""}</button>
                      </div>

                      {dTab === "warranty" && (() => {
                        const w = p.warranty || {};
                        const setW = (patch) => setP(p.id, "warranty", { ...w, ...patch });
                        return (<>
                          <div className="rbrow"><label>
                            <input type="checkbox" checked={!!w.applies} style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }}
                              onChange={(e) => setW({ applies: e.target.checked })} />
                            This product carries a warranty
                          </label></div>
                          {w.applies && (<>
                            <div className="sub" style={{ margin: "4px 0 6px" }}>Coverage is per component — a heater can be Parts 24 months plus Heat exchanger 60. Serial capture and the deductible math key off this.</div>
                            {(w.terms || []).map((tm) => (
                              <div key={tm.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                                <input list="pm-warranty-scopes" value={tm.scope || ""} placeholder="Coverage — e.g. Complete unit" style={{ flex: 1 }}
                                  onChange={(e) => setW({ terms: (w.terms || []).map((x) => (x.id === tm.id ? { ...x, scope: e.target.value } : x)) })} />
                                <input inputMode="numeric" value={tm.months || ""} placeholder="months" style={{ width: 80 }}
                                  onChange={(e) => setW({ terms: (w.terms || []).map((x) => (x.id === tm.id ? { ...x, months: e.target.value } : x)) })} />
                                <button type="button" className="btn warn mini" onClick={() => setW({ terms: (w.terms || []).filter((x) => x.id !== tm.id) })}>✕</button>
                              </div>
                            ))}
                            <datalist id="pm-warranty-scopes">{WARRANTY_SCOPES.map((x) => <option key={x} value={x} />)}</datalist>
                            <button type="button" className="btn ghost mini" onClick={() => setW({ terms: [...(w.terms || []), { id: cid(), scope: "", months: "" }] })}>+ Add coverage term</button>
                            <div className="rbrow" style={{ marginTop: 10 }}><label>Deductible ($ per repair, blank = none)</label>
                              <input inputMode="decimal" value={(w.deductible || {}).amount || ""} placeholder="e.g. 250.00"
                                onChange={(e) => setW({ deductible: e.target.value ? { ...(w.deductible || {}), amount: e.target.value } : null })} /></div>
                            {(w.deductible || {}).amount && (
                              <div className="rbrow"><label>Free period (months from purchase)</label>
                                <input inputMode="numeric" value={(w.deductible || {}).freeMonths || ""} placeholder="e.g. 12 — year one free"
                                  onChange={(e) => setW({ deductible: { ...(w.deductible || {}), freeMonths: e.target.value } })} /></div>
                            )}
                            <div className="sub" style={{ margin: "12px 0 4px", fontWeight: 800 }}>Warranty reference — what staff read at the counter (all optional)</div>
                            <div className="rbrow"><label>What's covered</label>
                              <textarea value={w.covered || ""} style={{ minHeight: 44 }} onChange={(e) => setW({ covered: e.target.value })} /></div>
                            <div className="rbrow"><label>What's NOT covered</label>
                              <textarea value={w.excluded || ""} style={{ minHeight: 44 }} onChange={(e) => setW({ excluded: e.target.value })} /></div>
                            <div className="rbrow"><label>Requirements (registration, proof of purchase, professional install…)</label>
                              <textarea value={w.requirements || ""} style={{ minHeight: 44 }} onChange={(e) => setW({ requirements: e.target.value })} /></div>
                            <div className="rbrow"><label>How to file a claim</label>
                              <textarea value={w.claimProcess || ""} style={{ minHeight: 44 }} onChange={(e) => setW({ claimProcess: e.target.value })} /></div>
                            <div className="rbrow"><label>INTERNAL notes (staff only — never printed or emailed)</label>
                              <textarea value={w.notes || ""} style={{ minHeight: 44 }} onChange={(e) => setW({ notes: e.target.value })} /></div>
                            {(w.docs || []).map((doc) => (
                              <div key={doc.id} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
                                <input value={doc.label} placeholder="e.g. Manufacturer warranty PDF" style={{ flex: 1 }}
                                  onChange={(e) => setW({ docs: w.docs.map((x) => (x.id === doc.id ? { ...x, label: e.target.value } : x)) })} />
                                <input value={doc.url} placeholder="https://…" style={{ flex: 2 }}
                                  onChange={(e) => setW({ docs: w.docs.map((x) => (x.id === doc.id ? { ...x, url: e.target.value } : x)) })} />
                                <button type="button" className="btn warn mini" onClick={() => setW({ docs: w.docs.filter((x) => x.id !== doc.id) })}>✕</button>
                              </div>
                            ))}
                            <button type="button" className="btn ghost mini" onClick={() => setW({ docs: [...(w.docs || []), { id: cid(), label: "", url: "" }] })}>+ Add document link</button>
                          </>)}
                        </>);
                      })()}

                      {dTab === "pricing" && (<>
                        <div className="rbrow"><label>Retail price ($)</label><input inputMode="decimal" value={p.price} onChange={(e) => setP(p.id, "price", e.target.value)} /></div>
                        {showMargins && <div className="rbrow"><label>Our cost — Supplier #1 ($)</label><input inputMode="decimal" value={p.cost} onChange={(e) => setP(p.id, "cost", e.target.value)} /></div>}
                        {showMargins && <div className="rbrow"><label>Total value</label><b style={{ fontSize: 13.5 }}>${(num(p.qty) * num(p.cost)).toFixed(2)} <span className="sub" style={{ display: "inline", marginLeft: 4 }}>on hand × cost</span></b></div>}
                        <div className="rbrow"><label></label>
                          <label style={{ fontSize: 12.5 }}><input type="checkbox" checked={!!p.noTax} style={{ width: "auto", marginRight: 7 }} onChange={(e) => setP(p.id, "noTax", e.target.checked)} />No tax (e.g. water)</label>
                        </div>
                        <div className="rbrow"><label></label>
                          <label style={{ fontSize: 12.5 }}><input type="checkbox" checked={!!p.favorite} style={{ width: "auto", marginRight: 7 }} onChange={(e) => setP(p.id, "favorite", e.target.checked)} />Quick key on the till</label>
                        </div>
                      </>)}

                      {dTab === "stock" && (<>
                        <div className="rbrow"><label>On hand</label><input inputMode="decimal" value={p.qty} style={low ? { borderColor: "var(--clay)", color: "var(--clay)", fontWeight: 700 } : undefined} onChange={(e) => setP(p.id, "qty", e.target.value)} /></div>
                        <div className="rbrow"><label>Fills from bulk</label>
                          <input list="pd-bulkparts" value={p.bulkCode || ""} placeholder="container part # — e.g. LC" onChange={(e) => setP(p.id, "bulkCode", e.target.value)} />
                          <input inputMode="decimal" value={p.bulkQty || ""} placeholder="L per sale" style={{ flex: "0 0 84px" }} onChange={(e) => setP(p.id, "bulkQty", e.target.value)} />
                        </div>
                        {(p.bulkCode || "").trim() && (() => {
                          const b = pList.find((x) => (x.code || "").trim().toLowerCase() === String(p.bulkCode).trim().toLowerCase() && x.id !== p.id);
                          const per = num(p.bulkQty) > 0 ? num(p.bulkQty) : 1;
                          return (
                            <div className="sub" style={{ marginBottom: 6 }}>
                              {b
                                ? <>Each sale takes <b>{per} L</b> from <b>{b.name || b.code}</b> — {num(b.qty)} L in the container ≈ {Math.floor(num(b.qty) / per)} of these left. This item keeps no stock of its own.</>
                                : <span style={{ color: "var(--clay)", fontWeight: 700 }}>No part with code "{p.bulkCode}" — add the bulk container part first.</span>}
                            </div>
                          );
                        })()}
                        <div className="rbrow"><label>Reorder at</label><input inputMode="decimal" value={p.lowAt} onChange={(e) => setP(p.id, "lowAt", e.target.value)} /></div>
                        <div className="rbrow"><label>Order amount</label><input inputMode="decimal" value={p.reorderQty || ""} placeholder="approved qty, e.g. 12" onChange={(e) => setP(p.id, "reorderQty", e.target.value)} /></div>
                        <div className="rbrow"><label></label>
                          <label style={{ fontSize: 12.5 }}>
                            <input type="checkbox" checked={!!p.autoOrder} style={{ width: "auto", marginRight: 7 }} onChange={(e) => setP(p.id, "autoOrder", e.target.checked)} />
                            Auto-order: when stock hits the reorder level, add the order amount to the holding purchase order
                          </label>
                        </div>
                        <button type="button" className="btn mini" style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
                          title="Add the approved amount to the holding purchase order" onClick={() => orderMore(p)}>Order more — add to purchase order</button>
                      </>)}

                      {dTab === "suppliers" && (<>
                        <div className="sub" style={{ marginTop: 0, marginBottom: 8 }}>In order of priority — #1 is who we order from first. Our cost with each shown beside.</div>
                        <div className="rbrow"><label>Supplier #1</label>
                          <input list="pd-suppliers" value={p.supplier1 || ""} placeholder="pick or type — e.g. SCP" onChange={(e) => setP(p.id, "supplier1", e.target.value)} />
                          <input inputMode="decimal" value={p.cost || ""} placeholder="cost $" style={{ flex: "0 0 84px" }} onChange={(e) => setP(p.id, "cost", e.target.value)} />
                        </div>
                        <div className="rbrow"><label>Supplier #2</label>
                          <input list="pd-suppliers" value={p.supplier2 || ""} placeholder="pick or type" onChange={(e) => setP(p.id, "supplier2", e.target.value)} />
                          <input inputMode="decimal" value={p.cost2 || ""} placeholder="cost $" style={{ flex: "0 0 84px" }} onChange={(e) => setP(p.id, "cost2", e.target.value)} />
                        </div>
                        <div className="rbrow"><label>Supplier #3</label>
                          <input list="pd-suppliers" value={p.supplier3 || ""} placeholder="pick or type" onChange={(e) => setP(p.id, "supplier3", e.target.value)} />
                          <input inputMode="decimal" value={p.cost3 || ""} placeholder="cost $" style={{ flex: "0 0 84px" }} onChange={(e) => setP(p.id, "cost3", e.target.value)} />
                        </div>
                        <div className="sub" style={{ marginTop: 4 }}>Supplier #1's cost is the one margins and purchase orders use.</div>
                      </>)}

                      {dTab === "image" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {p.image
                            ? <img src={p.image} alt="" style={{ width: 74, height: 74, borderRadius: 9, objectFit: "cover", border: "1px solid var(--line)" }} />
                            : <div style={{ width: 74, height: 74, borderRadius: 9, background: "var(--card)", border: "1px dashed var(--line)" }} />}
                          <label className="btn ghost mini" style={{ cursor: "pointer" }}>
                            {p.image ? "Replace photo" : "Add photo"}
                            <input type="file" accept="image/*" style={{ display: "none" }}
                              onChange={async (e) => { const f = e.target.files && e.target.files[0]; if (f) setP(p.id, "image", await shrink(f, 480, 0.72)); e.target.value = ""; }} />
                          </label>
                          {p.image && <button type="button" className="btn warn mini" onClick={() => setP(p.id, "image", "")}>Remove</button>}
                        </div>
                      )}

                      <div className="btnrow" style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 10 }}>
                        <button type="button" className="btn ghost mini" onClick={() => setP(p.id, "active", p.active === false)}>
                          {p.active === false ? "Re-Activate Part" : "In-Activate Part"}
                        </button>
                        <button type="button" className="btn warn mini" style={{ marginLeft: "auto" }}
                          onClick={() => { if (window.confirm(`Delete ${p.name || "this part"} from the catalogue?`)) { dropP(p.id); setOpenId(null); } }}>Delete Part</button>
                      </div>
                    </>);
                  })()}
                </div>
              </div>

              {/* the big grid — RB right side */}
              <div className="rbright">
                {(() => {
                  const dir = sortDir === "asc" ? 1 : -1;
                  const val = (p, k) => (k === "qty" ? num(p.qty) : k === "price" ? num(p.price) : String(p[k] || "").toLowerCase());
                  const sorted = [...shownParts].sort((a, b) => { const A = val(a, sortKey), B = val(b, sortKey); return (A < B ? -1 : A > B ? 1 : 0) * dir; });
                  const Th = ({ k, label, right }) => (
                    <th style={right ? { textAlign: "right" } : undefined}
                      onClick={() => { if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } }}>
                      {label}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  );
                  return (
                    <div className="invgridwrap" style={{ maxHeight: "68vh" }}>
                      <table className="invgrid">
                        <thead><tr>
                          <Th k="code" label="Part #" />
                          <Th k="qty" label="QOH" right />
                          <Th k="name" label="Description" />
                          <Th k="category" label="Category" />
                          <Th k="subcategory" label="Sub category" />
                          <Th k="manufacturer" label="Manufacturer" />
                          <Th k="supplier1" label="Supplier" />
                          <Th k="price" label="Price" right />
                        </tr></thead>
                        <tbody>
                          {sorted.map((p) => {
                            const low = isLow(p);
                            const out = p.lowAt !== "" && p.lowAt != null && num(p.qty) <= 0;
                            return (
                              <tr key={p.id} data-sel={openId === p.id ? 1 : 0} data-s={out ? "out" : low ? "low" : ""}
                                data-inactive={p.active === false ? 1 : 0}
                                onClick={() => setOpenId(p.id)}>
                                <td className="mono">{p.code || "—"}</td>
                                <td className="mono" style={{ textAlign: "right", fontWeight: 700, color: out ? "#B31212" : low ? "#9A5B00" : undefined }}>
                                  {p.qty === "" || p.qty == null ? "—" : num(p.qty)}
                                  {truckTotalOf(p) > 0 && <span style={{ color: "var(--tile)", fontWeight: 800 }} title="on trucks"> +{truckTotalOf(p)}t</span>}
                                </td>
                                <td style={{ fontWeight: 650 }}>{p.name || "Unnamed"}{p.active === false ? " · inactive" : ""}</td>
                                <td>{p.category || <span style={{ color: "var(--clay)", fontWeight: 700 }}>—</span>}</td>
                                <td>{p.subcategory || ""}{p.category === "Chemicals" ? ` (${p.chemFor === "spa" ? "Spa" : p.chemFor === "both" ? "Pool+spa" : "Pool"})` : ""}</td>
                                <td>{p.manufacturer || (p.category ? <span style={{ color: "var(--clay)", fontWeight: 700 }}>—</span> : "")}</td>
                                <td>{p.supplier1 || ""}</td>
                                <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{num(p.price) ? posMoney(p.price) : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {shownParts.length === 0 && <div className="empty">{pList.length === 0 ? "No parts yet. Add one and it becomes available to scan or pick on a visit." : "No parts match those filters."}</div>}
                    </div>
                  );
                })()}

                {/* Warehouse quantities — RB bottom-right: every location listed individually */}
                {!selPart && (
                  <div className="rbbox" style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 800, color: "var(--muted)", marginBottom: 6 }}>
                      Warehouse quantities
                    </div>
                    <div className="sub" style={{ marginTop: 0 }}>{WAREHOUSE_QTY_HELP}</div>
                  </div>
                )}
                {selPart && (() => {
                  const p = selPart;
                  const trucks = p.trucks || {};
                  const truckTotal = truckTotalOf(p);
                  const lastMoveFor = (loc) => {
                    const m = (p.moves || []).filter((x) => x.from === loc || x.to === loc).slice(-1)[0];
                    if (!m) return "";
                    return `${m.to === loc ? "+" : "−"}${m.qty} · ${m.date} ${m.time}${m.by ? " · " + m.by : ""}`;
                  };
                  return (
                    <div className="rbbox" style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 800, color: "var(--muted)", marginBottom: 8 }}>
                        Warehouse quantities — {p.name || p.code || "part"}
                      </div>
                      <table className="salest" style={{ marginBottom: 10 }}>
                        <thead><tr><th>Location</th><th style={{ textAlign: "right" }}>Qty</th><th>Last movement</th></tr></thead>
                        <tbody>
                          <tr style={{ background: "var(--plaster)" }}>
                            <td><b>Total — everywhere</b></td>
                            <td className="mono" style={{ textAlign: "right", fontWeight: 800 }}>{num(p.qty) + truckTotal}</td>
                            <td></td>
                          </tr>
                          <tr>
                            <td><b>Main warehouse (Shop)</b></td>
                            <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{num(p.qty)}</td>
                            <td className="sub" style={{ marginTop: 0 }}>{lastMoveFor("shop")}</td>
                          </tr>
                          {warehouseTrucks.map((l) => (
                            <tr key={l.id} style={num(trucks[l.id]) > 0 ? { background: "#EAF6EE" } : undefined}>
                              <td>{l.label}{l.secondary ? <span className="sub" style={{ marginTop: 0, marginLeft: 5, display: "inline" }}>({l.secondary})</span> : null}</td>
                              <td className="mono" style={{ textAlign: "right", fontWeight: num(trucks[l.id]) > 0 ? 800 : 400, color: num(trucks[l.id]) > 0 ? "#0E7A3C" : "var(--muted)" }}>{num(trucks[l.id])}</td>
                              <td className="sub" style={{ marginTop: 0 }}>{lastMoveFor(l.id)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span className="sub" style={{ marginTop: 0, fontWeight: 700 }}>Transfer</span>
                        <input inputMode="decimal" value={wQty} onChange={(e) => setWQty(e.target.value)} style={{ width: 64 }} />
                        <span className="sub" style={{ marginTop: 0 }}>from</span>
                        <select value={wFrom} onChange={(e) => setWFrom(e.target.value)} style={{ width: "auto" }}>
                          <option value="shop">Shop ({num(p.qty)})</option>
                          {warehouseTrucks.map((l) => <option key={l.id} value={l.id}>{l.secondary ? `${l.label} (${l.secondary})` : l.label} — {num(trucks[l.id])}</option>)}
                        </select>
                        <span className="sub" style={{ marginTop: 0 }}>to</span>
                        <select value={wTo} onChange={(e) => setWTo(e.target.value)} style={{ width: "auto" }}>
                          <option value="">— pick —</option>
                          <option value="shop">Shop</option>
                          {warehouseTrucks.map((l) => <option key={l.id} value={l.id}>{l.secondary ? `${l.label} (${l.secondary})` : l.label}</option>)}
                        </select>
                        {partHasWarranty(p) && wTo && wTo !== "shop" && (
                          <input value={wSer} placeholder={num(wQty) > 1 ? `${num(wQty)} serials, comma-separated` : "serial # (required)"} style={{ width: 190 }}
                            onChange={(e) => setWSer(e.target.value)} />
                        )}
                        <button type="button" className="btn mini" disabled={!wTo || wTo === wFrom || !(num(wQty) > 0) || (partHasWarranty(p) && wTo !== "shop" && serialList(wSer).length !== num(wQty))} onClick={() => doTransfer(p)}>Transfer</button>
                      </div>
                      {(p.moves || []).length > 0 && (
                        <div style={{ marginTop: 10, background: "#F7FAFD", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 11px" }}>
                          <div className="sub" style={{ marginTop: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>Recent movements</div>
                          {p.moves.slice(-8).reverse().map((m) => (
                            <div key={m.id} className="sub" style={{ marginTop: 3, display: "flex", gap: 8 }}>
                              <span className="mono" style={{ minWidth: 118 }}>{m.date} {m.time}</span>
                              <span>{m.qty} × {locName(m.from)} → {locName(m.to)}{m.by ? ` · ${m.by}` : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </>)}

          {tab === "services" && (<>
            {svcMsg && <div className="notice" style={{ marginBottom: 12, background: "#E6F6EC", border: "1px solid #A9D9BC", color: "#0E7A3C", fontWeight: 600 }}>{svcMsg}</div>}
            <div className="btnrow" style={{ marginBottom: 12, alignItems: "center" }}>
              <button type="button" className="btn ghost mini" onClick={exportServiceCsv}>CSV</button>
              {isOwner && (<>
                <input ref={svcCsvRef} type="file" accept=".csv,text/csv" style={{ display: "none" }}
                  onChange={(e) => { importServiceCsv(e.target.files && e.target.files[0]); e.target.value = ""; }} />
                <button type="button" className="btn mini" onClick={() => svcCsvRef.current && svcCsvRef.current.click()}>Import CSV</button>
              </>)}
              <span className="sub" style={{ marginTop: 0 }}>{isOwner ? "Download, edit prices in Excel, Import CSV — matching names update, new rows add." : "Download the current rate list."}</span>
            </div>
            {sList.map((s) => (
              <div key={s.id} className="contact" style={{ background: "#fff", opacity: s.active === false ? 0.55 : 1 }}>
                <div className="fields">
                  <div className="f-wide"><label>Description</label><input value={s.name} onChange={(e) => setS(s.id, "name", e.target.value)} /></div>
                  <div className="f-wide"><label>What it includes (shows in the Service catalog)</label>
                    <textarea rows={4} value={s.desc || ""} placeholder="Point-form — one item per line. What the customer gets, how long it takes, what to tell them on the phone."
                      onChange={(e) => setS(s.id, "desc", e.target.value)} /></div>
                  <div><label>Zone 1 rate ($)</label><input inputMode="decimal" value={s.rate} onChange={(e) => setS(s.id, "rate", e.target.value)} /></div>
                  <div><label>Zone 2 rate ($)</label><input inputMode="decimal" value={s.rateZone2 || ""} placeholder="blank = same as Zone 1" onChange={(e) => setS(s.id, "rateZone2", e.target.value)} /></div>
                  <div>
                    <label>Type</label>
                    <select value={s.kind || "service"} onChange={(e) => setS(s.id, "kind", e.target.value)}>
                      {SERVICE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div className="f-wide">
                    <label>
                      <input type="checkbox" checked={s.active !== false} onChange={(e) => setS(s.id, "active", e.target.checked)}
                        style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                      Active
                    </label>
                  </div>
                </div>
                <div className="btnrow" style={{ marginTop: 8 }}>
                  <button className="btn warn mini" onClick={() => dropS(s.id)}>Delete</button>
                </div>
              </div>
            ))}
            {sList.length === 0 && <div className="empty">No services yet. Add your service and labour rates here.</div>}
          </>)}

          {tab === "terms" && (<>
            <div className="notice" style={{ marginBottom: 12 }}>Paste or upload the wording once. Tagged blocks print on bookings, drop-off receipts, work orders and invoices — staff and customers always see the current version. Tag each one so the right terms suggest themselves for the type of work.</div>
            <div className="btnrow" style={{ marginBottom: 12 }}>
              <label className="btn ghost mini" style={{ cursor: "pointer" }}>Upload a T&amp;C file
                <input type="file" accept=".txt,.md,.text,text/plain" style={{ display: "none" }}
                  onChange={(e) => { importT(e.target.files && e.target.files[0]); e.target.value = ""; }} />
              </label>
              <span className="sub" style={{ marginTop: 0 }}>.txt or .md — becomes a new terms block you can tag and edit</span>
            </div>
            {tList.map((t) => (
              <div key={t.id} className="contact" style={{ background: "#fff", opacity: t.active === false ? 0.55 : 1 }}>
                <div className="fields">
                  <div className="f-wide"><label>Name</label><input value={t.name} onChange={(e) => setT(t.id, "name", e.target.value)} /></div>
                  <div>
                    <label>Applies to</label>
                    <select value={t.tag || "any"} onChange={(e) => setT(t.id, "tag", e.target.value)}>
                      {TERM_TAGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div className="f-wide">
                    <label>Terms text</label>
                    <textarea value={t.body} style={{ minHeight: 80 }} placeholder="The wording that appears on the booking, drop-off, work order or invoice."
                      onChange={(e) => setT(t.id, "body", e.target.value)} />
                    <label className="btn ghost mini" style={{ cursor: "pointer", marginTop: 6 }}>Upload into this block
                      <input type="file" accept=".txt,.md,.text,text/plain" style={{ display: "none" }}
                        onChange={(e) => { uploadTBody(t.id, e.target.files && e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                  <div className="f-wide">
                    <label>
                      <input type="checkbox" checked={t.active !== false} onChange={(e) => setT(t.id, "active", e.target.checked)}
                        style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                      Active
                    </label>
                  </div>
                </div>
                <div className="btnrow" style={{ marginTop: 8 }}>
                  <button className="btn warn mini" onClick={() => dropT(t.id)}>Delete</button>
                </div>
              </div>
            ))}
            {tList.length === 0 && <div className="empty">No terms yet. Add your standard terms, warranty wording, opening/closing terms, and quote validity here.</div>}
          </>)}

          {tab === "centres" && (<>
            <div className="notice" style={{ marginBottom: 12 }}>Revenue centres are the parts of the business a counter sale is attributed to. The cashier picks one per sale, and the day report totals sales by centre.</div>
            {rcList.map((r) => (
              <div key={r.id} className="contact" style={{ background: "#fff", opacity: r.active === false ? 0.55 : 1 }}>
                <div className="fields">
                  <div className="f-wide"><label>Name</label><input value={r.name} onChange={(e) => setRc(r.id, "name", e.target.value)} /></div>
                  <div className="f-wide">
                    <label>Sub-categories (one per line){(r.subs || []).filter(Boolean).length ? ` · ${(r.subs || []).filter(Boolean).length}` : ""}</label>
                    <textarea value={(r.subs || []).join("\n")} style={{ minHeight: 90 }} placeholder="e.g. Robotic repairs&#10;Pump repairs"
                      onChange={(e) => setRc(r.id, "subs", e.target.value.split("\n"))} />
                  </div>
                  <div className="f-wide">
                    <label>
                      <input type="checkbox" checked={r.active !== false} onChange={(e) => setRc(r.id, "active", e.target.checked)}
                        style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                      Active
                    </label>
                  </div>
                </div>
                <div className="btnrow" style={{ marginTop: 8 }}>
                  <button className="btn warn mini" onClick={() => dropRc(r.id)}>Delete</button>
                </div>
              </div>
            ))}
            {rcList.length === 0 && <div className="empty">No revenue centres yet. Add ones like Retail counter, Chemicals, or Service department.</div>}
          </>)}

          {tab === "servicetypes" && (<>
            <div className="notice" style={{ marginBottom: 12 }}>Service types show in the Book service work-order form. Picking one fills the "what needs doing" instructions automatically. Leave the instructions blank for a type that doesn't need them (e.g. Quotes).</div>
            {styList.map((s) => (
              <div key={s.id} className="contact" style={{ background: "#fff", opacity: s.active === false ? 0.55 : 1 }}>
                <div className="fields">
                  <div><label>Service type name</label><input value={s.name} onChange={(e) => setSty(s.id, "name", e.target.value)} /></div>
                  <div>
                    <label>Schedule colour</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="color" value={s.color || "#5A6472"} onChange={(e) => setSty(s.id, "color", e.target.value)} style={{ width: 44, height: 34, padding: 2 }} />
                      <span className="doc" style={{ background: s.color || "#5A6472", color: textOn(s.color) }}>{s.name || "Sample"}</span>
                    </div>
                  </div>
                  <div className="f-wide">
                    <label>Instructions (fills "what needs doing")</label>
                    <textarea value={s.instructions || ""} style={{ minHeight: 80 }} placeholder="Step-by-step instructions for the tech. Leave blank for none (e.g. Quotes)."
                      onChange={(e) => setSty(s.id, "instructions", e.target.value)} />
                  </div>
                  <div className="f-wide">
                    <label>
                      <input type="checkbox" checked={s.active !== false} onChange={(e) => setSty(s.id, "active", e.target.checked)}
                        style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                      Active
                    </label>
                  </div>
                </div>
                <div className="btnrow" style={{ marginTop: 8 }}>
                  <button className="btn warn mini" onClick={() => dropSty(s.id)}>Delete</button>
                </div>
              </div>
            ))}
            {styList.length === 0 && <div className="empty">No service types yet. Add ones like Weekly service, Pool opening, Repair, Quotes.</div>}
          </>)}

          {tab === "po" && poScreen === "start" && (
            <div className="posstart" style={{ maxWidth: "none", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
              <button type="button" className="posstart-btn" onClick={() => setPoScreen("create")}>
                <PlusIcon size={30} />
                <span className="posstart-lbl">Create purchase orders{holdingItemsTotal ? ` (${holdingItemsTotal})` : ""}{holdings.length > 1 ? ` · ${holdings.length} POs` : ""}</span>
                <span className="posstart-sub">{holdingItemsTotal ? `${holdingItemsTotal} item${holdingItemsTotal === 1 ? "" : "s"} waiting to be ordered` : "items land here from Order more / auto-order"}</span>
              </button>
              <button type="button" className="posstart-btn" onClick={() => setPoScreen("receive")}>
                <SpecialOrderIcon size={30} />
                <span className="posstart-lbl">Receive an order{orderedPOs.length ? ` (${orderedPOs.length})` : ""}</span>
                <span className="posstart-sub">{orderedPOs.length ? `${orderedPOs.length} PO${orderedPOs.length === 1 ? "" : "s"} out with suppliers` : "check goods in — stock counts update"}</span>
              </button>
              <button type="button" className="posstart-btn" onClick={() => setPoScreen("history")}>
                <QuoteIcon size={30} />
                <span className="posstart-lbl">Review previous purchase orders</span>
                <span className="posstart-sub">{pastPOs.length} past PO{pastPOs.length === 1 ? "" : "s"} · reprint any time</span>
              </button>
            </div>
          )}

          {tab === "po" && poScreen !== "start" && (
            <div className="btnrow" style={{ marginBottom: 12 }}>
              <button className="btn ghost mini" onClick={() => setPoScreen("start")}>‹ Purchase orders</button>
              <strong style={{ fontSize: 15, marginLeft: 4 }}>
                {poScreen === "create" ? "Create purchase orders" : poScreen === "receive" ? "Receive an order" : "Previous purchase orders"}
              </strong>
            </div>
          )}

          {tab === "po" && poScreen === "create" && (<>
              <div className="contact" style={{ background: "#fff", maxWidth: 860 }}>
                {holdings.length > 1 && (
                  <div className="seg" style={{ marginBottom: 12, flexWrap: "wrap" }}>
                    {holdings.map((h) => (
                      <button key={h.id} type="button" data-on={holding && h.id === holding.id ? 1 : 0} onClick={() => setPoEditId(h.id)}>
                        {h.supplier || "No supplier"} ({(h.items || []).length})
                      </button>
                    ))}
                  </div>
                )}
                {(!holding || holding.items.length === 0) && (
                  <div className="sub" style={{ marginTop: 0, marginBottom: 8 }}>
                    New purchase order — search a part below to add the first line, or use <b>Order more</b> on the Parts tab.
                  </div>
                )}
                {holding && holding.items.length > 0 && (<>
                {/* header: vendor top-left · date + PO number top-right */}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 18, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 300px", minWidth: 260 }}>
                    <div style={{ background: "var(--ink)", color: "#fff", fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 800, padding: "5px 10px", borderRadius: 6, display: "inline-block", marginBottom: 8 }}>Vendor</div>
                    <div className="fields" style={{ gridTemplateColumns: "1fr" }}>
                      <div><label>Supplier</label>
                        <input list="pd-suppliers" value={holding.supplier || ""} placeholder="e.g. SCP"
                          onChange={(e) => {
                            const name = e.target.value;
                            const known = (supplierInfo || {})[name] || {};
                            setHolding({ supplier: name,
                              supplierContact: known.contact ?? holding.supplierContact ?? "",
                              supplierAddress: known.address ?? holding.supplierAddress ?? "",
                              supplierPhone: known.phone ?? holding.supplierPhone ?? "" });
                          }} /></div>
                      <div><label>Contact / department</label><input value={holding.supplierContact || ""} placeholder="e.g. Orders desk" onChange={(e) => setHolding({ supplierContact: e.target.value })} /></div>
                      <div><label>Address</label><input value={holding.supplierAddress || ""} placeholder="street, city" onChange={(e) => setHolding({ supplierAddress: e.target.value })} /></div>
                      <div><label>Phone</label><input value={holding.supplierPhone || ""} onChange={(e) => setHolding({ supplierPhone: e.target.value })} /></div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flex: "0 1 auto" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--tile)", letterSpacing: 2, marginBottom: 10 }}>PURCHASE ORDER</div>
                    <div className="sub" style={{ marginTop: 0 }}>DATE <b className="mono" style={{ color: "var(--ink)", marginLeft: 8 }}>{holding.createdDate || ""}</b></div>
                    <div className="sub" style={{ marginTop: 4 }}>PO # <b className="mono" style={{ color: "var(--clay)", marginLeft: 8 }}>{nextPoNo || "—"}</b> <span style={{ fontWeight: 400 }}>(assigned on send)</span></div>
                    <div className="sub" style={{ marginTop: 10 }}>Created by <b style={{ color: "var(--ink)" }}>{holding.createdBy || "—"}</b></div>
                    <div className="sub" style={{ marginTop: 2 }}>Sent by <b style={{ color: "var(--ink)" }}>{me ? me.name : "—"}</b> <span style={{ fontWeight: 400 }}>(stamped on send)</span></div>
                  </div>
                </div>

                </>)}

                {/* search & add — stays pinned at the top of the items list */}
                <div style={{ display: "flex", gap: 8, marginTop: holding && holding.items.length ? 14 : 0 }}>
                  <input value={poQ} placeholder="Add an item — search part # or description" style={{ flex: 1 }}
                    onChange={(e) => setPoQ(e.target.value)} />
                  <input inputMode="decimal" value={poQty} title="Quantity to add" style={{ width: 70, textAlign: "center" }}
                    onChange={(e) => setPoQty(e.target.value)} />
                </div>
                {poQ.trim() && (
                  <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 300 }}>
                    {poMatches.length === 0 ? <div className="sub" style={{ padding: "8px 10px" }}>No part matches that.</div>
                      : poMatches.map((p) => {
                        const sups = [p.supplier1, p.supplier2, p.supplier3].map((x) => (x || "").trim()).filter(Boolean);
                        const chosen = poSupPick[p.id] || sups[0] || "";
                        return (
                          <div key={p.id} className="poslookupitem" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "default" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <b>{p.name}{p.code ? ` · ${p.code}` : ""}</b>
                              <span className="sub" style={{ marginTop: 0, display: "block" }}>{num(p.qty)} on hand{num(p.cost) ? ` · cost $${num(p.cost).toFixed(2)}` : ""}</span>
                            </div>
                            {sups.length > 0 ? (
                              <select value={chosen} style={{ width: "auto", maxWidth: 160 }}
                                onChange={(e) => setPoSupPick({ ...poSupPick, [p.id]: e.target.value })}>
                                {sups.map((sName, si) => <option key={sName} value={sName}>#{si + 1} {sName}</option>)}
                              </select>
                            ) : <span className="sub" style={{ marginTop: 0 }}>no supplier set</span>}
                            <button type="button" className="btn mini" onClick={() => addPoItem(p, chosen)}>Add {num(poQty) > 0 ? num(poQty) : 1}</button>
                          </div>
                        );
                      })}
                  </div>
                )}

                {holding && holding.items.length > 0 && (<>
                {/* items — template-style table */}
                <table className="salest" style={{ marginTop: 14 }}>
                  <thead><tr><th>Item #</th><th>Description</th><th style={{ textAlign: "right" }}>Qty</th><th style={{ textAlign: "right" }}>Unit cost</th><th style={{ textAlign: "right" }}>Total</th><th></th></tr></thead>
                  <tbody>
                    {holding.items.map((it, i) => (
                      <tr key={i}>
                        <td className="mono">{it.code || "—"}</td>
                        <td style={{ fontWeight: 600 }}>{it.name}</td>
                        <td style={{ textAlign: "right" }}><input inputMode="decimal" value={it.qty} style={{ width: 64, textAlign: "right" }} onChange={(e) => setPoItem(i, "qty", e.target.value)} /></td>
                        <td className="mono" style={{ textAlign: "right" }}>{num(it.cost) ? "$" + num(it.cost).toFixed(2) : "—"}</td>
                        <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{num(it.cost) ? "$" + (num(it.qty) * num(it.cost)).toFixed(2) : "—"}</td>
                        <td style={{ textAlign: "right" }}><button className="btn warn mini" onClick={() => dropPoItem(i)}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={4} style={{ textAlign: "right", fontWeight: 800 }}>TOTAL</td>
                    <td className="mono" style={{ textAlign: "right", fontWeight: 800 }}>{poTotal(holding) ? "$" + poTotal(holding).toFixed(2) : "—"}</td><td></td></tr></tfoot>
                </table>
                {(() => {
                  const gap = poMinimumGap(holding.supplier, holding.items || []);
                  if (!gap.flagged) return null;
                  const sugg = suggestItemsToMinimum(pList, gap.vendor, gap.shortfall, (holding.items || []).map((it) => it.code));
                  return (
                    <div className="notice" style={{ marginTop: 10, borderColor: "#F2C063", background: "#FDF6E7" }} data-po-min="1">
                      <b>{gap.vendor} minimum order ${gap.minimum.toFixed(2)}</b>
                      <div className="sub" style={{ marginTop: 2 }}>
                        This P.O. is ${gap.total.toFixed(2)} — short ${gap.shortfall.toFixed(2)}. Suggestions to build the order up:
                      </div>
                      {sugg.length === 0
                        ? <div className="sub">No other {gap.vendor} inventory suggestions on file.</div>
                        : <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                            {sugg.map((s) => (
                              <li key={s.code || s.name}>
                                {s.name}{s.code ? ` · ${s.code}` : ""} — qty {s.qty} ≈ ${s.cover.toFixed(2)}
                                <button type="button" className="btn ghost mini" style={{ marginLeft: 8 }}
                                  onClick={() => {
                                    const item = { code: s.code || "", name: s.name || "", qty: s.qty, cost: s.cost };
                                    setHoldingFor(holding, { items: mergeItem(holding, item, s.qty) });
                                  }}>Add</button>
                              </li>
                            ))}
                          </ul>}
                    </div>
                  );
                })()}

                <div style={{ marginTop: 12 }}>
                  <label>Comments or special instructions</label>
                  <textarea value={holding.notes || ""} style={{ minHeight: 60 }} onChange={(e) => setHolding({ notes: e.target.value })} />
                </div>
                <div className="btnrow" style={{ marginTop: 14 }}>
                  <button className="btn" onClick={() => {
                    if (onSaveSupplierInfo && (holding.supplier || "").trim()) {
                      onSaveSupplierInfo(holding.supplier.trim(), { contact: holding.supplierContact || "", address: holding.supplierAddress || "", phone: holding.supplierPhone || "" });
                    }
                    onFinalizePO(holding);
                    setPoScreen("start");
                  }} disabled={!holding.items.length}>
                    Send purchase order — {nextPoNo || "assign number"}
                  </button>
                </div>
                </>)}
              </div>
          </>)}

          {tab === "po" && poScreen === "receive" && (<>
            {orderedPOs.length === 0 ? (
              <div className="empty">Nothing out with suppliers. When you create a purchase order it waits here until the goods arrive.</div>
            ) : orderedPOs.map((po) => (
              <div key={po.id} className="contact" style={{ background: "#fff" }}>
                <div className="bar" style={{ marginBottom: 8 }}>
                  <div>
                    <b className="mono" style={{ color: "var(--clay)" }}>{po.poNo}</b>
                    <span className="sub" style={{ marginTop: 0, marginLeft: 8, display: "inline" }}>{po.orderedDate || ""} · {po.supplier || "No supplier"} · by {po.orderedBy || po.createdBy || "—"}</span>
                  </div>
                  <div className="btnrow">
                    <button className="btn ghost mini" onClick={() => printPO(po)}>Print</button>
                    <button className="btn mini" onClick={() => {
                      if (window.confirm(`Receive ${po.poNo}? Every line goes into shop stock and the counts update.`)) receivePO(po);
                    }}>Receive — into stock</button>
                  </div>
                </div>
                {(po.items || []).map((it, i) => (
                  <div key={i} className="sub" style={{ marginTop: 2, display: "flex", gap: 10 }}>
                    <span className="mono" style={{ minWidth: 90 }}>{it.code || "—"}</span>
                    <span style={{ flex: 1 }}>{it.name}</span>
                    <b>×{num(it.qty)}</b>
                  </div>
                ))}
              </div>
            ))}
          </>)}

          {tab === "po" && poScreen === "history" && (<>
            {pastPOs.length === 0 && <div className="empty">No purchase orders yet.</div>}
            {pastPOs.map((po) => (
              <div key={po.id} className="contact" style={{ background: "#fff", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <b className="mono" style={{ color: "var(--clay)" }}>{po.poNo}</b>
                <span className="doc" style={{ background: po.status === "received" ? "#12A650" : "#B7791F" }}>{po.status === "received" ? `Received ${po.receivedDate || ""}` : "On order"}</span>
                <span className="sub" style={{ marginTop: 0 }}>{po.orderedDate || ""} · {po.supplier || "No supplier"} · {(po.items || []).length} item{(po.items || []).length === 1 ? "" : "s"}{poTotal(po) ? ` · $${poTotal(po).toFixed(2)}` : ""} · created {po.createdBy || "—"} · sent {po.orderedBy || "—"}</span>
                <div style={{ marginLeft: "auto" }}>
                  <button className="btn ghost mini" onClick={() => printPO(po)}>Print</button>
                </div>
              </div>
            ))}
          </>)}
          </div>
          )}
        </div>
    </>
  );
  return inline
    ? <div>{content}</div>
    : (
      <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
        <div className="sheet" style={{ maxWidth: 1360 }}>{content}</div>
      </div>
    );
}

function ServerSheet({ serverEmail, onImport, onGoShared, onGoLocal, onBackup, onClose }) {
  const shared = serverMode();
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 560 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Server &amp; shared data</strong>
            <div className="sub">{shared ? `Shared mode · signed in as ${serverEmail || ""}` : "Browser-only mode — data lives on this device"}</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          {!shared ? (
            <>
              <div className="notice" style={{ marginBottom: 14 }}>
                You're in <b>browser-only</b> mode: data is saved on this device only. Switching to <b>shared</b> mode
                puts it on the server so every device sees the same thing — after that you'll sign in with your email and password.
              </div>
              <ol style={{ margin: "0 0 14px 18px", fontSize: 13.5, lineHeight: 1.7 }}>
                <li><b>Back up first</b> (safety) — <button className="btn ghost mini" onClick={onBackup}>Download backup</button></li>
                <li>Then switch to shared mode. The app reloads and asks you to sign in.</li>
                <li>After signing in, come back here and press <b>Copy this device's data to the server</b>.</li>
              </ol>
              <button className="btn" onClick={onGoShared}>Switch to shared mode</button>
            </>
          ) : (
            <>
              <div className="notice" style={{ marginBottom: 14 }}>
                You're connected to the shared server. If this is the first device, send its data up so nothing's missing.
              </div>
              <div className="btnrow">
                <button className="btn" onClick={onImport}>Copy this device's data to the server</button>
                <button className="btn ghost" onClick={onBackup}>Download backup</button>
              </div>
              <div className="sub" style={{ margin: "16px 0 6px" }}>Trouble? You can always go back:</div>
              <button className="btn warn" onClick={onGoLocal}>Switch back to browser-only mode</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StaffSheet({ staff, roles, punches = [], onSave, onClose, onOpenRoles }) {
  const [list, setList] = useState(staff.map((x) => ({ ...x })));
  const [screen, setScreen] = useState("start"); // start | lookup | form | sheets
  const [selId, setSelId] = useState(null);
  const [q, setQ] = useState("");
  const set = (id, k, v) => setList((L) => L.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const dup = (x, k) => list.filter((y) => y.id !== x.id && y[k] && y[k] === x[k]).length > 0;
  const roleName = (id) => (roles.find((r) => r.id === id) || {}).name || "";
  const emp = list.find((x) => x.id === selId) || null;
  const addNew = () => {
    const e = { id: cid(), name: "", roleId: "tech", pin: "", clock: "", active: true,
      hourlyRate: "", address: "", phone: "", email: "", emergencyContact: "", directDeposit: "", driversLicense: "", benefitsNo: "" };
    setList((L) => [...L, e]); setSelId(e.id); setScreen("form");
  };
  const empMatches = q.trim()
    ? list.filter((x) => `${x.name || ""} ${roleName(x.roleId)} ${x.phone || ""} ${x.email || ""}`.toLowerCase().includes(q.trim().toLowerCase()))
    : list;

  // Sign-in accounts (Supabase logins) — visible to admins in shared mode.
  const [logins, setLogins] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null); // { email, password, kind }
  const loginAction = async (action, emp2) => {
    setLoginBusy(true); setTempPw(null);
    try {
      const { data, error } = await supabase.functions.invoke("manage-logins", {
        body: { action, email: emp2.email, name: emp2.name, position: emp2.roleId },
      });
      if (error || !data || data.error) throw new Error((data && data.error) || "failed");
      setTempPw({ email: emp2.email, password: data.tempPassword, kind: action });
      if (action === "create") setLogins((L) => [...(L || []), { email: emp2.email.toLowerCase(), role: data.role }]);
    } catch (e) {
      setTempPw({ email: emp2.email, error: String(e.message || e) });
    }
    setLoginBusy(false);
  };
  // Last sign-in per login (admin-only, from the auth system) + who's in the app right now.
  const [loginMeta, setLoginMeta] = useState({});
  const [presence, setPresence] = useState({});
  useEffect(() => {
    if (!serverMode() || !supabase) return;
    supabase.from("profiles").select("id,email,role").order("email")
      .then(({ data }) => setLogins(data || []));
    supabase.functions.invoke("manage-logins", { body: { action: "list" } })
      .then(({ data }) => { if (data && data.users) setLoginMeta(Object.fromEntries(data.users.map((u) => [u.email, u]))); })
      .catch(() => {});
    const loadPresence = () => supabase.from("presence").select("email,last_seen")
      .then(({ data }) => { if (data) setPresence(Object.fromEntries(data.map((p) => [(p.email || "").toLowerCase(), p.last_seen]))); });
    loadPresence();
    const t = setInterval(loadPresence, 60 * 1000); // keep the live dots fresh while this sheet is open
    return () => clearInterval(t);
  }, []);
  // Admin-only: peek at a login's latest activity breadcrumb (screens + tagged clicks, no typing).
  const [trailFor, setTrailFor] = useState({});
  const loadTrail = async (l) => {
    if (!l.id) return;
    setTrailFor((m) => ({ ...m, [l.email]: "loading" }));
    const { data: rows } = await supabase.from("activity_trail").select("current_route,events,last_seen_at")
      .eq("user_id", l.id).order("last_seen_at", { ascending: false }).limit(1);
    const r = rows && rows[0];
    setTrailFor((m) => ({ ...m, [l.email]: r ? { route: r.current_route, at: r.last_seen_at, summary: trailSummary(r.events) } : "none" }));
  };
  const liveNow = (email) => {
    const seen = presence[(email || "").toLowerCase()];
    return !!seen && Date.now() - new Date(seen).getTime() < 3 * 60 * 1000;
  };
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  const loginFor = (emp) => (logins || []).find((l) => l.email && emp.email && l.email.toLowerCase() === emp.email.toLowerCase());

  // Timesheets (payroll): hours pulled from the time clock, Monday–Sunday.
  const [empId, setEmpId] = useState("all");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const days = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i));
  const shiftWeek = (n) => setWeekStart((w) => new Date(w.getFullYear(), w.getMonth(), w.getDate() + n * 7));
  const dayPunches = (sid, d) => punches.filter((p) => p.staffId === sid && fmtDate(new Date(p.inAt)) === fmtDate(d));
  const dayHours = (sid, d) => dayPunches(sid, d).filter((p) => p.outAt).reduce((a, p) => a + hoursBetween(p.inAt, p.outAt), 0);
  const weekHours = (sid) => days.reduce((a, d) => a + dayHours(sid, d), 0);
  const sheetStaff = list.filter((x) => x.active !== false || weekHours(x.id) > 0);
  const weekLabel = `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  const one = empId !== "all" ? list.find((x) => x.id === empId) : null;

  // Payroll export — a CSV of the shown week's hours, ready to map into Payworks' hours import.
  const exportPayrollCsv = () => {
    const esc = (c) => { const s = String(c ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const people = one ? [one] : sheetStaff;
    const rows = [];
    rows.push(["Pay period", `${fmtDate(days[0])} to ${fmtDate(days[6])}`]);
    rows.push(["Generated", fmtDate(new Date())]);
    rows.push([]);
    rows.push(["Employee", "Employee #", "Position", "Hourly rate",
      ...days.map((d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })),
      "Total hours"]);
    people.forEach((x) => rows.push([
      x.name || "", x.benefitsNo || x.clock || "", roleName(x.roleId), x.hourlyRate || "",
      ...days.map((d) => dayHours(x.id, d).toFixed(2)), weekHours(x.id).toFixed(2),
    ]));
    const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll-hours-${fmtDate(days[0])}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 860 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Employee Profiles</strong>
            <div className="sub">{screen === "sheets" ? "Hours from the time clock · use All employees for payroll" : screen === "form" ? "Full profile · 4-digit code opens the app, 9-digit punches the clock" : "Look up an employee or set up a new one"}</div>
          </div>
          <div className="btnrow">
            {onOpenRoles && <button className="btn ghost" onClick={onOpenRoles}>Who sees what</button>}
            <button className="btn ghost" onClick={() => setScreen("sheets")}>Timesheets</button>
            <button className="btn ghost" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="pad">

          {screen === "start" && (<>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <input placeholder="Search employees — name, position, phone, email" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: 1 }} />
              <button className="btn" onClick={addNew}><PlusIcon size={13} /> Set up new employee</button>
            </div>
            <div className="poslookup" style={{ position: "static", boxShadow: "none", maxHeight: "none", marginBottom: 4 }}>
              {empMatches.length === 0 ? <div className="sub" style={{ padding: "8px 10px" }}>No employees match.</div>
                : empMatches.map((x) => (
                  <button key={x.id} type="button" className="poslookupitem" onClick={() => { setSelId(x.id); setScreen("form"); }}>
                    <b>{x.name || "Unnamed"}{x.active === false ? " · inactive" : ""}{x.hasLicense ? " · 🚗 licensed" : ""}</b>
                    <span className="sub" style={{ marginTop: 0 }}>
                      {roleName(x.roleId)}{x.phone ? " · " + x.phone : ""}{x.email ? " · " + x.email : ""} — tap for the full profile & privileges
                    </span>
                  </button>
                ))}
            </div>
            {logins && logins.length > 0 && (
              <div className="stackpanel" style={{ maxWidth: 800, margin: "16px auto 0" }}>
                <div className="ptitle">App sign-in accounts <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{logins.length}</span></div>
                {logins.map((l) => {
                  const live = liveNow(l.email);
                  const meta = loginMeta[(l.email || "").toLowerCase()] || {};
                  return (
                    <div key={l.email} className="lrow" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 2px", borderTop: "1px solid var(--line)", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 650, display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        {live && <span className="livedot" title="In the app right now" />}
                        {l.email}
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <span className="sub" style={{ marginTop: 0, whiteSpace: "nowrap" }}>
                          {live && <b style={{ color: "#16A34A" }}>Live now · </b>}
                          {meta.lastSignIn ? "Last sign-in " + fmtWhen(meta.lastSignIn)
                            : Object.keys(loginMeta).length ? "Never signed in" : ""}
                        </span>
                        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", minWidth: 84, textAlign: "right",
                          color: l.role === "admin" ? "#17356E" : l.role === "office" ? "#086EB6" : "#5A6E86" }}>
                          {l.role === "admin" ? "Owner" : l.role === "office" ? "Office" : "Technician"}
                        </span>
                        <button className="btn ghost mini" onClick={() => loadTrail(l)}>Activity</button>
                      </span>
                      {trailFor[l.email] && (
                        <div className="sub" style={{ width: "100%", marginTop: 4 }}>
                          {trailFor[l.email] === "loading" ? "Loading…"
                            : trailFor[l.email] === "none" ? "No activity recorded yet (they need the newest version loaded)."
                            : <>Last on <b>{trailFor[l.email].route || "?"}</b> · {new Date(trailFor[l.email].at).toLocaleString()}<br />{trailFor[l.email].summary || "no tagged clicks yet"}</>}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="sub" style={{ marginTop: 9 }}>
                  These are the accounts that can sign in to the app. Passwords can never be viewed — by anyone,
                  by design — only reset. Link a login to an employee by putting the same email on their profile.
                </div>
              </div>
            )}
          </>)}

          {screen === "lookup" && (<>
            <div className="btnrow" style={{ marginBottom: 12 }}>
              <button className="btn ghost mini" onClick={() => setScreen("start")}>‹ Back</button>
              <button className="btn ghost mini" onClick={addNew}>+ New employee</button>
            </div>
            <input autoFocus placeholder="Search employees — name, position, phone, email" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 12 }} />
            <div className="poslookup" style={{ position: "static", boxShadow: "none" }}>
              {empMatches.length === 0 ? <div className="sub" style={{ padding: "8px 10px" }}>No employees match.</div>
                : empMatches.map((x) => (
                  <button key={x.id} type="button" className="poslookupitem" onClick={() => { setSelId(x.id); setScreen("form"); }}>
                    <b>{x.name || "Unnamed"}{x.active === false ? " · inactive" : ""}</b>
                    <span className="sub" style={{ marginTop: 0 }}>{roleName(x.roleId)}{x.phone ? " · " + x.phone : ""}</span>
                  </button>
                ))}
            </div>
          </>)}

          {screen === "form" && emp && (<>
            <div className="btnrow" style={{ marginBottom: 12, justifyContent: "space-between" }}>
              <button className="btn ghost mini" onClick={() => { onSave(list); setScreen("start"); }}>‹ Back to list</button>
              <button className="btn" onClick={() => { onSave(list); setScreen("start"); }}>Save employee</button>
            </div>
            <div className="contact" style={{ background: "#fff", opacity: emp.active === false ? 0.7 : 1 }}>
              <div className="fields">
                <div><label>Name</label><input value={emp.name} onChange={(e) => set(emp.id, "name", e.target.value)} /></div>
                <div>
                  <label>Position</label>
                  <select value={emp.roleId} onChange={(e) => set(emp.id, "roleId", e.target.value)}>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="f-wide">
                  <label>Privileges — what {roleName(emp.roleId) || "this position"} is allowed</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {PERMS.map((p) => {
                      const on = ((roles.find((r) => r.id === emp.roleId) || {}).perms || []).includes(p.k);
                      return (
                        <span key={p.k} className="doc" title={p.note}
                          style={{ background: on ? "#12A650" : "#D9E2EC", color: on ? "#fff" : "#5A6E86" }}>
                          {on ? "✓ " : "— "}{p.label}
                        </span>
                      );
                    })}
                  </div>
                  <div className="sub" style={{ marginTop: 6 }}>
                    Privileges follow the position — pick a different position to change them, or use
                    {onOpenRoles ? <button type="button" className="link" style={{ margin: "0 4px" }} onClick={onOpenRoles}>Who sees what</button> : " Who sees what "}
                    to edit what each position allows.
                  </div>
                </div>
                <div><label>Hourly rate ($)</label><input inputMode="decimal" value={emp.hourlyRate || ""} onChange={(e) => set(emp.id, "hourlyRate", e.target.value)} /></div>
                <div><label>Phone</label><input value={emp.phone || ""} onChange={(e) => set(emp.id, "phone", e.target.value)} /></div>
                <div><label>Email</label><input value={emp.email || ""} onChange={(e) => set(emp.id, "email", e.target.value)} /></div>
                <div className="f-wide"><label>Address</label>
                  <AddressAutocomplete value={emp.address || ""} onChange={(v) => set(emp.id, "address", v)} placeholder="Start typing the address…" />
                </div>
                <div className="f-wide"><label>Emergency contact</label><input value={emp.emergencyContact || ""} placeholder="Name · relationship · phone" onChange={(e) => set(emp.id, "emergencyContact", e.target.value)} /></div>
                <div className="f-wide"><label>Direct deposit information</label><input value={emp.directDeposit || ""} placeholder="Institution · transit · account #" onChange={(e) => set(emp.id, "directDeposit", e.target.value)} /></div>
                <div><label>Driver's license</label><input value={emp.driversLicense || ""} onChange={(e) => set(emp.id, "driversLicense", e.target.value)} /></div>
                <div className="f-wide">
                  <label>Driver's license photo</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {emp.licenseImage
                      ? <img src={emp.licenseImage} alt="Driver's license" style={{ width: 110, height: 70, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />
                      : <div style={{ width: 110, height: 70, borderRadius: 8, background: "var(--card)", border: "1px dashed var(--line)" }} />}
                    <label className="btn ghost mini" style={{ cursor: "pointer" }}>
                      {emp.licenseImage ? "Replace photo" : "📷 Add photo"}
                      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                        onChange={async (e) => { const f = e.target.files && e.target.files[0]; if (f) set(emp.id, "licenseImage", await shrink(f, 800, 0.78)); e.target.value = ""; }} />
                    </label>
                    {emp.licenseImage && <button type="button" className="btn warn mini" onClick={() => set(emp.id, "licenseImage", "")}>Remove</button>}
                  </div>
                  <div className="sub">Kept on the employee's profile — visible to the owner only, like the rest of their personal details.</div>
                </div>
                <div><label>Benefits employee #</label><input value={emp.benefitsNo || ""} onChange={(e) => set(emp.id, "benefitsNo", e.target.value)} /></div>
                <div>
                  <label>Access code — 4 digits</label>
                  <input value={emp.pin} inputMode="numeric" maxLength={4} onChange={(e) => set(emp.id, "pin", e.target.value.replace(/[^0-9]/g, ""))} />
                  {dup(emp, "pin") && <div className="sub" style={{ color: "var(--clay)" }}>Already used by someone else</div>}
                </div>
                <div>
                  <label>Clock code — 9 digits</label>
                  <input value={emp.clock} inputMode="numeric" maxLength={9} onChange={(e) => set(emp.id, "clock", e.target.value.replace(/[^0-9]/g, ""))} />
                  {dup(emp, "clock") && <div className="sub" style={{ color: "var(--clay)" }}>Already used by someone else</div>}
                </div>
                <div className="f-wide">
                  <label>
                    <input type="checkbox" checked={!!emp.hasLicense} onChange={(e) => set(emp.id, "hasLicense", e.target.checked)} style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                    Driver's licence — service techs with a licence get their own column on the schedule and a truck
                  </label>
                </div>
                {emp.hasLicense && (
                  <div>
                    <label>Works out of (vehicle)</label>
                    <input value={emp.vehicle || ""} placeholder="e.g. Red Caravan" onChange={(e) => set(emp.id, "vehicle", e.target.value)} />
                  </div>
                )}
                <div className="f-wide" style={{ background: "#F2F7FC", border: "1px solid var(--line)", borderRadius: 9, padding: "10px 12px" }}>
                  <label style={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, fontSize: 10.5 }}>App sign-in</label>
                  {!(emp.email || "").includes("@") ? (
                    <div className="sub" style={{ marginTop: 2 }}>Add their email above, then create their sign-in here — one tap.</div>
                  ) : (() => {
                    const has = (logins || []).some((l) => l.email === String(emp.email).toLowerCase());
                    return (
                      <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                        {has
                          ? <span className="doc" style={{ background: "#12A650" }}>Sign-in active — {emp.email}</span>
                          : <span className="doc" style={{ background: "#B7791F" }}>No sign-in yet</span>}
                        {!has && <button type="button" className="btn mini" disabled={loginBusy} onClick={() => loginAction("create", emp)}>{loginBusy ? "Creating…" : "Create sign-in"}</button>}
                        {has && <button type="button" className="btn ghost mini" disabled={loginBusy} onClick={() => { if (window.confirm(`Reset ${emp.name || "this employee"}'s password? Their old one stops working immediately.`)) loginAction("reset", emp); }}>{loginBusy ? "Working…" : "Reset password"}</button>}
                      </div>
                    );
                  })()}
                  {tempPw && tempPw.email === emp.email && (
                    tempPw.error
                      ? <div className="sub" style={{ marginTop: 7, color: "var(--clay)", fontWeight: 700 }}>{tempPw.error}</div>
                      : <div style={{ marginTop: 8, background: "#E6F6EC", border: "1px solid #A9D9BC", borderRadius: 8, padding: "9px 12px" }}>
                          <b style={{ color: "#0E7A3C" }}>Temporary password: <span className="mono" style={{ fontSize: 15 }}>{tempPw.password}</span></b>
                          <div className="sub" style={{ marginTop: 3 }}>
                            Hand this to {emp.name || "them"} now — it can't be viewed again, only reset. They sign in at app.poolmaster.ca
                            and can change it themselves with “Forgot password?”. Their access level follows their Position above.
                          </div>
                        </div>
                  )}
                </div>
                <div className="f-wide">
                  <label>
                    <input type="checkbox" checked={emp.active !== false} onChange={(e) => set(emp.id, "active", e.target.checked)} style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                    Currently employed
                  </label>
                </div>
              </div>
            </div>
            <div className="notice" style={{ marginTop: 12 }}>
              Access codes gate what shows on screen, not what's protected — and direct-deposit / licence details live with the app data. Keep this owner-only until the server with real sign-in is in place.
            </div>
          </>)}

          {screen === "sheets" && (<>
            <div className="btnrow" style={{ marginBottom: 12 }}>
              <button className="btn ghost mini" onClick={() => setScreen("start")}>‹ Back</button>
            </div>
            <div className="btnrow" style={{ marginBottom: 12, alignItems: "center", gap: 10 }}>
              <select value={empId} onChange={(e) => setEmpId(e.target.value)} style={{ width: "auto", fontWeight: 650 }}>
                <option value="all">All employees (payroll)</option>
                {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <span className="stepper">
                <button type="button" onClick={() => shiftWeek(-1)}>&lsaquo;</button>
                <span className="val">{weekLabel}</span>
                <button type="button" onClick={() => shiftWeek(1)}>&rsaquo;</button>
              </span>
              <button className="btn ghost mini" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>
              <button className="btn mini" onClick={exportPayrollCsv} title="Download this week's hours as a CSV to import into Payworks">Export for payroll (CSV)</button>
            </div>

            {one ? (
              <table className="salest">
                <thead><tr><th>Day</th><th>Punches</th><th>Hours</th></tr></thead>
                <tbody>
                  {days.map((d) => {
                    const ps = dayPunches(one.id, d);
                    const h = dayHours(one.id, d);
                    return (
                      <tr key={fmtDate(d)}>
                        <td>{d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</td>
                        <td><span className="sub" style={{ marginTop: 0 }}>{ps.length === 0 ? "—" : ps.map((p) => p.outAt ? `${clockTime(p.inAt)}–${clockTime(p.outAt)}` : `${clockTime(p.inAt)}– on the clock`).join(", ")}</span></td>
                        <td className="mono">{h ? hhmm(h) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr><td colSpan={2}><b>Week total — {one.name}</b></td><td className="mono"><b>{hhmm(weekHours(one.id))} ({weekHours(one.id).toFixed(2)})</b></td></tr></tfoot>
              </table>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="salest">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      {days.map((d) => <th key={fmtDate(d)} style={{ textAlign: "right" }}>{d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}</th>)}
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheetStaff.map((x) => (
                      <tr key={x.id}>
                        <td>{x.name}<span className="sub" style={{ marginTop: 0 }}>{roleName(x.roleId)}</span></td>
                        {days.map((d) => { const h = dayHours(x.id, d); return <td key={fmtDate(d)} className="mono">{h ? h.toFixed(2) : "—"}</td>; })}
                        <td className="mono"><b>{weekHours(x.id).toFixed(2)}</b></td>
                      </tr>
                    ))}
                    {sheetStaff.length === 0 && <tr><td colSpan={9} className="sub">No hours recorded this week.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><b>All staff</b></td>
                      {days.map((d) => <td key={fmtDate(d)} className="mono">{sheetStaff.reduce((a, x) => a + dayHours(x.id, d), 0).toFixed(2)}</td>)}
                      <td className="mono"><b>{sheetStaff.reduce((a, x) => a + weekHours(x.id), 0).toFixed(2)}</b></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <div className="sub" style={{ marginTop: 10 }}>Hours shown in decimal for payroll. Week runs Monday to Sunday. A punch counts once the employee has clocked out.</div>
          </>)}

        </div>
      </div>
    </div>
  );
}

function RoleSheet({ roles, currentRole, staff, onSave, onClose }) {
  const [list, setList] = useState(roles.map((r) => ({ ...r, perms: [...r.perms] })));
  const toggle = (id, k) => setList((L) => L.map((r) => r.id !== id ? r
    : { ...r, perms: r.perms.includes(k) ? r.perms.filter((x) => x !== k) : [...r.perms, k] }));
  const setAll = (id, on) => setList((L) => L.map((r) => (r.id === id ? { ...r, perms: on ? ALL_PERMS.slice() : [] } : r)));
  const rename = (id, name) => setList((L) => L.map((r) => (r.id === id ? { ...r, name } : r)));
  const add = () => setList((L) => [...L, { id: cid(), name: "New role", perms: ["flags", "photos"] }]);
  const drop = (id) => setList((L) => L.filter((r) => r.id !== id));

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 720 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Who sees what</strong>
            <div className="sub">Tick the information each role can open</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={add}>Add role</button>
            <button className="btn" onClick={() => { onSave(list); onClose(); }}>Save</button>
          </div>
        </div>
        <div className="pad">
          {list.map((r) => (
            <div key={r.id} className="contact" style={{ background: "#fff" }}>
              <div className="ctop">
                <input value={r.name} style={{ maxWidth: 240, fontWeight: 700 }} onChange={(e) => rename(r.id, e.target.value)} />
                <div className="btnrow">
                  <button className="btn ghost mini" onClick={() => setAll(r.id, true)}>Everything</button>
                  <button className="btn ghost mini" onClick={() => setAll(r.id, false)}>Nothing</button>
                  {r.id !== currentRole && list.length > 1 && !(staff || []).some((x) => x.roleId === r.id) && (
                    <button className="btn warn mini" onClick={() => drop(r.id)}>Remove</button>
                  )}
                </div>
              </div>
              <div className="permgrid">
                {PERMS.map((prm) => (
                  <label key={prm.k} className="perm" data-on={r.perms.includes(prm.k) ? 1 : 0}>
                    <input type="checkbox" checked={r.perms.includes(prm.k)} onChange={() => toggle(r.id, prm.k)}
                      style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                    <span>
                      <b>{prm.label}</b>
                      <em>{prm.note}</em>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="notice">
            This controls what shows on screen for the person signed in. It keeps the rating and the money
            off a technician's phone, but it isn't a lock — anyone who can open this app on a device can
            switch roles. Real access control needs accounts and a server.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- water testing ---------- */
// Live chemistry analysis: LSI + dosing sized to this pool's volume (src/lib/chemistry.js).
function ChemPanel({ client, results }) {
  const gallons = parseGallons(client.gallons) || parseGallons(client.spaGallons);
  const saltPool = isSaltPool(client);
  const hasAny = Object.values(results || {}).some((v) => String(v ?? "").trim() !== "");
  if (!hasAny) return null;
  const { lsi: idx, recs } = chemAnalysis(results || {}, gallons || 10000, { saltPool });
  return (
    <div className="chempanel">
      <div className="chemtop">
        {idx ? (
          <span className="lsichip" data-b={idx.band}>
            LSI {idx.value >= 0 ? "+" : ""}{idx.value.toFixed(2)} · {idx.band === "balanced" ? "Balanced" : idx.band === "corrosive" ? "Corrosive" : "Scale-forming"}
          </span>
        ) : (
          <span className="sub" style={{ marginTop: 0 }}>Enter pH, alkalinity &amp; calcium to compute the saturation index</span>
        )}
        <span className="chemvol">{gallons ? `${gallons.toLocaleString()} gal` : "no volume on file — doses assume 10,000 gal"}{saltPool ? " · salt pool" : ""}</span>
      </div>
      {recs.length === 0 ? (
        <div className="chemok">✓ {idx ? "Water is balanced — no chemicals needed today." : "Everything entered is in range."}</div>
      ) : (
        <div>
          {recs.map((r) => (
            <div key={r.k} className="chemrec" data-sev={r.severity}>
              <b>{r.title}</b>
              <span className="dose">{r.dose}</span>
              {r.note && <span className="note">{r.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Tiny inline trend line for the water-test history.
function Spark({ pts, w = 116, h = 26 }) {
  if (!pts || pts.length < 2) return null;
  const min = Math.min(...pts), max = Math.max(...pts);
  const span = max - min || 1;
  const step = (w - 6) / (pts.length - 1);
  const xy = pts.map((v, i) => [3 + i * step, h - 4 - ((v - min) / span) * (h - 8)]);
  const last = xy[xy.length - 1];
  return (
    <svg width={w} height={h} aria-hidden="true" style={{ display: "block" }}>
      <polyline points={xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ")}
        fill="none" stroke="var(--tile)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill="var(--tile)" />
    </svg>
  );
}

function WaterTestSheet({ client, tests, saveTests, onClose, can }) {
  const [paste, setPaste] = useState("");
  const [form, setForm] = useState(null);

  const startNew = (results) => setForm({
    id: cid(), date: new Date().toISOString().slice(0, 10),
    source: results ? "spinlab" : "manual", where: "store", notes: "", results: results || {},
  });

  const importPaste = () => {
    const results = parseSpinLab(paste);
    if (!Object.keys(results).length) return;
    startNew(results);
    setPaste("");
  };

  const commit = () => {
    if (!waterTestSaveGate(form, client).ok) return;
    saveTests(client.id, [...tests.filter((t) => t.id !== form.id), { ...form, clientId: client.id }]);
    setForm(null);
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet">
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Water tests</strong>
            <div className="sub">{client.name} · {tests.length} on file · named customer required, even on rush days</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={() => startNew(null)}>Enter a test</button>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="pad">
          {!form && (
            <div className="stackpanel" style={{ marginTop: 0 }}>
              <div className="ptitle">From the Spin Lab</div>
              <textarea value={paste} style={{ minHeight: 84 }}
                placeholder={"Paste the result export from WaterLink Connect, e.g.\nFree Chlorine, 2.4\npH, 7.6\nTotal Alkalinity, 104"}
                onChange={(e) => setPaste(e.target.value)} />
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn" onClick={importPaste} disabled={!paste.trim()}>Read the results</button>
              </div>
              <div className="notice">
                This app can't talk to the Spin Lab directly. Export or copy the reading out of WaterLink
                Connect and paste it here — the parameter names are matched automatically.
              </div>
            </div>
          )}

          {form && (
            <div className="testcard latest" style={{ marginBottom: 14 }}>
              <div className="fields" style={{ marginBottom: 10 }}>
                <div><label>Test date</label><input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div>
                  <label>Source</label>
                  <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    <option value="spinlab">LaMotte Spin Lab</option>
                    <option value="manual">Manual / strip</option>
                  </select>
                </div>
                <div>
                  <label>Tested</label>
                  <select value={form.where || "store"} onChange={(e) => setForm({ ...form, where: e.target.value })}>
                    {TEST_PLACES.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="counts">
                {WATER_PARAMS.map((prm) => (
                  <div key={prm.k}>
                    <label>{prm.label}{prm.unit ? ` (${prm.unit})` : ""}</label>
                    <input value={form.results[prm.k] || ""} placeholder={`${prm.lo}–${prm.hi}`}
                      onChange={(e) => setForm({ ...form, results: { ...form.results, [prm.k]: e.target.value } })} />
                  </div>
                ))}
              </div>
              <ChemPanel client={client} results={form.results} />
              <div style={{ marginTop: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: form.algae ? "var(--clay)" : undefined, fontWeight: form.algae ? 750 : undefined }}>
                  <input type="checkbox" checked={!!form.algae} style={{ width: "auto" }}
                    onChange={(e) => setForm({ ...form, algae: e.target.checked })} />
                  Algae present in this water
                </label>
                {form.algae && <div className="sub" style={{ marginTop: 4 }}>Saving this test notifies service management and the owner so the property gets tracked.</div>}
              </div>
              <div style={{ marginTop: 10 }}>
                <label>Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="What you told the customer, what they bought." />
              </div>
              <div className="btnrow" style={{ marginTop: 10 }}>
                <button className="btn" onClick={commit}>Save test</button>
                <button className="btn ghost" onClick={() => setForm(null)}>Cancel</button>
              </div>
            </div>
          )}

          {tests.length === 0 && !form ? (
            <div className="empty">No water tests on file yet.</div>
          ) : tests.map((t, i) => (
            <div key={t.id} className={"testcard" + (i === 0 ? " latest" : "")}>
              <div className="vhead">
                <span className="vdate">{t.date}</span>
                <div className="vdocs">
                  <span className="srcchip" data-s={t.source}>{t.source === "spinlab" ? "Spin Lab" : "Manual"}</span>
                  <span className="srcchip" data-s="manual">{TEST_PLACE_LABEL[t.where || "store"]}</span>
                  {t.algae && <span className="srcchip" style={{ background: "var(--clay)", color: "#fff" }}>⚠ Algae</span>}
                  {t.by && <span className="srcchip" data-s="manual">{t.by}</span>}
                </div>
              </div>
              <div className="readings">
                {WATER_PARAMS.filter((prm) => t.results[prm.k] !== undefined && t.results[prm.k] !== "").map((prm) => (
                  <span key={prm.k} className="reading" data-s={readingState(prm, t.results[prm.k])}>
                    <span className="rl">{prm.label}</span>
                    <span className="rv">{t.results[prm.k]}</span>
                  </span>
                ))}
              </div>
              {i === 0 && <ChemPanel client={client} results={t.results} />}
              {t.notes && <div className="vnotes">{t.notes}</div>}
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn ghost mini" onClick={() => setForm({ ...t })}>Edit</button>
                <button className="btn warn mini" onClick={() => saveTests(client.id, tests.filter((x) => x.id !== t.id))}>Delete</button>
              </div>
            </div>
          ))}

          {!form && tests.length >= 2 && (
            <div className="stackpanel">
              <div className="ptitle">Trends · last {Math.min(tests.length, 8)} tests</div>
              <div className="trendgrid">
                {WATER_PARAMS.filter((p) => ["fc", "ph", "ta", "ch", "cya", "salt"].includes(p.k)).map((p) => {
                  const pts = tests.slice(0, 8).reverse()
                    .map((t) => parseFloat(t.results[p.k])).filter((v) => isFinite(v));
                  if (pts.length < 2) return null;
                  return (
                    <div key={p.k} className="trendcell">
                      <div className="tl">{p.label}</div>
                      <div className="tv num">{pts[pts.length - 1]}{p.unit ? <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}> {p.unit}</span> : null}</div>
                      <Spark pts={pts} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- account flag ---------- */
function FlagSheet({ client, onSave, onClose }) {
  const f = { ...defFlag(), ...(client.flag || {}) };
  const [message, setMessage] = useState(f.message);
  const [scope, setScope] = useState(f.scope);
  const [until, setUntil] = useState(f.until || "");
  const expired = f.on && f.until && String(f.until) < new Date().toISOString().slice(0, 10);

  const commit = (on) => {
    onSave({ on, message: message.trim(), scope, until, updated: new Date().toISOString().slice(0, 10) });
    onClose();
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 520 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Flag message</strong>
            <div className="sub">{client.name}</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
        </div>
        <div className="pad">
          {expired && (
            <div className="notice" style={{ marginBottom: 10 }}>
              This flag auto-cleared on {f.until} and no longer shows. Update it to bring it back, or leave it retired.
            </div>
          )}
          <label>Message — pinned at the top of this account</label>
          <textarea value={message} style={{ minHeight: 96 }} autoFocus
            placeholder="Never enter through the house. PO required before anything leaves the counter."
            onChange={(e) => setMessage(e.target.value)} />

          <div style={{ marginTop: 13 }}>
            <label>Who needs to see it</label>
            <div className="seg">
              {FLAG_SCOPES.map((o) => (
                <button key={o.v} type="button" data-on={scope === o.v ? 1 : 0} onClick={() => setScope(o.v)}>
                  {o.label}
                </button>
              ))}
            </div>
            <div className="sub">
              {scope === "service" ? "Shows on service accounts. A retail-only customer never sees it."
                : scope === "retail" ? "Shows on retail accounts. A service-only customer never sees it."
                : "Shows on every account, whichever side of the business you're on."}
            </div>
          </div>

          <div style={{ marginTop: 13, maxWidth: 240 }}>
            <label>Auto-clear on (optional)</label>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            <div className="sub">Leave blank to keep the flag until someone clears it. With a date, it quietly retires itself after that day.</div>
          </div>

          <div className="btnrow" style={{ marginTop: 15 }}>
            <button className="btn" onClick={() => commit(true)} disabled={!message.trim()}>
              {f.on ? "Update flag" : "Flag this account"}
            </button>
            {f.on && <button className="btn warn" onClick={() => commit(false)}>Clear flag</button>}
          </div>

          {(client.flagHistory || []).length > 0 && (
            <div className="stackpanel" style={{ marginTop: 14 }}>
              <div className="ptitle">Past flags</div>
              {(client.flagHistory || []).slice(-5).reverse().map((h) => (
                <div key={h.id} className="sub" style={{ padding: "5px 0", borderTop: "1px solid var(--line)" }}>
                  “{h.message}” — {h.how} {h.endedAt}{h.endedBy ? ` by ${h.endedBy}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- pricing tiers ---------- */
// --- Two-way texting: helpers kept pure so the math is testable ---
export function normPhone10(s) {
  const d = String(s || "").replace(/\D/g, "");
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
}
export function clientByPhone(clients, number) {
  const n = normPhone10(number);
  if (n.length !== 10) return null;
  for (const c of clients || []) {
    for (const k of c.contacts || []) if (normPhone10(k.phone) === n) return c;
  }
  return null;
}
// Merge inbound texts and business-line sends into per-number conversations,
// newest conversation first, messages oldest-first inside each.
export function smsThreads(inbound, sent) {
  const map = new Map();
  const bucket = (num) => {
    const key = normPhone10(num) || String(num || "");
    if (!map.has(key)) map.set(key, { number: num, msgs: [], unread: 0 });
    return map.get(key);
  };
  for (const m of inbound || []) {
    const t = bucket(m.from_number);
    t.number = m.from_number;
    t.msgs.push({ dir: "in", body: m.body || "", at: m.received_at, media: m.media || null });
    if (!m.read) t.unread++;
  }
  for (const m of sent || []) {
    bucket(m.to_number).msgs.push({ dir: "out", body: m.body || "", at: m.created_at });
  }
  const out = [...map.values()];
  for (const t of out) t.msgs.sort((a, b) => String(a.at).localeCompare(String(b.at)));
  out.sort((a, b) => String(b.msgs[b.msgs.length - 1]?.at || "").localeCompare(String(a.msgs[a.msgs.length - 1]?.at || "")));
  return out;
}

function prettyNum(s) {
  const n = normPhone10(s);
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : String(s || "");
}

function TextsInbox({ inbound, sent, clients, onReply, onMarkRead, onDeleteThread, onOpenClient, onClose }) {
  const threads = useMemo(() => smsThreads(inbound, sent), [inbound, sent]);
  const [openNum, setOpenNum] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    if (deleting) return;
    setDeleting(true); setErr("");
    try { await onDeleteThread(cur.number); setOpenNum(null); setConfirmDel(false); }
    catch (e) { setErr(String((e && e.message) || e)); }
    setDeleting(false);
  };
  const cur = openNum ? threads.find((t) => normPhone10(t.number) === normPhone10(openNum)) : null;
  const curClient = cur ? clientByPhone(clients, cur.number) : null;
  const openThread = (t) => { setOpenNum(t.number); setErr(""); setDraft(""); setConfirmDel(false); if (t.unread) onMarkRead(t.number); };
  const sendReply = async () => {
    if (sending || !draft.trim()) return;
    setErr(""); setSending(true);
    try { await onReply(cur.number, draft.trim(), curClient); setDraft(""); }
    catch (e) { setErr(String((e && e.message) || e)); }
    setSending(false);
  };
  const when = (iso) => {
    const d = new Date(iso); if (isNaN(d)) return "";
    return `${fmtDate(d)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 640 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>{cur ? (curClient ? curClient.name : prettyNum(cur.number)) : "Customer texts"}</strong>
            <div className="sub">
              {cur
                ? (curClient ? prettyNum(cur.number) : `Pool Master line · ${prettyNum(cur.number)} · not saved on a customer`)
                : "Business texting line · Pool Master · (249) 805-0438"}
            </div>
          </div>
          <div className="btnrow">
            {cur && onDeleteThread && !confirmDel && (
              <button className="btn ghost" style={{ color: "#B3261E", borderColor: "#E8B4B4" }} onClick={() => setConfirmDel(true)}>Delete conversation</button>
            )}
            {cur && curClient && !confirmDel && <button className="btn ghost" onClick={() => onOpenClient(curClient.id)}>View account</button>}
            {cur && !confirmDel && <button className="btn ghost" onClick={() => { setOpenNum(null); setConfirmDel(false); }}>All texts</button>}
            {!confirmDel && <button className="btn ghost" onClick={onClose}>Close</button>}
            {cur && confirmDel && (<>
              <span style={{ alignSelf: "center", fontSize: 13, fontWeight: 700, color: "#B3261E" }}>Delete this whole conversation?</span>
              <button className="btn" style={{ background: "#B3261E" }} onClick={doDelete} disabled={deleting}>{deleting ? "Deleting…" : "Yes, delete"}</button>
              <button className="btn ghost" onClick={() => setConfirmDel(false)}>Keep it</button>
            </>)}
          </div>
        </div>
        <div className="pad">
          {!cur && (threads.length === 0
            ? <div className="empty">No customer texts yet. Replies to (249) 805-0438 land here the moment they arrive.</div>
            : threads.map((t) => {
              const c = clientByPhone(clients, t.number);
              const last = t.msgs[t.msgs.length - 1];
              return (
                <button key={t.number} className="clientrow" onClick={() => openThread(t)} style={{ width: "100%" }}>
                  <div className="cr-name" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {t.unread > 0 && <span className="badge" style={{ position: "static" }}>{t.unread}</span>}
                    <span style={{ fontWeight: t.unread ? 800 : 600 }}>{c ? c.name : prettyNum(t.number)}</span>
                    {!c && <span className="pill" data-t="retail">Pool Master · unsaved number</span>}
                  </div>
                  <div className="cr-meta" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {last ? `${last.dir === "out" ? "You: " : ""}${last.body}` : ""}
                    <span style={{ color: "#7C8DA5", marginLeft: 8 }}>{last ? when(last.at) : ""}</span>
                  </div>
                </button>
              );
            }))}
          {cur && (<>
            <div style={{ maxHeight: "48vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, padding: "4px 2px" }}>
              {cur.msgs.map((m, i) => (
                <div key={i} style={{ alignSelf: m.dir === "out" ? "flex-end" : "flex-start", maxWidth: "80%" }}>
                  <div style={{
                    padding: "8px 12px", borderRadius: 14, whiteSpace: "pre-wrap", fontSize: 14,
                    background: m.dir === "out" ? "#0B6FBE" : "#EEF3F9",
                    color: m.dir === "out" ? "#fff" : "#12233B",
                    borderBottomRightRadius: m.dir === "out" ? 4 : 14,
                    borderBottomLeftRadius: m.dir === "in" ? 4 : 14 }}>
                    {m.body}
                    {m.media && m.media.map((u, j) => (
                      <div key={j}><a href={u} target="_blank" rel="noreferrer" style={{ color: m.dir === "out" ? "#D6E9FA" : "#0B6FBE" }}>View attachment {j + 1}</a></div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: "#7C8DA5", textAlign: m.dir === "out" ? "right" : "left", marginTop: 2 }}>{m.dir === "out" ? "Pool Master · " : ""}{when(m.at)}</div>
                </div>
              ))}
            </div>
            {err && <div className="notice" style={{ borderColor: "#E8B4B4", background: "#FDF2F2", color: "#8A2C2C" }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} style={{ flex: 1 }}
                placeholder={`Reply to ${curClient ? curClient.name : prettyNum(cur.number)}…`} />
              <button className="btn" onClick={sendReply} disabled={sending || !draft.trim()}>{sending ? "Sending…" : "Send"}</button>
            </div>
            <div className="notice" style={{ marginTop: 8 }}>
              Replies go out from the business texting line and are logged on the account{curClient ? "" : " once this number is attached to a customer"}.
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

const MAIL_FROMS = [
  ["invoices", "accounts@ — invoices & A/R"], ["service", "service@ — service"],
  ["retail", "sales@ — retail & orders"], ["repairs", "repairs@ — repairs"],
  ["owner", "tcrocione@ — Talon (owner)"], ["victoria", "vnash@ — Victoria"],
];
function ComposeSheet({ client, kind, to, subject: subj0, body: body0, emailCat, onClose, onSendText, onSendMail, clients = [], businessProfile = {} }) {
  const [fromCat, setFromCat] = useState(emailCat || "service");
  const who = client || { name: "Pool Master" };
  const isMail = kind === "mail";
  const [addr, setAddr] = useState(to || "");
  const [custQ, setCustQ] = useState("");
  const [pickedClient, setPickedClient] = useState(client || null);
  const [subject, setSubject] = useState(subj0 || (isMail ? `${who.name} — pool service` : ""));
  const [body, setBody] = useState(body0 || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendErr, setSendErr] = useState("");
  const smsFrom = smsSenderIdentity(businessProfile);
  const canSendNow = !isMail && typeof onSendText === "function";
  const canSendMail = isMail && typeof onSendMail === "function";
  const emailMatches = isMail ? searchClientsForEmail(clients, custQ || addr, 8) : [];
  const sendNow = async () => {
    if (sending || sent) return;
    setSendErr(""); setSending(true);
    try {
      const outBody = isMail ? body : ensureSmsBrand(body, smsFrom);
      await (isMail ? onSendMail(addr, subject, outBody, fromCat) : onSendText(addr, outBody));
      setSent(true); setTimeout(onClose, 900);
    }
    catch (e) { setSendErr(String((e && e.message) || e)); }
    setSending(false);
  };
  const href = isMail
    ? `mailto:${encodeURIComponent(addr)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : `sms:${encodeURIComponent(addr.replace(/[^0-9+]/g, ""))}?&body=${encodeURIComponent(body)}`;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch { setCopied(false); }
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 560 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>{isMail ? "Send an email" : "Send a text"}</strong>
            <div className="sub">{client ? who.name : "Not tied to a customer"}</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={onClose}>Close</button>
            {canSendNow || canSendMail ? (
              <>
                <a className="btn ghost" href={href} target="_blank" rel="noreferrer" onClick={() => setTimeout(onClose, 400)}>{isMail ? "Open in mail" : "Open in messages"}</a>
                <button className="btn" onClick={sendNow} disabled={sending || !addr || !body.trim()}>
                  {sent ? "Sent ✓" : sending ? "Sending…" : isMail ? "Send email now" : "Send text now"}
                </button>
              </>
            ) : (
              <a className="btn" href={href} target="_blank" rel="noreferrer" onClick={() => setTimeout(onClose, 400)}>
                {isMail ? "Open in mail" : "Open in messages"}
              </a>
            )}
          </div>
        </div>
        <div className="pad">
          <div className="fields">
            <div className="f-wide">
              <label>{isMail ? "To" : "Number"}</label>
              <input value={addr} onChange={(e) => { setAddr(e.target.value); if (isMail) setCustQ(e.target.value); }}
                placeholder={isMail ? "Type a customer name or email…" : "Phone number"} />
              {isMail && emailMatches.length > 0 && (
                <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6 }}>
                  {emailMatches.map((m) => (
                    <button type="button" key={m.id} className="poslookupitem" style={{ width: "100%", textAlign: "left" }}
                      onClick={() => { setAddr(m.email); setCustQ(""); setPickedClient(m.client); }}>
                      <b>{m.name}</b>
                      <span className="sub" style={{ marginTop: 0, display: "block" }}>{m.email}</span>
                    </button>
                  ))}
                </div>
              )}
              {!isMail && (
                <div className="sub" style={{ marginTop: 4 }}>
                  Sending as <b>{smsFrom}</b> — even if this number is not saved on a customer phone.
                </div>
              )}
            </div>
            {isMail && canSendMail && (
              <div className="f-wide">
                <label>Send from</label>
                <select value={fromCat} onChange={(e) => setFromCat(e.target.value)}>
                  {MAIL_FROMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
            {isMail && (
              <div className="f-wide">
                <label>Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
            )}
            <div className="f-wide">
              <label>Message</label>
              <textarea rows={isMail ? 8 : 5} value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={isMail ? "Hi — following up on your pool service…" : "On my way, about 20 minutes out."} />
            </div>
          </div>
          <div className="btnrow" style={{ marginTop: 4 }}>
            <button className="btn ghost" onClick={copy}>{copied ? "Copied" : "Copy message"}</button>
          </div>
          {sendErr && <div className="notice" style={{ borderColor: "#E8B4B4", background: "#FDF2F2", color: "#8A2C2C" }}>{sendErr}</div>}
          <div className="notice">
            {isMail
              ? (canSendMail
                ? "Send email now delivers it from the right company address for this message (accounts@ / service@ / sales@ / repairs@) and logs it on the account."
                : "This hands the message to whatever mail app this device uses. Sending straight from your Gmail account needs the Google account connected — that comes later.")
              : canSendNow
                ? "Send text now delivers it from the business texting line — (249) 805-0438 — and logs it on the account. Open in messages uses this device's own number instead."
                : "This hands the message to the device's messaging app. Sending from your business line needs the eSIM or SMS service connected — that comes later."}
          </div>
        </div>
      </div>
    </div>
  );
}

// HelcimPay.js loads once, from Helcim's own servers — the card form is theirs, not ours.
let helcimPayLoader = null;
function loadHelcimPay() {
  if (typeof window !== "undefined" && window.appendHelcimPayIframe) return Promise.resolve();
  if (!helcimPayLoader) {
    helcimPayLoader = new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = "https://secure.helcim.app/helcim-pay/services/start.js";
      s.onload = res;
      s.onerror = () => { helcimPayLoader = null; rej(new Error("Couldn't load Helcim's secure form — check the connection.")); };
      document.head.appendChild(s);
    });
  }
  return helcimPayLoader;
}

function CardSheet({ client, onSave, onClose }) {
  const b = { ...defBilling(), ...(client.billing || {}) };
  const [card, setCard] = useState({ ...defBilling().card, ...(b.card || {}) });
  const set = (k, v) => setCard((c) => ({ ...c, [k]: v }));
  const enough = card.last4.length === 4 || card.ref.trim();
  const [hpBusy, setHpBusy] = useState(false);
  const [hpMsg, setHpMsg] = useState("");
  const tokenized = /^helcim:/.test(String(card.ref || ""));

  // The real path: Helcim's hosted form saves the card into their vault; we keep only the token.
  const helcimSave = async () => {
    setHpBusy(true); setHpMsg("");
    try {
      const prior = /^helcim:([^:]+):/.exec(String(card.ref || "").trim());
      const { data: init, error } = await supabase.functions.invoke("payments", {
        body: { action: "init-card-save", customerCode: prior ? prior[1] : undefined },
      });
      if (error || !init || init.error || !init.checkoutToken) throw new Error((init && init.error) || "Couldn't start the secure card form.");
      await loadHelcimPay();
      const token = init.checkoutToken;
      const result = await new Promise((resolve) => {
        const handler = (ev) => {
          if (!ev.data || ev.data.eventName !== "helcim-pay-js-" + token) return;
          if (["ABORTED", "HIDE", "DECLINE"].includes(ev.data.eventStatus)) { cleanup(); resolve(null); }
          if (ev.data.eventStatus === "SUCCESS") { cleanup(); resolve(ev.data.eventMessage); }
        };
        const cleanup = () => { window.removeEventListener("message", handler); try { if (window.removeHelcimPayIframe) window.removeHelcimPayIframe(); } catch { /* iframe already gone */ } };
        window.addEventListener("message", handler);
        window.appendHelcimPayIframe(token);
      });
      if (!result) { setHpMsg("Card entry closed — nothing was saved."); setHpBusy(false); return; }
      const d = (result && result.data) || result || {};
      const { data: conf, error: cErr } = await supabase.functions.invoke("payments", {
        body: { action: "confirm-card", customerCode: d.customerCode, cardToken: d.cardToken },
      });
      if (cErr || !conf || conf.error || !conf.ok) throw new Error((conf && conf.error) || "Helcim didn't confirm the saved card — try again.");
      onSave({
        holder: conf.holder || d.cardHolderName || card.holder || "",
        brand: conf.brand || d.cardType || "Card",
        last4: conf.last4 || String(d.cardNumber || "").replace(/[^0-9]/g, "").slice(-4),
        exp: conf.exp || "",
        ref: `helcim:${d.customerCode}:${d.cardToken}`,
      });
    } catch (e) {
      setHpMsg(String(e.message || e));
      setHpBusy(false);
    }
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 520 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Card on file</strong>
            <div className="sub">{client.name} · needed before credit card can be the payment method</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn" disabled={!enough} onClick={() => onSave(card)}>Save card</button>
          </div>
        </div>
        <div className="pad">
          {serverMode() && supabase && (
            <div className="stackpanel" style={{ marginTop: 0, marginBottom: 13, borderColor: tokenized ? "#12A650" : undefined }}>
              <div className="ptitle">{tokenized ? "✓ Chargeable card in the Helcim vault" : "Add the card the real way"}</div>
              <div className="sub" style={{ marginBottom: 9 }}>
                {tokenized
                  ? `${card.brand} ending ${card.last4 || "····"}${card.exp ? " · expires " + card.exp : ""} — saved securely with Helcim. Charge now and AutoPay can use it.`
                  : "Opens Helcim's secure form — the customer's card number goes straight to Helcim and never touches this app. This is the only way a card becomes chargeable."}
              </div>
              <button type="button" className="btn" disabled={hpBusy} onClick={helcimSave}>
                {hpBusy ? "Opening the secure form…" : tokenized ? "Replace card — secure Helcim form" : "💳 Enter card — secure Helcim form"}
              </button>
              {hpMsg && <div className="sub" style={{ marginTop: 7, color: "var(--clay)", fontWeight: 650 }}>{hpMsg}</div>}
            </div>
          )}
          <div className="fields">
            <div className="f-wide"><label>Cardholder name</label>
              <input autoFocus value={card.holder} onChange={(e) => set("holder", e.target.value)} /></div>
            <div>
              <label>Brand</label>
              <select value={card.brand} onChange={(e) => set("brand", e.target.value)}>
                {CARD_BRANDS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div><label>Last 4 digits</label>
              <input value={card.last4} maxLength={4} placeholder="4412"
                onChange={(e) => set("last4", e.target.value.replace(/[^0-9]/g, ""))} /></div>
            <div><label>Expiry (MM/YY)</label>
              <input value={card.exp} placeholder="09/28" onChange={(e) => set("exp", e.target.value)} /></div>
            <div className="f-wide"><label>Processor reference / token</label>
              <input value={card.ref} placeholder="cus_9F2K71 — the saved-card ID from your processor"
                onChange={(e) => set("ref", e.target.value)} /></div>
          </div>
          <div className="notice">
            The fields above are reference-only — a card typed here <b>cannot be charged</b>. Never type a full
            card number anywhere in this app. Use the secure Helcim form above to make a card chargeable.
          </div>
          {!enough && <div className="sub">Enter either the last 4 digits or the processor reference to save.</div>}
        </div>
      </div>
    </div>
  );
}

function LevelSheet({ levels, clients, onSave, onClose }) {
  const [list, setList] = useState(levels.map((l) => ({ ...l })));
  const set = (id, k, v) => setList((L) => L.map((l) => (l.id === id
    ? { ...l, [k]: k === "name" || k === "note" ? v : Math.max(0, Math.min(100, Number(v) || 0)) } : l)));
  const add = () => setList((L) => [...L, { id: cid(), rank: L.length + 1, name: "New level", labour: 0, chemical: 0, material: 0, note: "" }]);
  const drop = (id) => setList((L) => L.filter((l) => l.id !== id));
  const used = (id) => clients.filter((c) => {
    const b = c.billing || {};
    return (b.discountMode || "level") === "level" && (b.levelId || "ok") === id;
  }).length;

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 660 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Customer levels</strong>
            <div className="sub">Each level sets labour, chemical and material discounts</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={add}>Add level</button>
            <button className="btn" onClick={() => { onSave(list); onClose(); }}>Save levels</button>
          </div>
        </div>
        <div className="pad">
          {list.map((l, i) => (
            <div key={l.id} className="contact" style={{ background: "#fff" }}>
              <div className="ctop">
                <div style={{ display: "flex", alignItems: "center", gap: 9, flex: 1 }}>
                  <span className="lvlrank">{i + 1}</span>
                  <input value={l.name} style={{ maxWidth: 200, fontWeight: 700 }} onChange={(e) => set(l.id, "name", e.target.value)} />
                </div>
                <div className="btnrow">
                  <span className="sub" style={{ marginTop: 0 }}>{used(l.id)} on this level</span>
                  {used(l.id) === 0 && list.length > 1 && (
                    <button className="btn warn mini" onClick={() => drop(l.id)}>Remove</button>
                  )}
                </div>
              </div>
              <div className="fields">
                {CATS.map(({ k, label }) => (
                  <div key={k}>
                    <label>{label} discount (%)</label>
                    <input type="number" min="0" max="100" value={l[k]} onChange={(e) => set(l.id, k, e.target.value)} />
                  </div>
                ))}
                <div className="f-wide">
                  <label>Who it's for</label>
                  <input value={l.note || ""} onChange={(e) => set(l.id, "note", e.target.value)} />
                </div>
              </div>
            </div>
          ))}
          <div className="notice">
            Picking a level fills all three discounts at once. Any account can still be switched to its own
            numbers without touching the level.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- shared line-items editor (materials + labour), used by visits and quotes ---------- */
function WorkLines({ materials, labourLines, parts = [], services = [], onChange, listId = "pm-partcodes" }) {
  const mats = materials || [];
  const labs = labourLines || [];
  const setMats = (next) => onChange({ materials: next, labourLines: labs });
  const setLabs = (next) => onChange({ materials: mats, labourLines: next });
  const activeParts = (parts || []).filter((p) => p.active !== false);
  const activeServices = (services || []).filter((s) => s.active !== false);

  const addMaterial = () => setMats([...mats, { id: cid(), code: "", desc: "", qty: "1", price: "" }]);
  const setMaterial = (id, k, v) => setMats(mats.map((m) => (m.id === id ? { ...m, [k]: v } : m)));
  const dropMaterial = (id) => setMats(mats.filter((m) => m.id !== id));
  // When a part # / barcode matches the catalogue, fill in the description and price automatically.
  const onMaterialCode = (id, code) => setMats(mats.map((m) => {
    if (m.id !== id) return m;
    const hit = activeParts.find((p) => (p.code || "").trim().toLowerCase() === code.trim().toLowerCase());
    const next = { ...m, code };
    if (hit) { if (!m.desc) next.desc = hit.name || ""; if (!m.price) next.price = hit.price || ""; }
    return next;
  }));
  const addPart = (partId) => { const p = activeParts.find((x) => x.id === partId); if (p) setMats([...mats, { id: cid(), code: p.code || "", desc: p.name || "", qty: "1", price: p.price || "" }]); };
  const addLabour = () => setLabs([...labs, { id: cid(), desc: "", rate: "" }]);
  const setLabourLine = (id, k, v) => setLabs(labs.map((l) => (l.id === id ? { ...l, [k]: v } : l)));
  const dropLabour = (id) => setLabs(labs.filter((l) => l.id !== id));
  // The customer's billing zone decides which catalogue rate a booked service bills at.
  const zoneRate = (s) => ((client && (client.billing || {}).zone === "z2" && String(s.rateZone2 ?? "").trim()) ? s.rateZone2 : s.rate);
  const addService = (svcId) => { const s = activeServices.find((x) => x.id === svcId); if (s) setLabs([...labs, { id: cid(), desc: s.name || "", rate: zoneRate(s) || "" }]); };
  const matTotal = sumMaterials(mats);
  const labTotal = sumLabour(labs);

  return (<>
    <div className="f-wide">
      <label>Materials — type the part # or scan a barcode into the box (catalogue parts fill in the rest)</label>
      <datalist id={listId}>{activeParts.map((p) => <option key={p.id} value={p.code}>{p.name}</option>)}</datalist>
      {mats.map((m) => {
        const wp = activeParts.find((p) => partHasWarranty(p) && (p.code || "").trim().toLowerCase() === String(m.code || "").trim().toLowerCase());
        const known = wp ? [...new Set([...Object.values(wp.truckSerials || {}).flat(), ...(wp.shelfSerials || [])])] : [];
        return (
        <div key={m.id}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.5fr 54px 84px 30px", gap: 6, marginBottom: 6, alignItems: "center" }}>
          <input placeholder="Part # / barcode" list={listId} value={m.code} onChange={(e) => onMaterialCode(m.id, e.target.value)} />
          <input placeholder="Description" value={m.desc} onChange={(e) => setMaterial(m.id, "desc", e.target.value)} />
          <input placeholder="Qty" inputMode="decimal" value={m.qty} onChange={(e) => setMaterial(m.id, "qty", e.target.value)} />
          <input placeholder="$ each" inputMode="decimal" value={m.price} onChange={(e) => setMaterial(m.id, "price", e.target.value)} />
          <button type="button" className="btn warn mini" title="Remove this material" onClick={() => dropMaterial(m.id)}>×</button>
        </div>
        {wp && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", margin: "-2px 0 6px" }}>
            <span className="doc" style={{ background: serialList(m.serial).length >= Math.max(1, Math.round(num(m.qty) || 1)) ? "#0E7A3C" : "#DC1515", flex: "none" }}>SERIAL</span>
            {known.length > 0 && (
              <select value="" style={{ width: "auto" }} onChange={(e) => { if (e.target.value) setMaterial(m.id, "serial", [serialList(m.serial), e.target.value].flat().filter(Boolean).join(", ")); e.target.value = ""; }}>
                <option value="">pick from stock…</option>
                {known.filter((x) => !serialList(m.serial).includes(x)).map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            )}
            <input value={m.serial || ""} placeholder={num(m.qty) > 1 ? `${Math.round(num(m.qty))} serials, comma-separated` : "serial # — required to bill"} style={{ flex: 1 }}
              onChange={(e) => setMaterial(m.id, "serial", e.target.value)} />
          </div>
        )}
        </div>
      ); })}
      <div className="btnrow" style={{ marginTop: 2, alignItems: "center" }}>
        <button type="button" className="btn ghost mini" onClick={addMaterial}>+ Add material</button>
        {activeParts.length > 0 && (
          <select value="" onChange={(e) => { if (e.target.value) addPart(e.target.value); e.target.value = ""; }}>
            <option value="">Add from catalogue…</option>
            {activeParts.map((p) => (
              <option key={p.id} value={p.id}>{p.code ? p.code + " — " : ""}{p.name}{num(p.qty) <= 0 ? " (out of stock)" : ""}</option>
            ))}
          </select>
        )}
        {matTotal > 0 && <span className="sub">Materials total ${matTotal.toFixed(2)}</span>}
      </div>
    </div>
    <div className="f-wide">
      <label>Labour — fill in ahead of time, add more lines as needed</label>
      {labs.map((l) => (
        <div key={l.id} style={{ display: "grid", gridTemplateColumns: "1fr 100px 30px", gap: 6, marginBottom: 6, alignItems: "center" }}>
          <input placeholder="Service / description (e.g. Weekly clean, opening)" value={l.desc} onChange={(e) => setLabourLine(l.id, "desc", e.target.value)} />
          <input placeholder="$ rate" inputMode="decimal" value={l.rate} onChange={(e) => setLabourLine(l.id, "rate", e.target.value)} />
          <button type="button" className="btn warn mini" title="Remove this labour line" onClick={() => dropLabour(l.id)}>×</button>
        </div>
      ))}
      <div className="btnrow" style={{ marginTop: 2, alignItems: "center" }}>
        <button type="button" className="btn ghost mini" onClick={addLabour}>+ Add labour</button>
        {activeServices.length > 0 && (
          <select value="" onChange={(e) => { if (e.target.value) addService(e.target.value); e.target.value = ""; }}>
            <option value="">Add from services…</option>
            {activeServices.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{zoneRate(s) ? ` — $${zoneRate(s)}${(client && (client.billing || {}).zone === "z2") ? " (Zone 2)" : ""}` : ""}</option>
            ))}
          </select>
        )}
        {labTotal > 0 && <span className="sub">Labour total ${labTotal.toFixed(2)}</span>}
      </div>
    </div>
  </>);
}

// Resolve selected term presets (+ any custom note) into one block of text.
// Bodies are looked up live from data.terms, so an owner edit shows on the next print.
function resolveTerms(terms, termIds, note) {
  const picked = (termIds || []).map((id) => (terms || []).find((t) => t.id === id)).filter(Boolean);
  const parts = picked.map((t) => t.body).filter(Boolean);
  if (note && note.trim()) parts.push(note.trim());
  return parts.join("\n\n");
}
function defaultTermIds(terms, workType) {
  return (terms || []).filter((t) => t.active !== false && (t.tag === "any" || t.tag === (workType || "any"))).map((t) => t.id);
}
// Paperwork always shows the current catalogue text. If staff ticked specific
// blocks, those ids win; otherwise every active block tagged for this work (and "any") prints.
function currentTermsText(terms, workType, termIds, note) {
  const ids = (termIds && termIds.length) ? termIds : defaultTermIds(terms, workType);
  return resolveTerms(terms, ids, note);
}
function termsPrintHtml(text) {
  if (!text) return "";
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  return `<div class="terms"><strong>Terms &amp; conditions</strong>\n${esc(text)}</div>`;
}
const TERMS_PRINT_CSS = `.terms{white-space:pre-wrap;background:#f6f9fc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin-top:16px;font-size:12.5px;color:#33414f;}`;
function jobWorkType(j) {
  return deriveKind(j && j.serviceType) || (j && j.kind) || "service";
}
function printJobDoc(job, client, terms) {
  const w = window.open("", "_blank"); if (!w) return false;
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const t = currentTermsText(terms, jobWorkType(job), job.termIds, job.termsNote);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Work order ${esc((job && job.workOrder) || "")}</title>
<style>
  body{font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;margin:40px;}
  h1{font-size:22px;margin:0;color:#0B4F8A;} .muted{color:#5a6472;}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0B4F8A;padding-bottom:12px;margin-bottom:18px;}
  ${TERMS_PRINT_CSS}
  @media print{body{margin:0;}}
</style></head><body onload="window.print()">
  <div class="head"><div><h1>Pool Master Canada</h1><div class="muted">Work order ${esc((job && job.workOrder) || "")}</div></div>
  <div class="muted">${esc((job && job.bookedDate) || "")}</div></div>
  <div><strong>${esc((client && client.name) || "")}</strong>${client && client.address ? `<div class="muted">${esc(client.address)}</div>` : ""}</div>
  <p><b>${esc((job && (job.serviceType || JOB_LABEL[job.kind])) || "Service")}</b></p>
  ${job && job.what ? `<p>${esc(job.what)}</p>` : ""}
  ${job && job.note ? `<p class="muted">${esc(job.note)}</p>` : ""}
  ${termsPrintHtml(t)}
</body></html>`);
  w.document.close();
  return true;
}
function printVisitInvoice(v, client, terms) {
  const w = window.open("", "_blank"); if (!w) return false;
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const money = (n) => "$" + num(n).toFixed(2);
  const mats = (v.materials || []).filter((m) => m.desc || m.code || m.price);
  const labs = (v.labourLines || []).filter((l) => l.desc || l.rate);
  const rows = [];
  mats.forEach((m) => { const qty = num(m.qty) || 1; rows.push(`<tr><td>${esc([m.code, m.desc].filter(Boolean).join(" — "))}</td><td class="r">${qty}</td><td class="r">${money(m.price)}</td><td class="r">${money(qty * num(m.price))}</td></tr>`); });
  labs.forEach((l) => rows.push(`<tr><td>${esc(l.desc || "Labour")}</td><td class="r">—</td><td class="r">—</td><td class="r">${money(l.rate)}</td></tr>`));
  if (!rows.length && (v.desc || num(v.amount))) rows.push(`<tr><td>${esc(v.desc || "Service")}</td><td class="r">—</td><td class="r">—</td><td class="r">${money(v.amount)}</td></tr>`);
  const workType = v.repairType ? "repair" : (visitSide(v) === "retail" ? "any" : "service");
  const t = currentTermsText(terms, workType, v.termIds, v.termsNote);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Invoice — ${esc((client && client.name) || "")}</title>
<style>
  body{font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;margin:40px;}
  h1{font-size:22px;margin:0;color:#0B4F8A;} .muted{color:#5a6472;}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0B4F8A;padding-bottom:12px;margin-bottom:18px;}
  table{width:100%;border-collapse:collapse;margin:14px 0;} th,td{text-align:left;padding:8px 6px;border-bottom:1px solid #e2e8f0;} .r{text-align:right;}
  tfoot td{font-weight:700;border-top:2px solid #12233b;border-bottom:none;}
  ${TERMS_PRINT_CSS}
  @media print{body{margin:0;}}
</style></head><body onload="window.print()">
  <div class="head"><div><h1>Pool Master Canada</h1><div class="muted">Invoice ${esc(v.invoiceNo || v.workOrder || "")}</div></div>
  <div class="muted">${esc(v.date || "")}</div></div>
  <div><strong>${esc((client && client.name) || "")}</strong>${client && client.address ? `<div class="muted">${esc(client.address)}</div>` : ""}</div>
  <table><thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Each</th><th class="r">Amount</th></tr></thead>
  <tbody>${rows.join("") || `<tr><td colspan="4" class="muted">No line items.</td></tr>`}</tbody>
  <tfoot><tr><td colspan="3" class="r">${v.paid === false ? "Amount due" : "Total"}</td><td class="r">${money(v.amount)}</td></tr></tfoot></table>
  ${termsPrintHtml(t)}
</body></html>`);
  w.document.close();
  return true;
}
function markInvoiceMailed(visit, meta = {}) {
  if (!visit) return visit;
  return {
    ...visit,
    mailedAt: meta.at || new Date().toISOString(),
    mailedBy: meta.by || "",
    mailPath: meta.mailPath || "print-for-mail",
  };
}
function invoiceMailStamp(visit) {
  if (!visit || !visit.mailedAt) return "";
  const path = visit.mailPath === "mail" ? "Mailed" : "Printed for mail";
  const by = visit.mailedBy ? ` by ${visit.mailedBy}` : "";
  return `${path}${by}`;
}
function printRentalAgreement(unit, current, client, terms) {
  const w = window.open("", "_blank"); if (!w) return false;
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const t = currentTermsText(terms, "rental", current && current.termIds, current && current.termsNote);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Rental — ${esc((unit && unit.tag) || "")}</title>
<style>
  body{font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;margin:40px;}
  h1{font-size:22px;margin:0;color:#0B4F8A;} .muted{color:#5a6472;}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0B4F8A;padding-bottom:12px;margin-bottom:18px;}
  ${TERMS_PRINT_CSS}
  @media print{body{margin:0;}}
</style></head><body onload="window.print()">
  <div class="head"><div><h1>Pool Master Canada</h1><div class="muted">Cleaner rental agreement</div></div>
  <div class="muted">${esc((current && current.out) || "")}</div></div>
  <p><strong>${esc((client && client.name) || (current && current.customerName) || "")}</strong></p>
  <p>Unit ${esc((unit && unit.tag) || "")}${unit && (unit.make || unit.model) ? ` — ${esc([unit.make, unit.model].filter(Boolean).join(" "))}` : ""}</p>
  ${current && current.dueDate ? `<p class="muted">Due back ${esc(current.dueDate)}${current.rate ? ` · rate $${esc(current.rate)}` : ""}</p>` : ""}
  ${termsPrintHtml(t)}
</body></html>`);
  w.document.close();
  return true;
}

/* ---------- terms & conditions picker, used by visits and quotes ---------- */
function TermsPicker({ terms = [], workType = "any", termIds = [], note = "", onChange }) {
  const relevant = (terms || []).filter((t) => t.active !== false && (t.tag === "any" || t.tag === workType));
  const others = (terms || []).filter((t) => t.active !== false && !(t.tag === "any" || t.tag === workType));
  const toggle = (id) => {
    const on = termIds.includes(id);
    onChange({ termIds: on ? termIds.filter((x) => x !== id) : [...termIds, id], note });
  };
  const row = (t) => (
    <label key={t.id} className="perm" data-on={termIds.includes(t.id) ? 1 : 0} style={{ display: "block", marginBottom: 6 }}>
      <input type="checkbox" checked={termIds.includes(t.id)} onChange={() => toggle(t.id)}
        style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
      <b>{t.name}</b> <span className="sub" style={{ marginLeft: 6 }}>{(TERM_TAGS.find(([k]) => k === t.tag) || [, "Any work"])[1]}</span>
      <div className="sub" style={{ marginTop: 2 }}>{t.body}</div>
    </label>
  );
  return (
    <div className="f-wide">
      <label>Terms &amp; conditions — tick the ones that apply to this work</label>
      {relevant.length === 0 && others.length === 0 && <div className="sub">No terms saved yet. Add them under Catalogue → Terms &amp; conditions.</div>}
      {relevant.map(row)}
      {others.length > 0 && <div className="sub" style={{ margin: "8px 0 4px" }}>Other terms</div>}
      {others.map(row)}
      <label style={{ marginTop: 8 }}>Extra terms just for this one (optional)</label>
      <textarea value={note} style={{ minHeight: 60 }} placeholder="Anything specific to this job or quote."
        onChange={(e) => onChange({ termIds, note: e.target.value })} />
    </div>
  );
}

/* ---------- gifted account (prompt #15) ---------- */
// Whole-account free-of-charge flag. Same owner|manager gate as Fleet.
// Staff may still log a one-off gifted VISIT; they cannot flip the account flag.
function canFlagGiftedAccount(role) {
  if (!role) return false;
  const id = typeof role === "string" ? role : (role.id || role.roleId);
  return id === "owner" || id === "manager";
}
// Service Schedule call-order / membership: same gate as Fleet + gifted account
// (owner | manager). Retail can still see the board; only isMgr may drag, auto-stage,
// Place/Move, To bin, Book onto a truck/day, or delete from a truck.
function canAdjustSchedule(role) {
  if (!role) return false;
  const id = typeof role === "string" ? role : (role.id || role.roleId);
  return id === "owner" || id === "manager";
}
const canAdjustCallOrder = canAdjustSchedule;
function applyGiftedAccountFlag(existing, next, role) {
  const keep = !!(existing && existing.giftedService);
  if (!canFlagGiftedAccount(role)) return { ...next, giftedService: keep };
  return { ...next, giftedService: !!next.giftedService };
}
function visitGiftedDefault(client) {
  return !!(client && client.giftedService);
}
function applyGiftedVisitPricing(visit, matTotal = 0, labTotal = 0) {
  if (!visit || !visit.gifted) return visit;
  const sub = (Number(matTotal) || 0) + (Number(labTotal) || 0);
  return {
    ...visit,
    subtotal: sub.toFixed(2),
    giftValue: (num(visit.amount) || sub).toFixed(2),
    amount: "0",
    taxRate: "0",
    tax: "0",
  };
}

/* ---------- visits ---------- */
function VisitSheet({ client, visits, saveVisits, onClose, can, isOwner, revCentres = [], initialSide, autoStart, seed, onBooked, parts = [], services = [], terms = [], onSendReport }) {
  const giftedAcct = visitGiftedDefault(client); // account we service free of charge (documented, not billed)
  const visitCentres = revCentres.filter((r) => r.active !== false && (isOwner || !r.ownerOnly));
  const chLabel = client.trade ? "Trade" : "Retail";
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(null);
  const [side, setSide] = useState(initialSide === "retail" || initialSide === "service" ? initialSide : "all");
  const shown = visits.filter((v) => (side === "all" ? true : visitSide(v) === side));

  const startNew = (from) => {
    const base = { id: cid(), date: new Date().toISOString().slice(0, 10), workOrder: "", invoiceNo: "", amount: "", by: "", notes: "",
      paid: true, side: side === "retail" ? "retail" : "service", repairType: "", gifted: giftedAcct,
      termIds: defaultTermIds(terms, side === "retail" ? "any" : "service"), termsNote: "" };
    const v = from ? { ...base, ...from, id: cid() } : base;
    setForm(v); setEditId(v.id);
  };
  const startEdit = (v) => { setForm({ ...v }); setEditId(v.id); };
  useEffect(() => { if (autoStart) startNew(seed); }, []);
  const setLines = ({ materials, labourLines }) => setForm((f) => ({ ...f, materials, labourLines }));

  const commit = () => {
    {
      const gaps = saleSerialGaps(form.materials || [], parts);
      if (gaps.length) { alert(`Serial number required before this visit can save:\n${gaps.map((g) => `• ${g.desc} — ${g.have} of ${g.need} recorded`).join("\n")}`); return; }
    }
    const next = { ...form };
    const mats = next.materials || [];
    const labs = next.labourLines || [];
    const matTotal = sumMaterials(mats);
    const labTotal = sumLabour(labs);
    // Roll itemized lines into the existing totals so margins and reporting keep working
    if (mats.length) next.matPrice = matTotal.toFixed(2);
    if (labs.length) next.labour = labTotal.toFixed(2);
    // Gifted service: documented but not billed. Record the value given, charge nothing.
    if (next.gifted) {
      Object.assign(next, applyGiftedVisitPricing(next, matTotal, labTotal));
    } else if (!num(next.amount) && (mats.length || labs.length)) {
      // Invoice total auto-fills as subtotal + tax (13% HST, or 5% GST for Status cardholders).
      const sub = matTotal + labTotal;
      const rate = hstRateFor(client);
      next.subtotal = sub.toFixed(2);
      next.taxRate = String(rate);
      next.tax = (sub * rate / 100).toFixed(2);
      next.amount = (sub * hstFactorFor(client)).toFixed(2);
      if (client && client.statusExempt) next.taxNote = "First Nations Status rebate applied";
    }
    // If this service came from an approved quote, mark that quote booked now that it's saved.
    const from = next.fromQuote;
    delete next.fromQuote;
    const rest = visits.filter((v) => v.id !== next.id);
    saveVisits(client.id, [...rest, next]);
    if (from && onBooked) onBooked(from);
    setEditId(null); setForm(null);
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet">
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Invoices and visits</strong>
            <div className="sub">{client.name} · {shown.length} of {visits.length} shown</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={() => startNew()}>Log a visit</button>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="pad">
          <div className="seg" style={{ marginBottom: 13 }}>
            {[["service", "Service"], ["retail", chLabel], ["all", "Show all"]].map(([v, l]) => (
              <button key={v} type="button" data-on={side === v ? 1 : 0} onClick={() => setSide(v)}>{l}</button>
            ))}
          </div>
          {editId && form && (
            <div className="visit latest" style={{ marginBottom: 14 }}>
              <div className="fields">
                <div><label>Date</label><input value={form.date} placeholder="YYYY-MM-DD" onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><label>Performed by</label><input value={form.by} onChange={(e) => setForm({ ...form, by: e.target.value })} /></div>
                <div><label>Work order #</label><input value={form.workOrder} placeholder="WO-4821" onChange={(e) => setForm({ ...form, workOrder: e.target.value })} /></div>
                <div><label>Invoice #</label><input value={form.invoiceNo} placeholder="INV-2291" onChange={(e) => setForm({ ...form, invoiceNo: e.target.value })} /></div>
                <div><label>Invoice total</label><input value={form.gifted ? "" : form.amount} disabled={!!form.gifted} placeholder={form.gifted ? "No charge — gifted" : "Auto: subtotal + 13% HST"} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                {!form.gifted && visitCentres.length > 0 && (
                  <div>
                    <label>Revenue centre</label>
                    <select value={form.revCentre || ""} onChange={(e) => setForm({ ...form, revCentre: e.target.value })}>
                      <option value="">&mdash; none &mdash;</option>
                      {visitCentres.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="f-wide" style={{ background: form.gifted ? "#FFF7E8" : undefined, border: form.gifted ? "1px solid #F0D89A" : undefined, borderRadius: 8, padding: form.gifted ? "8px 10px" : 0 }}>
                  <label style={{ marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={!!form.gifted} onChange={(e) => setForm({ ...form, gifted: e.target.checked })} style={{ width: "auto", margin: 0 }} />
                    Gifted service (free of charge) — documented and tracked, not billed
                  </label>
                </div>
                <div>
                  <label>Side of the business</label>
                  <select value={visitSide(form)} disabled={!!form.repairType}
                    onChange={(e) => setForm({ ...form, side: e.target.value })}>
                    <option value="service">Service</option>
                    <option value="retail">Retail</option>
                  </select>
                </div>
                <div>
                  <label>In-house repair</label>
                  <select value={form.repairType || ""} onChange={(e) => setForm({ ...form, repairType: e.target.value })}>
                    <option value="">Not a repair</option>
                    {REPAIR_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <WorkLines materials={form.materials} labourLines={form.labourLines} parts={parts} services={services} onChange={setLines} />
                <TermsPicker terms={terms} workType={form.repairType ? "repair" : "service"}
                  termIds={form.termIds || []} note={form.termsNote || ""}
                  onChange={({ termIds, note }) => setForm((f) => ({ ...f, termIds, termsNote: note }))} />
                {can("margins") && <><div><label>Materials cost to us</label><input value={form.matCost || ""} placeholder="38.00" onChange={(e) => setForm({ ...form, matCost: e.target.value })} /></div>
                <div className="f-wide">
                  <label>
                    <input type="checkbox" checked={!!form.bookedEarly} onChange={(e) => setForm({ ...form, bookedEarly: e.target.checked })}
                      style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                    Booked well ahead of time
                  </label>
                </div></>}
                <div className="f-wide">
                  <label>
                    <input type="checkbox" checked={!!form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.checked })}
                      style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                    Delivery — reports as delivery sales
                  </label>
                </div>
                <div>
                  <label>Invoice status</label>
                  <select value={form.paid === false ? "unpaid" : "paid"} onChange={(e) => setForm({ ...form, paid: e.target.value === "paid" })}>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Outstanding</option>
                  </select>
                </div>
                <div className="f-wide">
                  <label>Visit notes</label>
                  <textarea value={form.notes} style={{ minHeight: 90 }}
                    placeholder="What you did, what you found, what needs watching next visit."
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <div className="btnrow" style={{ marginTop: 10 }}>
                <button className="btn" onClick={commit}>Save visit</button>
                <button className="btn ghost" onClick={() => { setEditId(null); setForm(null); }}>Cancel</button>
              </div>
            </div>
          )}

          {can("margins") && visits.length > 0 && (
            <div className="counts" style={{ marginBottom: 13 }}>
              {[["service", "Service"], ["retail", chLabel]].map(([k, l]) => {
                const tot = visits.filter((v) => (k === "service" ? visitSide(v) === "service" : visitSide(v) === "retail"))
                  .reduce((a, v) => a + num(v.amount), 0);
                return (
                  <div key={k} className="count-cell" data-zero={tot ? 0 : 1}>
                    <div className="n">${tot.toFixed(0)}</div>
                    <div className="l">{l} invoiced</div>
                  </div>
                );
              })}
              <div className="count-cell">
                <div className="n">${visits.reduce((a, v) => a + num(v.amount), 0).toFixed(0)}</div>
                <div className="l">Gross</div>
              </div>
            </div>
          )}
          {shown.length === 0 && !editId ? (
            <div className="empty">
              {side === "retail" ? "No retail invoices on this account yet."
                : side === "service" ? "No service visits or repairs logged yet."
                : "Nothing logged yet. Log one and the date will show on the client's page."}
            </div>
          ) : shown.map((v, i) => (
            <div key={v.id} className={"visit" + (i === 0 ? " latest" : "")}>
              <div className="vhead">
                <span className="vdate">{v.date}</span>
                <div className="vdocs">
                  {v.workOrder && <span className="doc">{v.workOrder}</span>}
                  {v.invoiceNo && <span className="doc inv">{v.invoiceNo}{v.amount ? " · $" + v.amount : ""}</span>}
                  <span className="doc" style={{ background: visitSide(v) === "retail" ? (client.trade ? "#B31212" : "#086EB6") : "#17356E" }}>
                    {visitSide(v) === "retail" ? chLabel : v.repairType ? "Repair" : "Service"}
                  </span>
                  {v.repairType && <span className="doc" style={{ background: "#B31212" }}>{v.repairType}</span>}
                  {visitSide(v) === "service" && !v.repairType && onSendReport && (
                    <button type="button" className="btn ghost mini" style={{ padding: "1px 8px" }}
                      title="Email this visit's service report to the customer"
                      onClick={() => onSendReport(v)}>Email report</button>
                  )}
                  <button type="button" className="btn ghost mini" style={{ padding: "1px 8px" }}
                    title="Print this invoice with the current terms & conditions"
                    onClick={() => printVisitInvoice(v, client, terms)}>Print invoice</button>
                  <button type="button" className="btn ghost mini" style={{ padding: "1px 8px" }}
                    title="Print this invoice for mailing — stamps a mail path on the invoice. No postage API."
                    onClick={() => {
                      printVisitInvoice(v, client, terms);
                      saveVisits(client.id, visits.map((x) => (x.id === v.id ? markInvoiceMailed(x, { by: "" }) : x)));
                    }}>Print for mail</button>
                  {v.mailedAt && <span className="doc" style={{ background: "#5A6E86" }} title={v.mailedAt}>{invoiceMailStamp(v)}</span>}
                  {v.paid === false && <span className="doc owe">Outstanding</span>}
                </div>
              </div>
              {v.by && <div className="sub">Performed by {v.by}{v.minutes ? ` · on site ${v.minutes} min` : ""}</div>}
              {v.signature && <img src={v.signature} alt="customer signature" style={{ height: 36, border: "1px solid var(--line)", borderRadius: 6, background: "#fff", marginTop: 4 }} />}
              {(v.refundLog || []).map((r) => (
                <div key={r.id} className="sub" style={{ color: "#B31212", fontWeight: 600 }}>
                  Refund ${num(r.total).toFixed(2)} processed by {r.by || "?"} on {r.date}
                </div>
              ))}
              {((v.materials && v.materials.length) || (v.labourLines && v.labourLines.length)) ? (
                <div className="vnotes" style={{ fontSize: 12.5 }}>
                  {v.materials && v.materials.length > 0 && (
                    <div><b>Materials:</b> {v.materials.map((m) => `${m.code || "—"}${m.desc ? " " + m.desc : ""}${num(m.qty) > 1 ? " ×" + m.qty : ""}`).join("; ")}</div>
                  )}
                  {v.labourLines && v.labourLines.length > 0 && (
                    <div><b>Labour:</b> {v.labourLines.map((l) => `${l.desc || "Labour"}${num(l.rate) ? " $" + l.rate : ""}`).join("; ")}</div>
                  )}
                </div>
              ) : null}
              {can("margins") && (num(v.matPrice) > 0 || num(v.labour) > 0) && (
                <div className="readings">
                  {num(v.matPrice) > 0 && (
                    <span className="reading" data-s={num(v.matPrice) - num(v.matCost) > 0 ? "ok" : "high"}>
                      <span className="rl">Materials margin</span>
                      <span className="rv">${(num(v.matPrice) - num(v.matCost)).toFixed(2)}</span>
                    </span>
                  )}
                  {num(v.labour) > 0 && (
                    <span className="reading"><span className="rl">Labour</span><span className="rv">${num(v.labour).toFixed(2)}</span></span>
                  )}
                  {v.bookedEarly && <span className="reading" data-s="ok"><span className="rl">Booked early</span><span className="rv">✓</span></span>}
                </div>
              )}
              <div className="vnotes">{v.notes || "No notes recorded."}</div>
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn ghost mini" onClick={() => startEdit(v)}>Edit</button>
                <button className="btn warn mini" onClick={() => saveVisits(client.id, visits.filter((x) => x.id !== v.id))}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- quotes ---------- */
// accepted = customer approved, waiting for the manager to book · booked = turned into a service
const QUOTE_STATUS = [["draft", "Draft"], ["sent", "Sent"], ["accepted", "Accepted"], ["declined", "Declined"], ["booked", "Booked"]];

function quoteTotal(q) { return sumMaterials(q.materials) + sumLabour(q.labourLines); }

// 30-day validity matches the quote email and the "Quote validity" term. Expired is a
// DISPLAY status only — we never store a sixth status, so accepted/booked/declined quotes
// stay as they are even after they age. Staff still see them on the customer.
const QUOTE_VALID_DAYS = 30;
function quoteListAmount(q) {
  if (!q) return 0;
  if (q.total != null && q.total !== "") return num(q.total);
  if (Array.isArray(q.items) && q.items.length) return q.items.reduce((a, it) => a + num(it.amount), 0);
  return quoteTotal(q);
}
function quoteSender(q) {
  if (!q) return "";
  return q.sentBy || q.by || "";
}
function quoteDisplayStatus(q, today) {
  const st = String((q && q.status) || "draft").toLowerCase();
  const mapped = st === "approved" ? "accepted" : st;
  if (mapped === "sent") {
    const d = parseDate(String((q && (q.date || q.sentAt)) || "").slice(0, 10));
    const t = parseDate(today);
    if (d && t && Math.round((t - d) / 86400000) > QUOTE_VALID_DAYS) return "expired";
  }
  return mapped;
}
function quoteStatusLabel(st) {
  const k = String(st || "draft").toLowerCase();
  if (k === "expired") return "Expired";
  if (k === "approved") return "Accepted";
  return (QUOTE_STATUS.find(([x]) => x === k) || [, k ? k.charAt(0).toUpperCase() + k.slice(1) : "Draft"])[1];
}
function quoteStatusColor(st) {
  const k = String(st || "").toLowerCase();
  if (k === "accepted" || k === "approved" || k === "booked") return "#0B7A3B";
  if (k === "declined") return "#B31212";
  if (k === "expired") return "#B36B00";
  if (k === "sent") return "#0B4F8A";
  return "#5A6472";
}
// One list of every quote already on file. Customer-builder quotes (QUOTE_KEY) plus
// in-house repair estimates (t.quote). Site-visit "quote" jobs open the builder —
// they are not a second store, so they do not get a second row here.
function quoteListRows({ quotesMap = {}, repairs = [], clients = [], clientId = null, today = "" }) {
  const nameOf = (id) => ((clients || []).find((c) => c.id === id) || {}).name || "";
  const rows = [];
  const pushClient = (cid, list) => {
    (list || []).forEach((q) => {
      if (!q) return;
      rows.push({
        kind: "quote", id: q.id, clientId: cid, clientName: nameOf(cid),
        date: q.date || String(q.sentAt || "").slice(0, 10),
        amount: quoteListAmount(q), sentBy: quoteSender(q),
        status: quoteDisplayStatus(q, today), rawStatus: q.status || "draft",
        forWhom: q.forWhom || "", quoteId: q.id,
      });
    });
  };
  if (clientId) pushClient(clientId, quotesMap[clientId] || []);
  else Object.keys(quotesMap || {}).forEach((cid) => pushClient(cid, quotesMap[cid]));
  (repairs || []).forEach((t) => {
    if (!t || !t.quote) return;
    if (clientId && t.customerId !== clientId) return;
    const q = t.quote;
    const lastQ = (t.comms || []).filter((c) => c && c.type === "QUOTE").slice(-1)[0];
    rows.push({
      kind: "repair", id: "repair:" + t.id, clientId: t.customerId || "",
      clientName: t.customerName || nameOf(t.customerId),
      date: String(q.sentAt || q.decidedAt || t.at || t.date || "").slice(0, 10),
      amount: quoteListAmount(q),
      sentBy: quoteSender(q) || (lastQ && lastQ.by) || "",
      status: quoteDisplayStatus(q, today), rawStatus: q.status || "Draft",
      forWhom: t.ticketNo ? `Repair ${t.ticketNo}` : "Repair estimate",
      ticketId: t.id, ticketNo: t.ticketNo || "",
    });
  });
  rows.sort((a, b) => (b.date || "").localeCompare(a.date || "") || String(b.id).localeCompare(String(a.id)));
  return rows;
}

// Short, friendly email body — the itemised detail rides along as the PDF the manager attaches.
function quoteEmailText(q, client) {
  const first = (((client.contacts || [])[0] || {}).name || "").trim().split(/\s+/)[0];
  return `Hi ${first || "there"},\n\nThank you for considering Pool Master Canada. Your quote is attached as a PDF.\n\nEstimated total: $${quoteTotal(q).toFixed(2)}\nThis quote is valid for 30 days.\n\nIf you'd like to go ahead, just reply to this email and we'll get you booked in.\n\nThanks,\nPool Master Canada`;
}

// Printable quote → the browser's print dialog offers "Save as PDF" on a Mac.
function quoteHtml(q, client, terms) {
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const money = (n) => "$" + num(n).toFixed(2);
  const mats = (q.materials || []).filter((m) => m.desc || m.code || m.price);
  const labs = (q.labourLines || []).filter((l) => l.desc || l.rate);
  const rows = [];
  mats.forEach((m) => { const qty = num(m.qty) || 1; rows.push(`<tr><td>${esc([m.code, m.desc].filter(Boolean).join(" — "))}</td><td class="r">${qty}</td><td class="r">${money(m.price)}</td><td class="r">${money(qty * num(m.price))}</td></tr>`); });
  labs.forEach((l) => rows.push(`<tr><td>${esc(l.desc || "Labour")}</td><td class="r">—</td><td class="r">—</td><td class="r">${money(l.rate)}</td></tr>`));
  const t = currentTermsText(terms, q.workType || "quote", q.termIds, q.termsNote);
  return `<!doctype html><html><head><meta charset="utf-8"><title>Quote — ${esc(client.name)}</title>
<style>
  body{font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;margin:40px;}
  h1{font-size:22px;margin:0;color:#0B4F8A;} .muted{color:#5a6472;}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0B4F8A;padding-bottom:12px;margin-bottom:18px;}
  table{width:100%;border-collapse:collapse;margin:14px 0;} th,td{text-align:left;padding:8px 6px;border-bottom:1px solid #e2e8f0;} .r{text-align:right;}
  tfoot td{font-weight:700;border-top:2px solid #12233b;border-bottom:none;}
  ${TERMS_PRINT_CSS}
  @media print{body{margin:0;}}
</style></head><body onload="window.print()">
  <div class="head"><div><h1>Pool Master Canada</h1><div class="muted">Quote</div></div><div class="muted">${esc(q.date || "")}</div></div>
  <div><strong>${esc(client.name)}</strong>${client.address ? `<div class="muted">${esc(client.address)}</div>` : ""}</div>
  ${q.forWhom ? `<div class="muted" style="margin-top:6px">For: ${esc(q.forWhom)}</div>` : ""}
  <table><thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Each</th><th class="r">Amount</th></tr></thead>
  <tbody>${rows.join("") || `<tr><td colspan="4" class="muted">No line items.</td></tr>`}</tbody>
  <tfoot><tr><td colspan="3" class="r">Estimated total</td><td class="r">${money(quoteTotal(q))}</td></tr></tfoot></table>
  ${termsPrintHtml(t)}
</body></html>`;
}
function printQuote(q, client, terms) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(quoteHtml(q, client, terms));
  w.document.close();
  return true;
}

function QuoteSheet({ client, quotes, saveQuotes, onClose, parts = [], services = [], terms = [], autoStart, focusId, meName = "", onEmail, onBook }) {
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(null);

  const startNew = () => {
    const q = { id: cid(), date: new Date().toISOString().slice(0, 10), workType: "quote", forWhom: "",
      materials: [], labourLines: [], termIds: [], termsNote: "", notes: "", status: "draft", by: meName || "" };
    setForm(q); setEditId(q.id);
  };
  const startEdit = (q) => { setForm({ ...q }); setEditId(q.id); };
  useEffect(() => {
    if (focusId) {
      const q = (quotes || []).find((x) => x.id === focusId);
      if (q) startEdit(q);
    } else if (autoStart) startNew();
  }, []);
  const setLines = ({ materials, labourLines }) => setForm((f) => ({ ...f, materials, labourLines }));

  const commit = () => {
    const next = { ...form, total: quoteTotal(form).toFixed(2) };
    const rest = quotes.filter((q) => q.id !== next.id);
    saveQuotes(client.id, [...rest, next]);
    setEditId(null); setForm(null);
    return next;
  };
  const email = (q) => {
    const to = ((client.contacts || [])[0] || {}).email || "";
    const stamped = { ...q,
      status: (q.status === "draft" || !q.status) ? "sent" : q.status,
      sentBy: q.sentBy || meName || "",
      sentAt: q.sentAt || new Date().toISOString() };
    if (stamped.status !== q.status || stamped.sentBy !== (q.sentBy || "") || stamped.sentAt !== (q.sentAt || "")) {
      saveQuotes(client.id, [...quotes.filter((x) => x.id !== stamped.id), stamped]);
    }
    onEmail({ kind: "mail", client, to, subject: `${client.name} — quote from Pool Master`, body: quoteEmailText(stamped, client) });
  };
  const today = fmtDate(new Date());
  const pdf = (q) => printQuote(q, client, terms);
  // Turn an accepted quote into a booked service — copies parts, labour and terms across.
  // The quote is only marked "booked" once the service is actually saved, so it stays in the
  // approved queue if you back out of the booking form.
  const book = (q) => {
    onBook && onBook({
      side: "service",
      fromQuote: { clientId: client.id, quoteId: q.id },
      materials: (q.materials || []).map((m) => ({ ...m, id: cid() })),
      labourLines: (q.labourLines || []).map((l) => ({ ...l, id: cid() })),
      termIds: q.termIds || [], termsNote: q.termsNote || "",
      notes: q.forWhom ? `From accepted quote: ${q.forWhom}` : "From accepted quote",
    });
  };

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet">
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Quotes</strong>
            <div className="sub">{client.name} · {quotes.length} saved</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={startNew}>New quote</button>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="pad">
          {editId && form && (
            <div className="visit latest" style={{ marginBottom: 14 }}>
              <div className="fields">
                <div><label>Date</label><input value={form.date} placeholder="YYYY-MM-DD" onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div>
                  <label>Type of work</label>
                  <select value={form.workType || "quote"} onChange={(e) => setForm({ ...form, workType: e.target.value })}>
                    {WORK_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label>Status</label>
                  <select value={form.status || "draft"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {QUOTE_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div className="f-wide"><label>For (optional)</label><input value={form.forWhom || ""} placeholder="e.g. Heater replacement" onChange={(e) => setForm({ ...form, forWhom: e.target.value })} /></div>
                <WorkLines materials={form.materials} labourLines={form.labourLines} parts={parts} services={services} onChange={setLines} listId="pm-quote-partcodes" />
                <TermsPicker terms={terms} workType={form.workType || "quote"}
                  termIds={form.termIds || []} note={form.termsNote || ""}
                  onChange={({ termIds, note }) => setForm((f) => ({ ...f, termIds, termsNote: note }))} />
                <div className="f-wide">
                  <label>Notes (not shown on the quote)</label>
                  <textarea value={form.notes} style={{ minHeight: 70 }} placeholder="Internal notes about this quote."
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <div className="btnrow" style={{ marginTop: 10, alignItems: "center" }}>
                <button className="btn" onClick={commit}>Save quote</button>
                <button className="btn ghost" onClick={() => { const q = commit(); pdf(q); }}>Save &amp; PDF</button>
                <button className="btn ghost" onClick={() => { const q = commit(); email(q); }}>Save &amp; email</button>
                <button className="btn ghost" onClick={() => { setEditId(null); setForm(null); }}>Cancel</button>
                <span className="sub" style={{ marginLeft: "auto", fontWeight: 700 }}>Total ${quoteTotal(form).toFixed(2)}</span>
              </div>
            </div>
          )}

          {quotes.length === 0 && !editId ? (
            <div className="empty">No quotes yet. Start one, add parts and labour, pick the terms, then save or email it.</div>
          ) : quotes.map((q, i) => (
            <div key={q.id} className={"visit" + (i === 0 ? " latest" : "")}>
              <div className="vhead">
                <span className="vdate">{q.date}</span>
                <div className="vdocs">
                  {q.forWhom && <span className="doc">{q.forWhom}</span>}
                  <span className="doc" style={{ background: "#17356E" }}>{(WORK_TYPES.find(([k]) => k === q.workType) || [, "Quote"])[1]}</span>
                  <span className="doc inv">${num(q.total || quoteTotal(q)).toFixed(2)}</span>
                  {quoteSender(q) ? <span className="doc">{quoteSender(q)}</span> : null}
                  <span className="doc" style={{ background: quoteStatusColor(quoteDisplayStatus(q, today)) }}>
                    {quoteStatusLabel(quoteDisplayStatus(q, today))}
                  </span>
                </div>
              </div>
              {((q.materials && q.materials.length) || (q.labourLines && q.labourLines.length)) ? (
                <div className="vnotes" style={{ fontSize: 12.5 }}>
                  {q.materials && q.materials.length > 0 && <div><b>Materials:</b> {q.materials.map((m) => `${m.code || "—"}${m.desc ? " " + m.desc : ""}${num(m.qty) > 1 ? " ×" + m.qty : ""}`).join("; ")}</div>}
                  {q.labourLines && q.labourLines.length > 0 && <div><b>Labour:</b> {q.labourLines.map((l) => `${l.desc || "Labour"}${num(l.rate) ? " $" + l.rate : ""}`).join("; ")}</div>}
                </div>
              ) : null}
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn ghost mini" onClick={() => startEdit(q)}>Edit</button>
                <button className="btn ghost mini" onClick={() => pdf(q)}>Save PDF</button>
                <button className="btn ghost mini" onClick={() => email(q)}>Email</button>
                {onBook && <button className="btn mini" title="Copy this quote into a new booked service" onClick={() => book(q)}>Book this</button>}
                <button className="btn warn mini" onClick={() => saveQuotes(client.id, quotes.filter((x) => x.id !== q.id))}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Shop-wide list of every quote already on file (customer builder + repair estimates).
function QuotesListSheet({ rows, onOpen, onClose, title = "Quotes" }) {
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet">
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>{title}</strong>
            <div className="sub">{rows.length} on file · customer quotes and repair estimates</div>
          </div>
          <div className="btnrow"><button className="btn" onClick={onClose}>Close</button></div>
        </div>
        <div className="pad">
          {rows.length === 0 ? (
            <div className="empty">No quotes on file yet. Build one from a customer, or add an estimate on a repair ticket.</div>
          ) : rows.map((r) => (
            <div key={r.id} className="visit">
              <div className="vhead">
                <span className="vdate">{r.date || "—"}</span>
                <div className="vdocs">
                  {r.clientName && <span className="doc" style={{ background: "#17356E" }}>{r.clientName}</span>}
                  {r.forWhom && <span className="doc">{r.forWhom}</span>}
                  <span className="doc inv">${num(r.amount).toFixed(2)}</span>
                  <span className="doc" style={{ background: quoteStatusColor(r.status) }}>{quoteStatusLabel(r.status)}</span>
                </div>
              </div>
              <div className="sub" style={{ marginTop: 4 }}>
                {r.sentBy ? `Sent by ${r.sentBy}` : "Not sent yet"}
                {r.kind === "repair" ? " · in-house repair estimate" : " · customer quote"}
              </div>
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn mini" onClick={() => onOpen(r)}>Open quote</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Manager's queue: every customer's accepted-but-not-yet-booked quote in one place.
function ApprovedQuotesSheet({ clients, quotesMap, onBookQuote, onOpen, onClose }) {
  const rows = [];
  (clients || []).forEach((c) => (quotesMap[c.id] || []).forEach((q) => { if (q.status === "accepted") rows.push({ c, q }); }));
  rows.sort((a, b) => (a.q.date || "").localeCompare(b.q.date || ""));
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet">
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Approved quotes</strong>
            <div className="sub">{rows.length} waiting to be booked</div>
          </div>
          <div className="btnrow"><button className="btn" onClick={onClose}>Close</button></div>
        </div>
        <div className="pad">
          {rows.length === 0 ? (
            <div className="empty">Nothing waiting. When a quote is marked <b>Accepted</b>, it lands here for booking.</div>
          ) : rows.map(({ c, q }) => (
            <div key={q.id} className="visit">
              <div className="vhead">
                <span className="vdate">{q.date}</span>
                <div className="vdocs">
                  <span className="doc" style={{ background: "#17356E" }}>{c.name}</span>
                  {q.forWhom && <span className="doc">{q.forWhom}</span>}
                  <span className="doc inv">${num(q.total || quoteTotal(q)).toFixed(2)}</span>
                </div>
              </div>
              {((q.materials && q.materials.length) || (q.labourLines && q.labourLines.length)) ? (
                <div className="vnotes" style={{ fontSize: 12.5 }}>
                  {q.materials && q.materials.length > 0 && <div><b>Materials:</b> {q.materials.map((m) => `${m.code || "—"}${m.desc ? " " + m.desc : ""}${num(m.qty) > 1 ? " ×" + m.qty : ""}`).join("; ")}</div>}
                  {q.labourLines && q.labourLines.length > 0 && <div><b>Labour:</b> {q.labourLines.map((l) => `${l.desc || "Labour"}${num(l.rate) ? " $" + l.rate : ""}`).join("; ")}</div>}
                </div>
              ) : null}
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn mini" title="Copy this quote into a booked service" onClick={() => onBookQuote(c.id, q)}>Book this</button>
                <button className="btn ghost mini" onClick={() => onOpen(c.id)}>Open customer</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Routing({ contacts }) {
  return (
    <div style={{ marginBottom: 12, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
      {COMMS.map(({ k, label }, i) => {
        const who = contacts.filter((c) => c.comms && c.comms[k] && c.comms[k] !== "none");
        return (
          <div key={k} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "9px 12px", borderTop: i ? "1px solid var(--line)" : 0 }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--muted)", fontWeight: 800, minWidth: 118 }}>{label}</span>
            {who.length === 0
              ? <span style={{ fontSize: 12.5, color: "var(--clay)", fontWeight: 700 }}>Nobody set</span>
              : who.map((c) => (
                <span key={c.id} className="chip" data-ch={c.comms[k]}>
                  {c.name || "Unnamed"} · {CHANNEL_LABEL[c.comms[k]]}
                </span>
              ))}
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "9px 12px", borderTop: "1px solid var(--line)" }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--muted)", fontWeight: 800, minWidth: 118 }}>Promotions</span>
        {contacts.filter((c) => String(c.email || "").trim()).length === 0
          ? <span style={{ fontSize: 12.5, color: "var(--clay)", fontWeight: 700 }}>No email on file</span>
          : contacts.filter((c) => String(c.email || "").trim()).map((c) => (
            <span key={c.id} className="chip" data-ch={c.promos === "no" ? "none" : "email"}>
              {c.name || "Unnamed"} · {c.promos === "no" ? "Opted out" : c.promos === "yes" ? "Yes" : "Auto"}
            </span>
          ))}
      </div>
    </div>
  );
}

// A retained communication log for one category (service notices / general) on a customer.
function CommLogSheet({ title, cat, client, onLog, onDelete, onClose }) {
  const [note, setNote] = useState("");
  const entries = (client.commLog || []).filter((e) => e.cat === cat)
    .sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
  const usDate = (d) => { const p = (d || "").split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : d; };
  const add = () => { const t = note.trim(); if (!t) return; onLog(cat, t); setNote(""); };
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 560 }}>
        <div className="bar">
          <div><strong style={{ fontSize: 16 }}>{title}</strong><div className="sub">{client.name} · {entries.length} logged</div></div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          <div className="commlog-add">
            <textarea rows={2} placeholder="What was sent / said? (a note)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn" onClick={add} disabled={!note.trim()}>Add to log</button>
          </div>
          {entries.length === 0
            ? <div className="empty" style={{ marginTop: 14 }}>Nothing logged yet. Add a note above and it's kept here with the date and time.</div>
            : <div className="commlog-list">
                {entries.map((e) => (
                  <div key={e.id} className="commlog-entry">
                    <div className="cle-when mono">{usDate(e.date)}<br />{e.time}</div>
                    <div className="cle-note">{e.note}{e.by ? <span className="sub" style={{ marginTop: 0 }}> — {e.by}</span> : null}</div>
                    <button className="btn warn mini" onClick={() => onDelete(e.id)} title="Delete entry">×</button>
                  </div>
                ))}
              </div>}
        </div>
      </div>
    </div>
  );
}

// Click-to-dial for a service-issue call when a number is on file (tel:);
// otherwise (or after the call) staff log date/time, who, number, outcome.
// Same outcome language as the repair pickup-call log; the note lands on
// the customer's Service communication log.
const CALL_HOW_LABEL = { spoken: "Spoke to the customer", voicemail: "Left a voicemail", noanswer: "Called — no answer" };
function telHref(phone) {
  const d = String(phone || "").replace(/[^0-9+]/g, "");
  return d ? "tel:" + d : "";
}
function serviceIssueNote({ how, phone, note, jobLabel }) {
  const outcome = CALL_HOW_LABEL[how] || how || "Called";
  const num = String(phone || "").trim() || "no number on file";
  const head = jobLabel ? `Service issue call · ${jobLabel}` : "Service issue call";
  return [head, num, outcome].join(" — ") + (note ? " — " + note : "");
}
function ServiceIssueCallSheet({ client, job = null, meName = "", onLog, onJobLog, onClose }) {
  const pref = servicePrefFor(client || {});
  const contacts = (client && client.contacts) || [];
  const k = (pref.contact && pref.contact.phone) ? pref.contact
    : (contacts.find((x) => x.phone) || pref.contact || contacts[0] || {});
  const [phone, setPhone] = useState(k.phone || "");
  const [note, setNote] = useState("");
  const href = telHref(phone);
  const jobLabel = job
    ? `${job.serviceType || JOB_LABEL[job.kind] || "service"}${job.date ? " on " + job.date : ""}${job.workOrder ? " · " + job.workOrder : ""}`
    : "";
  const commit = (how) => {
    if (!how) return;
    const text = serviceIssueNote({ how, phone: phone.trim(), note: note.trim(), jobLabel });
    onLog && onLog(text);
    onJobLog && onJobLog(how, phone.trim());
    onClose();
  };
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 520 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Service issue call</strong>
            <div className="sub">{(client && client.name) || "Customer"}{jobLabel ? " · " + jobLabel : ""}{k.name ? " · " + k.name : ""}</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          {href
            ? <a className="btn" style={{ textDecoration: "none" }} href={href}><CommIcon v="phone" /> Call {phone}</a>
            : <div className="sub">No phone on file — log the call below. Date, time and your name are recorded automatically.</div>}
          <div className="fields" style={{ marginTop: 12 }}>
            <div className="f-wide">
              <label>Number called</label>
              <input value={phone} placeholder="Phone number" onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <div className="sechead">Outcome</div>
          <div className="btnrow" style={{ marginTop: 6, flexWrap: "wrap" }}>
            <button type="button" className="btn mini" onClick={() => commit("spoken")}>Spoke to them ✓</button>
            <button type="button" className="btn ghost mini" onClick={() => commit("voicemail")}>Left voicemail</button>
            <button type="button" className="btn ghost mini" onClick={() => commit("noanswer")}>No answer</button>
          </div>
          <div style={{ marginTop: 10 }}>
            <label>Note (optional)</label>
            <textarea rows={2} placeholder="What the issue was / what was said" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="sub" style={{ marginTop: 8 }}>Goes on the Service communication log{meName ? ` as ${meName}` : ""} with today's date and time.</div>
        </div>
      </div>
    </div>
  );
}

function PhotoBoard({ clientId, photos, savePhotos, setStatus, onZoom }) {
  const fileRef = useRef(null);
  const [tag, setTag] = useState("house");
  const [showing, setShowing] = useState(null);
  const pendingTag = useRef("house");
  const pick = (v) => {
    pendingTag.current = v;
    setTag(v);
    if (fileRef.current) fileRef.current.click();
  };
  const [busy, setBusy] = useState(false);
  const list = photos || [];
  const shown = showing ? list.filter((p) => normalizeTag(p.tag) === showing) : list;

  const addFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const shots = [];
      for (const f of Array.from(files).slice(0, 8)) {
        if (!f.type.startsWith("image/")) continue;
        shots.push({ id: cid(), tag: pendingTag.current, caption: "", dataUrl: await shrink(f), added: new Date().toISOString().slice(0, 10) });
      }
      if (shots.length) await savePhotos(clientId, [...list, ...shots]);
    } catch { setStatus("That file couldn't be read as an image. Try a JPG or PNG."); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div>
      <div className="sechead">
        Reference photos
        <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>
          {showing ? `${shown.length} of ${list.length} · ${PHOTO_TAG_LABEL[showing]}` : list.length}
        </span>
      </div>
      <div className="phototags">
        {PHOTO_TAGS.map((t) => (
          <div key={t.v} className="phototag">
            <button type="button" className="opt" data-on={showing === t.v ? 1 : 0}
              onClick={() => setShowing((x) => (x === t.v ? null : t.v))}>
              <PhotoTagIcon v={t.v} />
              <span className="cap">{t.label}</span>
              <span className="tcount">{list.filter((p) => normalizeTag(p.tag) === t.v).length}</span>
            </button>
            {t.add && (
              <button type="button" className="showall photoadd" disabled={busy} onClick={() => pick(t.v)}>
                <span className="plus">+</span>{busy && tag === t.v ? "Adding…" : t.add}
              </button>
            )}
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple capture="environment"
        style={{ display: "none" }} onChange={(e) => addFiles(e.target.files)} />
      {shown.length > 0 && (
        <div className="photos">
          {shown.map((p) => (
            <div key={p.id} className="shot">
              <img src={p.signedUrl || p.dataUrl} alt={p.caption || p.tag} onClick={() => onZoom(p)} />
              <div className="cap">
                <div className="tag">
                  <span style={{ color: "var(--tile)" }}><PhotoTagIcon v={normalizeTag(p.tag)} size={15} /></span>
                  {PHOTO_TAG_LABEL[normalizeTag(p.tag)]}
                </div>
                <input value={p.caption} placeholder="Caption" onChange={(e) =>
                  savePhotos(clientId, list.map((x) => (x.id === p.id ? { ...x, caption: e.target.value } : x)))} />
                <div className="btnrow" style={{ marginTop: 6, justifyContent: "space-between" }}>
                  <span className="sub mono" style={{ marginTop: 0 }}>{p.added}</span>
                  <button className="btn warn mini" onClick={() => savePhotos(clientId, list.filter((x) => x.id !== p.id))}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StructureGlyph({ v, size = 46 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinejoin: "round", strokeLinecap: "round" };
  const jets = (x0) => [0, 1, 2].map((i) => <line key={i} {...P} x1={x0 + i * 6} y1={5} x2={x0 + i * 6} y2={2} />);
  return (
    <svg width={size} height={size * 0.66} viewBox="0 0 48 32" aria-hidden="true">
      {v === "pool" && <rect {...P} x="5" y="8" width="38" height="18" rx="5" />}
      {(v === "pool_spa" || v === "pool_spa_features") && <>
        <rect {...P} x="3" y="8" width="28" height="18" rx="5" />
        <circle {...P} cx="38" cy="19" r="7" />
        <path {...P} d="M31 15h2" />
      </>}
      {v === "pool_spa_features" && jets(9)}
      {(v === "spa" || v === "spa_features") && <circle {...P} cx="24" cy="18" r="10" />}
      {v === "spa_features" && jets(18)}
    </svg>
  );
}

function CoverGlyph({ v, size = 46 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size * 0.66} viewBox="0 0 48 32" aria-hidden="true">
      {v === "safety" && <>
        <rect {...P} x="5" y="8" width="38" height="18" rx="3" />
        <path {...P} d="M17 8v18M29 8v18" />
        <circle fill="currentColor" cx="5" cy="8" r="1.6" /><circle fill="currentColor" cx="43" cy="8" r="1.6" />
        <circle fill="currentColor" cx="5" cy="26" r="1.6" /><circle fill="currentColor" cx="43" cy="26" r="1.6" />
      </>}
      {v === "tarp" && <>
        <path {...P} d="M5 11c6-3 12 3 19 0s12-3 19 0v12c-6 3-12-3-19 0s-13 3-19 0z" />
        <rect fill="currentColor" x="3" y="7" width="5" height="4" rx="1" />
        <rect fill="currentColor" x="40" y="7" width="5" height="4" rx="1" />
        <rect fill="currentColor" x="3" y="23" width="5" height="4" rx="1" />
        <rect fill="currentColor" x="40" y="23" width="5" height="4" rx="1" />
      </>}
      {v === "snapin" && <>
        <rect {...P} x="5" y="8" width="38" height="18" rx="3" />
        <rect {...P} x="9" y="12" width="30" height="10" rx="2" strokeDasharray="3 3" />
        {[9, 19, 29, 39].map((x) => <circle key={x} fill="currentColor" cx={x} cy="8" r="1.5" />)}
        {[9, 19, 29, 39].map((x) => <circle key={"b" + x} fill="currentColor" cx={x} cy="26" r="1.5" />)}
      </>}
      {v === "leafnet" && <>
        <rect {...P} x="5" y="8" width="38" height="18" rx="3" />
        <path {...P} strokeWidth="1" d="M13 8l10 18M23 8l10 18M33 8l10 18M35 8L25 26M25 8L15 26M15 8L5 26" />
      </>}
      {v === "none" && <rect {...P} x="5" y="8" width="38" height="18" rx="5" strokeDasharray="4 4" opacity="0.6" />}
    </svg>
  );
}

function RecordIcon({ v, size = 26 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" aria-hidden="true" style={{ display: "block" }}>
      {v === "visit" && <>
        <rect {...P} x="4" y="6" width="20" height="18" rx="3" />
        <path {...P} d="M4 11h20M9 3v5M19 3v5" />
        <path {...P} d="M10 17l3 3 5-6" />
      </>}
      {v === "retail" && <>
        <path {...P} d="M6 9h16l-1.5 13a3 3 0 0 1-3 2.6H10.5a3 3 0 0 1-3-2.6z" />
        <path {...P} d="M11 12V8a3 3 0 0 1 6 0v4" />
      </>}
      {v === "test" && <>
        <path {...P} d="M11 3h6M12.5 3v7L7 21a3 3 0 0 0 2.7 4h8.6a3 3 0 0 0 2.7-4l-5.5-11V3" />
        <path {...P} d="M9.4 16h9.2" />
      </>}
    </svg>
  );
}

function PhotoTagIcon({ v, size = 26 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" aria-hidden="true" style={{ display: "block" }}>
      {v === "house" && <>
        <path {...P} d="M4 13.2 14 5l10 8.2" />
        <path {...P} d="M6.6 11.4V23h14.8V11.4" />
        <path {...P} d="M11.4 23v-6.4h5.2V23" />
      </>}
      {v === "pad" && <>
        <rect {...P} x="3" y="17.5" width="22" height="5.5" rx="1.6" />
        <circle {...P} cx="9" cy="12" r="4.2" />
        <path {...P} d="M9 7.8V5.4h4.6" />
        <path {...P} d="M17.6 8.2a3 1.6 0 0 1 6 0v7.2a3 1.6 0 0 1-6 0z" />
        <path {...P} d="M17.6 8.2a3 1.6 0 0 0 6 0" />
      </>}
      {v === "water" && <>
        <path {...P} d="M3 10.5c3.6-3 7.3 3 11 0s7.4 3 11 0" />
        <path {...P} d="M3 16c3.6-3 7.3 3 11 0s7.4 3 11 0" />
        <path {...P} d="M3 21.5c3.6-3 7.3 3 11 0s7.4 3 11 0" />
      </>}
    </svg>
  );
}

function TrendChart({ points, height = 130 }) {
  const W = 520, H = height, padL = 6, padR = 6, padT = 12, padB = 22;
  const n = points.length || 1;
  const bw = (W - padL - padR) / n;
  const max = Math.max(1, ...points.map((p) => Math.max(p.gross, p.profit)));
  const y = (v) => padT + (H - padT - padB) * (1 - v / max);
  const line = points.map((p, i) => `${padL + bw * (i + 0.5)},${y(p.profit)}`).join(" ");
  const anyProfit = points.some((p) => p.profit > 0);

  return (
    <div className="trend">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H }}>
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="var(--line)" strokeWidth="1" />
        {points.map((p, i) => {
          const h = Math.max(p.gross > 0 ? 2 : 0, (H - padT - padB) * (p.gross / max));
          return (
            <rect key={i} x={padL + bw * i + bw * 0.18} y={H - padB - h} width={bw * 0.64} height={h}
              rx="2" fill={p.current ? "var(--brand)" : "var(--sky)"}>
              <title>{p.label}: {money0(p.gross)} sales{p.profit ? " · " + money0(p.profit) + " profit" : ""}</title>
            </rect>
          );
        })}
        {anyProfit && <polyline points={line} fill="none" stroke="var(--clay)" strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="tlabels">
        {points.map((p, i) => (
          <span key={i} data-on={p.current ? 1 : 0}>
            {points.length > 12 && i % 2 && !p.current ? "" : p.short}
          </span>
        ))}
      </div>
      <div className="tkey">
        <span><i className="sw" style={{ background: "var(--sky)" }} />Gross sales</span>
        <span><i className="sw" style={{ background: "var(--clay)" }} />Gross profit</span>
        <span className="sub" style={{ marginTop: 0 }}>Peak {money0(max)}</span>
      </div>
    </div>
  );
}

function SalesIcon({ v, size = 20 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      {v === "service" && <>
        <path {...P} d="M3 9.5c3-2.6 6.2 2.6 9.3 0S18.9 6.9 21 9.5" />
        <path {...P} d="M3 15c3-2.6 6.2 2.6 9.3 0S18.9 12.4 21 15" />
        <path {...P} d="M3 20.5c3-2.6 6.2 2.6 9.3 0S18.9 17.9 21 20.5" />
      </>}
      {v === "retail" && <>
        <path {...P} d="M5 8h14l-1.2 11.4a2.4 2.4 0 0 1-2.4 2.1H8.6a2.4 2.4 0 0 1-2.4-2.1z" />
        <path {...P} d="M9 10.5V7a3 3 0 0 1 6 0v3.5" />
      </>}
      {v === "trade" && <>
        <path {...P} d="M3 7.8 12 3.5l9 4.3-9 4.3z" />
        <path {...P} d="M3 12.2 12 16.5l9-4.3" />
        <path {...P} d="M3 16.6 12 21l9-4.4" />
      </>}
      {v === "delivery" && <>
        <path {...P} d="M2.5 6.5h10.2v9.8H2.5z" />
        <path {...P} d="M12.7 9.6h4l3.3 3.2v3.5h-7.3z" />
        <circle {...P} cx="7" cy="18.6" r="1.9" />
        <circle {...P} cx="16.6" cy="18.6" r="1.9" />
      </>}
      {v === "repair" && <>
        <path {...P} d="M16.4 3.2a5.2 5.2 0 0 0-6.2 6.7l-6.3 6.3a2.2 2.2 0 0 0 3.2 3.2l6.3-6.3a5.2 5.2 0 0 0 6.7-6.2l-2.8 2.8-2.7-2.7z" />
      </>}
      {v === "gross" && <>
        <circle {...P} cx="12" cy="12" r="8.6" />
        <path {...P} d="M14.7 9.1c-.6-.9-1.6-1.4-2.7-1.4-1.6 0-2.7.8-2.7 2s1 1.7 2.7 2.1 2.9.9 2.9 2.2-1.2 2.1-2.9 2.1a3.4 3.4 0 0 1-2.9-1.5" />
        <path {...P} d="M12 6v12" />
      </>}
      {v === "profit" && <>
        <path {...P} d="M3.5 17.5 9.4 11l3.6 3.4 6.9-7.6" />
        <path {...P} d="M15.4 6.5h4.9v4.9" />
        <path {...P} d="M3.5 20.6h17" />
      </>}
    </svg>
  );
}

function TimeOffIcon({ size = 16 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <rect {...P} x="2.4" y="3.8" width="15.2" height="13.4" rx="2.2" />
      <path {...P} d="M2.4 7.8h15.2M6.4 2.4v2.8M13.6 2.4v2.8" />
      <path {...P} d="m7.2 12.6 1.9 2 3.7-4" />
    </svg>
  );
}

function PlusIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path d="M10 4v12M4 10h12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

const NAVP = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
function CustomersIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <circle {...NAVP} cx="7.2" cy="7" r="2.7" />
      <path {...NAVP} d="M2.5 16c.4-2.6 2.4-4.2 4.7-4.2s4.3 1.6 4.7 4.2" />
      <path {...NAVP} d="M13 5.2a2.5 2.5 0 0 1 .3 4.6M14.4 11.9c1.7.4 2.8 1.8 3.1 3.9" />
    </svg>
  );
}
function DashboardIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...NAVP} d="M3 3v13.5h14" />
      <path {...NAVP} d="M6.4 13.5v-2.7M9.8 13.5v-5.6M13.2 13.5v-4.2M16 13.5v-7" />
    </svg>
  );
}
function RetailIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...NAVP} d="M3 6.5 4 3h12l1 3.5M3 6.5h14M3 6.5v9.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6.5" />
      <path {...NAVP} d="M7 6.5v2.2a3 3 0 0 0 6 0V6.5" />
    </svg>
  );
}
function StaffIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <circle {...NAVP} cx="10" cy="6.5" r="2.9" />
      <path {...NAVP} d="M4.4 16.4c.5-2.9 2.8-4.6 5.6-4.6s5.1 1.7 5.6 4.6" />
    </svg>
  );
}

function FleetIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...NAVP} d="M2.5 12.5h10.5V8.2L10.2 5H4.5a2 2 0 0 0-2 2z" />
      <path {...NAVP} d="M13 12.5h3.2l1.3-3.2H13z" />
      <circle {...NAVP} cx="6" cy="14.6" r="1.5" />
      <circle {...NAVP} cx="14.4" cy="14.6" r="1.5" />
    </svg>
  );
}

function SpecialOrderIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...NAVP} d="M10 2.6 3 6v8l7 3.4 7-3.4V6z" />
      <path {...NAVP} d="M3 6l7 3.4L17 6M10 9.4v8" />
      <path {...NAVP} d="M6.5 4.3 13.5 7.7" />
    </svg>
  );
}

// Active special orders (not yet picked up) become an account flag: ordered, then ready for pickup.
function specialOrderFlags(clientId, specialOrders) {
  return (specialOrders || []).filter((o) => o.customerId === clientId && o.status !== "pickedup" && !o.archived).map((o) => {
    const item = `${o.item || "Part"}${o.qty && num(o.qty) > 1 ? " ×" + o.qty : ""}`;
    const source = o.status === "received" ? "Special order — ready for pickup" : o.status === "ordered" ? "Special order — on order" : "Special order — to be ordered";
    const message = o.status === "received" ? `${item} has arrived and is ready for pickup.`
      : o.status === "ordered" ? `${item} is on order${o.supplier ? " from " + o.supplier : ""}${o.orderedDate ? " (ordered " + o.orderedDate + ")" : ""}.`
      : `${item} still needs to be ordered${o.supplier ? " from " + o.supplier : ""}.`;
    return { key: "so-" + o.id, auto: true, scope: "both", source, message };
  });
}

function CalendarIcon({ size = 15 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <rect {...P} x="2.7" y="4.2" width="14.6" height="13" rx="1.8" />
      <path {...P} d="M2.7 8h14.6M6.6 2.7v3M13.4 2.7v3" />
      <path {...P} d="M6.4 11.3h2.2M11.4 11.3h2.2M6.4 14.2h2.2" />
    </svg>
  );
}

function QuoteIcon({ size = 15 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...P} d="M5 2.5h6.2L15.4 6.6v10.4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-13.5a1 1 0 0 1 1-1z" />
      <path {...P} d="M11 2.7V6.6h3.9" />
      <path {...P} d="M6.7 10.2h6.4M6.7 12.7h6.4M6.7 15.2h3.9" />
    </svg>
  );
}

function AskIcon({ size = 16 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...P} d="M3 5.4A1.9 1.9 0 0 1 4.9 3.5h10.2A1.9 1.9 0 0 1 17 5.4v6.3a1.9 1.9 0 0 1-1.9 1.9H8l-4 3.2v-3.2h-.1A1.9 1.9 0 0 1 3 11.7z" />
      <path {...P} d="M10 5.8v1.4M10 10.4v1.4M7.1 8.8h1.4M11.5 8.8h1.4M10 7.2 8.9 8.8 10 10.4l1.1-1.6z" strokeWidth="1.4" />
    </svg>
  );
}

function CommIcon({ v, size = 15 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      {v === "mail" && <>
        <rect {...P} x="2.2" y="4.5" width="15.6" height="11" rx="1.8" />
        <path {...P} d="m2.8 5.6 7.2 5 7.2-5" />
      </>}
      {v === "phone" && (
        <path {...P} d="M6.3 2.9 8 6.1 6.4 7.9c.7 1.9 2 3.2 3.8 3.9l1.8-1.6 3.2 1.7-.5 3c-.2.8-.9 1.3-1.7 1.2C7.6 15.4 4.1 11.9 3.2 5.1c-.1-.8.4-1.5 1.2-1.7z" />
      )}
      {v === "text" && <>
        <path {...P} d="M3 5.4A1.9 1.9 0 0 1 4.9 3.5h10.2A1.9 1.9 0 0 1 17 5.4v6.3a1.9 1.9 0 0 1-1.9 1.9H8l-4 3.2v-3.2h-.1A1.9 1.9 0 0 1 3 11.7z" />
        <path {...P} d="M6.6 7.2h6.8M6.6 10h4.4" />
      </>}
      {v === "sms" && <>
        <path {...P} d="M3 5.4A1.9 1.9 0 0 1 4.9 3.5h10.2A1.9 1.9 0 0 1 17 5.4v6.3a1.9 1.9 0 0 1-1.9 1.9H8l-4 3.2v-3.2h-.1A1.9 1.9 0 0 1 3 11.7z" />
        <circle cx="6.7" cy="8.6" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="10" cy="8.6" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="13.3" cy="8.6" r="0.9" fill="currentColor" stroke="none" />
      </>}
    </svg>
  );
}

function ListIcon({ size = 14 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" style={{ display: "block" }}>
      <path {...P} d="M6 4h8M6 8h8M6 12h8" />
      <circle fill="currentColor" cx="2.6" cy="4" r="1.2" />
      <circle fill="currentColor" cx="2.6" cy="8" r="1.2" />
      <circle fill="currentColor" cx="2.6" cy="12" r="1.2" />
    </svg>
  );
}

function MenuIcon({ size = 16 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" style={{ display: "block" }}>
      <path {...P} d="M2.5 4h11M2.5 8h11M2.5 12h11" />
    </svg>
  );
}

function FlagIcon({ size = 15, filled }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" style={{ display: "block" }}>
      <path d="M4 2v12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4.9 2.6h7.4l-1.7 3 1.7 3H4.9z" fill={filled ? "currentColor" : "none"}
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function EquipGlyph({ v, size = 30 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" style={{ display: "block" }}>
      {v === "pump" && <><circle {...P} cx="14" cy="18" r="7" /><path {...P} d="M14 11V7h7M21 18h5" /></>}
      {v === "booster" && <><circle {...P} cx="14" cy="18" r="5.5" /><path {...P} d="M14 12.5V8h6M19.5 18H25" /></>}
      {v === "filter" && <><path {...P} d="M10 10a6 3 0 0 1 12 0v13a6 3 0 0 1-12 0z" /><path {...P} d="M10 10a6 3 0 0 0 12 0M12 16h8M12 20h8" /></>}
      {v === "heater" && <><rect {...P} x="7" y="9" width="18" height="15" rx="3" /><path {...P} d="M12 14v5M16 13v7M20 15v4" /></>}
      {v === "chlorinator" && <><path {...P} d="M11 11h10v13a3 3 0 0 1-3 3h-4a3 3 0 0 1-3-3z" /><path {...P} d="M13 11V7h6v4" /><circle fill="currentColor" cx="16" cy="19" r="1.6" /></>}
      {v === "alt" && <><circle {...P} cx="16" cy="16" r="5" /><path {...P} d="M16 5v3M16 24v3M5 16h3M24 16h3M8.5 8.5l2 2M21.5 21.5l2 2M23.5 8.5l-2 2M10.5 21.5l-2 2" /></>}
      {v === "automation" && <><rect {...P} x="7" y="7" width="18" height="18" rx="3" /><path {...P} d="M11 12h10M11 16h10M11 20h6" /></>}
      {v === "cleaner" && <><path {...P} d="M8 22a8 8 0 0 1 16 0z" /><path {...P} d="M8 22h16M13 14l3-5 3 5" /></>}
      {v === "blower" && <><circle {...P} cx="16" cy="16" r="3" /><path {...P} d="M16 13c0-5 6-6 6-2s-3 3-6 3M16 19c0 5-6 6-6 2s3-3 6-3M13 16c-5 0-6-6-2-6s3 3 3 6M19 16c5 0 6 6 2 6s-3-3-3-6" /></>}
      {v === "light" && <><path {...P} d="M12 21a6 6 0 1 1 8 0v3h-8z" /><path {...P} d="M13 27h6" /></>}
      {v === "other" && <><rect {...P} x="8" y="8" width="16" height="16" rx="3" /><path {...P} d="M16 13v6M13 16h6" /></>}
    </svg>
  );
}

function Picker({ options, value, onChange, Glyph, isOn }) {
  const lit = (v) => (isOn ? isOn(v) : value === v);
  return (
    <div className="picker" role="radiogroup">
      {options.map((o) => (
        <button key={o.v} type="button" className="opt" data-on={lit(o.v) ? 1 : 0}
          role="radio" aria-checked={lit(o.v)} onClick={() => onChange(o.v)}>
          <Glyph v={o.v} />
          <span className="cap">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// Days until an invoice is "due" for each billing term.
const TERM_DAYS = { receipt: 0, net10: 10, net15: 15, net30: 30, net60: 60 };
// A/R aging: invoices 10 days old or less are new (not due). Day 11 they are due —
// and get auto-charged to the card on file by the 8am sweep.
const AR_GRACE_DAYS = 10;
// Master switch for the day-11 auto-charge sweep. Kept OFF until the system is
// finished and the owner says go — flip to true to arm it.
const AR_AUTO_CHARGE_ENABLED = false;
const arAge = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - d) / 86400000));
};
// Why an overdue account still isn't collected — shown on the follow-up page.
function arFollowReason(billing) {
  const b = { ...defBilling(), ...(billing || {}) };
  if (!hasCard(b)) return "No card on file";
  if (cardStatus(b.card).expired) return "Card expired";
  if (!b.autoCharge) return "Auto charge off";
  return "Charge failed";
}
// Reasons an account can be blocked from service — recorded with the Do-not-service switch.
const NO_SERVICE_REASONS = [
  "Unpaid invoice(s)", "Difficult / rude", "Unjustified complaints", "Complains a lot",
  "Bad payer", "Aggressive dog", "Unsafe backyard", "Dirty backyard / shed",
  "System is too complicated", "Needy customer",
];
// Split a client's unpaid invoices into "due now" vs "total owing" per their terms.
function accountBalances(billing, visits) {
  const termDays = TERM_DAYS[(billing && billing.terms) || "receipt"] || 0;
  const unpaid = (visits || []).filter((v) => v.paid === false);
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - termDays);
  const cutoffStr = fmtDate(cutoff);
  const due = unpaid.filter((v) => (v.date || "") <= cutoffStr);
  const sum = (arr) => arr.reduce((a, v) => a + num(v.amount), 0);
  return { unpaid, due, owing: sum(unpaid), dueNow: sum(due) };
}

function BillingPanel({ c, tiers, visits, onToggleAutoCharge, onToggleNoService, onSetPayMethod, onChargeNow, chargeSignal, onViewStatusCard, onPortalLink }) {
  const b = { ...defBilling(), ...(c.billing || {}) };
  const level = levelOf(tiers, b.levelId);
  const cuts = effDiscounts(b, tiers);
  const byLevel = (b.discountMode || "level") === "level";
  const cardOnFile = hasCard(b);
  const cs = cardStatus(b.card);
  const [chargePop, setChargePop] = useState(false);
  // The top banner's "Take payment now" bumps chargeSignal — open the same confirm flow.
  useEffect(() => { if (chargeSignal) setChargePop(true); }, [chargeSignal]);
  const [nsPop, setNsPop] = useState(false);
  const [nsReason, setNsReason] = useState("");
  const [nsNote, setNsNote] = useState("");
  const { owing, dueNow, unpaid, due } = accountBalances(b, visits);
  const fmt$ = (n) => "$" + num(n).toFixed(2);
  const rates = [
    ["Monthly service", money(b.monthlyRate)],
    ["Labour / hour", money(b.hourlyLabour)],
    ["Trip charge", money(b.tripCharge)],
  ].filter(([, v]) => v);

  return (
    <div>
      <div className="sechead">
        Pricing and billing
        <span className="ctag">{b.acctNo ? "Acct " + b.acctNo : CYCLE_LABEL[b.cycle]}</span>
      </div>

      {b.onHold && (
        <div className="alert">
          <strong>Account on hold.</strong> {b.holdReason || "No reason recorded."} Don't schedule or sell against it until this clears.
        </div>
      )}

      <div className="ledger">
        <div className="lrow">
          <span>Account due now</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
            <b style={dueNow > 0 ? { color: "var(--clay)" } : undefined}>{fmt$(dueNow)}</b>
            {cardOnFile && !cs.expired && owing > 0 && (
              <button className="btn mini" data-cobrowse="billing.chargenow" onClick={() => setChargePop(true)}>Charge now</button>
            )}
          </span>
        </div>
        <div className="lrow"><span>Total account owing</span><b>{fmt$(owing)}</b></div>
        <div className="lrow"><span>Cycle</span><b>{CYCLE_LABEL[b.cycle]}</b></div>
        <div className="lrow"><span>Terms</span><b>{TERM_LABEL[b.terms]}</b></div>
        {c.type !== "retail" && (
          <div className="lrow"><span>Billing rate</span><b style={{ color: "var(--tile)" }}>{b.zone === "z2" ? "Zone 2" : "Zone 1"}</b></div>
        )}
        <div className="lrow" data-flash={cardOnFile && cs.expired ? 1 : 0}>
          <span>
            Pays by
            {cardOnFile && cs.expired && (
              <b className="payexp">⚠ {b.card.brand || "Card"} ending {b.card.last4 || "····"} expired {b.card.exp} — get a new card{b.autoCharge ? "; auto charges will fail" : ""}</b>
            )}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 9, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <select className="paypick" value={b.payMethod || ""} onChange={(e) => onSetPayMethod(e.target.value)}>
              {PAY_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
            {cardOnFile && (
              <button className="switch acsmall" data-on={b.autoCharge ? 1 : 0} aria-pressed={b.autoCharge}
                title={b.autoCharge
                  ? (b.cycle === "monthly" ? `Auto charge is on — charged on the ${b.chargeDay || 1}${ordinal(b.chargeDay || 1)} of each month to the card on file. Tap to turn off.` : "Auto charge is on — charged after each visit to the card on file. Tap to turn off.")
                  : "Tap to charge the card on file automatically."}
                onClick={() => onToggleAutoCharge(!b.autoCharge)}>
                <span className="dot" />{b.autoCharge ? "Auto charge on" : "Auto charge off"}
              </button>
            )}
          </span>
        </div>
        {b.poRequired && <div className="lrow"><span>PO required</span><b>{b.poNumber || "No PO on file"}</b></div>}
        {!b.taxable && <div className="lrow"><span>Tax exempt</span><b>{b.taxExemptNo || "No number on file"}</b></div>}
        {num(c.storeCredit) > 0 && (
          <div className="lrow"><span>Store credit</span><b style={{ color: "#12A650" }}>{posMoney(c.storeCredit)} — spendable at the till</b></div>
        )}
        {c.giftedService && (
          <div className="lrow"><span>Gifted account</span>
            <b style={{ color: "#B7791F" }}>Free of charge — visits documented, not billed</b>
          </div>
        )}
        {c.statusExempt && (
          <div className="lrow"><span>First Nations HST Rebate</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <b style={{ color: "var(--tile)" }}>5% GST (−8% HST)</b>
              {c.statusCardImage
                ? <button className="btn ghost mini" onClick={onViewStatusCard}>Status card on file — view</button>
                : <b style={{ color: "var(--clay)" }}>No card photo on file</b>}
            </span>
          </div>
        )}
        {b.creditLimit && <div className="lrow"><span>Credit limit</span><b>{money(b.creditLimit)}</b></div>}
        {b.lateFeePct && <div className="lrow"><span>Late fee</span><b>{b.lateFeePct}% / month</b></div>}
        {b.paperInvoice && <div className="lrow"><span>Paper invoice</span><b>Mailed to billing address</b></div>}
        {onPortalLink && (
          <div className="lrow"><span>Customer portal</span>
            <button className="btn ghost mini" data-cobrowse="billing.portallink"
              title="Their private read-only account page — balance, invoices, service history. Emails the invite; they sign in with their email + mobile number on file."
              onClick={() => onPortalLink(c)}>Email portal invite</button>
          </div>
        )}
      </div>

      <div className="autocharge stopwork" data-on={b.noService ? 1 : 0} style={{ marginTop: 11 }}>
        <button className="switch stop" data-on={b.noService ? 1 : 0} aria-pressed={!!b.noService}
          onClick={() => {
            if (b.noService) {
              if (window.confirm(`Resume service for ${c.name}?`)) onToggleNoService(false, "", "");
            } else {
              setNsReason(""); setNsNote(""); setNsPop(true);
            }
          }}>
          <span className="dot" />{b.noService ? "DO NOT SERVICE" : "Do not service"}
        </button>
        <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, flex: 1, minWidth: 170 }}>
          {b.noService
            ? <><b style={{ color: "var(--clay)" }}>{b.noServiceReason || "No reason recorded"}</b>{b.noServiceNote ? ` — ${b.noServiceNote}` : ""}. Retail and counter sales are unaffected. Tap to lift.</>
            : "Turn on to stop service on this account. Flags the service side only — retail keeps trading."}
        </div>
      </div>

      {nsPop && (
        <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setNsPop(false); }}>
          <div className="sheet" style={{ maxWidth: 440 }}>
            <div className="bar">
              <div>
                <strong style={{ fontSize: 16 }}>Stop servicing this account?</strong>
                <div className="sub">{c.name}</div>
              </div>
              <button className="btn ghost" onClick={() => setNsPop(false)}>Cancel</button>
            </div>
            <div className="pad">
              <div className="notice" style={{ marginBottom: 12 }}>
                Are you sure? Every time this account is opened, a <b>Do not service</b> flag will show
                with the reason below. Retail and counter sales keep working.
              </div>
              <label>Reason (required)</label>
              <select value={nsReason} onChange={(e) => setNsReason(e.target.value)} style={{ marginBottom: 10 }}>
                <option value="">&mdash; pick a reason &mdash;</option>
                {NO_SERVICE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <label>Notes</label>
              <textarea value={nsNote} style={{ minHeight: 64 }} placeholder="What happened, who decided, anything the next person should know."
                onChange={(e) => setNsNote(e.target.value)} />
              <div className="btnrow" style={{ marginTop: 12 }}>
                <button className="btn warn" disabled={!nsReason}
                  onClick={() => { onToggleNoService(true, nsReason, nsNote.trim()); setNsPop(false); }}>
                  Block service on this account
                </button>
                <button className="btn ghost" onClick={() => setNsPop(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {b.payMethod === "credit" && !cardOnFile && (
        <div className="alert" style={{ marginTop: 11 }}>
          Set to pay by credit card, but there's no card on file. Pick Credit card again on the Pays by line to enter one.
        </div>
      )}

      {!b.payMethod && (
        <div className="alert" style={{ marginTop: 11 }}>
          No payment preference on file — ask the customer their preferred payment and set it on the Pays by line.
        </div>
      )}

      {cardOnFile && (
        <div style={{ marginBottom: 11 }}>
          <div className="cardface">
            <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "#8FCFF0" }}>{b.card.brand} on file</div>
            <div className="num">•••• {b.card.last4 || "····"}</div>
            <div className="who"><span>{b.card.holder || "—"}</span><span>Exp {b.card.exp || "—"}</span></div>
          </div>
          {cs.expired && <div className="alert" style={{ marginTop: 8 }}>That card expired. Auto charges will fail until it's replaced.</div>}
          {cs.soon && <div className="notice" style={{ marginTop: 8 }}>Card expires within a few months — worth asking on the next visit.</div>}
        </div>
      )}

      <div className="fields">
        <div className="kv f-wide">
          <em>Customer level</em><br />
          <span className="tier" style={{ marginTop: 4 }}>{byLevel ? level.name : "Set individually"}</span>
          {byLevel && level.note && <div className="sub">{level.note}</div>}
          <div className="counts" style={{ marginTop: 9 }}>
            {CATS.map(({ k, label }) => (
              <div key={k} className="count-cell" data-zero={cuts[k] ? 0 : 1}>
                <div className="n">{cuts[k]}%</div>
                <div className="l">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {rates.length > 0 && (
        <div className="ledger" style={{ marginTop: 10 }}>
          {rates.map(([l, v]) => <div key={l} className="lrow"><span>{l}</span><b>{v}</b></div>)}
        </div>
      )}

      {(b.seasonStart || b.contractTotal) && (
        <div className="ledger" style={{ marginTop: 10 }}>
          {b.seasonStart && <div className="lrow"><span>Opened in {new Date().getFullYear() - 1}</span><b>{b.seasonStart}</b></div>}
          {b.seasonEnd && <div className="lrow"><span>Closed in {new Date().getFullYear() - 1}</span><b>{b.seasonEnd}</b></div>}
          {b.contractTotal && <div className="lrow"><span>Contract</span><b>{money(b.contractTotal)}{b.instalments ? ` over ${b.instalments}` : ""}</b></div>}
        </div>
      )}

      {b.billingNotes && <div className="notice" style={{ marginTop: 10 }}>{b.billingNotes}</div>}

      {chargePop && (
        <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setChargePop(false); }}>
          <div className="sheet" style={{ maxWidth: 460 }}>
            <div className="bar">
              <div>
                <strong style={{ fontSize: 16 }}>Charge card on file</strong>
                <div className="sub">{c.name} · {b.card.brand || "Card"} ending {b.card.last4 || "····"}</div>
              </div>
              <button className="btn ghost" onClick={() => setChargePop(false)}>Cancel</button>
            </div>
            <div className="pad">
              <div className="sub" style={{ marginTop: 0, marginBottom: 12 }}>What would the customer like to pay?</div>
              <button className="btn chargeopt" onClick={() => { setChargePop(false); onChargeNow("all"); }}>
                <b>Pay everything owing — {fmt$(owing)}</b>
                <span>All {unpaid.length} unpaid invoice{unpaid.length === 1 ? "" : "s"}, including amounts not yet due</span>
              </button>
              {dueNow > 0 && dueNow < owing && (
                <button className="btn ghost chargeopt" onClick={() => { setChargePop(false); onChargeNow("due"); }}>
                  <b>Pay amount due now — {fmt$(dueNow)}</b>
                  <span>{due.length} invoice{due.length === 1 ? "" : "s"} past the {TERM_LABEL[b.terms] || "due"} window</span>
                </button>
              )}
              <div className="sub" style={{ marginTop: 10 }}>A receipt goes to the customer the way they receive invoices — text, email, or printed for the mail.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ordinal = (n) => { const v = Number(n) % 100; if (v >= 11 && v <= 13) return "th"; return ["th","st","nd","rd"][Math.min(Number(n) % 10, 4)] || "th"; };

const FLAG_MIN = new Set(); // customers whose flag banner is minimized — resets when the app reloads

/* Jasmine's permanent record: every repair ever done for this customer, one
   row each in her column order, expandable to the full story. Read-only —
   edits happen in the workshop. On-site repair visits ride along, tagged. */
function RepairHistoryPanel({ rows = [], siteRows = [], businessProfile = {}, terms = [] }) {
  const [showAll, setShowAll] = useState(false);
  const [openTk, setOpenTk] = useState(null);
  const merged = [...rows.map((r) => ({ ...r, kind: "shop" })), ...siteRows.map((r) => ({ ...r, kind: "site" }))]
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  if (!merged.length) return null;
  const shown = showAll ? merged : merged.slice(0, 5);
  return (
    <div className="stackpanel" style={{ marginTop: 10, marginBottom: 4 }}>
      <div className="ptitle">Repair history ({merged.length}) <span className="sub" style={{ display: "inline", marginLeft: 6 }}>permanent record — read-only</span></div>
      {shown.map((r, i) => (
        <div key={i} style={{ padding: "7px 0", borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", cursor: r.ticket ? "pointer" : "default" }}
            onClick={() => r.ticket && setOpenTk(openTk === r.ticket.id ? null : r.ticket.id)}>
            <b style={{ minWidth: 84 }}>{r.date}</b>
            <span style={{ flex: 1, minWidth: 150, fontSize: 13 }}>
              <b>{r.unit}</b>{r.serial ? <span className="sub" style={{ display: "block", marginTop: 0 }}>S/N {r.serial}</span> : null}
            </span>
            {r.techName && <span className="doc" style={{ background: "#5A6E86" }}>{r.techName}</span>}
            {r.kind === "site" && <span className="doc" style={{ background: "#17356E" }}>On-site</span>}
            <span style={{ flex: 2, minWidth: 170, fontSize: 13 }}>{r.workDone}</span>
            <span className="mono" style={{ fontWeight: 700 }} title={r.amountHow}>{posMoney(r.amount)}</span>
            {r.kind === "shop" && <span className="doc" style={{ background: r.warrantyKind === "Warranty repair" ? "#0E7A3C" : "#5A6E86" }}>{r.warrantyKind}</span>}
            {r.workWarrantyUntil && (r.stillCovered
              ? <span className="doc" style={{ background: "#12A650" }}>Repair warranty until {r.workWarrantyUntil}</span>
              : <span className="doc" style={{ background: "#8A97A8" }}>repair warranty expired {r.workWarrantyUntil}</span>)}
          </div>
          {r.ticket && openTk === r.ticket.id && (() => {
            const t = r.ticket;
            return (
              <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                {t.closing && <RepairClosingSummary t={t} />}
                {(t.photos || []).filter((p) => p.dataUrl).length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {(t.photos || []).filter((p) => p.dataUrl).map((p, j) => <img key={j} src={p.dataUrl} alt="unit" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 7, border: "1px solid var(--line)" }} />)}
                  </div>
                )}
                {t.payment && t.payment.paid && <div className="sub">Paid {posMoney(t.payment.amount)} by {t.payment.method}</div>}
                {t.deductible && t.deductible.applies && <div className="sub">Deductible ${num(t.deductible.amount).toFixed(2)} — {t.deductible.status}</div>}
                <div className="sub" style={{ marginTop: 4, fontWeight: 800 }}>Timeline</div>
                {repairTimeline(t).slice(0, 20).map((x, j) => (
                  <div key={j} className="sub" style={{ marginTop: 3, display: "flex", gap: 6, alignItems: "baseline" }}>
                    <span className="doc" style={{ background: x.side === "tech" ? "#0E7A3C" : "#0B4F8A", flex: "none" }}>{repairTimelineBadge(x)}</span>
                    <span>{String(x.at || "").slice(0, 16).replace("T", " ")} · <b>{x.by}</b> — {x.text}</span>
                  </div>
                ))}
                <div className="btnrow" style={{ marginTop: 6 }}>
                  <button className="btn ghost mini" onClick={() => printRepairDoc("wo", t, businessProfile, terms)}>Work order</button>
                  <button className="btn ghost mini" onClick={() => printRepairDoc("receipt", t, businessProfile, terms)}>Receipt</button>
                </div>
              </div>
            );
          })()}
        </div>
      ))}
      {merged.length > 5 && (
        <button className="showall" style={{ marginTop: 6 }} onClick={() => setShowAll(!showAll)}>{showAll ? "Show recent only" : `Show all ${merged.length}`}</button>
      )}
    </div>
  );
}

function Detail({ c, tiers, can, photos, visits, tests, specialOrders, savePhotos, setStatus, onZoom, onOpenVisits, onOpenTests, onEdit, onToggleAutoCharge, onToggleNoService, onSetPayMethod, onEditFlag, onCompose, onLogComm, onDeleteComm, onChargeNow , onOpenLinked, allClientsForLinks = [], onAddCard, followupsOpen = [], onAddFollowup, onDoneFollowup, repairsOpen = [], repairHistoryRows = [], quotes = [], repairsForQuotes = [], onOpenQuote, onNewQuote, onOpenRepairQuote, businessProfileForDocs = {}, termsForDocs = [], onWarrantyRef, onPortalLink }) {
  const [repShow, setRepShow] = useState(null); // expanded repair timeline on the customer card
  const [fuNote, setFuNote] = useState("");
  const flags = [...allFlags(c, visits), ...specialOrderFlags(c.id, specialOrders)];
  const [flagsMin, setFlagsMin] = useState(FLAG_MIN.has(c.id));
  const [eqShowMissing, setEqShowMissing] = useState(false);
  const [chargeSig, setChargeSig] = useState(0); // banner "Take payment now" → opens the charge confirm in BillingPanel
  const billRef = useRef(null);
  useEffect(() => { setFlagsMin(FLAG_MIN.has(c.id)); setEqShowMissing(false); }, [c.id]);
  const flagAction = (act) => {
    if (act === "chargenow") {
      setChargeSig((s) => s + 1);
      if (billRef.current) billRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (onAddCard) onAddCard();
  };
  const FLAG_ACT_LABEL = { addcard: "Add card", updatecard: "Update card", chargenow: "Take payment now" };
  const contacts = c.contacts || [];
  const [noTest, setNoTest] = useState(false);
  const [logCat, setLogCat] = useState(null); // "service" | "general" — which comm log is open
  const [issueCall, setIssueCall] = useState(false);
  const noInvoice = !contacts.some((k) => k.comms && k.comms.invoices !== "none");
  const commCount = (cat) => (c.commLog || []).filter((e) => e.cat === cat).length;
  // How this customer prefers to receive a comm type (across contacts): Email / Text / both, or Not set.
  const prefFor = (ck) => {
    const chans = [...new Set(contacts.map((k) => (k.comms || {})[ck]).filter((v) => v && v !== "none"))];
    return chans.length ? chans.map((v) => CHANNEL_LABEL[v]).join(" · ") : "Not set";
  };
  const contactCard = (k, i) => {
    const ini = (k.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
    return (
      <div key={k.id} className={"contact" + (i === 0 ? " primary" : "")}>
        <div className="chead">
          <span className="cavatar">{ini}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cname">{k.name || "Unnamed contact"}</div>
            {i > 0 && k.role && <div className="crole">{k.role}</div>}
          </div>
          {i === 0 && <span className="cbadge">Primary</span>}
        </div>
        <div className="crow">
          <CommIcon v="phone" size={15} />
          <span className="cval mono">{k.phone || "No phone"}</span>
          {k.phone && (
            <span className="cacts">
              <a className="commbtn" href={`tel:${k.phone.replace(/[^0-9+]/g, "")}`} title={`Call ${k.name || "contact"}`}><CommIcon v="phone" />Call</a>
              <button className="commbtn" title={`Text ${k.name || "contact"}`} onClick={() => onCompose({ kind: "text", to: k.phone })}><CommIcon v="text" />Text</button>
            </span>
          )}
        </div>
        <div className="crow">
          <CommIcon v="mail" size={15} />
          <span className="cval">{k.email || "No email"}</span>
          {k.email && (
            <span className="cacts">
              <button className="commbtn" title={`Email ${k.name || "contact"}`} onClick={() => onCompose({ kind: "mail", to: k.email })}><CommIcon v="mail" />Email</button>
            </span>
          )}
        </div>
      </div>
    );
  };
  const sp = scoreParts(c);
  const cx = effectiveCx(c);
  const rating = ratingParts(c, visits);
  const f = { ...defFeatures(), ...(c.features || {}) };
  const visitCount = (visits || []).length;
  const svcVisits = (visits || []).filter((v) => visitSide(v) === "service");
  const retVisits = (visits || []).filter((v) => visitSide(v) === "retail");
  const lastServiceDate = (svcVisits[0] || {}).date || c.lastService || "";
  const lastRetailDate = (retVisits[0] || {}).date || c.lastRetail || "";
  const lastServiceRepair = (svcVisits[0] || {}).repairType || "";
  const testCount = (tests || []).length;
  const latestTest = (tests || [])[0];

  return (
    <div className="detailroot" data-stop={(c.billing || {}).noService ? 1 : 0}>
      {flags.length > 0 && (
        <div className="flagsticky">
          {flagsMin ? (
            <button type="button" className="flagchip" onClick={() => { FLAG_MIN.delete(c.id); setFlagsMin(false); }}>
              <FlagIcon size={12} filled /> {flags.length} flag{flags.length === 1 ? "" : "s"} on this account — tap to show
            </button>
          ) : (<>
            {flags.map((f) => (
              <div key={f.key} className="gatenote bannerflag" data-stop={f.stop ? 1 : 0} data-amber={f.amber ? 1 : 0}
                data-tone={f.stop ? "stop" : f.amber ? "amber" : f.auto && !String(f.key).startsWith("so-") ? "hard" : "soft"}
                style={{ margin: 0 }}>
                <span className="bf-scope">{f.auto ? f.source : (FLAG_SCOPE_LABEL[f.scope] || "Account")}</span>
                {f.message}
                {f.act && can("pricing") && (
                  <button type="button" className="flagact" onClick={() => flagAction(f.act)}>{FLAG_ACT_LABEL[f.act]}</button>
                )}
              </div>
            ))}
            <button type="button" className="flagmini" onClick={() => { FLAG_MIN.add(c.id); setFlagsMin(true); }}>
              Minimize — stays out of the way until the app is reloaded
            </button>
          </>)}
        </div>
      )}
      {c.deactivated && (
        <div className="alert" style={{ marginBottom: 11 }}>
          <b>Inactive account</b> — {c.deactivated.reason || "no reason recorded"} · deactivated {c.deactivated.date}
          {c.deactivated.by ? ` by ${c.deactivated.by}` : ""}. History stays on file; use the active profile for new work.
        </div>
      )}
      {noTest && (
        <div className="overlay" style={{ alignItems: "center" }} onClick={(e) => { if (e.target.className === "overlay") setNoTest(false); }}>
          <div className="flagpop" style={{ borderTopColor: "var(--tile)" }}>
            <div className="top">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CommIcon v="mail" size={14} />Nothing to send
              </span>
              <span className="who2">{c.name}</span>
            </div>
            <div className="msg2">No water test on record for this account.</div>
            <div className="acts">
              <button className="btn ghost" onClick={() => setNoTest(false)}>Close</button>
              <button className="btn" onClick={() => { setNoTest(false); onOpenTests(); }}>Add a test</button>
            </div>
          </div>
        </div>
      )}

      <div className="bar">
        <div>
          <div className="detail-name" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {c.name}
            {c.type === "retail" && !c.trade && <span className="pill" data-t="retail">Retail Only Account</span>}
            {c.giftedService && <span className="doc" style={{ background: "#B7791F" }}>Gifted account</span>}
          </div>
          <div className="sub" style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            {c.address || "No address on file"}
            {c.type !== "retail" && !c.trade && (() => {
              const zoneStyle = { fontSize: 13, fontWeight: 800, padding: "4px 12px", borderRadius: 999, letterSpacing: 0.3, color: "#fff" };
              const zb = c.geo ? serviceBand(c.geo.lat, c.geo.lng) : null;
              if (zb && zb.band === "never") return <span style={{ ...zoneStyle, background: "#B31212" }} title={`${zb.km} km from the shop — beyond the ${SERVICE_LIMIT_KM} km limit`}>Out of service area · {zb.km} km</span>;
              if (zb && zb.band === "ask") return <span style={{ ...zoneStyle, background: "#D98200" }} title={`${zb.km} km from the shop — outside the zones; check with service before booking`}>Outside zones — ask service · {zb.km} km</span>;
              return (c.billing || {}).zone === "z2"
                ? <span style={{ ...zoneStyle, background: "#0887C8" }} title="Service zone — set from the address, adjustable under Billing rate">Zone 2</span>
                : <span style={{ ...zoneStyle, background: "#12A650" }} title="Service zone — set from the address, adjustable under Billing rate">Zone 1</span>;
            })()}
          </div>
        </div>
        <div className="btnrow">
          {can("flags") && <button className="flagbtn" data-on={flagShows(c) ? 1 : 0} data-alert={flags.length ? 1 : 0} onClick={onEditFlag}>
            <FlagIcon size={16} filled={flags.length > 0} />Flag message{flags.length > 1 ? ` (${flags.length})` : ""}
          </button>}
          {can("edit") && <button className="btn ghost" data-cobrowse="customers.edit" onClick={onEdit}>Edit</button>}
        </div>
      </div>

      <div className="pad">
        <div className="gatecard">
          <div className="gaterow">
            {c.trade ? (
              <div>
                <div className="lbl">Trade account</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>Counter pickup</div>
                <div style={{ fontSize: 12.5, color: "#9CD3F0", marginTop: 3 }}>Purchases report as trade sales</div>
              </div>
            ) : (<>
              <div>
                <div className="lbl">Gate code</div>
                <div className="gatecode mono">{c.gateCode || "—"}</div>
                {c.dog && <div style={{ marginTop: 8 }}><span className="flag">Dog on property</span></div>}
              </div>
              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                <div className="lbl">Key / entry</div>
                <div style={{ fontSize: 13.5, marginTop: 5, lineHeight: 1.45 }}>{c.keyLocation || "—"}</div>
              </div>
            </>)}
            <div className="meterwrap">
              {can("rating") && (
                <div className="mcell">
                  <div className="lbl">Account rating</div>
                  <Rating n={rating.score} showWord dark size={44} />
                </div>
              )}
              {c.type !== "retail" && !c.trade && (
                <div className="mcell">
                  <div className="lbl">Complexity</div>
                  <Complexity n={cx} showWord dark size={44} />
                </div>
              )}
            </div>
          </div>
          {!c.trade && c.type !== "retail" && c.accessNotes && <div className="gatenote">{c.accessNotes}</div>}
        </div>

        {(() => {
          // At-a-glance strip: the numbers staff scroll for, surfaced under the header.
          const b = { ...defBilling(), ...(c.billing || {}) };
          const bal = accountBalances(b, visits || []);
          const lastPaid = (visits || []).filter((v) => v.paid !== false && num(v.amount)).map((v) => v.date).filter(Boolean).sort().slice(-1)[0] || "";
          const next = c.type !== "retail" && !c.trade ? nextDue(c) : null;
          const cells = [];
          if (can("pricing")) {
            cells.push({ l: "Owing", v: posMoney(bal.owing), hot: bal.owing > 0 });
            cells.push({ l: "Due now", v: posMoney(bal.dueNow), hot: bal.dueNow > 0 });
            cells.push({ l: "Last payment", v: lastPaid || "—" });
            const cs = hasCard(b) ? cardStatus(b.card) : {};
            const wantsCard = b.autoCharge || b.payMethod === "credit";
            const bs = wantsCard && !hasCard(b) ? { v: "No card on file", hot: true }
              : cs.expired ? { v: "Card expired", hot: true }
              : cs.soon ? { v: "Card expires soon", warn: true }
              : !b.payMethod ? { v: "Ask payment pref.", warn: true }
              : { v: PAY_LABEL[b.payMethod] || "OK" };
            cells.push({ l: "Billing status", txt: true, ...bs });
          }
          if (next) cells.push({ l: "Next visit", v: fmtDate(next) });
          if (!c.trade) cells.push({ l: "Last service", v: lastServiceDate || "—" });
          if (!cells.length) return null;
          return (
            <div className="glance">
              {cells.map((x) => (
                <div key={x.l} className="gcell" data-hot={x.hot ? 1 : 0} data-warn={x.warn ? 1 : 0}>
                  <div className={"gv" + (x.txt ? " txt" : "")}>{x.v}</div>
                  <div className="gl">{x.l}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {onAddFollowup && (followupsOpen.length > 0 || true) && (
          <div className="stackpanel" style={{ marginTop: 10, marginBottom: 4, borderLeft: followupsOpen.some((f) => followupOverdue(f, fmtDate(new Date()))) ? "4px solid #DC1515" : undefined }}>
            <div className="ptitle">{displayTitleCase("Follow-ups")}{followupsOpen.length ? ` (${followupsOpen.length} open)` : ""}</div>
            <div className="btnrow" style={{ marginBottom: 6 }}>
              <button type="button" className="btn ghost mini"
                onClick={() => {
                  const r = makeWaterTestReminder(c.id, { createdBy: "" });
                  if (r.ok) onAddFollowup(r.followup.note);
                }}>Remind customer to test their water</button>
            </div>
            {followupsOpen.map((f) => (
              <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderTop: "1px solid var(--line)" }}>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>{f.note}</span>
                {f.dueDate && <span className="doc" style={{ background: followupOverdue(f, fmtDate(new Date())) ? "#DC1515" : "#5A6E86" }}>{f.dueDate}</span>}
                <button className="btn ghost mini" onClick={() => onDoneFollowup(f.id)}>✓ Done</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 7 }}>
              <input placeholder="New follow-up for this customer — e.g. chase the pump gasket" value={fuNote} style={{ flex: 1 }}
                onChange={(e) => setFuNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && fuNote.trim()) { onAddFollowup(fuNote.trim()); setFuNote(""); } }} />
              <button className="btn mini" disabled={!fuNote.trim()} onClick={() => { onAddFollowup(fuNote.trim()); setFuNote(""); }}>Add</button>
            </div>
          </div>
        )}

        {(() => {
          const today = fmtDate(new Date());
          const rows = quoteListRows({ quotesMap: { [c.id]: quotes || [] }, repairs: repairsForQuotes || [], clients: [{ id: c.id, name: c.name }], clientId: c.id, today });
          if (!onOpenQuote && rows.length === 0) return null;
          return (
            <div className="stackpanel" style={{ marginTop: 10, marginBottom: 4 }}>
              <div className="ptitle">Quotes ({rows.length}) <span className="sub" style={{ display: "inline", marginLeft: 6 }}>every quote sent to this customer</span></div>
              {rows.length === 0 ? (
                <div className="sub" style={{ marginTop: 0 }}>None on file yet — customer quotes and repair estimates both land here.</div>
              ) : rows.map((r) => (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 0", borderTop: "1px solid var(--line)" }}>
                  <span className="mono" style={{ fontWeight: 700, minWidth: 88 }}>{r.date || "—"}</span>
                  <span style={{ flex: 1, minWidth: 110, fontSize: 13 }}>{r.forWhom || (r.kind === "repair" ? "Repair estimate" : "Quote")}</span>
                  <span className="doc inv">${num(r.amount).toFixed(2)}</span>
                  <span className="sub" style={{ marginTop: 0, minWidth: 86 }}>{r.sentBy || "Not sent"}</span>
                  <span className="doc" style={{ background: quoteStatusColor(r.status) }}>{quoteStatusLabel(r.status)}</span>
                  <button className="btn ghost mini" onClick={() => r.kind === "repair" ? (onOpenRepairQuote && onOpenRepairQuote(r.ticketNo || r.ticketId)) : (onOpenQuote && onOpenQuote(r.quoteId))}>Open</button>
                </div>
              ))}
              {onNewQuote && can("edit") && (
                <div style={{ marginTop: 8 }}>
                  <button className="btn mini" onClick={onNewQuote}><QuoteIcon size={13} /> New quote</button>
                </div>
              )}
            </div>
          );
        })()}

        {repairsOpen.length > 0 && (
          <div className="stackpanel" style={{ marginTop: 10, marginBottom: 4, borderLeft: repairsOpen.some((t) => repairStale(t, fmtDate(new Date())) || repairEtaOverdue(t, fmtDate(new Date()))) ? "4px solid #B36B00" : undefined }}>
            <div className="ptitle">In-house repairs ({repairsOpen.length} active) <span className="sub" style={{ display: "inline", marginLeft: 6 }}>read-only — updates happen in the workshop</span></div>
            {repairsOpen.map((t) => {
              const today = fmtDate(new Date());
              const lu = repairLastUpdated(t);
              return (
                <div key={t.id} style={{ padding: "6px 0", borderTop: "1px solid var(--line)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <b className="mono">{t.ticketNo}</b>
                    <span style={{ flex: 1, minWidth: 140, fontSize: 13 }}>{[t.category, t.subCategory].filter(Boolean).join(" · ")}{t.model ? ` · ${t.model}` : ""}</span>
                    <span className="doc" style={{ background: "#0B4F8A" }}>{t.stage || REPAIR_STATUS_LABEL[t.status] || t.status}</span>
                    {t.purchase && t.purchase.termScope && <span className="doc" style={{ background: "#0E7A3C" }} title={`Bought ${t.purchase.date}`}>{t.purchase.termScope} warranty</span>}
                    {t.deductible && t.deductible.applies && (deductibleOutstanding(t) > 0
                      ? <span className="doc" style={{ background: "#DC1515" }} title="Collect before pickup">collect ${deductibleOutstanding(t).toFixed(2)} + HST</span>
                      : <span className="doc" style={{ background: "#12A650" }}>deductible {t.deductible.status === "waived" ? "waived" : "paid"} ✓</span>)}
                    {t.etaDate && <span className="doc" style={{ background: repairEtaOverdue(t, today) ? "#DC1515" : "#0B6FBE" }}>ETA {t.etaDate}</span>}
                    {repairStale(t, today)
                      ? <span className="doc" style={{ background: "#B36B00" }}>no update {businessDaysBetween(lu, today)}+ biz days</span>
                      : lu ? <span className="sub" style={{ marginTop: 0 }}>updated {String(lu).slice(0, 10)}</span> : null}
                    <button className="btn ghost mini" onClick={() => setRepShow(repShow === t.id ? null : t.id)}>{repShow === t.id ? "Hide" : "Timeline"}</button>
                  </div>
                  <div className="sub" style={{ marginTop: 2 }}>Received {String(t.at || "").slice(0, 10)}{t.techId ? "" : " · unassigned"}</div>
                  {repShow === t.id && repairTimeline(t).map((r, i) => (
                    <div key={i} className="sub" style={{ marginTop: 3, display: "flex", gap: 6, alignItems: "baseline" }}>
                      <span className="doc" style={{ background: r.side === "tech" ? "#0E7A3C" : "#0B4F8A", flex: "none" }}>{repairTimelineBadge(r)}</span>
                      <span>{String(r.at || "").slice(0, 16).replace("T", " ")} · <b>{r.by}</b> — {r.text}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        <RepairHistoryPanel rows={repairHistoryRows} businessProfile={businessProfileForDocs} terms={termsForDocs}
          siteRows={(visits || []).filter((v) => v.repairType).map((v) => ({
            date: v.date || "", unit: v.repairType, serial: "", techName: v.by || "",
            workDone: v.notes ? (v.notes.length > 90 ? v.notes.slice(0, 87) + "…" : v.notes) : "On-site repair",
            amount: num(v.matPrice) + num(v.labour), amountHow: v.paid ? "invoiced on the visit" : "unpaid visit invoice",
            warrantyKind: "", workWarrantyUntil: "", stillCovered: false, ticket: null }))} />

        {(c.linkedIds || []).length > 0 && (
          <div className="gatenote" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <b style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}>Same owner:</b>
            {(c.linkedIds || []).map((id) => (
              <button key={id} type="button" className="btn ghost mini" onClick={() => onOpenLinked && onOpenLinked(id)}>
                {(allClientsForLinks.find((x) => x.id === id) || {}).name || "Linked property"}
              </button>
            ))}
          </div>
        )}
        <div className="sechead">Contacts <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{contacts.length}</span></div>
        {contacts.length === 0 ? <div className="kv">—</div> : (<>
          {contactCard(contacts[0], 0)}
          {contacts.slice(1).map((k, i) => contactCard(k, i + 1))}

          <div className="sechead">Communication</div>
          <div className="commtiles">
            <button type="button" className="commtile" onClick={() => onOpenVisits && onOpenVisits("all")}>
              <span className="ct-ic"><QuoteIcon size={28} /></span>
              <b>Invoices</b>
              <span className="ct-pref" data-set={prefFor("invoices") === "Not set" ? 0 : 1}>{prefFor("invoices")}</span>
              <span>retail + service</span>
            </button>
            <button type="button" className="commtile" onClick={() => setLogCat("service")}>
              <span className="ct-ic"><CalendarIcon size={28} /></span>
              <b>Service</b>
              <span className="ct-pref" data-set={prefFor("service") === "Not set" ? 0 : 1}>{prefFor("service")}</span>
              <span>{commCount("service")} logged · open log</span>
            </button>
            <button type="button" className="commtile" onClick={() => setLogCat("general")}>
              <span className="ct-ic"><RetailIcon size={28} /></span>
              <b>Retail / Water Testing</b>
              <span className="ct-pref" data-set={prefFor("general") === "Not set" ? 0 : 1}>{prefFor("general")}</span>
              <span>{commCount("general")} logged · open log</span>
            </button>
            <button type="button" className="commtile" onClick={() => setLogCat("repairs")}>
              <span className="ct-ic"><CommIcon v="text" size={28} /></span>
              <b>Repairs</b>
              <span className="ct-pref" data-set={prefFor("repairs") === "Not set" ? 0 : 1}>{prefFor("repairs")}</span>
              <span>{commCount("repairs")} logged · open log</span>
            </button>
          </div>
          <div className="btnrow" style={{ marginTop: 8, marginBottom: 4 }}>
            <button type="button" className="btn mini" onClick={() => setIssueCall(true)}><CommIcon v="phone" /> Call about a service issue</button>
          </div>

          <div className="recpair" style={{ marginBottom: 6 }}>
            <div className="recrow">
              <span className="ico"><RecordIcon v="visit" /></span>
              <div className="body">
                <div className="lbl2">
                  Last service
                  {svcVisits.length > 0 && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{svcVisits.length}</span>}
                </div>
                {lastServiceDate
                  ? <button className="bigdate" onClick={() => onOpenVisits("service")}>{lastServiceDate}</button>
                  : <div className="none2">Nothing logged</div>}
                {lastServiceRepair && <div className="sub" style={{ marginTop: 3 }}>{lastServiceRepair}</div>}
                <button className="showall" onClick={() => onOpenVisits("service")}>
                  <ListIcon />Show all service invoices{svcVisits.length ? ` (${svcVisits.length})` : ""}
                </button>
              </div>
            </div>
            <div className="recrow">
              <span className="ico"><RecordIcon v="retail" /></span>
              <div className="body">
                <div className="lbl2">
                  {c.trade ? "Last trade" : "Last retail"}
                  {retVisits.length > 0 && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{retVisits.length}</span>}
                </div>
                {lastRetailDate
                  ? <button className="bigdate" onClick={() => onOpenVisits("retail")}>{lastRetailDate}</button>
                  : <div className="none2">Nothing logged</div>}
                <button className="showall" onClick={() => onOpenVisits("retail")}>
                  <ListIcon />Show all {c.trade ? "trade" : "retail"} invoices{retVisits.length ? ` (${retVisits.length})` : ""}
                </button>
              </div>
            </div>
          </div>

        </>)}

        {logCat && (
          <CommLogSheet
            title={logCat === "service" ? "Service log" : logCat === "repairs" ? "Repairs log" : "Retail / water testing log"}
            cat={logCat} client={c} onLog={onLogComm} onDelete={onDeleteComm}
            onClose={() => setLogCat(null)} />
        )}
        {issueCall && (
          <ServiceIssueCallSheet client={c} onLog={(note) => onLogComm && onLogComm("service", note)}
            onClose={() => setIssueCall(false)} />
        )}

        <div className="sechead">
          Account
          <span className="ctag">{TYPE_LABEL[c.type]}{c.trade ? " · trade" : ""}{c.waterTesting ? " · water testing" : ""}</span>
        </div>

        {can("water") && <div className="recrow">
          <span className="ico"><RecordIcon v="test" /></span>
          <div className="body">
            <div className="lbl2">
              Last water test
              {testCount > 0 && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{testCount} on file</span>}
              {latestTest && <span className="srcchip" data-s={latestTest.source}>{latestTest.source === "spinlab" ? "Spin Lab" : "Manual"}</span>}
              {latestTest && <span className="srcchip" data-s="manual">{TEST_PLACE_LABEL[latestTest.where || "store"]}</span>}
            </div>
            {c.lastWaterTest
              ? <button className="bigdate" onClick={onOpenTests}>{c.lastWaterTest}</button>
              : <div className="none2">No test on file</div>}
            {(() => {
              const dv = diyVerdict(tests || []);
              const ink = { pro: "#0E7A3C", diy: "#7A4FB6", both: "#0B6FBE", unknown: "#8091A9" }[dv.verdict];
              return (
                <div className="sub careverdict" style={{ marginTop: 4 }}>
                  <span className="doc" style={{ background: ink }} title={`Derived from ${dv.n} labelled test${dv.n === 1 ? "" : "s"} — shop vs service. Never set by hand.`}>
                    {dv.label}
                  </span>{" "}
                  <span className="carenote">Water care · from test history</span>
                </div>
              );
            })()}
            {latestTest && (
              <div className="readings">
                {WATER_PARAMS.filter((prm) => latestTest.results[prm.k]).slice(0, 6).map((prm) => (
                  <span key={prm.k} className="reading" data-s={readingState(prm, latestTest.results[prm.k])}>
                    <span className="rl">{prm.label}</span>
                    <span className="rv">{latestTest.results[prm.k]}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="btnrow" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
            <button className="btn ghost mini" onClick={onOpenTests}>{c.lastWaterTest ? "Open" : "Add test"}</button>
            <button className="commbtn" style={{ justifyContent: "center" }}
              onClick={() => (latestTest
                ? onCompose({
                    kind: "mail",
                    to: (contacts.find((k) => (k.comms || {}).general !== "none" && k.email) || contacts.find((k) => k.email) || {}).email || "",
                    subject: `${c.name} — water test ${latestTest.date}`,
                    body: testAsText(c, latestTest),
                  })
                : setNoTest(true))}>
              <CommIcon v="mail" />Email last test
            </button>
          </div>
        </div>}

        {c.type !== "retail" && !c.trade && (
          <div className="schedcard">
            <div className="freq">{FREQ_LABEL[c.frequency] || "Weekly"}</div>
            <div className="when">{cadenceLine(c)}</div>
            {c.frequency === "short" && c.shortTermUntil && (
              <div className="until">Short term — through {c.shortTermUntil}</div>
            )}
            {nextDue(c) && <div className="nextup">Next visit {fmtDate(nextDue(c))}</div>}
          </div>
        )}

        <div className="addr" style={{ marginTop: 11 }}>
          <div className="addrcard">
            <span className="ctag">Service address</span>
            <div className="txt">{c.address || "—"}</div>
          </div>
          <div className="addrcard">
            <span className="ctag">Billing address</span>
            {c.billingSame
              ? <div className="txt muted2">Same as service address</div>
              : <div className="txt">{c.billingAddress || "—"}</div>}
          </div>
        </div>

        <div className="sechead">{displayTitleCase("Account notes")}</div>
        <div className={"kv" + (c.notes ? " notealert" : "")} style={{ whiteSpace: "pre-wrap", marginBottom: 4 }}>{c.notes || "—"}</div>

        {can("pricing") && <div ref={billRef}><BillingPanel c={c} tiers={tiers} visits={visits} chargeSignal={chargeSig} onChargeNow={onChargeNow} onPortalLink={onPortalLink} onToggleAutoCharge={onToggleAutoCharge} onToggleNoService={onToggleNoService} onSetPayMethod={onSetPayMethod}
          onViewStatusCard={() => c.statusCardImage && onZoom({ dataUrl: c.statusCardImage, tag: "Status card", caption: `${c.name}${c.statusCardNo ? " · card # " + c.statusCardNo : ""}` })} /></div>}

        {!c.trade && <>
        <div className="sechead">Structure</div>
        <div className="structrow">
          <div className="structhero">
            <span style={{ color: "var(--tile)" }}><StructureGlyph v={c.structure || "pool"} size={54} /></span>
            <div>
              <div className="nm">{STRUCTURE_LABEL[c.structure || "pool"]}</div>
              <div className="sub">
                {c.surface || "Surface not recorded"}
                {c.spaSeparate ? " · spa on its own water" : hasSpa(c.structure || "pool") ? " · one shared body of water" : ""}
              </div>
            </div>
          </div>
          <div className="structhero">
            <span style={{ color: "var(--tile)" }}><CoverGlyph v={c.coverType || "none"} size={54} /></span>
            <div>
              <div className="nm">{COVER_LABEL[c.coverType || "none"]}</div>
              <div className="sub">
                {c.coverLeafNet && canAddLeafNet(c.coverType) ? "Leaf net used as well" : "Cover on file"}
              </div>
            </div>
          </div>
        </div>
        <div className="counts" style={{ marginBottom: 11 }}>
          <div className="count-cell" data-zero={c.gallons ? 0 : 1}>
            <div className="n">{c.gallons ? num(c.gallons).toLocaleString() : "—"}</div>
            <div className="l">{c.spaSeparate ? "Pool volume (gal)" : "Volume (gal)"}{c.gallons ? ` · ${Number(galToL(c.gallons)).toLocaleString()} L` : ""}</div>
          </div>
          {c.spaSeparate && (
            <div className="count-cell" data-zero={c.spaGallons ? 0 : 1}>
              <div className="n">{c.spaGallons ? num(c.spaGallons).toLocaleString() : "—"}</div>
              <div className="l">Spa volume (gal){c.spaGallons ? ` · ${Number(galToL(c.spaGallons)).toLocaleString()} L` : ""}</div>
            </div>
          )}
          <div className="count-cell" data-zero={c.sanitizerType ? 0 : 1}>
            <div className="t">{c.sanitizerType || "—"}</div>
            <div className="l">Sanitizer</div>
          </div>
        </div>
        {c.spaSeparate && c.spaNotes && <div className="notice" style={{ marginBottom: 11 }}>{c.spaNotes}</div>}
        <div className="counts">
          {FEATURES.map(({ k, label }) => (
            <div key={k} className="count-cell" data-zero={Number(f[k]) ? 0 : 1}>
              <div className="n">{Number(f[k]) || 0}</div>
              <div className="l">{label}</div>
            </div>
          ))}
        </div>

        </>}


        {can("equipment") && !c.trade && (() => {
          const hasData = (e) => !!(e.detail || e.serial || e.installed);
          const recorded = (c.equipmentList || []).filter(hasData);
          const missing = (c.equipmentList || []).filter((e) => !hasData(e));
          return (<>
            <div className="sechead">
              Equipment
              <span className="ctag">{recorded.length} recorded</span>
            </div>
            {recorded.map((e) => (
              <div key={e.id} className="eqrow">
                <span className="ico"><EquipGlyph v={e.kind} /></span>
                <div className="body">
                  <div className="kind">{EQUIP_LABEL[e.kind] || "Equipment"}</div>
                  <div className="val">{e.detail || "—"}</div>
                  {(e.serial || e.installed) && (
                    <div className="val" style={{ fontSize: 11.5, color: "var(--muted)" }}>
                      {e.serial ? `S/N ${e.serial}` : ""}{e.serial && e.installed ? " · " : ""}{e.installed ? `installed ${e.installed}` : ""}
                    </div>
                  )}
                </div>
                {onWarrantyRef && (e.serial || e.detail) && (
                  <button className="btn ghost mini" style={{ alignSelf: "center" }} title="Coverage, deductible and claim steps for this unit"
                    onClick={() => onWarrantyRef({ q: e.serial || e.detail, purchaseDate: e.installed || "", source: "equipment record" })}>Warranty</button>
                )}
              </div>
            ))}
            {recorded.length === 0 && missing.length === 0 && (
              <div className="kv" style={{ color: "var(--muted)", marginBottom: 4 }}>No equipment on file — add it under Edit.</div>
            )}
            {missing.length > 0 && (eqShowMissing ? (
              <div className="eqmissing">
                {missing.map((e) => <span key={e.id} className="eqmchip">{EQUIP_LABEL[e.kind] || "Equipment"}</span>)}
                <button type="button" className="flagmini" style={{ color: "var(--muted)", alignSelf: "center" }} onClick={() => setEqShowMissing(false)}>hide</button>
              </div>
            ) : (
              <button type="button" className="eqmore" onClick={() => setEqShowMissing(true)}>
                {missing.length} equipment item{missing.length === 1 ? "" : "s"} not recorded — show
              </button>
            ))}
          </>);
        })()}

        {can("photos") && <PhotoBoard clientId={c.id} photos={photos} savePhotos={savePhotos} setStatus={setStatus} onZoom={onZoom} />}

        {can("rating") && <>
        <div className="sechead">
          How the account rating is scored
          <span className="ctag">{rating.score}/10 · {ratingWord[ratingBand(rating.score)]}</span>
        </div>
        {rating.thin ? (
          <div className="notice" style={{ marginBottom: 13 }}>
            No materials or labour logged yet, so this is the baseline. Add the cost and price of materials
            and the labour on each visit and the rating starts reflecting what the account is actually worth.
          </div>
        ) : (
          <div className="breakdown" style={{ marginBottom: 13 }}>
            {rating.parts.map((prt, i) => (
              <div key={i} className="brow">
                <span>{prt.label}</span>
                <b style={prt.pts < 0 ? { color: "var(--clay)" } : undefined}>{prt.pts > 0 ? "+" : ""}{prt.pts}</b>
              </div>
            ))}
            <div className="brow" style={{ fontWeight: 700 }}>
              <span>Total</span><b>{rating.raw} → {rating.score}/10</b>
            </div>
          </div>
        )}

        </>}

        {!c.trade && <>
        <div className="sechead">
          How the complexity is scored
          <span className="ctag">{cx}/10 · {c.complexityMode === "manual" ? "set by hand" : "from pool details"}</span>
        </div>
        {c.complexityMode === "manual" ? (
          <div className="sub">Auto score from the pool details would be {sp.score}/10.</div>
        ) : (
          <div className="breakdown">
            {sp.parts.map((p, i) => (
              <div key={i} className="brow"><span>{p.label}</span><b>+{p.pts}</b></div>
            ))}
            <div className="brow" style={{ marginTop: 4, fontWeight: 700 }}>
              <span>Total</span><b>{sp.raw} → {sp.score}/10</b>
            </div>
          </div>
        )}
        </>}

      </div>
    </div>
  );
}

// Canada Post AddressComplete — suggests real addresses and fills the full formatted line.
const CP_KEY = import.meta.env.VITE_CANADAPOST_KEY || "";
const CP_FIND = "https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3.ws";
const CP_RETRIEVE = "https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Retrieve/v2.11/json3.ws";
const cpFormat = (a) => [a.Line1, a.Line2, a.City, a.ProvinceCode, a.PostalCode].filter(Boolean).join(", ");

function AddressAutocomplete({ value, onChange, placeholder, onPicked }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const box = useRef(null);

  const find = async (term, lastId = "") => {
    if (!CP_KEY || !term.trim()) { setItems([]); setOpen(false); return; }
    setBusy(true);
    try {
      const url = `${CP_FIND}?Key=${encodeURIComponent(CP_KEY)}&SearchTerm=${encodeURIComponent(term)}&Country=CAN&LanguagePreference=en&MaxResults=8${lastId ? "&LastId=" + encodeURIComponent(lastId) : ""}`;
      const j = await (await fetch(url)).json();
      const list = (j.Items || []).filter((x) => x.Id && !x.Error);
      setItems(list); setOpen(list.length > 0);
    } catch { setItems([]); setOpen(false); }
    setBusy(false);
  };

  const onType = (v) => {
    onChange(v);
    if (!CP_KEY) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => find(v), 250);
  };

  const pick = async (item) => {
    if (item.Next === "Retrieve") {
      try {
        const url = `${CP_RETRIEVE}?Key=${encodeURIComponent(CP_KEY)}&Id=${encodeURIComponent(item.Id)}`;
        const j = await (await fetch(url)).json();
        const a = (j.Items || [])[0];
        if (a && a.Line1) {
          const full = cpFormat(a);
          onChange(full);
          if (onPicked) onPicked(full);
        }
      } catch {}
      setOpen(false); setItems([]);
    } else {
      find(value, item.Id); // drill into a street/community
    }
  };

  return (
    <div ref={box} style={{ position: "relative" }} onBlur={(e) => { if (!box.current.contains(e.relatedTarget)) setOpen(false); }}>
      <input value={value} placeholder={placeholder} autoComplete="off"
        onChange={(e) => onType(e.target.value)} onFocus={() => items.length && setOpen(true)} />
      {open && (
        <div className="poslookup" style={{ position: "absolute", left: 0, right: 0, zIndex: 40 }}>
          {items.map((it) => (
            <button key={it.Id} type="button" className="poslookupitem" onClick={() => pick(it)}>
              <b>{it.Text}</b>
              <span className="sub" style={{ marginTop: 0 }}>{it.Description}{it.Next === "Find" ? " ›" : ""}</span>
            </button>
          ))}
        </div>
      )}
      {CP_KEY && busy && <div className="sub" style={{ marginTop: 3 }}>Searching Canada Post…</div>}
      {!CP_KEY && <div className="sub" style={{ marginTop: 3 }}>Canada Post address lookup isn't set up yet — type the address manually.</div>}
    </div>
  );
}

// A stable field component (defined at module level so typing never remounts the input).
function F({ draft, change, k, label, wide, type = "text", placeholder }) {
  return (
    <div className={wide ? "f-wide" : ""}>
      <label>{label}</label>
      <input type={type} value={draft[k]} placeholder={placeholder} onChange={(e) => change(k, e.target.value)} />
    </div>
  );
}

function ComplaintAddRow({ onAdd }) {
  const [text, setText] = useState("");
  const [validity, setValidity] = useState("valid");
  const [source, setSource] = useState("customer");
  return (
    <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
      <textarea rows={2} value={text} placeholder="Describe the complaint…" onChange={(e) => setText(e.target.value)} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <select value={validity} onChange={(e) => setValidity(e.target.value)} style={{ width: "auto" }}>
          {COMPLAINT_VALIDITY.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} style={{ width: "auto" }}>
          {COMPLAINT_SOURCE.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <button type="button" className="btn mini" disabled={!text.trim()}
          onClick={() => { onAdd({ text, validity, source }); setText(""); }}>Store complaint</button>
      </div>
    </div>
  );
}

function EditForm({ draft, change, setDraft, onSave, onCancel, onDelete, isNew, tiers, techs, onManageTiers, can, isOwner, isMgr = false, allClients = [], parts = [], me }) {
  const [spOpen, setSpOpen] = useState(false);
  const [spSearch, setSpSearch] = useState("");
  // Picking an address auto-detects the service zone from its coordinates.
  const [zoneHint, setZoneHint] = useState(null); // "busy" | "z1" | "z2" | "ask" | "never" | "none"
  const [zoneKm, setZoneKm] = useState(null);
  const detectZone = async (full) => {
    setZoneHint("busy");
    const g = await geocodeAddr(full);
    if (!g) { setZoneHint("none"); return; }
    setDraft((d) => ({ ...d, geo: { lat: g.lat, lng: g.lng } }));
    const b = serviceBand(g.lat, g.lng);
    const z = b.zone;
    setZoneKm(b.km);
    setZoneHint(z === "out" ? b.band : (z || "none"));
    if (z === "z1" || z === "z2") {
      setDraft((d) => {
        const lb = { ...defBilling(), ...(d.billing || {}), card: { ...defBilling().card, ...((d.billing || {}).card || {}) }, discounts: { ...defBilling().discounts, ...((d.billing || {}).discounts || {}) } };
        return { ...d, billing: { ...lb, zone: z } };
      });
    }
  };
  const setContact = (id, k, v) =>
    setDraft((d) => ({ ...d, contacts: d.contacts.map((c) => (c.id === id ? { ...c, [k]: v } : c)) }));
  // First/Last edits keep the combined `name` in sync so every display keeps working.
  const setContactName = (id, part, v) =>
    setDraft((d) => ({ ...d, contacts: d.contacts.map((c) => {
      if (c.id !== id) return c;
      const words = String(c.name || "").trim().split(/\s+/);
      const first = part === "first" ? v : (c.firstName ?? (words[0] || ""));
      const last = part === "last" ? v : (c.lastName ?? words.slice(1).join(" "));
      return { ...c, firstName: first, lastName: last, name: [first, last].map((x) => String(x).trim()).filter(Boolean).join(" ") };
    }) }));
  const setComm = (id, kind, v) =>
    setDraft((d) => ({ ...d, contacts: d.contacts.map((c) => (c.id === id ? { ...c, comms: { ...c.comms, [kind]: v } } : c)) }));
  // Promotional preference is consent, so every change is logged: what, when, who.
  const setPromoPref = (id, v) =>
    setDraft((d) => ({ ...d, contacts: d.contacts.map((c) => (c.id === id
      ? { ...c, promos: v === "auto" ? undefined : v,
          promosLog: [...(c.promosLog || []), { at: new Date().toISOString(), to: v, by: me ? me.name : "staff", how: "staff" }] }
      : c)) }));
  const addContact = () =>
    setDraft((d) => ({ ...d, contacts: [...d.contacts, { id: cid(), name: "", role: "Spouse", phone: "", email: "", comms: defComms(false) }] }));
  const dropContact = (id) => setDraft((d) => ({ ...d, contacts: d.contacts.filter((c) => c.id !== id) }));
  const makePrimary = (id) => setDraft((d) => {
    const c = d.contacts.find((x) => x.id === id);
    return { ...d, contacts: [c, ...d.contacts.filter((x) => x.id !== id)] };
  });
  const setStanding = (k, v) =>
    setDraft((d) => ({ ...d, standing: { ...defStanding(), ...(d.standing || {}), [k]: Math.max(0, Number(v) || 0) } }));
  const setEquip = (id, k, v) =>
    setDraft((d) => ({ ...d, equipmentList: (d.equipmentList || []).map((e) => {
      if (e.id !== id) return e;
      const next = { ...e, [k]: v };
      if (k === "timerOn" || k === "timerOff") next.timerNote = equipmentTimerChangeNote(e, next);
      return next;
    }) }));
  const addEquip = () =>
    setDraft((d) => ({ ...d, equipmentList: addEquipmentOfKind(d.equipmentList, "other") }));
  const dropEquip = (id) =>
    setDraft((d) => ({ ...d, equipmentList: (d.equipmentList || []).filter((e) => e.id !== id) }));
  const setFeature = (k, v) =>
    setDraft((d) => ({ ...d, features: { ...defFeatures(), ...(d.features || {}), [k]: Math.max(0, Number(v) || 0) } }));
  const bill = { ...defBilling(), ...(draft.billing || {}),
    card: { ...defBilling().card, ...((draft.billing || {}).card || {}) },
    discounts: { ...defBilling().discounts, ...((draft.billing || {}).discounts || {}) } };
  const liveBill = (d) => ({ ...defBilling(), ...(d.billing || {}),
    card: { ...defBilling().card, ...((d.billing || {}).card || {}) },
    discounts: { ...defBilling().discounts, ...((d.billing || {}).discounts || {}) } });
  const setBill = (k, v) => setDraft((d) => ({ ...d, billing: { ...liveBill(d), [k]: v } }));
  const setCard = (k, v) => setDraft((d) => {
    const lb = liveBill(d);
    return { ...d, billing: { ...lb, card: { ...lb.card, [k]: v, updated: new Date().toISOString().slice(0, 10) } } };
  });
  const setDiscount = (k, v) => setDraft((d) => {
    const lb = liveBill(d);
    return { ...d, billing: { ...lb, discounts: { ...lb.discounts, [k]: Math.max(0, Math.min(100, Number(v) || 0)) } } };
  });

  const sp = scoreParts(draft);

  return (
    <div>
      <div className="bar">
        <strong style={{ fontSize: 15 }}>{isNew ? "New customer" : "Editing " + draft.name}</strong>
        <div className="btnrow">
          {!isNew && <button className="btn warn" onClick={onDelete}>Delete</button>}
          <button className="btn ghost" onClick={onCancel}>Cancel</button>
          <button className="btn" data-cobrowse="customers.save" onClick={onSave}>Save client</button>
        </div>
      </div>
      <div className="pad">
        <div className="sechead">Client</div>
        <div className="fields">
          <F draft={draft} change={change} k="name" label="Customer Name" wide placeholder="First Name, Last Name, or Company Name" />
          <div className="f-wide">
            <label>Service address</label>
            <AddressAutocomplete value={draft.address || ""} onChange={(v) => change("address", v)} placeholder="Start typing an address…" onPicked={detectZone} />
            {zoneHint && (
              <div className="sub" style={{ marginTop: 5, fontWeight: 700 }}>
                {zoneHint === "busy" && "Detecting service zone…"}
                {zoneHint === "z1" && <span style={{ color: "#12A650" }}>🟢 Zone 1 Detected — billing rate set to Zone 1 (adjust under Billing rate if needed)</span>}
                {zoneHint === "z2" && <span style={{ color: "#0887C8" }}>🔵 Zone 2 Detected — billing rate set to Zone 2 (adjust under Billing rate if needed)</span>}
                {zoneHint === "ask" && <span style={{ color: "#B36B00" }}>🟠 Outside Zones 1 & 2 ({zoneKm} km from the shop) — check with service whether we book this area; the schedule will ask again</span>}
                {zoneHint === "never" && <span style={{ color: "#B31212" }}>🔴 Beyond the {SERVICE_LIMIT_KM} km service limit ({zoneKm} km away) — service can't be booked here; retail only</span>}
                {zoneHint === "none" && <span style={{ color: "var(--muted)" }}>Couldn't place that address on the map — pick the zone under Billing rate.</span>}
              </div>
            )}
          </div>
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={draft.billingSame} onChange={(e) => change("billingSame", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Bill to the service address
            </label>
          </div>
          <div className="f-wide">
            <label>Linked properties — same owner ({(draft.linkedIds || []).length})</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {(draft.linkedIds || []).map((id) => {
                const lc = allClients.find((c) => c.id === id);
                return (
                  <span key={id} className="chip" data-ch="email" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {lc ? lc.name : "?"}
                    <button type="button" style={{ border: 0, background: "transparent", cursor: "pointer", fontWeight: 800, color: "inherit" }}
                      onClick={() => change("linkedIds", (draft.linkedIds || []).filter((x) => x !== id))}>✕</button>
                  </span>
                );
              })}
              <select value="" style={{ width: "auto", minWidth: 180 }}
                onChange={(e) => { if (e.target.value) change("linkedIds", [...(draft.linkedIds || []), e.target.value]); e.target.value = ""; }}>
                <option value="">+ link another property…</option>
                {allClients.filter((c) => c.id !== draft.id && !(draft.linkedIds || []).includes(c.id))
                  .slice().sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          {!draft.billingSame && (
            <div className="f-wide">
              <label>Billing address</label>
              <textarea value={draft.billingAddress} onChange={(e) => change("billingAddress", e.target.value)}
                placeholder="Attention, company, street, city, postal code" />
            </div>
          )}
          <div>
            <label>Account type</label>
            <select value={draft.type} onChange={(e) => change("type", e.target.value)}>
              <option value="service">Service</option><option value="retail">Retail</option>
              <option value="both">Retail + Service</option>
            </select>
          </div>
          <div className="f-wide">
            <div className="autocharge" data-on={draft.trade ? 1 : 0} style={{ marginTop: 0, marginBottom: 4 }}>
              <button type="button" className="switch" data-on={draft.trade ? 1 : 0} aria-pressed={!!draft.trade}
                onClick={() => change("trade", !draft.trade)}>
                <span className="dot" />{draft.trade ? "Trades account" : "Trades account — off"}
              </button>
              <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, flex: 1, minWidth: 180 }}>
                {draft.trade
                  ? "Counter pickup account. Sales count toward gross but report as trade — no route, structure, equipment or complexity to fill in."
                  : "Product sales on this account report as retail."}
              </div>
            </div>
          </div>
          {(isMgr || isOwner) && (
          <div className="f-wide">
            <div className="autocharge" data-on={draft.giftedService ? 1 : 0} style={{ marginTop: 0, marginBottom: 4 }}>
              <button type="button" className="switch" data-on={draft.giftedService ? 1 : 0} aria-pressed={!!draft.giftedService}
                onClick={() => change("giftedService", !draft.giftedService)}>
                <span className="dot" />{draft.giftedService ? "Gifted account" : "Gifted account — off"}
              </button>
              <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, flex: 1, minWidth: 180 }}>
                {draft.giftedService
                  ? "We service this account free of charge. Visits default to gifted (documented, not billed). Only owners and managers can change this."
                  : "Mark the whole account as gifted — every visit defaults to no charge. Staff can still log a one-off gifted visit on a normal account."}
              </div>
            </div>
          </div>
          )}
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={!!draft.waterTesting} onChange={(e) => change("waterTesting", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Water testing customer
            </label>
            <div className="sub">Tests are logged from the client's page, under Account.</div>
          </div>
          <div className="f-wide">
            <label>Softub customer</label>
            <div className="seg" style={{ margin: "4px 0 0" }}>
              {[["auto", "Auto-detect"], ["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
                <button key={v} type="button" data-on={(draft.softub === true ? "yes" : draft.softub === false ? "no" : "auto") === v ? 1 : 0}
                  onClick={() => change("softub", v === "auto" ? undefined : v === "yes")}>{l}</button>
              ))}
            </div>
            <div className="sub">Auto-detect looks at Softub purchases and repair tickets. Used for Softub promotions.</div>
          </div>
        </div>

        <div className="sechead">
          Contacts
          <button className="btn ghost mini" onClick={addContact}>Add contact</button>
        </div>
        {draft.contacts.map((k, i) => (
          <div key={k.id} className={"contact" + (i === 0 ? " primary" : "")}>
            <div className="ctop">
              <span className="ctag">{i === 0 ? "Primary contact" : "Alternate contact"}</span>
              <div className="btnrow">
                {i !== 0 && <button className="btn ghost mini" onClick={() => makePrimary(k.id)}>Make primary</button>}
                {draft.contacts.length > 1 && <button className="btn warn mini" onClick={() => dropContact(k.id)}>Remove</button>}
              </div>
            </div>
            <div className="fields">
              <div><label>First name</label><input value={k.firstName ?? (String(k.name || "").trim().split(/\s+/)[0] || "")} onChange={(e) => setContactName(k.id, "first", e.target.value)} /></div>
              <div><label>Last name</label><input value={k.lastName ?? String(k.name || "").trim().split(/\s+/).slice(1).join(" ")} onChange={(e) => setContactName(k.id, "last", e.target.value)} /></div>
              <div><label>Relationship / role</label><input value={k.role} placeholder="Owner, spouse, property manager" onChange={(e) => setContact(k.id, "role", e.target.value)} /></div>
              <div><label>Phone</label><input value={k.phone} onChange={(e) => setContact(k.id, "phone", e.target.value)} /></div>
              <div><label>Email</label><input value={k.email} onChange={(e) => setContact(k.id, "email", e.target.value)} /></div>
              <div className="f-wide" style={{ marginTop: 4 }}>
                <label style={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, fontSize: 10.5 }}>
                  How this contact hears from us
                </label>
              </div>
              {COMMS.map(({ k: ck, label }) => (
                <div key={ck}>
                  <label>{label}</label>
                  <select value={(k.comms && k.comms[ck]) || "none"} onChange={(e) => setComm(k.id, ck, e.target.value)}>
                    {CHANNELS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </div>
              ))}
              <div>
                <label>Promotional emails</label>
                <select value={k.promos || "auto"} onChange={(e) => setPromoPref(k.id, e.target.value)}>
                  <option value="auto">Automatic — yes while an active customer</option>
                  <option value="yes">Yes</option>
                  <option value="no">No — never</option>
                </select>
                {(k.promosLog || []).length > 0 && (
                  <div className="sub" style={{ marginTop: 3 }}>
                    Last changed {new Date(k.promosLog[k.promosLog.length - 1].at).toLocaleDateString()} by {k.promosLog[k.promosLog.length - 1].by}
                    {k.promosLog[k.promosLog.length - 1].how === "unsubscribe link" ? " (customer unsubscribed)" : ""}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {!draft.trade && <>
        {draft.type !== "retail" && <>
        <div className="sechead">Route</div>
        <label>How often they're serviced</label>
        <div className="freqrow">
          {FREQS.map((f) => (
            <button key={f.v} type="button" data-on={draft.frequency === f.v ? 1 : 0}
              onClick={() => setDraft((x) => ({ ...x, frequency: f.v, days: capServiceDays(clientDays(x), f.v) }))}>{f.label}</button>
          ))}
        </div>
        <label>Which day{freqWant(draft.frequency) > 1 ? "s" : ""}</label>
        <div className="dayline">
          {DAYS.map((d) => {
            const on = clientDays(draft).includes(d);
            return (
              <button key={d} type="button" className="daypill pick" data-on={on ? 1 : 0}
                disabled={!on && draft.frequency === "twice" && clientDays(draft).length >= 2}
                onClick={() => setDraft((x) => {
                  const cur = clientDays(x);
                  if (on) return { ...x, days: cur.filter((y) => y !== d) };
                  if (!canPickServiceDay(cur, x.frequency, d, false)) return x;
                  return { ...x, days: [...cur, d] };
                })}>
                <div className="dd">{d}</div>
              </button>
            );
          })}
        </div>
        <div className="sub" style={{ marginTop: 8 }}>
          {clientDays(draft).length === 0
            ? "Pick at least one day or they won't appear on the week."
            : freqWant(draft.frequency) && clientDays(draft).length !== freqWant(draft.frequency)
              ? `${FREQ_LABEL[draft.frequency]} usually means ${freqWant(draft.frequency)} day${freqWant(draft.frequency) === 1 ? "" : "s"} — you've picked ${clientDays(draft).length}.`
              : `${FREQ_LABEL[draft.frequency]} on ${daysSentence(draft)}.`}
        </div>
        {draft.frequency === "biweekly" && (
          <div className="stackpanel">
            <div className="ptitle">Which week</div>
            <F draft={draft} change={change} k="anchorDate" label="A date they were serviced (YYYY-MM-DD)" wide />
            <div className="sub" style={{ marginTop: 6 }}>
              {parseDate(draft.anchorDate)
                ? `Every other week from there — next visit ${nextDue(draft) ? fmtDate(nextDue(draft)) : "—"}.`
                : "Without this, they'll show up every week on the board."}
            </div>
          </div>
        )}

        {draft.frequency === "monthly" && (
          <div className="stackpanel">
            <div className="ptitle">Which day of the month</div>
            <div className="seg">
              <button type="button" data-on={draft.monthlyMode !== "date" ? 1 : 0} onClick={() => change("monthlyMode", "nth")}>
                A weekday of the month
              </button>
              <button type="button" data-on={draft.monthlyMode === "date" ? 1 : 0} onClick={() => change("monthlyMode", "date")}>
                A fixed date
              </button>
            </div>
            {draft.monthlyMode === "date" ? (
              <div className="fields">
                <div>
                  <label>Day of the month</label>
                  <input type="number" min="1" max="28" value={draft.monthlyDate}
                    onChange={(e) => change("monthlyDate", Math.max(1, Math.min(28, Number(e.target.value) || 1)))} />
                </div>
                <div className="f-wide sub">Keep it 1–28 so every month has that date. If it lands on a Sunday it shows on the Monday.</div>
              </div>
            ) : (
              <div className="fields">
                <div>
                  <label>Which one</label>
                  <select value={draft.monthlyNth} onChange={(e) => change("monthlyNth", Number(e.target.value))}>
                    {NTHS.map((n) => <option key={n.v} value={n.v}>{n.label}</option>)}
                  </select>
                </div>
                <div className="f-wide sub">{cadenceLine(draft)}. Next visit {nextDue(draft) ? fmtDate(nextDue(draft)) : "—"}.</div>
              </div>
            )}
          </div>
        )}

        <div className="fields" style={{ marginTop: 11 }}>
          <F draft={draft} change={change} k="order" label="Stop order on route" type="number" />
          <div>
            <label>Assigned technician</label>
            <select value={draft.techId || ""} onChange={(e) => change("techId", e.target.value)}>
              <option value="">Not assigned</option>
              {(techs || DEFAULT_TECHS).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {draft.frequency === "short" && <F draft={draft} change={change} k="shortTermUntil" label="Short term through (YYYY-MM-DD)" />}
        </div>

        <div className="sechead">Property complexity</div>
        <div className="sub" style={{ marginBottom: 8 }}>This is the PROPERTY structure (skimmers, returns, water features, extras) — not the person. Used to choose which tech to schedule.</div>
        <div className="fields">
          <div className="f-wide">
            <label>How the score is set</label>
            <select value={draft.complexityMode} onChange={(e) => change("complexityMode", e.target.value)}>
              <option value="auto">Calculate from the pool details above</option>
              <option value="manual">Set it by hand</option>
            </select>
          </div>
          {draft.complexityMode === "manual" ? (
            <div className="f-wide">
              <label>Property complexity — 1 is a routine structure, 10 needs your strongest tech</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input type="range" min="1" max="10" step="1" value={draft.complexity}
                  onChange={(e) => change("complexity", Number(e.target.value))} style={{ flex: 1, padding: 0 }} />
                <Complexity n={Number(draft.complexity)} showWord />
              </div>
              <div className="sub">Calculated score would be {sp.score}/10.</div>
            </div>
          ) : (
            <div className="f-wide">
              <div style={{ marginBottom: 8 }}><Complexity n={sp.score} showWord /></div>
              <div className="breakdown">
                {sp.parts.map((p, i) => (
                  <div key={i} className="brow"><span>{p.label}</span><b>+{p.pts}</b></div>
                ))}
                <div className="brow" style={{ fontWeight: 700 }}><span>Total</span><b>{sp.raw} → {sp.score}/10</b></div>
              </div>
              <div className="sub">Updates on its own as you change the counts or equipment above.</div>
            </div>
          )}
        </div>
        </>}

        <div className="sechead">
          {displayTitleCase("Structure")}
          <span className="ctag">Set once</span>
        </div>
        <label>Structure type</label>
        <select value={draft.structure || "pool"}
          onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, structure: v,
            spaSeparate: canSplitWater(v) ? d.spaSeparate : false })); }}>
          {STRUCTURES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>

        <div className="stackpanel">
          <div className="ptitle">{displayTitleCase("The water")}</div>
          {canSplitWater(draft.structure) && (
            <div className="seg">
              <button type="button" data-on={!draft.spaSeparate ? 1 : 0} onClick={() => change("spaSeparate", false)}>
                One body of water
              </button>
              <button type="button" data-on={draft.spaSeparate ? 1 : 0} onClick={() => change("spaSeparate", true)}>
                Spa is separate
              </button>
            </div>
          )}
          <div className="fields">
            <div className="f-wide">
              <label>Structure / volume presets</label>
              <select value={VOLUME_PRESETS.some((p) => p.gallons && String(p.gallons) === String(draft.gallons || "").replace(/,/g, "")) ? VOLUME_PRESETS.find((p) => p.gallons && String(p.gallons) === String(draft.gallons || "").replace(/,/g, "")).v : (draft.gallons ? "custom" : "")}
                onChange={(e) => {
                  const r = gallonsFromVolumePreset(e.target.value);
                  if (r.custom) { /* keep current; show calculator below */ change("gallons", draft.gallons || ""); }
                  else change("gallons", r.gallons);
                }}>
                <option value="">Pick a standard size…</option>
                {VOLUME_PRESETS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
              <div className="sub">Standard sizes and volumes, or Custom volume calculator below.</div>
            </div>
            <VolumePair label={draft.spaSeparate ? "Pool volume" : "Water volume (custom calculator)"} gallons={draft.gallons} onChange={(v) => change("gallons", v)} />
            <VolumeCalcPanel onApply={(g) => change("gallons", g)} />
            <div>
              <label>Type of sanitizer</label>
              <select value={draft.sanitizerType} onChange={(e) => change("sanitizerType", e.target.value)}>
                <option value="">Not set</option>
                {SANITIZERS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            {needsSaltSystemType(draft.sanitizerType) && (
              <div>
                <label>Salt system type</label>
                <select value={draft.saltSystemType || ""} onChange={(e) => change("saltSystemType", e.target.value)}>
                  <option value="">Not set</option>
                  {SALT_SYSTEM_TYPES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
            )}
            {draft.spaSeparate && <VolumePair label="Spa volume" gallons={draft.spaGallons} onChange={(v) => change("spaGallons", v)} />}
            <F draft={draft} change={change} k="surface" label="Surface / construction" wide={!draft.spaSeparate} placeholder="Gunite, vinyl, fiberglass" />
            {draft.spaSeparate && (
              <div className="f-wide">
                <label>Spa notes</label>
                <textarea value={draft.spaNotes || ""} onChange={(e) => change("spaNotes", e.target.value)}
                  placeholder="Its own pump, heater or sanitizer — anything balanced separately from the pool." />
              </div>
            )}
          </div>
          {canSplitWater(draft.structure) && !draft.spaSeparate && (
            <div className="sub" style={{ marginTop: 8 }}>A spillover spa that shares the pool's water counts as one volume.</div>
          )}
        </div>

        <div className="stackpanel">
          <div className="ptitle">Cover</div>
          <div className="sub" style={{ marginBottom: 6 }}>Leaf net combines with a tarp or snap-in — tap both and both light up.</div>
          <Picker options={COVERS} value={draft.coverType || "none"} Glyph={CoverGlyph}
            isOn={(v) => v === (draft.coverType || "none") || (v === "leafnet" && !!draft.coverLeafNet)}
            onChange={(v) => setDraft((d) => {
              const cur = d.coverType || "none";
              // Leaf net rides along with tarp/snap-in; tapping it toggles the combo.
              if (v === "leafnet") {
                if (canAddLeafNet(cur)) return { ...d, coverLeafNet: !d.coverLeafNet };
                return { ...d, coverType: cur === "leafnet" ? "none" : "leafnet", coverLeafNet: false };
              }
              // Picking tarp/snap-in while Leaf net is the cover keeps the net as the add-on.
              if (canAddLeafNet(v) && cur === "leafnet") return { ...d, coverType: v, coverLeafNet: true };
              return { ...d, coverType: v, coverLeafNet: canAddLeafNet(v) ? d.coverLeafNet : false };
            })} />
          {(canAddLeafNet(draft.coverType) || draft.coverLeafNet) && (
            <div className="leafrow">
              <label>
                <input type="checkbox" checked={!!draft.coverLeafNet} onChange={(e) => change("coverLeafNet", e.target.checked)}
                  style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                They use a leaf net as well
              </label>
            </div>
          )}
        </div>

        <div className="sechead">{displayTitleCase("Counts")}</div>
        <div className="counts">
          {FEATURES.map(({ k, label }) => (
            <div key={k}>
              <label>{label}</label>
              <input type="number" min="0" value={(draft.features || defFeatures())[k] ?? 0}
                onChange={(e) => setFeature(k, e.target.value)} />
            </div>
          ))}
        </div>

        <div className="sechead">{displayTitleCase("Equipment")}</div>
        <div className="sub" style={{ marginBottom: 8 }}>You can add more than one pump, filter, cleaner, or general equipment.</div>
        <div className="btnrow" style={{ marginBottom: 8, flexWrap: "wrap" }}>
          {["pump", "filter", "cleaner", "other"].map((k) => (
            <button type="button" key={k} className="btn ghost mini"
              onClick={() => setDraft((d) => ({ ...d, equipmentList: addEquipmentOfKind(d.equipmentList, k) }))}>
              + {EQUIP_LABEL[k] || k}
            </button>
          ))}
        </div>
        {(draft.equipmentList || []).map((e) => (
          <div key={e.id} className="eqrow edit">
            <span className="ico"><EquipGlyph v={e.kind} size={26} /></span>
            <div className="grow">
              <select value={e.kind} onChange={(ev) => setEquip(e.id, "kind", ev.target.value)}>
                {EQUIP_KINDS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <input value={e.detail} placeholder={EQUIP_HINT[e.kind] || "Make, model, anything a tech needs"}
                onChange={(ev) => setEquip(e.id, "detail", ev.target.value)} />
              <div style={{ display: "flex", gap: 7, marginTop: 6 }}>
                <input value={e.serial || ""} placeholder="Serial #" style={{ flex: 1 }} onChange={(ev) => setEquip(e.id, "serial", ev.target.value)} />
                <input value={e.installed || ""} placeholder="Installed (e.g. 2021)" style={{ flex: 1 }} onChange={(ev) => setEquip(e.id, "installed", ev.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 7, marginTop: 6, flexWrap: "wrap" }}>
                <input value={e.timerOn || ""} placeholder="Timer on (e.g. 8:00 AM)" style={{ flex: 1, minWidth: 120 }}
                  onChange={(ev) => setEquip(e.id, "timerOn", ev.target.value)} />
                <input value={e.timerOff || ""} placeholder="Timer off (e.g. 4:00 PM)" style={{ flex: 1, minWidth: 120 }}
                  onChange={(ev) => setEquip(e.id, "timerOff", ev.target.value)} />
              </div>
              <input value={e.timerNote || ""} placeholder="Note if times change…" style={{ marginTop: 6 }}
                onChange={(ev) => setEquip(e.id, "timerNote", ev.target.value)} />
            </div>
            <button type="button" className="btn warn mini" onClick={() => dropEquip(e.id)}>Remove</button>
          </div>
        ))}
        <button type="button" className="addrow" onClick={addEquip}>
          <span className="plus">+</span> Add equipment
        </button>

        {draft.type !== "retail" && <>
        <div className="sechead">{displayTitleCase("Access")}</div>
        <div className="fields">
          <F draft={draft} change={change} k="gateCode" label="Gate code" placeholder="4417#" />
          <F draft={draft} change={change} k="keyLocation" label="Key / entry point" placeholder="Lockbox on north fence post" />
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={draft.dog} onChange={(e) => change("dog", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Dog on property
            </label>
          </div>
          <div className="f-wide">
            <label>Access notes</label>
            <textarea value={draft.accessNotes} onChange={(e) => change("accessNotes", e.target.value)}
              placeholder="Side gate only. Close the gate behind you." />
          </div>
        </div>
        </>}

        </>}

        <div className="sechead">{displayTitleCase("Account notes")}</div>
        <textarea value={draft.notes} onChange={(e) => change("notes", e.target.value)}
          placeholder="Open quotes, recurring problems, what they buy in-store." />

        {can("standing") && <>
        <div className="sechead">{displayTitleCase("Account standing")}</div>
        <div className="fields">
          <div>
            <label>Refunds</label>
            <input type="number" min="0" value={(draft.standing || defStanding()).refunds}
              onChange={(e) => setStanding("refunds", e.target.value)} />
          </div>
          <div>
            <label>Exchanges</label>
            <input type="number" min="0" value={(draft.standing || defStanding()).exchanges}
              onChange={(e) => setStanding("exchanges", e.target.value)} />
          </div>
          <div>
            <label>Documented complaints (count)</label>
            <input type="number" min="0" value={complaintCount(draft.complaints, (draft.standing || defStanding()).complaints)}
              onChange={(e) => setStanding("complaints", e.target.value)} />
          </div>
          <div className="f-wide">
            <label>{displayTitleCase("Documented complaints")}</label>
            <div className="sub">Valid / Not valid · Customer-created issue or Service issue — stored on the account.</div>
            {(draft.complaints || []).map((c) => (
              <div key={c.id} style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", marginTop: 6 }}>
                <div style={{ fontWeight: 600 }}>{c.text}</div>
                <div className="sub" style={{ marginTop: 2 }}>
                  {(COMPLAINT_VALIDITY.find((x) => x.v === c.validity) || {}).label || c.validity}
                  {" · "}
                  {(COMPLAINT_SOURCE.find((x) => x.v === c.source) || {}).label || c.source}
                </div>
              </div>
            ))}
            <ComplaintAddRow onAdd={(entry) => {
              const r = storeComplaint(draft.complaints || [], entry);
              if (!r.ok) return;
              setDraft((d) => ({
                ...d,
                complaints: r.list,
                standing: { ...defStanding(), ...(d.standing || {}), complaints: r.list.length },
              }));
            }} />
          </div>
          <div className="f-wide sub">
            Materials cost, materials price, labour and early bookings come off each logged visit — they're
            entered in the service history, not here.
          </div>
        </div>

        </>}

        {can("pricing") && <>
        <div className="sechead">
          Billing — how they pay
          <button className="btn ghost mini" onClick={onManageTiers}>Manage pricing tiers</button>
        </div>
        <div className="fields">
          <div><label>Account number</label><input value={bill.acctNo} placeholder="H-1042" onChange={(e) => setBill("acctNo", e.target.value)} /></div>
          <div>
            <label>Billing cycle</label>
            <select value={bill.cycle} onChange={(e) => setBill("cycle", e.target.value)}>
              {CYCLES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label>Payment terms</label>
            <select value={bill.terms} onChange={(e) => setBill("terms", e.target.value)}>
              {TERMS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label>Customer pays by</label>
            <select value={bill.payMethod} onChange={(e) => setBill("payMethod", e.target.value)}>
              {PAY_METHODS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={bill.paperInvoice} onChange={(e) => setBill("paperInvoice", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Mail a paper invoice to the billing address
            </label>
          </div>
        </div>

        <div className="autocharge" data-on={bill.autoCharge ? 1 : 0} style={{ marginTop: 11 }}>
          <button type="button" className="switch" data-on={bill.autoCharge ? 1 : 0}
            aria-pressed={bill.autoCharge} disabled={!bill.autoCharge && !hasCard(bill)}
            onClick={() => setBill("autoCharge", !bill.autoCharge)}>
            <span className="dot" />{bill.autoCharge ? "Auto charge on" : "Auto charge off"}
          </button>
          <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, flex: 1, minWidth: 170 }}>
            {!hasCard(bill) && !bill.autoCharge
              ? "Fill in the card on file below to switch this on."
              : bill.autoCharge && !hasCard(bill)
                ? "On, but no card is saved yet — fill in the card below."
                : bill.autoCharge
                  ? "Charges the card on file " + (bill.cycle === "monthly" ? "once a month." : "after each visit.")
                  : "Off — invoices go out for manual payment."}
          </div>
          {bill.autoCharge && bill.cycle === "monthly" && (
            <div style={{ width: 120 }}>
              <label>Charge on day</label>
              <input type="number" min="1" max="28" value={bill.chargeDay}
                onChange={(e) => setBill("chargeDay", Math.max(1, Math.min(28, Number(e.target.value) || 1)))} />
            </div>
          )}
        </div>

        <div className="cardbox">
          <div className="fields">
            <div><label>Cardholder name</label><input value={bill.card.holder} onChange={(e) => setCard("holder", e.target.value)} /></div>
            <div>
              <label>Card type</label>
              <select value={bill.card.brand} onChange={(e) => setCard("brand", e.target.value)}>
                {CARD_BRANDS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div><label>Last 4 digits</label><input value={bill.card.last4} maxLength={4} placeholder="4412" onChange={(e) => setCard("last4", e.target.value.replace(/[^0-9]/g, ""))} /></div>
            <div><label>Expiry (MM/YY)</label><input value={bill.card.exp} placeholder="09/28" onChange={(e) => setCard("exp", e.target.value)} /></div>
            <div className="f-wide"><label>Processor reference / token</label>
              <input value={bill.card.ref} placeholder="cus_9F2K71 — the saved-card ID from your payment processor"
                onChange={(e) => setCard("ref", e.target.value)} /></div>
          </div>
          <div className="notice">
            Don't type the full card number here. Save the card with your payment processor and keep its
            reference — that's what authorizes the charge, and it keeps card data out of this app where it
            would be your liability to protect.
          </div>
        </div>

        {draft.type !== "retail" ? (<>
          <div className="sechead">Billing rate</div>
          <div className="seg" style={{ marginBottom: 6 }}>
            <button type="button" data-on={(bill.zone || "z1") === "z1" ? 1 : 0} onClick={() => setBill("zone", "z1")}>Zone 1</button>
            <button type="button" data-on={bill.zone === "z2" ? 1 : 0} onClick={() => setBill("zone", "z2")}>Zone 2</button>
          </div>
          <div className="sub" style={{ marginBottom: 6 }}>
            Service and labour prices come from the catalogue — every service there carries a Zone 1 and a Zone 2 rate,
            and this choice decides which one their booked services bill at.
          </div>
        </>) : (
          <div className="sub" style={{ margin: "6px 0" }}>Retail account — a billing zone gets picked if they ever add service.</div>
        )}

        <div className="sechead">
          Discounts
          <button className="btn ghost mini" onClick={onManageTiers}>Manage levels</button>
        </div>
        <div className="seg" style={{ marginBottom: 11, flexWrap: "wrap" }}>
          <button type="button" data-on={(bill.discountMode || "level") === "level" ? 1 : 0}
            onClick={() => setBill("discountMode", "level")}>By customer level</button>
          <button type="button" data-on={bill.discountMode === "custom" ? 1 : 0}
            onClick={() => setBill("discountMode", "custom")}>Set each one</button>
          <button type="button" data-on={spOpen ? 1 : 0} onClick={() => setSpOpen(!spOpen)}>
            Liquid Special pricing{(bill.specialPrices || []).length ? ` (${bill.specialPrices.length})` : ""}
          </button>
        </div>

        {spOpen && (
          <div className="stackpanel" style={{ marginBottom: 12 }}>
            <div className="ptitle">Special pricing — this customer only</div>
            <div className="sub" style={{ marginBottom: 8 }}>
              Set a fixed price on any chemical or material — 10L / 20L fills, bulk LC per litre, anything in the
              catalogue. A price here <b>supersedes every other discount</b> on that item for this customer.
            </div>
            {(bill.specialPrices || []).map((x) => (
              <div key={x.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
                <span style={{ flex: 1, fontWeight: 650, fontSize: 13 }}>{x.name || x.code}<span className="sub" style={{ display: "inline", marginLeft: 6 }}>{x.code}</span></span>
                <span className="sub" style={{ textDecoration: "line-through" }}>
                  {(() => { const p = parts.find((pp) => (pp.code || "").trim().toLowerCase() === String(x.code).trim().toLowerCase()); return p && num(p.price) ? "$" + num(p.price).toFixed(2) : ""; })()}
                </span>
                <input inputMode="decimal" value={x.price} style={{ width: 86 }} placeholder="$"
                  onChange={(e) => setBill("specialPrices", (bill.specialPrices || []).map((y) => (y.id === x.id ? { ...y, price: e.target.value } : y)))} />
                <button type="button" className="btn warn mini"
                  onClick={() => setBill("specialPrices", (bill.specialPrices || []).filter((y) => y.id !== x.id))}>✕</button>
              </div>
            ))}
            <div style={{ position: "relative", marginTop: 8 }}>
              <input placeholder="Search the catalogue — name or part #, e.g. 10L chlorine" value={spSearch}
                onChange={(e) => setSpSearch(e.target.value)} />
              {spSearch.trim() && (
                <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 220 }}>
                  {parts.filter((p) => p.active !== false && !(bill.specialPrices || []).some((y) => String(y.code || "").toLowerCase() === String(p.code || "").toLowerCase()) &&
                    [p.code, p.name, p.category, p.subcategory].join(" ").toLowerCase().includes(spSearch.trim().toLowerCase()))
                    .slice(0, 8).map((p) => (
                      <button key={p.id} type="button" className="poslookupitem"
                        onClick={() => {
                          setBill("specialPrices", [...(bill.specialPrices || []), {
                            id: cid(), code: p.code || "", name: p.name || "", price: p.price || "",
                            by: me ? me.name : "", date: new Date().toISOString().slice(0, 10),
                          }]);
                          setSpSearch("");
                        }}>
                        <b>{p.name}</b>
                        <span className="sub" style={{ marginTop: 0 }}>{p.code ? p.code + " · " : ""}list {posMoney(p.price)}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            {(bill.specialPrices || []).some((x) => x.by) && (
              <div className="sub" style={{ marginTop: 7 }}>
                {(bill.specialPrices || []).filter((x) => x.by).map((x) => `${x.name || x.code}: set by ${x.by} ${x.date || ""}`).join(" · ")}
              </div>
            )}
          </div>
        )}

        {(bill.discountMode || "level") === "level" ? (
          <>
            <div className="lvlpick">
              {(tiers || DEFAULT_LEVELS).map((l, i) => (
                <button key={l.id} type="button" className="lvl" data-on={bill.levelId === l.id ? 1 : 0}
                  onClick={() => setBill("levelId", l.id)}>
                  <span className="lvlrank">{i + 1}</span>
                  <span className="lvlname">{l.name}</span>
                  <span className="lvlcols">
                    {CATS.map(({ k, label }) => (
                      <span key={k} className="lvlcol">
                        <span className="lvlpct">{l[k]}%</span>
                        <span className="lvlcap">{label}</span>
                      </span>
                    ))}
                  </span>
                </button>
              ))}
            </div>
            <div className="sub" style={{ marginTop: 8 }}>{levelOf(tiers, bill.levelId).note}</div>
          </>
        ) : (
          <div className="fields">
            {CATS.map(({ k, label }) => (
              <div key={k}>
                <label>{label} discount (%)</label>
                <input type="number" min="0" max="100" value={bill.discounts[k] ?? 0}
                  onChange={(e) => setDiscount(k, e.target.value)} />
              </div>
            ))}
            <div className="f-wide sub">Set on this account only — the customer levels are left alone.</div>
          </div>
        )}

        {!draft.trade && <>
        <div className="sechead">Season & contract</div>
        <div className="fields">
          <div><label>Opened in {new Date().getFullYear() - 1} (date last opened)</label><input value={bill.seasonStart} placeholder={`${new Date().getFullYear() - 1}-05-01`} onChange={(e) => setBill("seasonStart", e.target.value)} /></div>
          <div><label>Closed in {new Date().getFullYear() - 1} (date last closed)</label><input value={bill.seasonEnd} placeholder={`${new Date().getFullYear() - 1}-10-15`} onChange={(e) => setBill("seasonEnd", e.target.value)} /></div>
          <div><label>Contract total</label><input value={bill.contractTotal} placeholder="1295" onChange={(e) => setBill("contractTotal", e.target.value)} /></div>
          <div><label>Spread over</label><input value={bill.instalments} placeholder="6 monthly payments" onChange={(e) => setBill("instalments", e.target.value)} /></div>
        </div>

        </>}

        <div className="sechead">Tax & account standing</div>
        <div className="fields">
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={bill.taxable} onChange={(e) => setBill("taxable", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Charge tax on this account
            </label>
          </div>
          {!bill.taxable && (
            <div className="f-wide"><label>Exemption / resale number</label>
              <input value={bill.taxExemptNo} onChange={(e) => setBill("taxExemptNo", e.target.value)} /></div>
          )}
          <div className="f-wide" style={{ background: "#F0F9FF", border: "1px solid #BBDDF2", borderRadius: 8, padding: "9px 11px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: "#0B4F8A", marginBottom: 6 }}>First Nations HST Rebate (-8%)</div>
            <label style={{ marginBottom: 0 }}>
              <input type="checkbox" checked={!!draft.statusExempt} onChange={(e) => change("statusExempt", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              <b>Enable First Nations HST Rebate</b> — bills 5% GST instead of 13% HST on every sale and invoice (Ontario point-of-sale relief).
              <span style={{ display: "block", marginTop: 4, color: "#8A5A00", fontWeight: 650 }}>Check the customer's Status Card (photo ID) before enabling — the till asks again at every rebate checkout.</span>
            </label>
          </div>
          {draft.statusExempt && (<>
            <div><label>Registration Number (or Status Card Number)</label>
              <input value={draft.statusCardNo || ""} placeholder="registry number on the card" onChange={(e) => change("statusCardNo", e.target.value)} /></div>
            <div className="f-wide">
              <label>Upload Certificate of Indian Status (Status Card) — audit evidence for the reduced rate</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {draft.statusCardImage
                  ? <img src={draft.statusCardImage} alt="Status card" style={{ width: 104, height: 66, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />
                  : <div style={{ width: 104, height: 66, borderRadius: 8, background: "var(--card)", border: "1px dashed var(--line)" }} />}
                <label className="btn ghost mini" style={{ cursor: "pointer" }}>
                  {draft.statusCardImage ? "Replace photo" : "📷 Add photo"}
                  <input type="file" accept="image/*" style={{ display: "none" }}
                    onChange={async (e) => { const file = e.target.files && e.target.files[0]; if (file) change("statusCardImage", await shrink(file, 700, 0.78)); e.target.value = ""; }} />
                </label>
                {draft.statusCardImage && <button type="button" className="btn warn mini" onClick={() => change("statusCardImage", "")}>Remove</button>}
              </div>
              {!draft.statusCardImage && (
                <div className="sub" style={{ color: "var(--clay)", fontWeight: 650 }}>
                  {isOwner
                    ? "No Status Card photo on file yet — as owner you can still save, but add the photo when you can."
                    : "The rebate can't be saved without a Status Card photo — take one now, or ask the owner to apply it."}
                </div>
              )}
            </div>
          </>)}
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={bill.poRequired} onChange={(e) => setBill("poRequired", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Purchase order number required on invoices
            </label>
          </div>
          {bill.poRequired && (
            <div><label>PO number on file</label><input value={bill.poNumber} onChange={(e) => setBill("poNumber", e.target.value)} /></div>
          )}
          <div><label>Credit limit</label><input value={bill.creditLimit} placeholder="5000" onChange={(e) => setBill("creditLimit", e.target.value)} /></div>
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={bill.onHold} onChange={(e) => setBill("onHold", e.target.checked)}
                style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
              Put this account on hold
            </label>
          </div>
          {bill.onHold && (
            <div className="f-wide"><label>Why it's on hold</label>
              <input value={bill.holdReason} placeholder="Two invoices past 60 days" onChange={(e) => setBill("holdReason", e.target.value)} /></div>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <label>Billing notes</label>
          <textarea value={bill.billingNotes} onChange={(e) => setBill("billingNotes", e.target.value)}
            placeholder="Statement to head office, PO required, holiday shutdown." />
        </div>

        </>}

        <div className="btnrow" style={{ marginTop: 14 }}>
          <button className="btn" data-cobrowse="customers.save" onClick={onSave}>Save client</button>
          <button className="btn ghost" onClick={onCancel}>Cancel</button>
        </div>
        <div className="sub" style={{ marginTop: 10 }}>Photos and service visits are added from the client's page — they save the moment you enter them.</div>
      </div>
    </div>
  );
}

/* ---------- special orders ---------- */
const SO_BADGE = {
  toorder: ["To be ordered", "#B7791F"],
  ordered: ["Ordered", "#2C6EBE"],
  received: ["Received", "#0B7A3B"],
  pickedup: ["Picked up", "#9AA4B2"],
};
const soUsDate = (ymd) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || ""); return m ? `${m[2]}/${m[3]}/${m[1]}` : (ymd || ""); };
const SO_STEPS = ["To be ordered", "Ordered", "Received"];
const SO_LABEL = { toorder: "To be ordered", ordered: "Ordered", received: "Received", pickedup: "Picked up" };
const soStepIndex = (s) => (s === "received" || s === "pickedup") ? 2 : s === "ordered" ? 1 : 0;
const soRank = (s) => (s === "received" ? 0 : s === "ordered" ? 1 : 2); // received on top

// Auto message to the customer, written from the order's current status.
function specialOrderMsg(o) {
  const first = (o.customerName || "").trim().split(/\s+/)[0] || "there";
  const item = `${o.item || "your part"}${num(o.qty) > 1 ? " ×" + o.qty : ""}`;
  if (o.status === "received")
    return `Hi ${first}, good news — your special order (${item}) has arrived and is ready for pickup at Pool Master Canada. See you soon!`;
  if (o.status === "pickedup")
    return `Hi ${first}, thanks for picking up your ${o.item || "order"} — we appreciate your business. — Pool Master Canada`;
  if (o.status === "toorder")
    return `Hi ${first}, we're arranging your special order (${item})${o.supplier ? " through " + o.supplier : ""}. We'll let you know once it's ordered and when it arrives. — Pool Master Canada`;
  return `Hi ${first}, just confirming your special order (${item}) has been placed${o.supplier ? " with " + o.supplier : ""}. We'll let you know the moment it's in. — Pool Master Canada`;
}

function SpecialOrdersView({ specialOrders = [], clients = [], parts = [], me, seed, onSeedDone, onSave, onDelete, onOpenClient, onCompose, onHoldSale, purchaseOrders = [], onSavePOs }) {
  const [form, setForm] = useState(null);
  const [custSearch, setCustSearch] = useState("");
  const [payAsk, setPayAsk] = useState(false); // "Did you take payment yet?" gate on new orders
  const [soPicked, setSoPicked] = useState(null); // inventory item confirmed in stock for this order
  const [screen, setScreen] = useState("start"); // start | waiting | pickedup

  // Live inventory search for the new-order description / part #. Lists IN-STOCK matches to pick
  // from; picking one confirms it with a green banner. Nothing surfaces when nothing's in stock.
  const soMatches = (() => {
    if (!form || soPicked) return [];
    const code = (form.code || "").trim().toLowerCase();
    const item = (form.item || "").trim().toLowerCase();
    if (code.length < 1 && item.length < 2) return [];
    return parts.filter((p) => p.active !== false && num(p.qty) > 0).filter((p) => {
      const name = (p.name || "").toLowerCase(), pc = (p.code || "").toLowerCase();
      return (code && pc.includes(code)) || (item.length >= 2 && (name.includes(item) || item.includes(name)));
    }).slice(0, 6);
  })();

  const today = fmtDate(new Date());
  // Supplier #1 = the most recently used supplier (changeable per order after the fact).
  const defaultSupplier = () => {
    const recent = specialOrders.filter((o) => (o.supplier || "").trim())
      .sort((a, b) => (b.orderedDate || "").localeCompare(a.orderedDate || ""));
    return recent.length ? recent[0].supplier : "SCP";
  };
  const blankOrder = () => ({ id: cid(), customerId: "", customerName: "", item: "", code: "", orderNo: "", internalPO: "", qty: "1",
    supplier: defaultSupplier(), notes: "", status: "toorder", orderedDate: "", receivedDate: "",
    createdBy: me ? me.name : "", createdDate: fmtDate(new Date()), changes: [] });
  const startNew = () => { setForm(blankOrder()); setCustSearch(""); setSoPicked(null); };
  const startEdit = (o) => { setForm({ ...o }); setCustSearch(""); setSoPicked(null); };
  const pickCust = (c) => setForm((f) => ({ ...f, customerId: c.id, customerName: c.name }));

  // A retail sale with an out-of-stock item seeds a pre-filled order (from the POS).
  useEffect(() => {
    if (!seed) return;
    setForm({ ...blankOrder(), ...seed });
    setScreen("status"); setCustSearch(""); setSoPicked(null);
    if (onSeedDone) onSeedDone();
  }, [seed]); // eslint-disable-line

  // Audit trail: record who changed what on every edit.
  const CHANGE_FIELDS = [["item", "Item"], ["code", "Part #"], ["qty", "Qty"], ["supplier", "Supplier"],
    ["orderNo", "Supplier Order #"], ["internalPO", "Internal P.O #"], ["status", "Status"],
    ["orderedDate", "Date ordered"], ["receivedDate", "Date received"], ["notes", "Notes"]];
  const changeEntry = (note) => {
    const now = new Date();
    return { id: cid(), date: fmtDate(now), time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0"), by: me ? me.name : "", note };
  };
  const pushSpecialOrderPo = (o) => {
    if (!onSavePOs) return;
    const { list, created } = upsertSpecialOrderPo(purchaseOrders || [], o, {
      createdBy: me ? me.name : "Special order",
      createdDate: today,
    });
    if (created || list !== purchaseOrders) onSavePOs(list);
    return created;
  };
  const commit = (payMode) => {
    if (!form.item.trim()) return;
    const existing = specialOrders.find((x) => x.id === form.id);
    // New orders stop at the payment question first — payment up front is the rule.
    if (!existing && !form.invoicePaid && !payMode) { setPayAsk(true); return; }
    let next = { ...form };
    if (existing) {
      const diffs = CHANGE_FIELDS
        .filter(([k]) => String(existing[k] ?? "") !== String(form[k] ?? ""))
        .map(([k, label]) => `${label}: ${String(existing[k] ?? "") || "—"} → ${String(form[k] ?? "") || "—"}`);
      if (diffs.length) next.changes = [...(existing.changes || []), changeEntry(diffs.join(" · "))];
    }
    if (!existing && payMode === "paid") {
      next.invoicePaid = true;
      next.invoiceDate = next.invoiceDate || today;
      next.changes = [...(next.changes || []), changeEntry(`Payment taken before ordering — confirmed by ${me ? me.name : "staff"}`)];
    }
    // A brand-new unpaid order gets parked as a held retail sale at the till —
    // payment happens there, and pickup is blocked until it does.
    let held = null;
    if (!existing && !next.invoicePaid && !next.invoiceDate && next.customerId) {
      const part = parts.find((x) => x.code && next.code && x.code.trim().toLowerCase() === next.code.trim().toLowerCase());
      held = {
        id: cid(), at: new Date().toISOString(), mode: "sale", soId: next.id,
        customerId: next.customerId, customerName: next.customerName,
        items: [{ code: next.code || "", desc: next.item, qty: String(num(next.qty) || 1), price: part ? String(part.price || "") : "", disc: "", discType: "$" }],
      };
      next.heldSaleId = held.id;
      next.changes = [...(next.changes || []), changeEntry("Unpaid — parked as a held sale at the till for payment before pickup")];
    }
    // New special order that needs ordering → create a holding P.O.
    if (!existing && (next.status === "toorder" || next.status === "ordered")) {
      const made = pushSpecialOrderPo(next);
      next.changes = [...(next.changes || []), changeEntry(made ? "Purchase order created from special order" : "Linked to holding purchase order")];
    }
    onSave(next, held); setForm(null); setSoPicked(null); setPayAsk(false);
  };
  const advance = (o, status) => {
    const notes = [];
    // Hard rule: no pickup until payment is applied (till or account) — the button is
    // disabled, and this guard backs it up.
    if (status === "pickedup" && !o.invoicePaid) return;
    const upd = { ...o, status };
    if (status === "ordered" && !o.orderedDate) upd.orderedDate = today;
    if (status === "received") { if (!o.orderedDate) upd.orderedDate = today; if (!o.receivedDate) upd.receivedDate = today; }
    if (status === "pickedup") upd.pickedUpAt = new Date().toISOString();
    notes.push(`Status: ${SO_LABEL[o.status] || o.status} → ${SO_LABEL[status] || status}`);
    // When we need to order something, create / merge a holding P.O. for the supplier.
    if (status === "ordered" || (status === "toorder" && o.status !== "toorder")) {
      const made = pushSpecialOrderPo(upd);
      if (made) notes.push("Purchase order created from special order");
      else notes.push("Added to holding purchase order");
    }
    upd.changes = [...(o.changes || []), changeEntry(notes.join(" · "))];
    onSave(upd);
  };
  // Log a contact: every call, text, and email lands in the contact log with date + 24h time.
  const logContact = (o, kind) => {
    const now = new Date();
    const time = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
    const entry = { id: cid(), date: fmtDate(now), time, kind, by: me ? me.name : "" };
    onSave({ ...o, contactLog: [...(o.contactLog || []), entry] });
  };
  const kindLabel = { call: "Call", text: "Text", email: "Email" };
  const archive = (o) => onSave({ ...o, archived: !o.archived });
  const createRGA = (o) => {
    const num = window.prompt("RGA number (Return Goods Authorization):", o.rgaNo || "");
    if (num === null) return;
    onSave({ ...o, rgaNo: num.trim(), rgaDate: num.trim() ? (o.rgaDate || today) : "" });
  };

  const custMatches = custSearch.trim()
    ? clients.filter((c) => {
        const q = custSearch.trim().toLowerCase();
        return [c.name, c.address, ...(c.contacts || []).map((k) => `${k.name} ${k.phone} ${k.email}`)].join(" ").toLowerCase().includes(q);
      }).slice(0, 8)
    : [];
  // The customer's preferred General-communication channel (contact + text/email).
  const generalChannelFor = (cust) => {
    const k = ((cust && cust.contacts) || []).find((x) => x.comms && x.comms.general && x.comms.general !== "none");
    if (!k) return null;
    const ch = k.comms.general;
    if ((ch === "text" || ch === "both") && k.phone) return { kind: "text", to: k.phone, contact: k };
    if ((ch === "email" || ch === "both") && k.email) return { kind: "mail", to: k.email, contact: k };
    return null;
  };
  const nowStamp = () => {
    const now = new Date();
    return { date: fmtDate(now), time: String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0") };
  };

  // Received: move to waiting-for-pickup AND notify the customer per their General preference.
  const markReceived = (o) => {
    const cust = clients.find((c) => c.id === o.customerId);
    const ch = cust ? generalChannelFor(cust) : null;
    const st = nowStamp();
    const entry = ch ? { id: cid(), ...st, kind: ch.kind === "mail" ? "email" : "text", by: me ? me.name : "" } : null;
    onSave({ ...o, status: "received", orderedDate: o.orderedDate || st.date, receivedDate: o.receivedDate || st.date,
      contactLog: [...(o.contactLog || []), ...(entry ? [entry] : [])],
      changes: [...(o.changes || []), changeEntry(`Status: ${SO_LABEL[o.status] || o.status} → Received`)] });
    if (ch) {
      const first = ((cust.contacts || [])[0] || {}).name || "";
      const msg = `Pool Master: Good news${first ? " " + first.split(" ")[0] : ""} — your special order (${o.item}) has arrived and is ready for pickup at the shop. See you soon!`;
      if (ch.kind === "mail") {
        const mail = specialOrderEmailPayload({ ...o, status: "received" }, cust, { body: msg, subject: "Your special order has arrived — Pool Master" });
        if (mail.ok) onCompose(mail.payload);
        else window.alert("Order moved to Waiting for pickup, but email could not be prepared: " + (mail.missing || []).map((m) => m.label).join(", "));
      } else {
        onCompose({ kind: "text", client: cust, to: ch.to, body: ensureSmsBrand(msg) });
      }
    } else {
      // Fallback: still try email path when preference missing but an email exists (prior bug: silent skip).
      const mail = specialOrderEmailPayload({ ...o, status: "received" }, cust);
      if (mail.ok) {
        onCompose(mail.payload);
      } else {
        window.alert("Order moved to Waiting for pickup. No General-communication preference is set on this customer — no notice was prepared.");
      }
    }
  };

  // Weekly nudge while an order waits for pickup.
  const lastTouch = (o) => (o.contactLog || []).map((e) => e.date).sort().slice(-1)[0] || o.receivedDate || "";
  const reminderDue = (o) => {
    const last = lastTouch(o);
    if (!last) return false;
    return (new Date(fmtDate(new Date())) - new Date(last)) / 86400000 >= 7;
  };
  const sendReminder = (o) => {
    const cust = clients.find((c) => c.id === o.customerId);
    const ch = cust ? generalChannelFor(cust) : null;
    if (!ch) { window.alert("No General-communication preference set on this customer."); return; }
    const st = nowStamp();
    onSave({ ...o, contactLog: [...(o.contactLog || []), { id: cid(), ...st, kind: ch.kind === "mail" ? "email" : "text", by: me ? me.name : "" }] });
    const msg = `Pool Master: Friendly reminder — your special order (${o.item}) is still waiting for pickup at the shop.`;
    if (ch.kind === "mail") {
      const mail = specialOrderEmailPayload(o, cust, { body: msg, subject: "Your order is waiting for pickup — Pool Master" });
      if (mail.ok) onCompose(mail.payload);
      else window.alert("Could not prepare email: " + (mail.missing || []).map((m) => m.label).join(", "));
    } else {
      onCompose({ kind: "text", client: cust, to: ch.to, body: ensureSmsBrand(msg) });
    }
  };

  const placedList = specialOrders.filter((o) => (o.status === "toorder" || o.status === "ordered") && !o.archived)
    .sort((a, b) => (soRank(a.status) - soRank(b.status)) || (b.orderedDate || "").localeCompare(a.orderedDate || ""));
  const waitingList = specialOrders.filter((o) => o.status === "received" && !o.archived)
    .sort((a, b) => (b.receivedDate || "").localeCompare(a.receivedDate || ""));
  const active = specialOrders.filter((o) => o.status !== "pickedup" && !o.archived)
    .sort((a, b) => (soRank(a.status) - soRank(b.status)) || (b.orderedDate || "").localeCompare(a.orderedDate || ""));
  const doneList = specialOrders.filter((o) => o.status === "pickedup" && !o.archived)
    .sort((a, b) => (b.pickedUpAt || "").localeCompare(a.pickedUpAt || "")).slice(0, 25);
  const archivedList = specialOrders.filter((o) => o.archived)
    .sort((a, b) => (b.orderedDate || "").localeCompare(a.orderedDate || "")).slice(0, 40);

  const card = (o, done) => {
    const colour = (SO_BADGE[o.status] || SO_BADGE.toorder)[1];
    const cust = clients.find((c) => c.id === o.customerId);
    const k0 = (cust && (cust.contacts || [])[0]) || {};
    const step = soStepIndex(o.status);
    const refs = [];
    if (o.orderNo) refs.push(["Supplier Order #", o.orderNo]);
    if (o.internalPO) refs.push(["Internal P.O #", o.internalPO]);
    if (o.rgaNo) refs.push(["RGA #", o.rgaNo + (o.rgaDate ? " · " + soUsDate(o.rgaDate) : "")]);
    return (
      <div key={o.id} className="visit" style={done ? { opacity: 0.75 } : undefined}>
        {/* header: item + who */}
        <div className="vhead">
          <span className="vdate" style={{ fontSize: 15 }}>{o.item || "Untitled part"}{num(o.qty) > 1 ? ` ×${o.qty}` : ""}</span>
          <div className="vdocs">
            {o.code && <span className="doc">{o.code}</span>}
            {o.invoicePaid
              ? <span className="doc" style={{ background: "#12A650" }}>Item paid ✓</span>
              : <span className="doc unpaidflash">UNPAID</span>}
          </div>
        </div>
        {!o.invoicePaid && (
          <div className="sub" style={{ marginTop: 2, color: "#B31212", fontWeight: 700 }}>
            Must be paid before pickup{o.heldSaleId ? " — waiting as a held sale at the till" : ""}.
          </div>
        )}
        {(o.contactLog || []).length > 0 && (() => {
          const e = o.contactLog[o.contactLog.length - 1];
          return (
            <div className="sub" style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 7 }}>
              Last communication:
              <span className="doc" style={{ background: e.kind === "call" ? "#2C6EBE" : e.kind === "text" ? "#0B7A3B" : "#7A4FB6" }}>{kindLabel[e.kind] || e.kind}</span>
              <span className="mono">{soUsDate(e.date)}{e.time ? " " + e.time : ""}</span>
              {e.by && <span>· {e.by}</span>}
            </div>
          );
        })()}
        <div className="sub" style={{ marginTop: 2 }}>
          {cust ? <button className="link" onClick={() => onOpenClient(o.customerId)}>{o.customerName}</button> : (o.customerName || "No customer")}
          {o.supplier ? ` · ${o.supplier}` : ""}
        </div>

        {/* status */}
        <div className="statuswrap" style={{ margin: "12px 0 5px" }}>
          <div className="statusbar"><span style={{ width: (step / 2) * 100 + "%", background: colour }} /></div>
          <div className="statussteps">
            {SO_STEPS.map((lab, i) => <span key={lab} data-on={i <= step ? 1 : 0} style={i === step ? { color: colour } : undefined}>{lab}</span>)}
          </div>
        </div>
        <div className="sub" style={{ marginTop: 0 }}>
          Ordered {o.orderedDate ? soUsDate(o.orderedDate) : "—"} · Received {o.receivedDate ? soUsDate(o.receivedDate) : "—"}
          {o.invoiceDate && <> · Invoiced {soUsDate(o.invoiceDate)}</>}
        </div>

        {/* reference numbers */}
        {refs.length > 0 && (
          <div className="sub" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "3px 16px" }}>
            {refs.map(([l, v]) => <span key={l}><b style={{ color: "var(--muted)" }}>{l}</b> {v}</span>)}
          </div>
        )}

        {/* who placed it + change history */}
        {(o.createdBy || (o.changes && o.changes.length > 0)) && (
          <div style={{ marginTop: 10, background: "#FBF9F4", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 11px" }}>
            <div className="sub" style={{ marginTop: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>Order history</div>
            {o.createdBy && (
              <div className="sub" style={{ marginTop: 3, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span className="mono" style={{ minWidth: 120 }}>{o.createdDate ? soUsDate(o.createdDate) : ""}</span>
                <span><b style={{ color: "var(--muted)" }}>{o.createdBy}</b> placed the order</span>
              </div>
            )}
            {(o.changes || []).map((e, i) => (
              <div key={e.id || i} className="sub" style={{ marginTop: 3, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span className="mono" style={{ minWidth: 120 }}>{soUsDate(e.date)}{e.time ? " " + e.time : ""}</span>
                <span><b style={{ color: "var(--muted)" }}>{e.by || "?"}</b> — {e.note}</span>
              </div>
            ))}
          </div>
        )}

        {/* contact log */}
        {(o.contactLog && o.contactLog.length > 0) && (
          <div style={{ marginTop: 10, background: "#F7FAFD", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 11px" }}>
            <div className="sub" style={{ marginTop: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>Contact log</div>
            {o.contactLog.slice().reverse().map((e, i) => (
              <div key={e.id || i} className="sub" style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 8 }}>
                <span className="mono" style={{ minWidth: 120 }}>{soUsDate(e.date)}{e.time ? " " + e.time : ""}</span>
                <span className="doc" style={{ background: e.kind === "call" ? "#2C6EBE" : e.kind === "text" ? "#0B7A3B" : "#7A4FB6" }}>{kindLabel[e.kind] || e.kind}</span>
                {e.by && <span>{e.by}</span>}
              </div>
            ))}
          </div>
        )}
        {o.notes && <div className="vnotes" style={{ fontSize: 12.5, marginTop: 9 }}>{o.notes}</div>}

        {/* actions: advance · contact · manage */}
        <div className="btnrow" style={{ marginTop: 12, alignItems: "center" }}>
          {o.status === "toorder" && <button className="btn mini" onClick={() => advance(o, "ordered")}>Mark ordered</button>}
          {(o.status === "ordered" || o.status === "toorder") && <button className="btn mini" onClick={() => markReceived(o)}>Received ✓ (notify customer)</button>}
          {o.status === "received" && (
            <button className="btn mini" disabled={!o.invoicePaid}
              title={!o.invoicePaid ? "Locked until payment is applied — recall the held sale at the till, or apply payment from the customer's account." : ""}
              onClick={() => advance(o, "pickedup")}>
              {o.invoicePaid ? "Picked up ✓" : "🔒 Picked up — payment required"}
            </button>
          )}
          {o.status === "received" && !done && (
            <button className="btn ghost mini" onClick={() => sendReminder(o)}>
              {reminderDue(o) ? "⏰ Weekly reminder due — send" : "Send reminder"}
            </button>
          )}
          {o.status === "pickedup" && <button className="btn ghost mini" onClick={() => advance(o, "received")}>Reopen</button>}
          <span className="sub" style={{ marginTop: 0, marginLeft: 6 }}>Log:</span>
          <button className="btn ghost mini icon" title="Record that you phoned the customer today" onClick={() => logContact(o, "call")}><CommIcon v="phone" size={13} /> Call</button>
          {onCompose && k0.email && <button className="btn ghost mini icon" title={`Email: “${specialOrderMsg(o)}”`} onClick={() => { logContact(o, "email"); onCompose({ kind: "mail", client: cust, to: k0.email, emailCat: "retail", subject: "Pool Master — your special order", body: specialOrderMsg(o) }); }}><CommIcon v="mail" size={13} /> Email</button>}
          {onCompose && k0.phone && <button className="btn ghost mini icon" title={`Text: “${specialOrderMsg(o)}”`} onClick={() => { logContact(o, "text"); onCompose({ kind: "text", client: cust, to: k0.phone, body: specialOrderMsg(o) }); }}><CommIcon v="text" size={13} /> Text</button>}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn ghost mini" onClick={() => startEdit(o)}>Edit</button>
            <button className="btn ghost mini" onClick={() => createRGA(o)}>Create RGA</button>
            <button className="btn ghost mini" onClick={() => archive(o)}>{o.archived ? "Unarchive" : "Archive"}</button>
            <button className="btn warn mini" onClick={() => onDelete(o.id)}>Delete</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {screen === "start" && (
        <div>
          <div className="bar" style={{ marginBottom: 16 }}>
            <div>
              <strong style={{ fontSize: 16 }}>Special orders</strong>
              <div className="sub">Parts ordered in for a customer · flags their account until picked up</div>
            </div>
          </div>
          <div className="posstart" style={{ maxWidth: "none", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
            <button type="button" className="posstart-btn" onClick={() => { startNew(); setScreen("status"); }}>
              <SpecialOrderIcon size={36} />
              <span className="posstart-lbl">Create a special order</span>
              <span className="posstart-sub">Order a part in for a customer</span>
            </button>
            <button type="button" className="posstart-btn" onClick={() => setScreen("status")}>
              <RetailIcon size={36} />
              <span className="posstart-lbl">Update order status ({placedList.length})</span>
              <span className="posstart-sub">Placed orders — mark Received when they arrive</span>
            </button>
            <button type="button" className="posstart-btn" onClick={() => setScreen("waiting")}>
              <CommIcon v="text" size={34} />
              <span className="posstart-lbl">Waiting to be picked up ({waitingList.length})</span>
              <span className="posstart-sub">Weekly customer reminders until collected</span>
            </button>
            <button type="button" className="posstart-btn" onClick={() => setScreen("pickedup")}>
              <CustomersIcon size={36} />
              <span className="posstart-lbl">Picked-up orders</span>
              <span className="posstart-sub">Review orders customers have collected</span>
            </button>
          </div>
        </div>
      )}

      {(screen === "waiting" || screen === "status") && (<>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 16 }}>{screen === "status" ? "Update order status" : "Waiting to be picked up"}</strong>
          <div className="sub">{screen === "status"
            ? `${placedList.length} placed order${placedList.length === 1 ? "" : "s"} — mark Received when they arrive (customer gets notified)`
            : `${waitingList.length} received · customer reminded weekly until collected`}</div>
        </div>
        <div className="btnrow" style={{ alignItems: "center" }}>
          <button className="btn ghost mini" onClick={() => setScreen("start")}>‹ Back</button>
          {screen === "status"
            ? <button className="btn ghost" onClick={() => setScreen("waiting")}>Waiting ({waitingList.length})</button>
            : <button className="btn ghost" onClick={() => setScreen("status")}>Order status ({placedList.length})</button>}
          <button className="btn ghost" onClick={() => setScreen("pickedup")}>Picked up ({doneList.length})</button>
          {screen === "status" && <button className="btn" onClick={startNew}>New special order</button>}
        </div>
      </div>

      {form && (
        <div className="visit latest" style={{ marginBottom: 14 }}>
          <div className="bar" style={{ marginBottom: 12 }}>
            <strong style={{ fontSize: 15 }}>{specialOrders.some((x) => x.id === form.id) ? "Edit special order" : "New special order"}</strong>
            <div className="btnrow">
              <button className="btn" onClick={() => commit()} disabled={!form.item.trim()}>Save order</button>
              <button className="btn ghost" onClick={() => { setForm(null); setSoPicked(null); }}>Cancel</button>
            </div>
          </div>
          {soPicked && num(soPicked.qty) > 0 && (
            <div className="notice" style={{ marginBottom: 12, background: "#E6F6EC", border: "1px solid #A9D9BC", color: "#0E7A3C", fontWeight: 600 }}>
              ✓ In stock: <b>{soPicked.name}</b>{soPicked.code ? ` (${soPicked.code})` : ""} — {num(soPicked.qty)} on hand{num(soPicked.price) > 0 ? ` · ${posMoney(soPicked.price)}` : ""}. You may already have this — check before special-ordering.
            </div>
          )}
          <div className="fields">
            <div className="f-wide">
              <label>Customer</label>
              {form.customerId ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="chip" data-ch="email"><b>{form.customerName}</b></span>
                  <button type="button" className="btn ghost mini" onClick={() => setForm((f) => ({ ...f, customerId: "", customerName: "" }))}>Change</button>
                </div>
              ) : (
                <>
                  <input placeholder="Search a customer — name, phone, address" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} />
                  {custSearch.trim() && (
                    <div className="poslookup">
                      {custMatches.length === 0 ? <div className="sub" style={{ padding: "8px 10px" }}>No match.</div>
                        : custMatches.map((c) => (
                          <button key={c.id} type="button" className="poslookupitem" onClick={() => { pickCust(c); setCustSearch(""); }}>
                            <b>{c.name}{c.trade ? " · Trade" : ""}</b>
                            <span className="sub" style={{ marginTop: 0 }}>{c.address || ((c.contacts || [])[0] || {}).phone || "No address"}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="f-wide"><label>Part / item ordered (required)</label><input value={form.item} placeholder="e.g. Hayward pump motor 1.5 HP" onChange={(e) => { setForm({ ...form, item: e.target.value }); setSoPicked(null); }} /></div>
            <div><label>Part # (optional)</label><input value={form.code} onChange={(e) => { setForm({ ...form, code: e.target.value }); setSoPicked(null); }} /></div>
            {soMatches.length > 0 && (
              <div className="f-wide">
                <div className="sub" style={{ marginTop: 0, marginBottom: 4 }}>In stock — tap to confirm which item:</div>
                <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 0, maxHeight: 210 }}>
                  {soMatches.map((p) => (
                    <button key={p.id} type="button" className="poslookupitem" onClick={() => { setForm((f) => ({ ...f, item: p.name, code: p.code || f.code })); setSoPicked(p); }}>
                      <b>{p.name}{p.code ? ` · ${p.code}` : ""}</b>
                      <span className="sub" style={{ marginTop: 0 }}>{num(p.qty)} on hand{num(p.price) > 0 ? ` · ${posMoney(p.price)}` : ""}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div><label>Supplier Order # (Optional)</label><input value={form.orderNo || ""} placeholder="Supplier Order #" onChange={(e) => setForm({ ...form, orderNo: e.target.value })} /></div>
            <div><label>Internal P.O # (optional)</label><input value={form.internalPO || ""} placeholder="Internal P.O #" onChange={(e) => setForm({ ...form, internalPO: e.target.value })} /></div>
            <div><label>Qty</label><input inputMode="decimal" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></div>
            <div><label>Supplier (optional)</label><input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
            <div>
              <label>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="toorder">To be ordered</option><option value="ordered">Ordered</option><option value="received">Received</option>
              </select>
            </div>
            <div><label>Date ordered</label><input type="date" value={form.orderedDate || ""} onChange={(e) => setForm({ ...form, orderedDate: e.target.value })} /></div>
            <div><label>Date received</label><input type="date" value={form.receivedDate || ""} onChange={(e) => setForm({ ...form, receivedDate: e.target.value })} /></div>
            {form.invoiceDate && (
              <div className="f-wide">
                <label>Date of invoice</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ fontSize: 13.5 }}>{soUsDate(form.invoiceDate)}</span>
                  <span className="doc" style={{ background: form.invoicePaid ? "#12A650" : "#B31212" }}>{form.invoicePaid ? "Paid" : "Un-paid"}</span>
                </div>
              </div>
            )}
            <div className="f-wide">
              <label>Last communication</label>
              {(form.contactLog || []).length === 0 ? (
                <div className="sub" style={{ marginTop: 2 }}>No contact yet — calls, texts, and emails are logged here automatically with date and time.</div>
              ) : (
                <div style={{ background: "#F7FAFD", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 11px" }}>
                  {form.contactLog.slice().reverse().map((e, i) => (
                    <div key={e.id || i} className="sub" style={{ marginTop: i === 0 ? 0 : 3, display: "flex", alignItems: "center", gap: 8, fontWeight: i === 0 ? 650 : 400 }}>
                      <span className="mono" style={{ minWidth: 120 }}>{soUsDate(e.date)}{e.time ? " " + e.time : ""}</span>
                      <span className="doc" style={{ background: e.kind === "call" ? "#2C6EBE" : e.kind === "text" ? "#0B7A3B" : "#7A4FB6" }}>{kindLabel[e.kind] || e.kind}</span>
                      {e.by && <span>{e.by}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="f-wide"><label>Notes</label><textarea value={form.notes} style={{ minHeight: 60 }} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="btnrow" style={{ marginTop: 12, alignItems: "center" }}>
            <button className="btn" onClick={() => commit()} disabled={!form.item.trim()}>Save order</button>
            <button className="btn ghost" onClick={() => setForm(null)}>Cancel</button>
            {!form.item.trim() && <span className="sub" style={{ marginTop: 0, color: "var(--clay)" }}>Enter the part / item to save.</span>}
          </div>
        </div>
      )}

      {screen === "status" && (placedList.length === 0 && !form
        ? <div className="empty">No placed orders right now. Create one and it sits here until it arrives.</div>
        : placedList.map((o) => card(o, false)))}
      {screen === "waiting" && (waitingList.length === 0
        ? <div className="empty">Nothing waiting for pickup. When an order is marked Received it lands here.</div>
        : waitingList.map((o) => card(o, false)))}
      </>)}

      {screen === "pickedup" && (<>
        <div className="bar" style={{ marginBottom: 12 }}>
          <div>
            <strong style={{ fontSize: 16 }}>Picked up</strong>
            <div className="sub">Orders customers have collected · kept for reference</div>
          </div>
          <div className="btnrow" style={{ alignItems: "center" }}>
            <button className="btn ghost mini" onClick={() => setScreen("start")}>‹ Back</button>
            <button className="btn ghost" onClick={() => setScreen("waiting")}>Waiting ({active.length})</button>
          </div>
        </div>
        {doneList.length === 0
          ? <div className="empty">Nothing picked up yet. When you mark an order “Picked up ✓” it moves here.</div>
          : doneList.map((o) => card(o, true))}
        {archivedList.length > 0 && (
          <>
            <div className="sechead">Archived</div>
            {archivedList.map((o) => card(o, true))}
          </>
        )}
      </>)}

      {payAsk && form && (
        <div className="overlay">
          <div className="sheet" style={{ maxWidth: 460 }}>
            <div className="bar">
              <div>
                <strong style={{ fontSize: 16 }}>💳 Did you take payment yet?</strong>
                <div className="sub">{form.customerName || "This order"}{form.item ? " · " + form.item : ""}</div>
              </div>
            </div>
            <div className="pad">
              <div className="notice" style={{ marginBottom: 12 }}>
                Special orders are paid up front — take the payment at the till before the order goes to the supplier.
              </div>
              <div className="btnrow" style={{ flexWrap: "wrap" }}>
                <button className="btn" onClick={() => commit("paid")}>Yes — payment's taken</button>
                <button className="btn ghost" onClick={() => setPayAsk(false)}>Not yet — I'll take it first</button>
              </div>
              <div style={{ marginTop: 12 }}>
                <button className="btn ghost mini" onClick={() => commit("unpaid")}>
                  Place without payment (on account / pay at pickup) — parks at the till, pickup stays blocked until paid
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- point of sale ---------- */
const POS_METHODS = [["credit", "Credit"], ["debit", "Debit"], ["cash", "Cash"], ["account", "On account"], ["split", "Split (cash + credit)"]];
/* ---------- Follow-ups: the don't-forget list ---------- */
function followupOverdue(f, today) {
  return !f.done && !!f.dueDate && f.dueDate < today;
}
// Visibility: a follow-up is either for everyone, or pinned to one or more
// employees — then only those employees, whoever wrote it, and the owner see it.
// Older items carry a single forStaffId; newer ones a forStaffIds array.
function followupPinnedTo(f) {
  if (f && Array.isArray(f.forStaffIds) && f.forStaffIds.length) return f.forStaffIds;
  return f && f.forStaffId ? [f.forStaffId] : [];
}
function visibleFollowup(f, meId, isOwner) {
  if (isOwner) return true;
  const pinned = followupPinnedTo(f);
  if (!pinned.length) return true;
  return pinned.includes(meId) || f.createdById === meId;
}
// Sign-in reminders: open follow-ups whose remind-on date has arrived and that
// are this person's to chase — pinned to them, written by them, or unpinned
// (anyone's job). Pinned-to-someone-else never nags you, owner included.
function followupReminders(followups, meId, isOwner, today) {
  return (followups || []).filter((f) => {
    if (f.done || !f.dueDate || f.dueDate > today) return false;
    if (!visibleFollowup(f, meId, isOwner)) return false;
    const pinned = followupPinnedTo(f);
    return !pinned.length || pinned.includes(meId) || f.createdById === meId;
  });
}
function sortFollowups(list, today) {
  return [...(list || [])].sort((a, b) => {
    const ao = followupOverdue(a, today) ? 0 : 1, bo = followupOverdue(b, today) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    if ((a.dueDate || "") !== (b.dueDate || "")) return (a.dueDate || "9999") < (b.dueDate || "9999") ? -1 : 1;
    return (b.at || "").localeCompare(a.at || "");
  });
}

/* ---------- Company tools: formal check-out / check-in / transfer ---------- */
// Responsibility is FORMAL: it moves only on a recorded check-in or transfer.
const toolStamp = (by) => ({ at: new Date().toISOString(), by: by || "" });
function toolCheckout(t, staffId, staffName, byName) {
  if (t.status === "out") return { ok: false, reason: `${t.holderName || "Someone"} still has this tool — it must be checked in (or formally transferred) first.` };
  if (t.flag) return { ok: false, reason: "This tool is flagged for management review — a manager has to clear it before it goes out again." };
  const st = toolStamp(byName);
  return { ok: true, tool: { ...t, status: "out", holderId: staffId, holderName: staffName, outAt: st.at,
    log: [...(t.log || []), { id: cid(), ...st, action: "checkout", to: staffName }] } };
}
function toolCheckin(t, byName) {
  if (t.status !== "out") return { ok: false, reason: "This tool isn't checked out." };
  const st = toolStamp(byName);
  return { ok: true, tool: { ...t, status: "in", holderId: "", holderName: "", outAt: "",
    lastHolderId: t.holderId, lastHolderName: t.holderName,
    log: [...(t.log || []), { id: cid(), ...st, action: "checkin", from: t.holderName }] } };
}
// The formal Transfer: records BOTH employees; until it exists, the last
// check-out name stays responsible — no informal hand-offs.
function toolTransfer(t, staffId, staffName, byName) {
  if (t.status !== "out") return { ok: false, reason: "Only a checked-out tool can be transferred — check it out first." };
  if (t.holderId === staffId) return { ok: false, reason: "That employee already has it." };
  const st = toolStamp(byName);
  return { ok: true, tool: { ...t, holderId: staffId, holderName: staffName, outAt: st.at,
    log: [...(t.log || []), { id: cid(), ...st, action: "transfer", from: t.holderName, to: staffName }] } };
}
function toolFlag(t, kind, note, byName) {
  const responsible = t.status === "out" ? t.holderName : (t.lastHolderName || "");
  const st = toolStamp(byName);
  return { ...t, flag: { kind, note: note || "", responsible, ...st },
    log: [...(t.log || []), { id: cid(), ...st, action: "flag", note: `${kind}${note ? " — " + note : ""} · last responsible: ${responsible || "unknown"}` }] };
}
function toolClearFlag(t, note, byName) {
  const st = toolStamp(byName);
  return { ...t, flag: null, status: t.status === "out" ? "out" : "in",
    log: [...(t.log || []), { id: cid(), ...st, action: "clear", note: note || "resolved" }] };
}
// "How long they've had it" — humane duration text off the checkout stamp.
function toolHeldFor(outAt, nowIso) {
  if (!outAt) return "";
  const ms = new Date(nowIso || new Date().toISOString()) - new Date(outAt);
  if (!(ms > 0)) return "just now";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60000))} min`;
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"}`;
  const d2 = Math.floor(h / 24);
  return `${d2} day${d2 === 1 ? "" : "s"}`;
}

/* ---------- Promotional email campaigns (CASL-aware) ----------
   Consent logic here MUST match consent() in supabase/functions/promo-worker
   — the worker re-checks at send time, this side drives the audience preview. */
const PROMO_IMPLIED_MONTHS = 24; // CASL implied-consent window

// {ok:true, how:"express"|"implied"} or {ok:false, reason} in plain language.
function promoConsent(client, contact, todayIso) {
  if (!client || client.deactivated) return { ok: false, reason: "account deactivated" };
  if (!contact || !String(contact.email || "").trim()) return { ok: false, reason: "no email address" };
  if (contact.promos === "no") return { ok: false, reason: "opted out of promotional email" };
  if (contact.promos === "yes") return { ok: true, how: "express" };
  const activeService = (client.type === "service" || client.type === "both") && !client.deactivated;
  const cutoff = addMonthsIso(todayIso, -PROMO_IMPLIED_MONTHS);
  const recent = [client.lastRetail, client.lastWaterTest].some((d) => d && String(d) >= cutoff);
  if (activeService || recent) return { ok: true, how: "implied" };
  return { ok: false, reason: "implied consent expired (no activity in 2 years)" };
}

// Softub interest: explicit tag wins; unset falls back to purchase/repair history.
function softubLikely(client, sales = [], repairs = []) {
  const rx = /softub|hydromate/i;
  if (sales.some((x) => x.customerId === client.id && !x.voided
    && (rx.test(x.revCentre || "") || (x.items || []).some((it) => rx.test(it.name || "") || rx.test(it.code || ""))))) return true;
  return repairs.some((r) => r.customerId === client.id
    && rx.test([r.category, r.subCategory, r.model].filter(Boolean).join(" ")));
}
const softubTagged = (client, sales, repairs) =>
  client.softub === true ? true : client.softub === false ? false : softubLikely(client, sales, repairs);

// Audience = who gets it and, just as important, who does NOT and why.
// One email address is counted once, ever (first contact wins).
function promoAudience(clients, aud, ctx, todayIso) {
  const seg = (aud && aud.seg) || "all";
  const inSeg = (c) =>
    seg === "service" ? (c.type === "service" || c.type === "both")
    : seg === "retail" ? c.type === "retail"
    : seg === "trade" ? !!c.trade
    : seg === "watertest" ? !!c.waterTesting
    : seg === "softub" ? softubTagged(c, (ctx || {}).sales || [], (ctx || {}).repairs || [])
    : true;
  const included = [], excluded = [], seen = new Set();
  for (const c of clients || []) {
    if (!inSeg(c)) continue;
    if (aud && aud.noRetailSince && c.lastRetail && c.lastRetail >= aud.noRetailSince) continue;
    if (c.deactivated) { excluded.push({ name: c.name, reason: "account deactivated" }); continue; }
    const withEmail = (c.contacts || []).filter((k) => String(k.email || "").trim());
    if (!withEmail.length) { excluded.push({ name: c.name, reason: "no email address" }); continue; }
    for (const k of withEmail) {
      const label = `${c.name} — ${k.name || k.email}`;
      const chk = promoConsent(c, k, todayIso);
      if (!chk.ok) { excluded.push({ name: label, reason: chk.reason }); continue; }
      const em = String(k.email).trim().toLowerCase();
      if (seen.has(em)) { excluded.push({ name: label, reason: "duplicate email (already counted)" }); continue; }
      seen.add(em);
      included.push({ clientId: c.id, contactId: k.id, clientName: c.name, name: k.name || "", email: String(k.email).trim(), how: chk.how });
    }
  }
  return { included, excluded };
}

const campaignDue = (c, nowIso) => c.status === "scheduled" && !!c.sendAt && c.sendAt <= nowIso;
const PROMO_FAIL_STATUSES = new Set(["failed", "fail", "error", "bounce", "bounced", "hard_bounce", "soft_bounce", "hard-bounce", "soft-bounce"]);
function promoRowFailed(row) {
  if (!row) return false;
  const st = String(row.status || row.state || row.result || "").toLowerCase().replace(/\s+/g, "_");
  if (PROMO_FAIL_STATUSES.has(st)) return true;
  if (row.error || row.bounce || row.bounced_at || row.bouncedAt) return true;
  return false;
}
function promoSendCounts(campaign, rows) {
  const list = Array.isArray(rows) ? rows
    : (campaign && Array.isArray(campaign.recipients) ? campaign.recipients : null);
  const stored = (campaign && campaign.counts) || {};
  if (list && list.length) {
    let sent = 0, failed = 0, skipped = 0, queued = 0;
    for (const r of list) {
      const st = String(r.status || r.state || "").toLowerCase();
      if (promoRowFailed(r)) failed += 1;
      else if (st === "sent" || st === "delivered") sent += 1;
      else if (st === "skipped" || st === "opted_out" || st === "unsubscribed") skipped += 1;
      else queued += 1;
    }
    return { sent, failed, skipped, queued };
  }
  const n = (...keys) => {
    for (const k of keys) {
      if (stored[k] != null && stored[k] !== "") {
        const v = Number(stored[k]);
        if (Number.isFinite(v)) return v;
      }
    }
    return 0;
  };
  return {
    sent: n("sent", "delivered"),
    failed: n("failed", "fail", "errors", "bounces", "bounce"),
    skipped: n("skipped"),
    queued: n("queued", "waiting"),
  };
}
function promoFailCount(campaign, rows) {
  return promoSendCounts(campaign, rows).failed;
}
const PROMO_SEGMENTS = [
  { v: "all", label: "All customers" },
  { v: "service", label: "Service customers" },
  { v: "retail", label: "Retail-only customers" },
  { v: "trade", label: "Trade accounts" },
  { v: "watertest", label: "Water-testing customers" },
  { v: "softub", label: "Softub customers" },
];
const CAMPAIGN_STATUS = { draft: "Draft", scheduled: "Scheduled", sending: "Sending", paused: "Paused", cancelled: "Cancelled", done: "Done" };

/* ---------- Robotic cleaner rentals (fleet in the workshop) ---------- */
const RENTAL_STATUS = { available: "Available", rented: "Rented", overdue: "Overdue", inspection: "Inspection/Cleaning" };
const RENTAL_INK = { available: "#12A650", rented: "#0887C8", overdue: "#DC1515", inspection: "#B7791F" };
const RENTAL_ACCESSORIES = ["Cable", "Caddy", "Filter cartridges", "Power supply", "Remote"];
function rentalLateDays(dueDate, today) {
  if (!dueDate || !today || today <= dueDate) return 0;
  return Math.max(0, Math.round((new Date(today + "T12:00:00") - new Date(dueDate + "T12:00:00")) / 86400000));
}
// Live status: a rented unit past its due date reads Overdue even before the sweep persists it.
function rentalLiveStatus(u, today) {
  if (u && u.status === "rented" && u.current && rentalLateDays(u.current.dueDate, today) > 0) return "overdue";
  return (u && u.status) || "available";
}
// Late fee is COMPUTED from the dates — running it twice a day can't double-charge.
function rentalFee(u, today, defFee = 0) {
  const per = num(u && u.lateFee) || num(defFee) || 0;
  const days = u && u.current ? rentalLateDays(u.current.dueDate, today) : 0;
  return { days, per, fee: Math.round(days * per * 100) / 100 };
}
function rentalTotals(u) {
  const h = (u && u.history) || [];
  const sum = (k) => h.reduce((a, r) => a + (num(r[k]) || 0), 0);
  const revenue = sum("revenue"), lateFees = sum("lateFee"), repairCost = sum("repairCost");
  return { rentals: h.length, revenue, lateFees, repairCost,
    net: Math.round((revenue - repairCost - (num(u && u.purchaseCost) || 0)) * 100) / 100 };
}
// Which rentals want a notice today: due-soon once (lead days before), overdue once per day.
function rentalNotices(units, today, leadDays = 1) {
  const out = [];
  for (const u of units || []) {
    if (!u.current || !["rented", "overdue"].includes(rentalLiveStatus(u, today))) continue;
    const sent = u.current.notices || [];
    const late = rentalLateDays(u.current.dueDate, today);
    if (late > 0) {
      if (!sent.some((n) => n.kind === "overdue" && n.date === today)) out.push({ unit: u, kind: "overdue", late });
    } else {
      const lead = new Date(u.current.dueDate + "T12:00:00");
      lead.setDate(lead.getDate() - (Number(leadDays) || 1));
      if (today >= fmtDate(lead) && !sent.some((n) => n.kind === "due")) out.push({ unit: u, kind: "due", late: 0 });
    }
  }
  return out;
}

// The rental fleet screen — lives inside the workshop. Phone-first: checkout
// and check-in are single-column sheets with big tap targets.
function RentalView({ units = [], clients = [], me, isMgr = false, businessProfile = {}, terms = [], onSave, onCheckout, onCheckIn, onOpenCard, onPick, onCompose }) {
  const today = fmtDate(new Date());
  const [filter, setFilter] = useState("all");
  const [editU, setEditU] = useState(null);   // add/edit unit form
  const [coU, setCoU] = useState(null);       // checkout sheet
  const [ciU, setCiU] = useState(null);       // check-in sheet
  const [histU, setHistU] = useState(null);   // history sheet
  const defFee = num(businessProfile.rentalLateFee) || 10;
  const leadDays = num(businessProfile.rentalLeadDays) || 1;
  const clientOf = (id) => clients.find((c) => c.id === id);
  const live = (u) => rentalLiveStatus(u, today);
  const shown = units
    .filter((u) => filter === "all" || live(u) === filter)
    .sort((a, b) => {
      const oa = live(a) === "overdue" ? 0 : 1, ob = live(b) === "overdue" ? 0 : 1;
      if (oa !== ob) return oa - ob;
      return ((a.current || {}).dueDate || "9999").localeCompare((b.current || {}).dueDate || "9999");
    });
  const saveUnit = (u) => onSave(units.some((x) => x.id === u.id) ? units.map((x) => (x.id === u.id ? u : x)) : [...units, u]);
  const notices = rentalNotices(units, today, leadDays);
  const markNotice = (u, kind, channel) => onSave(units.map((x) => (x.id === u.id
    ? { ...x, current: { ...x.current, notices: [...((x.current || {}).notices || []), { kind, date: today, channel, by: me ? me.name : "", at: new Date().toISOString() }] } } : x)));
  const noticeBody = (u, kind) => {
    const c = clientOf((u.current || {}).customerId);
    const k = (c && (c.contacts || [])[0]) || {};
    const fee = (num(u.lateFee) || defFee).toFixed(2);
    const f = contactFirst(k);
    return kind === "due"
      ? `${f ? `Hi ${f}, ` : ""}Pool Master: a reminder that your rental robotic cleaner (${u.tag || u.id}) is due back ${u.current.dueDate}. A late fee of $${fee}/day applies after that date. Thank you!`
      : `${f ? `Hi ${f}, ` : ""}Pool Master: your rental robotic cleaner (${u.tag || u.id}) was due back ${u.current.dueDate} and is now overdue — a late fee of $${fee}/day is accruing. Please return it at your earliest convenience. Thank you!`;
  };

  return (
    <div>
      {notices.length > 0 && (
        <div className="panel" style={{ padding: 12, marginBottom: 10, borderLeft: "4px solid #D9822B" }}>
          <b style={{ fontSize: 13.5 }}>📣 Rental notices to send ({notices.length})</b>
          <div className="sub" style={{ marginTop: 2 }}>Due-back reminders and overdue notices — one tap opens the message ready to send; sending is logged on the unit.</div>
          {notices.map(({ unit: u, kind, late }) => {
            const c = clientOf((u.current || {}).customerId);
            const k = (c && (c.contacts || []).find((x) => x.comms && x.comms.general && x.comms.general !== "none")) || (c && (c.contacts || [])[0]) || {};
            return (
              <div key={u.id + kind} className="visit" style={{ marginTop: 8 }}>
                <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <b>{u.tag || u.id}</b>
                  <span style={{ flex: 1, minWidth: 140 }}>{(u.current || {}).customerName}</span>
                  <span className="doc" style={{ background: kind === "overdue" ? "#DC1515" : "#B36B00" }}>
                    {kind === "overdue" ? `OVERDUE ${late} day${late === 1 ? "" : "s"}` : `due ${(u.current || {}).dueDate}`}
                  </span>
                  {k.phone && onCompose && <button className="btn mini" onClick={() => { onCompose({ kind: "text", client: c, to: k.phone, body: noticeBody(u, kind) }); markNotice(u, kind, "text"); }}>Text</button>}
                  {k.email && onCompose && <button className="btn ghost mini" onClick={() => { onCompose({ kind: "mail", client: c, to: k.email, emailCat: "repairs", subject: "Pool Master — your rental cleaner", body: noticeBody(u, kind) }); markNotice(u, kind, "email"); }}>Email</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bar" style={{ marginBottom: 10 }}>
        <div className="seg">
          {[["all", "All"], ["available", "Available"], ["rented", "Rented"], ["overdue", "Overdue"], ["inspection", "Inspection"]].map(([v, l]) => (
            <button key={v} type="button" data-on={filter === v ? 1 : 0} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
        <button className="btn" onClick={() => setEditU({ id: cid(), tag: "", make: "", model: "", serial: "", purchaseDate: "", purchaseCost: "", rateDay: "", rateWeek: "", lateFee: "", status: "available", notes: "", history: [], log: [] })}>+ Add unit</button>
      </div>

      {shown.length === 0 && <div className="empty">No units{filter === "all" ? " in the fleet yet — add the first cleaner." : " with that status."}</div>}
      {shown.map((u) => {
        const st = live(u);
        const fee = rentalFee(u, today, defFee);
        const t = rentalTotals(u);
        return (
          <div key={u.id} className="visit" style={{ marginBottom: 8, borderLeft: `4px solid ${RENTAL_INK[st]}` }}>
            <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <b>{u.tag || "—"}</b>
              <span style={{ flex: 1, minWidth: 140 }}>{[u.make, u.model].filter(Boolean).join(" ")}</span>
              <span className="doc" style={{ background: RENTAL_INK[st] }}>{RENTAL_STATUS[st]}</span>
              {u.current && <button className="link" onClick={() => onPick && onPick(u.current.customerId)}>{u.current.customerName}</button>}
              {u.current && <span className="sub" style={{ marginTop: 0 }}>out {u.current.out} · due {u.current.dueDate}</span>}
              {st === "overdue" && fee.fee > 0 && <span className="doc" style={{ background: "#DC1515" }}>late fee ${fee.fee.toFixed(2)}</span>}
            </div>
            <div className="btnrow" style={{ marginTop: 6, flexWrap: "wrap" }}>
              {st === "available" && <button className="btn mini" onClick={() => setCoU(u)}>Check out</button>}
              {(st === "rented" || st === "overdue") && <button className="btn mini" onClick={() => setCiU(u)}>Check in</button>}
              {st === "inspection" && (
                <button className="btn mini" onClick={() => {
                  const work = prompt("Ready to rent — any work performed? (blank is fine)") ?? null;
                  if (work === null) return;
                  saveUnit({ ...u, status: "available", log: [...(u.log || []), { at: new Date().toISOString(), by: me ? me.name : "", note: `Ready to rent${work.trim() ? " — " + work.trim() : ""}` }] });
                }}>Ready to rent ✓</button>
              )}
              <button className="btn ghost mini" onClick={() => setHistU(u)}>History{t.rentals ? ` (${t.rentals})` : ""}</button>
              <button className="btn ghost mini" onClick={() => setEditU(u)}>Edit</button>
            </div>
          </div>
        );
      })}

      {editU && <RentalUnitSheet unit={editU} onSave={(u) => { saveUnit(u); setEditU(null); }} onClose={() => setEditU(null)} />}
      {coU && <RentalCheckoutSheet unit={coU} clients={clients} me={me} defFee={defFee} terms={terms} onOpenCard={onOpenCard}
        onDone={(current) => { onCheckout(coU.id, current); setCoU(null); }} onClose={() => setCoU(null)} />}
      {ciU && <RentalCheckinSheet unit={ciU} me={me} isMgr={isMgr} defFee={defFee}
        onDone={(hist, held) => { onCheckIn(ciU.id, hist, held); setCiU(null); }} onClose={() => setCiU(null)} />}
      {histU && <RentalHistorySheet unit={histU} isMgr={isMgr} onPick={onPick} onClose={() => setHistU(null)} />}
    </div>
  );
}

function RentalUnitSheet({ unit, onSave, onClose }) {
  const [u, setU] = useState({ ...unit });
  const f = (k, label, ph, type) => (
    <div><label>{label}</label><input type={type || "text"} inputMode={type ? undefined : undefined} value={u[k] || ""} placeholder={ph || ""} onChange={(e) => setU((x) => ({ ...x, [k]: e.target.value }))} /></div>
  );
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 560 }}>
        <div className="bar">
          <div><strong style={{ fontSize: 16 }}>{unit.tag ? `Unit ${unit.tag}` : "New rental unit"}</strong><div className="sub">The asset record — one per cleaner in the fleet</div></div>
          <div className="btnrow">
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn" disabled={!String(u.tag || "").trim()} onClick={() => onSave({ ...u, tag: u.tag.trim() })}>Save unit</button>
          </div>
        </div>
        <div className="pad"><div className="fields">
          {f("tag", "Unit ID / asset tag", "RC-01")}
          {f("make", "Make", "Dolphin")}
          {f("model", "Model", "S200")}
          {f("serial", "Serial number")}
          {f("purchaseDate", "Purchase date", "", "date")}
          {f("purchaseCost", "Purchase cost ($)")}
          {f("rateDay", "Daily rate ($)")}
          {f("rateWeek", "Weekly rate ($)")}
          {f("lateFee", "Late fee $/day (blank = shop default)")}
          <div className="f-wide"><label>Unit notes</label><textarea rows={2} value={u.notes || ""} onChange={(e) => setU((x) => ({ ...x, notes: e.target.value }))} /></div>
        </div></div>
      </div>
    </div>
  );
}

function RentalCheckoutSheet({ unit, clients, me, defFee, terms = [], onOpenCard, onDone, onClose }) {
  const today = fmtDate(new Date());
  const [custId, setCustId] = useState("");
  const [q, setQ] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [rate, setRate] = useState(unit.rateDay || "");
  const [deposit, setDeposit] = useState("");
  const [condition, setCondition] = useState("Good — tested working");
  const [acc, setAcc] = useState(RENTAL_ACCESSORIES.slice(0, 4)); // remote often stays home
  const cust = clients.find((c) => c.id === custId);
  const card = cust && (cust.billing || {}).card;
  const cardOk = !!(card && (card.last4 || card.ref) && !cardStatus(card).expired);
  const matches = q.trim() ? clients.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  const toggleAcc = (a) => setAcc((t) => (t.includes(a) ? t.filter((x) => x !== a) : [...t, a]));
  return (
    <div className="overlay">
      <div className="sheet" style={{ maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div className="bar">
          <div><strong style={{ fontSize: 16 }}>Check out {unit.tag}</strong><div className="sub">{[unit.make, unit.model].filter(Boolean).join(" ")}</div></div>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
        </div>
        <div className="pad">
          <div className="fields">
            <div className="f-wide" style={{ position: "relative" }}>
              <label>Customer</label>
              {cust ? (
                <div className="btnrow"><span className="chip" data-ch="email"><b>{cust.name}</b></span>
                  <button className="btn ghost mini" onClick={() => setCustId("")}>✕</button></div>
              ) : (<>
                <input placeholder="Search name, phone, address…" value={q} onChange={(e) => setQ(e.target.value)} />
                {matches.length > 0 && (
                  <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 180 }}>
                    {matches.map((c) => <button key={c.id} type="button" className="poslookupitem" onClick={() => { setCustId(c.id); setQ(""); }}><b>{c.name}</b></button>)}
                  </div>
                )}
              </>)}
            </div>
            {cust && !cardOk && (
              <div className="f-wide">
                <div className="notice" style={{ borderColor: "#E8B4B4", background: "#FDF2F2", color: "#8A2C2C" }}>
                  {card && card.last4 ? `The ${card.brand || "card"} on file expired ${card.exp} — a valid card is required before a cleaner leaves the shop.` : "No credit card on file — a valid card is required before a cleaner leaves the shop."}
                  <div className="btnrow" style={{ marginTop: 6 }}>
                    <button className="btn mini" onClick={() => onOpenCard && onOpenCard(cust.id)}>{card && card.last4 ? "Update card" : "Add card"}</button>
                  </div>
                </div>
              </div>
            )}
            <div><label>Checkout date</label><input type="date" value={today} disabled /></div>
            <div><label>Due back</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
            <div><label>Rate applied ($)</label><input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
            <div><label>Deposit taken ($)</label><input inputMode="decimal" value={deposit} placeholder="0" onChange={(e) => setDeposit(e.target.value)} /></div>
            <div className="f-wide"><label>Condition at checkout</label><input value={condition} onChange={(e) => setCondition(e.target.value)} /></div>
            <div className="f-wide">
              <label>Going out with the unit</label>
              <div className="picklist">
                {RENTAL_ACCESSORIES.map((a) => (
                  <button key={a} type="button" className="pick" data-on={acc.includes(a) ? 1 : 0} onClick={() => toggleAcc(a)}><span className="pnm">{a}</span></button>
                ))}
              </div>
            </div>
          </div>
          <div className="sub" style={{ margin: "8px 0" }}>Late fee if not back on time: ${(num(unit.lateFee) || defFee).toFixed(2)}/day — the customer hears this in the reminder.</div>
          {currentTermsText(terms, "rental") ? (
            <div className="notice" style={{ marginBottom: 10, whiteSpace: "pre-wrap" }}>
              <b>Rental terms &amp; conditions</b>
              <div className="sub" style={{ marginTop: 4 }}>{currentTermsText(terms, "rental")}</div>
            </div>
          ) : null}
          <div className="btnrow" style={{ marginBottom: 8 }}>
            <button type="button" className="btn ghost" disabled={!cust}
              onClick={() => printRentalAgreement(unit, { customerName: cust ? cust.name : "", out: today, dueDate, rate, termIds: defaultTermIds(terms, "rental") }, cust, terms)}>
              Print rental terms
            </button>
          </div>
          <button className="btn" style={{ width: "100%", justifyContent: "center", fontSize: 16, padding: "13px" }}
            disabled={!cust || !cardOk || !dueDate || !String(rate).trim()}
            onClick={() => onDone({ customerId: cust.id, customerName: cust.name, out: today, dueDate,
              rate: String(rate), deposit: String(deposit || ""), condition, accessories: acc, by: me ? me.name : "",
              termIds: defaultTermIds(terms, "rental") })}>
            Complete checkout ✓
          </button>
          {cust && !cardOk && <div className="sub" style={{ marginTop: 6, textAlign: "center" }}>Blocked until a valid card is saved — it re-checks the moment the card window closes.</div>}
        </div>
      </div>
    </div>
  );
}

function RentalCheckinSheet({ unit, me, isMgr, defFee, onDone, onClose }) {
  const today = fmtDate(new Date());
  const cur = unit.current || {};
  const auto = rentalFee(unit, today, defFee);
  const [condition, setCondition] = useState("Good");
  const [damage, setDamage] = useState("");
  const [damagePhoto, setDamagePhoto] = useState("");
  const [accBack, setAccBack] = useState(cur.accessories || []);
  const [lateFee, setLateFee] = useState(String(auto.fee || ""));
  const [feeReason, setFeeReason] = useState("");
  const [rentCharge, setRentCharge] = useState(String(cur.rate || ""));
  const [damageCharge, setDamageCharge] = useState("");
  const [notes, setNotes] = useState("");
  const missing = (cur.accessories || []).filter((a) => !accBack.includes(a));
  const feeEdited = String(lateFee) !== String(auto.fee || "");
  const total = (num(rentCharge) || 0) + (num(lateFee) || 0) + (num(damageCharge) || 0);
  const toggleAcc = (a) => setAccBack((t) => (t.includes(a) ? t.filter((x) => x !== a) : [...t, a]));
  return (
    <div className="overlay">
      <div className="sheet" style={{ maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div className="bar">
          <div><strong style={{ fontSize: 16 }}>Check in {unit.tag}</strong>
            <div className="sub">{cur.customerName} · out {cur.out} · was due {cur.dueDate}{auto.days ? ` · ${auto.days} day${auto.days === 1 ? "" : "s"} late` : " · on time"}</div></div>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
        </div>
        <div className="pad">
          <div className="fields">
            <div><label>Return date</label><input type="date" value={today} disabled /></div>
            <div><label>Condition on return</label><input value={condition} onChange={(e) => setCondition(e.target.value)} /></div>
            <div className="f-wide"><label>Damage or missing parts</label>
              <textarea rows={2} value={damage} placeholder="Blank if all good" onChange={(e) => setDamage(e.target.value)} /></div>
            <div className="f-wide">
              <label className="btn ghost mini" style={{ display: "inline-flex" }}>📷 Damage photo{damagePhoto ? " ✓ attached" : ""}
                <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                  onChange={async (e) => { const fl = e.target.files && e.target.files[0]; e.target.value = ""; if (fl) setDamagePhoto(await shrink(fl, 900, 0.75)); }} />
              </label>
            </div>
            <div className="f-wide">
              <label>Accessories returned {missing.length > 0 && <b style={{ color: "#DC1515" }}>— {missing.join(", ")} missing</b>}</label>
              <div className="picklist">
                {(cur.accessories || []).map((a) => (
                  <button key={a} type="button" className="pick" data-on={accBack.includes(a) ? 1 : 0} onClick={() => toggleAcc(a)}><span className="pnm">{a}</span></button>
                ))}
              </div>
            </div>
            <div><label>Rental charge ($)</label><input inputMode="decimal" value={rentCharge} onChange={(e) => setRentCharge(e.target.value)} /></div>
            <div><label>Late fee ($){auto.fee ? ` — auto: $${auto.fee.toFixed(2)}` : ""}</label>
              <input inputMode="decimal" value={lateFee} disabled={!isMgr && auto.fee > 0} onChange={(e) => setLateFee(e.target.value)} />
              {!isMgr && auto.fee > 0 && <div className="sub">Only a manager or the owner can change the late fee.</div>}
            </div>
            {isMgr && feeEdited && (
              <div className="f-wide"><label>Reason for changing the late fee</label>
                <input value={feeReason} placeholder="required when the fee is edited" onChange={(e) => setFeeReason(e.target.value)} /></div>
            )}
            <div><label>Damage charge ($)</label><input inputMode="decimal" value={damageCharge} placeholder="0" onChange={(e) => setDamageCharge(e.target.value)} /></div>
            <div className="f-wide"><label>Staff notes</label><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
          <div style={{ fontWeight: 800, fontSize: 16, margin: "8px 0" }}>Total to collect: ${total.toFixed(2)}</div>
          <button className="btn" style={{ width: "100%", justifyContent: "center", fontSize: 16, padding: "13px" }}
            disabled={feeEdited && isMgr && !feeReason.trim()}
            onClick={() => {
              const hist = { id: cid(), customerId: cur.customerId, customerName: cur.customerName,
                out: cur.out, due: cur.dueDate, returned: today, daysLate: auto.days,
                condition, damage: damage.trim(), damagePhoto: damagePhoto || "",
                accessoriesMissing: missing, lateFee: String(num(lateFee) || 0), rentalCharge: String(num(rentCharge) || 0),
                damageCharge: String(num(damageCharge) || 0), revenue: String(total),
                feeAdjust: feeEdited ? { by: me ? me.name : "", was: String(auto.fee), reason: feeReason.trim() } : null,
                repairCost: "0", by: me ? me.name : "", notes: notes.trim() };
              const held = total > 0 ? { id: cid(), at: new Date().toISOString(), mode: "sale",
                customerId: cur.customerId, customerName: cur.customerName, revCentre: "Repairs",
                items: [
                  { code: "", desc: `Cleaner rental — ${unit.tag} (${cur.out} → ${today})`, qty: "1", price: String(num(rentCharge) || 0), disc: "", discType: "$" },
                  ...(num(lateFee) > 0 ? [{ code: "", desc: `Late fee — ${auto.days} day${auto.days === 1 ? "" : "s"}`, qty: "1", price: String(num(lateFee)), disc: "", discType: "$" }] : []),
                  ...(num(damageCharge) > 0 ? [{ code: "", desc: `Damage/missing — ${unit.tag}${missing.length ? " (" + missing.join(", ") + ")" : ""}`, qty: "1", price: String(num(damageCharge)), disc: "", discType: "$" }] : []),
                ] } : null;
              onDone(hist, held);
            }}>
            Complete check-in → Inspection/Cleaning
          </button>
          <div className="sub" style={{ marginTop: 6, textAlign: "center" }}>The charge parks as a held sale at the till (Repairs revenue). The unit can't rent again until someone marks it Ready to Rent.</div>
        </div>
      </div>
    </div>
  );
}

function RentalHistorySheet({ unit, isMgr, onPick, onClose }) {
  const t = rentalTotals(unit);
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 640, maxHeight: "92vh", overflowY: "auto" }}>
        <div className="bar">
          <div><strong style={{ fontSize: 16 }}>{unit.tag} — history</strong>
            <div className="sub">{[unit.make, unit.model].filter(Boolean).join(" ")}{unit.serial ? ` · s/n ${unit.serial}` : ""}</div></div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          {isMgr && (
            <div className="panel" style={{ padding: 10, marginBottom: 10, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
              <span><b>{t.rentals}</b> rentals</span>
              <span>revenue <b>${t.revenue.toFixed(2)}</b></span>
              <span>late fees <b>${t.lateFees.toFixed(2)}</b></span>
              <span>repairs <b>${t.repairCost.toFixed(2)}</b></span>
              <span>net since purchase <b style={{ color: t.net >= 0 ? "#0E7A3C" : "#DC1515" }}>${t.net.toFixed(2)}</b></span>
            </div>
          )}
          {(unit.history || []).length === 0 && <div className="empty">No rentals yet.</div>}
          {(unit.history || []).map((r) => (
            <div key={r.id} className="visit" style={{ marginBottom: 8, borderLeft: `4px solid ${r.daysLate ? "#DC1515" : "#12A650"}` }}>
              <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <button className="link" onClick={() => onPick && onPick(r.customerId)}>{r.customerName}</button>
                <span className="sub" style={{ marginTop: 0 }}>{r.out} → {r.returned}</span>
                {r.daysLate > 0 && <span className="doc" style={{ background: "#DC1515" }}>{r.daysLate} day{r.daysLate === 1 ? "" : "s"} late</span>}
                {isMgr && <span style={{ fontWeight: 700 }}>${num(r.revenue).toFixed(2)}</span>}
              </div>
              {r.damage && <div className="sub" style={{ color: "#B36B00" }}>Damage: {r.damage}{r.accessoriesMissing && r.accessoriesMissing.length ? ` · missing: ${r.accessoriesMissing.join(", ")}` : ""}</div>}
              {r.feeAdjust && <div className="sub">Late fee adjusted by {r.feeAdjust.by} (was ${r.feeAdjust.was}): {r.feeAdjust.reason}</div>}
              {r.notes && <div className="sub">{r.notes}</div>}
            </div>
          ))}
          {(unit.log || []).length > 0 && (<>
            <div className="sechead">Unit log</div>
            {(unit.log || []).slice(-15).reverse().map((l, i) => (
              <div key={i} className="sub" style={{ marginTop: 2 }}>{String(l.at || "").slice(0, 16).replace("T", " ")} · <b>{l.by}</b> — {l.note}</div>
            ))}
          </>)}
        </div>
      </div>
    </div>
  );
}

/* ---------- In-House Repair (workshop) ---------- */
// Completed repairs the customer hasn't actually been REACHED about yet —
// voicemails and no-answers keep it in the queue until someone speaks to them.
function repairsAwaitingContact(repairs) {
  // Disposal-approved closures need no pickup call — the customer already decided.
  return (repairs || []).filter((t) => t.status === "COMPLETED" && !t.archived && !t.disposal
    && !(t.contactLog || []).some((c) => c.how === "spoken"));
}
const REPAIR_STATUS_LABEL = {
  INTAKE: "Drop-off form — confirm with the customer",
  HOLDING_BIN: "Holding bin — awaiting proof of purchase",
  REPAIRS_QUEUE: "Repairs queue",
  WAITING_FOR_PARTS: "Waiting for parts",
  COMPLETED: "Completed",
};
const REPAIR_CATS = { "Pool Cleaners": ["Corded Unit", "Cordless", "Surface Skimmers", "Pressure-Driven"], "Hydromates": [], "Softubs & Hydromates": [] };
// Intake branching: warranty+invoice → straight to the bench; warranty without proof →
// holding bin until the invoice shows up; no warranty → payment first, then the bench.
function repairIntakeRoute(warranty, hasInvoice) {
  if (warranty && hasInvoice) return { status: "REPAIRS_QUEUE", paymentRequired: false };
  if (warranty) return { status: "HOLDING_BIN", paymentRequired: false };
  return { status: "REPAIRS_QUEUE", paymentRequired: true };
}
function withRepairStatus(t, to, by) {
  if (t.status === to) return t;
  return {
    ...t, status: to,
    ...(to === "COMPLETED" ? { completedAt: new Date().toISOString() } : {}),
    statusHistory: [...(t.statusHistory || []), { id: cid(), from: t.status || "", to, at: new Date().toISOString(), by: by || "" }],
  };
}
function repairTurnaround(list) {
  const done = (list || []).filter((t) => t.completedAt && t.at);
  if (!done.length) return null;
  return done.reduce((a, t) => a + Math.max(0, new Date(t.completedAt) - new Date(t.at)), 0) / done.length / 86400000;
}
function repairKpis(list, today) {
  const l = (list || []).filter((t) => !t.archived);
  return {
    receivedToday: l.filter((t) => t.date === today).length,
    completedToday: l.filter((t) => t.completedAt && fmtDate(new Date(t.completedAt)) === today).length,
    waitingParts: l.filter((t) => t.status === "WAITING_FOR_PARTS").length,
    avgDays: repairTurnaround(l),
  };
}
// The bench pipeline (Module 3) — every active repair sits at exactly one stage.
const REPAIR_QUEUE_STAGE = "In queue — awaiting diagnosis";
const BENCH_STAGES = [REPAIR_QUEUE_STAGE, "Assigned to technician", "Initial inspection", "Diagnostic completed", "Estimate generated",
  "Customer approval received", "Parts ordered", "Repair in progress", "Quality control testing", "Final inspection", "Ready for pickup"];
// Off-path statuses (Yvonne): a job can wait, sit unapproved, or be declined without
// walking the "Customer approval received" path. Stored as `stage` strings like the
// rest so existing ticket stage/status ids stay put. Pipeline `status` (INTAKE /
// REPAIRS_QUEUE / WAITING_FOR_PARTS / COMPLETED) still drives the queue tabs.
const BENCH_PARK_STAGES = ["Waiting on customer", "Not approved", "Declined"];
const REPAIR_FORM_STATUSES = (() => {
  const i = Math.max(0, BENCH_STAGES.indexOf("Customer approval received"));
  return [...BENCH_STAGES.slice(0, i), ...BENCH_PARK_STAGES, ...BENCH_STAGES.slice(i), "WAITING_FOR_PARTS", "COMPLETED"];
})();
function repairFormChoiceLabel(choice) {
  if (choice === "WAITING_FOR_PARTS") return REPAIR_STATUS_LABEL.WAITING_FOR_PARTS;
  if (choice === "COMPLETED") return REPAIR_STATUS_LABEL.COMPLETED;
  if (choice === "INTAKE") return REPAIR_STATUS_LABEL.INTAKE.split(" — ")[0];
  if (choice === "HOLDING_BIN") return REPAIR_STATUS_LABEL.HOLDING_BIN.split(" — ")[0];
  return choice;
}
function repairFormChoice(t) {
  if (!t) return REPAIR_QUEUE_STAGE;
  if (t.status === "COMPLETED") return "COMPLETED";
  if (t.status === "WAITING_FOR_PARTS") return "WAITING_FOR_PARTS";
  return t.stage || REPAIR_QUEUE_STAGE;
}
function applyRepairFormChoice(t, choice, by) {
  if (choice === "WAITING_FOR_PARTS") return withRepairStatus(t, "WAITING_FOR_PARTS", by);
  if (choice === "COMPLETED") return withRepairStatus(t, "COMPLETED", by);
  const next = { ...t, stage: choice, ...(t.status === "HOLDING_BIN" ? { hold: null } : {}) };
  if (next.status === "REPAIRS_QUEUE") return next;
  return withRepairStatus(next, "REPAIRS_QUEUE", by);
}
// Retail chases the bench when a repair sits untouched this many BUSINESS days.
const REPAIR_STALE_BUSINESS_DAYS = 5;
function businessDaysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  let a = new Date(String(fromIso).slice(0, 10) + "T12:00:00");
  const b = new Date(String(toIso).slice(0, 10) + "T12:00:00");
  if (a >= b) return 0;
  let n = 0;
  while (a < b) { a.setDate(a.getDate() + 1); const d = a.getDay(); if (d !== 0 && d !== 6) n++; }
  return n;
}
// One merged, date-descending timeline across every stream on the ticket.
// Derived on render — the underlying arrays stay the single source of truth.
function repairTimeline(t) {
  const rows = [];
  (t.shopNotes || []).forEach((n) => rows.push({ at: n.at, by: n.by, kind: n.kind || ((n.side || "office") === "tech" ? "bench" : "office"), side: n.side || "office", text: n.note }));
  (t.comms || []).forEach((c) => rows.push({ at: c.at, by: c.by, kind: (c.type || "comm").toLowerCase(), side: "office", text: c.note }));
  (t.contactLog || []).forEach((c) => rows.push({ at: c.at, by: c.by, kind: "pickup-call", side: "office", text: c.how === "spoken" ? "Spoke to the customer" : c.how === "voicemail" ? "Left a voicemail" : "Called — no answer" }));
  (t.partsUsed || []).forEach((p) => rows.push({ at: p.at || "", by: p.by || "", kind: "parts", side: "tech", text: `Part used: ${p.name || p.code}${(Number(p.qty) || 1) > 1 ? " ×" + p.qty : ""}` }));
  [...(t.closingHistory || []), ...(t.closing ? [t.closing] : [])].forEach((c) => c.at && rows.push({ at: c.at, by: c.by, kind: "closing", side: "tech", text: `Closing checklist completed by ${c.by || "technician"}${c.revisedFrom ? " (revised)" : ""}` }));
  if (t.pickup && t.pickup.at) rows.push({ at: t.pickup.at, by: t.pickup.by || "", kind: "pickup", side: "office", text: `Picked up / released${t.pickup.unit ? " — " + t.pickup.unit : ""}` });
  return rows.sort((x, y) => String(y.at).localeCompare(String(x.at)));
}
// Stored shop-note `side` stays "office" | "tech" (existing tickets). Staff-visible
// timeline badges for the office id say Bench — shop-floor status rows were
// reading as Office (Yvonne). Same pattern as keeping id "pushy" while renaming the label.
function repairTimelineBadge(row) {
  const kind = String((row && row.kind) || (row && row.side === "tech" ? "bench" : "office"));
  if (kind === "office") return "BENCH";
  return kind.toUpperCase();
}
function repairLastUpdated(t) {
  const rows = repairTimeline(t);
  return (rows[0] && rows[0].at) || t.at || "";
}
function repairStale(t, todayIso) {
  if (!t || t.status === "COMPLETED" || t.archived) return false;
  return businessDaysBetween(repairLastUpdated(t), todayIso) >= REPAIR_STALE_BUSINESS_DAYS;
}
function repairEtaOverdue(t, todayIso) {
  return !!(t && t.etaDate && t.status !== "COMPLETED" && !t.archived && todayIso > t.etaDate);
}
/* ---- Our OWN repair warranty on parts we previously replaced (distinct from
   the manufacturer warranty on catalogue parts). If a customer comes back and
   the new ticket touches a part we installed inside the term, the part is not
   chargeable — labour still is. Term lengths live on the business profile. ---- */
const isSoftubTicket = (t) => /softub|hydromate/i.test([t && t.category, t && t.subCategory, t && t.model].filter(Boolean).join(" "));
// Corded / Cordless pool cleaners are the shop's robotic units. Pressure-Driven
// and Surface Skimmers are not. Field jobs stamp repairType "Robotic cleaner".
const isRoboticRepair = (t) => {
  if (!t) return false;
  if (/robot/i.test([t.category, t.subCategory, t.model, t.repairType].filter(Boolean).join(" "))) return true;
  return t.category === "Pool Cleaners" && /^(corded unit|cordless)$/i.test(String(t.subCategory || "").trim());
};
// First-class disposal close — same t.disposal stamp the pickup-call skip already honours.
// Robots get kind: "robot" so staff can see this was never a pickup.
function withDisposalClose(t, why, by, when = new Date().toISOString()) {
  const reason = String(why || "").trim();
  if (!t || !reason) return t;
  const robot = isRoboticRepair(t);
  const next = {
    ...t,
    disposal: { at: when, by: by || "", reason, ...(robot ? { kind: "robot" } : {}) },
    quote: t.quote && t.quote.status !== "Approved" ? { ...t.quote, status: "Declined", decidedAt: t.quote.decidedAt || when } : t.quote,
    comms: [...(t.comms || []), { id: cid(), type: "QUOTE",
      note: robot
        ? `Customer agreed to dispose of the robotic cleaner — closed without pickup (${reason})`
        : `Customer not going ahead with the repair — confirmed disposal of the unit (${reason})`,
      at: when, by: by || "" }],
  };
  return withRepairStatus(next, "COMPLETED", by);
}
const REPAIR_LABOUR_DAYS = 90; // owner 08/16: labour on Softub repairs is covered 90 days
function repairWarrantyMonthsFor(ticket, bp) {
  const rw = ((bp || {}).repairWarranty) || {};
  const m = isSoftubTicket(ticket) ? rw.softubHydromate : rw.other;
  return num(m) > 0 ? num(m) : 12; // owner 08/16: replaced parts carry 1 year by default
}
// Part codes a ticket touched: partsUsed codes plus the "(CODE)" suffix the
// quote picker embeds in its line descriptions.
function repairPartCodes(t) {
  const map = new Map();
  ((t && t.partsUsed) || []).forEach((p) => { const c = String(p.code || "").trim().toLowerCase(); if (c) map.set(c, p.name || p.code); });
  (((t && t.quote) || {}).items || []).forEach((it) => {
    const m = /\(([^()]+)\)\s*$/.exec(String(it.desc || "").trim());
    const c = m ? m[1].trim().toLowerCase() : "";
    if (c && !map.has(c)) map.set(c, String(it.desc).replace(/\s*\([^()]*\)\s*$/, "").trim());
  });
  return map;
}
// warrantyMonths: a number, or a function(sourceTicket) → months. Archived
// tickets still count — archiving clears the board, it never voids a warranty.
// Missing completion dates are treated as not covered, never a crash.
function repairWarrantyMatches(ticket, allRepairs, warrantyMonths, today, staff = []) {
  if (!ticket || !ticket.customerId) return [];
  const mine = repairPartCodes(ticket);
  if (!mine.size) return [];
  const past = (allRepairs || []).filter((x) => x.id !== ticket.id && x.customerId === ticket.customerId
    && x.status === "COMPLETED" && !x.disposal && x.completedAt)
    .sort((a, b) => String(b.completedAt).localeCompare(String(a.completedAt)));
  const out = [];
  for (const [code, name] of mine) {
    const src = past.find((x) => repairPartCodes(x).has(code));
    if (!src) continue;
    const done = String(src.completedAt).slice(0, 10);
    const months = typeof warrantyMonths === "function" ? num(warrantyMonths(src)) : num(warrantyMonths);
    const until = (src.workWarranty && src.workWarranty.until) || (months > 0 ? addMonthsIso(done, months) : "");
    if (!until) continue;
    const serialMatch = !!(ticket.serial && src.serial && String(ticket.serial).trim().toLowerCase() === String(src.serial).trim().toLowerCase());
    const covered = String(today) <= until;
    // Softub/Hydromate repairs: OUR labour is free for 90 days after the repair.
    const ld = new Date(done + "T12:00:00"); ld.setDate(ld.getDate() + REPAIR_LABOUR_DAYS);
    const labourUntil = isSoftubTicket(src) ? fmtDate(ld) : "";
    out.push({ code, name: repairPartCodes(src).get(code) || name, replacedOn: done,
      byTech: (staff.find((y) => y.id === src.techId) || {}).name || "",
      sourceTicketId: src.id, sourceTicketNo: src.ticketNo || "", expiresOn: until,
      status: covered ? "covered" : "expired",
      daysLeft: covered ? Math.max(0, Math.round((new Date(until + "T12:00:00") - new Date(String(today).slice(0, 10) + "T12:00:00")) / 86400000)) : 0,
      labourUntil, labourCovered: !!(labourUntil && String(today) <= labourUntil),
      matchedBy: serialMatch ? "serial" : "customer" });
  }
  return out;
}
/* ---- Permanent repair history: derived straight from data.repairs, so there
   is no second collection to sync. Archived tickets STAY in history — archiving
   clears the workshop board, it never erases what was done. ---- */
// One number for "what did this repair cost the customer", resolved in a fixed
// order and labelled with where it came from. No new money math — HST is shown
// as "+ HST" the same as the rest of the app.
function repairAmountCharged(t) {
  const q = (t && t.quote) || {};
  if (q.status === "Approved" && (q.items || []).length) {
    return { amount: (q.items || []).reduce((a, it) => a + num(it.amount), 0), how: "approved quote" };
  }
  if (t && t.payment && t.payment.paid) return { amount: num(t.payment.amount), how: "payment taken" };
  const d = t && t.deductible;
  if (d && d.applies && (d.status === "paid" || num(d.waivedAmount) > 0)) {
    return { amount: Math.max(0, num(d.amount) - num(d.waivedAmount || 0)), how: "warranty deductible" };
  }
  return { amount: 0, how: t && t.warranty !== "no" ? "no charge — warranty" : "no charge" };
}
// The rows staff quote from on the phone: every COMPLETED ticket, newest first.
function repairHistory(repairs, customerId, staff = [], bp = {}, today = "") {
  return (repairs || []).filter((t) => t.customerId === customerId && t.status === "COMPLETED")
    .sort((a, b) => String(b.completedAt || b.at || "").localeCompare(String(a.completedAt || a.at || "")))
    .map((t) => {
      const charged = repairAmountCharged(t);
      const done = String(t.completedAt || "").slice(0, 10) || t.date || "";
      // Work performed: the tech's own closing answer first, then the approved
      // quote, then parts used, then the reported problem.
      const c = t.closing;
      const workDone = t.disposal
        ? ((t.disposal.kind === "robot" || isRoboticRepair(t)) ? "Robotic cleaner disposed — customer agreed, no pickup" : "Unit disposed — customer declined the quote")
        : c && c.noParts ? `${c.failure || "Repair"} — labour only`
        : c && (c.parts || []).some((p) => String(p.name || "").trim())
          ? c.parts.filter((p) => String(p.name || "").trim()).map((p) => `Replaced ${p.name}`).join(", ")
        : ((t.quote || {}).status === "Approved" && (t.quote.items || []).length) ? t.quote.items.map((it) => it.desc).join(", ")
        : (t.partsUsed || []).length ? t.partsUsed.map((p) => `Replaced ${p.name || p.code}`).join(", ")
        : (t.problem || "Repair");
      // Our repair warranty on the work: the stamp going forward; derived from
      // the profile terms for older tickets that closed before the stamp existed.
      const until = t.disposal ? "" : (t.workWarranty && t.workWarranty.until)
        || (done ? addMonthsIso(done, repairWarrantyMonthsFor(t, bp)) : "");
      return { date: done,
        unit: ((t.purchase || {}).productName) || [[t.category, t.subCategory].filter(Boolean).join(" · "), t.model ? `Model ${t.model}` : ""].filter(Boolean).join(" · ") || "Unit",
        serial: t.serial || "",
        techName: (staff.find((x) => x.id === t.techId) || {}).name || (t.disposal ? "" : ""),
        workDone: workDone.length > 90 ? workDone.slice(0, 87) + "…" : workDone,
        amount: charged.amount, amountHow: charged.how,
        warrantyKind: t.warranty !== "no" ? "Warranty repair" : "Customer paid",
        workWarrantyUntil: until, stillCovered: !!(until && String(today) <= until),
        ticket: t };
    });
}
// The closing checklist: seven answers, every one required, before a repair
// can reach "Ready for pickup" or COMPLETED. Disposal closures are exempt
// (a short reason is captured there instead). The gate lives on the DATA —
// hiding a button is not the security boundary.
const REPAIR_BILLED_LABEL = { billed: "Billed to customer", warranty: "Warranty — no charge", goodwill: "Goodwill — no charge", partial: "Partly billed" };
// Parts replaced, billing, and "what failed" already live on this ticket
// (Parts used, Quote/warranty/payment, Reported problem). The closing
// checklist must not ask for them a second time. Copy those facts onto the
// closing record so the gate, receipt, and history keep working — and keep
// any checklist-only values saved before this change when the form is empty.
function formPartsForClosing(t) {
  return ((t && t.partsUsed) || []).map((p) => ({ name: p.name || p.code, qty: p.qty || 1 }))
    .filter((p) => String(p.name || "").trim());
}
function formBilledForClosing(t) {
  const q = (t && t.quote) || {};
  const totalQ = q.status === "Approved" ? (q.items || []).reduce((a, it) => a + num(it.amount), 0) : 0;
  if (totalQ > 0) return "billed";
  if (t && t.warranty && t.warranty !== "no") return "warranty";
  if (t && t.payment && t.payment.paid) return "billed";
  return "";
}
function closingAnswersFromForm(t, answers = {}) {
  const formParts = formPartsForClosing(t);
  const savedParts = (answers.parts || []).filter((p) => String(p.name || "").trim());
  // Union so a later save cannot drop checklist-only names that never
  // made it onto Parts used (stock). Form names win on duplicates.
  const parts = [];
  const seen = new Set();
  for (const p of [...formParts, ...savedParts]) {
    const k = String(p.name || "").trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    parts.push(p);
  }
  const billed = REPAIR_BILLED_LABEL[answers.billed] ? answers.billed : (formBilledForClosing(t) || "");
  const failure = String(answers.failure || "").trim() ? answers.failure : ((t && t.problem) || "");
  return { ...answers, failure, parts, noParts: parts.length ? false : !!answers.noParts, billed };
}
function repairClosingGate(t) {
  if (t && t.disposal) return { ok: true };
  const c = t && t.closing;
  if (!c) return { ok: false, reason: "Fill in the Repair Closing Checklist first — all seven answers are required before this repair can close." };
  const a = closingAnswersFromForm(t, c);
  const missing = [];
  if (!String(a.failure || "").trim()) missing.push("what failed");
  if (!String(a.rootCause || "").trim()) missing.push("the root cause");
  if (!a.noParts && !(a.parts || []).some((p) => String(p.name || "").trim())) missing.push("parts replaced (or tick 'no parts were replaced')");
  if (!REPAIR_BILLED_LABEL[a.billed]) missing.push("whether it was billed");
  if (a.billed === "partial" && !String(a.billedNote || "").trim()) missing.push("the partly-billed explanation");
  if (a.warrantyAnswer !== "yes" && a.warrantyAnswer !== "no") missing.push("the warranty answer");
  if (a.warrantyAnswer === "yes" && !(a.warrantyParts || []).some((w) => String(w.name || "").trim() && w.until)) missing.push("each warranty part with its expiry date");
  if (a.followUp !== "yes" && a.followUp !== "no") missing.push("the follow-up answer");
  if (a.followUp === "yes" && (!String(a.followUpWhat || "").trim() || !a.followUpWhen)) missing.push("what the follow-up is and by when");
  if (!String(a.retailNotes || "").trim()) missing.push("notes for retail (or tap 'Nothing to add')");
  if (isPressureSideRepair(t) && !(a.opCheck || (t && t.pressureOpCheck))) missing.push("the operational check (pressure-side — hose assembly is not assumed)");
  return missing.length ? { ok: false, reason: `Closing checklist incomplete — still needed: ${missing.join("; ")}.` } : { ok: true };
}
// The pipeline gate: nothing past the queue without a technician, and nothing
// into "Repair in progress" or beyond without an approved estimate — except
// warranty repairs, which skip the customer-approval gate.
// Unit label the pickup form pre-fills — same ingredients the history rows use.
function repairUnitLabel(t) {
  if (!t) return "Unit";
  const bought = (t.purchase || {}).productName;
  if (bought) return bought;
  return [[t.category, t.subCategory].filter(Boolean).join(" · "), t.model ? `Model ${t.model}` : "", t.serial ? `S/N ${t.serial}` : ""].filter(Boolean).join(" · ") || "Unit";
}
// Auto-check for the Repair Pickup/Release form: warranty vs customer-pay, and
// whether money still has to be collected. Deductible uses the existing
// collect-before-pickup math. Disposal never comes through here (the gate
// short-circuits). Hiding the Completed button is not the security boundary.
function repairPickupPayCheck(t) {
  if (t && t.disposal) return { ok: true, skip: true, warranty: false, paymentRequired: false, collected: true, amount: 0, kind: "disposal", alert: "Disposal-approved — no pickup." };
  const warranty = !!(t && t.warranty && t.warranty !== "no");
  const deductible = deductibleOutstanding(t);
  if (deductible > 0) {
    return { ok: false, skip: false, warranty, paymentRequired: true, collected: false, amount: deductible, kind: "deductible",
      alert: `${warranty ? "Warranty repair" : "Repair"} — payment required: $${deductible.toFixed(2)} deductible has NOT been collected.`,
      reason: `Can't complete — $${deductible.toFixed(2)} warranty deductible is still outstanding. Collect it, mark it paid, or have a manager waive it first.` };
  }
  const billed = (t && t.closing && t.closing.billed) || "";
  const charged = repairAmountCharged(t);
  const paidAmt = (t && t.payment && t.payment.paid) ? num(t.payment.amount) : 0;
  const confirmed = !!(t && ((t.pickup && t.pickup.paymentConfirmed) || (t.payment && t.payment.repairCollected)));
  if (warranty) {
    const hadDed = t && t.deductible && t.deductible.applies;
    return { ok: true, skip: false, warranty: true, paymentRequired: false, collected: true, amount: 0, kind: "warranty",
      alert: hadDed
        ? `Warranty repair — deductible ${t.deductible.status === "waived" ? "waived" : "collected"}. Payment is not required.`
        : "Warranty repair — no charge. Payment is not required." };
  }
  if (billed === "goodwill" || billed === "warranty") {
    return { ok: true, skip: false, warranty: false, paymentRequired: false, collected: true, amount: 0, kind: "no-charge",
      alert: billed === "goodwill"
        ? "Non-warranty repair — goodwill (no charge). Payment is not required."
        : "Closing billed this as warranty — no charge. Payment is not required." };
  }
  const quoteAmt = ((t.quote || {}).status === "Approved")
    ? (t.quote.items || []).reduce((a, it) => a + num(it.amount), 0) : 0;
  const due = quoteAmt > 0 ? quoteAmt : charged.amount;
  const booksPaid = due > 0 && paidAmt + 0.005 >= due && !!(t.payment && t.payment.paid);
  if (quoteAmt > 0 && (booksPaid || confirmed)) {
    return { ok: true, skip: false, warranty: false, paymentRequired: true, collected: true, amount: due, kind: "paid",
      alert: `Non-warranty repair — payment of $${due.toFixed(2)} has been collected.` };
  }
  if (quoteAmt > 0) {
    return { ok: false, skip: false, warranty: false, paymentRequired: true, collected: false, amount: due, kind: "balance",
      alert: `Non-warranty repair — PAYMENT REQUIRED. $${due.toFixed(2)} has NOT been collected. Collect payment before this unit leaves.`,
      reason: `Can't complete — this is not a warranty repair and $${due.toFixed(2)} is still outstanding. Collect payment and confirm it on the pickup form.` };
  }
  if (confirmed) {
    return { ok: true, skip: false, warranty: false, paymentRequired: true, collected: true, amount: charged.amount || paidAmt, kind: "paid",
      alert: "Non-warranty repair — payment has been confirmed collected." };
  }
  return { ok: false, skip: false, warranty: false, paymentRequired: true, collected: false, amount: charged.amount, kind: "balance",
    alert: "Non-warranty repair — PAYMENT REQUIRED. Confirm payment has been collected before this unit leaves.",
    reason: "Can't complete — this is not a warranty repair and payment has not been confirmed. Collect payment first." };
}
// Pickup form is mandatory to leave as COMPLETED, except disposal-approved
// closures (the customer already decided; closeAsDisposal never opens this).
function repairPickupGate(t) {
  if (t && t.disposal) return { ok: true, skip: true };
  const pay = repairPickupPayCheck(t);
  if (!pay.ok) return pay;
  const p = t && t.pickup;
  if (!p || !p.at || !String(p.by || "").trim()) {
    return { ok: false, reason: "Fill in the Repair Pickup/Release form before this unit can leave as Completed." };
  }
  return { ok: true, skip: false };
}

function isPressureSideRepair(t) {
  return !!(t && t.subCategory === "Pressure-Driven");
}
function isHoseRepair(t) {
  if (!t) return false;
  const blob = [
    t.problem,
    ...(((t.quote || {}).items) || []).map((it) => it.desc),
    ...((t.partsUsed) || []).map((x) => x.name || x.code),
    t.closing && t.closing.failure,
    t.closing && t.closing.rootCause,
    ...(((t.closing || {}).parts) || []).map((x) => x.name),
  ].filter(Boolean).join(" ").toLowerCase();
  return /\bhose/.test(blob);
}
function pressurePayOpCheck(t) {
  const hose = isHoseRepair(t);
  return {
    applies: isPressureSideRepair(t),
    label: "OPERATIONAL CHECK: the pressure-side unit has been tested and is operating.",
    hint: "Confirm the operational check first. Hose assembly is not assumed.",
    hoseLabel: "Hose assembly is part of this repair",
    hoseDefaultOn: hose,
    hoseOptional: true,
  };
}
function repairPressurePayGate(t) {
  if (!isPressureSideRepair(t) || (t && t.disposal)) return { ok: true };
  if (t && (t.pressureOpCheck || (t.closing && t.closing.opCheck))) return { ok: true };
  return { ok: false, reason: "Operational check required on a pressure-side unit before payment or completion. Hose assembly is not assumed." };
}

function repairStageGate(t, target) {
  // Parked jobs (waiting / not approved / declined) are not on the approval path.
  if (BENCH_PARK_STAGES.includes(target)) return { ok: true };
  const idx = BENCH_STAGES.indexOf(target);
  if (idx <= 0) return { ok: true };
  if (!t.techId) return { ok: false, reason: "Assign a technician first — the queue stage is the only one that runs unassigned." };
  const repairIdx = BENCH_STAGES.indexOf("Repair in progress");
  if (idx >= repairIdx) {
    const warrantySkip = t.warranty && t.warranty !== "no";
    if (!warrantySkip) {
      const q = t.quote || {};
      if (!(q.items || []).length) return { ok: false, reason: "No estimate yet — generate the quote before repair work starts." };
      if (q.status !== "Approved") return { ok: false, reason: "The customer hasn't approved the estimate — record their approval (or a warranty authorization) first." };
    }
  }
  if (target === "Ready for pickup") {
    const cg = repairClosingGate(t);
    if (!cg.ok) return cg;
  }
  return { ok: true };
}
// Why a unit can sit in the holding bin (Module 2).
const HOLD_REASONS = ["Awaiting proof of purchase", "Awaiting customer approval", "Awaiting estimate approval", "Seasonal hold",
  "Warranty authorization pending", "Technician unavailable", "Customer requested delay", "Awaiting manufacturer response"];
const REPAIR_PRIORITY_RANK = { rush: 0, normal: 1, low: 2 };
function sortRepairQueue(list) {
  return [...(list || [])].sort((a, b) =>
    (REPAIR_PRIORITY_RANK[a.priority || "normal"] - REPAIR_PRIORITY_RANK[b.priority || "normal"])
    || (a.at || "").localeCompare(b.at || ""));
}
// Hold from anywhere with a reason; release puts the unit back where it was.
function holdRepair(t, reason, by, resume) {
  const resumeStatus = resume || (t.status && t.status !== "HOLDING_BIN" && t.status !== "INTAKE" ? t.status : "REPAIRS_QUEUE");
  return withRepairStatus({ ...t, hold: { reason, at: new Date().toISOString(), by: by || "" }, resumeStatus }, "HOLDING_BIN", by);
}
function releaseRepair(t, by) {
  const back = t.resumeStatus && t.resumeStatus !== "HOLDING_BIN" ? t.resumeStatus : "REPAIRS_QUEUE";
  return withRepairStatus({ ...t, hold: null }, back, by);
}
function repairReminderDue(t, now = Date.now()) {
  if (t.status !== "HOLDING_BIN") return false;
  const last = t.reminderLastAt || t.at;
  return now - new Date(last).getTime() > 48 * 3600 * 1000;
}

// Automatic service-zone detection for the GTA pricing boundaries.
// Zone 1 core: Eglinton→Finch between Yonge Blvd→Victoria Park. Zone 2 extends out to
// Queen St→John St and Dufferin→Kennedy. Anything beyond that is out of the area.
const ZONE1_BOX = { s: 43.708, n: 43.780, w: -79.408, e: -79.298 };
const ZONE2_BOX = { s: 43.653, n: 43.818, w: -79.448, e: -79.271 };
function determineServiceZone(lat, lng) {
  const la = Number(lat), lo = Number(lng);
  if (!isFinite(la) || !isFinite(lo) || (la === 0 && lo === 0)) return null;
  const inBox = (b) => la >= b.s && la <= b.n && lo >= b.w && lo <= b.e;
  if (inBox(ZONE1_BOX)) return "z1";
  if (inBox(ZONE2_BOX)) return "z2";
  return "out";
}

// Service perimeter (Jasmine's rule): inside the zones books normally; outside
// the zones but within SERVICE_LIMIT_KM the office must OK the booking first
// ("ask" band); beyond the limit it can NEVER be booked. Distance is measured
// from the shop (centre of the Zone 1 box).
const SHOP_POINT = { lat: 43.744, lng: -79.353 };
const SERVICE_LIMIT_KM = 50;
// (distance uses the shared kmBetween haversine defined with the route math)
function serviceBand(lat, lng, limitKm = SERVICE_LIMIT_KM) {
  const zone = determineServiceZone(lat, lng);
  if (!zone) return { zone: null, band: null, km: null };
  const km = Math.round(kmBetween({ lat: Number(lat), lng: Number(lng) }, SHOP_POINT));
  if (zone === "z1" || zone === "z2") return { zone, band: "zone", km };
  return { zone, band: km <= limitKm ? "ask" : "never", km };
}
function lookupServiceZone(lat, lng, limitKm = SERVICE_LIMIT_KM) {
  const band = serviceBand(lat, lng, limitKm);
  return { ...band, ...zoneLookupLabel(band), createdClient: false };
}
function zoneLookupLabel(band) {
  if (!band || !band.zone) return { title: "Couldn't place that address", detail: "Try a fuller street address. No customer is created." };
  if (band.zone === "z1") return { title: "Zone 1", detail: band.km != null ? `${band.km} km from the shop — rough quote band` : "Inside Zone 1" };
  if (band.zone === "z2") return { title: "Zone 2", detail: band.km != null ? `${band.km} km from the shop — rough quote band` : "Inside Zone 2" };
  if (band.band === "ask") return { title: "Outside zones — ask service", detail: `${band.km} km from the shop` };
  if (band.band === "never") return { title: "Out of service area", detail: `${band.km} km from the shop` };
  return { title: "Outside zones", detail: band.km != null ? `${band.km} km from the shop` : "" };
}
// Staff quoting from a customer's own stored address: if geocode fails, reuse the
// zone already on that profile. Never creates a customer. Never invents a zone.
function normalizeLookupAddr(s) {
  return String(s || "").toLowerCase().replace(/[.,/#]/g, " ").replace(/\s+/g, " ").trim();
}
function clientAddrKeys(c) {
  const keys = [];
  const full = normalizeLookupAddr(c && c.address);
  if (full) keys.push(full);
  const streetCity = normalizeLookupAddr([c && c.street, c && c.city].filter((x) => String(x || "").trim()).join(" "));
  if (streetCity) keys.push(streetCity);
  return keys;
}
function addressesMatchLookup(query, client) {
  const q = normalizeLookupAddr(query);
  if (!q) return false;
  return clientAddrKeys(client).some((k) => k === q || k.startsWith(q) || q.startsWith(k));
}
function storedClientZoneBand(c) {
  if (!c) return null;
  if (c.geo && isFinite(Number(c.geo.lat)) && isFinite(Number(c.geo.lng))) {
    const band = serviceBand(c.geo.lat, c.geo.lng);
    if (band && band.zone) return band;
  }
  const raw = (c.billing && c.billing.zone) || c.zone || c.band || "";
  const z = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (z === "z1" || z === "z2") return { zone: z, band: "zone", km: null };
  if (z === "out" || z === "ask" || z === "never") return { zone: "out", band: z === "out" ? "ask" : z, km: null };
  const sb = c.serviceBand;
  if (sb && typeof sb === "object" && sb.zone) return { zone: sb.zone, band: sb.band || (sb.zone === "z1" || sb.zone === "z2" ? "zone" : sb.band), km: sb.km != null ? sb.km : null };
  return null;
}
function lookupStoredClientZone(query, clients) {
  const list = Array.isArray(clients) ? clients : [];
  for (const c of list) {
    if (!addressesMatchLookup(query, c)) continue;
    const band = storedClientZoneBand(c);
    if (!band || !band.zone) continue;
    return { ...band, ...zoneLookupLabel(band), createdClient: false };
  }
  return null;
}

// Retail-only accounts carry no route data — service-flavoured fields are cleared on
// save so a type flip never leaves orphaned schedule/access data behind. Structure and
// volume stay: retail water-test customers still need them for the dosing engine.
function stripServiceFields(c) {
  if (c.type !== "retail") return c;
  return { ...c, techId: "", order: "", days: [], day: "", frequency: "weekly", shortTermUntil: "", gateCode: "", keyLocation: "", accessNotes: "", dog: false };
}

// Dual-unit water volume: gallons stay the stored source of truth (the dosing engine
// runs on gallons); litres are always derived at 1 US gal = 3.78541 L.
const L_PER_GAL = 3.78541;
function galToL(g) {
  if (!String(g ?? "").trim()) return "";
  return String(Math.round(num(g) * L_PER_GAL));
}
function lToGal(l) {
  if (!String(l ?? "").trim()) return "";
  const g = num(l) / L_PER_GAL;
  return String(Number(g.toFixed(1)));
}
// Two linked inputs — type in either unit, the other follows live. Clearing one clears both.
function VolumeCalcPanel({ onApply }) {
  const [shape, setShape] = useState("rect");
  const [lengthFt, setLengthFt] = useState("");
  const [widthFt, setWidthFt] = useState("");
  const [diameterFt, setDiameterFt] = useState("");
  const [avgDepthFt, setAvgDepthFt] = useState("");
  const calc = volumeCalcGallons({
    shape, lengthFt, widthFt, diameterFt, avgDepthFt,
  });
  return (
    <div className="f-wide" style={{ border: "1px dashed var(--line)", borderRadius: 8, padding: "10px 12px", marginTop: 6 }}>
      <div className="sub" style={{ fontWeight: 700, marginBottom: 6 }}>{displayTitleCase("Custom volume calculator")}</div>
      <div className="seg" style={{ marginBottom: 8 }}>
        <button type="button" data-on={shape === "rect" ? 1 : 0} onClick={() => setShape("rect")}>Rectangle</button>
        <button type="button" data-on={shape === "round" ? 1 : 0} onClick={() => setShape("round")}>Round</button>
      </div>
      <div className="fields">
        {shape === "round" ? (
          <div><label>Diameter (ft)</label><input inputMode="decimal" value={diameterFt} onChange={(e) => setDiameterFt(e.target.value)} /></div>
        ) : (<>
          <div><label>Length (ft)</label><input inputMode="decimal" value={lengthFt} onChange={(e) => setLengthFt(e.target.value)} /></div>
          <div><label>Width (ft)</label><input inputMode="decimal" value={widthFt} onChange={(e) => setWidthFt(e.target.value)} /></div>
        </>)}
        <div><label>Avg depth (ft)</label><input inputMode="decimal" value={avgDepthFt} onChange={(e) => setAvgDepthFt(e.target.value)} /></div>
      </div>
      <div className="btnrow" style={{ marginTop: 8, alignItems: "center" }}>
        <span className="sub" style={{ marginTop: 0 }}>{calc.ok ? `${calc.gallons.toLocaleString()} gal` : "Enter size + depth"}</span>
        <button type="button" className="btn mini" disabled={!calc.ok}
          onClick={() => onApply(String(calc.gallons))}>Use this volume</button>
      </div>
    </div>
  );
}

function VolumePair({ label, gallons, onChange }) {
  const [focus, setFocus] = useState(null); // "gal" | "l" — the field being typed keeps its raw text
  const [text, setText] = useState("");
  const galShow = focus === "gal" ? text : (String(gallons ?? "").trim() ? String(gallons) : "");
  const lShow = focus === "l" ? text : galToL(gallons);
  return (<>
    <div>
      <label>{label} (gallons)</label>
      <input inputMode="decimal" placeholder="22,000" value={galShow}
        onFocus={() => { setFocus("gal"); setText(galShow); }}
        onBlur={() => setFocus(null)}
        onChange={(e) => { const v = e.target.value; setText(v); onChange(v.trim() === "" ? "" : v); }} />
    </div>
    <div>
      <label>{label} (litres)</label>
      <input inputMode="decimal" placeholder="83,000" value={lShow}
        onFocus={() => { setFocus("l"); setText(lShow); }}
        onBlur={() => setFocus(null)}
        onChange={(e) => { const v = e.target.value; setText(v); onChange(v.trim() === "" ? "" : lToGal(v)); }} />
    </div>
  </>);
}

// Scheduling confirmations: a call that leaves the holding bin (gains BOTH a day and a
// truck) queues a "confirm with the customer" item, sent by their Service preference.
function needsConfirm(oldJob, newJob) {
  if (!newJob || !newJob.clientId || !newJob.date || !newJob.techId || newJob.done || newJob.cancelled) return false;
  if (newJob.confirmPending || newJob.confirmSent) return false;
  if (!oldJob) return true;
  return !oldJob.date || !oldJob.techId;
}
// Which channel to confirm on: the first contact with a Service preference set.
// Nobody opted into email/text → it becomes a CALL task for the office.
function servicePrefFor(c) {
  const contacts = (c && c.contacts) || [];
  const k = contacts.find((x) => x.comms && x.comms.service && x.comms.service !== "none");
  if (!k) return { channel: "phone", contact: contacts[0] || {} };
  const v = k.comms.service; // email | text | both
  return { channel: v === "both" ? "text" : v, contact: k };
}
/* ---- After-service report (5.4): the digital door hanger. Plain text, built
   from what the visit actually recorded — notes, time on site, water readings,
   what was dosed, chemicals billed. Sent to the contact whose Service
   preference includes email. ---- */
const READING_LABEL = { fc: "Free chlorine", tc: "Total chlorine", ph: "pH", ta: "Total alkalinity",
  ch: "Calcium hardness", cya: "Stabilizer (CYA)", salt: "Salt", phos: "Phosphates", copper: "Copper", iron: "Iron" };
// The contact who should get service EMAIL (report) — comms.service email/both.
function serviceEmailContact(c) {
  return ((c && c.contacts) || []).find((k) => String(k.email || "").trim()
    && k.comms && (k.comms.service === "email" || k.comms.service === "both")) || null;
}
function afterServiceReport(client, contact, visit, test, bp) {
  const first = contact ? contactFirst(contact) : "";
  const b = [];
  b.push(first ? `Hi ${first},` : "Hello,");
  b.push("");
  b.push(`Here's your service report from ${(bp && bp.name) || "Pool Master Canada"} for ${visit.date}.`);
  b.push("");
  b.push(`What was done: ${visit.desc || visit.notes || "Scheduled service visit"}`);
  if (Number(visit.minutes) > 0) b.push(`Time on site: ${visit.minutes} minutes.`);
  const res = (test && test.results) || null;
  if (res) {
    const reads = Object.entries(READING_LABEL)
      .filter(([k]) => String(res[k] === undefined || res[k] === null ? "" : res[k]).trim() !== "")
      .map(([k, l]) => `${l} ${res[k]}`);
    if (reads.length) { b.push(""); b.push(`Water readings: ${reads.join(" · ")}`); }
    if (test.dosedSummary) b.push(`Treatment added: ${test.dosedSummary}`);
  }
  const mats = (visit.materials || []).filter((m) => m && m.desc);
  if (mats.length) {
    b.push("");
    b.push(`Chemicals used${num(visit.amount) > 0 ? " (billed to your account)" : ""}: ${mats.map((m) => `${m.qty}× ${m.desc}`).join("; ")}`);
  }
  b.push("");
  b.push("Questions? Just reply to this email.");
  b.push(`${(bp && bp.name) || "Pool Master Canada"}${bp && bp.phone ? " · " + bp.phone : ""}`);
  return { subject: `Your pool service report — ${visit.date}`, body: b.join("\n") };
}

// First name for greetings — split field first, else first word of the name.
// Every outgoing email/text/AI message addresses the person by first name.
function contactFirst(k) {
  return ((k && (k.firstName || String(k.name || "").trim().split(/\s+/)[0])) || "").trim();
}
function confirmMsgFor(c, j, contact) {
  const d = j.date ? new Date(j.date + "T12:00:00") : null;
  const day = d && !isNaN(d) ? d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : (j.date || "");
  const f = contactFirst(contact);
  return `${f ? `Hi ${f}, ` : ""}Pool Master: your ${(j.serviceType || "service visit").toLowerCase()} is confirmed for ${day}. If that day doesn't work, reply or call the shop and we'll move it. Thank you!`;
}

// Retail returns default to in-store credit; a money-back refund needs a manager's 4-digit PIN.
function managerByPin(staff, pin) {
  const p = String(pin || "").trim();
  if (p.length < 4) return null;
  return (staff || []).find((s) => s.active !== false && ["owner", "manager"].includes(s.roleId) && String(s.pin || "").trim() === p) || null;
}
// Store credit ledger on the client — positive delta grants credit, negative spends it.
function withStoreCredit(c, delta, entry) {
  const bal = Math.max(0, num(c.storeCredit) + delta);
  return { ...c, storeCredit: Number(bal.toFixed(2)), creditLog: [...(c.creditLog || []), entry].slice(-100) };
}
const posMoney = (n) => "$" + num(n).toFixed(2);
// Bulk-linked parts (e.g. 10L / 20L chlorine fills): the item holds no stock of its
// own — every unit sold drains `bulkQty` litres from the container part whose code is
// `bulkCode` (e.g. LC, the bulk liquid chlorine tank, counted per litre).
// Turns a list of sold {code, qty} into { code → qty } with bulk-linked codes re-routed.
function bulkRoute(parts, items) {
  const find = (code) => (parts || []).find((p) => (p.code || "").trim().toLowerCase() === code);
  const eff = {};
  for (const it of items || []) {
    const c = String(it.code || "").trim().toLowerCase(); if (!c) continue;
    const q = num(it.qty) || 0; if (!q) continue;
    const p = find(c);
    const bc = p && p.bulkCode ? String(p.bulkCode).trim().toLowerCase() : "";
    if (bc && bc !== c && find(bc)) eff[bc] = (eff[bc] || 0) + q * (num(p.bulkQty) > 0 ? num(p.bulkQty) : 1);
    else eff[c] = (eff[c] || 0) + q;
  }
  return eff;
}
// On-hand for selling: a bulk-linked part's stock is however many fills the container has left.
function bulkOnHand(p, parts) {
  const bc = p && p.bulkCode ? String(p.bulkCode).trim().toLowerCase() : "";
  if (!bc) return num(p.qty);
  const b = (parts || []).find((x) => (x.code || "").trim().toLowerCase() === bc);
  if (!b || b === p) return num(p.qty);
  const per = num(p.bulkQty) > 0 ? num(p.bulkQty) : 1;
  return Math.floor(num(b.qty) / per);
}
// Ontario point-of-sale relief for First Nations Status cardholders: the 8%
// provincial part of HST comes off, leaving 5% GST. Separate from the full
// tax-exempt flag (billing.taxable = false), which zeroes tax entirely.
function hstRateFor(c) { return c && c.statusExempt ? 5 : 13; }
function hstFactorFor(c) { return 1 + hstRateFor(c) / 100; }
const hstLabelFor = (c) => (c && c.statusExempt ? "5% GST (Status rebate)" : "13% HST");

// A customer's special price for an item, if one is set. Special prices supersede
// every other discount — level, custom and order discounts all leave these lines alone.
function spOf(cust, code) {
  const list = ((cust || {}).billing || {}).specialPrices || [];
  const k = String(code || "").trim().toLowerCase();
  if (!k) return null;
  return list.find((x) => String(x.code || "").trim().toLowerCase() === k) || null;
}
function posLineNet(it) {
  const gross = (num(it.qty) || 1) * num(it.price);
  const d = it.discType === "%" ? gross * (num(it.disc) / 100) : num(it.disc);
  return Math.max(0, gross - d);
}
function posTotals(s) {
  const items = s.items || [];
  const subtotal = items.reduce((a, it) => a + posLineNet(it), 0);
  // Tax applies per line — items flagged noTax (e.g. water) are never taxed.
  const taxableSub = items.reduce((a, it) => a + (it.noTax ? 0 : posLineNet(it)), 0);
  // Special-priced lines (it.sp) sit outside every discount — the order discount only touches the rest.
  const discSub = items.reduce((a, it) => a + (it.sp ? 0 : posLineNet(it)), 0);
  const odRaw = s.orderDiscType === "%" ? discSub * (num(s.orderDisc) / 100) : num(s.orderDisc);
  const orderDiscAmt = Math.min(Math.max(0, odRaw), discSub);
  const afterDisc = subtotal - orderDiscAmt;
  // Spread the order discount proportionally across the discountable lines only.
  const factor = discSub > 0 ? (discSub - orderDiscAmt) / discSub : 1;
  const taxableAfterDisc = items.reduce((a, it) => a + (it.noTax ? 0 : posLineNet(it) * (it.sp ? 1 : factor)), 0);
  const tax = s.taxExempt ? 0 : taxableAfterDisc * (num(s.taxRate) / 100);
  return { subtotal, orderDiscAmt, afterDisc, taxableSub, tax, total: afterDisc + tax };
}
// Complete sale is off whenever the cart has no line items (first load AND after Clear).
function posCartCanComplete(cart) {
  const items = Array.isArray(cart) ? cart : (cart && Array.isArray(cart.items) ? cart.items : []);
  return items.length > 0;
}
// Soft #1 — Confirm for billing must not create a $0 invoice from empty materials.
// Block when there are no billable materials/labour lines (not merely when total is $0 —
// gifted visits can be legitimately $0 *with* lines; empty lines are the Soft risk).
// Gifted / No-charge is a separate path; this gate only checks line presence.
const CONFIRM_BILLING_EMPTY_MSG = "Add materials or labour lines before confirming for billing";
function confirmBillingBillableLines(jobOrVisit) {
  if (!jobOrVisit || typeof jobOrVisit !== "object") return [];
  const lines = [];
  // Guided work-order: truck chemicals become the billed materials on Confirm for billing.
  const wf = jobOrVisit.wf;
  if (wf) {
    const chems = (((wf.results || {}).chems || {}).items) || [];
    chems.forEach((x) => {
      if (x && x.src === "truck" && (x.desc || x.code)) lines.push(x);
    });
  }
  // Visit / quote-like: materials + labour lines (gifted-with-lines still counts here).
  (jobOrVisit.materials || []).forEach((m) => {
    if (m && (m.desc || m.code || Number(m.price))) lines.push(m);
  });
  (jobOrVisit.labourLines || []).forEach((l) => {
    if (l && (l.desc || Number(l.rate))) lines.push(l);
  });
  return lines;
}
function confirmBillingCanProceed(jobOrVisit) {
  return confirmBillingBillableLines(jobOrVisit).length > 0;
}
function cashChangeDue(collected, total) {
  const raw = String(collected ?? "").trim();
  const c = Number(collected), t = Number(total);
  if (raw === "" || !isFinite(c) || c <= 0) {
    return { ok: false, collected: isFinite(c) ? c : 0, total: isFinite(t) ? t : 0, change: 0, reason: "collected" };
  }
  if (!isFinite(t) || t < 0) return { ok: false, collected: c, total: 0, change: 0, reason: "total" };
  const change = Math.round((c - t) * 100) / 100;
  if (c + 1e-9 < t) return { ok: false, collected: c, total: t, change, reason: "short" };
  return { ok: true, collected: c, total: t, change: Math.max(0, change) };
}
const posEsc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function posReceiptHtml(sale) {
  const t = posTotals(sale);
  const isRet = sale.type === "return";
  const rows = (sale.items || []).map((it) => {
    const qty = num(it.qty) || 1;
    const dtxt = num(it.disc) > 0 ? ` (−${it.discType === "%" ? num(it.disc) + "%" : posMoney(it.disc)})` : "";
    return `<tr><td>${posEsc([it.code, it.desc].filter(Boolean).join(" — ") || "Item")}${dtxt}</td><td class="r">${qty}</td><td class="r">${posMoney(it.price)}</td><td class="r">${posMoney(posLineNet(it))}</td></tr>`;
  }).join("");
  const when = sale.at ? new Date(sale.at).toLocaleString() : sale.date;
  const payLine = sale.method === "split"
    ? `Split · Cash ${posMoney((sale.payments || {}).cash)} · Credit ${posMoney((sale.payments || {}).credit)}`
    : `Paid by ${posEsc(sale.method === "storecredit" ? "in-store credit" : sale.method)}${sale.tendered ? ` · Tendered ${posMoney(sale.tendered)} · Change ${posMoney(sale.change)}` : ""}`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title>
<style>
  body{font:13px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;max-width:320px;margin:24px auto;padding:0 12px;}
  h1{font-size:18px;margin:0;color:#0B4F8A;text-align:center;} .muted{color:#5a6472;} .c{text-align:center;}
  table{width:100%;border-collapse:collapse;margin:12px 0;} td{padding:4px 2px;} .r{text-align:right;}
  .tot td{border-top:1px solid #cbd5e1;} .grand td{border-top:2px solid #12233b;font-weight:700;font-size:15px;}
  hr{border:0;border-top:1px dashed #cbd5e1;margin:10px 0;}
  @media print{body{margin:0;}}
</style></head><body onload="window.print()">
  <h1>Pool Master Canada</h1>
  <div class="c muted">${isRet ? "Return receipt" : "Sales receipt"}</div>
  <div class="c muted">${posEsc(when)}</div>
  <hr>
  <div class="muted">Customer: ${posEsc(sale.customerName || "Walk-in")}${sale.by ? ` · Served by ${posEsc(sale.by)}` : ""}</div>
  <table>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr class="tot"><td colspan="3">Subtotal</td><td class="r">${posMoney(t.subtotal)}</td></tr>
      ${t.orderDiscAmt > 0 ? `<tr><td colspan="3">Order discount</td><td class="r">−${posMoney(t.orderDiscAmt)}</td></tr>` : ""}
      <tr><td colspan="3">Tax ${sale.taxExempt ? "(exempt)" : `(${posEsc(sale.taxRate)}%)`}${sale.statusRebate ? " — First Nations Status rebate applied" : ""}${sale.statusIdCheckedBy ? `, Status Card ID checked by ${posEsc(sale.statusIdCheckedBy)}` : ""}</td><td class="r">${posMoney(t.tax)}</td></tr>
      <tr class="grand"><td colspan="3">${isRet ? "Refund" : "Total"}</td><td class="r">${posMoney(t.total)}</td></tr>
    </tfoot>
  </table>
  <div class="muted">${payLine}</div>
  ${(sale.refundLog || []).map((r) => `<div class="muted">Refund ${posMoney(r.total)} processed by ${posEsc(r.by || "?")} on ${posEsc(r.date)}</div>`).join("")}
  <hr>
  <div class="c muted">Thank you!</div>
</body></html>`;
}
function printPosSale(sale) { const w = window.open("", "_blank"); if (!w) return; w.document.write(posReceiptHtml(sale)); w.document.close(); }

function posReceiptText(sale) {
  const t = posTotals(sale);
  const lines = [`Pool Master Canada — ${sale.type === "return" ? "Return receipt" : "Sales receipt"}`, sale.at ? new Date(sale.at).toLocaleString() : sale.date, ""];
  (sale.items || []).forEach((it) => lines.push(`  ${[it.code, it.desc].filter(Boolean).join(" ") || "Item"} x${num(it.qty) || 1} — ${posMoney(posLineNet(it))}`));
  lines.push("", `Subtotal: ${posMoney(t.subtotal)}`);
  if (t.orderDiscAmt > 0) lines.push(`Order discount: -${posMoney(t.orderDiscAmt)}`);
  lines.push(`Tax: ${posMoney(t.tax)}`, `${sale.type === "return" ? "Refund" : "Total"}: ${posMoney(t.total)}`, "", "Thank you!\nPool Master Canada");
  return lines.join("\n");
}

function posDaySummary(sales) {
  const live = (sales || []).filter((s) => !s.voided);
  const salesOnly = live.filter((s) => s.type !== "return");
  const returns = live.filter((s) => s.type === "return");
  const gross = salesOnly.reduce((a, s) => a + num(s.total), 0);
  const refunds = returns.reduce((a, s) => a + num(s.total), 0);
  const tax = live.reduce((a, s) => a + (s.type === "return" ? -num(s.tax) : num(s.tax)), 0);
  const byMethod = {};
  const bump = (m, amt) => { byMethod[m] = (byMethod[m] || 0) + amt; };
  const byCentre = {};
  live.forEach((s) => {
    const sign = s.type === "return" ? -1 : 1;
    if (s.method === "split") { bump("cash", sign * num((s.payments || {}).cash)); bump("credit", sign * num((s.payments || {}).credit)); }
    else bump(s.method, sign * num(s.total));
    const c = (s.revCentre || "Unassigned") + (s.revSub ? " / " + s.revSub : "");
    byCentre[c] = (byCentre[c] || 0) + sign * num(s.total);
  });
  return { count: salesOnly.length, returnCount: returns.length, gross, refunds, net: gross - refunds, tax, byMethod, byCentre };
}
function posZReportHtml(sales, dayLabel, staffName) {
  const d = posDaySummary(sales);
  const mRows = Object.entries(d.byMethod).map(([m, v]) => `<tr><td>${posEsc(m)}</td><td class="r">${posMoney(v)}</td></tr>`).join("");
  const cRows = Object.entries(d.byCentre).map(([c, v]) => `<tr><td>${posEsc(c)}</td><td class="r">${posMoney(v)}</td></tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Day report</title>
<style>
  body{font:13px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;max-width:360px;margin:24px auto;padding:0 12px;}
  h1{font-size:18px;margin:0;color:#0B4F8A;text-align:center;} .muted{color:#5a6472;} .c{text-align:center;}
  table{width:100%;border-collapse:collapse;margin:10px 0;} td{padding:4px 2px;} .r{text-align:right;}
  .grand td{border-top:2px solid #12233b;font-weight:700;font-size:15px;} hr{border:0;border-top:1px dashed #cbd5e1;margin:10px 0;}
  @media print{body{margin:0;}}
</style></head><body onload="window.print()">
  <h1>Pool Master Canada</h1>
  <div class="c muted">Day report (Z) · ${posEsc(dayLabel)}</div>
  ${staffName ? `<div class="c muted">Cashed out by ${posEsc(staffName)}</div>` : ""}
  <hr>
  <table><tbody>
    <tr><td>Sales</td><td class="r">${d.count}</td></tr>
    <tr><td>Returns</td><td class="r">${d.returnCount}</td></tr>
    <tr><td>Gross sales</td><td class="r">${posMoney(d.gross)}</td></tr>
    <tr><td>Refunds</td><td class="r">−${posMoney(d.refunds)}</td></tr>
    <tr><td>Tax collected</td><td class="r">${posMoney(d.tax)}</td></tr>
    <tr class="grand"><td>Net takings</td><td class="r">${posMoney(d.net)}</td></tr>
  </tbody></table>
  <div class="muted">By revenue centre</div>
  <table><tbody>${cRows || `<tr><td class="muted">No takings.</td></tr>`}</tbody></table>
  <div class="muted">By payment method</div>
  <table><tbody>${mRows || `<tr><td class="muted">No takings.</td></tr>`}</tbody></table>
  <hr><div class="c muted">End of report</div>
</body></html>`;
}

// A stable colour per till category — for the coloured edge and label on each tile.
const POS_CAT_COLORS = ["#0B77C2", "#12A650", "#E0872B", "#8B5CF6", "#D4426A", "#0E9BA6", "#C2410C", "#4F6BED"];
function catColor(cat) {
  const s = String(cat || "Other");
  if (/water/i.test(s)) return "#0B77C2";
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return POS_CAT_COLORS[h % POS_CAT_COLORS.length];
}
// Stock is only tracked (and shown/flagged) when the part has a reorder level set.
const posTracked = (p) => !p.noTax && p.lowAt !== "" && p.lowAt != null;
function PosTile({ p, onAdd }) {
  const tracked = posTracked(p);
  const out = tracked && num(p.qty) <= 0;
  const color = catColor(p.category);
  return (
    <button type="button" className="postile" data-out={out ? 1 : 0} style={{ borderLeftColor: color }} onClick={() => onAdd(p.id)} title={p.name}>
      {p.image
        ? <img className="pt-img" src={p.image} alt="" />
        : <span className="pt-cat" style={{ color }}>{p.category || "Other"}</span>}
      <span className="pt-name">{p.name}</span>
      <span className="pt-foot">
        <span className="pt-price">{num(p.price) > 0 ? posMoney(p.price) : "Enter $"}</span>
        <span className={out ? "pt-out" : "pt-stk"}>{p.noTax ? "no tax" : out ? "out" : tracked ? num(p.qty) + " on hand" : ""}</span>
      </span>
    </button>
  );
}

// In-Store Credit / Return — from a walk-in receipt (a past POS sale) or a customer's
// retail/service invoice. Policy: returns default to in-store credit; giving money back
// (cash/card) requires a manager's PIN and is logged on the transaction.
function PosReturns({ posSales = [], clients = [], staff = [], me, onSale, onClose }) {
  const [source, setSource] = useState("receipt"); // receipt | invoice
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [sel, setSel] = useState({});
  const [method, setMethod] = useState("credit");
  const [taxRate, setTaxRate] = useState("13");
  const [done, setDone] = useState(null);
  const [custId, setCustId] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [invoices, setInvoices] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invNo, setInvNo] = useState("");
  const [returnType, setReturnType] = useState("credit"); // credit (default) | refund (manager only)
  const [override, setOverride] = useState(null);         // { id, name } of the authorizing manager
  const [pinPop, setPinPop] = useState(false);
  const [pinVal, setPinVal] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [creditCustId, setCreditCustId] = useState("");   // attach a profile to a walk-in receipt for credit
  const [ccSearch, setCcSearch] = useState("");
  const money = posMoney;
  const cust = clients.find((c) => c.id === custId) || null;
  // Who the in-store credit lands on: the sale's customer, the invoice customer, or an attached profile.
  const creditTargetId = picked ? (picked.kind === "sale" ? (picked.sale.customerId || creditCustId) : custId) : "";
  const creditTarget = clients.find((c) => c.id === creditTargetId) || null;
  const ccMatches = ccSearch.trim() ? clients.filter((c) => [c.name, c.address, ...(c.contacts || []).map((k) => `${k.name} ${k.phone} ${k.email}`)].join(" ").toLowerCase().includes(ccSearch.trim().toLowerCase())).slice(0, 6) : [];
  const tryPin = () => {
    const m = managerByPin(staff, pinVal);
    if (!m) { setPinErr("That PIN doesn't match an active manager or owner."); return; }
    setOverride({ id: m.id, name: m.name });
    setReturnType("refund");
    setPinPop(false); setPinVal(""); setPinErr("");
  };

  const saleList = (posSales || []).filter((s) => s.type !== "return" && !s.voided)
    .filter((s) => { const t = q.trim().toLowerCase(); return !t || (s.customerName || "").toLowerCase().includes(t) || String(s.total).includes(t) || (s.date || "").includes(t); })
    .slice().reverse().slice(0, 40);

  const pickedLines = (() => {
    if (!picked) return [];
    if (picked.kind === "sale") return (picked.sale.items || []).map((it, i) => ({ i, code: it.code, desc: it.desc || it.code || "Item", qty: num(it.qty) || 1, amount: posLineNet(it) }));
    const v = picked.visit; const lines = [];
    (v.materials || []).forEach((m, i) => lines.push({ i: "m" + i, code: m.code, desc: [m.code, m.desc].filter(Boolean).join(" ") || "Material", qty: num(m.qty) || 1, amount: (num(m.qty) || 1) * num(m.price) }));
    (v.labourLines || []).forEach((l, i) => lines.push({ i: "l" + i, code: "", desc: l.desc || "Labour", qty: 1, amount: num(l.rate) }));
    if (lines.length === 0 && num(v.amount)) lines.push({ i: "whole", code: "", desc: "Invoice " + (v.invoiceNo || v.workOrder || ""), qty: 1, amount: num(v.amount) });
    return lines;
  })();
  const allSelected = pickedLines.length > 0 && pickedLines.every((l) => sel[l.i]);
  const toggleAll = () => { const on = !allSelected; const next = {}; pickedLines.forEach((l) => { next[l.i] = on; }); setSel(next); };
  const selectedLines = pickedLines.filter((l) => sel[l.i]);
  const refundSubtotal = selectedLines.reduce((a, l) => a + l.amount, 0);
  const refundTax = refundSubtotal * (num(taxRate) / 100);
  const refundTotal = refundSubtotal + refundTax;

  const pickSale = (s) => { setPicked({ kind: "sale", sale: s }); setSel({}); setTaxRate(s.taxExempt ? "0" : (s.taxRate || "13")); setMethod(["cash", "debit", "credit"].includes(s.method) ? s.method : "credit"); setReturnType("credit"); setOverride(null); setCreditCustId(""); setCcSearch(""); };
  const pickInvoice = (v) => { setPicked({ kind: "invoice", visit: v }); setSel({}); setTaxRate("13"); setReturnType("credit"); setOverride(null); }; // invoice tax follows the account
  const loadInvoices = async (id) => { setInvLoading(true); let list = []; try { list = JSON.parse((await window.storage.get(VISIT_KEY(id))).value) || []; } catch { list = []; } setInvoices(list); setInvLoading(false); };
  const pickCust = (c) => { setCustId(c.id); setCustSearch(""); setPicked(null); setInvoices(null); loadInvoices(c.id); };
  const custMatches = custSearch.trim() ? clients.filter((c) => [c.name, c.address, ...(c.contacts || []).map((k) => `${k.name} ${k.phone} ${k.email}`)].join(" ").toLowerCase().includes(custSearch.trim().toLowerCase())).slice(0, 8) : [];
  const shownInvoices = (invoices || []).filter((v) => { const t = invNo.trim().toLowerCase(); return !t || (v.invoiceNo || "").toLowerCase().includes(t) || (v.workOrder || "").toLowerCase().includes(t); }).slice(0, 40);

  const process = () => {
    if (!selectedLines.length || refundTotal <= 0) return;
    if (returnType === "credit" && !creditTargetId) return; // credit needs an account to land on
    if (returnType === "refund" && !override) return;       // money back needs a manager
    const items = selectedLines.map((l) => ({ code: l.code || "", desc: l.desc, qty: String(l.qty), price: String(l.qty ? (l.amount / l.qty).toFixed(2) : l.amount), disc: "", discType: "$" }));
    const customerId = picked.kind === "sale" ? (picked.sale.customerId || creditCustId || "") : custId;
    const customerName = picked.kind === "sale"
      ? (picked.sale.customerName && picked.sale.customerName !== "Walk-in" ? picked.sale.customerName : (creditTarget ? creditTarget.name : "Walk-in"))
      : (cust ? cust.name : "Walk-in");
    const refFrom = picked.kind === "sale" ? ("Receipt " + (picked.sale.at ? new Date(picked.sale.at).toLocaleString() : picked.sale.date)) : ("Invoice " + (picked.visit.invoiceNo || picked.visit.workOrder || picked.visit.date));
    const sale = {
      id: cid(), at: new Date().toISOString(), date: fmtDate(new Date()), type: "return",
      returnType,
      override: returnType === "refund" && override ? { managerId: override.id, managerName: override.name, at: new Date().toISOString() } : null,
      items, orderDisc: "", orderDiscType: "$", orderDiscAmt: "0",
      subtotal: refundSubtotal.toFixed(2), taxRate, taxExempt: num(taxRate) === 0, tax: refundTax.toFixed(2), total: refundTotal.toFixed(2),
      method: returnType === "credit" ? "storecredit" : method, payments: null, tendered: "", change: "",
      revCentre: "", revSub: "", customerId, customerName, by: me ? me.name : "", refFrom,
      refSaleId: picked.kind === "sale" ? picked.sale.id : "",
      refClientId: picked.kind === "invoice" ? custId : "",
      refVisitId: picked.kind === "invoice" ? picked.visit.id : "",
    };
    onSale(sale); setDone(sale); setPicked(null); setSel({});
  };

  if (done) {
    return (
      <div>
        <div className="punchdone" data-dir="out" style={{ maxWidth: 480, margin: "0 auto" }}>
          <div className="big">{done.returnType === "credit" ? "In-store credit issued" : "Refund done"}</div>
          <div className="who4">{money(done.total)} · {done.customerName}</div>
          <div className="stamp">{done.items.length} line{done.items.length === 1 ? "" : "s"} · {done.refFrom}</div>
          {done.returnType === "refund" && done.override && (
            <div className="stamp" style={{ color: "#B7791F", fontWeight: 700 }}>Refund authorized by {done.override.managerName}</div>
          )}
          {done.returnType === "credit" && (
            <div className="stamp">The amount is on the customer's account as store credit — spendable at the till.</div>
          )}
          <div className="btnrow" style={{ justifyContent: "center", marginTop: 16 }}>
            <button className="btn ghost" onClick={() => printPosSale(done)}>Print</button>
            <button className="btn" onClick={() => setDone(null)}>Another return</button>
            <button className="btn ghost" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div><strong style={{ fontSize: 16 }}>In-Store Credit / Return</strong><div className="sub">Returns go to in-store credit — money back needs a manager. From a walk-in receipt or a customer's invoice.</div></div>
        <button className="btn ghost" onClick={onClose}>Back to till</button>
      </div>
      <div className="seg" style={{ marginBottom: 14 }}>
        <button type="button" data-on={source === "receipt" ? 1 : 0} onClick={() => { setSource("receipt"); setPicked(null); setSel({}); }}>Walk-in receipt</button>
        <button type="button" data-on={source === "invoice" ? 1 : 0} onClick={() => { setSource("invoice"); setPicked(null); setSel({}); }}>Customer account</button>
      </div>

      {!picked && source === "receipt" && (
        <div className="panel" style={{ padding: 13, maxWidth: 640 }}>
          <input placeholder="Search receipts — customer, amount, date" value={q} onChange={(e) => setQ(e.target.value)} />
          <div style={{ overflowX: "auto" }}>
            <table className="salest" style={{ marginTop: 10 }}>
              <thead><tr><th>When</th><th>Customer</th><th>Items</th><th style={{ textAlign: "right" }}>Total</th><th></th></tr></thead>
              <tbody>
                {saleList.length === 0 ? <tr><td colSpan={5} className="sub">No receipts found.</td></tr>
                  : saleList.map((s) => (
                    <tr key={s.id}>
                      <td className="mono">{s.at ? new Date(s.at).toLocaleString() : s.date}</td>
                      <td>{s.customerName}</td><td>{(s.items || []).length}</td>
                      <td className="mono" style={{ textAlign: "right" }}>{money(s.total)}</td>
                      <td><button className="btn ghost mini" onClick={() => pickSale(s)}>Open</button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!picked && source === "invoice" && (
        <div className="panel" style={{ padding: 13, maxWidth: 640 }}>
          {!custId ? (
            <>
              <label>Customer</label>
              <input placeholder="Search a customer — name, phone, address" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} />
              {custSearch.trim() && (
                <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 8 }}>
                  {custMatches.length === 0 ? <div className="sub" style={{ padding: "8px 10px" }}>No match.</div>
                    : custMatches.map((c) => <button key={c.id} type="button" className="poslookupitem" onClick={() => pickCust(c)}><b>{c.name}{c.trade ? " · Trade" : ""}</b><span className="sub" style={{ marginTop: 0 }}>{c.address || ((c.contacts || [])[0] || {}).phone || ""}</span></button>)}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="btnrow" style={{ marginBottom: 10 }}>
                <span className="chip" data-ch="email"><b>{cust ? cust.name : ""}</b></span>
                <button className="btn ghost mini" onClick={() => { setCustId(""); setInvoices(null); }}>Change customer</button>
              </div>
              <label>Invoice / work order #</label>
              <input placeholder="Type an invoice or work order number to filter" value={invNo} onChange={(e) => setInvNo(e.target.value)} />
              {invLoading ? <div className="sub" style={{ marginTop: 10 }}>Loading invoices…</div> : (
                <div style={{ overflowX: "auto" }}>
                  <table className="salest" style={{ marginTop: 10 }}>
                    <thead><tr><th>Date</th><th>Invoice #</th><th>Side</th><th style={{ textAlign: "right" }}>Amount</th><th></th></tr></thead>
                    <tbody>
                      {shownInvoices.length === 0 ? <tr><td colSpan={5} className="sub">No invoices for this customer.</td></tr>
                        : shownInvoices.map((v) => (
                          <tr key={v.id}>
                            <td className="mono">{v.date}</td><td>{v.invoiceNo || v.workOrder || "—"}</td>
                            <td>{visitSide(v) === "retail" ? "Retail" : "Service"}</td>
                            <td className="mono" style={{ textAlign: "right" }}>{v.amount ? money(v.amount) : "—"}</td>
                            <td><button className="btn ghost mini" onClick={() => pickInvoice(v)}>Open</button></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {picked && (
        <div className="panel" style={{ padding: 14, maxWidth: 640 }}>
          <div className="btnrow" style={{ marginBottom: 10, alignItems: "center" }}>
            <button className="btn ghost mini" onClick={() => { setPicked(null); setSel({}); }}>‹ Back</button>
            <strong>{picked.kind === "sale" ? "Receipt" : "Invoice " + (picked.visit.invoiceNo || picked.visit.workOrder || "")} · {picked.kind === "sale" ? picked.sale.customerName : (cust ? cust.name : "")}</strong>
          </div>
          <div className="sub" style={{ marginBottom: 8 }}>Tick the lines to refund, or refund the whole thing.</div>
          <table className="salest">
            <thead><tr><th style={{ width: 30 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th><th>Item</th><th>Qty</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
            <tbody>
              {pickedLines.map((l) => (
                <tr key={l.i}>
                  <td><input type="checkbox" checked={!!sel[l.i]} onChange={(e) => setSel((s) => ({ ...s, [l.i]: e.target.checked }))} /></td>
                  <td>{l.desc}</td><td>{l.qty}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12 }}>
            <label>How the money goes back</label>
            <div className="seg">
              <button type="button" data-on={returnType === "credit" ? 1 : 0}
                onClick={() => { setReturnType("credit"); setOverride(null); }}>Issue In-Store Credit</button>
              <button type="button" data-on={returnType === "refund" ? 1 : 0}
                onClick={() => { if (override) setReturnType("refund"); else { setPinVal(""); setPinErr(""); setPinPop(true); } }}>
                Issue Refund (original payment) 🔒
              </button>
            </div>
            {returnType === "refund" && override && (
              <div className="sub" style={{ marginTop: 5, color: "#B7791F", fontWeight: 700 }}>Refund authorized by {override.name} — logged on the transaction.</div>
            )}
            {returnType === "credit" && (
              <div className="sub" style={{ marginTop: 5 }}>Store policy: returns go to the customer's account as in-store credit, spendable at the till.</div>
            )}
          </div>

          {returnType === "credit" && picked.kind === "sale" && !picked.sale.customerId && (
            <div style={{ marginTop: 10 }}>
              {creditTarget ? (
                <div className="btnrow" style={{ alignItems: "center" }}>
                  <span className="chip" data-ch="email"><b>Credit goes to: {creditTarget.name}</b></span>
                  <button className="btn ghost mini" onClick={() => { setCreditCustId(""); setCcSearch(""); }}>Change</button>
                </div>
              ) : (
                <>
                  <label>Walk-in receipt — pick the customer account the credit lands on</label>
                  <input placeholder="Search a customer — name, phone, address" value={ccSearch} onChange={(e) => setCcSearch(e.target.value)} />
                  {ccSearch.trim() && (
                    <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 220 }}>
                      {ccMatches.length === 0 ? <div className="sub" style={{ padding: "8px 10px" }}>No match — add them as a customer first (in-store credit needs an account).</div>
                        : ccMatches.map((c) => <button key={c.id} type="button" className="poslookupitem" onClick={() => { setCreditCustId(c.id); setCcSearch(""); }}><b>{c.name}</b><span className="sub" style={{ marginTop: 0 }}>{c.address || ((c.contacts || [])[0] || {}).phone || ""}</span></button>)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="fields" style={{ marginTop: 10 }}>
            <div><label>Tax on the return (%)</label><input inputMode="decimal" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} /></div>
            {returnType === "refund" && (
              <div><label>Refund method</label><select value={method} onChange={(e) => setMethod(e.target.value)}>{POS_METHODS.filter(([k]) => k !== "split" && k !== "account").map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontWeight: 800, fontSize: 18, borderTop: "2px solid var(--ink)", paddingTop: 8 }}>
            <span>{returnType === "credit" ? "In-store credit" : "Refund total"}</span><span className="mono">{money(refundTotal)}</span>
          </div>
          <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 12, padding: "12px" }}
            disabled={!selectedLines.length || refundTotal <= 0 || (returnType === "credit" && !creditTargetId) || (returnType === "refund" && !override)} onClick={process}>
            {returnType === "credit" ? `Issue ${money(refundTotal)} in-store credit` : `Refund ${money(refundTotal)}`}
          </button>
          {returnType === "credit" && !creditTargetId && <div className="sub" style={{ marginTop: 6, color: "var(--clay)", fontWeight: 650 }}>Pick the customer account the credit lands on.</div>}
          <div className="sub" style={{ marginTop: 8 }}>Returned items go back into inventory and the return is logged in the day report{returnType === "refund" ? " with the authorizing manager" : ""}.</div>

          {pinPop && (
            <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") { setPinPop(false); setReturnType("credit"); } }}>
              <div className="sheet" style={{ maxWidth: 380 }}>
                <div className="bar">
                  <div><strong style={{ fontSize: 16 }}>Manager authorization</strong><div className="sub">A money-back refund needs a manager or owner</div></div>
                  <button className="btn ghost" onClick={() => { setPinPop(false); setReturnType("credit"); }}>Cancel</button>
                </div>
                <div className="pad">
                  <label>Manager / owner 4-digit PIN</label>
                  <input type="password" inputMode="numeric" maxLength={6} autoFocus value={pinVal}
                    onChange={(e) => { setPinVal(e.target.value.replace(/[^0-9]/g, "")); setPinErr(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") tryPin(); }}
                    style={{ fontSize: 22, letterSpacing: 8, textAlign: "center" }} />
                  {pinErr && <div className="sub" style={{ color: "var(--clay)", fontWeight: 700, marginTop: 6 }}>{pinErr}</div>}
                  <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 12 }} disabled={pinVal.length < 4} onClick={tryPin}>
                    Authorize refund
                  </button>
                  <div className="sub" style={{ marginTop: 8 }}>The authorizing manager's name is logged on the refund. Cancel keeps the return as in-store credit.</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ZoneLookupBar({ clients = [] }) {
  const [addr, setAddr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    const q = addr.trim();
    if (!q) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const g = await geocodeAddr(q);
      const hasGeo = g && isFinite(Number(g.lat)) && isFinite(Number(g.lng)) && !(Number(g.lat) === 0 && Number(g.lng) === 0);
      if (hasGeo) { setResult(lookupServiceZone(g.lat, g.lng)); }
      else {
        const stored = lookupStoredClientZone(q, clients);
        if (stored) setResult(stored);
        else setErr("Couldn't place that address — no customer was created.");
      }
    } catch { setErr("Lookup failed — try again. No customer was created."); }
    setBusy(false);
  };
  return (
    <div className="panel" style={{ padding: 12, marginBottom: 12, background: "#F7FAFD" }}>
      <b>Zone 1 / Zone 2 lookup</b>
      <div className="sub">Rough quote band for an address — does not create a customer.</div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <input value={addr} placeholder="Type an address — no account is created" style={{ flex: 1, minWidth: 180 }}
          onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") run(); }} />
        <button type="button" className="btn" disabled={busy || !addr.trim()} onClick={run}>{busy ? "Looking…" : "Look up"}</button>
      </div>
      {err && <div className="sub" style={{ color: "var(--clay)", marginTop: 6 }}>{err}</div>}
      {result && (
        <div style={{ marginTop: 8, fontWeight: 800 }}>{result.title}
          <div className="sub" style={{ fontWeight: 600 }}>{result.detail}</div>
        </div>
      )}
    </div>
  );
}
function ServiceCatalogBody({ services = [], serviceTypes = [], clients = [] }) {
  const [q, setQ] = useState("");
  const rows = filterServiceCatalog(serviceCatalogRows(services, serviceTypes), q);
  return (
    <>
      <ZoneLookupBar clients={clients} />
      <input autoFocus placeholder="Search by name, what it includes, or type…"
        value={q} onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 12, fontSize: 16, padding: "12px 14px" }} />
      {rows.map((s) => {
        const points = catalogDescPoints(s.desc);
        return (
        <div key={s.id} className="panel" style={{ padding: "12px 14px", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <b style={{ fontSize: 15, flex: 1, minWidth: 200 }}>{s.name}</b>
            <span className="doc" style={{ background: CATALOG_KIND_INK[s.kind] || CATALOG_KIND_INK.service }}>{s.kindLabel}</span>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{money(num(s.rate))}<span style={{ fontWeight: 600, fontSize: 12, color: "#5A6E86" }}> Zone 1</span></span>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{money(num(s.rateZone2))}<span style={{ fontWeight: 600, fontSize: 12, color: "#5A6E86" }}> Zone 2</span></span>
          </div>
          {points.length
            ? <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.45 }}>{points.map((pt, i) => <li key={i}>{pt}</li>)}</ul>
            : <div className="sub" style={{ marginTop: 6 }}>{CATALOG_DESC_PLACEHOLDER}</div>}
        </div>
        );
      })}
      {rows.length === 0 && <div className="empty">{String(q || "").trim() ? "No services match that search." : "No services in the catalogue yet."}</div>}
    </>
  );
}
function ServiceCatalogSheet({ services = [], serviceTypes = [], clients = [], onClose }) {
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose && onClose(); }}>
      <div className="sheet" style={{ maxWidth: 720 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Service catalog</strong>
            <div className="sub">Look up a service — what it includes and what it costs. Reference only; it does not book a call.</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          <ServiceCatalogBody services={services} serviceTypes={serviceTypes} clients={clients} />
        </div>
      </div>
    </div>
  );
}

function PosView({ parts = [], services = [], serviceTypes = [], clients = [], me, isOwner, revCentres = [], posSales = [], posHeld = [], staff = [], onSale, onVoid, onHold, onDropHeld, onEmail, onAddCustomer }) {
  const activeParts = parts.filter((p) => p.active !== false);
  const activeCentres = revCentres.filter((r) => r.active !== false && (isOwner || !r.ownerOnly));
  const [screen, setScreen] = useState("start"); // start | sell | held | report | returns
  const [mode, setMode] = useState("sale"); // sale | return
  const [cart, setCart] = useState([]);
  const [custId, setCustId] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [taxRate, setTaxRate] = useState("13");
  const [taxExempt, setTaxExempt] = useState(false);
  const [orderDisc, setOrderDisc] = useState("");
  const [orderDiscType, setOrderDiscType] = useState("%");
  const [method, setMethod] = useState("credit");
  const [revCentre, setRevCentre] = useState("");
  const [revSub, setRevSub] = useState("");
  const [tendered, setTendered] = useState("");
  const [splitCash, setSplitCash] = useState("");
  const [done, setDone] = useState(null);
  const [cat, setCat] = useState("★ Quick keys");
  const [prodSearch, setProdSearch] = useState("");
  const [custLookup, setCustLookup] = useState(false);
  const [heldRef, setHeldRef] = useState(""); // held-sale id this cart came from (links special orders)
  const [idAsk, setIdAsk] = useState(false); // Status-Card ID check gate before a rebate sale completes
  const [cashAsk, setCashAsk] = useState(false); // till: amount collected → change before the sale closes
  const [cashCollected, setCashCollected] = useState("");

  const cats = ["★ Quick keys", ...Array.from(new Set(activeParts.map((p) => p.category || "Other")))];
  const gridItems = activeParts.filter((p) => (cat === "★ Quick keys" ? p.favorite : (p.category || "Other") === cat));
  const prodMatches = prodSearch.trim()
    ? activeParts.filter((p) => {
        const pq = prodSearch.trim().toLowerCase();
        return (p.code || "").toLowerCase().includes(pq) || (p.name || "").toLowerCase().includes(pq);
      }).slice(0, 8)
    : [];

  const addItem = () => setCart((c) => [...c, { id: cid(), code: "", desc: "", qty: "1", price: "", disc: "", discType: "$" }]);
  const setItem = (id, k, v) => setCart((c) => c.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const dropItem = (id) => setCart((c) => c.filter((x) => x.id !== id));
  const onCode = (id, code) => setCart((c) => c.map((x) => {
    if (x.id !== id) return x;
    const hit = activeParts.find((p) => (p.code || "").trim().toLowerCase() === code.trim().toLowerCase());
    const nx = { ...x, code };
    if (hit) {
      const sp = spOf(cust, hit.code);
      if (!x.desc) nx.desc = hit.name || "";
      if (sp) { nx.price = sp.price; nx.disc = ""; nx.sp = 1; }
      else if (!x.price) nx.price = hit.price || "";
      nx.noTax = !!hit.noTax;
    }
    return nx;
  }));
  const addFromCatalogue = (pid) => {
    const p = activeParts.find((x) => x.id === pid); if (!p) return;
    const sp = spOf(cust, p.code);
    setCart((c) => {
      const ex = p.code && c.find((x) => (x.code || "").toLowerCase() === p.code.toLowerCase());
      if (ex) return c.map((x) => (x.id === ex.id ? { ...x, qty: String((num(x.qty) || 0) + 1) } : x));
      return [...c, { id: cid(), code: p.code || "", desc: p.name || "", qty: "1", price: sp ? sp.price : (p.price || ""), disc: "", discType: "$", noTax: !!p.noTax, ...(sp ? { sp: 1 } : {}) }];
    });
  };

  const pickCustomer = (id) => {
    setCustId(id);
    const c = clients.find((x) => x.id === id);
    // Status cardholders bill 5% GST at the till; everyone else 13% HST.
    setTaxRate(String(hstRateFor(c)));
    // Re-price the cart for this customer's special prices (and undo the last customer's).
    setCart((cart) => cart.map((x) => {
      const sp = c ? spOf(c, x.code) : null;
      if (sp) return { ...x, price: sp.price, disc: "", sp: 1 };
      if (x.sp) {
        const p = activeParts.find((pp) => (pp.code || "").trim().toLowerCase() === String(x.code || "").trim().toLowerCase());
        return { ...x, price: p ? (p.price || "") : x.price, sp: undefined };
      }
      return x;
    }));
    if (c) {
      const md = num(((c.billing || {}).discounts || {}).material);
      if (md > 0) { setOrderDiscType("%"); setOrderDisc(String(md)); }
      setRevCentre(""); setRevSub("");
    } else { setRevCentre(""); setRevSub(""); }
  };

  const cust = clients.find((c) => c.id === custId) || null;
  const exempt = taxExempt; // whole-sale tax exemption (manual). Water is untaxed per-line instead.
  const rc = revCentre || (activeCentres[0] ? activeCentres[0].name : "");
  const rcObj = activeCentres.find((r) => r.name === rc);
  const rcSubs = ((rcObj && rcObj.subs) || []).filter(Boolean);
  const custMatches = custSearch.trim()
    ? clients.filter((c) => {
        const q = custSearch.trim().toLowerCase();
        const hay = [c.name, c.address, ...(c.contacts || []).map((k) => `${k.name} ${k.phone} ${k.email}`)].join(" ").toLowerCase();
        return hay.includes(q);
      }).slice(0, 8)
    : [];
  const live = { items: cart, orderDisc, orderDiscType, taxRate, taxExempt: exempt };
  const t = posTotals(live);
  const change = num(tendered) - t.total;
  const cardPortion = Math.max(0, t.total - num(splitCash));
  const accountNoCust = method === "account" && !custId;
  const scShort = method === "storecredit" && (!cust || num(cust.storeCredit) < t.total - 0.005);
  const isRet = mode === "return";

  const resetSale = () => { setCart([]); setTendered(""); setSplitCash(""); setOrderDisc(""); setRevSub(""); setMode("sale"); setCashAsk(false); setCashCollected(""); };

  const complete = (idChecked) => {
    if (!posCartCanComplete(cart) || t.total <= 0 || accountNoCust || scShort) return;
    if (method === "cash" && !isRet) {
      const ch = cashChangeDue(cashAsk ? cashCollected : tendered, t.total);
      if (!ch.ok) { setCashCollected(tendered); setCashAsk(true); return; }
      if (!cashAsk) { setCashCollected(tendered || String(t.total.toFixed(2))); setCashAsk(true); return; }
    }
    const gaps = saleSerialGaps(cart, parts);
    if (gaps.length) { alert(`Serial number required before this sale can complete:\n${gaps.map((g) => `• ${g.desc} — ${g.have} of ${g.need} serial${g.need === 1 ? "" : "s"} recorded`).join("\n")}\n\nType it off the unit (or pick from shelf stock) on the line.`); return; }
    const cust = clients.find((c) => c.id === custId);
    // First Nations HST Rebate on this sale → the clerk must confirm the
    // physical Status Card was checked before the reduced rate goes through.
    if (cust && cust.statusExempt && num(taxRate) === 5 && !idChecked) { setIdAsk(true); return; }
    const sale = {
      id: cid(), at: new Date().toISOString(), date: fmtDate(new Date()), type: mode,
      items: cart.map(({ id, ...r }) => r),
      orderDisc, orderDiscType, orderDiscAmt: t.orderDiscAmt.toFixed(2),
      subtotal: t.subtotal.toFixed(2), taxRate, taxExempt: exempt, tax: t.tax.toFixed(2), total: t.total.toFixed(2),
      statusRebate: !!(cust && cust.statusExempt && num(taxRate) === 5),
      statusIdCheckedBy: cust && cust.statusExempt && num(taxRate) === 5 ? (me ? me.name : "staff") : "",
      method, payments: method === "split" ? { cash: num(splitCash).toFixed(2), credit: cardPortion.toFixed(2) } : null,
      tendered: method === "cash" ? num(cashAsk ? cashCollected : tendered).toFixed(2) : "", change: method === "cash" ? cashChangeDue(cashAsk ? cashCollected : tendered, t.total).change.toFixed(2) : "",
      revCentre: rc, revSub,
      customerId: custId || "", customerName: cust ? cust.name : "Walk-in", by: me ? me.name : "",
      fromHeldId: heldRef || "",
    };
    onSale(sale);
    setHeldRef("");
    setDone(sale); resetSale(); setIdAsk(false);
  };
  const newSale = () => { setDone(null); setCustId(""); setMethod("credit"); setTaxExempt(false); setScreen("start"); };

  const hold = () => {
    if (!cart.length) return;
    const cust = clients.find((c) => c.id === custId);
    onHold({ id: cid(), at: new Date().toISOString(), customerId: custId || "", customerName: cust ? cust.name : "Walk-in",
      items: cart.map(({ id, ...r }) => r), orderDisc, orderDiscType, taxRate, taxExempt, revCentre: rc, mode });
    resetSale(); setCustId("");
  };
  const recall = (h) => {
    setCart((h.items || []).map((it) => ({ id: cid(), disc: "", discType: "$", ...it })));
    setCustId(h.customerId || ""); setOrderDisc(h.orderDisc || ""); setOrderDiscType(h.orderDiscType || "%");
    setTaxRate(h.taxRate || "13"); setTaxExempt(!!h.taxExempt); setRevCentre(h.revCentre || ""); setMode(h.mode || "sale");
    setHeldRef(h.id); // completing this sale settles anything linked to the held ticket (special orders)
    onDropHeld(h.id); setScreen("sell");
  };
  const emailReceipt = (sale) => {
    const c = clients.find((x) => x.id === sale.customerId);
    const to = c ? ((c.contacts || [])[0] || {}).email || "" : "";
    onEmail({ kind: "mail", client: c || null, to, subject: "Your Pool Master receipt", body: posReceiptText(sale) });
  };

  const money = posMoney;
  const today = fmtDate(new Date());
  const todays = (posSales || []).filter((s) => s.date === today);
  const day = posDaySummary(todays);
  const printZ = () => { const w = window.open("", "_blank"); if (!w) return; w.document.write(posZReportHtml(todays, today, me ? me.name : "")); w.document.close(); };

  // Customer search window — shared by the start screen and the till.
  const lookupModal = custLookup && (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setCustLookup(false); }}>
      <div className="sheet" style={{ maxWidth: 718 }}>
        <div className="bar">
          <div><strong style={{ fontSize: 16 }}>Attach a customer</strong><div className="sub">Search an account to link to this sale</div></div>
          <button className="btn ghost" onClick={() => setCustLookup(false)}>Close</button>
        </div>
        <div className="pad">
          <input autoFocus placeholder="Search a customer — name, phone, address" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} style={{ padding: "15px 14px", fontSize: 18 }} />
          {custSearch.trim() ? (
            custMatches.length === 0 ? (
              <div style={{ marginTop: 14, textAlign: "center" }}>
                <div className="sub" style={{ marginBottom: 12 }}>No customer found matching “{custSearch.trim()}”. They may not be in the system yet.</div>
                {onAddCustomer && (
                  <button className="btn" style={{ width: "100%", justifyContent: "center", fontSize: 15.5, fontWeight: 750, padding: "14px 16px" }}
                    onClick={() => onAddCustomer(custSearch.trim())}>
                    <PlusIcon size={15} /> Add New Customer Now
                  </button>
                )}
              </div>
            ) : (
              <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 8, maxHeight: 470 }}>
                {custMatches.map((c) => (
                  <button key={c.id} type="button" className="poslookupitem" onClick={() => { pickCustomer(c.id); setCustSearch(""); setCustLookup(false); setScreen("sell"); }}>
                    <b>{c.name}{c.trade ? " · Trade" : ""}</b>
                    <span className="sub" style={{ marginTop: 0 }}>{c.address || ((c.contacts || [])[0] || {}).phone || "No address on file"}</span>
                  </button>
                ))}
              </div>
            )
          ) : <div className="sub" style={{ marginTop: 10 }}>Start typing a name, phone, or address to find a customer.</div>}
        </div>
      </div>
    </div>
  );

  // ----- done (receipt) -----
  if (done) {
    return (
      <div>
        <div className="punchdone" data-dir={done.type === "return" ? "out" : "in"} style={{ maxWidth: 480, margin: "0 auto" }}>
          <div className="big">{done.type === "return" ? "Refund done" : "Sale complete"}</div>
          <div className="who4">{money(done.total)} · {done.customerName}</div>
          <div className="stamp">{done.items.length} item{done.items.length === 1 ? "" : "s"} · {done.method === "split" ? "split payment" : "paid by " + done.method}
            {done.tendered ? ` · change ${money(done.change)}` : ""}</div>
          <div className="btnrow" style={{ justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
            <button className="btn ghost" onClick={() => printPosSale(done)}>Print receipt</button>
            <button className="btn ghost" onClick={() => emailReceipt(done)}>Email receipt</button>
            <button className="btn" onClick={newSale}>New sale</button>
          </div>
        </div>
      </div>
    );
  }

  // ----- returns / refunds -----
  if (screen === "returns") {
    return <PosReturns posSales={posSales} clients={clients} staff={staff} me={me} onSale={onSale} onClose={() => setScreen("sell")} />;
  }

  // ----- start: choose account vs walk-in -----
  // ----- held sales -----
  if (screen === "held") {
    return (
      <div>
        <div className="bar" style={{ marginBottom: 12 }}>
          <div><strong style={{ fontSize: 16 }}>Held sales</strong><div className="sub">Parked carts waiting to be finished</div></div>
          <button className="btn ghost" onClick={() => setScreen("sell")}>Back to till</button>
        </div>
        {posHeld.length === 0 ? <div className="empty">Nothing parked. Use “Hold” on a sale to park it here.</div> : (
          posHeld.slice().reverse().map((h) => (
            <div key={h.id} className="visit">
              <div className="vhead">
                <span className="vdate">{h.at ? new Date(h.at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : ""}</span>
                <div className="vdocs">
                  <span className="doc">{h.customerName || "Walk-in"}</span>
                  <span className="doc inv">{(h.items || []).length} item{(h.items || []).length === 1 ? "" : "s"}</span>
                  {h.mode === "return" && <span className="doc" style={{ background: "#B31212" }}>Return</span>}
                  {h.soId && <span className="doc" style={{ background: "#B7791F" }}>Special order — pay before pickup</span>}
                </div>
              </div>
              <div className="vnotes" style={{ fontSize: 12.5 }}>{(h.items || []).map((it) => `${it.desc || it.code || "item"} ×${num(it.qty) || 1}`).join("; ")}</div>
              <div className="btnrow" style={{ marginTop: 9 }}>
                <button className="btn mini" onClick={() => recall(h)}>Recall</button>
                <button className="btn warn mini" onClick={() => onDropHeld(h.id)}>Discard</button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  // ----- day report (Z) -----
  if (screen === "report") {
    return (
      <div>
        <div className="bar" style={{ marginBottom: 12 }}>
          <div><strong style={{ fontSize: 16 }}>Day report — {today}</strong><div className="sub">Cash-out / Z report for today</div></div>
          <div className="btnrow" style={{ alignItems: "center" }}>
            <button className="btn ghost" onClick={printZ}>Print</button>
            <button className="btn ghost" onClick={() => csvDownload(`sales-${today}.csv`,
              [["Time", "Customer", "By", "Type", "Method", "Items", "Subtotal", "Tax", "Total", "Voided"],
                ...todays.map((s2) => [s2.at ? new Date(s2.at).toLocaleTimeString() : "", s2.customerName || "Walk-in", s2.by || "",
                  s2.type || "sale", s2.method || "", (s2.items || []).length, s2.subtotal || "", s2.tax || "", s2.total || "", s2.voided ? "yes" : ""])])}>CSV</button>
            <button className="btn ghost" onClick={() => setScreen("sell")}>Back to till</button>
          </div>
        </div>
        <div className="counts" style={{ marginBottom: 14 }}>
          <div className="count-cell"><div className="n">{day.count}</div><div className="l">Sales</div></div>
          <div className="count-cell"><div className="n">{day.returnCount}</div><div className="l">Returns</div></div>
          <div className="count-cell"><div className="n">{money(day.gross)}</div><div className="l">Gross</div></div>
          <div className="count-cell"><div className="n">{money(day.net)}</div><div className="l">Net takings</div></div>
        </div>
        <table className="salest">
          <tbody>
            <tr><td>Gross sales</td><td className="mono" style={{ textAlign: "right" }}>{money(day.gross)}</td></tr>
            <tr><td>Refunds</td><td className="mono" style={{ textAlign: "right" }}>−{money(day.refunds)}</td></tr>
            <tr><td>Tax collected</td><td className="mono" style={{ textAlign: "right" }}>{money(day.tax)}</td></tr>
            <tr><td><b>Net takings</b></td><td className="mono" style={{ textAlign: "right" }}><b>{money(day.net)}</b></td></tr>
          </tbody>
        </table>
        <div className="sechead">By revenue centre</div>
        <table className="salest">
          <tbody>
            {(() => {
              const rows = Object.entries(day.byCentre);
              return rows.length === 0 ? <tr><td className="sub">No takings today.</td></tr>
                : rows.map(([c, v]) => <tr key={c}><td>{c}</td><td className="mono" style={{ textAlign: "right" }}>{money(v)}</td></tr>);
            })()}
          </tbody>
        </table>
        <div className="sechead">By payment method</div>
        <table className="salest">
          <tbody>
            {Object.keys(day.byMethod).length === 0 ? <tr><td className="sub">No takings today.</td></tr>
              : Object.entries(day.byMethod).map(([m, v]) => <tr key={m}><td style={{ textTransform: "capitalize" }}>{m}</td><td className="mono" style={{ textAlign: "right" }}>{money(v)}</td></tr>)}
          </tbody>
        </table>
        <div className="sechead">Today's sales</div>
        <table className="salest">
          <thead><tr><th>Time</th><th>Customer</th><th>By</th><th>Items</th><th>Pay</th><th style={{ textAlign: "right" }}>Total</th><th></th></tr></thead>
          <tbody>
            {todays.length === 0 ? <tr><td colSpan={7} className="sub">No sales yet today.</td></tr>
              : todays.slice().reverse().map((s) => (
                <tr key={s.id} style={s.voided ? { opacity: 0.5, textDecoration: "line-through" } : undefined}>
                  <td className="mono">{s.at ? new Date(s.at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : ""}</td>
                  <td>{s.customerName || "Walk-in"}</td>
                  <td>{s.by || ""}</td>
                  <td>{(s.items || []).length}{s.type === "return" && <span className="doc" style={{ background: "#B31212", marginLeft: 6 }}>Return</span>}{s.voided && <span className="doc" style={{ background: "#5A6E86", marginLeft: 6 }}>Void</span>}</td>
                  <td style={{ textTransform: "capitalize" }}>{s.method}</td>
                  <td className="mono" style={{ textAlign: "right" }}><b>{money(s.total)}</b></td>
                  <td>
                    <div className="btnrow" style={{ justifyContent: "flex-end", flexWrap: "nowrap" }}>
                      <button className="btn ghost mini" onClick={() => printPosSale(s)}>Receipt</button>
                      {!s.voided && s.type !== "return" && <button className="btn warn mini" onClick={() => { if (window.confirm("Void this sale and put the stock back?")) onVoid(s.id); }}>Void</button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ----- start: choose customer look-up or walk-in -----
  if (screen === "start") {
    return (
      <div>
        <div className="bar" style={{ marginBottom: 16 }}>
          <div>
            <strong style={{ fontSize: 16 }}>Retail point of sale</strong>
            <div className="sub">Start a sale · Today {money(day.net)}</div>
          </div>
        </div>
        <div className="posstart" style={{ maxWidth: "none", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
          <button type="button" className="posstart-btn" onClick={() => { setCustSearch(""); setCustLookup(true); }}>
            <CustomersIcon size={38} />
            <span className="posstart-lbl">Customer look up</span>
            <span className="posstart-sub">Search an account and attach it to the sale</span>
          </button>
          <button type="button" className="posstart-btn" onClick={() => { pickCustomer(""); setScreen("sell"); }}>
            <RetailIcon size={38} />
            <span className="posstart-lbl">Walk-in sale</span>
            <span className="posstart-sub">Ring up without a customer account</span>
          </button>
          <button type="button" className="posstart-btn" onClick={() => setScreen("returns")}>
            <QuoteIcon size={36} />
            <span className="posstart-lbl">In-Store Credit / Return</span>
            <span className="posstart-sub">Refund from a receipt or invoice</span>
          </button>
          <button type="button" className="posstart-btn" onClick={() => setScreen("held")}>
            <TimeOffIcon size={36} />
            <span className="posstart-lbl">Held{posHeld.length ? ` (${posHeld.length})` : ""}</span>
            <span className="posstart-sub">Parked sales waiting to be finished</span>
          </button>
          <button type="button" className="posstart-btn" onClick={() => setScreen("guide")}>
            <ListIcon size={36} />
            <span className="posstart-lbl">Service Guide</span>
            <span className="posstart-sub">What every service includes, with Zone 1 / Zone 2 pricing — for the counter and the phone</span>
          </button>
        </div>
        <div style={{ maxWidth: 800, margin: "16px auto 0" }}>
          <button type="button" className="posstart-btn" style={{ width: "100%" }} onClick={() => setScreen("report")}>
            <DashboardIcon size={36} />
            <span className="posstart-lbl">End of day reports</span>
            <span className="posstart-sub">Cash-out / Z report for today</span>
          </button>
        </div>
        {lookupModal}
      </div>
    );
  }

  // ----- service guide: same rate-driven catalog as Service Schedule (searchable, reference only) -----
  if (screen === "guide") {
    return (
      <div>
        <div className="bar" style={{ marginBottom: 12 }}>
          <div>
            <strong style={{ fontSize: 16 }}>Service Guide</strong>
            <div className="sub">What each service includes and what it costs — read this to the customer with confidence. Prices come from Inventory → Labour &amp; services.</div>
          </div>
          <button className="btn ghost" onClick={() => setScreen("start")}>‹ Retail home</button>
        </div>
        <ServiceCatalogBody services={services} serviceTypes={serviceTypes} clients={clients} />
      </div>
    );
  }

  // ----- sell screen -----
  return (
    <div>
      <div className="bar" style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn ghost mini" title="Back to start" onClick={() => setScreen("start")}>‹</button>
          <div>
            <strong style={{ fontSize: 16 }}>{custId && cust ? cust.name : "Walk-in sale"}</strong>
            <div className="sub">{isRet ? "Return mode — items go back on the shelf" : "Scan or tap a product · stock drops as you sell"} · Today {money(day.net)}</div>
          </div>
        </div>
        <div className="btnrow" style={{ alignItems: "center" }}>
          <div className="seg" style={{ margin: 0 }}>
            <button type="button" data-on={mode === "sale" ? 1 : 0} onClick={() => setMode("sale")}>Sale</button>
            <button type="button" data-on={mode === "return" ? 1 : 0} onClick={() => setMode("return")}>Return</button>
          </div>
          <button className="btn ghost" onClick={() => setScreen("returns")}>In-Store Credit / Return</button>
          <button className="btn ghost" onClick={() => setScreen("held")}>Held{posHeld.length ? ` (${posHeld.length})` : ""}</button>
          <button className="btn ghost" onClick={() => setScreen("report")}>Day report</button>
        </div>
      </div>

      {/* retail-scoped flag messages follow the customer to the till */}
      {cust && allFlags(cust, null).filter((f) => f.scope === "retail" || f.scope === "both").length > 0 && (
        <div style={{ marginBottom: 10, display: "grid", gap: 6 }}>
          {allFlags(cust, null).filter((f) => f.scope === "retail" || f.scope === "both").map((f) => (
            <div key={f.key} className="gatenote bannerflag" data-stop={f.stop ? 1 : 0} style={{ background: "rgba(220,21,21,.12)", color: "#7A1010", border: "1px solid rgba(220,21,21,.35)" }}>
              <span className="bf-scope">{f.auto ? f.source : FLAG_SCOPE_LABEL[f.scope] || "Account"}</span>
              {f.message}
            </div>
          ))}
        </div>
      )}

      {/* products left · cart pinned right */}
      <div className="possplit">
        <div className="posproducts2">
          <div className="posprodhead">
            <div className="poscats">
              {cats.map((c) => <button key={c} type="button" className="poschip" data-on={cat === c ? 1 : 0} onClick={() => setCat(c)}>{c}</button>)}
            </div>
            <div className="posprodsearch">
              <input placeholder="Search product — name or code" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} />
              {prodSearch.trim() && (
                <div className="poslookup">
                  {prodMatches.length === 0 ? (
                    <div className="sub" style={{ padding: "8px 10px" }}>No product found. Use the cart’s “+ Add line” for an off-catalogue item.</div>
                  ) : prodMatches.map((p) => (
                    <button key={p.id} type="button" className="poslookupitem" onClick={() => { addFromCatalogue(p.id); setProdSearch(""); }}>
                      <b>{p.name}</b>
                      <span className="sub" style={{ marginTop: 0 }}>{p.code ? p.code + " · " : ""}{posMoney(p.price)}{p.noTax ? " · no tax" : posTracked(p) ? (bulkOnHand(p, activeParts) <= 0 ? " · out of stock" : " · " + bulkOnHand(p, activeParts) + " on hand" + (p.bulkCode ? " (from bulk)" : "")) : ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="postiles">
            {gridItems.length === 0 ? <div className="sub" style={{ padding: 8 }}>No matching products. Add them under Inventory.</div>
              : gridItems.map((p) => <PosTile key={p.id} p={p} onAdd={addFromCatalogue} />)}
          </div>
        </div>

        {/* cart — pinned on the right */}
        <div className="poscartcol">
          <div className="bar" style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--line)" }}>
            <div>
              <strong style={{ fontSize: 15 }}>{isRet ? "Return cart" : "Cart"}</strong>
              <div className="sub">{cart.length} item{cart.length === 1 ? "" : "s"}</div>
            </div>
            <button type="button" className="btn ghost mini" onClick={addItem}>+ Add line</button>
          </div>

          <datalist id="pos-partcodes">{activeParts.map((p) => <option key={p.id} value={p.code}>{p.name}</option>)}</datalist>
          {cart.length === 0 && <div className="empty" style={{ marginBottom: 10 }}>Cart is empty. Tap a product, scan a barcode, or search to add.</div>}
          {cart.map((it) => (
            <div key={it.id} className="poscartline">
              <div className="pcl-top">
                <input placeholder="Code" list="pos-partcodes" value={it.code} onChange={(e) => onCode(it.id, e.target.value)} />
                <input placeholder="Description" value={it.desc} onChange={(e) => setItem(it.id, "desc", e.target.value)} />
                <button type="button" className="btn warn mini" title="Remove" onClick={() => dropItem(it.id)}>×</button>
              </div>
              <div className="pcl-bot">
                <label>Qty<input inputMode="decimal" value={it.qty} onChange={(e) => setItem(it.id, "qty", e.target.value)} /></label>
                <label>$ each<input inputMode="decimal" value={it.price} onChange={(e) => setItem(it.id, "price", e.target.value)} /></label>
                <label>Disc<input inputMode="decimal" value={it.disc} disabled={!!it.sp} onChange={(e) => setItem(it.id, "disc", e.target.value)} /></label>
                <label>&nbsp;<select value={it.discType} disabled={!!it.sp} onChange={(e) => setItem(it.id, "discType", e.target.value)}><option value="$">$</option><option value="%">%</option></select></label>
              </div>
              {(() => {
                const wp = parts.find((p) => partHasWarranty(p) && (p.code || "").trim().toLowerCase() === String(it.code || "").trim().toLowerCase());
                if (!wp) return null;
                const need = Math.max(1, Math.round(num(it.qty) || 1));
                const ok = serialList(it.serial).length >= need;
                const shelf = (wp.shelfSerials || []).filter((x) => !serialList(it.serial).includes(x));
                return (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                    <span className="doc" style={{ background: ok ? "#0E7A3C" : "#DC1515", flex: "none" }}>SERIAL</span>
                    {shelf.length > 0 && (
                      <select value="" style={{ width: "auto" }} onChange={(e) => { if (e.target.value) setItem(it.id, "serial", [serialList(it.serial), e.target.value].flat().filter(Boolean).join(", ")); e.target.value = ""; }}>
                        <option value="">shelf stock…</option>
                        {shelf.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    )}
                    <input value={it.serial || ""} placeholder={need > 1 ? `${need} serials, comma-separated — required` : "serial # off the unit — required"} style={{ flex: 1 }}
                      onChange={(e) => setItem(it.id, "serial", e.target.value)} />
                  </div>
                );
              })()}
              {it.sp ? <div className="sub" style={{ color: "var(--tile)", fontWeight: 700 }}>Special price for {cust ? cust.name : "this customer"} — no other discounts apply</div> : null}
              <div className="pcl-tot mono">{money(posLineNet(it))}</div>
            </div>
          ))}
          <div style={{ position: "relative", marginTop: 4 }}>
            <input placeholder="Search a product to add" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} />
            {prodSearch.trim() && (
              <div className="poslookup">
                {prodMatches.length === 0 ? (
                  <div className="sub" style={{ padding: "8px 10px" }}>No product found. Use “+ Add line” for an off-catalogue item.</div>
                ) : prodMatches.map((p) => (
                  <button key={p.id} type="button" className="poslookupitem" onClick={() => { addFromCatalogue(p.id); setProdSearch(""); }}>
                    <b>{p.name}</b>
                    <span className="sub" style={{ marginTop: 0 }}>{p.code ? p.code + " · " : ""}{posMoney(p.price)}{p.noTax ? " · no tax" : posTracked(p) ? (bulkOnHand(p, activeParts) <= 0 ? " · out of stock" : " · " + bulkOnHand(p, activeParts) + " on hand" + (p.bulkCode ? " (from bulk)" : "")) : ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="panel" style={{ padding: 12, marginTop: 12, background: "#F7FAFD" }}>
            {/* options */}
            <div className="fields">
                  <div>
                    <label>Payment</label>
                    <select value={method} onChange={(e) => setMethod(e.target.value)}>
                      {POS_METHODS.filter(([k]) => k !== "account" || custId).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      {cust && num(cust.storeCredit) > 0 && <option value="storecredit">Store credit ({posMoney(cust.storeCredit)} available)</option>}
                    </select>
                  </div>
                  {activeCentres.length > 0 && (
                    <div>
                      <label>Revenue centre</label>
                      <select value={rc} onChange={(e) => { setRevCentre(e.target.value); setRevSub(""); }}>
                        {activeCentres.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                      </select>
                    </div>
                  )}
                  {rcSubs.length > 0 && (
                    <div>
                      <label>Sub-category</label>
                      <select value={revSub} onChange={(e) => setRevSub(e.target.value)}>
                        <option value="">&mdash; none &mdash;</option>
                        {rcSubs.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label>Order discount</label>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input inputMode="decimal" value={orderDisc} placeholder="0" onChange={(e) => setOrderDisc(e.target.value)} />
                      <select value={orderDiscType} style={{ width: 62 }} onChange={(e) => setOrderDiscType(e.target.value)}><option value="%">%</option><option value="$">$</option></select>
                    </div>
                  </div>
                  <div>
                    <label>Tax rate (%)</label>
                    <input inputMode="decimal" value={exempt ? "0" : taxRate} disabled={exempt} onChange={(e) => setTaxRate(e.target.value)} />
                  </div>
                  {method === "cash" && !isRet && (
                    <div><label>Cash tendered ($)</label><input inputMode="decimal" value={tendered} onChange={(e) => setTendered(e.target.value)} /></div>
                  )}
                  {method === "split" && (<>
                    <div><label>Cash ($)</label><input inputMode="decimal" value={splitCash} onChange={(e) => setSplitCash(e.target.value)} /></div>
                    <div><label>Credit ($)</label><input value={cardPortion.toFixed(2)} disabled /></div>
                  </>)}
                  <div className="f-wide">
                    <label><input type="checkbox" checked={exempt} onChange={(e) => setTaxExempt(e.target.checked)} style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />No tax on this sale <span className="sub" style={{ marginTop: 0 }}>(water is already untaxed automatically)</span></label>
                  </div>
                </div>

                {/* 3 — summary */}
                <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span className="sub" style={{ marginTop: 0 }}>Subtotal</span><span className="mono">{money(t.subtotal)}</span></div>
                  {t.orderDiscAmt > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span className="sub" style={{ marginTop: 0 }}>Discount</span><span className="mono">−{money(t.orderDiscAmt)}</span></div>}
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span className="sub" style={{ marginTop: 0 }}>Tax {taxExempt ? "(exempt)" : `(${num(taxRate)}%)`}{cust && cust.statusExempt && num(taxRate) === 5 ? " — First Nations Status rebate" : ""}</span><span className="mono">{money(t.tax)}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontWeight: 800, fontSize: 26, borderTop: "2px solid var(--ink)", paddingTop: 10 }}><span>{isRet ? "Refund" : "Total"}</span><span className="mono">{money(t.total)}</span></div>
                  {method === "cash" && !isRet && num(tendered) > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", color: change >= 0 ? "var(--tile)" : "var(--clay)", fontWeight: 700, marginTop: 4 }}><span>Change due</span><span className="mono">{money(Math.max(0, change))}</span></div>
                  )}
                </div>

                {accountNoCust && <div className="sub" style={{ color: "var(--clay)", marginTop: 8 }}>Pick a customer to charge on account.</div>}
                {scShort && <div className="sub" style={{ color: "var(--clay)", marginTop: 8 }}>{cust ? `Store credit only covers ${posMoney(cust.storeCredit)} of this ${posMoney(t.total)} sale — take the difference another way, or split the sale.` : "Attach the customer to spend their store credit."}</div>}

                {/* 4 — complete */}
                <button className="btn" style={{ width: "100%", justifyContent: "center", fontSize: 17, fontWeight: 800, padding: "16px 14px", marginTop: 14 }}
                  data-cobrowse="pos.complete" onClick={() => { if (!posCartCanComplete(cart)) return; if (method === "cash" && !isRet) { setCashCollected(tendered); setCashAsk(true); return; } complete(); }} disabled={!posCartCanComplete(cart) || t.total <= 0 || accountNoCust || scShort}>
                  {isRet ? "Refund" : "Complete sale"} · {money(t.total)}
                </button>
                <div className="btnrow" style={{ marginTop: 8, justifyContent: "center" }}>
                  <button className="btn ghost mini" onClick={hold} disabled={!cart.length}>Hold sale</button>
                  <button className="btn ghost mini" onClick={resetSale} disabled={!cart.length}>Clear cart</button>
                </div>

                {cashAsk && method === "cash" && !isRet && (
                  <div className="overlay">
                    <div className="sheet" style={{ maxWidth: 420 }}>
                      <div className="bar">
                        <div>
                          <strong style={{ fontSize: 16 }}>Cash tender</strong>
                          <div className="sub">Till — amount collected, then change. Sale does not close without it.</div>
                        </div>
                      </div>
                      <div className="pad">
                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 22, marginBottom: 10 }}>
                          <span>Total</span><span className="mono">{money(t.total)}</span>
                        </div>
                        <label>Amount collected ($)</label>
                        <input autoFocus inputMode="decimal" value={cashCollected} placeholder="0.00"
                          onChange={(e) => setCashCollected(e.target.value)} style={{ fontSize: 22, padding: "12px 14px", fontWeight: 800 }} />
                        {(() => { const ch = cashChangeDue(cashCollected, t.total);
                          return (
                            <div style={{ marginTop: 10, fontWeight: 800, fontSize: 20, color: ch.ok ? "var(--tile)" : "var(--clay)" }}>
                              {ch.reason === "collected" ? "Enter the amount collected"
                                : ch.reason === "short" ? `Short ${money(Math.abs(ch.change))} — collect the full total`
                                : `Change due ${money(ch.change)}`}
                            </div>
                          );
                        })()}
                        <div className="btnrow" style={{ marginTop: 14 }}>
                          <button className="btn" disabled={!cashChangeDue(cashCollected, t.total).ok}
                            onClick={() => { setTendered(cashCollected); complete(); }}>Take cash — close sale</button>
                          <button className="btn ghost" onClick={() => setCashAsk(false)}>Go back</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {idAsk && (
                  <div className="overlay">
                    <div className="sheet" style={{ maxWidth: 460 }}>
                      <div className="bar">
                        <div>
                          <strong style={{ fontSize: 16 }}>🪪 Check the Status Card</strong>
                          <div className="sub">{cust ? cust.name : ""} · First Nations HST Rebate (-8%) on this sale</div>
                        </div>
                      </div>
                      <div className="pad">
                        <div className="notice" style={{ marginBottom: 12 }}>
                          Ask to see the customer's <b>Certificate of Indian Status (Status Card)</b> and check the photo ID
                          before completing the sale — the reduced 5% rate only applies to the cardholder.
                          {cust && cust.statusCardNo ? <><br />Card number on file: <b>{cust.statusCardNo}</b></> : null}
                        </div>
                        <div className="btnrow" style={{ flexWrap: "wrap" }}>
                          <button className="btn" onClick={() => complete(true)}>ID checked — complete sale</button>
                          <button className="btn ghost" onClick={() => setIdAsk(false)}>Go back</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

      {lookupModal}
        </div>
  );
}

// Owner-only tracking of gifted (free-of-charge) service — documented, never billed.
function OffBooksView({ clients = [], allVisits = {}, loading, onPick }) {
  const giftVal = (v) => num(v.giftValue) || num(v.subtotal) || (num(v.matPrice) + num(v.labour));
  const rows = clients.map((c) => {
    const gifts = (allVisits[c.id] || []).filter((v) => v.gifted);
    const value = gifts.reduce((a, v) => a + giftVal(v), 0);
    const last = gifts.map((v) => v.date).filter(Boolean).sort().slice(-1)[0] || "";
    return { c, count: gifts.length, value, last };
  }).filter((r) => r.count > 0).sort((a, b) => b.value - a.value);
  const grand = rows.reduce((a, r) => a + r.value, 0);
  const visits = rows.reduce((a, r) => a + r.count, 0);
  return (
    <div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 16 }}>Gifted services <span className="sub" style={{ marginTop: 0 }}>· free of charge · owner only</span></strong>
          <div className="sub">{loading ? "Reading visits…" : `${visits} gifted visit${visits === 1 ? "" : "s"} across ${rows.length} account${rows.length === 1 ? "" : "s"} — documented, not billed`}</div>
        </div>
        <div style={{ fontWeight: 800, fontSize: 22 }} className="mono">{money0(grand)}</div>
      </div>
      <div className="panel">
        <div style={{ overflowX: "auto" }}>
          <table className="salest">
            <thead><tr><th>Account</th><th style={{ textAlign: "right" }}>Gifted visits</th><th style={{ textAlign: "right" }}>Last visit</th><th style={{ textAlign: "right" }}>Value given</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={4} className="empty">{loading ? "Loading…" : "No gifted services logged yet. Mark a visit “Gifted” when you service a customer free of charge."}</td></tr>
                : rows.map((r) => (
                  <tr key={r.c.id} onClick={() => onPick(r.c.id)} style={{ cursor: "pointer" }}>
                    <td>{r.c.name}{r.c.giftedService && <span className="doc" style={{ background: "#B7791F", marginLeft: 7 }}>Gifted account</span>}{r.c.trade && <span className="pill" data-t="both" style={{ marginLeft: 6 }}>Trade</span>}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{r.count}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{r.last || "—"}</td>
                    <td className="mono" style={{ textAlign: "right" }}><b>{money0(r.value)}</b></td>
                  </tr>
                ))}
            </tbody>
            <tfoot><tr><td><b>Total gifted</b></td><td className="mono" style={{ textAlign: "right" }}><b>{visits}</b></td><td></td><td className="mono" style={{ textAlign: "right" }}><b>{money0(grand)}</b></td></tr></tfoot>
          </table>
        </div>
      </div>
      <div className="notice" style={{ marginTop: 12 }}>Gifted services are recorded like any other visit but billed at $0 — this is the value of the work you’ve given away, for your own tracking. It isn’t counted as revenue anywhere.</div>
    </div>
  );
}

// Owner-only accounts receivable — outstanding balances + what's been given to tax-exempt accounts.
// Next monthly auto-charge date for an account (null if auto-charge is off or not monthly).
function nextChargeDate(b) {
  if (!b || !b.autoCharge || b.cycle !== "monthly") return null;
  const day = Math.max(1, Math.min(28, Number(b.chargeDay) || 1));
  const now = new Date();
  const m = now.getDate() <= day ? now.getMonth() : now.getMonth() + 1;
  return new Date(now.getFullYear(), m, day);
}
function ARView({ clients = [], allVisits = {}, posSales = [], loading, onPick, onCharge, onCompose, onLogComm, onRunBilling, terms = [], onPatchVisit, me }) {
  const [billPop, setBillPop] = useState(false);
  const monthKey = new Date().toISOString().slice(0, 7);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("aging"); // aging | followup
  const fmt$ = (n) => "$" + num(n).toFixed(2);

  // Aging buckets: ≤10 days = new, not due yet; then 11–30 / 60 / 90 / 120 / 150 / 180 / 180+.
  const BUCKETS = [
    { k: "new", label: "Not due (≤10d)", lo: 0, hi: AR_GRACE_DAYS },
    { k: "b30", label: "11–30", lo: AR_GRACE_DAYS + 1, hi: 30 },
    { k: "b60", label: "31–60", lo: 31, hi: 60 },
    { k: "b90", label: "61–90", lo: 61, hi: 90 },
    { k: "b120", label: "91–120", lo: 91, hi: 120 },
    { k: "b150", label: "121–150", lo: 121, hi: 150 },
    { k: "b180", label: "151–180", lo: 151, hi: 180 },
    { k: "over", label: "180+", lo: 181, hi: Infinity },
  ];
  const bucketOf = (age) => BUCKETS.find((b) => age >= b.lo && age <= b.hi) || BUCKETS[BUCKETS.length - 1];

  const rows = clients.map((c) => {
    const unpaid = (allVisits[c.id] || []).filter((v) => v.paid === false && num(v.amount) > 0);
    const cells = {}; BUCKETS.forEach((b) => { cells[b.k] = 0; });
    let oldest = 0;
    unpaid.forEach((v) => { const age = arAge(v.date); cells[bucketOf(age).k] += num(v.amount); oldest = Math.max(oldest, age); });
    const total = unpaid.reduce((a, v) => a + num(v.amount), 0);
    const overdue = total - cells.new;
    return { c, cells, total, overdue, oldest, count: unpaid.length, next: nextChargeDate(c.billing) };
  }).filter((r) => r.total > 0)
    .filter((r) => { const t = q.trim().toLowerCase(); return !t || r.c.name.toLowerCase().includes(t) || (r.c.address || "").toLowerCase().includes(t); })
    .sort((a, b) => b.oldest - a.oldest || b.total - a.total);

  const colTotal = (k) => rows.reduce((a, r) => a + r.cells[k], 0);
  const totalOut = rows.reduce((a, r) => a + r.total, 0);
  const totalOverdue = rows.reduce((a, r) => a + r.overdue, 0);
  const followups = rows.filter((r) => r.overdue > 0);

  const reminderMsg = (r, k) => (r.overdue > 0
    ? `${contactFirst(k) ? `Hi ${contactFirst(k)}, ` : ""}Pool Master: A friendly reminder — your account has a balance of ${fmt$(r.overdue)} past due (${r.count} invoice${r.count === 1 ? "" : "s"}). Please give us a call, or reply to arrange payment. Thank you!`
    : `${contactFirst(k) ? `Hi ${contactFirst(k)}, ` : ""}Pool Master: A friendly note — your account has a balance of ${fmt$(r.total)} (${r.count} invoice${r.count === 1 ? "" : "s"}). Please give us a call, or reply to arrange payment. Thank you!`);
  // Full account statement — open invoices with aging, totals, terms. Sent by the
  // customer's preferred channel, or printed for mailing (single or the whole batch).
  const statementText = (r) => {
    const unpaid = (allVisits[r.c.id] || []).filter((v) => v.paid === false && num(v.amount) > 0)
      .slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const b = { ...defBilling(), ...(r.c.billing || {}) };
    const today = new Date().toISOString().slice(0, 10);
    return [
      `Pool Master Canada — Account statement`,
      `${today} · ${r.c.name}${b.acctNo ? ` · Account ${b.acctNo}` : ""}`,
      ``,
      `Open invoices:`,
      ...unpaid.map((v) => {
        const age = arAge(v.date);
        return `  ${v.date || "—"} · ${v.invoiceNo || v.workOrder || "Invoice"} — ${fmt$(v.amount)}${age > AR_GRACE_DAYS ? `  (${age} days old — past due)` : ""}`;
      }),
      ``,
      `Total outstanding: ${fmt$(r.total)}`,
      `Past due: ${fmt$(r.overdue)}`,
      `Terms: ${TERM_LABEL[b.terms] || TERM_LABEL.net10}`,
      ``,
      r.overdue > 0
        ? `Please arrange payment for the past-due amount — reply to this message or call the shop. Thank you!`
        : `Nothing is past due — this statement is for your records. Thank you for your business!`,
    ].join("\n");
  };
  const printStatements = (list) => {
    if (!list.length) return;
    const w = window.open("", "_blank"); if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Statements</title>
<style>body{font:13.5px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;max-width:560px;margin:26px auto;}h1{font-size:19px;color:#0B4F8A;margin:0 0 2px;}pre{font:inherit;white-space:pre-wrap;}.pg{page-break-after:always;padding-bottom:18px;}@media print{body{margin:12px auto;}}</style>
</head><body onload="window.print()">${list.map((r) => `<div class="pg"><h1>Pool Master Canada</h1><pre>${posEsc(statementText(r))}</pre></div>`).join("")}</body></html>`);
    w.document.close();
    list.forEach((r) => onLogComm(r.c.id, `Statement printed for mailing — ${fmt$(r.total)} outstanding (${r.count} invoice${r.count === 1 ? "" : "s"})`));
  };
  const doStatement = (r) => {
    const k0 = (r.c.contacts || [])[0] || {};
    const pref = (k0.comms || {}).invoices || "none";
    if ((pref === "email" || pref === "both") && k0.email) {
      onLogComm(r.c.id, `Statement sent by email — ${fmt$(r.total)} outstanding (${r.count} invoice${r.count === 1 ? "" : "s"})`);
      onCompose({ kind: "mail", client: r.c, to: k0.email, emailCat: "invoices", subject: `Pool Master — account statement ${new Date().toISOString().slice(0, 10)}`, body: `${contactFirst(k0) ? `Hi ${contactFirst(k0)},\n\n` : ""}${statementText(r)}` });
    } else if ((pref === "text" || pref === "both") && k0.phone) {
      onLogComm(r.c.id, `Statement sent by text — ${fmt$(r.total)} outstanding (${r.count} invoice${r.count === 1 ? "" : "s"})`);
      onCompose({ kind: "text", client: r.c, to: k0.phone, body: `${contactFirst(k0) ? `Hi ${contactFirst(k0)}, ` : ""}${statementText(r)}` });
    } else {
      printStatements([r]);
    }
  };
  const doCall = (r, k0) => {
    onLogComm(r.c.id, `A/R follow-up call — ${fmt$(r.overdue)} overdue (${arFollowReason(r.c.billing)})`);
    if (k0.phone) window.location.href = "tel:" + String(k0.phone).replace(/[^\d+]/g, "");
  };
  const doEmail = (r, k0) => {
    onLogComm(r.c.id, `A/R follow-up email — ${fmt$(r.overdue)} overdue (${arFollowReason(r.c.billing)})`);
    onCompose({ kind: "mail", client: r.c, to: k0.email || "", emailCat: "invoices", subject: "Pool Master — your account balance", body: reminderMsg(r, k0) });
  };
  const doText = (r, k0) => {
    onLogComm(r.c.id, `A/R follow-up text — ${fmt$(r.overdue)} overdue (${arFollowReason(r.c.billing)})`);
    onCompose({ kind: "text", client: r.c, to: k0.phone || "", body: reminderMsg(r, k0) });
  };

  return (
    <div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 16 }}>Accounts receivable</strong>
          <div className="sub">{loading ? "Reading invoices…" : `${rows.length} accounts owing · ${money0(totalOut)} outstanding · invoices are due after ${AR_GRACE_DAYS} days`}</div>
        </div>
        <div className="btnrow">
          {onRunBilling && <button className="btn mini ghost" onClick={() => setBillPop(true)}>Run monthly billing</button>}
          <button className="btn mini ghost" title="One statement per page, ready to mail" disabled={!followups.length}
            onClick={() => printStatements(followups)}>Print statements{followups.length ? ` (${followups.length})` : ""}</button>
          <button className="btn mini ghost" onClick={() => csvDownload(`ar-aging-${new Date().toISOString().slice(0, 10)}.csv`,
            [["Account", ...BUCKETS.map((b) => b.label), "Total", "Overdue", "Auto charge"],
              ...rows.map((r) => [r.c.name, ...BUCKETS.map((b) => r.cells[b.k].toFixed(2)), r.total.toFixed(2), r.overdue.toFixed(2),
                r.next ? r.next.toISOString().slice(0, 10) : "off"])])}>CSV</button>
          <button className={"btn mini" + (tab === "aging" ? "" : " ghost")} onClick={() => setTab("aging")}>Aging</button>
          <button className={"btn mini" + (tab === "followup" ? "" : " ghost")} onClick={() => setTab("followup")}>
            Follow-up{followups.length > 0 ? ` (${followups.length})` : ""}
          </button>
        </div>
      </div>

      <div className="counts" style={{ marginBottom: 14 }}>
        <div className="count-cell"><div className="n">{money0(totalOut)}</div><div className="l">Outstanding</div></div>
        <div className="count-cell"><div className="n">{money0(colTotal("new"))}</div><div className="l">Not due yet</div></div>
        <div className="count-cell"><div className="n" style={totalOverdue > 0 ? { color: "var(--clay)" } : undefined}>{money0(totalOverdue)}</div><div className="l">Past due</div></div>
        <div className="count-cell"><div className="n">{followups.length}</div><div className="l">Need follow-up</div></div>
      </div>

      <input placeholder="Search customer" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 300, marginBottom: 12 }} />

      {tab === "aging" && (
        <div className="panel">
          <div style={{ overflowX: "auto" }}>
            <table className="salest">
              <thead><tr>
                <th>Account</th>
                {BUCKETS.map((b) => <th key={b.k} style={{ textAlign: "right" }}>{b.label}</th>)}
                <th style={{ textAlign: "right" }}>Total</th><th>Auto charge</th><th></th>
              </tr></thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={BUCKETS.length + 3} className="empty">{loading ? "Loading…" : "Nothing owing. All caught up."}</td></tr>
                  : rows.map((r) => (
                    <tr key={r.c.id} onClick={() => onPick(r.c.id)} style={{ cursor: "pointer" }}>
                      <td>{r.c.name}{r.c.trade && <span className="pill" data-t="both" style={{ marginLeft: 6 }}>Trade</span>}</td>
                      {BUCKETS.map((b) => (
                        <td key={b.k} className="mono" style={{ textAlign: "right", color: b.k !== "new" && r.cells[b.k] > 0 ? "var(--clay)" : undefined, fontWeight: b.k !== "new" && r.cells[b.k] > 0 ? 700 : 400 }}>
                          {r.cells[b.k] > 0 ? money0(r.cells[b.k]) : "—"}
                        </td>
                      ))}
                      <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{money0(r.total)}</td>
                      <td>{r.next
                        ? <b style={{ color: "var(--tile)" }}>{r.next.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</b>
                        : <span className="sub" style={{ marginTop: 0 }}>Off</span>}</td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 5, justifyContent: "flex-end", flexWrap: "nowrap" }}>
                          {r.total > 0 && onCharge && hasCard(r.c.billing) && !cardStatus((r.c.billing || {}).card).expired && (
                            <button className="btn mini" onClick={(e) => { e.stopPropagation(); onCharge(r.c.id); }}>Charge now</button>
                          )}
                          <button className="btn ghost mini" title="Send the full account statement (their preferred channel, or print)"
                            onClick={(e) => { e.stopPropagation(); doStatement(r); }}>Stmt</button>
                          {(() => {
                            // Contact them the way THEY prefer (invoices preference); phone call as fallback.
                            const k0 = (r.c.contacts || [])[0] || {};
                            const pref = (k0.comms || {}).invoices || "none";
                            const btns = [];
                            if ((pref === "email" || pref === "both") && k0.email) btns.push(
                              <button key="m" className="btn ghost mini icon" title={`Email ${k0.email} — their preference`}
                                onClick={(e) => { e.stopPropagation(); doEmail(r, k0); }}><CommIcon v="mail" size={13} /></button>);
                            if ((pref === "text" || pref === "both") && k0.phone) btns.push(
                              <button key="t" className="btn ghost mini icon" title={`Text ${k0.phone} — their preference`}
                                onClick={(e) => { e.stopPropagation(); doText(r, k0); }}><CommIcon v="text" size={13} /></button>);
                            if (!btns.length && k0.phone) btns.push(
                              <button key="c" className="btn ghost mini icon" title={`Call ${k0.phone}`}
                                onClick={(e) => { e.stopPropagation(); doCall(r, k0); }}><CommIcon v="phone" size={13} /></button>);
                            return btns;
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot><tr>
                <td><b>Totals</b></td>
                {BUCKETS.map((b) => <td key={b.k} className="mono" style={{ textAlign: "right" }}><b>{colTotal(b.k) > 0 ? money0(colTotal(b.k)) : "—"}</b></td>)}
                <td className="mono" style={{ textAlign: "right" }}><b>{money0(totalOut)}</b></td><td></td><td></td>
              </tr></tfoot>
            </table>
          </div>
        </div>
      )}

      {tab === "followup" && (
        followups.length === 0 ? <div className="empty">{loading ? "Loading…" : "Nothing to chase — no overdue invoices that auto-charge couldn't collect."}</div>
          : followups.map((r) => {
            const k0 = (r.c.contacts || [])[0] || {};
            const reason = arFollowReason(r.c.billing);
            return (
              <div key={r.c.id} className="visit" style={{ borderLeft: "4px solid #B31212" }}>
                <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <button className="link" style={{ fontWeight: 700, fontSize: 14.5 }} onClick={() => onPick(r.c.id)}>{r.c.name}</button>
                  <div className="vdocs" style={{ alignItems: "center" }}>
                    <span className="doc" style={{ background: "#B31212" }}>{fmt$(r.overdue)} overdue</span>
                    <span className="doc">{r.oldest} days</span>
                    <span className="doc" style={{ background: "#B7791F" }}>{reason}</span>
                  </div>
                  <div className="btnrow" style={{ marginLeft: "auto", flexWrap: "nowrap" }}>
                    {onCharge && hasCard(r.c.billing) && !cardStatus((r.c.billing || {}).card).expired && (
                      <button className="btn mini" onClick={() => onCharge(r.c.id)}>Charge now</button>
                    )}
                    <button className="btn ghost mini icon" title={k0.phone ? `Call ${k0.phone}` : "No phone on file"} onClick={() => doCall(r, k0)}><CommIcon v="phone" size={13} /> Call</button>
                    <button className="btn ghost mini icon" title={k0.email ? `Email ${k0.email}` : "No email on file"} onClick={() => doEmail(r, k0)}><CommIcon v="mail" size={13} /> Email</button>
                    <button className="btn ghost mini icon" title={k0.phone ? `Text ${k0.phone}` : "No phone on file"} onClick={() => doText(r, k0)}><CommIcon v="text" size={13} /> Text</button>
                    <button className="btn ghost mini" title="Send the full account statement" onClick={() => doStatement(r)}>Statement</button>
                  </div>
                </div>
                <div className="sub" style={{ marginTop: 4 }}>
                  {r.count} unpaid invoice{r.count === 1 ? "" : "s"} · {money0(r.total)} total owing
                  {r.cells.new > 0 ? ` (${money0(r.cells.new)} not due yet)` : ""} · {k0.name || "No contact"}{k0.phone ? ` · ${k0.phone}` : ""}
                  {r.next && <b style={{ color: "var(--tile)" }}> · Auto charge coming {r.next.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</b>}
                </div>
                <div style={{ marginTop: 8 }}>
                  {(allVisits[r.c.id] || []).filter((v) => v.paid === false && num(v.amount) > 0).map((v) => (
                    <div key={v.id} className="btnrow" style={{ marginTop: 4, flexWrap: "wrap" }}>
                      <span className="sub" style={{ marginTop: 0, minWidth: 160 }}>{v.date || "—"} · {v.invoiceNo || v.workOrder || "Invoice"} · {fmt$(v.amount)}</span>
                      <button type="button" className="btn ghost mini" onClick={() => printVisitInvoice(v, r.c, terms)}>Print invoice</button>
                      <button type="button" className="btn ghost mini" title="Print this invoice for mailing — stamps a mail path. No postage API."
                        onClick={() => {
                          printVisitInvoice(v, r.c, terms);
                          const stamped = markInvoiceMailed(v, { by: me ? me.name : "", mailPath: "print-for-mail" });
                          onPatchVisit && onPatchVisit(r.c.id, v.id, stamped);
                          onLogComm && onLogComm(r.c.id, `Invoice ${v.invoiceNo || v.workOrder || v.id} printed for mail`);
                        }}>Print for mail</button>
                      {v.mailedAt && <span className="doc" style={{ background: "#5A6E86" }}>{invoiceMailStamp(v)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
      )}

      {billPop && (() => {
        const label = new Date(monthKey + "-15").toLocaleDateString(undefined, { month: "long", year: "numeric" });
        const targets = clients.filter((c) => {
          const b = { ...defBilling(), ...(c.billing || {}) };
          return c.type !== "retail" && !c.trade && b.cycle === "monthly" && num(b.monthlyRate) > 0 && !b.onHold;
        });
        return (
          <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setBillPop(false); }}>
            <div className="sheet" style={{ maxWidth: 520 }}>
              <div className="bar">
                <div>
                  <strong style={{ fontSize: 16 }}>Run monthly billing — {label}</strong>
                  <div className="sub">One invoice per monthly account · accounts already billed this month are skipped</div>
                </div>
                <button className="btn ghost" onClick={() => setBillPop(false)}>Cancel</button>
              </div>
              <div className="pad">
                {targets.length === 0 ? <div className="empty">No monthly-cycle accounts with a rate set.</div> : (<>
                  <table className="salest" style={{ marginBottom: 12 }}>
                    <thead><tr><th>Account</th><th style={{ textAlign: "right" }}>Rate</th><th style={{ textAlign: "right" }}>+ HST</th></tr></thead>
                    <tbody>
                      {targets.map((c) => {
                        const rate = num((c.billing || {}).monthlyRate);
                        return <tr key={c.id}><td>{c.name}{c.statusExempt && <span className="pill" data-t="both" style={{ marginLeft: 6 }}>5% GST</span>}</td><td className="mono" style={{ textAlign: "right" }}>${rate.toFixed(2)}</td><td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>${(rate * hstFactorFor(c)).toFixed(2)}</td></tr>;
                      })}
                    </tbody>
                  </table>
                  <button className="btn" style={{ width: "100%", justifyContent: "center" }}
                    onClick={async () => { setBillPop(false); await onRunBilling(monthKey); }}>
                    Invoice {targets.length} account{targets.length === 1 ? "" : "s"} for {label}
                  </button>
                </>)}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="notice" style={{ marginTop: 12 }}>
        Invoices {AR_GRACE_DAYS} days old or less are new and not due yet. On day {AR_GRACE_DAYS + 1}, accounts with auto-charge on and a valid card are charged automatically by the 8:00&nbsp;AM sweep and sent a thank-you receipt{AR_AUTO_CHARGE_ENABLED ? "" : " — the sweep is built but switched OFF until the system goes live"}. Anything past due that could not be collected lands in Follow-up for staff to reach out.
      </div>
    </div>
  );
}

function SalesView({ clients, allVisits, posSales = [], loading, onPick, mode = "dashboard", isOwner }) {
  // Gifted visits are billed at $0, so they naturally carry no revenue here.
  const billable = (clients || []);
  const rows = billable.map((c) => {
    const vs = allVisits[c.id] || [];
    const t = { service: 0, retail: 0, trade: 0, delivery: 0, repair: 0, gross: 0, profit: 0 };
    vs.forEach((v) => {
      const ch = channelOf(c, v);
      const amt = num(v.amount);
      t[ch] += amt; t.gross += amt;
      t.profit += num(v.matPrice) - num(v.matCost) + num(v.labour);
    });
    return { c, ...t, count: vs.length };
  }).filter((r) => r.count > 0).sort((a, b) => b.gross - a.gross);

  /* every invoice, flattened, for the period lines */
  const entries = [];
  billable.forEach((c) => (allVisits[c.id] || []).forEach((v) => {
    const d = parseDate(v.date);
    if (d) entries.push({
      d, amount: num(v.amount), channel: channelOf(c, v),
      profit: num(v.matPrice) - num(v.matCost) + num(v.labour),
    });
  }));

  const now = new Date();
  const years = Array.from(new Set(entries.map((e) => e.d.getFullYear()).concat([now.getFullYear()]))).sort((a, b) => b - a);
  const [year, setYear] = useState(now.getFullYear());
  const minYear = Math.min(now.getFullYear(), ...years);
  const maxYear = Math.max(now.getFullYear(), ...years);
  const setYearSafe = (y) => {
    setYear(y);
    setDoyN((n) => Math.min(n, daysInYear(y)));
    setWeekN((w) => Math.min(w, isoWeek(new Date(y, 11, 28))));
  };
  const [doyN, setDoyN] = useState(dayOfYear(now));
  const [weekN, setWeekN] = useState(isoWeek(now));
  const [monthN, setMonthN] = useState(now.getMonth());

  const inYear = entries.filter((e) => e.d.getFullYear() === year);
  const sum = (list) => list.reduce((a, e) => a + e.amount, 0);
  const dayList = inYear.filter((e) => dayOfYear(e.d) === doyN);
  const weekList = inYear.filter((e) => isoWeek(e.d) === weekN);
  const monthList = inYear.filter((e) => e.d.getMonth() === monthN);
  const lastYear = entries.filter((e) => e.d.getFullYear() === year - 1);
  const dayPrev = lastYear.filter((e) => dayOfYear(e.d) === doyN);
  const weekPrev = lastYear.filter((e) => isoWeek(e.d) === weekN);
  const monthPrev = lastYear.filter((e) => e.d.getMonth() === monthN);
  const dayDate = dateFromDoy(year, doyN);
  const [wkA, wkB] = weekRange(year, weekN);
  const maxDoy = daysInYear(year);
  const maxWeek = isoWeek(new Date(year, 11, 28));

  const step = (setter, val, min, max) => (n) => setter(Math.min(max, Math.max(min, val + n)));
  const [open, setOpen] = useState({});
  const inRange = (a, b) => entries.filter((e) => e.d >= a && e.d <= b);
  const sumsOf = (list) => ({
    gross: list.reduce((x, e) => x + e.amount, 0),
    profit: list.reduce((x, e) => x + e.profit, 0),
  });
  const totals = (list) => {
    const t = { service: 0, retail: 0, trade: 0, delivery: 0, repair: 0, gross: 0, profit: 0 };
    list.forEach((e) => { t[e.channel] += e.amount; t.gross += e.amount; t.profit += e.profit; });
    return t;
  };

  const seriesFor = (id) => {
    const out = [];
    if (id === "day") {
      for (let i = 13; i >= 0; i--) {
        const d = dateFromDoy(year, doyN - i);
        const list = entries.filter((e) => fmtDate(e.d) === fmtDate(d));
        out.push({ ...sumsOf(list), label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
          short: String(d.getDate()), current: i === 0 });
      }
    } else if (id === "week") {
      const [wStart] = weekRange(year, weekN);
      for (let i = 11; i >= 0; i--) {
        const a = new Date(wStart.getFullYear(), wStart.getMonth(), wStart.getDate() - i * 7);
        const b = new Date(a.getFullYear(), a.getMonth(), a.getDate() + 6, 23, 59);
        out.push({ ...sumsOf(inRange(a, b)), label: "Week " + isoWeek(a), short: "w" + isoWeek(a), current: i === 0 });
      }
    } else if (id === "month") {
      for (let m = 0; m < 12; m++) {
        const list = entries.filter((e) => e.d.getFullYear() === year && e.d.getMonth() === m);
        out.push({ ...sumsOf(list), label: MONTHS[m] + " " + year, short: MONTHS[m].slice(0, 1), current: m === monthN });
      }
    } else if (id === "year") {
      for (let y = Math.max(minYear, maxYear - 7); y <= maxYear; y++) {
        const list = entries.filter((e) => e.d.getFullYear() === y);
        out.push({ ...sumsOf(list), label: String(y), short: String(y).slice(2), current: y === year });
      }
    }
    return out;
  };

  const Line = ({ id, label, control, list, prev, strong }) => {
    const t = totals(list);
    const shown = !!open[id];
    const prevGross = prev ? prev.reduce((a, e) => a + e.amount, 0) : null;
    const dir = prevGross === null || (!prevGross && !t.gross) ? "flat"
      : !prevGross ? "up" : t.gross >= prevGross ? "up" : "down";
    const pct = prevGross ? Math.round(((t.gross - prevGross) / prevGross) * 100) : null;
    return (
      <div className="dashwrap" data-strong={strong ? 1 : 0}>
        <button type="button" className="dashline" data-open={shown ? 1 : 0}
          onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))}>
          <span className="dl-label">
            <span className="chev" data-open={shown ? 1 : 0}>&rsaquo;</span>{label}
            {prevGross !== null && (
              <span className="dl-prev">
                Last year {money0(prevGross)}
                {pct !== null && <b data-dir={dir}>{pct >= 0 ? "+" : ""}{pct}%</b>}
              </span>
            )}
          </span>
          <span className="dl-ctrl" onClick={(e) => e.stopPropagation()}>{control}</span>
          <span className="dl-amt" data-dir={prevGross === null ? "none" : dir}>
            {money0(t.gross)}
            <span className="dl-sub">{list.length} invoice{list.length === 1 ? "" : "s"}</span>
          </span>
        </button>
        <div className="salesgrid">
          {CHANNELS_SALES.map(({ k, label: l }) => (
            <div key={k} className="salecell" data-zero={t[k] ? 0 : 1}>
              <span className="ic"><SalesIcon v={k} /></span>
              <span className="n">{money0(t[k])}</span>
              <span className="l">{l}</span>
            </div>
          ))}
          <div className="salecell" data-zero={t.gross ? 0 : 1}>
            <span className="ic"><SalesIcon v="gross" /></span>
            <span className="n">{money0(t.gross)}</span>
            <span className="l">Gross sales</span>
          </div>
        </div>
        {shown && id !== "all" && <TrendChart points={seriesFor(id)} />}
        {shown && (
          <div className="salesgrid open">
            <div className="salecell wide" data-zero={t.gross ? 0 : 1}>
              <span className="ic"><SalesIcon v="gross" size={24} /></span>
              <span className="n big">{money0(t.gross)}</span>
              <span className="l">Gross sales</span>
            </div>
            <div className="salecell wide" data-zero={t.profit ? 0 : 1}>
              <span className="ic"><SalesIcon v="profit" size={24} /></span>
              <span className="n big">{money0(t.profit)}</span>
              <span className="l">Gross profit</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const Stepper = ({ onBack, onFwd, children }) => (
    <span className="stepper">
      <button type="button" onClick={onBack}>&lsaquo;</button>
      <span className="val">{children}</span>
      <button type="button" onClick={onFwd}>&rsaquo;</button>
    </span>
  );

  return (
    <div>
      {mode === "dashboard" && (<>
      <div className="panel bar" style={{ marginBottom: 12, borderBottom: 0 }}>
        <div>
          <strong style={{ fontSize: 15 }}>Sales dashboard</strong>
          <div className="sub">{loading ? "Reading invoices…" : `Every invoice on file \u00b7 ${entries.length} logged`}</div>
        </div>
        <div className="btnrow">
          <span className="sub" style={{ marginTop: 0 }}>Every line follows the year set below</span>
        </div>
      </div>

      <div className="panel" style={{ padding: 12, marginBottom: 12 }}>
        <Line id="day" label="Daily gross sales" list={dayList} prev={dayPrev}
          control={
            <Stepper onBack={() => step(setDoyN, doyN, 1, maxDoy)(-1)} onFwd={() => step(setDoyN, doyN, 1, maxDoy)(1)}>
              Day {doyN} of {maxDoy} <em>{dayDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</em>
            </Stepper>
          } />
        <Line id="week" label="Weekly gross sales" list={weekList} prev={weekPrev}
          control={
            <Stepper onBack={() => step(setWeekN, weekN, 1, maxWeek)(-1)} onFwd={() => step(setWeekN, weekN, 1, maxWeek)(1)}>
              Week {weekN} of {maxWeek} <em>{fmtShort(wkA)} \u2013 {fmtShort(wkB)}</em>
            </Stepper>
          } />
        <Line id="month" label="Monthly gross sales" list={monthList} prev={monthPrev}
          control={
            <Stepper onBack={() => step(setMonthN, monthN, 0, 11)(-1)} onFwd={() => step(setMonthN, monthN, 0, 11)(1)}>
              Month {monthN + 1} of 12 <em>{MONTHS[monthN]} {year}</em>
            </Stepper>
          } />
        <Line id="year" label="Yearly gross sales" list={inYear} prev={lastYear} strong
          control={
            <Stepper onBack={() => setYearSafe(Math.max(minYear, year - 1))} onFwd={() => setYearSafe(Math.min(maxYear, year + 1))}>
              {year} <em>{year === now.getFullYear() ? "this year" : year > now.getFullYear() ? "ahead" : "closed"}</em>
            </Stepper>
          } />
        <Line id="all" label="All time gross sales" list={entries} strong
          control={<span className="val" style={{ color: "var(--muted)" }}>Every invoice on file, all years</span>} />
      </div>
      </>)}

      {mode === "retail" && (
      <div className="panel">
        <div className="bar">
          <div>
            <strong style={{ fontSize: 15 }}>Retail sales by account</strong>
            <div className="sub">{loading ? "Reading invoices…" : `${rows.length} accounts with invoices on file`}</div>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="empty">{loading ? "Loading…" : "No invoices logged yet."}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="salest">
              <thead>
                <tr><th>Account</th><th>Service</th><th>Retail</th><th>Trade</th><th>Delivery</th><th>Repairs</th><th>Gross</th><th>Profit</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.c.id} onClick={() => onPick(r.c.id)}>
                    <td>
                      {r.c.name}
                      {r.c.trade && <span className="pill" data-t="both" style={{ marginLeft: 7 }}>Trade</span>}
                    </td>
                    <td className="mono">{r.service ? money0(r.service) : "—"}</td>
                    <td className="mono">{!r.c.trade && r.retail ? money0(r.retail) : "—"}</td>
                    <td className="mono">{r.c.trade && r.trade ? money0(r.trade) : "—"}</td>
                    <td className="mono">{r.delivery ? money0(r.delivery) : "—"}</td>
                    <td className="mono">{r.repair ? money0(r.repair) : "—"}</td>
                    <td className="mono"><b>{money0(r.gross)}</b></td>
                    <td className="mono">{money0(r.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// Book service → a work order that drops into the schedule's holding bin (no day/tech yet).
// "Parts needed" picker on a booked job — feeds the tech's Morning Pre-Flight pull sheet.
function PartsNeededField({ value = [], onChange, parts = [] }) {
  const [q, setQ] = useState("");
  const matches = q.trim()
    ? parts.filter((p) => p.active !== false && p.code && !value.some((x) => String(x.code).toLowerCase() === String(p.code).toLowerCase())
        && [p.code, p.name, p.category, p.subcategory].join(" ").toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6)
    : [];
  return (
    <div className="f-wide">
      <label>Parts needed on the truck (optional)</label>
      {value.map((x) => (
        <div key={x.code} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
          <span style={{ flex: 1, fontWeight: 650, fontSize: 13 }}>{x.name || x.code}<span className="sub" style={{ display: "inline", marginLeft: 6 }}>{x.code}</span></span>
          <input inputMode="numeric" value={x.qty} style={{ width: 64 }} aria-label="Quantity"
            onChange={(e) => onChange(value.map((y) => (y.code === x.code ? { ...y, qty: e.target.value } : y)))} />
          <button type="button" className="btn warn mini" onClick={() => onChange(value.filter((y) => y.code !== x.code))}>✕</button>
        </div>
      ))}
      <div style={{ position: "relative", marginTop: value.length ? 8 : 0 }}>
        <input placeholder="Search the catalogue to add a part…" value={q} onChange={(e) => setQ(e.target.value)} />
        {matches.length > 0 && (
          <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 200 }}>
            {matches.map((p) => (
              <button key={p.id} type="button" className="poslookupitem"
                onClick={() => { onChange([...value, { code: p.code, name: p.name || "", qty: "1" }]); setQ(""); }}>
                <b>{p.name}</b>
                <span className="sub" style={{ marginTop: 0 }}>{p.code}{p.qty !== "" && p.qty != null ? ` · ${num(p.qty)} at the shop` : ""}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="sub">Lands on the technician's Morning Pre-Flight — the route won't start until these are loaded on the truck.</div>
    </div>
  );
}

function WorkOrderSheet({ draft: draft0, clients, serviceTypes = [], onSave, onClose, parts = [], jobTypes = [], terms = [] }) {
  const [draft, setDraft] = useState({ ...draft0 });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const cust = clients.find((c) => c.id === draft.clientId);
  const activeTypes = serviceTypes.filter((s) => s.active !== false);
  const pickType = (name) => {
    const st = serviceTypes.find((s) => s.name === name);
    setDraft((d) => ({ ...d, serviceType: name, color: st ? st.color || "" : "", kind: deriveKind(name), what: st ? (st.instructions || "") : d.what }));
  };
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 520 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>New work order</strong>
            <div className="sub">Goes to the Service Schedule holding bin until you give it a day and a tech</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn" onClick={() => onSave(draft)} disabled={!draft.clientId}>Create work order</button>
          </div>
        </div>
        <div className="pad">
          <div className="fields">
            <div className="f-wide">
              <label>Customer</label>
              {cust ? (
                <span className="chip" data-ch="email"><b>{cust.name}</b>{cust.trade ? " · Trade" : ""}</span>
              ) : (
                <select value={draft.clientId} onChange={(e) => set("clientId", e.target.value)}>
                  <option value="">&mdash; pick a customer &mdash;</option>
                  {clients.slice().sort((a, b) => a.name.localeCompare(b.name)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label>Service type</label>
              <select value={draft.serviceType || ""} onChange={(e) => pickType(e.target.value)}>
                <option value="">&mdash; pick a service type &mdash;</option>
                {activeTypes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label>Guided workflow (for the tech's phone)</label>
              <select value={draft.wfTypeId || ""} onChange={(e) => set("wfTypeId", e.target.value)}>
                <option value="">None — free-form job</option>
                {(jobTypes || []).filter((t) => t.active !== false).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label>Slots it takes</label>
              <input type="number" min="1" max="8" value={draft.weight} onChange={(e) => set("weight", Math.max(1, Number(e.target.value) || 1))} />
            </div>
            <div className="f-wide">
              <label>What needs doing{draft.serviceType ? ` — ${draft.serviceType}` : ""}</label>
              <textarea rows={4} value={draft.what} onChange={(e) => set("what", e.target.value)} placeholder="Pick a service type to fill in the standard instructions, or type your own." />
            </div>
            <div className="f-wide">
              <label>Notes for the tech</label>
              <textarea rows={2} value={draft.note} onChange={(e) => set("note", e.target.value)} />
            </div>
            <PartsNeededField value={draft.parts || []} onChange={(v) => set("parts", v)} parts={parts} />
            <TermsPicker terms={terms} workType={deriveKind(draft.serviceType) || draft.kind || "service"}
              termIds={draft.termIds || []} note={draft.termsNote || ""}
              onChange={({ termIds, note }) => setDraft((d) => ({ ...d, termIds, termsNote: note }))} />
            <div className="f-wide" style={{ background: "#FFF7E8", border: "1px solid #F0D89A", borderRadius: 8, padding: "9px 11px" }}>
              <label style={{ marginBottom: 0 }}>
                <input type="checkbox" checked={!!draft.gifted} onChange={(e) => set("gifted", e.target.checked)}
                  style={{ width: "auto", marginRight: 8, verticalAlign: "middle" }} />
                <b>Gifted service</b> — free of charge; documented and tracked, not billed
              </label>
            </div>
          </div>
          <div className="notice" style={{ marginTop: 12 }}>Once saved it appears in the holding bin on the Service Schedule (daily and weekly) — use <b>Place</b> there to assign a day and technician.</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Follow-ups page ---------- */
function TickIcon({ size = 16 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <rect {...P} x="2.5" y="2.5" width="15" height="15" rx="4" />
      <path {...P} d="M6.4 10.4 L9 13 L13.8 7.4" />
    </svg>
  );
}

function FollowupsView({ followups = [], clients = [], staff = [], me, isOwner, onSave, onPick }) {
  const [mode, setMode] = useState("start"); // start | new | list
  const [who, setWho] = useState("mine"); // mine | all | done
  const [seeBy, setSeeBy] = useState("all"); // all | one — who can view the new follow-up
  const [note, setNote] = useState("");
  const [forStaffIds, setForStaffIds] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [custId, setCustId] = useState("");
  const [cq, setCq] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const pickCustomer = (c) => {
    setCustId(c.id); setCq("");
    setContactName(c.name || "");
    const k = (c.contacts || []).find((x) => x.phone) || {};
    setContactPhone(k.phone || "");
  };
  const today = fmtDate(new Date());
  const meId = me ? me.id : "";
  const staffName = (id) => (staff.find((s) => s.id === id) || {}).name || "";
  const clientName = (id) => (clients.find((c) => c.id === id) || {}).name || "";
  const visible = followups.filter((f) => visibleFollowup(f, meId, isOwner));
  const open = visible.filter((f) => !f.done);
  const shown = sortFollowups(
    who === "done" ? visible.filter((f) => f.done).slice(-60)
      : who === "mine" ? open.filter((f) => { const p = followupPinnedTo(f); return !p.length || p.includes(meId) || f.createdById === meId; })
      : open, today);
  const cMatches = cq.trim() ? clients.filter((c) => c.name.toLowerCase().includes(cq.trim().toLowerCase())).slice(0, 6) : [];

  const [editId, setEditId] = useState(null);
  const clearForm = () => { setNote(""); setDueDate(""); setCustId(""); setCq(""); setContactName(""); setContactPhone(""); setSeeBy("all"); setForStaffIds([]); setEditId(null); };
  const startEdit = (f) => {
    const pinned = followupPinnedTo(f);
    setEditId(f.id); setNote(f.note || ""); setDueDate(f.dueDate || ""); setCustId(f.customerId || "");
    setCq(""); setContactName(f.contactName || ""); setContactPhone(f.contactPhone || "");
    setSeeBy(pinned.length ? "one" : "all"); setForStaffIds(pinned); setMode("new");
  };
  const togglePin = (id) => setForStaffIds((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const add = () => {
    if (!note.trim()) return;
    const pinned = seeBy === "one" ? (forStaffIds.length ? forStaffIds : [meId]) : [];
    if (editId) {
      onSave(followups.map((x) => (x.id === editId ? { ...x, note: note.trim(), customerId: custId || "",
        contactName: contactName.trim(), contactPhone: contactPhone.trim(),
        forStaffIds: pinned, forStaffId: "", dueDate: dueDate || "",
        editedBy: me ? me.name : "", editedAt: new Date().toISOString() } : x)));
    } else {
      onSave([...followups, { id: cid(), note: note.trim(), customerId: custId || "",
        contactName: contactName.trim(), contactPhone: contactPhone.trim(),
        forStaffIds: pinned, dueDate: dueDate || "",
        createdBy: me ? me.name : "", createdById: meId, at: new Date().toISOString(), done: false }]);
    }
    clearForm(); setMode("list"); setWho("all");
  };
  const complete = (f) => onSave(followups.map((x) => (x.id === f.id ? { ...x, done: true, doneBy: me ? me.name : "", doneAt: new Date().toISOString() } : x)));
  const reopen = (f) => onSave(followups.map((x) => (x.id === f.id ? { ...x, done: false, doneBy: "", doneAt: "" } : x)));

  return (
    <div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div><strong style={{ fontSize: 16 }}>Follow-ups</strong><div className="sub">Loose ends that must not be forgotten — parts to source, calls to make, things to check</div></div>
        {mode !== "start" && <button className="btn ghost mini" onClick={() => setMode("start")}>‹ Follow-ups home</button>}
      </div>

      {mode === "start" && (
        <div className="posstart" style={{ marginTop: 24 }}>
          <button type="button" className="posstart-btn" onClick={() => { clearForm(); setMode("new"); }}>
            <PlusIcon size={44} />
            <span className="posstart-lbl">Create a Follow-up</span>
            <span className="posstart-sub">With a customer from the search — or none at all</span>
          </button>
          <button type="button" className="posstart-btn" style={{ borderColor: open.some((f) => followupOverdue(f, today)) ? "var(--sun)" : undefined }} onClick={() => { setMode("list"); setWho("mine"); }}>
            <TickIcon size={44} />
            <span className="posstart-lbl">Holding Follow-ups{open.length ? ` (${open.length})` : ""}</span>
            <span className="posstart-sub">{open.some((f) => followupOverdue(f, today)) ? "Some are overdue — open the list" : "Everything waiting on someone"}</span>
          </button>
        </div>
      )}

      {mode === "new" && (
      <div className="panel" style={{ padding: 13, maxWidth: 680, marginBottom: 14 }}>
        <label>{editId ? "Edit follow-up" : "New follow-up"}</label>
        <div className="fields" style={{ marginTop: 4 }}>
          <div className="f-wide" style={{ position: "relative" }}>
            <label>Customer (search — fills in name and number)</label>
            {custId ? (
              <div className="btnrow"><span className="chip" data-ch="email"><b>{clientName(custId)}</b></span>
                <button className="btn ghost mini" onClick={() => setCustId("")}>✕</button></div>
            ) : (<>
              <input placeholder="Search a customer — or leave blank and type below" value={cq} onChange={(e) => setCq(e.target.value)} />
              {cMatches.length > 0 && (
                <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 180 }}>
                  {cMatches.map((c) => <button key={c.id} type="button" className="poslookupitem" onClick={() => pickCustomer(c)}><b>{c.name}</b></button>)}
                </div>
              )}
            </>)}
          </div>
          <div><label>Full name</label><input value={contactName} placeholder="Who to follow up with" onChange={(e) => setContactName(e.target.value)} /></div>
          <div><label>Phone number</label><input value={contactPhone} placeholder="Their number" onChange={(e) => setContactPhone(e.target.value)} /></div>
          <div className="f-wide">
            <label>Reason for the follow-up</label>
            <textarea value={note} style={{ minHeight: 54 }} placeholder="e.g. Asked us to call back Thursday about the heater quote"
              onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <div className="fields" style={{ marginTop: 8 }}>
          <div className="f-wide">
            <label>Who can see it</label>
            <div className="seg">
              <button type="button" data-on={seeBy === "all" ? 1 : 0} onClick={() => setSeeBy("all")}>All employees</button>
              <button type="button" data-on={seeBy === "one" ? 1 : 0} onClick={() => setSeeBy("one")}>Specific employees</button>
            </div>
          </div>
          {seeBy === "one" && (
            <div className="f-wide">
              <label>Which employees{forStaffIds.length ? ` (${forStaffIds.length} picked)` : ""}</label>
              <div className="picklist">
                {staff.filter((s) => s.active !== false).map((s) => (
                  <button key={s.id} type="button" className="pick" data-on={forStaffIds.includes(s.id) ? 1 : 0} onClick={() => togglePin(s.id)}>
                    <span className="pnm">{s.name}</span>
                  </button>
                ))}
              </div>
              <div className="sub">Tap every employee who should see it. Only they, you, and the owner will — everyone else won't know it exists.</div>
            </div>
          )}
          <div><label>Remind on (optional)</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            <div className="sub">From this day on, whoever it's for gets a reminder pop-up when they sign in.</div></div>
        </div>
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button className="btn" data-cobrowse="followups.add" disabled={!note.trim()} onClick={add}>{editId ? "Save changes" : "Add follow-up"}</button>
          {editId && <button className="btn ghost" onClick={() => { clearForm(); setMode("list"); }}>Cancel</button>}
        </div>
      </div>
      )}

      {mode === "list" && (<>
      <div className="seg" style={{ marginBottom: 12 }}>
        {[["mine", `Mine (${open.filter((f) => { const p = followupPinnedTo(f); return !p.length || p.includes(meId); }).length})`], ["all", `Everyone (${open.length})`], ["done", "Done"]].map(([v, l]) => (
          <button key={v} type="button" data-on={who === v ? 1 : 0} onClick={() => setWho(v)}>{l}</button>
        ))}
      </div>

      {shown.length === 0 ? <div className="empty">{who === "done" ? "Nothing completed yet." : "Nothing outstanding — nice."}</div>
        : shown.map((f) => {
          const od = followupOverdue(f, today);
          return (
            <div key={f.id} className="visit" style={{ borderLeft: `4px solid ${f.done ? "#12A650" : od ? "#DC1515" : "#0887C8"}` }}>
              <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ flex: 1, minWidth: 200, fontWeight: 650, textDecoration: f.done ? "line-through" : "none", color: f.done ? "var(--muted)" : "inherit" }}>{f.note}</span>
                {f.customerId && <button className="link" onClick={() => onPick && onPick(f.customerId)}>{clientName(f.customerId) || "Customer"}</button>}
                {!f.customerId && f.contactName && <span style={{ fontWeight: 650 }}>{f.contactName}</span>}
                {f.contactPhone && <a className="link" href={"tel:" + String(f.contactPhone).replace(/[^0-9+]/g, "")}>{f.contactPhone}</a>}
                {followupPinnedTo(f).length > 0 && <span className="doc" style={{ background: "#17356E" }}>{followupPinnedTo(f).map(staffName).filter(Boolean).join(", ")}</span>}
                {f.dueDate && !f.done && <span className="doc" style={{ background: od ? "#DC1515" : "#5A6E86" }}>{od ? "OVERDUE · " : "due "}{f.dueDate}</span>}
                <span className="sub" style={{ marginTop: 0 }}>{f.done ? `done ${localDay(f.doneAt)} by ${f.doneBy}` : `added ${localDay(f.at)}${f.createdBy ? " by " + f.createdBy : ""}${f.editedAt ? ` · edited ${localDay(f.editedAt)}${f.editedBy ? " by " + f.editedBy : ""}` : ""}`}</span>
                {!f.done && <button className="btn ghost mini" onClick={() => startEdit(f)}>Edit</button>}
                {f.done
                  ? <button className="btn ghost mini" onClick={() => reopen(f)}>Reopen</button>
                  : <button className="btn mini" onClick={() => complete(f)}>✓ Done</button>}
              </div>
            </div>
          );
        })}
      </>)}
    </div>
  );
}

/* ---------- In-House Repair workshop page ---------- */
function WrenchIcon({ size = 16 }) {
  const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block" }}>
      <path {...P} d="M12.5 7.5 L16.5 3.5 M13.8 2.6 a4.4 4.4 0 0 0 -5.9 5.4 L2.6 13.3 a2.2 2.2 0 0 0 3.1 3.1 L11 11.1 a4.4 4.4 0 0 0 5.4 -5.9 L13.6 8 L12 6.4 Z" />
    </svg>
  );
}

const repairDocCss = `body{font:13.5px/1.55 -apple-system,Segoe UI,Roboto,sans-serif;color:#12233b;max-width:560px;margin:26px auto;padding:0 14px;}h1{font-size:19px;color:#0B4F8A;margin:0 0 2px;}h2{font-size:14px;margin:16px 0 6px;border-bottom:1px solid #cbd5e1;padding-bottom:3px;}.muted{color:#5a6472;}table{width:100%;border-collapse:collapse;}td,th{padding:4px 2px;text-align:left;vertical-align:top;}.r{text-align:right;}.big{font-size:22px;font-weight:800;font-family:ui-monospace,Menlo,monospace;}@media print{body{margin:10px auto;}}`;
// Phone cameras treat a bare ticket number as a web search (Yvonne: landed on Google).
// Encode a Service Desk URL; parseRepairTicketFromLocation + WorkshopView open that ticket.
function repairQrPayload(ticketNo, origin) {
  const no = String(ticketNo || "").trim();
  if (!no) return "";
  let base = origin || "https://app.poolmaster.ca";
  if (!origin) {
    try { if (window.location && window.location.origin) base = window.location.origin; } catch {}
  }
  return String(base).replace(/\/$/, "") + "/#repair=" + encodeURIComponent(no);
}
function repairTicketLink(ticketNo) {
  return repairQrPayload(ticketNo);
}
function parseRepairTicketFromLocation(loc) {
  try {
    const L = loc || (typeof window !== "undefined" ? window.location : { hash: "", search: "", href: "" });
    const hash = String(L.hash || "").replace(/^#/, "").replace(/^\//, "");
    const search = String(L.search || "").replace(/^\?/, "");
    const hashQs = hash.indexOf("=") >= 0 ? hash : "";
    const params = new URLSearchParams(hashQs + (hashQs && search ? "&" : "") + search);
    let raw = String(params.get("repair") || "").trim().toUpperCase();
    if (!raw) {
      const href = String(L.href || "");
      const hm = href.match(/[#?&]repair=([^&#]*)/i);
      if (hm) raw = decodeURIComponent(hm[1] || "").trim().toUpperCase();
    }
    const m = raw.match(/RP-\d+/);
    return m ? m[0] : "";
  } catch { return ""; }
}
function stripRepairTicketFromLocation(loc, hist) {
  try {
    const L = loc || window.location;
    const H = hist || window.history;
    const url = new URL(L.href);
    url.searchParams.delete("repair");
    const rawHash = String(url.hash || "").replace(/^#/, "");
    if (rawHash) {
      const hp = new URLSearchParams(rawHash);
      if (hp.has("repair")) {
        hp.delete("repair");
        url.hash = hp.toString();
      }
    }
    const next = `${url.pathname}${url.search}${url.hash || ""}`;
    H.replaceState(H.state, "", next);
    return next;
  } catch { return ""; }
}
function consumeRepairTicketFromLocation(loc, hist) {
  const ticket = parseRepairTicketFromLocation(loc);
  if (ticket) stripRepairTicketFromLocation(loc, hist);
  return ticket || "";
}
const repairQrImg = (ticketNo, px = 130) => {
  const payload = repairQrPayload(ticketNo);
  return `<img src="https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&data=${encodeURIComponent(payload)}" width="${px}" height="${px}" alt="QR ${String(ticketNo || "").trim()}">`;
};
function RepairQrImg({ ticketNo, px = 130 }) {
  const payload = repairQrPayload(ticketNo);
  if (!payload) return null;
  return (
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&data=${encodeURIComponent(payload)}`}
      width={px} height={px} alt={`QR ${ticketNo}`} title={payload} data-repair-qr="1"
      style={{ display: "block", background: "#fff", border: "1px solid var(--line)", borderRadius: 6 }}
    />
  );
}

function printRepairDoc(kind, t, bp = {}, terms = []) {
  const w = window.open("", "_blank"); if (!w) return;
  const shop = `${bp.name || "Pool Master Canada"}${bp.phone ? " · " + bp.phone : ""}${bp.address ? " · " + bp.address : ""}`;
  const warrantyLine = t.warranty === "no" ? "Non-warranty repair" : (t.warranty === "yes-invoice" || t.invoiceProof) ? "Warranty — proof of purchase on file" : "Warranty — WAITING on proof of purchase";
  if (kind === "label") {
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${posEsc(t.ticketNo)}</title>
<style>body{font:12px/1.3 -apple-system,sans-serif;width:220px;margin:6px;color:#000;}.big{font-size:20px;font-weight:900;font-family:ui-monospace,Menlo,monospace;}@media print{body{margin:0;}}</style></head>
<body onload="window.print()">
  <div class="big">${posEsc(t.ticketNo)}</div>
  <div style="font-weight:700">${posEsc(t.customerName || "")}</div>
  <div>${posEsc([t.category, t.subCategory].filter(Boolean).join(" · "))}</div>
  <div>${posEsc([t.model ? "Model " + t.model : "", t.serial ? "S/N " + t.serial : "", t.color || ""].filter(Boolean).join(" · "))}</div>
  <div style="margin-top:6px">${repairQrImg(t.ticketNo, 110)}</div>
  <div class="muted">${posEsc(t.date)}</div>
</body></html>`);
  } else {
    const isWO = kind === "wo";
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${posEsc(t.ticketNo)} ${isWO ? "Work Order" : "Receipt"}</title><style>${repairDocCss}</style></head>
<body onload="window.print()">
  <table><tr><td><h1>${posEsc(bp.name || "Pool Master Canada")}</h1><div class="muted">${posEsc(shop)}</div></td>
  <td class="r"><div class="big">${posEsc(t.ticketNo)}</div><div class="muted">${posEsc(t.date)}</div>${repairQrImg(t.ticketNo, 90)}</td></tr></table>
  <h2>${isWO ? "Repair Work Order (shop copy)" : "Drop-off Receipt (customer copy)"}</h2>
  <table>
    <tr><th style="width:130px">Customer</th><td>${posEsc(t.customerName || "")}${t.phone ? " · " + posEsc(t.phone) : ""}${t.email ? " · " + posEsc(t.email) : ""}</td></tr>
    ${t.address ? `<tr><th>Address</th><td>${posEsc(t.address)}</td></tr>` : ""}
    <tr><th>Unit</th><td>${posEsc([t.category, t.subCategory].filter(Boolean).join(" · "))}${t.color ? " · " + posEsc(t.color) : ""}</td></tr>
    ${t.model ? `<tr><th>Model #</th><td>${posEsc(t.model)}</td></tr>` : ""}
    ${t.serial ? `<tr><th>Serial #</th><td>${posEsc(t.serial)}</td></tr>` : ""}
    <tr><th>Reported problem</th><td>${posEsc(t.problem || "")}</td></tr>
    <tr><th>Warranty</th><td>${posEsc(warrantyLine)}</td></tr>
    ${t.payment && t.payment.paid ? `<tr><th>Payment</th><td>${posEsc(posMoney(t.payment.amount))} by ${posEsc(t.payment.method)}</td></tr>` : ""}
    ${t.status === "COMPLETED" ? `<tr><th>Completed</th><td>${posEsc(localDay(t.completedAt) || t.date)}${t.disposal ? ((t.disposal.kind === "robot" || isRoboticRepair(t)) ? " — robotic cleaner disposed, customer agreed (no pickup)" : " — customer declined the quote, disposal approved") : t.pickup && t.pickup.by ? ` — picked up, released by ${posEsc(t.pickup.by)}` : ""}</td></tr>`
      : t.stage && t.status === "REPAIRS_QUEUE" ? `<tr><th>Status</th><td>${posEsc(t.stage)}</td></tr>` : ""}
  </table>
  ${kind === "receipt" && t.closing ? `<h2>Repair summary</h2><table>
    <tr><th style="width:130px">What failed</th><td>${posEsc(t.closing.failure || "")}</td></tr>
    <tr><th>Work done</th><td>${posEsc(t.closing.noParts ? "Labour only — no parts replaced" : (t.closing.parts || []).filter((p) => String(p.name || "").trim()).map((p) => `${num(p.qty) > 1 ? p.qty + " × " : ""}${p.name}`).join(", "))}</td></tr>
    <tr><th>Billing</th><td>${posEsc(({ billed: "Billed to customer", warranty: "Warranty — no charge", goodwill: "No charge", partial: "Partly billed" })[t.closing.billed] || "")}${deductibleOutstanding(t) > 0 ? ` — $${deductibleOutstanding(t).toFixed(2)} owing` : ""}</td></tr>
    ${t.closing.warrantyAnswer === "yes" ? `<tr><th>Warranty coverage</th><td>${posEsc((t.closing.warrantyParts || []).filter((w) => w.name).map((w) => `${w.name} — covered until ${w.until}`).join("; "))}</td></tr>` : ""}
  </table>` : ""}
  ${isWO ? `
  ${t.quote && (t.quote.items || []).length ? `<h2>Quote${t.quote.status ? ` — ${posEsc(t.quote.status)}` : ""}</h2>
  <table>${t.quote.items.map((it) => `<tr><td>${posEsc(it.desc)}</td><td class="r">${posEsc(posMoney(it.amount))}</td></tr>`).join("")}
  <tr><th>Total</th><th class="r">${posEsc(posMoney(t.quote.items.reduce((a, it) => a + num(it.amount), 0)))}</th></tr></table>` : ""}
  <h2>Work performed${(t.labour || []).length ? ` — ${t.labour.reduce((a, l) => a + num(l.hours), 0).toFixed(1)}h total` : ""}</h2>
  ${(t.labour || []).length
    ? `<table>${t.labour.map((l) => `<tr><td style="width:84px">${posEsc(localDay(l.at))}</td><td style="width:110px">${posEsc(l.techName || "")}</td><td class="r" style="width:50px">${num(l.hours).toFixed(1)}h</td><td>${posEsc(l.notes || "")}</td></tr>`).join("")}</table>`
    : `<div style="height:90px;border:1px dashed #cbd5e1;border-radius:8px;"></div>`}
  ${(t.partsUsed || []).length ? `<h2>Parts used</h2>
  <table>${t.partsUsed.map((p) => `<tr><td>${num(p.qty)} × ${posEsc(p.name || p.code || "")}</td><td class="r">${posEsc(posMoney(num(p.price) * num(p.qty)))}</td></tr>`).join("")}</table>` : ""}
  <h2>Shop notes</h2>
  ${(t.shopNotes || []).length
    ? t.shopNotes.map((n) => `<div><b>${(n.side || "office") === "tech" ? "BENCH" : "OFFICE"}</b> · ${posEsc(localDay(n.at))} · ${posEsc(n.by || "")} — ${posEsc(n.note || "")}</div>`).join("")
    : `<div style="height:120px;border:1px dashed #cbd5e1;border-radius:8px;"></div>`}
  <p class="muted">This form lives in the Service Desk app: In-House Repair → type ${posEsc(t.ticketNo)} in the ticket search to view or update it.</p>` :
    `<p class="muted">Keep this receipt — the repair ID above is how we track your unit. ${t.warranty === "yes-noinvoice" ? "REMINDER: your warranty repair starts once we receive your proof of purchase." : "We'll contact you when it's ready."}</p>`}
  ${kind !== "label" ? termsPrintHtml(currentTermsText(terms, "repair", t.termIds, t.termsNote)) : ""}
</body></html>`);
  }
  w.document.close();
}

/* What happened, at a glance — the closing story retail reads to the customer.
   Root cause and retail notes are internal; the PRINTED receipt stays customer-safe. */
function RepairClosingSummary({ t }) {
  const c = t.closing;
  if (!c) return null;
  const today = fmtDate(new Date());
  const owed = deductibleOutstanding(t);
  return (
    <div className="notice" style={{ borderLeft: "4px solid #17356E", marginBottom: 10 }}>
      <b>What happened / what to tell the customer</b>
      <div className="sub" style={{ marginTop: 5, lineHeight: 1.7 }}>
        <b>Failed:</b> {c.failure} · <b>Root cause (internal):</b> {c.rootCause}<br />
        <b>Parts replaced:</b> {c.noParts ? "none — labour only" : (c.parts || []).filter((p) => String(p.name || "").trim()).map((p) => `${num(p.qty) > 1 ? p.qty + " × " : ""}${p.name}`).join(", ")}<br />
        <b>Billing:</b> {REPAIR_BILLED_LABEL[c.billed] || "—"}{c.billedNote ? ` — ${c.billedNote}` : ""}{owed > 0 ? ` · $${owed.toFixed(2)} still owing` : ""}<br />
        <b>Warranty:</b> {c.warrantyAnswer === "yes"
          ? (c.warrantyParts || []).filter((w) => w.name).map((w) => `${w.name}${w.serial ? ` (S/N ${w.serial})` : ""} — ${today <= w.until ? "covered until" : "EXPIRED"} ${w.until}`).join("; ")
          : "no parts under warranty"}<br />
        <b>Follow-up:</b> {c.followUp === "yes" ? `${c.followUpWhat} — by ${c.followUpWhen}` : "none needed"}<br />
        <b>For retail (internal):</b> {c.retailNotes}
        {isPressureSideRepair(t) && <><br /><b>Operational check:</b> {c.opCheck || t.pressureOpCheck ? "tested and operating ✓" : "not confirmed"}{c.hoseAssembly || t.hoseAssembly ? " · hose assembly included" : " · hose assembly not included"}</>}
      </div>
      <div className="sub" style={{ marginTop: 4 }}>Checklist completed by {c.by} · {c.at ? new Date(c.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}{c.revisedFrom ? " · revised (earlier answers kept on the timeline)" : ""}</div>
    </div>
  );
}

function WorkshopView({ repairs = [], clients = [], staff = [], me, businessProfile = {}, parts = [], terms = [], onUseParts, onSaveRepairs, onCreateTicket, onCompose, onPick, rentalUnits = [], onRentalSave, onRentalCheckout, onRentalCheckIn, isMgr = false, onOpenCard, allSales = [], allJobs = [], onHoldSale, onSaveProfile, onCompleteTicket, onWarrantyRef, openTicketNo, onOpenTicketDone }) {
  const [tab, setTab] = useState("start"); // start | intake | forms | queue | parts | holding | done | quotes | dash
  const [payPop, setPayPop] = useState(null); // ticket id being confirmed → diagnostic fee screen
  const [pickupPop, setPickupPop] = useState(null); // ticket id → pickup/release form
  const [pickupDraft, setPickupDraft] = useState(null);
  const [payAmt, setPayAmt] = useState("80.00");
  const [payMethod2, setPayMethod2] = useState("POS terminal");
  const [payFilterOk, setPayFilterOk] = useState(false);
  const [payHoseOk, setPayHoseOk] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [wsq, setWsq] = useState("");
  // Arriving from a printed drop-off receipt / work-order QR (ticketNo) or Quotes Open (same repairJump).
  useEffect(() => {
    if (!openTicketNo) return;
    const id = String(openTicketNo).toUpperCase();
    const t = (repairs || []).find((x) => (x.ticketNo || "").toUpperCase() === id || String(x.id || "").toUpperCase() === id);
    if (t) {
      setTab(t.status === "COMPLETED" ? "done" : t.status === "HOLDING_BIN" ? "holding" : t.status === "WAITING_FOR_PARTS" ? "parts" : t.status === "INTAKE" ? "forms" : "queue");
      setOpenId(t.id);
    } else {
      setTab("start");
      setWsq(String(openTicketNo));
    }
    onOpenTicketDone && onOpenTicketDone();
  }, [openTicketNo]); // eslint-disable-line react-hooks/exhaustive-deps
  const today = fmtDate(new Date());
  const live = repairs.filter((t) => !t.archived);
  const byStatus = (s) => (s === "REPAIRS_QUEUE" ? sortRepairQueue(live.filter((t) => t.status === s)) : live.filter((t) => t.status === s).sort((a, b) => (b.at || "").localeCompare(a.at || "")));
  const open = repairs.find((t) => t.id === openId) || null;
  const saveTicket = (t) => onSaveRepairs(repairs.map((x) => (x.id === t.id ? t : x)));
  // Closing checklist draft being edited (one at a time), keyed by ticket id.
  const [closingDraft, setClosingDraft] = useState(null);
  const [dispDraft, setDispDraft] = useState(null);
  const [cwQ, setCwQ] = useState(""); // warranty-part catalogue search inside the form
  const openClosingFor = (t) => {
    const c = closingAnswersFromForm(t, t.closing || {});
    setClosingDraft({ ticketId: t.id,
      failure: c.failure || "", rootCause: c.rootCause || "",
      parts: c.parts || [], noParts: (c.parts || []).length ? false : true,
      billed: c.billed, billedNote: c.billedNote || "",
      warrantyAnswer: c.warrantyAnswer || "", warrantyParts: c.warrantyParts || [],
      followUp: c.followUp || "", followUpWhat: c.followUpWhat || "", followUpWhen: c.followUpWhen || "",
      workMonths: c.workMonths || "", retailNotes: c.retailNotes || "", opCheck: !!c.opCheck, hoseAssembly: c.hoseAssembly != null ? !!c.hoseAssembly : (t.hoseAssembly != null ? !!t.hoseAssembly : isHoseRepair(t)) });
    setCwQ("");
  };
  const saveClosing = (t) => {
    const { ticketId, billedUnlock, failureUnlock, ...answers } = closingDraft;
    const merged = closingAnswersFromForm(t, answers);
    delete merged.billedUnlock; delete merged.failureUnlock;
    // Hidden parts question: empty Parts used (and no leftover checklist-only
    // names) means labour only — the form already captured that.
    if (!merged.parts.length) merged.noParts = true;
    const prev = t.closing;
    const next = { ...merged, by: meName, at: new Date().toISOString(), ...(prev && prev.at ? { revisedFrom: prev.at } : {}) };
    saveTicket({ ...t, closing: next,
      closingHistory: prev && prev.at ? [...(t.closingHistory || []), prev] : (t.closingHistory || []) });
    setClosingDraft(null);
  };
  // Completing a checklist-passed ticket is ONE save: the status flip and any
  // follow-up it spawns travel together — no stacked saves to clobber.
  // Pickup/release form is required here unless disposal already skipped it.
  const completeTicket = (t) => {
    if (!t.disposal) {
      const pg = repairPickupGate(t);
      if (!pg.ok) { alert(pg.reason); return; }
    }
    const c = t.closing || {};
    const wantsFu = c.followUp === "yes" && !c.followupCreated;
    const months = num(c.workMonths) > 0 ? num(c.workMonths) : repairWarrantyMonthsFor(t, businessProfile);
    const base = { ...t, ...(t.disposal ? {} : { workWarranty: { months, until: addMonthsIso(fmtDate(new Date()), months) } }) };
    const t2 = withRepairStatus(wantsFu ? { ...base, closing: { ...c, followupCreated: true } } : base, "COMPLETED", meName);
    const cust = custOf(t);
    const fu = wantsFu ? { id: cid(), note: `Repair follow-up (${t.ticketNo} — ${[t.category, t.subCategory].filter(Boolean).join(" ")}): ${c.followUpWhat}`,
      customerId: t.customerId || "", custName: t.customerName || (cust ? cust.name : ""), custPhone: t.phone || "",
      forStaffId: "", dueDate: c.followUpWhen || "", createdBy: meName, at: new Date().toISOString(), done: false } : null;
    if (onCompleteTicket) onCompleteTicket(repairs.map((x) => (x.id === t.id ? t2 : x)), fu);
    else saveTicket(t2);
  };
  const toDatetimeLocal = (d) => {
    const x = d instanceof Date ? d : new Date(d);
    if (isNaN(+x)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
  };
  const openPickupFor = (t) => {
    const charged = repairAmountCharged(t);
    setPickupDraft({
      ticketId: t.id,
      customerName: t.customerName || "",
      unit: repairUnitLabel(t),
      ticketNo: t.ticketNo || "",
      atLocal: toDatetimeLocal(new Date()),
      by: meName,
      cost: num(charged.amount).toFixed(2),
      payMethod: (t.payment && t.payment.method) || "POS terminal",
      paymentConfirmed: false,
    });
    setPickupPop(t.id);
  };
  // Unit photos and the proof of purchase live in the private bucket; resolve the open
  // ticket's paths into short-lived viewing links so staff can actually see them.
  const [fileUrls, setFileUrls] = useState({});
  useEffect(() => {
    if (!SERVER || !supabase || !open) return;
    const paths = [...(open.photos || []).map((p) => p.path), open.invoiceProof && open.invoiceProof.path]
      .filter((p) => p && !fileUrls[p]);
    if (!paths.length) return;
    supabase.storage.from("photos").createSignedUrls([...new Set(paths)], 60 * 60).then(({ data }) => {
      if (!data) return;
      setFileUrls((m) => { const out = { ...m }; data.forEach((d) => { if (d.signedUrl) out[d.path] = d.signedUrl; }); return out; });
    }).catch(() => {});
  }, [openId, repairs]); // eslint-disable-line
  const meName = me ? me.name : "";
  const uploadUrl = (t) => `https://app.poolmaster.ca/repair-upload.html?t=${encodeURIComponent(t.uploadToken || "")}`;

  /* ---- intake wizard ---- */
  const blankIntake = () => ({ step: "what", cat: "", sub: "", customerId: "", name: "", address: "", phone: "", email: "", date: today,
    model: "", serial: "", color: "", problem: "", photos: [], warranty: null, hasInvoice: null, invoicePhoto: null, noEmail: false,
    payMethod: "POS terminal", payAmount: "", filterOk: false,
    termIds: defaultTermIds(terms, "repair"), termsNote: "" });
  const [ink, setInk] = useState(blankIntake());
  const set = (k, v) => setInk((s) => ({ ...s, [k]: v }));
  const [custSearch, setCustSearch] = useState("");
  const [scanPop, setScanPop] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const videoRef = useRef(null);
  const scanStream = useRef(null);

  const custMatches = custSearch.trim()
    ? clients.filter((c) => [c.name, c.address, ...(c.contacts || []).map((k) => `${k.name} ${k.phone} ${k.email}`)].join(" ").toLowerCase().includes(custSearch.trim().toLowerCase())).slice(0, 6)
    : [];
  const attachCustomer = (c) => {
    const k0 = (c.contacts || [])[0] || {};
    setInk((s) => ({ ...s, customerId: c.id, name: c.name, address: c.address || "", phone: k0.phone || "", email: k0.email || "" }));
    setCustSearch("");
    // pull product details from their latest repair
    const past = repairs.filter((t) => t.customerId === c.id).sort((a, b) => (b.at || "").localeCompare(a.at || ""));
    if (past[0]) setInk((s) => ({ ...s, model: s.model || past[0].model || "", serial: s.serial || past[0].serial || "", color: s.color || past[0].color || "", cat: s.cat || past[0].category || "", sub: s.sub || past[0].subCategory || "" }));
  };
  const applyScanText = (text) => {
    const id = String(text || "").trim().toUpperCase().match(/RP-\d+/);
    if (!id) { setScanMsg("That code isn't one of our repair labels — search the customer instead."); return; }
    const past = repairs.find((t) => (t.ticketNo || "").toUpperCase() === id[0]);
    if (!past) { setScanMsg(`No repair found for ${id[0]}.`); return; }
    setScanMsg("");
    setInk((s) => ({ ...s, customerId: past.customerId || "", name: past.customerName || "", address: past.address || "", phone: past.phone || "", email: past.email || "", model: past.model || "", serial: past.serial || "", color: past.color || "", cat: past.category || "", sub: past.subCategory || "" }));
    setScanPop(false); stopScan();
  };
  const stopScan = () => { if (scanStream.current) { scanStream.current.getTracks().forEach((x) => x.stop()); scanStream.current = null; } };
  const startScan = async () => {
    setScanPop(true); setScanMsg("");
    if (!("BarcodeDetector" in window)) { setScanMsg("This device's browser can't scan — type the repair ID below instead."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      scanStream.current = stream;
      const video = videoRef.current;
      video.srcObject = stream; await video.play();
      const det = new window.BarcodeDetector({ formats: ["qr_code"] });
      const loop = async () => {
        if (!scanStream.current) return;
        try { const codes = await det.detect(video); if (codes[0]) { applyScanText(codes[0].rawValue); return; } } catch {}
        requestAnimationFrame(loop);
      };
      loop();
    } catch { setScanMsg("Couldn't open the camera — type the repair ID below instead."); }
  };
  useEffect(() => () => stopScan(), []);

  const addIntakePhoto = async (file, primary) => {
    if (!file) return;
    const dataUrl = await shrink(file, 1100, 0.75);
    setInk((s) => ({ ...s, photos: primary ? [{ dataUrl, primary: true }, ...s.photos.filter((p) => !p.primary)] : [...s.photos, { dataUrl }] }));
  };
  const primaryPhoto = ink.photos.find((p) => p.primary);
  const emailOk = /\S+@\S+\.\S+/.test(ink.email || "");

  const uploadPhotos = async (ticketId, photos) => {
    const out = [];
    for (let i = 0; i < photos.length; i++) {
      const p = photos[i];
      try {
        const blob = await (await fetch(p.dataUrl)).blob();
        const path = `repairs/${ticketId}/${p.primary ? "primary" : "extra-" + i}-${Date.now()}.jpg`;
        const { error } = await supabase.storage.from("photos").upload(path, blob, { contentType: "image/jpeg", upsert: true });
        out.push(error ? p : { path, primary: p.primary });
      } catch { out.push(p); }
    }
    return out;
  };

  const finishIntake = async (payment) => {
    const id = cid();
    const photos = await uploadPhotos(id, ink.photos);
    let invoiceProof = null;
    if (ink.invoicePhoto) {
      const up = await uploadPhotos(id, [{ dataUrl: ink.invoicePhoto }]);
      invoiceProof = { ...up[0], at: new Date().toISOString() };
    }
    // A back-dated drop-off (unit left after hours, form caught up later) keeps the real
    // arrival date — the 48h reminders and turnaround numbers run off it. Never future.
    const dropDate = ink.date && ink.date < today ? ink.date : today;
    const base = {
      id, at: dropDate === today ? new Date().toISOString() : new Date(dropDate + "T12:00:00").toISOString(), date: dropDate,
      customerId: ink.customerId || "", customerName: ink.name || "Walk-in",
      address: ink.address, phone: ink.phone, email: ink.email,
      category: ink.cat, subCategory: ink.sub, model: ink.model, serial: ink.serial, color: ink.color, problem: ink.problem,
      photos, warranty: ink.warranty === "no" ? "no" : (ink.hasInvoice ? "yes-invoice" : "yes-noinvoice"),
      ...(() => {
        const matchCode = ink.wmatch && !ink.wmatch.miss ? ink.wmatch.code : ink.wproduct;
        const wp = (parts || []).find((p) => partHasWarranty(p) && (p.code || "").trim().toLowerCase() === String(matchCode || "").trim().toLowerCase());
        if (!wp || !ink.purchaseDate) return {};
        const term = (wp.warranty.terms || []).find((x) => x.id === ink.wterm) || null;
        const d = deductibleFor(wp.warranty, ink.purchaseDate, fmtDate(new Date()));
        return {
          purchase: { date: ink.purchaseDate, productCode: wp.code, productName: wp.name || "", source: ink.wmatch && !ink.wmatch.miss ? ink.wmatch.source : "manual entry",
            termScope: term ? term.scope : "", termMonths: term ? term.months : "" },
          ...(d.applies ? { deductible: { applies: true, amount: d.amount, dueFrom: d.dueFrom, status: "outstanding" } } : {}),
        };
      })(),
      invoiceProof,
      payment: null,
      status: "INTAKE", createdBy: meName,
      statusHistory: [{ id: cid(), from: "", to: "INTAKE", at: new Date().toISOString(), by: meName }],
      labour: [], comms: [], quote: null, reminderLastAt: "",
      techId: "", priority: "normal", stage: REPAIR_QUEUE_STAGE, etaDate: "", hold: null, shopNotes: [], partsUsed: [],
      termIds: ink.termIds || defaultTermIds(terms, "repair"), termsNote: ink.termsNote || "",
    };
    const ticket = await onCreateTicket(base);
    setInk({ ...blankIntake(), step: "done", doneTicket: ticket });
  };

  const logComm = (t, type, note) =>
    saveTicket({ ...t, comms: [...(t.comms || []), { id: cid(), type, note, at: new Date().toISOString(), by: meName }] });

  /* ---- shared ticket row ---- */
  const custOf = (t) => clients.find((c) => c.id === t.customerId);
  const row = (t) => (
    <div key={t.id} className="visit" style={{ borderLeft: `4px solid ${t.status === "COMPLETED" ? "#12A650" : t.status === "WAITING_FOR_PARTS" ? "#B7791F" : t.status === "HOLDING_BIN" ? "#DC1515" : t.status === "INTAKE" ? "#7C6CE0" : "#0887C8"}`, cursor: "pointer" }}
      onClick={() => setOpenId(openId === t.id ? null : t.id)}>
      <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <b className="mono" style={{ fontSize: 14 }}>{t.ticketNo}</b>
        <b>{t.customerName}</b>
        <span className="doc" style={{ background: "#17356E" }}>{[t.category, t.subCategory].filter(Boolean).join(" · ") || "Unit"}</span>
        {t.model && <span className="doc">Model {t.model}</span>}
        {t.etaDate && t.status !== "COMPLETED" && (
          <span className="doc" style={{ background: repairEtaOverdue(t, fmtDate(new Date())) ? "#DC1515" : "#0B6FBE" }}>
            {repairEtaOverdue(t, fmtDate(new Date())) ? "ETA PASSED · " : "ETA "}{t.etaDate}
          </span>
        )}
        {(() => { const lu = repairLastUpdated(t); return lu ? (
          repairStale(t, fmtDate(new Date()))
            ? <span className="doc" style={{ background: "#B36B00" }} title="Retail: chase the repair department">no update {businessDaysBetween(lu, fmtDate(new Date()))}+ biz days</span>
            : <span className="sub" style={{ marginTop: 0 }}>updated {String(lu).slice(0, 10)}</span>
        ) : null; })()}
        {t.serial && <span className="doc">S/N {t.serial}</span>}
        {t.warranty !== "no" && <span className="doc" style={{ background: (t.warranty === "yes-invoice" || t.invoiceProof) ? "#12A650" : "#B7791F" }}>{(t.warranty === "yes-invoice" || t.invoiceProof) ? "Warranty ✓" : "Warranty — no proof yet"}</span>}
        {t.status !== "COMPLETED" && (() => {
          const n = repairWarrantyMatches(t, repairs, (src) => repairWarrantyMonthsFor(src, businessProfile), fmtDate(new Date()), staff).filter((m) => m.status === "covered").length;
          return n ? <span className="doc" style={{ background: "#B31212" }}>⚠ {n} WARRANTY-COVERED PART{n === 1 ? "" : "S"}</span> : null;
        })()}
        {t.deductible && t.deductible.applies && (deductibleOutstanding(t) > 0
          ? <span className="doc" style={{ background: "#DC1515" }}>${deductibleOutstanding(t).toFixed(2)} DEDUCTIBLE OUTSTANDING</span>
          : <span className="doc" style={{ background: "#12A650" }}>deductible {t.deductible.status === "waived" ? "waived" : "paid"} ✓</span>)}
        {(t.priority || "normal") !== "normal" && <span className="doc" style={{ background: t.priority === "rush" ? "#DC1515" : "#5A6E86" }}>{t.priority === "rush" ? "RUSH" : "Low priority"}</span>}
        {t.status === "REPAIRS_QUEUE" && t.stage && <span className="doc" style={{ background: t.stage === "Ready for pickup" ? "#12A650" : BENCH_PARK_STAGES.includes(t.stage) ? "#B7791F" : "#0887C8" }}>{t.stage}</span>}
        {t.disposal && <span className="doc" style={{ background: "#5A6E86" }}>{(t.disposal.kind === "robot" || isRoboticRepair(t)) ? "Robot disposed ✓" : "Disposal approved ✓"}</span>}
        {t.pickup && t.pickup.at && <span className="doc" style={{ background: "#12A650" }}>Picked up ✓</span>}
        {t.techId && <span className="doc" style={{ background: "#5A6E86" }}>{(staff.find((s2) => s2.id === t.techId) || {}).name || "tech"}</span>}
        {t.status === "HOLDING_BIN" && t.hold && <span className="doc" style={{ background: "#DC1515" }}>{t.hold.reason} · since {localDay(t.hold.at)}</span>}
        {repairReminderDue(t) && <span className="doc" style={{ background: "#DC1515" }}>48h reminder due</span>}
        <span className="sub" style={{ marginTop: 0, marginLeft: "auto" }}>{t.date}{(t.labour || []).length ? ` · ${(t.labour || []).reduce((a, l) => a + num(l.hours), 0).toFixed(1)}h labour` : ""}</span>
      </div>
      {openId === t.id && detail(t)}
    </div>
  );

  /* ---- ticket detail: form + bench work first; timeline & comms/status folded (Yvonne) ---- */
  const [lbHours, setLbHours] = useState("");
  const [lbNote, setLbNote] = useState("");
  const [cmNote, setCmNote] = useState("");
  const [qDesc, setQDesc] = useState("");
  const [qAmt, setQAmt] = useState("");
  const [snNote, setSnNote] = useState("");
  const [snSide, setSnSide] = useState("all");
  const [snKind, setSnKind] = useState("");
  // Which side of the counter the writer is on decides where their note files.
  const mySide = me && ["tech", "senior", "vac"].includes(me.roleId) ? "tech" : "office";
  const [puQ, setPuQ] = useState("");
  const [holdPop, setHoldPop] = useState(null); // ticket id
  const [tq, setTq] = useState(""); // ticket search on the start page
  const [opOkId, setOpOkId] = useState(null); // warranty intake: ticket whose DROP-OFF operational check is ticked
  // DROP-OFF confirm only (filter out / hose stayed with the customer). Payment
  // and completion for pressure-side use pressurePayOpCheck — a different gate.
  const opCheckFor = (t) => t.subCategory === "Pressure-Driven"
    ? { label: "OPERATIONAL CHECK: no hose assembly taken in — unless the repair is specifically about the hose.",
        hint: "Confirm the hose assembly stayed with the customer (or the repair is hose-related) first." }
    : { label: "OPERATIONAL CHECK: the filter has been removed from the unit.",
        hint: "Confirm the filter is out first." };
  const closeAsDisposal = (t, givenWhy) => {
    const why = String(givenWhy != null ? givenWhy : (prompt("Short reason for the disposal closure (required — e.g. 'quote declined, unit uneconomical to fix')") || "")).trim();
    if (!why) return;
    saveTicket(withDisposalClose(t, why, meName));
  };
  const [fEdit, setFEdit] = useState(null); // drop-off form being edited: { id, ...fields }
  const saveFormEdit = () => {
    const t = repairs.find((x) => x.id === fEdit.id);
    if (!t) { setFEdit(null); return; }
    saveTicket({ ...t, customerName: fEdit.name.trim() || t.customerName, phone: fEdit.phone, email: fEdit.email, address: fEdit.address,
      model: fEdit.model, serial: fEdit.serial, color: fEdit.color, problem: fEdit.problem,
      shopNotes: [...(t.shopNotes || []), { id: cid(), note: "Drop-off form details updated", by: meName, side: mySide, at: new Date().toISOString() }] });
    setFEdit(null);
  };
  const [holdWhy, setHoldWhy] = useState(HOLD_REASONS[1]);
  const detail = (t) => {
    const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
    const rwAll = repairWarrantyMatches(t, repairs, (src) => repairWarrantyMonthsFor(src, businessProfile), fmtDate(new Date()), staff);
    const rwLive = rwAll.filter((m) => m.status === "covered");
    const rwCov = new Map(rwLive.map((m) => [m.code, m]));
    // Manager-only price override on a covered line; the reason is auditable on the timeline.
    const overrideCharge = (part, apply) => {
      const amt = prompt(`Charge for ${part} anyway — how much?`) || "";
      if (!(num(amt) >= 0) || !amt.trim()) return;
      const why = prompt("Reason for charging a warranty-covered part (required — lands on the timeline)") || "";
      if (!why.trim()) return;
      apply(num(amt).toFixed(2), why.trim());
    };
    const c = custOf(t);
    // Profile contact first, but a ticket keeps its own phone/email from drop-off —
    // reminders must still work when the profile is thin.
    const k0 = c ? ((c.contacts || [])[0] || {}) : {};
    const contact = { ...k0, phone: k0.phone || t.phone, email: k0.email || t.email };
    const totalHours = (t.labour || []).reduce((a, l) => a + num(l.hours), 0);
    const q = t.quote;
    const fileUrl = (p) => p.signedUrl || (p.path && fileUrls[p.path]) || p.dataUrl;
    return (
      <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
        {(t.photos || []).length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {(t.photos || []).map((p, i) => fileUrl(p) ? (
              <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer">
                <img src={fileUrl(p)} alt={p.primary ? "unit" : "unit photo"} style={{ width: 74, height: 74, objectFit: "cover", borderRadius: 8, border: p.primary ? "2px solid var(--tile)" : "1px solid var(--line)" }} />
              </a>
            ) : null)}
          </div>
        )}
        {t.warranty !== "no" && t.invoiceProof && (
          <div className="notice" style={{ marginBottom: 10, borderLeft: "4px solid #12A650" }}>
            <b>Proof of purchase on file</b> · received {fmtStamp(t.invoiceProof.at)}
            {t.invoiceProof.via ? ` · ${t.invoiceProof.via}` : t.invoiceProof.manual ? " · marked received at the counter" : ""}
            {t.invoiceProof.path && fileUrls[t.invoiceProof.path] && <> · <a className="link" href={fileUrls[t.invoiceProof.path]} target="_blank" rel="noreferrer">view document</a></>}
          </div>
        )}

        {t.disposal && (
          <div className="notice" style={{ marginBottom: 10, borderLeft: "4px solid #5A6E86" }}>
            <b>{(t.disposal.kind === "robot" || isRoboticRepair(t))
              ? "Robot disposed — customer agreed, no pickup"
              : "Closed — customer declined the quote and approved disposal"}</b>
            {" "}· {localDay(t.disposal.at)}{t.disposal.by ? ` · recorded by ${t.disposal.by}` : ""}{t.disposal.reason ? ` · ${t.disposal.reason}` : ""}. No pickup call needed.
          </div>
        )}

        {isRoboticRepair(t) && t.status !== "INTAKE" && t.status !== "COMPLETED" && !t.disposal && (() => {
          const d = dispDraft && dispDraft.ticketId === t.id ? dispDraft : { ticketId: t.id, why: "", agreed: false };
          return (
            <div className="panel" style={{ padding: 12, marginBottom: 10, borderLeft: "4px solid #5A6E86" }}>
              <b>Dispose this robotic cleaner</b>
              <div className="sub" style={{ marginTop: 4 }}>
                This is not a pickup. The robot stays here and the ticket closes — no pickup call, no “ready for pickup” text. Use only when the customer has already agreed we can dispose of it.
              </div>
              <label style={{ marginTop: 8 }}>Why is it being disposed?</label>
              <textarea value={d.why} placeholder="e.g. quote declined, uneconomical to fix — customer agreed we can dispose of it"
                style={{ minHeight: 64, width: "100%" }}
                onChange={(e) => setDispDraft({ ...d, why: e.target.value })} />
              <label className="chk" style={{ marginTop: 6 }}>
                <input type="checkbox" checked={!!d.agreed} onChange={(e) => setDispDraft({ ...d, agreed: e.target.checked })} />
                Customer agreed — dispose the robot and close without a pickup
              </label>
              <div className="btnrow" style={{ marginTop: 8 }}>
                <button className="btn warn mini" disabled={!String(d.why || "").trim() || !d.agreed}
                  onClick={() => { closeAsDisposal(t, d.why); setDispDraft(null); }}>🗑 Dispose robot and close</button>
              </div>
            </div>
          );
        })()}

        {t.status !== "COMPLETED" && rwLive.length > 0 && (
          <div className="notice" style={{ marginBottom: 10, borderLeft: "6px solid #B31212", background: "#FDF2F2", fontSize: 14 }}>
            <b style={{ color: "#B31212", fontSize: 15 }}>⚠ WARRANTY ALERT — this repair contains parts WE already replaced and still cover:</b>
            {rwLive.map((m) => (
              <div key={m.code} style={{ marginTop: 7 }}>
                <b>{m.name}</b> — replaced {m.replacedOn}{m.byTech ? ` by ${m.byTech}` : ""} ({m.sourceTicketNo})
                {m.matchedBy === "serial" ? <span className="doc" style={{ background: "#17356E", marginLeft: 6 }}>same unit — serial match</span> : null}
                <span> · repair warranty expires <b>{new Date(m.expiresOn + "T12:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</b> ({m.daysLeft} days left)</span>
                {m.labourUntil && <span> · labour {m.labourCovered ? <b>also covered until {m.labourUntil}</b> : `was covered to ${m.labourUntil} — now billable`}</span>}
                <button className="btn ghost mini" style={{ marginLeft: 8 }} onClick={() => {
                  const src = repairs.find((x) => x.id === m.sourceTicketId);
                  if (src) { setTab(src.status === "COMPLETED" ? "done" : "queue"); setOpenId(src.id); }
                }}>Open original repair</button>
              </div>
            ))}
            <div className="sub" style={{ marginTop: 7 }}>These parts are <b>not chargeable</b> — the part guard below prices them at $0 automatically. Labour on Softub/Hydromate repairs is covered for {REPAIR_LABOUR_DAYS} days after our repair; otherwise labour is billable. A manager can override with a reason.</div>
          </div>
        )}
        {t.status !== "COMPLETED" && rwLive.length === 0 && rwAll.length > 0 && (
          <div className="sub" style={{ marginBottom: 10 }}>
            Previously replaced by us: {rwAll.map((m) => `${m.name} (warranty expired ${m.expiresOn})`).join(" · ")}
          </div>
        )}

        {t.status === "COMPLETED" && t.pickup && t.pickup.at && (
          <div className="notice" style={{ marginBottom: 10, borderLeft: "4px solid #12A650" }}>
            <b>Picked up / released</b> · {new Date(t.pickup.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            {t.pickup.by ? ` · released by ${t.pickup.by}` : ""}{t.pickup.unit ? ` · ${t.pickup.unit}` : ""}
            {t.pickup.paymentStatus ? ` · payment ${t.pickup.paymentStatus}` : ""}.
          </div>
        )}
        {t.status === "COMPLETED" && t.closing && <RepairClosingSummary t={t} />}

        {t.status !== "COMPLETED" && t.status !== "INTAKE" && !t.disposal && (() => {
          const cg = repairClosingGate(t);
          const d = closingDraft && closingDraft.ticketId === t.id ? closingDraft : null;
          const setD = (patch) => setClosingDraft({ ...d, ...patch });
          return (
            <div className="panel" style={{ padding: 12, marginBottom: 10, borderLeft: cg.ok ? "4px solid #12A650" : "4px solid #B7791F" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <b>Repair Closing Checklist</b>
                {cg.ok ? <span className="doc" style={{ background: "#12A650" }}>COMPLETE ✓</span> : <span className="doc" style={{ background: "#B7791F" }}>REQUIRED BEFORE CLOSE</span>}
                {!d && <button className="btn mini" style={{ marginLeft: "auto" }} onClick={() => openClosingFor(t)}>{t.closing ? "Edit answers" : "Fill it in"}</button>}
              </div>
              {!d && !cg.ok && <div className="sub" style={{ marginTop: 5 }}>{cg.reason}</div>}
              {!d && cg.ok && t.closing && <div className="sub" style={{ marginTop: 5 }}>Answered by {t.closing.by} — fill in the pickup/release form when the unit leaves (Completed).</div>}
              {d && (<div style={{ marginTop: 10 }}>
                {(() => {
                  // Hide any closing question whose answer is already on this
                  // ticket's form. Unique closing facts (root cause, follow-up,
                  // retail notes, our-warranty expiries) stay here.
                  const formFailure = String(t.problem || "").trim();
                  const showFailure = !formFailure || d.failureUnlock || (String(d.failure || "").trim() && String(d.failure).trim() !== formFailure);
                  const live = closingAnswersFromForm(t, d);
                  const partsLine = live.noParts || !(live.parts || []).some((p) => String(p.name || "").trim())
                    ? "none — labour only"
                    : (live.parts || []).filter((p) => String(p.name || "").trim()).map((p) => `${num(p.qty) > 1 ? p.qty + " × " : ""}${p.name}`).join(", ");
                  const billedFromForm = formBilledForClosing(t);
                  const billedLocked = !!billedFromForm && !d.billedUnlock && d.billed !== "goodwill" && d.billed !== "partial";
                  return (<>
                {showFailure ? (<>
                <label>1. What failed?</label>
                <input value={d.failure} placeholder={formFailure || ""} onChange={(e) => setD({ failure: e.target.value })} />
                {formFailure ? <div className="sub" style={{ marginTop: 3 }}>Customer reported: {formFailure}</div> : null}
                </>) : (
                <div className="sub" style={{ marginTop: 0 }}>1. What failed — <b>{formFailure}</b> (from Reported problem on the drop-off form)
                  <button type="button" className="link" style={{ marginLeft: 8 }} onClick={() => setD({ failureUnlock: true, failure: d.failure || formFailure })}>Different diagnosis…</button>
                </div>
                )}
                <label style={{ marginTop: 8 }}>2. What was the root cause?</label>
                <input value={d.rootCause} onChange={(e) => setD({ rootCause: e.target.value })} />
                <div className="sub" style={{ marginTop: 8 }}>3. Parts replaced — <b>{partsLine}</b> (from Parts used on this ticket — add or remove parts there, not here; leftover checklist names are kept)</div>
                {billedLocked ? (
                <div className="sub" style={{ marginTop: 8 }}>4. Billing — <b>{REPAIR_BILLED_LABEL[live.billed]}</b> (from the quote / warranty / payment on this ticket)
                  <button type="button" className="link" style={{ marginLeft: 8 }} onClick={() => setD({ billedUnlock: true, billed: d.billed || billedFromForm })}>Goodwill or partial…</button>
                </div>
                ) : (<>
                <label style={{ marginTop: 8 }}>4. Was it billed to the customer?</label>
                <div className="seg" style={{ margin: "4px 0 0" }}>
                  {Object.entries(REPAIR_BILLED_LABEL).map(([v, l]) => (
                    <button key={v} type="button" data-on={d.billed === v ? 1 : 0} onClick={() => setD({ billed: v })}>{l}</button>
                  ))}
                </div>
                {d.billed === "partial" && <input value={d.billedNote} placeholder="what was billed vs not — one line" style={{ marginTop: 5 }} onChange={(e) => setD({ billedNote: e.target.value })} />}
                </>)}
                <label style={{ marginTop: 8 }}>5. Are any replaced parts under warranty?</label>
                <div className="seg" style={{ margin: "4px 0 0" }}>
                  <button type="button" data-on={d.warrantyAnswer === "yes" ? 1 : 0} onClick={() => setD({ warrantyAnswer: "yes", warrantyParts: d.warrantyParts.length ? d.warrantyParts : [{ name: "", serial: "", start: fmtDate(new Date()), until: "" }] })}>Yes</button>
                  <button type="button" data-on={d.warrantyAnswer === "no" ? 1 : 0} onClick={() => setD({ warrantyAnswer: "no" })}>No</button>
                </div>
                {d.warrantyAnswer === "yes" && (<>
                  <div style={{ position: "relative", marginTop: 6 }}>
                    <input placeholder="search the catalogue — auto-fills the expiry from the part's warranty term" value={cwQ} onChange={(e) => setCwQ(e.target.value)} />
                    {cwQ.trim() && (
                      <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 5, maxHeight: 160 }}>
                        {parts.filter((p2) => p2.active !== false && [p2.code, p2.name].join(" ").toLowerCase().includes(cwQ.trim().toLowerCase())).slice(0, 5).map((p2) => {
                          const term = partHasWarranty(p2) ? (p2.warranty.terms || [])[0] : null;
                          return (
                            <button key={p2.id} type="button" className="poslookupitem" onClick={() => {
                              const start = fmtDate(new Date());
                              setD({ warrantyParts: [...d.warrantyParts.filter((w) => w.name), { name: p2.name || p2.code, code: p2.code || "", serial: "", start, until: term ? addMonthsIso(start, term.months) : "" }] });
                              setCwQ("");
                            }}>
                              <b>{p2.name}</b>
                              <span className="sub" style={{ marginTop: 0 }}>{term ? `${term.scope || "warranty"} — ${term.months} months from today` : "no term on file — set the expiry by hand"}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {d.warrantyParts.map((w, i) => (
                    <div key={i} style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                      <input value={w.name} placeholder="part" style={{ flex: 1, minWidth: 120 }} onChange={(e) => setD({ warrantyParts: d.warrantyParts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                      <input value={w.serial} placeholder="serial (if any)" style={{ width: 120 }} onChange={(e) => setD({ warrantyParts: d.warrantyParts.map((x, j) => (j === i ? { ...x, serial: e.target.value } : x)) })} />
                      <label className="sub" style={{ marginTop: 0 }}>expires <input type="date" value={w.until} style={{ width: "auto", marginLeft: 4 }} onChange={(e) => setD({ warrantyParts: d.warrantyParts.map((x, j) => (j === i ? { ...x, until: e.target.value } : x)) })} /></label>
                      <button className="btn ghost mini" onClick={() => setD({ warrantyParts: d.warrantyParts.filter((_, j) => j !== i) })}>×</button>
                    </div>
                  ))}
                </>)}
                <label style={{ marginTop: 8 }}>6. Does the customer need follow-up?</label>
                <div className="seg" style={{ margin: "4px 0 0" }}>
                  <button type="button" data-on={d.followUp === "yes" ? 1 : 0} onClick={() => setD({ followUp: "yes" })}>Yes</button>
                  <button type="button" data-on={d.followUp === "no" ? 1 : 0} onClick={() => setD({ followUp: "no" })}>No</button>
                </div>
                {d.followUp === "yes" && (
                  <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                    <input value={d.followUpWhat} placeholder="what needs to happen" style={{ flex: 1, minWidth: 160 }} onChange={(e) => setD({ followUpWhat: e.target.value })} />
                    <label className="sub" style={{ marginTop: 0 }}>by <input type="date" value={d.followUpWhen} style={{ width: "auto", marginLeft: 4 }} onChange={(e) => setD({ followUpWhen: e.target.value })} /></label>
                  </div>
                )}
                <label style={{ marginTop: 8 }}>7. Anything retail/office should know when they speak to the customer?</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={d.retailNotes} style={{ flex: 1 }} onChange={(e) => setD({ retailNotes: e.target.value })} />
                  <button className="btn ghost mini" onClick={() => setD({ retailNotes: "Nothing to add" })}>Nothing to add</button>
                </div>
                <label style={{ marginTop: 8 }}>Our repair warranty on this work</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input inputMode="numeric" value={d.workMonths ?? repairWarrantyMonthsFor(t, businessProfile)} style={{ width: 60, textAlign: "center" }} onChange={(e) => setD({ workMonths: e.target.value })} />
                  <span className="sub" style={{ marginTop: 0 }}>months from completion — a re-skin carries longer than a cable swap; the default comes from the shop setting.</span>
                </div>
                <div className="sub" style={{ marginTop: 5 }}>A completed follow-up answer of Yes books it into the Follow-ups page automatically when the repair is marked Completed.</div>
                {isPressureSideRepair(t) && (() => {
                  const pc = pressurePayOpCheck(t);
                  return (
                    <div className="notice" style={{ marginTop: 10, borderLeft: "4px solid #B7791F" }}>
                      <label className="chk" style={{ marginTop: 0, alignItems: "flex-start" }}>
                        <input type="checkbox" checked={!!d.opCheck} style={{ width: "auto", marginTop: 3 }} onChange={(e) => setD({ opCheck: e.target.checked })} />
                        {pc.label}
                      </label>
                      <label className="chk" style={{ marginTop: 6, alignItems: "flex-start" }}>
                        <input type="checkbox" checked={!!d.hoseAssembly} style={{ width: "auto", marginTop: 3 }} onChange={(e) => setD({ hoseAssembly: e.target.checked })} />
                        {pc.hoseLabel}{pc.hoseDefaultOn ? " — on because this looks like a hose repair" : " (optional — not assumed)"}
                      </label>
                      {!d.opCheck && <div className="sub" style={{ marginTop: 6, color: "var(--clay)", fontWeight: 650 }}>{pc.hint}</div>}
                    </div>
                  );
                })()}
                <div className="btnrow" style={{ marginTop: 10 }}>
                  <button className="btn" onClick={() => saveClosing(t)}>Save checklist</button>
                  <button className="btn ghost" onClick={() => setClosingDraft(null)}>Cancel</button>
                </div>
                  </>);
                })()}
              </div>)}
            </div>
          );
        })()}

        <div className="sechead" style={{ marginTop: 0 }}>Drop-off form</div>
        {fEdit && fEdit.id === t.id ? (<>
          <div className="fields">
            <div><label>Customer name</label><input value={fEdit.name} onChange={(e) => setFEdit({ ...fEdit, name: e.target.value })} /></div>
            <div><label>Phone</label><input value={fEdit.phone} inputMode="tel" onChange={(e) => setFEdit({ ...fEdit, phone: e.target.value })} /></div>
            <div><label>Email</label><input value={fEdit.email} onChange={(e) => setFEdit({ ...fEdit, email: e.target.value })} /></div>
            <div className="f-wide"><label>Address</label><input value={fEdit.address} onChange={(e) => setFEdit({ ...fEdit, address: e.target.value })} /></div>
            <div><label>Model number</label><input value={fEdit.model} onChange={(e) => setFEdit({ ...fEdit, model: e.target.value })} /></div>
            <div><label>Serial number</label><input value={fEdit.serial} onChange={(e) => setFEdit({ ...fEdit, serial: e.target.value })} /></div>
            <div><label>Colour</label><input value={fEdit.color} onChange={(e) => setFEdit({ ...fEdit, color: e.target.value })} /></div>
            <div className="f-wide"><label>Reported problem</label><textarea value={fEdit.problem} style={{ minHeight: 60 }} onChange={(e) => setFEdit({ ...fEdit, problem: e.target.value })} /></div>
          </div>
          <div className="btnrow" style={{ marginTop: 6, marginBottom: 8 }}>
            <button className="btn mini" onClick={saveFormEdit}>Save form</button>
            <button className="btn ghost mini" onClick={() => setFEdit(null)}>Cancel</button>
          </div>
        </>) : (
          <div className="sub" style={{ marginTop: 2, marginBottom: 8, lineHeight: 1.6 }}>
            <b>{t.customerName}</b>{t.phone ? ` · ${t.phone}` : " · no phone"}{t.email ? ` · ${t.email}` : " · no email"}{t.address ? ` · ${t.address}` : ""}<br />
            {[t.category, t.subCategory].filter(Boolean).join(" · ") || "Unit"}{t.model ? ` · Model ${t.model}` : ""}{t.serial ? ` · S/N ${t.serial}` : ""}{t.color ? ` · ${t.color}` : ""}<br />
            Reported problem: {t.problem || "—"}
            <div className="btnrow" style={{ marginTop: 5 }}>
              <button className="btn ghost mini" onClick={() => setFEdit({ id: t.id, name: t.customerName || "", phone: t.phone || "", email: t.email || "", address: t.address || "", model: t.model || "", serial: t.serial || "", color: t.color || "", problem: t.problem || "" })}>✎ Edit form</button>
            </div>
          </div>
        )}
        {t.status === "INTAKE" && (
          <div className="notice" style={{ marginBottom: 10, borderLeft: "4px solid var(--sun)" }}>
            <b>Confirm the drop-off with the customer:</b> name & contact, unit, model and serial, the reported problem, and the warranty answer below. Then hit confirm.
            {t.warranty !== "no" && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 9, margin: "8px 0 0", textTransform: "none", letterSpacing: 0, fontSize: 13.5, fontWeight: 650 }}>
                <input type="checkbox" checked={opOkId === t.id} style={{ width: "auto", marginTop: 3 }} onChange={(e) => setOpOkId(e.target.checked ? t.id : null)} />
                {opCheckFor(t).label}
              </label>
            )}
            <div className="btnrow" style={{ marginTop: 8 }}>
              <button className="btn" disabled={t.warranty !== "no" && opOkId !== t.id} onClick={() => {
                if (t.warranty === "no") { setPayAmt("80.00"); setPayMethod2("POS terminal"); setPayFilterOk(false); setPayHoseOk(isHoseRepair(t)); setPayPop(t.id); return; }
                const r = repairIntakeRoute(true, t.warranty === "yes-invoice");
                const t2 = { ...t, filterConfirmed: true };
                saveTicket(r.status === "HOLDING_BIN" ? holdRepair(t2, "Awaiting proof of purchase", meName, "REPAIRS_QUEUE") : withRepairStatus(t2, r.status, meName));
                setOpOkId(null);
              }}>
                ✓ Confirmed with customer{t.warranty === "no" ? " — take the $80.00 diagnostic fee" : t.warranty === "yes-invoice" ? " — to the repairs queue" : " — to the holding bin"}
              </button>
            </div>
            {t.warranty !== "no" && opOkId !== t.id && <div className="sub" style={{ marginTop: 6, color: "var(--clay)", fontWeight: 650 }}>{opCheckFor(t).hint}</div>}
          </div>
        )}
        <div className="btnrow" style={{ flexWrap: "wrap", alignItems: "center" }}>
          {t.status !== "INTAKE" && (() => {
            const cur = repairFormChoice(t);
            const choices = REPAIR_FORM_STATUSES.includes(cur) ? REPAIR_FORM_STATUSES : [cur, ...REPAIR_FORM_STATUSES];
            return (
              <label className="sub" style={{ marginTop: 0 }}>Status
                <select value={cur} style={{ width: "auto", marginLeft: 6, minWidth: 220 }} title="Repair status"
                  onChange={(e) => {
                    const choice = e.target.value;
                    if (choice === "COMPLETED" && !t.disposal) {
                      const cg = repairClosingGate(t);
                      if (!cg.ok) { alert(cg.reason); openClosingFor(t); return; }
                      openPickupFor(t);
                      return;
                    }
                    if (choice !== "WAITING_FOR_PARTS" && choice !== "COMPLETED") {
                      const gate = repairStageGate(t, choice);
                      if (!gate.ok) { alert(gate.reason); return; }
                    }
                    const base = t.disposal && choice !== "COMPLETED" ? { ...t, disposal: null } : t;
                    let next = applyRepairFormChoice(base, choice, meName);
                    if (choice !== "WAITING_FOR_PARTS" && choice !== "COMPLETED") {
                      next = { ...next, shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Status → ${choice}`, by: meName, side: "tech", at: new Date().toISOString() }] };
                    }
                    saveTicket(next);
                  }}>
                  {choices.map((st) => <option key={st} value={st}>{repairFormChoiceLabel(st)}</option>)}
                </select>
              </label>
            );
          })()}
          {t.status === "COMPLETED" && !t.archived && <button className="btn ghost mini" onClick={() => saveTicket({ ...t, archived: true })}>Archive</button>}
          <span style={{ marginLeft: "auto" }} className="btnrow" data-repair-qr-wrap="ticket">
            <RepairQrImg ticketNo={t.ticketNo} px={88} />
            <button className="btn ghost mini" onClick={() => printRepairDoc("wo", t, businessProfile, terms)}>Work order</button>
            <button className="btn ghost mini" onClick={() => printRepairDoc("receipt", t, businessProfile, terms)}>Receipt</button>
            <button className="btn ghost mini" onClick={() => printRepairDoc("label", t, businessProfile, terms)}>Label</button>
            {c && onPick && <button className="btn ghost mini" onClick={() => onPick(c.id)}>Profile</button>}
          </span>
        </div>

        {t.status !== "INTAKE" && t.status !== "COMPLETED" && (
          <div className="btnrow" style={{ marginTop: 9, flexWrap: "wrap", alignItems: "center" }}>
            <select value={t.techId || ""} style={{ width: "auto" }} title="Assigned technician"
              onChange={(e) => saveTicket({ ...t, techId: e.target.value,
                stage: t.stage === REPAIR_QUEUE_STAGE && e.target.value ? "Assigned to technician" : t.stage,
                shopNotes: e.target.value ? [...(t.shopNotes || []), { id: cid(), note: `Assigned to ${(staff.find((x) => x.id === e.target.value) || {}).name || "technician"}`, by: meName, side: "office", at: new Date().toISOString() }] : (t.shopNotes || []) })}>
              <option value="">— assign a technician —</option>
              {staff.filter((s2) => s2.active !== false).map((s2) => <option key={s2.id} value={s2.id}>{s2.name}</option>)}
            </select>
            {t.deductible && t.deductible.applies && (() => {
              const d = t.deductible;
              const owed = deductibleOutstanding(t);
              return (
                <div className="notice" style={{ width: "100%", marginBottom: 6,
                  borderColor: owed > 0 ? "#E8B4B4" : "#9CD3A8", background: owed > 0 ? "#FDF2F2" : "#F0FAF2" }}>
                  <b>${num(d.amount).toFixed(2)} warranty deductible — {d.status === "paid" ? `PAID${d.paidAt ? " " + String(d.paidAt).slice(0, 10) : ""}${d.by ? " · " + d.by : ""}` : d.status === "waived" && owed === 0 ? `WAIVED${d.waivedBy ? " by " + d.waivedBy : ""}` : "OUTSTANDING"}</b>
                  {d.waivedAmount && owed > 0 && <span> — ${num(d.waivedAmount).toFixed(2)} waived by {d.waivedBy}, ${owed.toFixed(2)} still owed</span>}
                  {owed > 0 && (
                    <div className="btnrow" style={{ marginTop: 6, flexWrap: "wrap" }}>
                      {onHoldSale && (
                        <button className="btn mini" onClick={() => {
                          onHoldSale({ id: cid(), at: new Date().toISOString(), mode: "sale", customerId: t.customerId || "", customerName: t.customerName || "", revCentre: "Repairs",
                            items: [{ code: "", desc: `Warranty deductible — ${t.ticketNo}`, qty: "1", price: owed.toFixed(2), disc: "", discType: "$" }] });
                          saveTicket({ ...t, shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Deductible $${owed.toFixed(2)} sent to the till as a held sale`, by: meName, side: "office", at: new Date().toISOString() }] });
                        }}>Send to till (+HST there)</button>
                      )}
                      <button className="btn ghost mini" onClick={() => {
                        const how = prompt("Mark deductible PAID — how was it taken? (till / card / cash)") || "";
                        if (!how.trim()) return;
                        saveTicket({ ...t, deductible: { ...d, status: "paid", paidAt: new Date().toISOString(), method: how.trim(), by: meName },
                          shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Deductible marked PAID (${how.trim()})`, by: meName, side: "office", at: new Date().toISOString() }] });
                      }}>Mark paid</button>
                      {isMgr && (
                        <button className="btn ghost mini" onClick={() => {
                          const amt = prompt(`Waive how much of the $${owed.toFixed(2)} deductible? (full or partial)`) || "";
                          if (!(num(amt) > 0)) return;
                          const why = prompt("Reason for the waive (required — lands on the timeline)") || "";
                          if (!why.trim()) return;
                          const newWaived = Math.min(num(d.amount), num(d.waivedAmount || 0) + num(amt));
                          saveTicket({ ...t, deductible: { ...d, waivedAmount: String(newWaived), waivedReason: why.trim(), waivedBy: meName, waivedAt: new Date().toISOString(), status: newWaived >= num(d.amount) ? "waived" : d.status },
                            shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Deductible waived $${num(amt).toFixed(2)} by ${meName} — ${why.trim()}`, by: meName, side: "office", at: new Date().toISOString() }] });
                        }}>Waive…</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            <label className="sub" style={{ marginTop: 0 }}>ETA
              <input type="date" value={t.etaDate || ""} style={{ width: "auto", marginLeft: 6 }} title="Estimated completion date — shows on the queue card and the customer page"
                onChange={(e) => saveTicket({ ...t, etaDate: e.target.value, shopNotes: [...(t.shopNotes || []), { id: cid(), note: `ETA set to ${e.target.value || "—"}`, by: meName, side: "office", at: new Date().toISOString() }] })} />
            </label>
            <select value={t.priority || "normal"} style={{ width: "auto" }} title="Priority"
              onChange={(e) => saveTicket({ ...t, priority: e.target.value })}>
              <option value="rush">🔥 Rush</option><option value="normal">Normal</option><option value="low">Low priority</option>
            </select>
            {t.status !== "HOLDING_BIN" && (
              <button className="btn ghost mini" onClick={() => { setHoldWhy(HOLD_REASONS[1]); setHoldPop(t.id); }}>⏸ Put on hold…</button>
            )}
            {t.status === "HOLDING_BIN" && (
              <button className="btn mini" onClick={() => saveTicket(releaseRepair(t, meName))}>▶ Release hold{t.resumeStatus ? ` — back to ${REPAIR_STATUS_LABEL[t.resumeStatus].split(" — ")[0].toLowerCase()}` : ""}</button>
            )}
            <button className="btn warn mini" style={{ marginLeft: "auto" }} onClick={() => closeAsDisposal(t)}>🗑 Not going ahead — disposal approved, close</button>
          </div>
        )}

        {t.status !== "COMPLETED" && t.stage === "Ready for pickup" && (
          <div className="notice" style={{ marginTop: 8, borderLeft: "4px solid #12A650" }}>
            <b>Unit is ready</b> — tell the customer, then fill in the Repair Pickup/Release form when it leaves the shop.
            <div className="btnrow" style={{ marginTop: 8 }}>
              <button className="btn mini" onClick={() => {
                const cg = repairClosingGate(t);
                if (!cg.ok) { alert(cg.reason); openClosingFor(t); return; }
                openPickupFor(t);
              }}>Pickup / release form</button>
            </div>
          </div>
        )}

        {t.status === "HOLDING_BIN" && t.hold && t.hold.reason !== "Awaiting proof of purchase" && (
          <div className="notice" style={{ marginTop: 9, borderLeft: "4px solid #DC1515" }}>
            <b>On hold — {t.hold.reason}</b> · since {localDay(t.hold.at)}{t.hold.by ? ` · placed by ${t.hold.by}` : ""}
          </div>
        )}

        {t.status === "HOLDING_BIN" && (!t.hold || t.hold.reason === "Awaiting proof of purchase") && (
          <div className="notice" style={{ marginTop: 9, borderLeft: "4px solid #DC1515" }}>
            Waiting on proof of purchase. Upload link for the customer: <span className="mono" style={{ fontSize: 11.5, overflowWrap: "anywhere" }}>{uploadUrl(t)}</span>
            <div className="btnrow" style={{ marginTop: 7 }}>
              {contact.email && onCompose && <button className="btn mini" onClick={() => { onCompose({ kind: "mail", client: c, to: contact.email, emailCat: "repairs", subject: `Pool Master repair ${t.ticketNo} — proof of purchase needed`, body: `Hi ${String(t.customerName || "").trim().split(/\s+/)[0] || "there"},\n\nYour warranty repair (${t.ticketNo}) is waiting on your proof of purchase. Upload a photo of your invoice here and the repair starts right away:\n${uploadUrl(t)}\n\nThank you!\nPool Master Canada` }); logComm({ ...t, reminderLastAt: new Date().toISOString() }, "EMAIL", `Proof-of-purchase reminder emailed (${t.ticketNo})`); }}>Email reminder</button>}
              {contact.phone && onCompose && <button className="btn mini" onClick={() => { onCompose({ kind: "text", client: c, to: contact.phone, body: `Pool Master: your warranty repair ${t.ticketNo} needs your proof of purchase. Upload it here: ${uploadUrl(t)}` }); logComm({ ...t, reminderLastAt: new Date().toISOString() }, "SMS", `Proof-of-purchase reminder texted (${t.ticketNo})`); }}>Text reminder</button>}
              <button className="btn ghost mini" onClick={() => saveTicket(withRepairStatus({ ...t, invoiceProof: t.invoiceProof || { manual: true, at: new Date().toISOString() }, warranty: "yes-invoice", hold: null }, "REPAIRS_QUEUE", meName))}>Proof received — to repairs queue</button>
            </div>
          </div>
        )}

        <div className="sechead">Labour hours <span className="ctag">{totalHours.toFixed(1)} h total</span></div>
        {(t.labour || []).map((l) => (
          <div key={l.id} className="sub" style={{ marginTop: 2 }}>{localDay(l.at)} · <b>{l.techName}</b> — {num(l.hours).toFixed(1)}h{l.notes ? ` · ${l.notes}` : ""}</div>
        ))}
        <div className="btnrow" style={{ marginTop: 6 }}>
          <input inputMode="decimal" placeholder="hours" value={lbHours} style={{ width: 80 }} onChange={(e) => setLbHours(e.target.value)} />
          <input placeholder="what was done" value={lbNote} style={{ flex: 1, minWidth: 140 }} onChange={(e) => setLbNote(e.target.value)} />
          <button className="btn mini" disabled={!num(lbHours)} onClick={() => { saveTicket({ ...t, labour: [...(t.labour || []), { id: cid(), techName: meName, hours: num(lbHours), notes: lbNote.trim(), at: new Date().toISOString() }] }); setLbHours(""); setLbNote(""); }}>Log time</button>
        </div>

        <div className="sechead">Quote <span className="ctag">{q ? q.status : "none"}</span></div>
        {q && (q.items || []).map((it, i) => (
          <div key={i} className="sub" style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ flex: 1 }}>{it.desc}
              {it.covered && !it.override && <span className="doc" style={{ background: "#12A650", marginLeft: 6 }}>Covered — repair warranty</span>}
              {it.override && <span className="doc" style={{ background: "#B36B00", marginLeft: 6 }} title={`${it.override.by}: ${it.override.reason}`}>warranty override — charged</span>}
            </span>
            {it.covered && !it.override && q.status === "Draft" && (isMgr
              ? <button className="btn ghost mini" onClick={() => overrideCharge(it.desc, (amt, why) =>
                  saveTicket({ ...t, quote: { ...q, items: q.items.map((x, j) => (j === i ? { ...x, amount: amt, override: { by: meName, reason: why, at: new Date().toISOString() } } : x)) },
                    shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Warranty charge override — ${it.desc} $${amt} by ${meName} — ${why}`, by: meName, side: "office", at: new Date().toISOString() }] }))}>Charge anyway…</button>
              : <span className="sub" style={{ marginTop: 0 }}>mgr only</span>)}
            <span className="mono">{posMoney(it.amount)}</span>
            {q.status === "Draft" && <button type="button" className="btn ghost mini" style={{ padding: "1px 7px" }} title="Remove line" onClick={() => saveTicket({ ...t, quote: { ...q, items: q.items.filter((_, j) => j !== i) } })}>×</button>}
          </div>
        ))}
        {q && <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, borderTop: "1px solid var(--line)", marginTop: 4, paddingTop: 4 }}><span>Total</span><span className="mono">{posMoney((q.items || []).reduce((a, it) => a + num(it.amount), 0))}</span></div>}
        <div className="btnrow" style={{ marginTop: 6, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 150, position: "relative" }}>
            <input placeholder="line item — type to search parts, or free text + $" value={qDesc} style={{ width: "100%" }} onChange={(e) => setQDesc(e.target.value)} />
            {qDesc.trim() && (() => {
              const q2 = qDesc.trim().toLowerCase();
              const hits = parts.filter((p2) => p2.active !== false && p2.code && [p2.code, p2.name].join(" ").toLowerCase().includes(q2)).slice(0, 5);
              return hits.length ? (
                <div className="poslookup" style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 5 }}>
                  {hits.map((p2) => (
                    <button key={p2.id} type="button" className="poslookupitem" onClick={() => {
                      const cov = rwCov.get(String(p2.code || "").trim().toLowerCase());
                      saveTicket({ ...t, quote: { status: "Draft", ...(q || {}), items: [...((q && q.items) || []), { desc: `${p2.name || p2.code}${p2.code ? ` (${p2.code})` : ""}`,
                        amount: cov ? "0.00" : num(p2.price).toFixed(2), ...(cov ? { covered: true } : {}) }] } });
                      setQDesc(""); setQAmt("");
                    }}>
                      <b>{p2.name}</b>
                      <span className="sub" style={{ marginTop: 0 }}>{p2.code} · {posMoney(p2.price)} — add to quote at this price</span>
                    </button>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
          <input inputMode="decimal" placeholder="$" value={qAmt} style={{ width: 90 }} onChange={(e) => setQAmt(e.target.value)} />
          <button className="btn ghost mini" disabled={!qDesc.trim() || !num(qAmt)} onClick={() => { saveTicket({ ...t, quote: { status: "Draft", ...(q || {}), items: [...((q && q.items) || []), { desc: qDesc.trim(), amount: num(qAmt).toFixed(2) }] } }); setQDesc(""); setQAmt(""); }}>Add line</button>
          {q && q.status === "Draft" && contact.email && onCompose && (
            <button className="btn mini" onClick={() => {
              const total = (q.items || []).reduce((a, it) => a + num(it.amount), 0);
              onCompose({ kind: "mail", to: contact.email, subject: `Pool Master — repair estimate ${t.ticketNo}`, body: `Hi ${String(t.customerName || "").trim().split(/\s+/)[0] || "there"},\n\nEstimate for your ${[t.category, t.subCategory].filter(Boolean).join(" ")} repair (${t.ticketNo}):\n\n${(q.items || []).map((it) => `  ${it.desc} — ${posMoney(it.amount)}`).join("\n")}\n\nTotal: ${posMoney(total)} + HST\n\nReply YES to approve and we'll get started. Thank you!` });
              const approvalI = BENCH_STAGES.indexOf("Customer approval received"); const si = BENCH_STAGES.indexOf(t.stage || BENCH_STAGES[0]); saveTicket({ ...t, quote: { ...q, status: "Sent", sentAt: new Date().toISOString(), sentBy: meName }, stage: (si < 0 || si < approvalI) ? "Waiting on customer" : t.stage });
            }}>Send quote</button>
          )}
          {q && (q.status === "Draft" || q.status === "Sent") && (<>
            <button className="btn mini" onClick={() => {
              const total = (q.items || []).reduce((a, it) => a + num(it.amount), 0);
              // Approval confirms the job: quote locks in, the bench stage jumps to
              // "Customer approval received", and an approval hold lets go.
              let next = { ...t,
                quote: { ...q, status: "Approved", decidedAt: new Date().toISOString() },
                stage: BENCH_STAGES.indexOf(t.stage || BENCH_STAGES[0]) < BENCH_STAGES.indexOf("Customer approval received") ? "Customer approval received" : t.stage,
                comms: [...(t.comms || []), { id: cid(), type: "QUOTE", note: `Quote approved by customer — ${posMoney(total)} (${t.ticketNo})`, at: new Date().toISOString(), by: meName }] };
              if (next.status === "HOLDING_BIN" && next.hold && /approval/i.test(next.hold.reason)) next = releaseRepair(next, meName);
              saveTicket(next);
            }}>Approved — confirmed, go ahead</button>
            <button className="btn warn mini" onClick={() => saveTicket({ ...t, quote: { ...q, status: "Declined", decidedAt: new Date().toISOString() }, stage: "Declined" })}>Declined</button>
          </>)}
          {q && q.status === "Declined" && !t.disposal && t.status !== "COMPLETED" && (
            <button className="btn warn mini" onClick={() => closeAsDisposal(t)}>Customer confirms disposal — close ticket</button>
          )}
        </div>

        <div className="sechead">Parts used <span className="ctag">{posMoney((t.partsUsed || []).reduce((a, p2) => a + num(p2.price) * num(p2.qty), 0))} + {totalHours.toFixed(1)}h labour</span></div>
        {(t.partsUsed || []).map((p2, i) => (
          <div key={i} className="sub" style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
            <span>{p2.qty} × {p2.name || p2.code}
              {p2.warrantyCovered && !p2.warrantyOverride && <span className="doc" style={{ background: "#12A650", marginLeft: 6 }}>Covered — repair warranty{p2.coveredBy ? ` (${p2.coveredBy})` : ""}</span>}
              {p2.warrantyOverride && <span className="doc" style={{ background: "#B36B00", marginLeft: 6 }} title={`${p2.warrantyOverride.by}: ${p2.warrantyOverride.reason}`}>warranty override — charged</span>}
            </span>
            <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              <span className="mono">{posMoney(num(p2.price) * num(p2.qty))}</span>
              {p2.warrantyCovered && !p2.warrantyOverride && (isMgr
                ? <button className="btn ghost mini" onClick={() => overrideCharge(p2.name || p2.code, (amt, why) =>
                    saveTicket({ ...t, partsUsed: (t.partsUsed || []).map((x, j) => (j === i ? { ...x, price: amt, warrantyOverride: { by: meName, reason: why, at: new Date().toISOString() } } : x)),
                      shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Warranty charge override — ${p2.name || p2.code} $${amt} by ${meName} — ${why}`, by: meName, side: "office", at: new Date().toISOString() }] }))}>Charge anyway…</button>
                : <span className="sub" style={{ marginTop: 0 }} title="Covered by our repair warranty — ask a manager to override">mgr only</span>)}
              <button className="btn warn mini" title="Remove and put the stock back" onClick={() => {
                onUseParts && onUseParts([{ code: p2.code, qty: p2.qty }], +1);
                saveTicket({ ...t, partsUsed: (t.partsUsed || []).filter((_, j) => j !== i) });
              }}>✕</button>
            </span>
          </div>
        ))}
        <div style={{ position: "relative", marginTop: 6 }}>
          <input placeholder="Search the catalogue — part used on this repair" value={puQ} onChange={(e) => setPuQ(e.target.value)} />
          {puQ.trim() && (
            <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 200 }}>
              {parts.filter((p2) => p2.active !== false && p2.code && [p2.code, p2.name].join(" ").toLowerCase().includes(puQ.trim().toLowerCase())).slice(0, 6).map((p2) => (
                <button key={p2.id} type="button" className="poslookupitem" onClick={() => {
                  const cov = rwCov.get(String(p2.code || "").trim().toLowerCase());
                  onUseParts && onUseParts([{ code: p2.code, qty: 1 }], -1);
                  saveTicket({ ...t, partsUsed: [...(t.partsUsed || []), { code: p2.code, name: p2.name || "", qty: 1, cost: p2.cost || "",
                    price: cov ? "0.00" : (p2.price || ""), at: new Date().toISOString(), by: meName,
                    ...(cov ? { warrantyCovered: true, coveredBy: cov.sourceTicketNo } : {}) }] });
                  setPuQ("");
                }}>
                  <b>{p2.name}</b>
                  <span className="sub" style={{ marginTop: 0 }}>{p2.code} · {posMoney(p2.price)} · comes off shop stock</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <details className="rfold">
          <summary>Repair timeline <span className="ctag">{repairTimeline(t).length}</span></summary>
        <div className="sub" style={{ marginBottom: 4 }}>What the customer told the FRONT goes on the office side; what they told the TECH goes on the bench side. Dated and named — no he-said/she-said.</div>
        <div className="seg" style={{ marginBottom: 6 }}>
          {[["all", "All"], ["office", "Office"], ["tech", "Bench"]].map(([v, l]) => (
            <button key={v} type="button" data-on={snSide === v ? 1 : 0} onClick={() => setSnSide(v)}>{l}</button>
          ))}
        </div>
        {repairTimeline(t).filter((r) => snSide === "all" || r.side === snSide).map((r, i) => (
          <div key={i} className="sub" style={{ marginTop: 3, display: "flex", gap: 6, alignItems: "baseline" }}>
            <span className="doc" style={{ background: r.side === "tech" ? "#0E7A3C" : "#0B4F8A", flex: "none" }}>{repairTimelineBadge(r)}</span>
            <span>{fmtStamp(r.at)} · <b>{r.by}</b> — {r.text}</span>
          </div>
        ))}
        </details>
        <div className="btnrow" style={{ marginTop: 8, flexWrap: "wrap" }}>
          {["Inspected", "Diagnosed", "Repaired", "Replaced", "Tested", "Still needed"].map((k) => (
            <button key={k} type="button" className="btn ghost mini"
              style={snKind === k ? { background: "#0B6FBE", color: "#fff" } : {}}
              onClick={() => setSnKind(snKind === k ? "" : k)}>{k}</button>
          ))}
        </div>
        {(() => {
          // Writing while the Office or Bench tab is selected files the note to THAT side;
          // on All, it defaults to the writer's side of the counter.
          const noteSide = snSide === "all" ? mySide : snSide;
          return (
            <div className="btnrow" style={{ marginTop: 6 }}>
              <input placeholder={noteSide === "tech" ? "bench note — never shown to the customer" : "office note — what the customer said at the counter/phone"} value={snNote} style={{ flex: 1, minWidth: 150 }} onChange={(e) => setSnNote(e.target.value)} />
              <button className="btn ghost mini" disabled={!snNote.trim() && !snKind} onClick={() => {
            const note = snKind ? `${snKind}${snNote.trim() ? ": " + snNote.trim() : ""}` : snNote.trim();
            saveTicket({ ...t, shopNotes: [...(t.shopNotes || []), { id: cid(), note, kind: snKind ? snKind.toLowerCase() : undefined, by: meName, side: mySide, at: new Date().toISOString() }] });
            setSnNote(""); setSnKind("");
          }}>Add update</button>
            </div>
          );
        })()}
        <div className="btnrow" style={{ marginTop: 6 }}>
          <input placeholder="phone call / conversation note" value={cmNote} style={{ flex: 1, minWidth: 150 }} onChange={(e) => setCmNote(e.target.value)} />
          <button className="btn ghost mini" disabled={!cmNote.trim()} onClick={() => { logComm(t, "PHONE", cmNote.trim()); setCmNote(""); }}>Log call</button>
          {(t.status === "COMPLETED" || t.stage === "Ready for pickup") && !t.disposal && contact.phone && onCompose && (
            <button className="btn mini" onClick={() => { onCompose({ kind: "text", client: c, to: contact.phone, body: `Pool Master: good news — your ${[t.category, t.subCategory].filter(Boolean).join(" ").toLowerCase() || "unit"} (${t.ticketNo}) is repaired and ready for pickup!` }); logComm(t, "SMS", "Ready-for-pickup text sent"); }}>Notify: ready for pickup</button>
          )}
        </div>

        <details className="rfold">
          <summary>Status history <span className="ctag">{(t.statusHistory || []).length}</span></summary>
        {(t.statusHistory || []).map((h) => (
          <div key={h.id} className="sub" style={{ marginTop: 2, display: "flex", gap: 6, alignItems: "baseline" }}>
            <span className="doc" style={{ background: "#0E7A3C", flex: "none" }}>BENCH</span>
            <span>{fmtStamp(h.at)} · {h.from ? `${REPAIR_STATUS_LABEL[h.from].split(" — ")[0]} → ` : ""}<b>{REPAIR_STATUS_LABEL[h.to].split(" — ")[0]}</b>{h.by ? ` · ${h.by}` : ""}</span>
          </div>
        ))}
        </details>
      </div>
    );
  };

  /* ---- render ---- */
  const kpi = repairKpis(repairs, today);
  return (
    <div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div><strong style={{ fontSize: 16 }}>In-House Repair</strong><div className="sub">Workshop intake, bench queues, and repair tracking</div></div>
        <button className="btn" data-cobrowse="repairs.dropoff" onClick={() => { setInk(blankIntake()); setTab("intake"); }}><WrenchIcon size={15} /> New drop-off</button>
      </div>
      {tab === "start" ? (<>
        {(() => {
          const q2 = wsq.trim().toLowerCase();
          const hits = q2 ? repairs.filter((t) => !t.archived && ((t.ticketNo || "").toLowerCase().includes(q2) || (t.customerName || "").toLowerCase().includes(q2))).slice(0, 6) : [];
          return (
            <div style={{ maxWidth: 560, margin: "0 auto 10px" }}>
              <input placeholder="Find a repair — ticket # or customer name" value={wsq} onChange={(e) => setWsq(e.target.value)} />
              {hits.map((t) => (
                <button key={t.id} type="button" className="poslookupitem" style={{ width: "100%" }}
                  onClick={() => { setWsq(""); setTab(t.status === "COMPLETED" ? "done" : t.status === "HOLDING_BIN" ? "holding" : t.status === "WAITING_FOR_PARTS" ? "parts" : t.status === "INTAKE" ? "forms" : "queue"); setOpenId(t.id); }}>
                  <b>{t.ticketNo}</b> · {t.customerName} <span className="sub" style={{ marginTop: 0 }}>{t.stage || REPAIR_STATUS_LABEL[t.status]}</span>
                </button>
              ))}
            </div>
          );
        })()}
        {(() => {
          const wait = repairsAwaitingContact(repairs);
          if (!wait.length) return null;
          return (
            <div className="panel" style={{ padding: 12, marginBottom: 6, borderLeft: "4px solid #D9822B" }}>
              <b style={{ fontSize: 13.5 }}>📞 Completed — tell the customer ({wait.length})</b>
              <div className="sub" style={{ marginTop: 2 }}>The repair's done; the pickup call is the last step. Every tap below is logged with your name and the date.</div>
              {wait.map((t) => {
                const log = t.contactLog || [];
                const last = log[log.length - 1];
                const logIt = (how) => saveTicket({ ...t, contactLog: [...log, { id: cid(), at: new Date().toISOString(), by: me ? me.name : "", how }] });
                return (
                  <div key={t.id} className="visit" style={{ marginTop: 8 }}>
                    <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <b>{t.ticketNo}</b>
                      <span style={{ flex: 1, minWidth: 160 }}>{t.customerName}
                        {t.phone ? <> · <a className="link" href={"tel:" + String(t.phone).replace(/[^0-9+]/g, "")}>{t.phone}</a></> : null}</span>
                      <span className="doc" style={{ background: !last ? "#DC1515" : last.how === "voicemail" ? "#B36B00" : "#5A6E86" }}>
                        {!last ? "NOT CONTACTED YET"
                          : last.how === "voicemail" ? `Voicemail ${localDay(last.at).slice(5)} · ${last.by}`
                          : `No answer ${localDay(last.at).slice(5)} · ${last.by}`}
                      </span>
                      <button className="btn mini" onClick={() => logIt("spoken")}>Spoke to them ✓</button>
                      <button className="btn ghost mini" onClick={() => logIt("voicemail")}>Left voicemail</button>
                      <button className="btn ghost mini" onClick={() => logIt("noanswer")}>No answer</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
        <div style={{ marginTop: 10 }}>
          <input placeholder="Find a drop-off form — ticket number or customer name" value={tq} onChange={(e) => setTq(e.target.value)} />
          {tq.trim() && (() => {
            const q2 = tq.trim().toLowerCase();
            const hits = live.filter((t) => `${t.ticketNo} ${t.customerName}`.toLowerCase().includes(q2))
              .sort((a, b) => (b.at || "").localeCompare(a.at || "")).slice(0, 6);
            return hits.length
              ? <div style={{ marginTop: 8 }}>{hits.map(row)}</div>
              : <div className="empty" style={{ marginTop: 8 }}>No ticket matches "{tq.trim()}".</div>;
          })()}
        </div>
        <div className="posstart" style={{ marginTop: 10, maxWidth: "none", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
          {[["forms", "Drop-off Forms", `${byStatus("INTAKE").length} to confirm with the customer`, byStatus("INTAKE").length > 0],
            ["queue", "Repairs Queue", `${byStatus("REPAIRS_QUEUE").length} on the bench`, false],
            ["quotes", "Quotes", "estimates sent & pending", false],
            ["parts", "Waiting on Parts", `${byStatus("WAITING_FOR_PARTS").length} unit${byStatus("WAITING_FOR_PARTS").length === 1 ? "" : "s"} held up`, false],
            ["holding", "Holding Bin", `${byStatus("HOLDING_BIN").length} on hold — approvals, proof, parts, seasonal`, false]].map(([v, l, sub2, hot]) => (
            <button key={v} type="button" className="posstart-btn" style={{ borderColor: hot ? "var(--sun)" : undefined }} onClick={() => setTab(v)}>
              <span className="posstart-lbl">{l}</span>
              <span className="posstart-sub">{sub2}</span>
            </button>
          ))}
        </div>
        <div className="posstart" style={{ margin: "18px auto 0", maxWidth: 620 }}>
          {[["done", "Completed", `${byStatus("COMPLETED").length} finished`],
            ["rentals", "Cleaner Rentals", (() => { const t = fmtDate(new Date()); const out = rentalUnits.filter((u) => ["rented", "overdue"].includes(rentalLiveStatus(u, t))).length; const od = rentalUnits.filter((u) => rentalLiveStatus(u, t) === "overdue").length; return od ? `${out} out · ${od} OVERDUE` : out ? `${out} of ${rentalUnits.length} out` : `${rentalUnits.length} in the fleet`; })()],
            ["dash", "Dashboard", "today's numbers & turnaround"]].map(([v, l, sub2]) => (
            <button key={v} type="button" className="posstart-btn" onClick={() => setTab(v)}>
              <span className="posstart-lbl">{l}</span>
              <span className="posstart-sub">{sub2}</span>
            </button>
          ))}
        </div>
      </>) : (
        <div className="btnrow" style={{ marginBottom: 12 }}>
          <button className="btn ghost mini" onClick={() => setTab("start")}>‹ All repair sections</button>
          <b style={{ fontSize: 14 }}>{{ intake: "New drop-off", forms: "Drop-off Forms", queue: "Repairs Queue", parts: "Waiting on Parts", holding: "Holding Bin", done: "Completed", quotes: "Quotes", dash: "Dashboard", rentals: "Cleaner Rentals" }[tab]}</b>
        </div>
      )}

      {tab === "intake" && (
        <div className="panel" style={{ padding: 14, maxWidth: 640 }}>
          {ink.step === "done" && ink.doneTicket ? (
            <div className="punchdone" data-dir="in" style={{ maxWidth: 480, margin: "0 auto" }}>
              <div className="big">{ink.doneTicket.ticketNo} saved</div>
              <div className="who4">{ink.doneTicket.customerName} · waiting in Drop-off Forms — confirm with the customer to proceed</div>
              <div data-repair-qr-wrap="dropoff" style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 12 }}>
                <RepairQrImg ticketNo={ink.doneTicket.ticketNo} px={140} />
                <div className="sub" style={{ marginTop: 6 }}>Scan to open this ticket on a shop phone — not a Google search</div>
              </div>
              <div className="btnrow" style={{ justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
                <button className="btn" onClick={() => printRepairDoc("label", ink.doneTicket, businessProfile, terms)}>Print label</button>
                <button className="btn ghost" onClick={() => printRepairDoc("wo", ink.doneTicket, businessProfile, terms)}>Work order</button>
                <button className="btn ghost" onClick={() => printRepairDoc("receipt", ink.doneTicket, businessProfile, terms)}>Receipt</button>
                <button className="btn ghost" onClick={() => { setInk(blankIntake()); }}>Another drop-off</button>
                <button className="btn ghost" onClick={() => setTab("forms")}>Open Drop-off Forms</button>
              </div>
            </div>
          ) : (<>
            <div className="btnrow" style={{ marginBottom: 10 }}>
              <button className="btn ghost mini" onClick={startScan}>📷 Scan repair label (QR)</button>
              <span className="sub" style={{ marginTop: 0 }}>Returning unit? Scan the old sticker to pull everything up.</span>
            </div>

            <label>What are you dropping off today?</label>
            <div className="seg" style={{ marginBottom: 8, flexWrap: "wrap" }}>
              {Object.keys(REPAIR_CATS).map((c2) => (
                <button key={c2} type="button" data-on={ink.cat === c2 ? 1 : 0} onClick={() => setInk((s) => ({ ...s, cat: c2, sub: "" }))}>{c2}</button>
              ))}
            </div>
            {ink.cat === "Pool Cleaners" && (
              <div className="seg" style={{ marginBottom: 8, flexWrap: "wrap" }}>
                {REPAIR_CATS["Pool Cleaners"].map((s2) => (
                  <button key={s2} type="button" data-on={ink.sub === s2 ? 1 : 0} onClick={() => set("sub", s2)}>{s2}</button>
                ))}
              </div>
            )}

            {ink.cat && (ink.cat !== "Pool Cleaners" || ink.sub) && (<>
              <label>Customer</label>
              {ink.customerId || ink.name ? (
                <div className="btnrow" style={{ marginBottom: 8 }}>
                  <span className="chip" data-ch="email"><b>{ink.name}</b>{ink.customerId ? "" : " · new — profile created on save"}</span>
                  <button className="btn ghost mini" onClick={() => setInk((s) => ({ ...s, customerId: "", name: "", address: "", phone: "", email: "" }))}>Change</button>
                </div>
              ) : (
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <input placeholder="Search a customer — or type a new name below" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} />
                  {custMatches.length > 0 && (
                    <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 6, maxHeight: 200 }}>
                      {custMatches.map((c2) => <button key={c2.id} type="button" className="poslookupitem" onClick={() => attachCustomer(c2)}><b>{c2.name}</b><span className="sub" style={{ marginTop: 0 }}>{c2.address || ""}</span></button>)}
                    </div>
                  )}
                </div>
              )}
              {ink.customerId && repairs.some((t) => t.customerId === ink.customerId) && (
                <div className="notice" style={{ marginBottom: 8 }}>
                  Previous repairs: {repairs.filter((t) => t.customerId === ink.customerId).slice(-3).map((t) => `${t.ticketNo} (${t.date}, ${REPAIR_STATUS_LABEL[t.status].split(" — ")[0]})`).join(" · ")}
                </div>
              )}
              <div className="fields">
                <div><label>Drop-off date</label><input type="date" value={ink.date || today} max={today} onChange={(e) => set("date", e.target.value)} />
                  {ink.date && ink.date !== today && <div className="sub" style={{ marginTop: 3, color: "var(--clay)", fontWeight: 650 }}>Back-dated — unit actually arrived {ink.date}</div>}
                </div>
                <div><label>Customer name</label><input value={ink.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div className="f-wide"><label>Address</label><input value={ink.address} onChange={(e) => set("address", e.target.value)} /></div>
                <div><label>Phone</label><input value={ink.phone} inputMode="tel" onChange={(e) => set("phone", e.target.value)} /></div>
                <div><label>Email</label><input value={ink.email} onChange={(e) => set("email", e.target.value)} />
                  {!emailOk && (
                    <label className="sub" style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, cursor: "pointer" }}>
                      <input type="checkbox" checked={ink.noEmail} onChange={(e) => set("noEmail", e.target.checked)} style={{ width: "auto" }} />
                      customer has no email / declined
                    </label>
                  )}
                </div>
                <div><label>Model number</label><input value={ink.model} onChange={(e) => set("model", e.target.value)} /></div>
                <div><label>Serial number</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={ink.serial} style={{ flex: 1 }} onChange={(e) => set("serial", e.target.value)} />
                    <button type="button" className="btn ghost mini" disabled={!String(ink.serial || "").trim()} onClick={() => {
                      const sn = String(ink.serial).trim().toLowerCase();
                      let hit = null;
                      for (const sale of (allSales || [])) {
                        for (const li of sale.items || []) {
                          if (serialList(li.serial).some((x) => x.toLowerCase() === sn)) { hit = { code: li.code, name: li.desc, date: sale.date, customerName: sale.customerName || "Walk-in", source: "till sale" }; break; }
                        }
                        if (hit) break;
                      }
                      if (!hit) for (const j of (allJobs || [])) {
                        for (const li of (((j.wf || {}).results || {}).chems || {}).items || []) {
                          if (serialList(li.serial).some((x) => x.toLowerCase() === sn)) { hit = { code: li.code, name: li.desc, date: (j.wf.completedAt || "").slice(0, 10), customerName: (clients.find((c) => c.id === j.clientId) || {}).name || "", source: "work order" }; break; }
                        }
                        if (hit) break;
                      }
                      set("wmatch", hit || { miss: true });
                      if (hit && hit.date) set("purchaseDate", hit.date);
                    }}>Look up warranty</button>
                  </div>
                </div>
                {(ink.wmatch || ink.purchaseDate) && (() => {
                  const matchCode = ink.wmatch && !ink.wmatch.miss ? ink.wmatch.code : ink.wproduct;
                  const wp = (parts || []).find((p) => partHasWarranty(p) && (p.code || "").trim().toLowerCase() === String(matchCode || "").trim().toLowerCase());
                  const today = fmtDate(new Date());
                  return (
                    <div className="f-wide">
                      {ink.wmatch && !ink.wmatch.miss && (
                        <div className="notice" style={{ borderColor: "#9CD3A8", background: "#F0FAF2", marginBottom: 6 }}>
                          <b>Found it:</b> {ink.wmatch.name || ink.wmatch.code} — sold to <b>{ink.wmatch.customerName}</b> on {ink.wmatch.date} ({ink.wmatch.source}).
                          {onWarrantyRef && <button type="button" className="link" style={{ marginLeft: 8 }} onClick={() => onWarrantyRef({ q: ink.serial, purchaseDate: ink.purchaseDate || ink.wmatch.date || "", source: "repair drop-off" })}>Open in Warranty Reference ↗</button>}
                        </div>
                      )}
                      {ink.wmatch && ink.wmatch.miss && (
                        <div className="sub" style={{ marginBottom: 6 }}>No sale found for that serial — normal for units sold before serial tracking. Pick the product and enter the purchase date; coverage and the deductible run from there.</div>
                      )}
                      <div className="fields">
                        {(!ink.wmatch || ink.wmatch.miss) && (
                          <div><label>Product (for coverage terms)</label>
                            <select value={ink.wproduct || ""} onChange={(e) => set("wproduct", e.target.value)}>
                              <option value="">— pick if known —</option>
                              {(parts || []).filter(partHasWarranty).map((p) => <option key={p.id} value={p.code}>{p.name || p.code}</option>)}
                            </select></div>
                        )}
                        <div><label>Purchase date</label><input type="date" value={ink.purchaseDate || ""} onChange={(e) => set("purchaseDate", e.target.value)} /></div>
                      </div>
                      {wp && ink.purchaseDate && (
                        <div style={{ marginTop: 6 }}>
                          {(wp.warranty.terms || []).map((tm) => {
                            const st = warrantyTermStatus(tm, ink.purchaseDate, today);
                            return (
                              <label key={tm.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                                <input type="radio" name="wterm" style={{ width: "auto" }} checked={ink.wterm === tm.id} onChange={() => set("wterm", tm.id)} />
                                <span style={{ flex: 1 }}>{st.scope || "Coverage"}</span>
                                <span className="doc" style={{ background: st.covered ? "#0E7A3C" : "#DC1515" }}>{st.covered ? `covered to ${st.end}` : `expired ${st.end}`}</span>
                              </label>
                            );
                          })}
                          {(() => {
                            const d = deductibleFor(wp.warranty, ink.purchaseDate, today);
                            return d.applies
                              ? <div className="sub" style={{ color: "#B36B00", fontWeight: 700 }}>Deductible applies: ${d.amount} + HST (free period ended {d.dueFrom}) — collected before pickup.</div>
                              : d.freeUntil ? <div className="sub" style={{ color: "#0E7A3C" }}>Inside the free period{d.freeUntil ? ` (through ${d.freeUntil})` : ""} — no deductible.</div> : null;
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div><label>Colour</label><input value={ink.color} onChange={(e) => set("color", e.target.value)} /></div>
                <div className="f-wide"><label>Reported problem</label><textarea value={ink.problem} style={{ minHeight: 70 }} onChange={(e) => set("problem", e.target.value)} /></div>
              </div>

              <label style={{ marginTop: 10 }}>Primary photo of unit <span style={{ color: "var(--clay)" }}>*</span></label>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {primaryPhoto ? <img src={primaryPhoto.dataUrl} alt="unit" style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 9, border: "2px solid #12A650" }} /> : <div style={{ width: 84, height: 84, borderRadius: 9, border: "2px dashed var(--clay)", display: "grid", placeItems: "center", color: "var(--clay)", fontSize: 11, fontWeight: 700, textAlign: "center" }}>required</div>}
                <label className="btn ghost mini" style={{ cursor: "pointer" }}>📷 {primaryPhoto ? "Replace" : "Take / upload"}
                  <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => { addIntakePhoto(e.target.files && e.target.files[0], true); e.target.value = ""; }} />
                </label>
                <label className="btn ghost mini" style={{ cursor: "pointer" }}>+ More photos
                  <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { Array.from(e.target.files || []).slice(0, 5).forEach((f) => addIntakePhoto(f, false)); e.target.value = ""; }} />
                </label>
                {ink.photos.filter((p) => !p.primary).map((p, i) => <img key={i} src={p.dataUrl} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 7, border: "1px solid var(--line)" }} />)}
              </div>

              <label style={{ marginTop: 14 }}>Warranty repair?</label>
              <div className="seg" style={{ marginBottom: 8 }}>
                <button type="button" data-on={ink.warranty === "yes" ? 1 : 0} onClick={() => set("warranty", "yes")}>Yes — warranty</button>
                <button type="button" data-on={ink.warranty === "no" ? 1 : 0} onClick={() => set("warranty", "no")}>No — customer pays</button>
              </div>
              {ink.warranty === "yes" && (<>
                <label>Do they have the invoice / proof of purchase?</label>
                <div className="seg" style={{ marginBottom: 8 }}>
                  <button type="button" data-on={ink.hasInvoice === true ? 1 : 0} onClick={() => set("hasInvoice", true)}>Yes — attaching it now</button>
                  <button type="button" data-on={ink.hasInvoice === false ? 1 : 0} onClick={() => set("hasInvoice", false)}>No — they'll send it</button>
                </div>
                {ink.hasInvoice === true && (
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                    {ink.invoicePhoto ? <img src={ink.invoicePhoto} alt="invoice" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid #12A650" }} /> : null}
                    <label className="btn ghost mini" style={{ cursor: "pointer" }}>📷 {ink.invoicePhoto ? "Replace invoice photo" : "Photograph the invoice"}
                      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={async (e) => { const f = e.target.files && e.target.files[0]; if (f) set("invoicePhoto", await shrink(f, 1100, 0.75)); e.target.value = ""; }} />
                    </label>
                  </div>
                )}
                {ink.hasInvoice === false && <div className="notice" style={{ marginBottom: 8 }}>No payment needed. The unit waits in the <b>holding bin</b> until their proof of purchase arrives — they'll get an upload link, with 48-hour reminders until it lands.</div>}
              </>)}
              {ink.warranty === "no" && <div className="notice" style={{ marginBottom: 8 }}>The form saves to <b>Drop-off Forms</b> — after you confirm the details with the customer, the <b>$80.00 diagnostic fee</b> is collected on the payment screen.</div>}

              <TermsPicker terms={terms} workType="repair"
                termIds={ink.termIds || []} note={ink.termsNote || ""}
                onChange={({ termIds, note }) => setInk((s) => ({ ...s, termIds, termsNote: note }))} />

              <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 10, padding: 13 }}
                disabled={!ink.name.trim() || !primaryPhoto || !ink.warranty || (!emailOk && !ink.noEmail) || (ink.warranty === "yes" && ink.hasInvoice === null) || (ink.warranty === "yes" && ink.hasInvoice === true && !ink.invoicePhoto)}
                onClick={() => finishIntake()}>
                Save drop-off form
              </button>
              {!primaryPhoto && <div className="sub" style={{ color: "var(--clay)", fontWeight: 650, marginTop: 6 }}>A primary photo of the unit is required.</div>}
              {!emailOk && !ink.noEmail && <div className="sub" style={{ color: "var(--clay)", fontWeight: 650, marginTop: 6 }}>Ask for their email — it's how we send updates and offers. No email? Tick the box beside the field.</div>}
            </>)}
          </>)}

          {scanPop && (
            <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") { setScanPop(false); stopScan(); } }}>
              <div className="sheet" style={{ maxWidth: 420 }}>
                <div className="bar"><strong style={{ fontSize: 16 }}>Scan a repair label</strong><button className="btn ghost" onClick={() => { setScanPop(false); stopScan(); }}>Close</button></div>
                <div className="pad">
                  <video ref={videoRef} playsInline muted style={{ width: "100%", borderRadius: 10, background: "#000", minHeight: 180 }} />
                  {scanMsg && <div className="sub" style={{ color: "var(--clay)", fontWeight: 650, marginTop: 8 }}>{scanMsg}</div>}
                  <label style={{ marginTop: 10 }}>Or type the repair ID</label>
                  <input placeholder="RP-3001" onKeyDown={(e) => { if (e.key === "Enter") applyScanText(e.target.value); }} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "forms" && (byStatus("INTAKE").length ? byStatus("INTAKE").map(row) : <div className="empty">No drop-off forms waiting — every unit has been confirmed in.</div>)}
      {tab === "queue" && (byStatus("REPAIRS_QUEUE").length ? byStatus("REPAIRS_QUEUE").map(row) : <div className="empty">Nothing on the bench. New drop-off starts an intake.</div>)}
      {tab === "parts" && (byStatus("WAITING_FOR_PARTS").length ? byStatus("WAITING_FOR_PARTS").map(row) : <div className="empty">No units waiting for parts.</div>)}
      {tab === "holding" && (byStatus("HOLDING_BIN").length ? byStatus("HOLDING_BIN").map(row) : <div className="empty">Holding bin is empty — no warranty units waiting on proof of purchase.</div>)}
      {tab === "done" && (<>
        {isMgr && onSaveProfile && (() => {
          const rw = (businessProfile || {}).repairWarranty || {};
          const setRw = (k, v) => onSaveProfile({ ...businessProfile, repairWarranty: { softubHydromate: 12, other: 12, ...rw, [k]: v } });
          return (
            <div className="panel" style={{ padding: 10, marginBottom: 10, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <b style={{ fontSize: 13 }}>Our repair warranty on replaced parts</b>
              <label className="sub" style={{ marginTop: 0 }}>Softub / Hydromate
                <input inputMode="numeric" value={rw.softubHydromate ?? 12} style={{ width: 54, marginLeft: 6, textAlign: "center" }} onChange={(e) => setRw("softubHydromate", e.target.value)} /> months
              </label>
              <label className="sub" style={{ marginTop: 0 }}>Everything else
                <input inputMode="numeric" value={rw.other ?? 12} style={{ width: 54, marginLeft: 6, textAlign: "center" }} onChange={(e) => setRw("other", e.target.value)} /> months
              </label>
              <span className="sub" style={{ marginTop: 0 }}>Labour on Softub/Hydromate repairs is covered {REPAIR_LABOUR_DAYS} days; other labour is billable.</span>
            </div>
          );
        })()}
        {byStatus("COMPLETED").length ? byStatus("COMPLETED").map(row) : <div className="empty">No completed repairs yet.</div>}
      </>)}
      {tab === "quotes" && (() => {
        const withQ = live.filter((t) => t.quote);
        return withQ.length ? withQ.map(row) : <div className="empty">No repair quotes yet — open a ticket and add estimate lines.</div>;
      })()}
      {holdPop && (() => {
        const t = repairs.find((x) => x.id === holdPop);
        if (!t) return null;
        return (
          <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setHoldPop(null); }}>
            <div className="sheet" style={{ maxWidth: 420 }}>
              <div className="bar">
                <div><strong style={{ fontSize: 16 }}>Put {t.ticketNo} on hold</strong><div className="sub">{t.customerName}</div></div>
                <button className="btn ghost" onClick={() => setHoldPop(null)}>Back</button>
              </div>
              <div className="pad">
                <label>Why is it held? (required)</label>
                <select value={holdWhy} onChange={(e) => setHoldWhy(e.target.value)} style={{ marginBottom: 12 }}>
                  {HOLD_REASONS.map((r2) => <option key={r2}>{r2}</option>)}
                </select>
                <button className="btn warn" style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => { saveTicket(holdRepair(t, holdWhy, meName)); setHoldPop(null); setTab("holding"); }}>
                  ⏸ Hold — {holdWhy}
                </button>
                <div className="sub" style={{ marginTop: 8 }}>The date, reason and who placed the hold show on the unit. Release puts it back where it was, stage intact.</div>
              </div>
            </div>
          </div>
        );
      })()}

      {pickupPop && pickupDraft && (() => {
        const t = repairs.find((x) => x.id === pickupPop);
        if (!t) return null;
        const pay = repairPickupPayCheck(t);
        const setPd = (patch) => setPickupDraft({ ...pickupDraft, ...patch });
        const blockedDeductible = pay.kind === "deductible";
        const needsPayConfirm = !pay.ok && pay.kind === "balance";
        const canRelease = pay.ok || (needsPayConfirm && pickupDraft.paymentConfirmed);
        return (
          <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") { setPickupPop(null); setPickupDraft(null); } }}>
            <div className="sheet" style={{ maxWidth: 520 }}>
              <div className="bar">
                <div><strong style={{ fontSize: 16 }}>Repair Pickup / Release — {t.ticketNo}</strong><div className="sub">Customer is collecting the unit. Confirm payment, then release.</div></div>
                <button className="btn ghost" onClick={() => { setPickupPop(null); setPickupDraft(null); }}>Back</button>
              </div>
              <div className="pad">
                <div className="notice" style={{ marginBottom: 12, borderLeft: `4px solid ${pay.ok ? "#12A650" : "#DC1515"}`, background: pay.ok ? "#F0FAF2" : "#FDF2F2" }}>
                  <b style={{ color: pay.ok ? "#0E7A3C" : "#B31212" }}>{pay.ok ? "Payment check" : "⚠ Payment check"}</b>
                  <div className="sub" style={{ marginTop: 4, fontWeight: 700, color: pay.ok ? undefined : "#B31212" }}>{pay.alert}</div>
                  <div className="sub" style={{ marginTop: 4 }}>Warranty: {pay.warranty ? "Yes — warranty repair" : "No — customer pays"} · Payment required: {pay.paymentRequired ? "yes" : "no"} · Collected: {pay.collected ? "yes" : "not yet"}</div>
                </div>
                <div className="fields">
                  <div><label>Customer</label><input value={pickupDraft.customerName} onChange={(e) => setPd({ customerName: e.target.value })} /></div>
                  <div><label>Unit being picked up</label><input value={pickupDraft.unit} onChange={(e) => setPd({ unit: e.target.value })} /></div>
                  <div><label>Repair / work-order #</label><input value={pickupDraft.ticketNo} readOnly /></div>
                  <div><label>Pickup date & time</label><input type="datetime-local" value={pickupDraft.atLocal} onChange={(e) => setPd({ atLocal: e.target.value })} /></div>
                  <div><label>Employee completing the pickup</label>
                    <select value={pickupDraft.by} onChange={(e) => setPd({ by: e.target.value })}>
                      {!(staff || []).some((s2) => s2.name === pickupDraft.by) && pickupDraft.by ? <option value={pickupDraft.by}>{pickupDraft.by}</option> : null}
                      {(staff || []).filter((s2) => s2.active !== false).map((s2) => <option key={s2.id} value={s2.name}>{s2.name}</option>)}
                    </select>
                  </div>
                  <div><label>Repair cost</label><input inputMode="decimal" value={pickupDraft.cost} onChange={(e) => setPd({ cost: e.target.value })} /></div>
                  <div><label>Warranty status</label><input value={pay.warranty ? "Warranty repair" : "Non-warranty — customer pays"} readOnly /></div>
                  <div><label>Payment status</label><input value={pay.collected ? (pay.paymentRequired ? "Collected" : "Not required") : "OUTSTANDING — collect before release"} readOnly /></div>
                </div>
                {blockedDeductible && t.deductible && (
                  <div className="btnrow" style={{ marginTop: 10, flexWrap: "wrap" }}>
                    <button className="btn mini" onClick={() => {
                      const how = prompt("Mark deductible PAID — how was it taken? (till / card / cash)") || "";
                      if (!how.trim()) return;
                      saveTicket({ ...t, deductible: { ...t.deductible, status: "paid", paidAt: new Date().toISOString(), method: how.trim(), by: meName },
                        shopNotes: [...(t.shopNotes || []), { id: cid(), note: `Deductible marked PAID (${how.trim()})`, by: meName, side: "office", at: new Date().toISOString() }] });
                    }}>Mark deductible paid</button>
                    <div className="sub" style={{ marginTop: 0 }}>Picked Up stays locked until the deductible is paid or waived.</div>
                  </div>
                )}
                {needsPayConfirm && (
                  <div className="notice" style={{ marginTop: 12, borderLeft: "4px solid #DC1515", background: "#FDF2F2" }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 8, textTransform: "none", letterSpacing: 0, fontSize: 13.5, fontWeight: 650 }}>
                      <input type="checkbox" checked={pickupDraft.paymentConfirmed} style={{ width: "auto", marginTop: 3 }}
                        onChange={(e) => setPd({ paymentConfirmed: e.target.checked })} />
                      Payment collected at the counter — I confirm the balance has been taken.
                    </label>
                    <label>Paid by</label>
                    <select value={pickupDraft.payMethod} onChange={(e) => setPd({ payMethod: e.target.value })}>
                      {["POS terminal", "Apple Pay", "Cash", "Card — manual entry"].map((m2) => <option key={m2}>{m2}</option>)}
                    </select>
                  </div>
                )}
                <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 14, padding: 12 }} disabled={!canRelease || !String(pickupDraft.by || "").trim() || !pickupDraft.atLocal}
                  onClick={() => {
                    const at = pickupDraft.atLocal ? new Date(pickupDraft.atLocal) : new Date();
                    const confirmed = !!(pay.ok || pickupDraft.paymentConfirmed);
                    let next = { ...t };
                    if (needsPayConfirm && pickupDraft.paymentConfirmed) {
                      next.payment = { ...(t.payment || {}), required: true, paid: true, amount: num(pickupDraft.cost).toFixed(2),
                        method: pickupDraft.payMethod, at: at.toISOString(), by: pickupDraft.by || meName, repairCollected: true };
                    }
                    next.pickup = {
                      at: at.toISOString(), by: String(pickupDraft.by || meName).trim(),
                      customerName: pickupDraft.customerName, unit: pickupDraft.unit, ticketNo: pickupDraft.ticketNo,
                      cost: num(pickupDraft.cost).toFixed(2),
                      warranty: pay.warranty ? "Warranty repair" : "Non-warranty repair",
                      paymentStatus: (pay.ok || confirmed) ? (pay.paymentRequired || needsPayConfirm ? "collected" : "not required") : "outstanding",
                      paymentConfirmed: confirmed,
                    };
                    const g = repairPickupGate(next);
                    if (!g.ok) { alert(g.reason); return; }
                    completeTicket(next);
                    setPickupPop(null); setPickupDraft(null); setTab("done");
                  }}>
                  {canRelease ? "Picked up — complete & release" : "🔒 Picked up — payment required"}
                </button>
                {!canRelease && <div className="sub" style={{ marginTop: 7, color: "var(--clay)", fontWeight: 650 }}>Collect payment (or the deductible) before this unit leaves the shop.</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {payPop && (() => {
        const t = repairs.find((x) => x.id === payPop);
        if (!t) return null;
        const pressurePay = pressurePayOpCheck(t);
        const opCheck = pressurePay.applies ? pressurePay : opCheckFor(t);
        return (
          <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setPayPop(null); }}>
            <div className="sheet" style={{ maxWidth: 440 }}>
              <div className="bar">
                <div><strong style={{ fontSize: 16 }}>Diagnostic fee — {t.ticketNo}</strong><div className="sub">{t.customerName}</div></div>
                <button className="btn ghost" onClick={() => setPayPop(null)}>Back</button>
              </div>
              <div className="pad">
                <div className="notice" style={{ borderLeft: "4px solid #B7791F", marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13.5, fontWeight: 650 }}>
                    <input type="checkbox" checked={payFilterOk} style={{ width: "auto", marginTop: 3 }} onChange={(e) => setPayFilterOk(e.target.checked)} />
                    {opCheck.label}
                  </label>
                  {pressurePay.applies && (
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 9, margin: "8px 0 0", textTransform: "none", letterSpacing: 0, fontSize: 13.5, fontWeight: 650 }}>
                      <input type="checkbox" checked={payHoseOk} style={{ width: "auto", marginTop: 3 }} onChange={(e) => setPayHoseOk(e.target.checked)} />
                      {pressurePay.hoseLabel}{pressurePay.hoseDefaultOn ? " — on because this looks like a hose repair" : " (optional — not assumed)"}
                    </label>
                  )}
                </div>
                <div className="fields">
                  <div><label>Diagnostic fee ($)</label><input inputMode="decimal" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} /></div>
                  <div><label>Paid by</label>
                    <select value={payMethod2} onChange={(e) => setPayMethod2(e.target.value)}>
                      {["POS terminal", "Apple Pay", "Cash", "Card — manual entry"].map((m2) => <option key={m2}>{m2}</option>)}
                    </select>
                  </div>
                </div>
                <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 12, padding: 12 }} disabled={!payFilterOk || !num(payAmt)}
                  onClick={() => {
                    saveTicket(withRepairStatus({ ...t, filterConfirmed: true,
                      ...(pressurePay.applies ? { pressureOpCheck: true, hoseAssembly: !!payHoseOk } : {}),
                      payment: { required: true, paid: true, method: payMethod2, amount: num(payAmt).toFixed(2), at: new Date().toISOString(), by: meName } }, "REPAIRS_QUEUE", meName));
                    setPayPop(null); setTab("queue");
                  }}>
                  Take {posMoney(payAmt)} & send to the repairs queue
                </button>
                {!payFilterOk && <div className="sub" style={{ marginTop: 7, color: "var(--clay)", fontWeight: 650 }}>{opCheck.hint}</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {tab === "rentals" && (
        <RentalView units={rentalUnits} clients={clients} me={me} isMgr={isMgr} businessProfile={businessProfile}
          terms={terms}
          onSave={onRentalSave} onCheckout={onRentalCheckout} onCheckIn={onRentalCheckIn}
          onOpenCard={onOpenCard} onPick={onPick} onCompose={onCompose} />
      )}
      {tab === "dash" && (
        <div>
          <div className="counts" style={{ marginBottom: 14 }}>
            <div className="count-cell"><div className="n">{kpi.receivedToday}</div><div className="l">Repairs received today</div></div>
            <div className="count-cell"><div className="n">{kpi.completedToday}</div><div className="l">Repairs completed today</div></div>
            <div className="count-cell"><div className="n" style={kpi.waitingParts ? { color: "#B7791F" } : undefined}>{kpi.waitingParts}</div><div className="l">Units waiting for parts</div></div>
            <div className="count-cell"><div className="n">{kpi.avgDays == null ? "—" : kpi.avgDays < 2 ? Math.round(kpi.avgDays * 24) + "h" : kpi.avgDays.toFixed(1) + "d"}</div><div className="l">Avg repair turnaround</div></div>
          </div>
          <div className="sub">Turnaround averages every completed ticket, intake to completion. Archived tickets stay in the math but leave the queues.</div>
        </div>
      )}
    </div>
  );
}

/* ---------- AI phone receptionist: call review queue + assistant setup ---------- */
function assistantExtensionLines(as) {
  const src = as || {};
  const raw = Array.isArray(src.extensionLines) ? src.extensionLines : [];
  const lines = raw.map((l, i) => ({
    id: (l && l.id) || ("ext-" + (i + 1)),
    name: String((l && (l.name || l.label)) || ""),
    number: String((l && (l.number || l.phone || l.ext)) || ""),
  }));
  if (!lines.length && (src.transferName || src.transferNumber)) {
    return [{ id: "ext-legacy", name: src.transferName || "", number: src.transferNumber || "" }];
  }
  return lines.length ? lines : [{ id: "ext-1", name: "", number: "" }];
}
function addAssistantExtensionLine(lines) {
  return [...(lines || []), { id: cid(), name: "", number: "" }];
}
const AI_VOICES = [
  ["Charon", "Male · deep, calm (current)"], ["Puck", "Male · upbeat"], ["Orus", "Male · firm, best French"],
  ["Fenrir", "Male · energetic"], ["Kore", "Female · firm, best French"], ["Aoede", "Female · breezy"],
  ["Leda", "Female · youthful"], ["Zephyr", "Female · bright"],
];
const AI_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const IVR_JOB_TYPES = ["WeeklyMaintenance", "Opening", "Closing", "Repair", "LeakDetection", "HotTubService", "Renovation", "Inspection", "Other"];

function AIReceptionistView({ data, save, me, clients = [], onPick }) {
  const [tab, setTab] = useState("calls"); // calls | setup | wishlist
  const [calls, setCalls] = useState(null);
  const [wishes, setWishes] = useState(null);
  const [openCall, setOpenCall] = useState(null);
  const [bp, setBp] = useState({ ...(data.businessProfile || {}) });
  const [as, setAs] = useState({ ...(data.assistantSettings || {}) });
  const [msg, setMsg] = useState("");

  const [mediaUrls, setMediaUrls] = useState({});
  const loadCalls = async () => {
    if (!supabase) return;
    const { data: rows } = await supabase.from("call_records").select("*").order("started_at", { ascending: false }).limit(200);
    setCalls(rows || []);
    // Web-form attachments live in the private photos bucket — sign fresh view links.
    const paths = (rows || []).flatMap((r) => (Array.isArray(r.media) ? r.media : []));
    if (paths.length) {
      const { data: su } = await supabase.storage.from("photos").createSignedUrls(paths, 3600);
      if (su) setMediaUrls(Object.fromEntries(su.filter((x) => x.signedUrl).map((x) => [x.path, x.signedUrl])));
    }
  };
  const loadWishes = async () => {
    if (!supabase) return;
    const { data: rows } = await supabase.from("feature_requests").select("*").order("created_at", { ascending: false }).limit(200);
    setWishes(rows || []);
  };
  useEffect(() => { loadCalls(); loadWishes(); }, []); // eslint-disable-line

  const clientName = (id) => (clients.find((c) => c.id === id) || {}).name || "";
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  const setHour = (day, k, v) => setBp((b) => ({ ...b, hours: { ...(b.hours || {}), [day]: { ...((b.hours || {})[day] || {}), [k]: v } } }));

  const saveSetup = () => {
    const lines = assistantExtensionLines(as);
    save({ ...data, businessProfile: bp, assistantSettings: { ...as, extensionLines: lines, transferName: (lines[0] || {}).name || "", transferNumber: (lines[0] || {}).number || "" } });
    setMsg("Saved — the assistant uses the new settings on the very next call.");
  };

  const approve = async (r) => {
    if (!r.customer_id) { setMsg("Link a customer first — this caller wasn't matched to an account."); return; }
    const now = new Date();
    if (r.intent === "Reschedule" && r.reschedule_of_job_id) {
      const job = (data.jobs || []).find((j) => j.id === r.reschedule_of_job_id);
      if (job) {
        save({ ...data, jobs: data.jobs.map((j) => (j.id === job.id ? { ...j, date: r.requested_date || j.date,
          changes: [...(j.changes || []), { id: cid(), date: fmtDate(now), time: "", by: me ? me.name : "", note: `Rescheduled by phone (AI receptionist) → ${r.requested_date}` }] } : j)) });
      }
      await supabase.from("call_records").update({ review_status: "Approved", job_id: r.reschedule_of_job_id }).eq("id", r.id);
    } else {
      const job = { id: cid(), clientId: r.customer_id, kind: "service", serviceType: r.requested_job_type || "",
        what: `Phone booking — ${r.requested_job_type || "service"}`, note: (r.notes || "").split("\n")[0] || "Booked via AI receptionist",
        date: r.requested_date || "", techId: "", weight: 1, at: new Date().toISOString(),
        bookedBy: "AI receptionist", bookedDate: fmtDate(now) };
      save({ ...data, jobs: [...(data.jobs || []), job] });
      await supabase.from("call_records").update({ review_status: "Approved", job_id: job.id }).eq("id", r.id);
    }
    setMsg("Approved — the booking is on the schedule" + (r.requested_date ? ` for ${r.requested_date}.` : " (in the holding bin — give it a day)."));
    loadCalls();
  };
  const dismiss = async (r) => { await supabase.from("call_records").update({ review_status: "Dismissed" }).eq("id", r.id); loadCalls(); };

  const pending = (calls || []).filter((r) => r.review_status === "PendingReview");
  const done = (calls || []).filter((r) => r.review_status !== "PendingReview");

  const callCard = (r) => (
    <div key={r.id} className="visit" style={r.review_status === "PendingReview" ? { borderLeft: "4px solid #B7791F" } : { opacity: 0.75 }}>
      <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span className="vdate">{fmtWhen(r.started_at)}</span>
        <div className="vdocs" style={{ alignItems: "center" }}>
          {String(r.external_call_id || "").startsWith("web-") && <span className="doc" style={{ background: "#7C6CE0" }}>🌐 Web form</span>}
          {r.customer_id
            ? <button className="link" style={{ fontWeight: 700 }} onClick={() => onPick(r.customer_id)}>{clientName(r.customer_id) || "Customer"}</button>
            : <span className="doc" style={{ background: "#5A6E86" }}>{r.caller_name || "Unknown caller"}</span>}
          <span className="doc">{r.caller_number || "no number"}</span>
          {r.intent && r.intent !== "Unknown" && <span className="doc" style={{ background: "#17356E" }}>{r.intent}</span>}
          {r.requested_job_type && <span className="doc" style={{ background: "#0887C8" }}>{r.requested_job_type}{r.requested_date ? " · " + r.requested_date : ""}</span>}
          {r.duration_seconds != null && <span className="doc inv">{Math.round(r.duration_seconds / 60)}m {r.duration_seconds % 60}s</span>}
          <span className="doc" style={{ background: r.review_status === "Approved" ? "#12A650" : r.review_status === "Dismissed" ? "#5A6E86" : "#B7791F" }}>{r.review_status}</span>
        </div>
      </div>
      {(r.notes || r.transcription) && (
        <div className="vnotes" style={{ fontSize: 12.5, whiteSpace: "pre-wrap", maxHeight: openCall === r.id ? "none" : 54, overflow: "hidden", cursor: "pointer" }}
          onClick={() => setOpenCall(openCall === r.id ? null : r.id)}>
          {r.notes || r.transcription}
        </div>
      )}
      {Array.isArray(r.media) && r.media.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
          {r.media.map((p) => {
            const u = mediaUrls[p];
            const isVid = /\.(mp4|mov|webm|qt)$/i.test(p);
            if (!u) return <span key={p} className="doc">attachment</span>;
            return isVid
              ? <a key={p} className="btn ghost mini" href={u} target="_blank" rel="noreferrer">▶ Watch video</a>
              : <a key={p} href={u} target="_blank" rel="noreferrer" title="Open full size">
                  <img src={u} alt="attached" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)", display: "block" }} />
                </a>;
          })}
        </div>
      )}
      {r.review_status === "PendingReview" && (
        <div className="btnrow" style={{ marginTop: 9 }}>
          {(r.intent === "NewVisit" || r.intent === "Reschedule") && <button className="btn mini" onClick={() => approve(r)}>Approve — put it on the schedule</button>}
          <button className="btn ghost mini" onClick={() => dismiss(r)}>Dismiss</button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 16 }}>AI Phone Receptionist</strong>
          <div className="sub">Calls answered by the assistant land here for review · settings apply on the next call</div>
        </div>
        <div className="btnrow">
          <button className={"btn mini" + (tab === "calls" ? "" : " ghost")} onClick={() => setTab("calls")}>Calls{pending.length ? ` (${pending.length})` : ""}</button>
          <button className={"btn mini" + (tab === "setup" ? "" : " ghost")} onClick={() => setTab("setup")}>AI Setup</button>
          <button className={"btn mini" + (tab === "wishlist" ? "" : " ghost")} onClick={() => setTab("wishlist")}>Wishlist{(wishes || []).filter((w) => w.status === "new").length ? ` (${wishes.filter((w) => w.status === "new").length})` : ""}</button>
        </div>
      </div>
      {msg && <div className="notice" style={{ marginBottom: 12 }}>{msg}</div>}

      {tab === "calls" && (
        calls === null ? <div className="empty">Loading calls…</div> : (<>
          {pending.length === 0 && <div className="empty">No calls waiting for review.</div>}
          {pending.map(callCard)}
          {done.length > 0 && <div className="sechead">Reviewed</div>}
          {done.slice(0, 30).map(callCard)}
        </>)
      )}

      {tab === "setup" && (<>
        <div className="sechead">Business (what the assistant says about you)</div>
        <div className="fields">
          <div><label>Business name</label><input value={bp.name || ""} placeholder="Pool Master Canada" onChange={(e) => setBp({ ...bp, name: e.target.value })} /></div>
          <div><label>Business phone</label><input value={bp.phone || ""} onChange={(e) => setBp({ ...bp, phone: e.target.value })} /></div>
          <div className="f-wide"><label>Address</label><input value={bp.address || ""} onChange={(e) => setBp({ ...bp, address: e.target.value })} /></div>
          <div><label>Owner name</label><input value={bp.ownerName || ""} onChange={(e) => setBp({ ...bp, ownerName: e.target.value })} /></div>
          <div><label>Owner phone (trusted for wishlist)</label><input value={bp.ownerPhone || ""} onChange={(e) => setBp({ ...bp, ownerPhone: e.target.value })} /></div>
        </div>
        <div className="sechead">Business hours</div>
        {AI_DAYS.map((day) => {
          const h = (bp.hours || {})[day] || {};
          const closed = h.closed !== false && !h.open;
          return (
            <div key={day} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
              <span style={{ width: 92, fontWeight: 700, fontSize: 12.5 }}>{day}</span>
              <label style={{ fontSize: 12 }}>
                <input type="checkbox" checked={!closed} style={{ width: "auto", marginRight: 6 }}
                  onChange={(e) => { setHour(day, "closed", !e.target.checked); if (e.target.checked && !h.open) { setHour(day, "open", "08:00"); setHour(day, "close", "17:00"); } }} />
                Open
              </label>
              {!closed && (<>
                <input type="time" value={h.open || "08:00"} style={{ width: 110 }} onChange={(e) => setHour(day, "open", e.target.value)} />
                <span className="sub" style={{ marginTop: 0 }}>to</span>
                <input type="time" value={h.close || "17:00"} style={{ width: 110 }} onChange={(e) => setHour(day, "close", e.target.value)} />
              </>)}
            </div>
          );
        })}
        <div className="sechead">Assistant</div>
        <div className="fields">
          <div><label>Voice</label>
            <select value={as.voiceName || "Charon"} onChange={(e) => setAs({ ...as, voiceName: e.target.value })}>
              {AI_VOICES.map(([v, d]) => <option key={v} value={v}>{v} — {d}</option>)}
            </select></div>
          <div><label>Language</label>
            <select value={as.language || "en"} onChange={(e) => setAs({ ...as, language: e.target.value })}>
              <option value="en">English</option><option value="fr">French</option><option value="auto">Auto — follow the caller</option>
            </select></div>
          <div className="f-wide"><label>Greeting (blank = default "Thank you for calling…")</label>
            <input value={as.greeting || ""} onChange={(e) => setAs({ ...as, greeting: e.target.value })} /></div>
          <div className="f-wide"><label>Custom instructions (added to the AI's brief — e.g. "Always mention the spring opening special")</label>
            <textarea value={as.customInstructions || ""} style={{ minHeight: 70 }} onChange={(e) => setAs({ ...as, customInstructions: e.target.value })} /></div>
          <div className="f-wide">
            <label>
              <input type="checkbox" checked={!!as.transferEnabled} style={{ width: "auto", marginRight: 8 }} onChange={(e) => setAs({ ...as, transferEnabled: e.target.checked })} />
              Offer live transfer to a person (during business hours)
            </label>
          </div>
          {as.transferEnabled && (<>
            <div className="f-wide"><label>Extension lines — staff can add more (no cap)</label>
              <div className="sub" style={{ marginTop: 0 }}>Live transfer destinations for this AI receptionist. Owner / manager settings.</div>
              {assistantExtensionLines(as).map((ln, i) => (
                <div key={ln.id} style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  <input placeholder={`Line ${i + 1} name`} value={ln.name} style={{ flex: 1, minWidth: 140 }}
                    onChange={(e) => setAs((s) => { const lines = assistantExtensionLines(s).map((x) => x.id === ln.id ? { ...x, name: e.target.value } : x); return { ...s, extensionLines: lines, transferName: (lines[0] || {}).name || "", transferNumber: (lines[0] || {}).number || "" }; })} />
                  <input placeholder="Number / extension" value={ln.number} style={{ flex: 1, minWidth: 140 }}
                    onChange={(e) => setAs((s) => { const lines = assistantExtensionLines(s).map((x) => x.id === ln.id ? { ...x, number: e.target.value } : x); return { ...s, extensionLines: lines, transferName: (lines[0] || {}).name || "", transferNumber: (lines[0] || {}).number || "" }; })} />
                  {assistantExtensionLines(as).length > 1 && (
                    <button type="button" className="btn warn mini" onClick={() => setAs((s) => { const lines = assistantExtensionLines(s).filter((x) => x.id !== ln.id); return { ...s, extensionLines: lines, transferName: (lines[0] || {}).name || "", transferNumber: (lines[0] || {}).number || "" }; })}>Remove</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn ghost mini" style={{ marginTop: 8 }}
                onClick={() => setAs((s) => ({ ...s, extensionLines: addAssistantExtensionLine(assistantExtensionLines(s)) }))}>+ Add another line</button>
            </div>
          </>)}
          <div><label>Feature-request PIN (blank = wishlist mode off)</label>
            <input value={as.featurePin || ""} maxLength={6} onChange={(e) => setAs({ ...as, featurePin: e.target.value.replace(/\D/g, "") })} /></div>
        </div>
        <div className="btnrow" style={{ marginTop: 14 }}>
          <button className="btn" onClick={saveSetup}>Save — applies on the next call</button>
        </div>
      </>)}

      {tab === "wishlist" && (
        wishes === null ? <div className="empty">Loading…</div> :
        wishes.length === 0 ? <div className="empty">No dictated ideas yet. Call the line, say "feature request", and speak your idea.</div> :
        wishes.map((w) => (
          <div key={w.id} className="visit" style={w.status !== "new" ? { opacity: 0.7 } : undefined}>
            <div className="vhead">
              <span className="vdate">{fmtWhen(w.created_at)}</span>
              <div className="vdocs">
                <span className="doc">{w.caller_number || "unknown"}</span>
                <span className="doc" style={{ background: w.status === "new" ? "#B7791F" : "#12A650" }}>{w.status}</span>
              </div>
            </div>
            <div className="vnotes" style={{ fontSize: 13 }}>{w.text}</div>
            {w.status === "new" && (
              <div className="btnrow" style={{ marginTop: 8 }}>
                <button className="btn ghost mini" onClick={async () => { await supabase.from("feature_requests").update({ status: "done" }).eq("id", w.id); loadWishes(); }}>Mark handled</button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

/* ---------- technician truck stock: scan in from the shop, return back ---------- */
/* ---------- Warranty Reference: the counter lookup ----------
   Read-only. One search box — serial first (strongest: resolves the actual
   sale and purchase date), then product code/name, then brand. Shows coverage
   terms, deductible wording, covered/excluded/requirements/claim steps, doc
   links, and INTERNAL notes. No cost or margin fields anywhere on this screen. */
function WarrantyRefView({ parts = [], clients = [], allSales = [], allJobs = [], seed = null, onSeedDone }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [mfr, setMfr] = useState("");
  const [pd, setPd] = useState(""); // purchase date in play (from serial hit, seed, or typed)
  const [pdSource, setPdSource] = useState("");
  useEffect(() => {
    if (seed) { setQ(seed.q || ""); setPd(seed.purchaseDate || ""); setPdSource(seed.purchaseDate ? seed.source || "equipment record" : ""); onSeedDone && onSeedDone(); }
  }, [seed ? 1 : 0]); // eslint-disable-line
  const today = fmtDate(new Date());
  const term = q.trim().toLowerCase();
  // serial pass — exact or partial, across till sales and work orders
  let snHit = null;
  if (term.length >= 4) {
    for (const sale of allSales) {
      for (const li of sale.items || []) {
        if (serialList(li.serial).some((x) => x.toLowerCase().includes(term))) { snHit = { code: li.code, name: li.desc, date: sale.date, customerName: sale.customerName || "Walk-in", source: "till sale" }; break; }
      }
      if (snHit) break;
    }
    if (!snHit) for (const j of allJobs) {
      for (const li of (((j.wf || {}).results || {}).chems || {}).items || []) {
        if (serialList(li.serial).some((x) => x.toLowerCase().includes(term))) { snHit = { code: li.code, name: li.desc, date: ((j.wf || {}).completedAt || "").slice(0, 10), customerName: (clients.find((c) => c.id === j.clientId) || {}).name || "", source: "work order" }; break; }
      }
      if (snHit) break;
    }
  }
  const withW = parts.filter((p) => p.active !== false && partHasWarranty(p));
  const matches = withW.filter((p) => {
    if (cat && p.category !== cat) return false;
    if (mfr && p.manufacturer !== mfr) return false;
    if (snHit) return String(p.code || "").trim().toLowerCase() === String(snHit.code || "").trim().toLowerCase();
    if (!term) return true;
    return [p.code, p.name, p.manufacturer].filter(Boolean).join(" ").toLowerCase().includes(term);
  }).slice(0, 12);
  const activeDate = (snHit && snHit.date) || pd;
  const cats = [...new Set(withW.map((p) => p.category).filter(Boolean))];
  const mfrs = [...new Set(withW.map((p) => p.manufacturer).filter(Boolean))].sort();
  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 16 }}>Warranty Reference</strong>
          <div className="sub">What's covered, for how long, and how to claim — while the customer is standing at the counter. Read-only.</div>
        </div>
      </div>
      <div className="panel" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input value={q} placeholder="Serial number, product code, name, or brand" style={{ flex: 1, minWidth: 220, padding: "12px", fontSize: 15 }} onChange={(e) => setQ(e.target.value)} />
          <select value={cat} style={{ width: "auto" }} onChange={(e) => setCat(e.target.value)}>
            <option value="">All categories</option>
            {cats.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={mfr} style={{ width: "auto" }} onChange={(e) => setMfr(e.target.value)}>
            <option value="">All brands</option>
            {mfrs.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <label className="sub" style={{ marginTop: 0 }}>Purchase date (for live status)
            <input type="date" value={pd} style={{ width: "auto", marginLeft: 6 }} onChange={(e) => { setPd(e.target.value); setPdSource(e.target.value ? "entered by hand" : ""); }} />
          </label>
          {snHit && <span className="doc" style={{ background: "#12A650" }}>Serial found — {snHit.source}: sold to {snHit.customerName} on {snHit.date}</span>}
          {!snHit && pd && pdSource && <span className="sub" style={{ marginTop: 0 }}>date from: {pdSource}</span>}
        </div>
      </div>

      {matches.length === 0 && <div className="empty">{term || cat || mfr ? "No warranty-carrying product matches that." : "Every product with a warranty on file is searchable here — start typing."}</div>}
      {matches.map((p) => {
        const w = p.warranty;
        const ded = w.deductible;
        const dedNow = activeDate ? deductibleFor(w, /^\d{4}$/.test(String(activeDate).trim()) ? `${String(activeDate).trim()}-01-01` : activeDate, today) : null;
        return (
          <div key={p.id} className="panel" style={{ padding: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <b style={{ fontSize: 15 }}>{p.name || p.code}</b>
              {p.code && <span className="doc">{p.code}</span>}
              {p.manufacturer && <span className="doc" style={{ background: "#17356E" }}>{p.manufacturer}</span>}
              {p.category && <span className="sub" style={{ marginTop: 0 }}>{p.category}{p.subCategory ? ` · ${p.subCategory}` : ""}</span>}
            </div>

            <div className="sechead">Coverage</div>
            {(w.terms || []).map((tm) => {
              const st = activeDate ? warrantyTermStatus(tm, /^\d{4}$/.test(String(activeDate).trim()) ? `${String(activeDate).trim()}-01-01` : activeDate, today) : null;
              const yr = activeDate ? warrantyYearOf(tm, activeDate, today) : null;
              return (
                <div key={tm.id} className="sub" style={{ marginTop: 3, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ flex: 1, minWidth: 140 }}><b>{tm.scope || "Coverage"}</b> — {tm.months} months</span>
                  {st && (st.covered
                    ? <span className="doc" style={{ background: "#0E7A3C" }}>IN WARRANTY — expires {st.end}</span>
                    : <span className="doc" style={{ background: "#DC1515" }}>EXPIRED {st.end}</span>)}
                  {yr && <span className="sub" style={{ marginTop: 0 }}>year {yr.year} of {yr.of}{yr.approx ? " (approximate — purchase year only)" : ""}</span>}
                </div>
              );
            })}
            {!activeDate && <div className="sub" style={{ marginTop: 5, color: "var(--clay)", fontWeight: 650 }}>No purchase date on file — ask for the receipt to give a live status.</div>}

            {ded && num(ded.amount) > 0 && (
              <div className="sub" style={{ marginTop: 6 }}>
                <b>Deductible:</b> ${num(ded.amount).toFixed(2)} per repair{num(ded.freeMonths) > 0 ? `, first ${ded.freeMonths} months free` : ""}
                {activeDate && dedNow ? (dedNow.applies
                  ? ` — today: $${dedNow.amount} owed on a repair`
                  : dedNow.expired ? " — today: coverage has ended"
                  : dedNow.freeUntil ? ` — today: FREE (free period runs to ${dedNow.freeUntil})` : "") : ""}
              </div>
            )}

            {w.covered && <><div className="sechead">What's covered</div><div className="sub" style={{ whiteSpace: "pre-wrap" }}>{w.covered}</div></>}
            {w.excluded && <><div className="sechead">Not covered</div><div className="sub" style={{ whiteSpace: "pre-wrap" }}>{w.excluded}</div></>}
            {w.requirements && <><div className="sechead">Requirements</div><div className="sub" style={{ whiteSpace: "pre-wrap" }}>{w.requirements}</div></>}
            {w.claimProcess && <><div className="sechead">How to claim</div><div className="sub" style={{ whiteSpace: "pre-wrap" }}>{w.claimProcess}</div></>}
            {(w.docs || []).filter((doc) => doc.url).length > 0 && (
              <><div className="sechead">Manufacturer documents</div>
              <div className="btnrow" style={{ flexWrap: "wrap" }}>
                {(w.docs || []).filter((doc) => doc.url).map((doc) => (
                  <a key={doc.id} className="btn ghost mini" href={doc.url} target="_blank" rel="noreferrer">{doc.label || "Document"} ↗</a>
                ))}
              </div></>
            )}
            {w.notes && (
              <div className="notice" style={{ marginTop: 8, borderLeft: "4px solid #B36B00" }}>
                <span className="doc" style={{ background: "#B36B00", marginRight: 6 }}>INTERNAL</span>{w.notes}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Promotions: write, target, schedule and track campaigns ----------
   Office-only (same gate as Retail Sales). The queue is written up front —
   one row per recipient — and a cron-driven worker sends it with nobody
   signed in, re-checking opt-outs at send time and throttling under Gmail's
   daily cap. Dry-run builds the whole queue but never touches SMTP. */
function PromoView({ campaigns = [], clients = [], sales = [], repairs = [], me, businessProfile = {}, onSave }) {
  const [mode, setMode] = useState("list");
  const [draft, setDraft] = useState(null);
  const [showExcl, setShowExcl] = useState(false);
  const [when, setWhen] = useState("now");
  const [schedDate, setSchedDate] = useState(fmtDate(new Date()));
  const [schedTime, setSchedTime] = useState("09:00");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const today = fmtDate(new Date());
  const bp = businessProfile || {};
  const aud = draft ? promoAudience(clients, draft.audience, { sales, repairs }, today) : null;
  const saveCampaign = (c) => onSave(campaigns.some((x) => x.id === c.id) ? campaigns.map((x) => (x.id === c.id ? c : x)) : [...campaigns, c]);
  const startNew = () => {
    setDraft({ id: cid(), name: "", subject: "", body: "", category: "retail", audience: { seg: "all", noRetailSince: "" },
      status: "draft", dryRun: false, createdBy: me ? me.name : "", createdAt: new Date().toISOString(), counts: {} });
    setWhen("now"); setErr(""); setMode("new");
  };
  const schedule = async () => {
    if (!draft.subject.trim() || !draft.body.trim()) { setErr("The email needs a subject and a body."); return; }
    if (!aud.included.length) { setErr("Nobody would receive this — loosen the audience filters."); return; }
    setBusy(true); setErr("");
    try {
      const sendAt = when === "now" ? new Date().toISOString() : new Date(`${schedDate}T${schedTime || "09:00"}:00`).toISOString();
      const rows = aud.included.map((r) => ({ campaign_id: draft.id, client_id: r.clientId, contact_id: r.contactId,
        name: `${r.clientName}${r.name ? " — " + r.name : ""}`, email: r.email }));
      for (let i = 0; i < rows.length; i += 200) {
        const { error } = await supabase.from("campaign_recipients").insert(rows.slice(i, i + 200));
        if (error) throw error;
      }
      saveCampaign({ ...draft, name: draft.name.trim() || draft.subject.trim(), status: "scheduled", sendAt,
        counts: { queued: rows.length, sent: 0, failed: 0, skipped: 0 } });
      setMode("list"); setDraft(null);
    } catch (e) { setErr("Couldn't queue the campaign: " + String(e.message || e)); }
    setBusy(false);
  };
  const cancel = async (c) => {
    saveCampaign({ ...c, status: "cancelled" });
    try { await supabase.from("campaign_recipients").update({ status: "skipped", reason: "campaign cancelled" }).eq("campaign_id", c.id).eq("status", "queued"); } catch { /* worker sweeps too */ }
  };
  const openDetail = async (c) => {
    try {
      const { data: rows } = await supabase.from("campaign_recipients").select("*").eq("campaign_id", c.id).order("created_at").limit(3000);
      setDetail({ c, rows: rows || [] });
    } catch { setDetail({ c, rows: [] }); }
  };
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  const previewText = draft ? `Hi {first name},\n\n${draft.body || "(the email body)"}\n\n\u2014\n${bp.name || "Pool Master Canada"}\n${bp.address || "(business mailing address \u2014 set it under Business Profile)"}${bp.phone ? "\n" + bp.phone : ""}\n\nTo stop receiving promotional emails from us, click here:\n[unsubscribe link \u2014 added automatically, unique per customer]` : "";
  const sorted = [...campaigns].sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <div className="bar" style={{ marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div>
          <strong style={{ fontSize: 16 }}>Promotions</strong>
          <div className="sub">Seasonal sales by email — opted-out customers are excluded automatically, and every email carries an instant unsubscribe link (that's the law in Canada).</div>
        </div>
        {mode === "list" && <button className="btn" onClick={startNew}>+ New promotion</button>}
      </div>

      {mode === "list" && (<>
        {sorted.length === 0 && <div className="empty">No campaigns yet. Write the first one — a Father's Day sale, a chemical special, an opening promo.</div>}
        {sorted.map((c) => {
          const ct = promoSendCounts(c, detail && detail.c.id === c.id ? detail.rows : null);
          return (
            <div key={c.id} className="panel" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <b>{c.name || c.subject}</b>{c.dryRun ? <span className="doc" style={{ background: "#5A6E86", marginLeft: 6 }}>DRY RUN</span> : null}
                  <div className="sub" style={{ marginTop: 2 }}>
                    {(PROMO_SEGMENTS.find((x) => x.v === (c.audience || {}).seg) || {}).label || "All customers"}
                    {c.sendAt ? ` · ${c.status === "scheduled" ? "sends" : "started"} ${fmtWhen(c.sendAt)}` : ""}
                    {ct.queued || ct.sent || ct.failed || ct.skipped
                      ? ` · ${ct.sent} sent · ${ct.failed} failed · ${ct.skipped} skipped${ct.queued ? ` · ${ct.queued} waiting` : ""}` : ""}
                  </div>
                </div>
                <span className="doc" style={{ background: c.status === "done" ? "#12A650" : c.status === "cancelled" ? "#5A6E86" : c.status === "paused" ? "#B7791F" : c.status === "sending" ? "#17356E" : c.status === "scheduled" ? "#B7791F" : "#5A6E86" }}>
                  {CAMPAIGN_STATUS[c.status] || c.status}
                </span>
                <div className="btnrow" style={{ flexWrap: "wrap" }}>
                  {(c.status === "scheduled" || c.status === "sending") && <button className="btn ghost mini" onClick={() => saveCampaign({ ...c, status: "paused", pausedFrom: c.status })}>Pause</button>}
                  {c.status === "paused" && <button className="btn mini" onClick={() => saveCampaign({ ...c, status: c.pausedFrom || "sending" })}>Resume</button>}
                  {["scheduled", "sending", "paused"].includes(c.status) && <button className="btn ghost mini" onClick={() => cancel(c)}>Cancel</button>}
                  {c.status !== "draft" && <button className="btn ghost mini" onClick={() => openDetail(c)}>Recipients</button>}
                  {c.status === "draft" && <button className="btn mini" onClick={() => { setDraft(c); setMode("new"); }}>Edit</button>}
                  {["draft", "cancelled", "done"].includes(c.status) && <button className="btn ghost mini" onClick={() => onSave(campaigns.filter((x) => x.id !== c.id))}>Delete</button>}
                </div>
              </div>
              {detail && detail.c.id === c.id && (
                <div style={{ marginTop: 10 }}>
                  {detail.rows.length === 0 ? <div className="sub">No recipient records.</div>
                    : detail.rows.map((r) => (
                      <div key={r.id} className="sub" style={{ marginTop: 3, display: "flex", gap: 8 }}>
                        <span className="doc" style={{ background: r.status === "sent" ? "#12A650" : r.status === "failed" ? "#B31212" : r.status === "skipped" ? "#5A6E86" : "#B7791F" }}>{r.status}</span>
                        <span style={{ flex: 1 }}>{r.name || r.email} · {r.email}{r.reason ? ` — ${r.reason}` : ""}{r.sent_at ? ` (${fmtWhen(r.sent_at)})` : ""}</span>
                      </div>
                    ))}
                  <button className="link" style={{ marginTop: 6 }} onClick={() => setDetail(null)}>Close</button>
                </div>
              )}
            </div>
          );
        })}
      </>)}

      {mode === "new" && draft && (<>
        <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
          <div className="sechead" style={{ marginTop: 0 }}>The email</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div><label>Campaign name (internal)</label><input value={draft.name} placeholder="Father's Day sale 2027" onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div><label>Subject line</label><input value={draft.subject} placeholder="20% off all chemicals this weekend" onChange={(e) => setDraft({ ...draft, subject: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label>Body — plain text; each customer is greeted by first name automatically</label>
            <textarea value={draft.body} rows={8} style={{ width: "100%", fontSize: 14.5 }} placeholder="This weekend only..." onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </div>
          <div className="sub" style={{ marginTop: 6 }}>Sends from Retail Team - Poolmaster Canada &lt;sales@poolmaster.ca&gt;. Business name, mailing address and an unsubscribe link are added to every email automatically.</div>
        </div>

        <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
          <div className="sechead" style={{ marginTop: 0 }}>Who gets it</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 200 }}>
              <label>Audience</label>
              <select value={draft.audience.seg} onChange={(e) => setDraft({ ...draft, audience: { ...draft.audience, seg: e.target.value } })}>
                {PROMO_SEGMENTS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
              </select>
            </div>
            <div>
              <label>Only if no retail purchase since (optional)</label>
              <input type="date" value={draft.audience.noRetailSince || ""} onChange={(e) => setDraft({ ...draft, audience: { ...draft.audience, noRetailSince: e.target.value } })} />
            </div>
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <b style={{ fontSize: 15 }}>{aud.included.length} will receive it</b>
            <button className="link" onClick={() => setShowExcl(!showExcl)}>{aud.excluded.length} excluded — {showExcl ? "hide" : "see why"}</button>
          </div>
          {showExcl && (
            <div style={{ marginTop: 8, maxHeight: 220, overflowY: "auto" }}>
              {aud.excluded.length === 0 ? <div className="sub">Nobody is excluded.</div>
                : aud.excluded.map((x, i) => <div key={i} className="sub" style={{ marginTop: 3 }}><b>{x.name}</b> — {x.reason}</div>)}
            </div>
          )}
        </div>

        <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
          <div className="sechead" style={{ marginTop: 0 }}>Exactly what they'll see</div>
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: 12, margin: 0 }}>
            <b>From:</b> Retail Team - Poolmaster Canada &lt;sales@poolmaster.ca&gt;{"\n"}<b>Subject:</b> {draft.subject || "(subject line)"}{"\n\n"}{previewText}
          </pre>
        </div>

        <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
          <div className="sechead" style={{ marginTop: 0 }}>When</div>
          <div className="seg" style={{ margin: "0 0 10px" }}>
            <button type="button" data-on={when === "now" ? 1 : 0} onClick={() => setWhen("now")}>Send now</button>
            <button type="button" data-on={when === "later" ? 1 : 0} onClick={() => setWhen("later")}>Schedule</button>
          </div>
          {when === "later" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <div><label>Date</label><input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} /></div>
              <div><label>Time</label><input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} /></div>
            </div>
          )}
          <label className="chk" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={!!draft.dryRun} onChange={(e) => setDraft({ ...draft, dryRun: e.target.checked })} />
            Dry run — build the full recipient list and work the queue, but send no actual emails
          </label>
          <div className="sub" style={{ marginTop: 6 }}>
            The sender robot checks every 10 minutes and spreads big lists out — at most 1,500 promotional emails a day, so the sales@ mailbox never hits Gmail's limit. Opt-outs are re-checked at the moment of sending.
          </div>
          {err && <div className="notice" style={{ marginTop: 10, borderLeft: "4px solid var(--sun)" }}>{err}</div>}
          <div className="btnrow" style={{ marginTop: 12 }}>
            <button className="btn" disabled={busy} onClick={schedule}>{busy ? "Queueing…" : when === "now" ? (draft.dryRun ? "Start dry run" : "Send now") : (draft.dryRun ? "Schedule dry run" : "Schedule it")}</button>
            <button className="btn ghost" disabled={busy} onClick={() => { saveCampaign(draft); setMode("list"); setDraft(null); }}>Save as draft</button>
            <button className="btn ghost" disabled={busy} onClick={() => { setMode("list"); setDraft(null); }}>Discard</button>
          </div>
        </div>
      </>)}
    </div>
  );
}

/* ---------- Company tools: the check-in / check-out board ----------
   One component, two homes: the office sees it as a tab on the Service page
   (check out to anyone, manage the list, clear flags); a tech sees it on My
   Truck (self check-out/in, formal transfers, raise a flag). */
const TOOL_FLAG_LABEL = { lost: "Lost", damaged: "Damaged", notreturned: "Not returned" };
function ToolsPanel({ tools = [], staff = [], meId = "", meName = "", isMgr = false, selfOnly = false, onSave }) {
  const [editing, setEditing] = useState(null);   // draft of the tool profile being edited (no id yet = new)
  const [histFor, setHistFor] = useState("");     // tool id whose full log is open
  const [acting, setActing] = useState(null);     // { toolId, kind: "checkout" | "transfer" | "flag" | "clear" }
  const [pickId, setPickId] = useState("");
  const [flagKind, setFlagKind] = useState("damaged");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const people = (staff || []).filter((p) => p.active !== false);
  const closeAct = () => { setActing(null); setPickId(""); setNote(""); setErr(""); };
  const saveTool = (t) => onSave(tools.some((x) => x.id === t.id) ? tools.map((x) => (x.id === t.id ? t : x)) : [...tools, t]);
  const run = (toolId, r) => {
    if (!r.ok) { setActing({ toolId, kind: "msg" }); setErr(r.reason); return; }
    saveTool(r.tool); closeAct();
  };
  const live = tools.filter((t) => !t.retired)
    .sort((a, b) => String(a.tag || a.name || "").localeCompare(String(b.tag || b.name || ""), undefined, { numeric: true }));
  const retired = tools.filter((t) => t.retired);
  const nOut = live.filter((t) => t.status === "out").length;
  const nFlag = live.filter((t) => t.flag).length;
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <div className="bar" style={{ marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div>
          <strong style={{ fontSize: 16 }}>Company tools</strong>
          <div className="sub">
            {live.length - nOut - nFlag} available · {nOut} out · {nFlag ? `${nFlag} flagged` : "none flagged"} —
            whoever last checked a tool out is responsible for it until it's checked in or formally transferred.
          </div>
        </div>
        {isMgr && <button className="btn ghost" onClick={() => setEditing({ tag: "", name: "", serial: "", notes: "" })}>+ Add a tool</button>}
      </div>

      {editing && (
        <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
          <div className="sechead" style={{ marginTop: 0 }}>{editing.id ? "Edit tool profile" : "New tool profile"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}>
            <div><label>ID number</label><input value={editing.tag} placeholder="T-01" onChange={(e) => setEditing({ ...editing, tag: e.target.value })} /></div>
            <div><label>Tool</label><input value={editing.name} placeholder="e.g. Hilti hammer drill" onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 8 }}><label>Serial number (optional)</label><input value={editing.serial} onChange={(e) => setEditing({ ...editing, serial: e.target.value })} /></div>
          <div style={{ marginTop: 8 }}><label>Notes (optional)</label><input value={editing.notes} placeholder="case, charger, blades…" onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
          {editing.id && (
            <label className="chk" style={{ marginTop: 10 }}>
              <input type="checkbox" checked={!!editing.retired} onChange={(e) => setEditing({ ...editing, retired: e.target.checked })} />
              Retired — off the board, history kept
            </label>
          )}
          <div className="btnrow" style={{ marginTop: 12 }}>
            <button className="btn" disabled={!editing.name.trim()} onClick={() => {
              const t = editing.id ? editing
                : { ...editing, id: cid(), status: "in", log: [{ id: cid(), ...toolStamp(meName), action: "add", note: "added to the board" }] };
              saveTool({ ...t, tag: t.tag.trim(), name: t.name.trim() }); setEditing(null);
            }}>Save tool</button>
            <button className="btn ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {live.length === 0 && <div className="empty">No tools on the board yet{isMgr ? " — add the first one above." : "."}</div>}
      {live.map((t) => {
        const open = acting && acting.toolId === t.id;
        const mineOut = t.status === "out" && t.holderId === meId;
        const canCheckIn = t.status === "out" && (!selfOnly || mineOut);
        const canTransfer = t.status === "out" && (!selfOnly || mineOut);
        const canCheckout = t.status !== "out" && !t.flag;
        return (
          <div key={t.id} className="panel" style={{ padding: 12, marginBottom: 8, borderLeft: t.flag ? "4px solid #B31212" : t.status === "out" ? "4px solid var(--sun)" : "4px solid #12A650" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <b>{t.tag ? `${t.tag} · ` : ""}{t.name}</b>
                {t.serial ? <span className="sub" style={{ display: "inline", marginLeft: 6 }}>S/N {t.serial}</span> : null}
                <div className="sub" style={{ marginTop: 2 }}>
                  {t.flag
                    ? `${TOOL_FLAG_LABEL[t.flag.kind] || "Flagged"}${t.flag.note ? " — " + t.flag.note : ""} · last responsible: ${t.flag.responsible || "unknown"} · awaiting management review`
                    : t.status === "out"
                      ? `${t.holderName}${mineOut ? " (you)" : ""} has it — ${toolHeldFor(t.outAt)} (since ${fmtWhen(t.outAt)})`
                      : `Available${t.lastHolderName ? ` · last out with ${t.lastHolderName}` : ""}${t.notes ? ` · ${t.notes}` : ""}`}
                </div>
              </div>
              {t.flag ? <span className="doc" style={{ background: "#B31212" }}>FLAGGED</span>
                : t.status === "out" ? <span className="doc" style={{ background: "#B7791F" }}>OUT</span>
                : <span className="doc" style={{ background: "#12A650" }}>IN</span>}
              <div className="btnrow" style={{ flexWrap: "wrap" }}>
                {canCheckout && (selfOnly
                  ? <button className="btn mini" onClick={() => run(t.id, toolCheckout(t, meId, meName, meName))}>Check out to me</button>
                  : <button className="btn mini" onClick={() => { closeAct(); setActing({ toolId: t.id, kind: "checkout" }); }}>Check out</button>)}
                {canCheckIn && <button className="btn mini" onClick={() => run(t.id, toolCheckin(t, meName))}>Check in</button>}
                {canTransfer && <button className="btn ghost mini" onClick={() => { closeAct(); setActing({ toolId: t.id, kind: "transfer" }); }}>Transfer</button>}
                {!t.flag && <button className="btn ghost mini" onClick={() => { closeAct(); setNote(""); setFlagKind(t.status === "out" ? "notreturned" : "damaged"); setActing({ toolId: t.id, kind: "flag" }); }}>Flag</button>}
                {t.flag && isMgr && <button className="btn mini" onClick={() => { closeAct(); setActing({ toolId: t.id, kind: "clear" }); }}>Back in service</button>}
                <button className="btn ghost mini" onClick={() => setHistFor(histFor === t.id ? "" : t.id)}>History</button>
                {isMgr && <button className="btn ghost mini" onClick={() => setEditing({ serial: "", notes: "", ...t })}>Edit</button>}
              </div>
            </div>

            {open && acting.kind === "msg" && err && <div className="notice" style={{ marginTop: 10, borderLeft: "4px solid var(--sun)" }}>{err}</div>}

            {open && (acting.kind === "checkout" || acting.kind === "transfer") && (
              <div style={{ marginTop: 10 }}>
                {acting.kind === "transfer" && (
                  <div className="sub" style={{ marginBottom: 6 }}>
                    Both names go on the record. Until a transfer is recorded here, <b>{t.holderName}</b> stays responsible for this tool.
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <select value={pickId} style={{ flex: 1, minWidth: 170 }} onChange={(e) => setPickId(e.target.value)}>
                    <option value="">{acting.kind === "transfer" ? "— who is taking it over? —" : "— who is taking it? —"}</option>
                    {people.filter((p) => !(acting.kind === "transfer" && p.id === t.holderId)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button className="btn" disabled={!pickId} onClick={() => {
                    const p = people.find((x) => x.id === pickId); if (!p) return;
                    run(t.id, acting.kind === "transfer" ? toolTransfer(t, p.id, p.name, meName) : toolCheckout(t, p.id, p.name, meName));
                  }}>{acting.kind === "transfer" ? "Record transfer" : "Check out"}</button>
                  <button className="btn ghost" onClick={closeAct}>Cancel</button>
                </div>
                {err && <div className="notice" style={{ marginTop: 8, borderLeft: "4px solid var(--sun)" }}>{err}</div>}
              </div>
            )}

            {open && acting.kind === "flag" && (
              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select value={flagKind} style={{ minWidth: 130 }} onChange={(e) => setFlagKind(e.target.value)}>
                  <option value="damaged">Damaged</option>
                  <option value="lost">Lost</option>
                  <option value="notreturned">Not returned</option>
                </select>
                <input value={note} placeholder="what happened?" style={{ flex: 1, minWidth: 160 }} onChange={(e) => setNote(e.target.value)} />
                <button className="btn" onClick={() => { saveTool(toolFlag(t, flagKind, note.trim(), meName)); closeAct(); }}>Flag for review</button>
                <button className="btn ghost" onClick={closeAct}>Cancel</button>
              </div>
            )}

            {open && acting.kind === "clear" && (
              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input value={note} placeholder="how it was resolved (repaired, found, written off…)" style={{ flex: 1, minWidth: 200 }} onChange={(e) => setNote(e.target.value)} />
                <button className="btn" onClick={() => { saveTool(toolClearFlag(t, note.trim(), meName)); closeAct(); }}>Back in service</button>
                <button className="btn ghost" onClick={closeAct}>Cancel</button>
              </div>
            )}

            {histFor === t.id && (
              <div style={{ marginTop: 10 }}>
                {[...(t.log || [])].reverse().slice(0, 30).map((e) => (
                  <div key={e.id || e.at} className="sub" style={{ marginTop: 3 }}>
                    <span className="mono">{fmtWhen(e.at)}</span> — {e.action === "checkout" ? `checked out to ${e.to}`
                      : e.action === "checkin" ? `checked in by ${e.from}`
                      : e.action === "transfer" ? `transferred ${e.from} → ${e.to}`
                      : e.action === "flag" ? `flagged: ${e.note}`
                      : e.action === "clear" ? `back in service — ${e.note}` : e.note || e.action}
                    {e.by && e.action !== "checkin" ? ` (recorded by ${e.by})` : ""}
                  </div>
                ))}
                {(t.log || []).length === 0 && <div className="sub" style={{ marginTop: 3 }}>No history yet.</div>}
              </div>
            )}
          </div>
        );
      })}

      {isMgr && retired.length > 0 && (<>
        <div className="sechead">Retired ({retired.length})</div>
        {retired.map((t) => (
          <div key={t.id} className="sub" style={{ marginTop: 3, display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ flex: 1 }}>{t.tag ? `${t.tag} · ` : ""}{t.name}</span>
            <button className="btn ghost mini" onClick={() => setEditing({ serial: "", notes: "", ...t })}>Edit</button>
          </div>
        ))}
      </>)}
    </div>
  );
}

/* ---------- Company fleet: vehicles, trailers, and staff-taken equipment ----------
   Owner + manager only. Tracker = in/out and who has the unit (not GPS or a typed
   job-site). Service = maintenance. Separate from rentalUnits (workshop robots)
   and from tools (hand-tool checkout on the Service page / My Truck). */
const FLEET_KINDS = [
  ["vehicle", "Vehicle"],
  ["trailer", "Trailer"],
  ["gas_pump", "Gas pump"],
  ["submersible", "Submersible pump"],
  ["heat_gun", "Heat gun"],
  ["equipment", "Other equipment"],
];
const FLEET_ROLLING = new Set(["vehicle", "trailer"]);
const FLEET_STATUS = [
  ["shop", "At the shop"],
  ["out", "Out"],
  ["assigned", "With a tech"],
  ["service", "In for service"],
  ["down", "Out of service"],
];
const FLEET_INK = { shop: "#12A650", out: "#0887C8", assigned: "#0B4F8A", service: "#B7791F", down: "#DC1515" };
const FLEET_SVC_ROLLING = ["Oil change", "Tires", "Brakes", "Inspection", "Repair", "Other"];
const FLEET_SVC_EQUIP = ["Inspection", "Repair", "Service", "Other"];
function fleetIsRolling(kind) { return FLEET_ROLLING.has(kind); }
function fleetKindLabel(kind) { return (FLEET_KINDS.find((k) => k[0] === kind) || ["", "Unit"])[1]; }
function fleetStLabel(s) { return (FLEET_STATUS.find((x) => x[0] === s) || [s, s])[1]; }
function fleetSvcKinds(kind) { return fleetIsRolling(kind) ? FLEET_SVC_ROLLING : FLEET_SVC_EQUIP; }
function fleetNamePh(kind) {
  return ({ vehicle: "Service Truck 1", trailer: "Dump trailer", gas_pump: "Gas pump 1",
    submersible: "Submersible 1", heat_gun: "Heat gun 1", equipment: "Shop equipment" })[kind] || "Unit 1";
}
function fleetTitle(u) {
  const who = [u.year, u.make, u.model].filter(Boolean).join(" ");
  const ident = fleetIsRolling(u.kind) ? (u.plate || "") : (u.vin || "");
  return `${u.name || fleetKindLabel(u.kind)}${who ? " · " + who : ""}${ident ? " · " + ident : ""}`;
}
function blankFleet() {
  return { kind: "vehicle", name: "", plate: "", vin: "", year: "", make: "", model: "",
    status: "shop", assignedTo: "", assignedName: "", notes: "",
    odometer: "", nextServiceDate: "", nextServiceNote: "", serviceLog: [] };
}
function FleetView({ units = [], staff = [], me, onSave }) {
  const [tab, setTab] = useState("tracker"); // tracker | service
  const [editing, setEditing] = useState(null);
  const [trackId, setTrackId] = useState("");
  const [trackSt, setTrackSt] = useState("shop");
  const [trackWho, setTrackWho] = useState("");
  const [logFor, setLogFor] = useState("");
  const [log, setLog] = useState({ date: fmtDate(new Date()), kind: "Inspection", note: "", odometer: "", cost: "" });
  const today = fmtDate(new Date());
  const people = (staff || []).filter((p) => p.active !== false);
  const live = (units || []).filter((u) => !u.retired)
    .sort((a, b) => String(a.name || a.plate || "").localeCompare(String(b.name || b.plate || ""), undefined, { numeric: true }));
  const saveUnit = (u) => {
    const next = { ...u };
    delete next.location; // pass 2: no typed job-site / "where" tracking
    onSave(units.some((x) => x.id === next.id) ? units.map((x) => (x.id === next.id ? next : x)) : [...units, next]);
  };
  const dueSoon = live.filter((u) => u.nextServiceDate).sort((a, b) => String(a.nextServiceDate).localeCompare(String(b.nextServiceDate)));
  const meName = me ? me.name : "";
  const personName = (id) => ((people.find((p) => p.id === id) || {}).name || "");
  const rollingEdit = editing && fleetIsRolling(editing.kind);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <div className="bar" style={{ marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div>
          <strong style={{ fontSize: 16 }}>Fleet Management</strong>
          <div className="sub">Company vehicles, trailers, and equipment (pumps, heat guns) — who has them, and their maintenance. Hand tools stay on the Service page / My Truck. Rental robots stay in In-House Repair.</div>
        </div>
        {tab === "tracker" && <button className="btn ghost" onClick={() => setEditing(blankFleet())}>+ Add a unit</button>}
      </div>
      <div className="btnrow" style={{ marginBottom: 14 }}>
        <button type="button" className={"btn" + (tab === "tracker" ? "" : " ghost")} onClick={() => setTab("tracker")}>Fleet Tracker</button>
        <button type="button" className={"btn" + (tab === "service" ? "" : " ghost")} onClick={() => setTab("service")}>Fleet Service</button>
      </div>

      {tab === "tracker" && (<>
        {editing && (
          <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
            <div className="sechead" style={{ marginTop: 0 }}>{editing.id ? "Edit unit" : "New unit"}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><label>Kind</label>
                <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {FLEET_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><label>Name</label><input value={editing.name} placeholder={fleetNamePh(editing.kind)} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              {rollingEdit && <div><label>Plate</label><input value={editing.plate} onChange={(e) => setEditing({ ...editing, plate: e.target.value })} /></div>}
              <div><label>Year</label><input value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} /></div>
              <div><label>Make</label><input value={editing.make} placeholder={rollingEdit ? "Ford" : "Honda"} onChange={(e) => setEditing({ ...editing, make: e.target.value })} /></div>
              <div><label>Model</label><input value={editing.model} placeholder={rollingEdit ? "Transit / dump trailer" : "2\" trash pump"} onChange={(e) => setEditing({ ...editing, model: e.target.value })} /></div>
            </div>
            <div style={{ marginTop: 8 }}><label>{rollingEdit ? "VIN (optional)" : "Serial (optional)"}</label><input value={editing.vin} onChange={(e) => setEditing({ ...editing, vin: e.target.value })} /></div>
            <div style={{ marginTop: 8 }}><label>Status</label>
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                {FLEET_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ marginTop: 8 }}><label>Assigned to</label>
              <select value={editing.assignedTo} onChange={(e) => {
                const id = e.target.value;
                setEditing({ ...editing, assignedTo: id, assignedName: personName(id) });
              }}>
                <option value="">— not assigned —</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ marginTop: 8 }}><label>Notes (optional)</label><input value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            {editing.id && (
              <label className="chk" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={!!editing.retired} onChange={(e) => setEditing({ ...editing, retired: e.target.checked })} />
                Retired — off the board, history kept
              </label>
            )}
            <div className="btnrow" style={{ marginTop: 12 }}>
              <button className="btn" disabled={!String(editing.name || editing.plate || "").trim()} onClick={() => {
                const u = editing.id ? editing : { ...editing, id: cid(), serviceLog: [] };
                saveUnit({ ...u, name: String(u.name || "").trim(), plate: String(u.plate || "").trim() });
                setEditing(null);
              }}>Save</button>
              <button className="btn ghost" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}

        {live.length === 0 && <div className="empty">No units on the board yet — add the first truck, trailer, pump, or heat gun above.</div>}
        {live.map((u) => {
          const open = trackId === u.id;
          return (
            <div key={u.id} className="panel" style={{ padding: 12, marginBottom: 8, borderLeft: "4px solid " + (FLEET_INK[u.status] || "#5A6E86") }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <b>{fleetTitle(u)}</b>
                  <span className="sub" style={{ display: "inline", marginLeft: 6 }}>{fleetKindLabel(u.kind)}</span>
                  <div className="sub" style={{ marginTop: 2 }}>
                    {u.assignedName ? u.assignedName : (u.status === "shop" ? "At the shop" : fleetStLabel(u.status))}
                    {fleetIsRolling(u.kind) && u.odometer ? ` · ${u.odometer} km` : ""}
                    {u.notes ? ` · ${u.notes}` : ""}
                  </div>
                </div>
                <span className="doc" style={{ background: FLEET_INK[u.status] || "#5A6E86" }}>{fleetStLabel(u.status)}</span>
                <div className="btnrow">
                  <button className="btn mini" onClick={() => {
                    if (open) { setTrackId(""); return; }
                    setTrackId(u.id); setTrackSt(u.status || "shop"); setTrackWho(u.assignedTo || "");
                  }}>{open ? "Close" : "Update status"}</button>
                  <button className="btn ghost mini" onClick={() => setEditing({ ...blankFleet(), ...u })}>Edit</button>
                </div>
              </div>
              {open && (
                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  <div><label>Status</label>
                    <select value={trackSt} onChange={(e) => setTrackSt(e.target.value)}>
                      {FLEET_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div><label>Assigned to</label>
                    <select value={trackWho} onChange={(e) => setTrackWho(e.target.value)}>
                      <option value="">— not assigned —</option>
                      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="btnrow">
                    <button className="btn" onClick={() => {
                      saveUnit({ ...u, status: trackSt, assignedTo: trackWho, assignedName: personName(trackWho) });
                      setTrackId("");
                    }}>Save</button>
                    <button className="btn ghost" onClick={() => setTrackId("")}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </>)}

      {tab === "service" && (<>
        {dueSoon.length > 0 && (
          <div className="panel" style={{ padding: 12, marginBottom: 12, borderLeft: "4px solid #B7791F" }}>
            <b style={{ fontSize: 13.5 }}>Maintenance schedule</b>
            <div className="sub" style={{ marginTop: 2 }}>Next service date on each unit — overdue in red.</div>
            {dueSoon.map((u) => {
              const late = u.nextServiceDate && u.nextServiceDate <= today;
              return (
                <div key={u.id} className="sub" style={{ marginTop: 6, color: late ? "#DC1515" : undefined, fontWeight: late ? 700 : undefined }}>
                  {u.nextServiceDate}{late ? " — OVERDUE" : ""} · {fleetTitle(u)}{u.nextServiceNote ? " — " + u.nextServiceNote : ""}
                </div>
              );
            })}
          </div>
        )}
        {live.length === 0 && <div className="empty">Add a unit on Fleet Tracker first, then log maintenance here.</div>}
        {live.map((u) => {
          const open = logFor === u.id;
          const hist = [...(u.serviceLog || [])].reverse();
          const rolling = fleetIsRolling(u.kind);
          return (
            <div key={u.id} className="panel" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <b>{fleetTitle(u)}</b>
                  <span className="sub" style={{ display: "inline", marginLeft: 6 }}>{fleetKindLabel(u.kind)}</span>
                  <div className="sub" style={{ marginTop: 2 }}>
                    {u.nextServiceDate ? `Next service ${u.nextServiceDate}${u.nextServiceNote ? " — " + u.nextServiceNote : ""}` : "No next-service date set"}
                    {rolling && u.odometer ? ` · ${u.odometer} km` : ""}
                  </div>
                </div>
                <button className="btn mini" onClick={() => {
                  if (open) { setLogFor(""); return; }
                  setLogFor(u.id);
                  setLog({ date: fmtDate(new Date()), kind: "Inspection", note: "", odometer: rolling ? (u.odometer || "") : "", cost: "" });
                }}>{open ? "Close" : "Log maintenance"}</button>
              </div>
              {open && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div><label>Date</label><input type="date" value={log.date} onChange={(e) => setLog({ ...log, date: e.target.value })} /></div>
                    <div><label>Kind</label>
                      <select value={log.kind} onChange={(e) => setLog({ ...log, kind: e.target.value })}>
                        {fleetSvcKinds(u.kind).map((k) => <option key={k}>{k}</option>)}
                      </select>
                    </div>
                    {rolling && <div><label>Odometer (km)</label><input inputMode="numeric" value={log.odometer} onChange={(e) => setLog({ ...log, odometer: e.target.value })} /></div>}
                    <div><label>Cost (optional)</label><input inputMode="decimal" value={log.cost} onChange={(e) => setLog({ ...log, cost: e.target.value })} /></div>
                  </div>
                  <div style={{ marginTop: 8 }}><label>What was done</label><input value={log.note} placeholder={rolling ? "oil + filter, winter tires, trailer bearings…" : "impeller, element, cord, new plug…"} onChange={(e) => setLog({ ...log, note: e.target.value })} /></div>
                  <div className="btnrow" style={{ marginTop: 10 }}>
                    <button className="btn" disabled={!log.date} onClick={() => {
                      const entry = { id: cid(), date: log.date, kind: log.kind, note: log.note.trim(), odometer: rolling ? log.odometer.trim() : "", cost: log.cost.trim(), by: meName, at: new Date().toISOString() };
                      saveUnit({ ...u, odometer: rolling ? (entry.odometer || u.odometer) : u.odometer, serviceLog: [...(u.serviceLog || []), entry] });
                      setLog({ date: fmtDate(new Date()), kind: "Inspection", note: "", odometer: rolling ? entry.odometer : "", cost: "" });
                    }}>Save log</button>
                  </div>
                  <div className="sechead">Next service</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div><label>Date</label><input type="date" value={u.nextServiceDate || ""} onChange={(e) => saveUnit({ ...u, nextServiceDate: e.target.value })} /></div>
                    <div><label>Note</label><input value={u.nextServiceNote || ""} placeholder={rolling ? "oil, inspection, tires…" : "seals, service, inspection…"} onChange={(e) => saveUnit({ ...u, nextServiceNote: e.target.value })} /></div>
                  </div>
                </div>
              )}
              {hist.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div className="sub" style={{ fontWeight: 700 }}>History</div>
                  {hist.slice(0, 20).map((e) => (
                    <div key={e.id || e.at} className="sub" style={{ marginTop: 3 }}>
                      {e.date} · {e.kind}{e.note ? " — " + e.note : ""}{e.odometer ? ` · ${e.odometer} km` : ""}{e.cost ? ` · $${e.cost}` : ""}{e.by ? ` (${e.by})` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </>)}
    </div>
  );
}

function TruckView({ me, parts = [], techs = [], onTransfer, tools = [], staff = [], onToolsSave }) {
  const myTech = techs.find((t) => t.staffId && me && t.staffId === me.id) || null;
  const [truckId, setTruckId] = useState(myTech ? myTech.id : "");
  const [q, setQ] = useState("");
  const [qty, setQty] = useState("1");
  const truck = techs.find((t) => t.id === truckId) || null;
  const active = parts.filter((p) => p.active !== false);
  const matches = q.trim()
    ? active.filter((p) => {
        const t = q.trim().toLowerCase();
        return (p.code || "").toLowerCase().includes(t) || (p.name || "").toLowerCase().includes(t);
      }).slice(0, 8)
    : [];
  const stocked = truckId ? parts.filter((p) => num((p.trucks || {})[truckId]) > 0) : [];
  const needsSerial = matches.some((p) => partHasWarranty(p));
  const [ser, setSer] = useState("");
  const stockIn = (p) => {
    const n = num(qty) || 1;
    if (partHasWarranty(p) && serialList(ser).length !== n) return; // button disabled anyway
    if (onTransfer(p.id, "shop", truckId, n, serialList(ser))) { setQ(""); setQty("1"); setSer(""); }
  };
  const giveBack = (p, n, serials) => onTransfer(p.id, truckId, "shop", n, serials);
  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 16 }}>My Truck{truck ? ` — ${truck.vehicle ? `${truck.vehicle} (${truck.name})` : truck.name}` : ""}</strong>
          <div className="sub">Scan a part into your truck — the shop count moves with it, both ways.</div>
        </div>
      </div>
      {!truck ? (
        <div className="panel" style={{ padding: 16 }}>
          <label>Whose truck is this?</label>
          <select value={truckId} onChange={(e) => setTruckId(e.target.value)}>
            <option value="">— pick a truck —</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      ) : (<>
        <div className="panel" style={{ padding: 14, marginBottom: 14 }}>
          <label>Scan or type a part # / description</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input autoFocus value={q} placeholder="e.g. CL-TAB-3" style={{ flex: 1, padding: "13px 12px", fontSize: 16 }}
              onChange={(e) => setQ(e.target.value)} />
            <input inputMode="decimal" value={qty} style={{ width: 74, textAlign: "center", fontSize: 16 }} onChange={(e) => setQty(e.target.value)} />
          </div>
          {needsSerial && (
            <div style={{ marginTop: 8 }}>
              <input value={ser} placeholder={num(qty) > 1 ? `Serial numbers — ${num(qty)} of them, comma-separated` : "Serial number — required before it leaves the shop"}
                style={{ width: "100%", padding: "12px", fontSize: 15 }} onChange={(e) => setSer(e.target.value)} />
              <div className="sub" style={{ marginTop: 3 }}>Warranty equipment is documented dry, on the shelf — not later in a wet mechanical room.</div>
            </div>
          )}
          {matches.length > 0 && (
            <div className="poslookup" style={{ position: "static", boxShadow: "none", marginTop: 8, maxHeight: 300 }}>
              {matches.map((p) => (
                <button key={p.id} type="button" className="poslookupitem" disabled={partHasWarranty(p) && serialList(ser).length !== (num(qty) || 1)} onClick={() => stockIn(p)}>
                  <b>{p.name}{p.code ? ` · ${p.code}` : ""}</b>
                  <span className="sub" style={{ marginTop: 0 }}>
                    Shop has {num(p.qty)}{num((p.trucks || {})[truckId]) > 0 ? ` · you carry ${num((p.trucks || {})[truckId])}` : ""} — tap to load {num(qty) || 1} into your truck
                  </span>
                </button>
              ))}
            </div>
          )}
          {q.trim() && matches.length === 0 && <div className="sub" style={{ marginTop: 8 }}>No part matches that.</div>}
        </div>

        <div className="sechead">On my truck ({stocked.length})</div>
        {stocked.length === 0 ? <div className="empty">Truck is empty. Search a part above and tap it to load up.</div>
          : stocked.map((p) => (
            <div key={p.id} className="visit" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <b style={{ fontSize: 13.5 }}>{p.name}</b>
                <div className="sub" style={{ marginTop: 0 }}>{p.code || "no part #"} · shop has {num(p.qty)}</div>
              </div>
              <b className="mono" style={{ fontSize: 16 }}>×{num((p.trucks || {})[truckId])}</b>
              <div className="btnrow" style={{ flexWrap: "nowrap" }}>
                <button className="btn ghost mini" onClick={() => giveBack(p, 1, ((p.truckSerials || {})[truckId] || []).slice(0, 1))}>1 back</button>
                <button className="btn ghost mini" onClick={() => giveBack(p, num((p.trucks || {})[truckId]), ((p.truckSerials || {})[truckId] || []).slice(0, num((p.trucks || {})[truckId])))}>All back to shop</button>
              </div>
              {((p.truckSerials || {})[truckId] || []).length > 0 && (
                <div className="sub" style={{ marginTop: 3 }}>On board: {((p.truckSerials || {})[truckId] || []).map((sn) => <span key={sn} className="doc" style={{ background: "#17356E", marginRight: 4 }}>S/N {sn}</span>)}</div>
              )}
            </div>
          ))}
        {myTech === null && (
          <div className="notice" style={{ marginTop: 12 }}>
            Tip: this account isn't linked to a truck, so the truck was picked by hand.
            <button type="button" className="link" style={{ marginLeft: 8 }} onClick={() => setTruckId("")}>Switch truck</button>
          </div>
        )}
      </>)}
      <div style={{ marginTop: 20 }}>
        <ToolsPanel tools={tools} staff={staff} meId={me ? me.id : ""} meName={me ? me.name : ""}
          selfOnly onSave={onToolsSave} />
      </div>
    </div>
  );
}

/* In-app AI assistant — its own page, styled like the customer search screen. */
// Starter questions the owner/manager answers to teach the assistant the
// business's opinions — brands, warranties, policies. Answered ones feed the
// assistant's knowledge on every question it gets asked.
const POOLMASTER_FAQ_SEED = [
  "Which pump brands do we recommend, and why those?",
  "Which salt systems do we sell, and what size pool suits each?",
  "Gas heater or heat pump — how do we steer customers, and when?",
  "Which robotic/pressure cleaners do we recommend at each price point?",
  "What warranty do we give on installations and on repairs?",
  "How far out are we usually booking? What should customers expect?",
  "Do we price-match? What discounts can staff offer without asking?",
  "What should staff NEVER promise a customer?",
];

function AssistantView({ me, data, save, canTeach = false }) {
  const [msgs, setMsgs] = useState([]); // {role, content}
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [teachOpen, setTeachOpen] = useState(false);
  const as = (data && data.assistantSettings) || {};
  const faq = Array.isArray(as.faq) && as.faq.length
    ? as.faq
    : POOLMASTER_FAQ_SEED.map((qq, i) => ({ id: "faq" + i, q: qq, a: "" }));
  const setFaq = (next) => save({ ...data, assistantSettings: { ...as, faq: next } });
  const answered = faq.filter((f) => (f.a || "").trim()).length;
  const endRef = useRef(null);
  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const ask = async () => {
    const text = q.trim();
    if (!text || busy) return;
    const next = [...msgs, { role: "user", content: text }];
    setMsgs(next); setQ(""); setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("assistant", { body: { messages: next.map((m) => ({ role: m.role, content: m.content })) } });
      if (error || !data || data.error) throw new Error((data && data.error) || "failed");
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "I couldn't reach the server just now — give it a moment and ask again." }]);
    }
    setBusy(false);
  };
  return (
    <div style={{ maxWidth: 680, margin: "34px auto", padding: "0 16px" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: "var(--ink)", marginBottom: 6, letterSpacing: 0.2 }}>Ask the Poolmaster</div>
        <div className="sub" style={{ marginBottom: 16 }}>Answers come from your own data · what you can see follows your access level</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={q} placeholder="Who owes us money? · What's on Tesla's schedule? · Which parts are low?" disabled={busy}
            style={{ flex: 1, padding: "16px 18px", fontSize: 17, borderRadius: 12, boxShadow: "0 6px 20px rgba(16,32,55,.08)" }}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }} autoFocus />
          <button className="btn" style={{ padding: "0 22px", fontSize: 15 }} onClick={ask} disabled={busy || !q.trim()}>Ask</button>
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        {msgs.length === 0 && !busy && (
          <div className="empty" style={{ padding: "30px 10px" }}>
            Ask anything about the business —<br />
            <span className="sub">"Who owes us money?" · "What's on Tesla's schedule tomorrow?" · "Which parts are low?" · "When did we last service the Halvorsens?"</span>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 10 }}>
            <div style={{
              maxWidth: "90%", padding: "10px 14px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.6, whiteSpace: "pre-wrap",
              background: m.role === "user" ? "var(--ink)" : "#fff",
              color: m.role === "user" ? "#fff" : "var(--ink)",
              border: m.role === "user" ? "none" : "1px solid var(--line)",
              boxShadow: m.role === "user" ? "none" : "0 3px 10px rgba(16,32,55,.06)",
            }}>{m.content}</div>
          </div>
        ))}
        {busy && <div className="sub" style={{ fontStyle: "italic" }}>Checking the books…</div>}
        <div ref={endRef} />
      </div>

      {canTeach && (
        <div className="panel" style={{ marginTop: 26, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <b style={{ fontSize: 14, flex: 1 }}>🎓 Teach the Poolmaster</b>
            <span className="sub" style={{ marginTop: 0 }}>{answered} of {faq.length} answered</span>
            <button className="btn ghost mini" onClick={() => setTeachOpen((v) => !v)}>{teachOpen ? "Close" : "Open"}</button>
          </div>
          {teachOpen && (<>
            <div className="sub" style={{ margin: "6px 0 10px" }}>
              Answer in your own words — brands you trust, policies, things staff shouldn't promise. The assistant treats these
              answers as the house rules when recommending products or answering staff. Blank ones are simply skipped.
            </div>
            {faq.map((f, i) => (
              <div key={f.id} style={{ marginBottom: 10 }}>
                {POOLMASTER_FAQ_SEED.includes(f.q)
                  ? <label>{f.q}</label>
                  : <input value={f.q} placeholder="Your question — e.g. What's our policy on green pools?"
                      style={{ marginBottom: 4, fontWeight: 700 }}
                      onChange={(e) => setFaq(faq.map((x, xi) => (xi === i ? { ...x, q: e.target.value } : x)))} />}
                <textarea rows={2} value={f.a || ""} placeholder="Your answer — plain language"
                  onChange={(e) => setFaq(faq.map((x, xi) => (xi === i ? { ...x, a: e.target.value } : x)))} />
              </div>
            ))}
            <div className="btnrow">
              <button className="btn ghost mini" onClick={() => setFaq([...faq, { id: cid(), q: "", a: "" }])}>+ Add my own question</button>
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}

/* Signature pad — finger or stylus, saved onto the visit record. */
function SignPad({ onSave, onCancel }) {
  const cRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  useEffect(() => {
    const c = cRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#17356E"; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
  }, []);
  const pos = (e) => {
    const r = cRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (cRef.current.width / r.width), y: (e.clientY - r.top) * (cRef.current.height / r.height) };
  };
  const down = (e) => { e.preventDefault(); drawing.current = true; const ctx = cRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); try { cRef.current.setPointerCapture(e.pointerId); } catch {} };
  const move = (e) => { if (!drawing.current) return; const ctx = cRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); dirty.current = true; };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = cRef.current, ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); dirty.current = false; };
  return (
    <div style={{ marginTop: 10 }}>
      <div className="sub" style={{ marginTop: 0, marginBottom: 6 }}>Have the customer sign below.</div>
      <canvas ref={cRef} width={560} height={180}
        style={{ width: "100%", maxWidth: 560, height: 180, border: "2px dashed var(--line)", borderRadius: 10, touchAction: "none", background: "#fff", display: "block" }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
      <div className="btnrow" style={{ marginTop: 8 }}>
        <button type="button" className="btn mini" onClick={() => { if (dirty.current) onSave(cRef.current.toDataURL("image/png")); }}>Use this signature</button>
        <button type="button" className="btn ghost mini" onClick={clear}>Clear</button>
        <button type="button" className="btn ghost mini" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------- technician field view: My Day ---------- */
const DEFAULT_STOP_CHECKLIST = [
  "Skim surface & empty skimmer baskets",
  "Empty pump basket",
  "Brush walls & steps",
  "Vacuum pool",
  "Test & balance water",
  "Check equipment for leaks or noise",
  "Secure gate on the way out",
];
const TAB_FEEDER_ITEM = "Fill chlorinator feeder — record tabs added";
// Shop language "chlorinator pool" = a tablet feeder to fill (inline / off-line),
// not a salt cell and not a liquid pump. One detector for the stop checklist and
// the morning extras — live sanitizerType, equipmentList kind chlorinator, and
// job notes / in-line|off-line chlorinator work. Empty CORE_EQUIP chlorinator
// slots do not count. Do not invent a separate chlorinator flag.
function needsFilledFeeder(client, job) {
  const san = String((client && client.sanitizerType) || "").toLowerCase();
  if (/tab|trichlor|dichlor|\bpuck/.test(san)) return true;
  if (/\bsalt\b/.test(san) || /liquid/.test(san) || /bromine/.test(san)
      || /mineral/.test(san) || /\buv\b|ozone/.test(san)) return false;
  const feederish = (t) => /feeder|inline|off-?line|rainbow|trichlor|\btabs?\b|\bpucks?\b|chlorinator|hayward\s*cl/.test(t);
  const notCellOrPump = (t) => !/salt|aqua.?rite|t-?cell|\bswg\b|stenner|peristaltic|liquid/.test(t);
  if (san && feederish(san) && notCellOrPump(san)) return true;
  const details = ((client && client.equipmentList) || [])
    .filter((e) => e && e.kind === "chlorinator")
    .map((e) => String(e.detail || "").trim().toLowerCase())
    .filter(Boolean);
  if (details.some((d) => feederish(d) && notCellOrPump(d))) return true;
  const jobTxt = String((job && [job.note, job.serviceType, job.kind].filter(Boolean).join("\n")) || "").toLowerCase();
  if (/fill(?:ed)?\s+(?:the\s+)?feeder|(?:chlorinator|tab)\s+feeder|tabs? (?:in|for) (?:the )?feeder/.test(jobTxt)) return true;
  if (jobTxt && /chlorinator|feeder/.test(jobTxt) && feederish(jobTxt) && notCellOrPump(jobTxt)) return true;
  return false;
}
// Checklists are generated, not static (Shaun's field test): the base comes
// from the service type, then the client's own equipment and structure records
// add what THIS pool actually has — a chlorinator / tab pool gets the feeder
// item, a salt pool gets cell checks, a pool with neither gets neither.
// Vacuuming is the service on a vac visit, so "vacuum if needed" wording is
// corrected to a required item; a tech who truly can't do an item marks it
// N/A with a reason that surfaces to the office.
function checklistFor(job, serviceTypes, client) {
  const st = job && job.serviceType && (serviceTypes || []).find((s) => s.name === job.serviceType);
  const src = (st && st.instructions) || (job && job.what) || "";
  let items = String(src).split(/\r?\n|\.\s+/).map((s) => s.trim().replace(/^[-•]\s*/, "").replace(/\.$/, "")).filter((s) => s.length > 2);
  if (!items.length) items = [...DEFAULT_STOP_CHECKLIST];
  if (!job || /vac/i.test(job.serviceType || "")) {
    items = items.map((s) => (/vacuum if needed/i.test(s) ? "Vacuum pool" : s));
  }
  if (client) {
    const add = (label) => { if (!items.some((x) => x.toLowerCase() === label.toLowerCase())) items.push(label); };
    const san = String(client.sanitizerType || "").toLowerCase();
    if (needsFilledFeeder(client, job)) add(TAB_FEEDER_ITEM);
    if (san.includes("salt")) { add("Check salt level"); add("Inspect salt cell"); }
    if (san.includes("bromine")) add("Top up brominator");
    if (san.includes("mineral")) add("Check mineral system cartridge");
    const f = client.features || {};
    if (num(f.waterfalls) > 0) add("Run waterfalls — check flow");
    if (num(f.deckJets) > 0) add("Check deck jets");
    if (num(parseGallons(client.spaGallons)) > 0 || /spa/i.test(String(client.structure || ""))) add("Spa — skim & check jets");
  }
  return items;
}
// The completion gate on a field stop: nothing completes with holes in it.
// Order is enforced too — the arrival photo starts the work order (and the
// clock), the after photo can only be taken once the work is done, and
// "Complete stop" stays off until every requirement below is met.
const CHEM_CHECK_REMINDER = "BEFORE USING ANY CHEMICALS, CHECK CUSTOMER SUPPLY OR CHECK THE SHED.";
const CHEM_CHECK_CHOICES = [
  { v: "checked_storage", label: "1 — Checked storage" },
  { v: "used_customer", label: "2 — Used customer chemicals" },
  { v: "none_usable", label: "3 — Customer had no usable chemicals" },
  { v: "empty_expired", label: "4 — Empty / expired / unusable" },
];
const CHEM_WHY_NEW = [
  { v: "none", label: "None" },
  { v: "empty", label: "Empty" },
  { v: "unusable", label: "Unusable / expired" },
  { v: "different", label: "Different product required" },
  { v: "other", label: "Other" },
];
function chemCheckDraft(src) {
  const d = src || {};
  return {
    check: d.chemCheck || d.check || "",
    usingNew: !!(d.usingNewChems || d.usingNew),
    whyNew: d.chemWhyNew || d.whyNew || "",
    whyOther: d.chemWhyOther || d.whyOther || "",
    chemPhoto: d.chemPhoto || d.chemPhotoId || "",
    storagePhoto: d.storagePhoto || d.storagePhotoId || "",
    billingChems: !!(d.billingChems || d.billChems),
  };
}
function chemCheckGate(draft) {
  const d = chemCheckDraft(draft);
  const missing = [];
  if (!CHEM_CHECK_CHOICES.some((c) => c.v === d.check)) missing.push({ k: "check", label: "customer chemicals check" });
  if (d.usingNew) {
    if (!CHEM_WHY_NEW.some((c) => c.v === d.whyNew)) missing.push({ k: "whyNew", label: "why new chemicals" });
    else if (d.whyNew === "other" && !String(d.whyOther || "").trim()) missing.push({ k: "whyOther", label: "other reason" });
    if (!String(d.storagePhoto || "").trim()) missing.push({ k: "storagePhoto", label: "photo of storage area (new chemical)" });
  }
  if (d.billingChems && !String(d.chemPhoto || "").trim()) missing.push({ k: "chemPhoto", label: "photo of billed chemicals" });
  return { ok: missing.length === 0, missing };
}
function serviceCloseGate(guidedDraft, chemDraft) {
  const a = guidedCloseGate(guidedDraft);
  const b = chemCheckGate(chemDraft);
  return { ok: a.ok && b.ok, missing: [...a.missing, ...b.missing] };
}
function ChemCheckPanel({ value, onChange, billingChems, onChemPhoto, onStoragePhoto }) {
  const d = chemCheckDraft({ ...(value || {}), billingChems });
  const set = (k, v) => onChange({ ...d, [k]: v, billingChems: !!billingChems });
  return (
    <div className="stackpanel" style={{ marginTop: 10 }} data-chem-check="1">
      <div className="ptitle">Customer chemicals check</div>
      <div className="notice" style={{ fontWeight: 800, letterSpacing: 0.2, marginBottom: 8 }}>{CHEM_CHECK_REMINDER}</div>
      <div className="seg" style={{ flexWrap: "wrap" }}>
        {CHEM_CHECK_CHOICES.map((c) => (
          <button type="button" key={c.v} data-on={d.check === c.v ? 1 : 0} onClick={() => set("check", c.v)}>{c.label}</button>
        ))}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontWeight: 700 }}>
        <input type="checkbox" checked={!!d.usingNew} onChange={(e) => set("usingNew", e.target.checked)} style={{ width: "auto" }} />
        Using new (our) chemicals
      </label>
      {d.usingNew && (<>
        <label>Why new chemicals</label>
        <select value={d.whyNew} onChange={(e) => set("whyNew", e.target.value)}>
          <option value="">Pick why…</option>
          {CHEM_WHY_NEW.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        {d.whyNew === "other" && <input value={d.whyOther} placeholder="Other — say why" onChange={(e) => set("whyOther", e.target.value)} />}
        <div className="sub">Photo of the storage area is required when using a brand-new chemical.</div>
        {onStoragePhoto && (
          <label className="btn ghost mini" style={{ display: "inline-flex", marginTop: 6 }}>
            {d.storagePhoto ? "Storage photo ✓" : "📷 Photo of storage area"}
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onStoragePhoto(f); }} />
          </label>
        )}
      </>)}
      {billingChems && (<>
        <div className="sub" style={{ fontWeight: 700, marginTop: 6 }}>Photo required — billing chemicals on this stop.</div>
        {onChemPhoto && (
          <label className="btn ghost mini" style={{ display: "inline-flex" }}>
            {d.chemPhoto ? "Chemicals photo ✓" : "📷 Photo of billed chemicals"}
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onChemPhoto(f); }} />
          </label>
        )}
      </>)}
    </div>
  );
}
function stopGate({ hasBefore, hasAfter, checks, listLen, readingSaved, chemCheck }) {
  const missing = [];
  if (!hasBefore) missing.push({ k: "before", label: "arrival photo" });
  let un = 0;
  for (let i = 0; i < (listLen || 0); i++) { const c = checks && checks[i]; if (!c || !c.s) un += 1; }
  if (un) missing.push({ k: "checklist", label: `checklist — ${un} item${un === 1 ? "" : "s"} not actioned` });
  if (!readingSaved) missing.push({ k: "water", label: "water test" });
  if (!hasAfter) missing.push({ k: "after", label: "after photo" });
  const cg = chemCheckGate(chemCheck);
  if (!cg.ok) cg.missing.forEach((m) => missing.push({ k: "chem-" + m.k, label: m.label }));
  return missing;
}
// Daily leave-the-shop checklist. Morning Pre-Flight already pulled job parts
// onto the truck; the standard kit and job-implied extras ride the same card
// so there is one Ready to Leave, one gate. Field-stop lists (checklistFor /
// stopGate) stay on site — they are a different list.
const DAILY_CHECKLIST = [
  { id: "d-tools", label: "Regular tools — net, pole, brush, vacuum head" },
  { id: "d-test", label: "Testing equipment — test kit / spin disks" },
  { id: "d-ppe", label: "PPE — gloves, safety glasses, boots" },
  { id: "d-chems", label: "Daily chemicals — shock, tabs, pH down, alkalinity" },
  { id: "d-paper", label: "Paperwork — work orders, tablet charged" },
  { id: "d-supplies", label: "Common supplies — hose, trash bags, towels" },
];
const JOB_LEAVE_KITS = [
  { re: /winter cover removal|pool.?opening|\bwcr_|cover removal|\bopening\b/i, id: "open", label: "Opening kit — winter plugs, cover pump, summerizing fittings" },
  { re: /winteriz|pool.?closing|\bwcl_|\bclosing\b/i, id: "close", label: "Closing kit — air compressor, winter plugs, antifreeze" },
  { re: /vacuum/i, id: "vac", label: "Vacuum hose + head" },
  { re: /install|heater|pump|filter|salt system|\buv\b|chlorinator|heat pump/i, id: "install", label: "Install tools, fittings, and the equipment for this stop" },
  { re: /repair/i, id: "repair", label: "Repair kit + common fittings" },
  { re: /deliver/i, id: "del", label: "Customer delivery / order for this stop" },
  { re: /green|recovery|alga/i, id: "green", label: "Shock + algaecide (green-pool recovery)" },
  { re: /quote/i, id: "quote", label: "Tablet + tape measure for the quote" },
  { re: /system startup/i, id: "startup", label: "Startup tools — leak-check fittings" },
];
function dailyExtrasFor(jobs, stops, { clients = [], specialOrders = [], jobTypes = [] } = {}) {
  const out = [];
  const seen = new Set();
  const add = (it) => { if (!it || !it.id || seen.has(it.id)) return; seen.add(it.id); out.push(it); };
  const who = (id) => ((clients || []).find((c) => c.id === id) || {}).name || "this stop";
  const ordersFor = (clientId, stopName) => {
    for (const o of (specialOrders || []).filter((x) => x.customerId === clientId && x.status === "received" && !x.archived)) {
      add({ id: "x-so-" + o.id, label: `Customer order — ${o.item || o.code || "item"}${num(o.qty) > 1 ? " ×" + o.qty : ""}`, special: true, stopName, source: "order" });
    }
  };
  for (const job of jobs || []) {
    if (job.done) continue;
    const stopName = who(job.clientId);
    const jt = (jobTypes || []).find((t) => t.id === job.wfTypeId);
    const blob = [job.serviceType, job.kind, job.wfTypeId, jt && jt.name].filter(Boolean).join(" ");
    for (const kit of JOB_LEAVE_KITS) {
      if (kit.re.test(blob)) add({ id: `x-${kit.id}-${job.id}`, label: kit.label, special: true, stopName, source: "job" });
    }
    const note = String(job.note || "").trim();
    if (note) add({ id: "x-note-" + job.id, label: note.length > 90 ? note.slice(0, 87) + "…" : note, special: true, stopName, source: "notes" });
    const cJob = (clients || []).find((x) => x.id === job.clientId);
    if (needsFilledFeeder(cJob, job)) add({ id: "x-tabs-" + (job.clientId || job.id), label: "Chlorine tabs for the feeder", special: true, stopName, source: "job" });
    ordersFor(job.clientId, stopName);
  }
  for (const c of stops || []) {
    if (!c || !c.id) continue;
    const san = String(c.sanitizerType || "").toLowerCase();
    if (needsFilledFeeder(c)) add({ id: "x-tabs-" + c.id, label: "Chlorine tabs for the feeder", special: true, stopName: c.name, source: "job" });
    if (san.includes("salt")) add({ id: "x-salt-" + c.id, label: "Salt / cell-check supplies", special: true, stopName: c.name, source: "job" });
    ordersFor(c.id, c.name);
  }
  return out;
}
function dailyChecklistFor(jobs, stops, opts) {
  return { standard: DAILY_CHECKLIST.map((x) => ({ ...x })), extras: dailyExtrasFor(jobs, stops, opts) };
}
// Same shape as stopGate: a list of holes. Ready to Leave with any of these
// still open is flagged, and the route (open stop / guided WO) stays blocked.
function leaveGate({ checked, standard, extras, partsReady, partsMissing }) {
  const has = (k) => !!(checked && (typeof checked.has === "function" ? checked.has(k) : checked[k]));
  const missing = [];
  for (const it of standard || []) if (!has(it.id)) missing.push({ k: it.id, label: it.label, kind: "standard" });
  for (const it of extras || []) if (!has(it.id)) missing.push({ k: it.id, label: it.label, kind: "special", stopName: it.stopName });
  for (const p of partsReady || []) if (!has(p.code)) missing.push({ k: p.code, label: p.name || p.code, kind: "part" });
  for (const p of partsMissing || []) missing.push({ k: "short:" + p.code, label: `${p.name || p.code} — still short on the truck`, kind: "stock" });
  return missing;
}
// Owner's photo retention rule (08/18): routine service visits keep only the
// last `keep` visits' before/after shots per property — a weekly account never
// piles up hundreds. Damage photos and every reference photo (property, pad,
// body of water, work orders) are never touched; equipment-install captures
// are filed as permanent Equipment-pad photos and live outside this rule.
function pruneVisitPhotos(photoList, visitList, keep = 4) {
  const isKeptKind = (p) => p.tag !== "visit" || /^Damage/i.test(String(p.caption || ""));
  if (!(photoList || []).some((p) => !isKeptKind(p))) return photoList || [];
  const dates = [...new Set((visitList || []).filter((v) => v.date).map((v) => v.date))].sort().reverse();
  const keepDates = new Set(dates.slice(0, keep));
  const keepIds = new Set();
  for (const v of visitList || []) {
    if (!keepDates.has(v.date)) continue;
    for (const id of v.photoIds || []) keepIds.add(id);
  }
  return (photoList || []).filter((p) => isKeptKind(p) || keepIds.has(p.id) || (p.added && keepDates.has(p.added)));
}
// In-app Back handling: sub-screens (an open stop, a running work order) push
// a handler here; the browser's Back triggers the top one instead of leaving
// the site (Shaun's field test: Back from My Day landed on the sign-in page).
const BACK_HANDLERS = [];
const STOP_DRAFTS = new Map(); // "<clientId>|<date>" -> in-progress stop state, survives Back
// Android fires beforeinstallprompt when the app can be installed; hold it so
// My Day can offer one-tap install. iOS never fires it — techs get a hint.
let deferredInstall = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredInstall = e; });
}
const isStandalone = () => {
  try { return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true; } catch { return false; }
};

const PREFLIGHT = new Map(); // "<techId>|<date>" -> Set of checked part codes (in-memory for the day)

// The guided work-order runner: one step at a time, phone-first, next step
// gated until the current one is satisfied. Job types are data (DEFAULT_JOB_TYPES
// / the job_types table) — this component knows step TYPES, never job types.
function GuidedRunner({ job, jobType, client, tests3, parts, me, onPatchWf, onSaveTest, onAddPhoto, onFinish, onIncomplete, onClose }) {
  const LKEY = "pm_worun_" + job.id;
  const [results, setResults] = useState(() => {
    try { const l = JSON.parse(localStorage.getItem(LKEY) || "null"); if (l) return { ...(l || {}), ...((job.wf || {}).results || {}) }; } catch { /* fresh */ }
    return (job.wf || {}).results || {};
  });
  const [noteDraft, setNoteDraft] = useState("");
  const [readings, setReadings] = useState({ fc: "", tc: "", ph: "", ta: "", ch: "", cya: "", salt: "" });
  const [items, setItems] = useState(null); // chem line items, lazily prefissed from the test
  const [inchesDown, setInchesDown] = useState(job.wf && job.wf.inchesDown || 0);
  const [closing, setClosing] = useState(false);
  const [incReason, setIncReason] = useState("");
  const [closeNotes, setCloseNotes] = useState(() => String((job.wf && job.wf.closeNotes) || ""));
  const [closeReason, setCloseReason] = useState(() => String((job.wf && job.wf.followReason) || ""));
  const [chemDraft, setChemDraft] = useState(() => chemCheckDraft(job.wf || {}));
  const geoRef = useRef(null);
  useEffect(() => {
    try { navigator.geolocation && navigator.geolocation.getCurrentPosition(
      (p) => { geoRef.current = { lat: Math.round(p.coords.latitude * 1e5) / 1e5, lng: Math.round(p.coords.longitude * 1e5) / 1e5 }; },
      () => {}, { maximumAge: 300000, timeout: 8000 }); } catch { /* no geo — captures still stamp time */ }
  }, []);

  const steps = wfResolve(jobType, results);
  const cur = steps.find((s) => !s.skipped && !s.hidden && !s.satisfied) || null;
  const doneCount = steps.filter((s) => s.satisfied && !s.hidden && !s.skipped).length;
  const visCount = steps.filter((s) => !s.hidden && !s.skipped).length;
  const lowWater = !!jobType.alwaysLowWater || (results.water && results.water.v === false);
  const gallons = parseGallons(client && client.gallons) || 10000;
  const doseGallons = Math.round(gallons * lowWaterFactor(lowWater ? inchesDown : 0));

  const commit = (stepId, patch) => {
    const next = { ...results, [stepId]: { ...(results[stepId] || {}), ...patch, by: me ? me.name : "", at: patch.at || new Date().toISOString(), geo: geoRef.current } };
    setResults(next);
    try { localStorage.setItem(LKEY, JSON.stringify(next)); } catch { /* storage full — server save still runs */ }
    onPatchWf({ jobTypeId: jobType.id, results: next, inchesDown: lowWater ? inchesDown : 0,
      startedAt: (next.arrive && next.arrive.at) || (job.wf || {}).startedAt || "" });
  };

  const snapPhoto = (step) => async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = "";
    if (!f) return;
    let dataUrl;
    if (f.type && f.type.startsWith("video/")) {
      if (f.size > 40 * 1024 * 1024) { alert("That video is too big — keep short clips (about 8–11 seconds) and try again."); return; }
      const secs = await readVideoDurationSeconds(f);
      const gate = videoDurationGate(secs);
      if (secs != null && !gate.ok) {
        alert(gate.reason === "short"
          ? `That video is only ${Math.round(secs)}s — aim for ${VIDEO_DURATION_MIN}–${VIDEO_DURATION_MAX} seconds for service/install clips.`
          : `That video is ${Math.round(secs)}s — keep service/install clips to ${VIDEO_DURATION_MIN}–${VIDEO_DURATION_MAX} seconds.`);
        return;
      }
      if (secs == null) {
        // Metadata unavailable — allow with guidance (local/blob path still works).
        if (!window.confirm(`Could not read video length. Service/install clips should be ${VIDEO_DURATION_MIN}–${VIDEO_DURATION_MAX} seconds. Keep this video?`)) return;
      }
      dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
    } else {
      dataUrl = await shrink(f, 1100, 0.8);
    }
    const photoId = await onAddPhoto(dataUrl, `WO ${job.workOrder || ""} — ${step.label}`);
    commit(step.id, { media: [...((results[step.id] || {}).media || []), photoId], at: new Date().toISOString() });
  };

  const saveWaterTest = () => {
    const hasAny = Object.values(readings).some((v) => String(v).trim() !== "");
    if (!hasAny) return;
    if (!waterTestSaveGate({ clientId: client && client.id }, client).ok) return;
    const t = { id: cid(), date: fmtDate(new Date()), source: "spinlab", where: "onsite",
      by: me ? me.name : "", notes: `Guided WO ${job.workOrder || ""}`, results: { ...readings }, clientId: client && client.id };
    onSaveTest(t);
    commit("wtest", { testId: t.id });
  };

  const prefillItems = () => {
    const { recs } = chemAnalysis(readings, doseGallons, { saltPool: isSaltPool(client || {}) });
    setItems(recs.map((r) => ({ id: cid(), desc: `${r.title} — ${r.dose}`, qty: "1", src: "truck", code: "" })));
  };

  const finishItems = (list) => commit(cur.id, { items: list.map(({ id, ...r }) => r), lowWater: lowWater ? { inchesDown, factor: lowWaterFactor(inchesDown), doseGallons } : null });

  const stepBody = (s) => {
    if (s.type === "photo" || s.type === "media") return (
      <label className="btn" style={{ display: "inline-flex", fontSize: 16, padding: "14px 22px" }}>
        📷 {s.type === "photo" ? "Take the photo" : `Photo / short video (${VIDEO_DURATION_MIN}–${VIDEO_DURATION_MAX}s)`}
        <input type="file" accept={s.type === "media" ? "image/*,video/*" : "image/*"} capture="environment" style={{ display: "none" }} onChange={snapPhoto(s)} />
      </label>
    );
    if (s.type === "ack") return (
      <div className="btnrow" style={{ flexWrap: "wrap" }}>
        <button className="btn" style={{ fontSize: 16, padding: "13px 20px" }} onClick={() => commit(s.id, { v: true })}>✓ Confirmed</button>
        <button className="btn ghost" style={{ fontSize: 15 }} onClick={() => commit(s.id, { v: false })}>⚠ There's a problem</button>
      </div>
    );
    if (s.type === "gate") return (
      <div className="btnrow" style={{ flexWrap: "wrap" }}>
        <button className="btn" style={{ fontSize: 16, padding: "13px 20px" }} onClick={() => commit(s.id, { v: true })}>Yes</button>
        <button className="btn ghost" style={{ fontSize: 16, padding: "13px 20px" }} onClick={() => commit(s.id, { v: false })}>No</button>
      </div>
    );
    if (s.type === "note") {
      const cap = Number(s.max) || 0;
      return (
        <div>
          <textarea rows={s.optional ? 3 : 2} value={noteDraft} placeholder={s.help || "Write it down…"} maxLength={cap || undefined}
            onChange={(e) => setNoteDraft(cap ? e.target.value.slice(0, cap) : e.target.value)} />
          {cap > 0 && <div className="sub" style={{ textAlign: "right" }}>{noteDraft.length} / {cap}</div>}
          <div className="btnrow" style={{ marginTop: 8 }}>
            <button className="btn" disabled={!noteDraft.trim()} onClick={() => { commit(s.id, { note: noteDraft.trim() }); setNoteDraft(""); }}>Save note</button>
            {s.optional && <button className="btn ghost" onClick={() => { commit(s.id, { note: "" }); setNoteDraft(""); }}>No notes — continue</button>}
          </div>
        </div>
      );
    }
    if (s.type === "external") return (
      <div>
        <div className="fields" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))" }}>
          {[["fc", "FC"], ["tc", "TC"], ["ph", "pH"], ["ta", "TA"], ["ch", "CH"], ["cya", "CYA"], ["salt", "Salt"]].map(([k, l]) => (
            <div key={k}><label>{l}</label><input inputMode="decimal" value={readings[k]} onChange={(e) => setReadings((r) => ({ ...r, [k]: e.target.value }))} /></div>
          ))}
        </div>
        <button className="btn" style={{ marginTop: 8 }} onClick={saveWaterTest} disabled={!Object.values(readings).some((v) => String(v).trim())}>Save test (service)</button>
      </div>
    );
    if (s.type === "line_items") {
      if (items === null) return (
        <div className="btnrow" style={{ flexWrap: "wrap" }}>
          <button className="btn" onClick={prefillItems}>Pre-fill from the test{lowWater ? ` (dosing ${doseGallons.toLocaleString()} gal — low water)` : ""}</button>
          <button className="btn ghost" onClick={() => finishItems([])}>No adjustment needed</button>
        </div>
      );
      return (
        <div>
          {lowWater && (
            <div className="notice" style={{ marginBottom: 8, borderColor: "#F2C063", background: "#FDF6E7" }}>
              <b>How far down is the water?</b> Doses calculate at the reduced volume — never the rated {gallons.toLocaleString()} gal.
              <div className="btnrow" style={{ marginTop: 6, flexWrap: "wrap" }}>
                {[2, 4, 6, 8, 10, 12].map((n) => (
                  <button key={n} type="button" className="btn ghost mini" data-on={inchesDown === n ? 1 : 0}
                    style={inchesDown === n ? { background: "#0B6FBE", color: "#fff" } : {}}
                    onClick={() => setInchesDown(n)}>{n}"{n === 12 ? "+" : ""}</button>
                ))}
              </div>
              <div className="sub" style={{ marginTop: 4 }}>Dosing {doseGallons.toLocaleString()} gal ({Math.round(lowWaterFactor(inchesDown) * 100)}% of rated).</div>
            </div>
          )}
          {items.map((it, i) => (
            <div key={it.id} className="visit" style={{ marginBottom: 6, padding: 8 }}>
              <input value={it.desc} style={{ width: "100%", marginBottom: 6 }} onChange={(e) => setItems(items.map((x, xi) => (xi === i ? { ...x, desc: e.target.value } : x)))} />
              <div className="btnrow" style={{ flexWrap: "wrap", alignItems: "center" }}>
                <input value={it.qty} inputMode="decimal" style={{ width: 60 }} onChange={(e) => setItems(items.map((x, xi) => (xi === i ? { ...x, qty: e.target.value } : x)))} />
                <div className="seg">
                  <button type="button" data-on={it.src === "truck" ? 1 : 0} onClick={() => setItems(items.map((x, xi) => (xi === i ? { ...x, src: "truck" } : x)))}>Truck stock</button>
                  <button type="button" data-on={it.src === "customer" ? 1 : 0} onClick={() => setItems(items.map((x, xi) => (xi === i ? { ...x, src: "customer" } : x)))}>Customer's own</button>
                </div>
                <input value={it.code} placeholder="part # (for truck stock)" style={{ width: 130 }} onChange={(e) => setItems(items.map((x, xi) => (xi === i ? { ...x, code: e.target.value } : x)))} />
                <button className="btn ghost mini" onClick={() => setItems(items.filter((_, xi) => xi !== i))}>✕</button>
              </div>
            </div>
          ))}
          <div className="btnrow" style={{ flexWrap: "wrap" }}>
            <button className="btn ghost mini" onClick={() => setItems([...items, { id: cid(), desc: "", qty: "1", src: "truck", code: "" }])}>+ Add line</button>
            <button className="btn" onClick={() => finishItems(items)}>Done — save adjustment</button>
          </div>
          <div className="sub" style={{ marginTop: 4 }}>Truck items bill as materials and come off truck stock; the customer's own don't.</div>
        </div>
      );
    }
    return null;
  };

  const complete = wfComplete(jobType, results);
  const closeDraft = { closeNotes, followReason: closeReason };
  const billedChems = !!(items && items.some((it) => it.src === "truck"));
  const closeGate = serviceCloseGate(closeDraft, { ...chemDraft, billingChems: billedChems });
  const finishClose = () => {
    if (!complete || !closeGate.ok) return;
    const draft = { closeNotes: String(closeNotes).trim(), followReason: closeReason };
    const chem = chemCheckDraft({ ...chemDraft, billingChems: billedChems || chemDraft.usingNew });
    onPatchWf({ jobTypeId: jobType.id, results, inchesDown: lowWater ? inchesDown : 0,
      startedAt: (results.arrive && results.arrive.at) || (job.wf || {}).startedAt || "",
      closeNotes: draft.closeNotes, followReason: draft.followReason,
      chemCheck: chem.check, usingNewChems: chem.usingNew, chemWhyNew: chem.whyNew, chemWhyOther: chem.whyOther,
      chemPhoto: chem.chemPhoto, storagePhoto: chem.storagePhoto });
    onFinish && onFinish(draft);
  };
  return (
    <div className="overlay">
      <div className="sheet" style={{ maxWidth: 560, maxHeight: "94vh", overflowY: "auto" }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>{jobType.name}</strong>
            <div className="sub">{client ? client.name : ""} · step {Math.min(doneCount + 1, visCount)} of {visCount}</div>
          </div>
          <div className="btnrow">
            {!complete && <button className="btn ghost" onClick={() => setClosing(true)}>Leave</button>}
            {complete && <button className="btn" disabled={!closeGate.ok} title={closeGate.ok ? "" : "Add notes and a follow-up reason first."} onClick={finishClose}>Close work order ✓</button>}
          </div>
        </div>
        <div className="pad">
          {tests3 && tests3.length > 0 && (
            <div className="panel" style={{ padding: 8, marginBottom: 10, overflowX: "auto" }}>
              <div className="sub" style={{ marginBottom: 4 }}>Last {tests3.length} test{tests3.length === 1 ? "" : "s"} — newest first, with what was dosed</div>
              <table style={{ fontSize: 12, borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["When", "Type", "FC", "pH", "TA", "CYA", "Dosed"].map((h) => <th key={h} style={{ textAlign: "left", padding: "2px 6px", color: "#5A6E86" }}>{h}</th>)}</tr></thead>
                <tbody>{tests3.map((t) => (
                  <tr key={t.id}>
                    <td style={{ padding: "2px 6px", whiteSpace: "nowrap" }}>{t.date}</td>
                    <td style={{ padding: "2px 6px" }}><span className="doc" style={{ background: testSide(t) === "shop" ? "#7A4FB6" : "#0E7A3C" }}>{testSide(t) === "shop" ? "SHOP" : "SERVICE"}</span></td>
                    <td style={{ padding: "2px 6px" }}>{(t.results || {}).fc || "—"}</td>
                    <td style={{ padding: "2px 6px" }}>{(t.results || {}).ph || "—"}</td>
                    <td style={{ padding: "2px 6px" }}>{(t.results || {}).ta || "—"}</td>
                    <td style={{ padding: "2px 6px" }}>{(t.results || {}).cya || "—"}</td>
                    <td style={{ padding: "2px 6px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.dosedSummary || "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {steps.filter((s) => !s.hidden && !s.skipped).map((s) => (
            <div key={s.id} className="visit" style={{ marginBottom: 8, borderLeft: `4px solid ${s.satisfied ? "#12A650" : cur && cur.id === s.id ? "#0B6FBE" : "#D8E4F1"}`, opacity: s.satisfied || (cur && cur.id === s.id) ? 1 : 0.55 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span style={{ fontSize: 16 }}>{s.satisfied ? "✅" : cur && cur.id === s.id ? "▶️" : "•"}</span>
                <div style={{ flex: 1 }}>
                  <b style={{ fontSize: 15 }}>{s.label}</b>
                  {s.help && cur && cur.id === s.id && <div className="sub">{s.help}</div>}
                  {s.satisfied && results[s.id] && (results[s.id].v === false) && <div className="sub" style={{ color: "#B36B00" }}>Marked as a problem</div>}
                  {s.satisfied && s.multi && !complete && (
                    <label className="btn ghost mini" style={{ display: "inline-flex", marginTop: 6 }}>
                      📷 Add another ({((results[s.id] || {}).media || []).length} so far)
                      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={snapPhoto(s)} />
                    </label>
                  )}
                  {cur && cur.id === s.id && <div style={{ marginTop: 8 }}>{stepBody(s)}</div>}
                </div>
              </div>
            </div>
          ))}

          {(steps.some((s) => s.skipped) || wfDeferred(jobType, results).length > 0) && (
            <div className="notice" style={{ borderColor: "#F2C063", background: "#FDF6E7" }}>
              System startup is deferred (low water) — a return-visit work order goes to the office automatically when you close. Finish the rest in full.
            </div>
          )}

          {complete && (
            <div className="notice" style={{ marginTop: 10, borderColor: "#0B6FBE", background: "#F3F8FD" }}>
              <b>Close this work order</b>
              <div className="sub">Notes and a follow-up reason are required before this order can close.</div>
              <label className="sub" style={{ display: "block", marginTop: 8, fontWeight: 700 }}>Notes</label>
              <textarea rows={3} value={closeNotes} placeholder="What was done, what's left, anything the office or next tech should know"
                onChange={(e) => setCloseNotes(e.target.value)} />
              <label className="sub" style={{ display: "block", marginTop: 8, fontWeight: 700 }}>Follow-up reason</label>
              <select value={closeReason} onChange={(e) => setCloseReason(e.target.value)}>
                <option value="">Pick a follow-up reason…</option>
                {SERVICE_FOLLOW_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <ChemCheckPanel value={chemDraft} billingChems={billedChems}
                onChange={setChemDraft}
                onChemPhoto={async (f) => {
                  let dataUrl;
                  if (f.type && f.type.startsWith("video/")) dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
                  else dataUrl = await shrink(f, 1100, 0.8);
                  const id = await onAddPhoto(dataUrl, `WO ${job.workOrder || ""} — Billed chemicals`);
                  if (id) setChemDraft((d) => ({ ...d, chemPhoto: id }));
                }}
                onStoragePhoto={async (f) => {
                  let dataUrl;
                  if (f.type && f.type.startsWith("video/")) dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
                  else dataUrl = await shrink(f, 1100, 0.8);
                  const id = await onAddPhoto(dataUrl, `WO ${job.workOrder || ""} — Chemical storage`);
                  if (id) setChemDraft((d) => ({ ...d, storagePhoto: id }));
                }} />
              {closeGate.missing.length > 0 && (
                <div className="sub" style={{ marginTop: 6, color: "#B36B00" }}>Still needed: {closeGate.missing.map((m) => m.label).join(", ")}.</div>
              )}
              <div className="btnrow" style={{ marginTop: 8 }}>
                <button className="btn" disabled={!closeGate.ok} onClick={finishClose}>Close work order ✓</button>
              </div>
            </div>
          )}

          {closing && !complete && (
            <div className="notice" style={{ marginTop: 10, borderColor: "#E8B4B4", background: "#FDF2F2" }}>
              <b>Leaving without the final photo?</b> This work order will have no finish time. Say why — the office sees it.
              <textarea rows={2} style={{ marginTop: 6 }} value={incReason} placeholder="e.g. customer sent us away / weather / ran out of day" onChange={(e) => setIncReason(e.target.value)} />
              <div className="btnrow" style={{ marginTop: 6 }}>
                <button className="btn" disabled={!incReason.trim()} onClick={() => onIncomplete(incReason.trim())}>Leave — mark incomplete</button>
                <button className="btn ghost" onClick={() => { setClosing(false); onClose(); }}>Just close this screen (resume later)</button>
                <button className="btn ghost" onClick={() => setClosing(false)}>Stay</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MyDayView({ me, clients, techs, jobs, serviceTypes, visits, tests, photos, saveVisits, saveTests, savePhotos, onSaveJobs, onOpen, onPick: onPickRaw, parts = [], onConsumeParts, onTransfer, fieldOnly, jobTypes = [], specialOrders = [], onWoClose, jumpJobId, onJumpDone, onOnMyWay, onAutoReport, onAddFollowup, onDamageFlag }) {
  const [runJob, setRunJob] = useState(null);
  // Arriving from the schedule's "Open guided WO" button: jump straight into the runner.
  useEffect(() => {
    if (!jumpJobId) return;
    const j = (jobs || []).find((x) => x.id === jumpJobId);
    if (j) { if (j.techId) setTechId(j.techId); setRunJob(j); }
    onJumpDone && onJumpDone();
  }, [jumpJobId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPick = fieldOnly ? () => {} : (onPickRaw || (() => {}));
  const myTech = techs.find((t) => t.staffId === (me && me.id));
  const [techId, setTechId] = useState(myTech ? myTech.id : (techs[0] ? techs[0].id : ""));
  const [openId, setOpenId] = useState(null);   // client stop currently open
  const [openJob, setOpenJob] = useState(null); // job attached to the open stop (if any)
  // checks[i] = {s:"done"|"na"|"issue", why?, tabs?} — every item must be
  // actioned before the stop can complete; N/A carries a reason to the office.
  const [checks, setChecks] = useState({});
  const [reading, setReading] = useState({});
  const [readingSaved, setReadingSaved] = useState(false);
  const [doneMap, setDoneMap] = useState({}); // clientId -> "done" | "skipped" (session view; visits are the record)
  const beforeRef = useRef(null); const afterRef = useRef(null);
  const checklistRef = useRef(null); const waterRef = useRef(null); const photoSecRef = useRef(null);
  const [beforeAt, setBeforeAt] = useState(null); // arrival photo time — starts the clock, unlocks the work
  const [afterAt, setAfterAt] = useState(null);   // finish photo time
  const [photoIds, setPhotoIds] = useState([]);   // photos taken ON THIS STOP — the visit owns them
  const [damage, setDamage] = useState([]);       // damage reports filed on this stop
  const [dmg, setDmg] = useState(null);           // open damage-report form {itemIdx, what, severity, area}
  const [chemDraft, setChemDraft] = useState(() => chemCheckDraft({}));

  const today = fmtDate(new Date());
  const tech = techs.find((t) => t.id === techId);
  const stops = clients
    .filter((c) => c.type !== "retail" && !c.trade && c.techId === techId && dueOnDate(c, new Date()))
    .sort((a, b) => Number(a.order) - Number(b.order) || a.name.localeCompare(b.name));
  const myJobs = (jobs || []).filter((j) => j.date === today && j.techId === techId);
  const jobFor = (clientId) => myJobs.find((j) => j.clientId === clientId && !j.done);

  // Merge persisted state: a visit dated today marks a stop done/skipped even after reload.
  const statusOf = (c) => {
    if (doneMap[c.id]) return doneMap[c.id];
    const v = (visits[c.id] || []).find((x) => x.date === today);
    if (v) return String(v.notes || "").startsWith("Skipped") ? "skipped" : "done";
    return "todo";
  };
  const jobCards = myJobs.filter((j) => j.clientId && !stops.some((c) => c.id === j.clientId));
  const items = [
    ...stops.map((c) => ({ kind: "stop", c, job: jobFor(c.id) })),
    ...jobCards.map((j) => ({ kind: "job", c: clients.find((c) => c.id === j.clientId), job: j })).filter((x) => x.c),
  ];
  const doneCount = items.filter((x) => (x.job && x.job.done) || statusOf(x.c) !== "todo").length;

  // ---- Morning Pre-Flight: every part today's jobs need, split truck vs shop ----
  const partByCode = (code) => parts.find((p) => (p.code || "").trim().toLowerCase() === String(code || "").trim().toLowerCase());
  const needMap = {};
  myJobs.filter((j) => !j.done).forEach((j) => (j.parts || []).forEach((p) => {
    const k = String(p.code || "").trim().toLowerCase(); if (!k) return;
    if (!needMap[k]) needMap[k] = { code: p.code, name: p.name || p.code, qty: 0 };
    needMap[k].qty += Math.max(1, num(p.qty) || 1);
  }));
  const needList = Object.values(needMap).map((n) => {
    const part = partByCode(n.code);
    const onTruck = part ? num((part.trucks || {})[techId]) : 0;
    return { ...n, part, onTruck, shopQty: part ? num(part.qty) : 0, short: Math.max(0, n.qty - onTruck) };
  });
  const pfReady = needList.filter((x) => x.short === 0);
  const pfMissing = needList.filter((x) => x.short > 0);
  const pfKey = techId + "|" + today;
  const [pfChecked, setPfChecked] = useState(() => new Set(PREFLIGHT.get(pfKey) || []));
  const [pfSer, setPfSer] = useState({}); // serials typed for warranty items before loading
  const [pfFlagged, setPfFlagged] = useState(false);
  useEffect(() => { setPfChecked(new Set(PREFLIGHT.get(techId + "|" + today) || [])); setPfFlagged(false); }, [techId, today]);
  const pfToggle = (code) => setPfChecked((s) => {
    const n = new Set(s);
    if (n.has(code)) { n.delete(code); n.delete("__ready"); } else n.add(code);
    PREFLIGHT.set(pfKey, n);
    return n;
  });
  const daily = dailyChecklistFor(myJobs.filter((j) => !j.done), stops, { clients, specialOrders, jobTypes });
  const leaveMissing = leaveGate({ checked: pfChecked, standard: daily.standard, extras: daily.extras, partsReady: pfReady, partsMissing: pfMissing });
  const leaveReady = pfChecked.has("__ready");
  const preflightOk = leaveMissing.length === 0 && leaveReady;
  const [pfMsg, setPfMsg] = useState("");
  const [pfScanBusy, setPfScanBusy] = useState(false);
  const [pfScanMsg, setPfScanMsg] = useState("");
  const pfLoadRef = useRef(null);
  // Photo of what's being loaded → AI matches against the missing list → shop→truck transfer.
  const scanLoad = async (file) => {
    if (!file || !pfMissing.length || !onTransfer) return;
    setPfScanBusy(true); setPfScanMsg("");
    try {
      const image = await shrink(file, 1400, 0.8);
      const catalogue = pfMissing.map((x) => ({ code: x.code, name: x.name, category: (x.part || {}).category, subcategory: (x.part || {}).subcategory }));
      const { data: res, error } = await supabase.functions.invoke("vision-scan", { body: { image, parts: catalogue } });
      if (error) throw error;
      let moved = 0, blocked = 0;
      for (const i of res.items || []) {
        const m = pfMissing.find((x) => String(x.code).toLowerCase() === String(i.code || "").toLowerCase());
        if (!m || !m.part) continue;
        const qty = Math.min(m.short, Math.max(1, num(i.qty) || 1));
        if (onTransfer(m.part.id, "shop", techId, qty)) moved += 1; else blocked += 1;
      }
      setPfScanMsg(!moved && !blocked
        ? "Couldn't match anything in that photo to the missing items — use the Move button beside the item instead."
        : `${moved} item${moved === 1 ? "" : "s"} moved to your truck${blocked ? ` · ${blocked} had no shop stock to move` : ""}.`);
    } catch { setPfScanMsg("The scan didn't go through — check the connection and try again."); }
    setPfScanBusy(false);
  };

  const open = openId ? clients.find((c) => c.id === openId) : null;
  const [chems, setChems] = useState([]);     // scanned chemicals to bill on this stop
  const [scanBusy, setScanBusy] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const [sig, setSig] = useState("");
  const [sigOpen, setSigOpen] = useState(false);
  const [, forceTick] = useState(0);
  // The on-site clock runs from the ARRIVAL PHOTO, not from opening the screen.
  useEffect(() => { if (!beforeAt) return; const t = setInterval(() => forceTick((x) => x + 1), 30000); return () => clearInterval(t); }, [beforeAt]);
  const draftKey = (clientId) => clientId + "|" + today;
  const refuseRoute = () => {
    setPfFlagged(true);
    setPfMsg(leaveMissing.length
      ? "Can't leave yet — check off every required item and tap Ready to Leave."
      : "Tap Ready to Leave before you start the route.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const markReadyToLeave = () => {
    if (leaveMissing.length) { refuseRoute(); return; }
    setPfChecked((s) => {
      const n = new Set(s);
      n.add("__ready");
      PREFLIGHT.set(pfKey, n);
      return n;
    });
    setPfFlagged(false);
    setPfMsg("");
  };
  const startGuided = (job) => {
    if (!preflightOk) { refuseRoute(); return; }
    setRunJob(job);
  };
  const openStop = (c, job) => {
    if (!preflightOk) { refuseRoute(); return; }
    onOpen(c.id); // lazy-loads visits/tests/photos for this client
    const d = STOP_DRAFTS.get(draftKey(c.id)) || {};
    setOpenId(c.id); setOpenJob(job || null);
    setChecks(d.checks || {}); setReading(d.reading || {}); setReadingSaved(!!d.readingSaved);
    setBeforeAt(d.beforeAt || null); setAfterAt(d.afterAt || null);
    setPhotoIds(d.photoIds || []); setDamage(d.damage || []); setDmg(null);
    setSig(d.sig || ""); setSigOpen(false); setChems(d.chems || []); setScanMsg("");
    setChemDraft(chemCheckDraft(d.chemDraft || {}));
  };
  const closeStop = () => { setOpenId(null); setOpenJob(null); setBeforeAt(null); setAfterAt(null); setPhotoIds([]); setDamage([]); setDmg(null); setSig(""); setSigOpen(false); setChems([]); setScanMsg(""); setChemDraft(chemCheckDraft({})); };
  // Everything in-progress persists as a draft: Back, a reload, or switching
  // stops loses nothing — reopening the stop picks up exactly where it left off.
  useEffect(() => {
    if (!openId) return;
    STOP_DRAFTS.set(draftKey(openId), { checks, reading, readingSaved, beforeAt, afterAt, photoIds, damage, sig, chems, chemDraft });
  }, [openId, checks, reading, readingSaved, beforeAt, afterAt, photoIds, damage, sig, chems, chemDraft]); // eslint-disable-line react-hooks/exhaustive-deps
  // Browser/hardware Back inside My Day: close the open sub-screen, never the
  // site. Mid-stop it confirms first; the draft keeps every bit of progress.
  useEffect(() => {
    if (!openId && !runJob) return;
    const h = () => {
      if (runJob) { setRunJob(null); return; } // the runner autosaves every step
      if (window.confirm("Leave this stop and go back to your day? Your progress is saved — reopen the stop to pick up where you left off.")) closeStop();
    };
    BACK_HANDLERS.push(h);
    return () => { const i = BACK_HANDLERS.indexOf(h); if (i >= 0) BACK_HANDLERS.splice(i, 1); };
  }, [openId, runJob]); // eslint-disable-line react-hooks/exhaustive-deps

  // Photograph the chemicals used → Claude vision suggests catalogue items → tech confirms.
  const scanChems = async (file) => {
    if (!file || !open) return;
    setScanBusy(true); setScanMsg("");
    try {
      const image = await shrink(file, 1400, 0.8);
      const catalogue = (parts || []).filter((p) => p.active !== false && p.code)
        .map((p) => ({ code: p.code, name: p.name, category: p.category, subcategory: p.subcategory }));
      const { data: res, error } = await supabase.functions.invoke("vision-scan", { body: { image, parts: catalogue } });
      if (error) throw error;
      const found = (res.items || []).map((i) => {
        const p = parts.find((x) => (x.code || "").toLowerCase() === String(i.code || "").toLowerCase());
        if (!p) return null;
        const sp = spOf(open, p.code); // customer's special price beats the list price
        return { code: p.code, desc: p.name, qty: num(i.qty) || 1, price: sp ? sp.price : (p.price || ""), confidence: i.confidence };
      }).filter(Boolean);
      if (!found.length) setScanMsg("Couldn't match anything in that photo to the catalogue — add items by hand or try a clearer shot.");
      setChems((c) => {
        let next = [...c];
        for (const f of found) {
          const j = next.findIndex((x) => x.code === f.code);
          if (j >= 0) next = next.map((x, k) => (k === j ? { ...x, qty: num(x.qty) + num(f.qty) } : x));
          else next.push(f);
        }
        return next;
      });
    } catch { setScanMsg("The scan didn't go through — check the connection and try again."); }
    setScanBusy(false);
  };
  const bumpChem = (code, d) => setChems((c) => c.map((x) => (x.code === code ? { ...x, qty: Math.max(1, num(x.qty) + d) } : x)).filter((x) => x.qty > 0));
  const dropChem = (code) => setChems((c) => c.filter((x) => x.code !== code));
  const chemTotal = chems.reduce((a, x) => a + num(x.qty) * num(x.price), 0);

  // Photos attach to THIS visit (tag "visit"), not to the property pile —
  // returns the new photo's id so the visit record can own it.
  const addPhoto = async (label, file) => {
    if (!file || !open) return null;
    const dataUrl = await shrink(file, 1400, 0.72);
    const id = cid();
    const next = [...(photos[open.id] || []), { id, tag: "visit", caption: `${label} — ${today}${me ? " · " + me.name : ""}${openJob && openJob.serviceType ? " · " + openJob.serviceType : ""}`, dataUrl, added: today }];
    await savePhotos(open.id, next);
    return id;
  };
  const stopShots = open ? (photos[open.id] || []).filter((p) => photoIds.includes(p.id)) : [];
  // Read-only reference: the last 3 visits' photos (same pattern as the last 3
  // water tests) so a tech can compare without hundreds piling onto the screen.
  const prevVisits = open ? (visits[open.id] || []).filter((v) => v.date && v.date !== today).sort((a, b) => String(b.date).localeCompare(String(a.date))).slice(0, 3)
    .map((v) => ({ v, shots: (photos[open.id] || []).filter((p) => (v.photoIds || []).includes(p.id) || (!v.photoIds && p.added === v.date)) })) : [];

  const saveReading = () => {
    if (!open) return;
    const results = Object.fromEntries(Object.entries(reading).filter(([, v]) => String(v).trim() !== ""));
    if (!Object.keys(results).length) return;
    if (!waterTestSaveGate({ clientId: open.id }, open).ok) return;
    saveTests(open.id, [...(tests[open.id] || []), { id: cid(), date: today, source: "manual", where: "onsite", notes: "Field reading" + (me ? " — " + me.name : ""), results, clientId: open.id }]);
    setReadingSaved(true);
  };

  const list = checklistFor(openJob, serviceTypes, open);
  const checkedCount = list.filter((_, i) => checks[i] && checks[i].s).length;
  const doneCountList = list.filter((_, i) => checks[i] && checks[i].s === "done").length;
  const missing = stopGate({ hasBefore: !!beforeAt, hasAfter: !!afterAt, checks, listLen: list.length, readingSaved,
    chemCheck: { ...chemDraft, billingChems: chems.length > 0 } });
  const checklistDone = list.every((_, i) => checks[i] && checks[i].s);
  const afterUnlocked = !!beforeAt && checklistDone && readingSaved;
  const isMgr = me && ["owner", "manager"].includes(me.roleId);

  // A skipped stop is NOT a completion: it records the reason, stays visible as
  // skipped, and routes back to the service desk as an unfinished call.
  const finishStop = (skipped, override) => {
    if (!open) return;
    if (!skipped && !override) {
      const g = chemCheckGate({ ...chemDraft, billingChems: chems.length > 0 });
      if (!g.ok) return;
    }
    let notes;
    if (skipped) {
      const why = window.prompt("Why is this stop being skipped?", "No access — gate locked") || "no reason given";
      notes = `Skipped — ${why}`;
      if (onAddFollowup) onAddFollowup(open.id, `Unfinished call — ${open.name}${openJob ? ` (${openJob.serviceType || JOB_LABEL[openJob.kind] || "job"})` : ""} skipped by ${me ? me.name : "tech"}: ${why}. Needs rebooking.`);
    } else {
      const naItems = list.map((it, i) => ({ it, c: checks[i] })).filter((x) => x.c && x.c.s === "na");
      const issueItems = list.map((it, i) => ({ it, c: checks[i] })).filter((x) => x.c && x.c.s === "issue");
      const tabItem = list.map((it, i) => ({ it, c: checks[i] })).find((x) => x.c && x.c.tabs);
      notes = `Field visit — ${doneCountList}/${list.length} checklist items done` +
        (openJob ? ` · ${openJob.serviceType || JOB_LABEL[openJob.kind] || "job"}` : "") +
        (tabItem ? `. Tabs added: ${tabItem.c.tabs}` : "") +
        (naItems.length ? `. N/A: ${naItems.map((x) => `${x.it} (${x.c.why || "no reason"})`).join("; ")}` : "") +
        (issueItems.length ? `. Issues reported: ${issueItems.map((x) => x.it).join("; ")}` : "") +
        (damage.length ? `. DAMAGE: ${damage.map((d) => `${d.severity === "urgent" ? "URGENT — " : ""}${d.what}${d.area ? ` (${d.area})` : ""}`).join("; ")}` : "");
      if (override) notes += `. MANAGER OVERRIDE by ${me ? me.name : "?"} — ${override}`;
    }
    const mins = beforeAt ? Math.max(1, Math.round((Date.now() - beforeAt) / 60000)) : null;
    const billChems = !skipped && chems.length > 0;
    if (billChems) notes += `. Chemicals: ${chems.map((x) => `${x.qty}× ${x.desc}`).join("; ")}`;
    const v = { id: cid(), date: today, workOrder: (openJob && openJob.workOrder) || "", invoiceNo: "",
      amount: billChems ? (chemTotal * hstFactorFor(open)).toFixed(2) : "",
      materials: billChems ? chems.map((x) => ({ code: x.code, desc: x.desc, qty: String(x.qty), price: String(x.price || "") })) : undefined,
      matPrice: billChems ? chemTotal.toFixed(2) : undefined,
      termIds: (openJob && openJob.termIds) || [], termsNote: (openJob && openJob.termsNote) || "",
      by: me ? me.name : "", notes, paid: billChems ? false : true, side: "service", repairType: "", gifted: !!open.giftedService,
      startAt: beforeAt ? new Date(beforeAt).toISOString() : "", minutes: mins, signature: sig || "",
      photoIds: photoIds.length ? photoIds : undefined,
      damage: damage.length ? damage : undefined,
      override: override ? { by: me ? me.name : "", reason: override } : undefined,
      chemCheck: skipped ? undefined : chemCheckDraft({ ...chemDraft, billingChems: billChems }) };
    if (billChems && onConsumeParts) onConsumeParts(techId, chems.map((x) => ({ code: x.code, qty: x.qty })));
    const nextVisits = [...(visits[open.id] || []), v];
    saveVisits(open.id, nextVisits);
    // Retention (owner's rule): completing a stop trims this property's visit
    // photos down to the last 4 visits — damage and reference photos stay.
    const prunedShots = pruneVisitPhotos(photos[open.id] || [], nextVisits);
    if (prunedShots.length !== (photos[open.id] || []).length) savePhotos(open.id, prunedShots);
    if (!skipped && onAutoReport) onAutoReport(open.id, v); // switch-gated in App; audit lives in email_log
    if (openJob && !skipped) onSaveJobs((jobs || []).map((j) => (j.id === openJob.id ? { ...j, done: true, doneAt: new Date().toISOString() } : j)));
    setDoneMap((m) => ({ ...m, [open.id]: skipped ? "skipped" : "done" }));
    STOP_DRAFTS.delete(draftKey(open.id));
    closeStop();
  };
  // File a damage report: what + severity + area + a mandatory photo. It lands
  // on the visit, pushes a follow-up to the service desk, and an URGENT report
  // raises a flag on the customer account.
  const fileDamage = async (file) => {
    if (!dmg || !open) return;
    if (!String(dmg.what || "").trim()) { alert("Say what the issue is — one line is fine."); return; }
    if (!file) { alert("A photo is required on a damage report."); return; }
    const sevLabel = { monitor: "monitor", repair: "needs repair", urgent: "URGENT — pool not serviceable" }[dmg.severity] || dmg.severity;
    const pid = await addPhoto(`Damage — ${sevLabel}`, file);
    if (pid) setPhotoIds((ids) => [...ids, pid]);
    const rep = { id: cid(), what: dmg.what.trim(), severity: dmg.severity, area: dmg.area || "", photoId: pid || "", by: me ? me.name : "", at: new Date().toISOString() };
    setDamage((ds) => [...ds, rep]);
    if (dmg.itemIdx !== undefined && dmg.itemIdx !== null) setChecks((ch) => ({ ...ch, [dmg.itemIdx]: { s: "issue" } }));
    if (onAddFollowup) onAddFollowup(open.id, `DAMAGE (${sevLabel}) — ${open.name}: ${rep.what}${rep.area ? ` · ${rep.area}` : ""}. Filed by ${me ? me.name : "tech"} on today's visit. Create a quote if a repair is needed.`);
    if (rep.severity === "urgent" && onDamageFlag) onDamageFlag(open.id, `URGENT damage reported ${today}: ${rep.what}${rep.area ? ` (${rep.area})` : ""} — pool may not be serviceable.`);
    setDmg(null);
  };

  /* ---- stop detail ---- */
  if (open) {
    const k0 = (open.contacts || [])[0] || {};
    const mapsUrl = open.address ? `https://maps.google.com/?q=${encodeURIComponent(open.address)}` : null;
    return (
      <div className="myday">
        <div className="bar" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button className="btn ghost mini" onClick={closeStop}>‹</button>
            <div>
              <strong style={{ fontSize: 17 }}>{open.name}</strong>
              <div className="sub">{openJob ? (openJob.serviceType || JOB_LABEL[openJob.kind] || "Job") + (openJob.workOrder ? " · " + openJob.workOrder : "") : "Routine service visit"}</div>
            </div>
          </div>
          {!fieldOnly && <button className="btn ghost mini" onClick={() => onPick(open.id)}>Full account</button>}
        </div>

        <div className="mdinfo">
          {open.address && <a className="mdaddr" href={mapsUrl} target="_blank" rel="noreferrer">📍 {open.address}</a>}
          <div className="mdchips">
            {open.gateCode && <span className="mdchip">Gate <b className="mono">{open.gateCode}</b></span>}
            {open.dog && <span className="mdchip warn">🐕 Dog on property</span>}
            {open.keyLocation && <span className="mdchip">Key: {open.keyLocation}</span>}
            {k0.phone && <a className="mdchip" href={`tel:${k0.phone.replace(/[^0-9+]/g, "")}`}>📞 {k0.name || "Call"}</a>}
          </div>
          {open.accessNotes && <div className="sub" style={{ marginTop: 6 }}>{open.accessNotes}</div>}
          {openJob && openJob.note && <div className="notice" style={{ marginTop: 8 }}>{openJob.note}</div>}
        </div>

        <div className="sechead" ref={photoSecRef}>Arrival & finish photos</div>
        <div className="btnrow">
          <input ref={beforeRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={async (e) => { const f = e.target.files && e.target.files[0]; e.target.value = "";
              if (!f) return; const pid = await addPhoto("Arrival", f);
              if (pid) { setPhotoIds((ids) => [...ids, pid]); if (!beforeAt) setBeforeAt(Date.now()); } }} />
          <input ref={afterRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={async (e) => { const f = e.target.files && e.target.files[0]; e.target.value = "";
              if (!f) return; const pid = await addPhoto("After", f);
              if (pid) { setPhotoIds((ids) => [...ids, pid]); if (!afterAt) setAfterAt(Date.now()); } }} />
          <button className="btn" disabled={!!beforeAt} onClick={() => beforeRef.current && beforeRef.current.click()}>
            {beforeAt ? "✓ Arrival photo taken" : "📷 Arrival photo — starts the visit"}
          </button>
          <button className="btn ghost" disabled={!afterUnlocked || !!afterAt}
            title={afterUnlocked ? "The finish-point photo — stamps your completion time." : "Unlocks once the checklist and water test are done."}
            onClick={() => afterRef.current && afterRef.current.click()}>
            {afterAt ? "✓ After photo taken" : "📷 After photo — finishes the visit"}
          </button>
        </div>
        {!beforeAt && <div className="sub" style={{ marginTop: 6, fontWeight: 700 }}>Take the arrival photo to begin — it stamps your start time and unlocks the rest of the stop.</div>}
        {beforeAt && !afterAt && !afterUnlocked && <div className="sub" style={{ marginTop: 6 }}>The after photo unlocks once every checklist item is actioned and the water test is saved.</div>}
        {stopShots.length > 0 && (
          <div className="mdshots">
            {stopShots.map((p) => <img key={p.id} src={p.signedUrl || p.dataUrl} alt={p.caption} title={p.caption} />)}
          </div>
        )}
        {prevVisits.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary className="sub" style={{ cursor: "pointer", marginTop: 0 }}>Previous visits — photos for reference ({prevVisits.length})</summary>
            {prevVisits.map(({ v, shots }) => (
              <div key={v.id} style={{ marginTop: 7 }}>
                <div className="sub" style={{ marginTop: 0, fontWeight: 700 }}>{v.date}{v.workOrder ? ` · ${v.workOrder}` : ""}{v.by ? ` · ${v.by}` : ""}</div>
                {shots.length
                  ? <div className="mdshots">{shots.map((p) => <img key={p.id} src={p.signedUrl || p.dataUrl} alt={p.caption} title={p.caption} />)}</div>
                  : <div className="sub" style={{ marginTop: 2 }}>No photos on this visit.</div>}
              </div>
            ))}
          </details>
        )}

        <div ref={checklistRef} style={!beforeAt ? { opacity: 0.45, pointerEvents: "none" } : undefined}>
        <div className="sechead">Checklist <span style={{ textTransform: "none", letterSpacing: 0 }}>{checkedCount}/{list.length} actioned</span></div>
        {list.map((item, i) => { const c = checks[i]; return (
          <div key={i} className="mdcheck" data-on={c && c.s === "done" ? 1 : 0} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "default" }}>
            <span className="mdbox" role="button" tabIndex={0} title={c ? "Tap to clear" : ""}
              style={c && c.s !== "done" ? { background: c.s === "na" ? "#B7791F" : "#B3541E", borderColor: "transparent", color: "#fff" } : undefined}
              onClick={() => c && setChecks((ch) => { const n = { ...ch }; delete n[i]; return n; })}>
              {c ? (c.s === "done" ? "✓" : c.s === "na" ? "–" : "⚠") : ""}
            </span>
            <span style={{ flex: 1 }}>
              {item}
              {c && c.s === "na" && c.why ? <span className="sub" style={{ display: "block", marginTop: 1 }}>N/A — {c.why}</span> : null}
              {c && c.tabs ? <span className="sub" style={{ display: "block", marginTop: 1 }}>{c.tabs} tab{num(c.tabs) === 1 ? "" : "s"} added</span> : null}
            </span>
            {!(c && c.s) && <>
              <button type="button" className="btn mini" onClick={() => {
                let tabs;
                if (/record tabs/i.test(item)) {
                  const n = window.prompt("How many tabs went in the feeder?", "3");
                  if (n === null) return;
                  tabs = String(Math.max(0, Math.round(num(n)) || 0));
                  const part = (parts || []).find((p) => p.active !== false && /\btabs?\b|\bpucks?\b/i.test(String(p.name || "")));
                  if (part && num(tabs) > 0) setChems((cs) => {
                    const j = cs.findIndex((x) => x.code === part.code);
                    const sp = spOf(open, part.code);
                    const price = sp ? sp.price : (part.price || "");
                    return j >= 0 ? cs.map((x, k) => (k === j ? { ...x, qty: num(tabs) } : x)) : [...cs, { code: part.code, desc: part.name, qty: num(tabs), price }];
                  });
                }
                setChecks((ch) => ({ ...ch, [i]: { s: "done", ...(tabs !== undefined ? { tabs } : {}) } }));
              }}>✓ Done</button>
              <button type="button" className="btn ghost mini" onClick={() => {
                const why = window.prompt("Why doesn't this item apply today? (required — the office sees it)");
                if (why === null) return;
                if (!why.trim()) { alert("N/A needs a short reason."); return; }
                setChecks((ch) => ({ ...ch, [i]: { s: "na", why: why.trim() } }));
              }}>N/A</button>
              <button type="button" className="btn warn mini" title="Report damage or an issue on this item"
                onClick={() => setDmg({ itemIdx: i, what: "", severity: "repair", area: "" })}>⚠</button>
            </>}
          </div>
        ); })}
        <div className="btnrow" style={{ marginTop: 6 }}>
          <button type="button" className="btn ghost mini" onClick={() => setDmg({ itemIdx: null, what: "", severity: "repair", area: "" })}>⚠ Report damage / an issue</button>
        </div>
        </div>

        <div ref={waterRef} style={!beforeAt ? { opacity: 0.45, pointerEvents: "none" } : undefined}>
        <div className="sechead">Water reading <span style={{ textTransform: "none", letterSpacing: 0 }}>required</span></div>
        <div className="mdread">
          {WATER_PARAMS.filter((p) => ["fc", "tc", "ph", "ta", "cya", "temp"].includes(p.k) || (p.k === "salt" && isSaltPool(open))).map((p) => (
            <div key={p.k}>
              <label>{p.k === "temp" ? "Temp °F" : p.label}</label>
              <input inputMode="decimal" value={reading[p.k] || ""} placeholder={`${p.lo}–${p.hi}`}
                onChange={(e) => { setReading((r) => ({ ...r, [p.k]: e.target.value })); setReadingSaved(false); }} />
            </div>
          ))}
        </div>
        <ChemPanel client={open} results={reading} />
        <div className="btnrow" style={{ marginTop: 8 }}>
          <button className="btn ghost" onClick={saveReading} disabled={readingSaved || !Object.values(reading).some((v) => String(v).trim())}>
            {readingSaved ? "Reading saved ✓" : "Save reading"}
          </button>
        </div>

        <div className="sechead" style={{ marginTop: 16 }}>Chemicals used — scan to bill</div>
        <label className="btn ghost" style={{ cursor: "pointer", display: "inline-flex" }}>
          {scanBusy ? "Reading the photo…" : "📷 Scan chemicals used"}
          <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} disabled={scanBusy}
            onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) scanChems(f); e.target.value = ""; }} />
        </label>
        {scanMsg && <div className="sub" style={{ marginTop: 6, color: "var(--clay)", fontWeight: 600 }}>{scanMsg}</div>}
        {chems.map((x) => (
          <div key={x.code} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", borderBottom: "1px solid var(--line)" }}>
            <span style={{ flex: 1, fontWeight: 650, fontSize: 13.5 }}>
              {x.desc}{x.confidence === "low" && <span className="doc" style={{ background: "#B7791F", marginLeft: 7 }}>check me</span>}
            </span>
            <button type="button" className="btn ghost mini" onClick={() => bumpChem(x.code, -1)}>−</button>
            <b className="mono" style={{ minWidth: 22, textAlign: "center" }}>{x.qty}</b>
            <button type="button" className="btn ghost mini" onClick={() => bumpChem(x.code, 1)}>+</button>
            <span className="mono" style={{ minWidth: 64, textAlign: "right", fontSize: 12.5 }}>{num(x.price) ? "$" + (num(x.qty) * num(x.price)).toFixed(2) : "—"}</span>
            <button type="button" className="btn warn mini" onClick={() => dropChem(x.code)}>✕</button>
          </div>
        ))}
        {chems.length > 0 && (
          <div className="sub" style={{ marginTop: 7, fontWeight: 700 }}>
            Bills ${chemTotal.toFixed(2)} + {hstLabelFor(open)} = ${(chemTotal * hstFactorFor(open)).toFixed(2)} on this invoice when the stop is completed · stock comes off your truck first.
          </div>
        )}
        <ChemCheckPanel value={chemDraft} billingChems={chems.length > 0} onChange={setChemDraft}
          onChemPhoto={async (f) => { const pid = await addPhoto("Billed chemicals", f); if (pid) setChemDraft((d) => ({ ...d, chemPhoto: pid })); }}
          onStoragePhoto={async (f) => { const pid = await addPhoto("Chemical storage", f); if (pid) setChemDraft((d) => ({ ...d, storagePhoto: pid })); }} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <span className="doc" style={{ background: "#17356E", fontSize: 12 }}>
            ⏱ {beforeAt ? `On site ${Math.max(1, Math.round((Date.now() - beforeAt) / 60000))} min` : "Clock starts at the arrival photo"}
          </span>
          {sig
            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <img src={sig} alt="signature" style={{ height: 34, border: "1px solid var(--line)", borderRadius: 6, background: "#fff" }} />
                <span className="sub" style={{ marginTop: 0, fontWeight: 700, color: "#0E7A3C" }}>Signed ✓</span>
                <button type="button" className="btn ghost mini" onClick={() => { setSig(""); setSigOpen(true); }}>Redo</button>
              </span>
            : <button type="button" className="btn ghost mini" onClick={() => setSigOpen((x) => !x)}>✍️ Customer signature (optional)</button>}
        </div>
        {sigOpen && !sig && <SignPad onSave={(d) => { setSig(d); setSigOpen(false); }} onCancel={() => setSigOpen(false)} />}
        {missing.length > 0 && (
          <div className="notice" style={{ marginTop: 12, borderColor: "#E8B4B4", background: "#FDF2F2" }}>
            <b>Still needed before this stop can complete:</b>
            <div className="btnrow" style={{ marginTop: 6, flexWrap: "wrap" }}>
              {missing.map((m) => (
                <button key={m.k} type="button" className="btn ghost mini"
                  onClick={() => { const r = m.k === "checklist" ? checklistRef : m.k === "water" ? waterRef : photoSecRef; if (r.current) r.current.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mdfinish">
          <button className="btn" style={{ flex: 1, justifyContent: "center", fontSize: 16, padding: "14px" }}
            disabled={missing.length > 0}
            title={missing.length ? "Finish the items listed above first." : ""}
            onClick={() => finishStop(false)}>
            Complete stop ✓
          </button>
          <button className="btn warn" title="Skipping records a reason and sends the call back to the service desk for rebooking — it does not complete the stop." onClick={() => finishStop(true)}>Skip</button>
        </div>
        {missing.length > 0 && isMgr && (
          <div className="btnrow" style={{ marginTop: 8 }}>
            <button type="button" className="btn ghost" onClick={() => {
              const r = window.prompt("Manager override — why is this stop being force-completed without the requirements?");
              if (r && r.trim()) finishStop(false, r.trim());
            }}>Force complete (manager) — reason required, stamped on the visit</button>
          </div>
        )}

        {dmg && (
          <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") setDmg(null); }}>
            <div className="sheet" style={{ maxWidth: 480 }}>
              <h3 style={{ margin: "0 0 4px" }}>Report damage / an issue</h3>
              <div className="sub" style={{ marginTop: 0 }}>Goes straight to the service desk with your photo.{dmg.itemIdx !== null && dmg.itemIdx !== undefined ? ` Checklist item: ${list[dmg.itemIdx]}` : ""}</div>
              <label className="sub" style={{ display: "block", marginTop: 10, fontWeight: 700 }}>What's the issue?</label>
              <textarea rows={3} autoFocus value={dmg.what} placeholder="e.g. Crack in the skimmer throat, coping lifting near the steps…"
                onChange={(e) => setDmg((d) => ({ ...d, what: e.target.value }))} />
              <label className="sub" style={{ display: "block", marginTop: 8, fontWeight: 700 }}>How serious?</label>
              <select value={dmg.severity} onChange={(e) => setDmg((d) => ({ ...d, severity: e.target.value }))}>
                <option value="monitor">Monitor — keep an eye on it</option>
                <option value="repair">Needs repair</option>
                <option value="urgent">URGENT — pool not serviceable</option>
              </select>
              <label className="sub" style={{ display: "block", marginTop: 8, fontWeight: 700 }}>Equipment / area affected</label>
              <select value={dmg.area} onChange={(e) => setDmg((d) => ({ ...d, area: e.target.value }))}>
                <option value="">Pick one…</option>
                {(open.equipmentList || []).filter((eq) => eq.detail).map((eq) => (
                  <option key={eq.id} value={`${eq.kind}: ${eq.detail}`}>{eq.kind} — {String(eq.detail).slice(0, 40)}</option>
                ))}
                {FEATURES.map((f) => <option key={f.k} value={f.label}>{f.label}</option>)}
                <option value="Pool structure">Pool structure (walls, liner, coping, deck)</option>
                <option value="Cover">Cover</option>
                <option value="Other">Other</option>
              </select>
              <div className="btnrow" style={{ marginTop: 12 }}>
                <label className="btn" style={{ cursor: "pointer", display: "inline-flex" }}>
                  📷 Take the photo & file the report
                  <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) fileDamage(f); }} />
                </label>
                <button type="button" className="btn ghost" onClick={() => setDmg(null)}>Cancel</button>
              </div>
              <div className="sub" style={{ marginTop: 7 }}>A photo is required — no photo, no report.</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ---- day list ---- */
  return (
    <div className="myday">
      <div className="bar" style={{ marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 17 }}>My Day{tech ? " — " + tech.name : ""}</strong>
          <div className="sub">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {doneCount} of {items.length} done</div>
        </div>
        {(!myTech || techs.length > 1) && (
          <select value={techId} style={{ width: "auto" }} onChange={(e) => setTechId(e.target.value)}>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>
      {!isStandalone() && !localStorage.getItem("pm_a2hs_hide") && (
        <div className="notice" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            <b>Put Pool Master on your home screen</b> — it opens like an app, and the Back button always stays inside it.
            {!deferredInstall && " On iPhone: tap Share, then “Add to Home Screen”."}
          </span>
          {deferredInstall && (
            <button type="button" className="btn mini" onClick={async () => {
              try { deferredInstall.prompt(); await deferredInstall.userChoice; deferredInstall = null; forceTick((x) => x + 1); } catch { /* dismissed */ }
            }}>Install</button>
          )}
          <button type="button" className="btn ghost mini" onClick={() => { try { localStorage.setItem("pm_a2hs_hide", "1"); } catch {} forceTick((x) => x + 1); }}>Hide</button>
        </div>
      )}
      <div className="preflight" data-ok={preflightOk ? 1 : 0}>
          <div className="pfhead">
            <b>{preflightOk ? "✓ Ready to leave — checklist complete" : "Daily Technician Checklist — before you leave the shop"}</b>
            <span className="sub" style={{ marginTop: 0 }}>{leaveMissing.length ? `${leaveMissing.length} still needed` : (leaveReady ? "All required items checked" : "Check every item, then tap Ready to Leave")}</span>
          </div>
          {pfMsg && !preflightOk && <div className="alert" style={{ marginBottom: 9 }}>{pfMsg}</div>}

          <div className="pfsec">Standard daily kit</div>
          {daily.standard.map((it) => (
            <button type="button" key={it.id} className="pfrow pfcheck" data-on={pfChecked.has(it.id) ? 1 : 0}
              data-miss={pfFlagged && !pfChecked.has(it.id) ? 1 : 0} onClick={() => pfToggle(it.id)}>
              <span className="pfbox">{pfChecked.has(it.id) ? "✓" : ""}</span>
              <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <b style={{ fontSize: 13.5 }}>{it.label}</b>
              </div>
            </button>
          ))}

          {daily.extras.length > 0 && (<>
            <div className="pfsec">Special for today's stops</div>
            {daily.extras.map((it) => (
              <button type="button" key={it.id} className="pfrow pfcheck" data-on={pfChecked.has(it.id) ? 1 : 0} data-special="1"
                data-miss={pfFlagged && !pfChecked.has(it.id) ? 1 : 0} onClick={() => pfToggle(it.id)}>
                <span className="pfbox">{pfChecked.has(it.id) ? "✓" : ""}</span>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <b style={{ fontSize: 13.5 }}>{it.label}</b>
                  <div className="sub" style={{ marginTop: 0 }}>
                    <span className="pftag">Special</span>{it.stopName}{it.source === "notes" ? " · job notes" : it.source === "order" ? " · customer order" : ""}
                  </div>
                </div>
              </button>
            ))}
          </>)}

          {pfMissing.length > 0 && (<>
            <div className="pfsec">Pull from the main warehouse</div>
            {pfMissing.map((x) => (
              <div key={x.code} className="pfrow" data-miss="1">
                {x.part && x.part.image ? <img className="pfimg" src={x.part.image} alt="" /> : <span className="pfimg pfimg-blank" />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontSize: 13.5 }}>{x.name}</b>
                  <div className="sub" style={{ marginTop: 0 }}>
                    Need {x.qty} · {x.onTruck} on your truck · {x.part ? (x.shopQty > 0 ? `${x.shopQty} at the shop` : "none at the shop — see the office") : "not in the catalogue"}
                  </div>
                </div>
                {x.part && x.shopQty > 0 && onTransfer && (partHasWarranty(x.part) ? (
                  <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input value={pfSer[x.code] || ""} placeholder={Math.min(x.short, x.shopQty) > 1 ? "serials, comma-sep" : "serial # required"} style={{ width: 150 }}
                      onChange={(e) => setPfSer((m2) => ({ ...m2, [x.code]: e.target.value }))} />
                    <button type="button" className="btn mini" disabled={serialList(pfSer[x.code]).length !== Math.min(x.short, x.shopQty)}
                      onClick={() => { if (onTransfer(x.part.id, "shop", techId, Math.min(x.short, x.shopQty), serialList(pfSer[x.code]))) setPfSer((m2) => ({ ...m2, [x.code]: "" })); }}>
                      Move to truck
                    </button>
                  </span>
                ) : (
                  <button type="button" className="btn mini" onClick={() => onTransfer(x.part.id, "shop", techId, Math.min(x.short, x.shopQty))}>
                    Move {Math.min(x.short, x.shopQty)} to truck
                  </button>
                ))}
              </div>
            ))}
            <label className="btn" style={{ cursor: "pointer", display: "inline-flex", marginTop: 8 }}>
              {pfScanBusy ? "Reading the photo…" : "📷 Scan loaded items"}
              <input ref={pfLoadRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} disabled={pfScanBusy}
                onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) scanLoad(f); e.target.value = ""; }} />
            </label>
            {pfScanMsg && <div className="sub" style={{ marginTop: 6, fontWeight: 600 }}>{pfScanMsg}</div>}
          </>)}

          {pfReady.length > 0 && (<>
            <div className="pfsec">Already on your truck — tap to confirm</div>
            {pfReady.map((x) => (
              <button type="button" key={x.code} className="pfrow pfcheck" data-on={pfChecked.has(x.code) ? 1 : 0}
                data-miss={pfFlagged && !pfChecked.has(x.code) ? 1 : 0} onClick={() => pfToggle(x.code)}>
                <span className="pfbox">{pfChecked.has(x.code) ? "✓" : ""}</span>
                {x.part && x.part.image ? <img className="pfimg" src={x.part.image} alt="" /> : <span className="pfimg pfimg-blank" />}
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <b style={{ fontSize: 13.5 }}>{x.name}</b>
                  <div className="sub" style={{ marginTop: 0 }}>Need {x.qty} · {x.onTruck} on your truck</div>
                </div>
              </button>
            ))}
          </>)}

          <div className="btnrow" style={{ marginTop: 10 }}>
            <button type="button" className="btn" disabled={preflightOk} onClick={markReadyToLeave}>
              {preflightOk ? "✓ Ready to Leave" : "Ready to Leave"}
            </button>
            {!preflightOk && <span className="sub" style={{ marginTop: 0 }}>Required — the route stays locked until this is green.</span>}
          </div>
        </div>
      {items.length > 0 && (
        <div className="mdprog"><i style={{ width: `${items.length ? Math.round((doneCount / items.length) * 100) : 0}%` }} /></div>
      )}
      {items.length === 0 ? (
        <div className="empty">Nothing scheduled for {tech ? tech.name : "this tech"} today.</div>
      ) : items.map(({ c, job }) => {
        const st = job && job.done ? "done" : statusOf(c);
        const col = job && (((serviceTypes || []).find((s) => s.name === job.serviceType) || {}).color || job.color);
        return (
          <button key={(job && job.id) || c.id} type="button" className="mdcard" data-st={st}
            style={col ? { borderLeftColor: col } : undefined}
            onClick={() => st === "todo" ? (job && job.wfTypeId ? startGuided(job) : openStop(c, job)) : onPick(c.id)}>
            <span className="mdst" data-st={st}>{st === "done" ? "✓" : st === "skipped" ? "–" : (c.order || "•")}</span>
            <span className="mdbody">
              <b>{c.name}</b>
              <span>{job ? (job.serviceType || JOB_LABEL[job.kind] || "Job") : FREQ_LABEL[c.frequency] || "Service"}{c.address ? " · " + c.address : ""}</span>
              {job && job.wfTypeId && st === "todo" && <span style={{ color: "#0B6FBE", fontWeight: 700 }}>▶ Guided work order{job.wf && job.wf.results && Object.keys(job.wf.results).length ? " — resume" : ""}</span>}
            </span>
            <span className="mdmeta">
              {c.dog && <span title="Dog on property">🐕</span>}
              {c.gateCode && <span title="Gate code on file">🔒</span>}
              {st === "todo" && onOnMyWay && (
                <span role="button" tabIndex={0} className="doc" style={{ background: "#0B6FBE", cursor: "pointer" }}
                  title="Text the customer that you're heading over"
                  onClick={(e) => { e.stopPropagation(); onOnMyWay(c); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onOnMyWay(c); } }}>📍 On my way</span>
              )}
            </span>
          </button>
        );
      })}

      {runJob && (() => {
        const rc = clients.find((x) => x.id === runJob.clientId);
        const rt = (jobTypes.length ? jobTypes : DEFAULT_JOB_TYPES).find((t) => t.id === runJob.wfTypeId);
        if (!rc || !rt) return null;
        const t3 = [...(tests[rc.id] || [])].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 3);
        return (
          <GuidedRunner job={runJob} jobType={rt} client={rc} tests3={t3} parts={parts} me={me}
            onPatchWf={(wf) => {
              setRunJob((r) => ({ ...r, wf: { ...(r.wf || {}), ...wf } }));
              onSaveJobs((jobs || []).map((j) => (j.id === runJob.id ? { ...j, wf: { ...(j.wf || {}), ...wf } } : j)));
            }}
            onSaveTest={(t) => saveTests(rc.id, [t, ...(tests[rc.id] || [])])}
            onAddPhoto={async (dataUrl, caption) => {
              // Owner's rule (08/18): equipment-install captures update the
              // Equipment pad reference photos and stay forever; other guided
              // WO photos keep their own tag (also exempt from visit pruning).
              const tag = rt && rt.id === "equip_install" ? "pad" : "workorder";
              const entry = { id: cid(), at: new Date().toISOString(), added: fmtDate(new Date()), tag, caption, dataUrl };
              await savePhotos(rc.id, [...(photos[rc.id] || []), entry]);
              return entry.id;
            }}
            onFinish={(draft) => {
              const j = applyGuidedClose(runJob, draft || guidedCloseDraft(runJob));
              setRunJob(null);
              try { localStorage.removeItem("pm_worun_" + j.id); } catch { /* gone anyway */ }
              onWoClose && onWoClose(j);
            }}
            onIncomplete={(reason) => {
              onSaveJobs((jobs || []).map((j) => (j.id === runJob.id ? { ...j, wf: { ...(j.wf || {}), incompleteReason: reason, incompleteAt: new Date().toISOString() } } : j)));
              setRunJob(null);
            }}
            onClose={() => setRunJob(null)} />
        );
      })()}
    </div>
  );
}

/* ---------- day-route planner: geocode once, order the stops, hand to Google Maps ---------- */
const kmBetween = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371, dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Auto-stage: reorder a truck's assigned jobs by distance from the shop.
// Uses stored client/job coordinates only — never geocodes in the sort path.
// direction: "furthest" (far → near) or "closest" (near → far). Missing geo
// stays last, keeping the caller's relative order among unknowns.
function autoStageJobsByDistance(jobs, shop, direction, getGeo) {
  const list = Array.isArray(jobs) ? jobs.slice() : [];
  const shopPt = shop && Number.isFinite(Number(shop.lat)) && Number.isFinite(Number(shop.lng))
    ? { lat: Number(shop.lat), lng: Number(shop.lng) }
    : SHOP_POINT;
  const furthest = direction !== "closest";
  const rows = list.map((j, i) => {
    const g = typeof getGeo === "function" ? getGeo(j) : (j && j.geo);
    const lat = g != null ? Number(g.lat) : NaN;
    const lng = g != null ? Number(g.lng) : NaN;
    const has = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    return { j, i, has, km: has ? kmBetween({ lat, lng }, shopPt) : null };
  });
  rows.sort((a, b) => {
    if (a.has !== b.has) return a.has ? -1 : 1;
    if (!a.has) return a.i - b.i;
    if (a.km !== b.km) return furthest ? (b.km - a.km) : (a.km - b.km);
    return a.i - b.i;
  });
  return rows.map((r, idx) => ({ ...r.j, order: idx + 1 }));
}

async function fetchGeo(q) {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=ca&q=${encodeURIComponent(q)}`);
    const j = await r.json();
    if (j && j[0]) return { lat: +j[0].lat, lng: +j[0].lon };
  } catch {}
  return null;
}
// Nominatim can't read Canadian unit prefixes ("12-667D Warden Ave" finds
// nothing), so a failed lookup retries with the unit dropped ("667D Warden
// Ave"), then with the letter suffix off the street number ("667 Warden Ave").
// Every lookup pins countrycodes=ca — free-text matching once sent a
// Scarborough postal code to a village in Spain.
function geocodeTries(addr) {
  const tries = [addr];
  const noUnit = addr.replace(/^\s*(?:unit|apt\.?|suite|ph)?\s*[0-9A-Za-z]+\s*[-–]\s*(?=\d)/i, "");
  if (noUnit !== addr) tries.push(noUnit);
  const noSuffix = noUnit.replace(/^(\s*\d+)[A-Za-z]\b/, "$1");
  if (noSuffix !== noUnit) tries.push(noSuffix);
  return tries;
}
async function geocodeAddr(addr) {
  const tries = geocodeTries(addr);
  for (let i = 0; i < tries.length; i++) {
    if (i) await new Promise((r) => setTimeout(r, 1100)); // be polite to the free geocoder
    const g = await fetchGeo(/canada/i.test(tries[i]) ? tries[i] : tries[i] + ", Canada");
    if (g) return g;
  }
  return null;
}

function RouteSheet({ tech, day, stops, shopAddress, onGeo, onClose }) {
  const [state, setState] = useState({ busy: true, note: "Looking up addresses…", ordered: null, km: 0 });
  useEffect(() => {
    let alive = true;
    (async () => {
      const pts = [];
      let shop = null;
      if (shopAddress) shop = await geocodeAddr(shopAddress);
      for (const c of stops.slice(0, 18)) {
        if (!alive) return;
        let geo = c.geo || null;
        if (!geo && c.address) {
          setState((x) => ({ ...x, note: `Looking up ${c.name}…` }));
          geo = await geocodeAddr(c.address);
          if (geo) onGeo(c.id, geo);
          await new Promise((r) => setTimeout(r, 1100)); // be polite to the free geocoder
        }
        pts.push({ c, geo });
      }
      if (!alive) return;
      const located = pts.filter((p) => p.geo);
      const missing = pts.filter((p) => !p.geo).map((p) => p.c.name);
      // nearest-neighbour from the shop (or the first stop)
      const ordered = [];
      let cur = shop || (located[0] && located[0].geo);
      const pool = [...located];
      let km = 0;
      while (pool.length) {
        let bi = 0, bd = Infinity;
        pool.forEach((p, i) => { const d = cur ? kmBetween(cur, p.geo) : 0; if (d < bd) { bd = d; bi = i; } });
        const [next] = pool.splice(bi, 1);
        km += cur ? bd : 0;
        ordered.push({ ...next, leg: cur ? bd : 0 });
        cur = next.geo;
      }
      if (shop && cur) km += kmBetween(cur, shop); // back to the shop
      setState({ busy: false, note: missing.length ? `No address match for: ${missing.join(", ")}` : "", ordered, km: km * 1.3 });
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line
  const addrs = (state.ordered || []).map((p) => p.c.address).filter(Boolean);
  const gmaps = "https://www.google.com/maps/dir/" +
    [shopAddress, ...addrs, shopAddress].filter(Boolean).map((a) => encodeURIComponent(a)).join("/");
  const driveMin = Math.round((state.km / 40) * 60);
  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 560 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Route — {tech.vehicle ? `${tech.vehicle} (${tech.name})` : tech.name}</strong>
            <div className="sub">{day.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })} · best driving order, shop to shop</div>
          </div>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
        <div className="pad">
          {state.busy ? <div className="empty">{state.note}</div> : (<>
            {state.note && <div className="notice" style={{ marginBottom: 10 }}>{state.note}</div>}
            <div className="counts" style={{ marginBottom: 12 }}>
              <div className="count-cell"><div className="n">{(state.ordered || []).length}</div><div className="l">Stops</div></div>
              <div className="count-cell"><div className="n">{state.km ? state.km.toFixed(0) + " km" : "—"}</div><div className="l">Est. distance</div></div>
              <div className="count-cell"><div className="n">{state.km ? "~" + driveMin + "m" : "—"}</div><div className="l">Est. driving</div></div>
            </div>
            {(state.ordered || []).map((p, i) => (
              <div key={p.c.id} className="sub" style={{ display: "flex", gap: 10, padding: "5px 0", borderTop: i ? "1px solid var(--line)" : 0, marginTop: 0 }}>
                <b className="mono" style={{ minWidth: 22, color: "var(--tile)" }}>{i + 1}</b>
                <span style={{ flex: 1 }}><b style={{ color: "var(--ink)" }}>{p.c.name}</b><br />{p.c.address || "no address"}</span>
                {i > 0 && <span className="mono">{p.leg ? "+" + p.leg.toFixed(1) + " km" : ""}</span>}
              </div>
            ))}
            <a className="btn" style={{ display: "flex", justifyContent: "center", marginTop: 14, textDecoration: "none" }}
              href={gmaps} target="_blank" rel="noreferrer">Open in Google Maps — turn-by-turn</a>
          </>)}
        </div>
      </div>
    </div>
  );
}

function ScheduleView({ clients, techs, staff, timeOff, schedule, jobs, serviceTypes = [], services = [], onSaveJobs, onPick, onSaveTechs, onSaveSchedule, mode, setMode, onBookService, shopAddress, onGeo, parts = [], terms = [], onCompose, onLogComm, meName = "", isOwner = false, canAdjust = false, followups = [], repairs = [], jobTypes = [], onWoBilling, onOpenGuided, onBuildQuote, tools = [], meId = "", onToolsSave, businessProfile = {}, onSaveProfile }) {
  const stColorOf = (name) => (serviceTypes.find((s) => s.name === name) || {}).color || "";
  const [day, setDay] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); });
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [month, setMonth] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [techEdit, setTechEdit] = useState(false);
  const [binOpen, setBinOpen] = useState(true);
  const boardScrollRef = useRef(null);
  const [adding, setAdding] = useState(null);
  const [placing, setPlacing] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [issueCall, setIssueCall] = useState(null);

  // Drag a job card between tech rows and the holding bin (desktop; the Place/Move
  // buttons stay for phones and tablets, where HTML drag events don't fire).
  const [dragId, setDragId] = useState(null);
  const [overRow, setOverRow] = useState(null);
  const dragAutoScroll = (e) => {
    const m = 70;
    const sc = boardScrollRef && boardScrollRef.current;
    if (sc) {
      const r = sc.getBoundingClientRect();
      if (e.clientY < r.top + m) sc.scrollBy(0, -14);
      else if (e.clientY > r.bottom - m) sc.scrollBy(0, 14);
      return;
    }
    if (e.clientY < m) window.scrollBy(0, -14);
    else if (window.innerHeight - e.clientY < m) window.scrollBy(0, 14);
  };
  const dropProps = (key, apply) => ({
    onDragOver: (e) => { if (!canAdjust || !dragId) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overRow !== key) setOverRow(key); dragAutoScroll(e); },
    onDragLeave: (e) => { if (overRow === key && !(e.currentTarget.contains(e.relatedTarget))) setOverRow(null); },
    onDrop: (e) => {
      e.preventDefault();
      const id = dragId || e.dataTransfer.getData("text/plain");
      const j = (jobs || []).find((x) => x.id === id);
      setOverRow(null); setDragId(null);
      if (j && canAdjust) apply(j);
    },
  });

  const today = fmtDate(new Date());
  const route = clients.filter((c) => c.type !== "retail" && !c.trade && (clientDays(c).length || c.monthlyMode === "date"));
  const sch = { ...defSchedule(), ...(schedule || {}) };
  const per = Number(sch.perTech) || 1;
  const offCount = (d) => techs.filter((t) => offOnDate(t.staffId, d, timeOff)).length;
  const capOn = (d) => Math.max(1, (techs.length - offCount(d)) * per);
  const stopsOn = (d) => route.filter((c) => dueOnDate(c, d))
    .sort((a, b) => Number(a.order) - Number(b.order) || a.name.localeCompare(b.name));

  const allJobs = jobs || [];
  // A job sits in the holding bin until it has BOTH a day and a truck.
  const bin = allJobs.filter((j) => (!j.date || !j.techId || !techs.some((t) => t.id === j.techId)) && !j.done && !j.cancelled);
  const jobsOn = (d) => allJobs.filter((j) => j.date === fmtDate(d));
  const loadOn = (d) => stopsOn(d).length + jobsOn(d).reduce((a, j) => a + (Number(j.weight) || 1), 0);
  const clientName = (id) => (clients.find((c) => c.id === id) || {}).name || "No customer";
  const [areaAsk, setAreaAsk] = useState(null); // { j, band, cl } — booking held at the service-area gate
  const saveJob = (j, areaOk) => {
    if (!canAdjust) return;
    const isNew = !allJobs.some((x) => x.id === j.id);
    // New bookings outside the zones stop here: "ask" needs the office's OK,
    // "never" (beyond the km limit) cannot be booked at all.
    if (isNew && !areaOk && j.clientId) {
      const cl = clients.find((c) => c.id === j.clientId);
      const b = cl && cl.geo ? serviceBand(cl.geo.lat, cl.geo.lng) : null;
      if (b && (b.band === "ask" || b.band === "never")) { setAreaAsk({ j, band: b, cl }); return; }
    }
    setAreaAsk(null);
    onSaveJobs(isNew ? [...allJobs, j] : allJobs.map((x) => (x.id === j.id ? j : x)));
  };
  const dropJob = (id) => { if (!canAdjust) return; onSaveJobs(allJobs.filter((x) => x.id !== id)); };
  const jobGeo = (j) => {
    const c = (clients || []).find((x) => x.id === j.clientId);
    return (c && c.geo) || j.geo || null;
  };
  // Per-truck: rearrange that day's assigned jobs by distance from the shop.
  // Same persist path as drag/drop (onSaveJobs). Holding bin untouched.
  const autoStageDay = (direction) => {
    if (!canAdjust) return;
    const dayStr = fmtDate(day);
    const byId = new Map();
    (techs || []).forEach((t) => {
      const mine = allJobs.filter((j) => j.date === dayStr && j.techId === t.id && !j.cancelled);
      if (!mine.length) return;
      autoStageJobsByDistance(mine, SHOP_POINT, direction, jobGeo).forEach((j) => byId.set(j.id, j));
    });
    if (!byId.size) return;
    onSaveJobs(allJobs.map((j) => (byId.has(j.id) ? byId.get(j.id) : j)));
  };
  const sortJobsByOrder = (list) => list.slice().sort((a, b) => {
    const ao = Number(a.order), bo = Number(b.order);
    const aHas = Number.isFinite(ao) && ao > 0, bHas = Number.isFinite(bo) && bo > 0;
    if (aHas !== bHas) return aHas ? -1 : 1;
    if (aHas && ao !== bo) return ao - bo;
    return String(a.at || "").localeCompare(String(b.at || "")) || String(a.id).localeCompare(String(b.id));
  });

  const JobCard = ({ j, inBin }) => {
    const col = (j.serviceType && stColorOf(j.serviceType)) || j.color || "";
    return (
    <div className="stop job" data-bin={inBin ? 1 : 0} data-done={j.done ? 1 : 0} data-drag={dragId === j.id ? 1 : 0}
      draggable={!!canAdjust}
      onDragStart={(e) => {
        if (!canAdjust) { e.preventDefault(); return; }
        e.dataTransfer.setData("text/plain", j.id); e.dataTransfer.effectAllowed = "move"; setDragId(j.id);
      }}
      onDragEnd={() => { setDragId(null); setOverRow(null); }}
      style={col ? { borderLeft: `5px solid ${col}` } : undefined}>
      <div className="jobtop">
        <span className="jobkind" style={col ? { background: col, color: textOn(col), borderColor: col } : undefined}>{j.serviceType || JOB_LABEL[j.kind] || "Job"}</span>
        {j.done && <span className="jobw" style={{ background: "#12A650", color: "#fff", borderColor: "#12A650" }}>Done ✓</span>}
        {inBin && j.date && <span className="jobw" style={{ background: "#B7791F", color: "#fff", borderColor: "#B7791F" }}>{j.date} · needs a truck</span>}
        {j.gifted && <span className="jobw" style={{ background: "#B7791F", color: "#fff", borderColor: "#B7791F" }}>Gifted</span>}
        {(Number(j.weight) || 1) > 1 && <span className="jobw">{j.weight} slots</span>}
      </div>
      <div style={{ fontWeight: 650, fontSize: 13.5, cursor: j.clientId ? "pointer" : "default" }}
        onClick={() => j.clientId && onPick(j.clientId)}>
        {clientName(j.clientId)}
      </div>
      {j.workOrder && <div className="sub mono" style={{ marginTop: 0 }}>{j.workOrder}</div>}
      {(j.bookedBy || (j.changes || []).length > 0) && (
        <div className="sub" style={{ marginTop: 1 }}>
          {j.bookedBy ? `Booked by ${j.bookedBy}` : ""}
          {(j.changes || []).length > 0 ? `${j.bookedBy ? " · " : ""}changed by ${[...new Set(j.changes.map((e) => e.by).filter(Boolean))].join(", ")}` : ""}
        </div>
      )}
      {j.what && <div className="jobwhat">{j.what}</div>}
      {j.note && <div className="cr-meta"><span>{j.note}</span></div>}
      {(j.contactLog || []).length > 0 && (() => {
        const e = j.contactLog[j.contactLog.length - 1];
        return <div className="sub" style={{ marginTop: 2 }}>{CALL_HOW_LABEL[e.how] || e.how}{e.by ? ` · ${e.by}` : ""}{e.phone ? ` · ${e.phone}` : ""}</div>;
      })()}
      <div className="btnrow" style={{ marginTop: 7 }}>
        {j.wfTypeId && !j.done && onOpenGuided && (
          <button className="btn mini" onClick={() => onOpenGuided(j)}>▶ Open guided WO{j.wf && j.wf.results && Object.keys(j.wf.results).length ? " (in progress)" : ""}</button>
        )}
        {j.clientId && (
          <button className="btn ghost mini" onClick={() => {
            const c = clients.find((x) => x.id === j.clientId);
            if (c) setIssueCall({ c, j });
          }}><CommIcon v="phone" size={13} /> Call</button>
        )}
        {canAdjust ? (
          <button className="btn ghost mini" onClick={() => setPlacing(j)}>{inBin ? "Place" : "Move"}</button>
        ) : (
          <button className="btn ghost mini" disabled title="Only a manager or the owner can place or move calls">{inBin ? "Place" : "Move"}</button>
        )}
        <button className="btn ghost mini" onClick={() => setAdding(j)}>{canAdjust ? "Edit" : "View"}</button>
        {!inBin && (
          canAdjust
            ? <button className="btn ghost mini" onClick={() => saveJob({ ...j, date: "", techId: "", order: "" })}>To bin</button>
            : <button className="btn ghost mini" disabled title="Only a manager or the owner can remove calls from a truck">To bin</button>
        )}
      </div>
    </div>
    );
  };

  const StopRow = ({ c, showTech }) => (
    <div className="stop" onClick={() => onPick(c.id)}>
      <div className="stop-n mono">{c.order}</div>
      <div style={{ fontWeight: 650, fontSize: 13.5 }}>
        {c.name}
        {(c.billing || {}).noService && <span className="stoptag">Do not service</span>}
      </div>
      <div className="cr-meta">
        <Complexity n={effectiveCx(c)} />
        {c.frequency !== "weekly" && <span>{FREQ_LABEL[c.frequency]}</span>}
        {c.dog && <span style={{ color: "var(--sun)", fontWeight: 700 }}>Dog</span>}
        {showTech && <span>{(techs.find((t) => t.id === c.techId) || {}).name || "Unassigned"}</span>}
      </div>
    </div>
  );

  const dayStops = stopsOn(day);
  const dayJobs = jobsOn(day);
  const shiftDay = (n) => setDay((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
  const unassigned = dayStops.filter((c) => !c.techId || !techs.some((t) => t.id === c.techId));
  const band = dayLoadBand(loadOn(day), capOn(day), sch.amberAt);

  const cols = DAYS.map((d, i) => ({ d, dt: new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i) }));
  const shiftWeek = (n) => setWeekStart((w) => new Date(w.getFullYear(), w.getMonth(), w.getDate() + n * 7));
  const thisWeek = fmtDate(startOfWeek(new Date())) === fmtDate(weekStart);

  const monthCells = (() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const grid = [];
    for (let i = 0; i < 42; i++) {
      const dt = new Date(month.getFullYear(), month.getMonth(), 1 - lead + i);
      grid.push({ dt, inMonth: dt.getMonth() === month.getMonth() });
    }
    return grid;
  })();
  const shiftMonth = (n) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));

  const JobSheet = ({ j, onClose }) => {
    const [draft, setDraft] = useState({ ...j });
    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const exists = allJobs.some((x) => x.id === j.id);
    const ro = !canAdjust;
    return (
      <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
        <div className="sheet" style={{ maxWidth: 520 }}>
          <div className="bar">
            <div>
              <strong style={{ fontSize: 16 }}>{ro ? (exists ? "View booked work" : "Add to the holding bin") : (exists ? "Edit booked work" : "Add to the holding bin")}</strong>
              <div className="sub">{ro ? "Look only — only a manager or the owner can change call order or membership" : "It waits in the bin until you give it a day"}</div>
            </div>
            <div className="btnrow">
              <button className="btn ghost" onClick={onClose}>{ro ? "Close" : "Cancel"}</button>
              {!ro && (
                <button className="btn" disabled={!draft.clientId} title={!draft.clientId ? "Every booked service needs a customer profile" : ""} onClick={() => { saveJob(draft); onClose(); }}>Save</button>
              )}
            </div>
          </div>
          <div className="pad">
            {ro && (
              <div className="notice" style={{ marginBottom: 10 }}>Only a manager or the owner can rearrange, add, or remove Service Schedule calls.</div>
            )}
            <div className="fields">
              <div className="f-wide">
                <label>Customer</label>
                <select value={draft.clientId} disabled={ro} onChange={(e) => set("clientId", e.target.value)}>
                  <option value="">&mdash; pick a customer &mdash;</option>
                  {clients.slice().sort((a, b) => a.name.localeCompare(b.name))
                    .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label>Kind of work</label>
                <select value={draft.kind} disabled={ro} onChange={(e) => set("kind", e.target.value)}>
                  {JOB_KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <label>Slots it takes</label>
                <input type="number" min="1" max="8" value={draft.weight} disabled={ro}
                  onChange={(e) => set("weight", Math.max(1, Number(e.target.value) || 1))} />
              </div>
              <div className="f-wide">
                <label>What needs doing</label>
                <input value={draft.what} disabled={ro} onChange={(e) => set("what", e.target.value)}
                  placeholder="Replace pump seal, water at the pad" />
              </div>
              <div className="f-wide">
                <label>Notes for the tech</label>
                <textarea rows={2} value={draft.note} disabled={ro} onChange={(e) => set("note", e.target.value)} />
              </div>
              {!ro && <PartsNeededField value={draft.parts || []} onChange={(v) => set("parts", v)} parts={parts} />}
              {ro && (draft.parts || []).length > 0 && (
                <div className="f-wide">
                  <label>Parts needed on the truck</label>
                  <div className="sub" style={{ marginTop: 0 }}>{(draft.parts || []).map((x) => `${x.name || x.code}${x.qty ? ` ×${x.qty}` : ""}`).join("; ")}</div>
                </div>
              )}
            </div>
            {!ro && !draft.clientId && <div className="sub" style={{ marginTop: 8, color: "var(--clay)" }}>Every booked service needs a customer profile — pick one to save.</div>}
            {draft.clientId && (
              <div className="btnrow" style={{ marginTop: 10 }}>
                <button type="button" className="btn mini" onClick={() => {
                  const c = clients.find((x) => x.id === draft.clientId);
                  if (c) setIssueCall({ c, j: allJobs.find((x) => x.id === j.id) || draft });
                }}><CommIcon v="phone" /> Call about a service issue</button>
              </div>
            )}
            {(j.contactLog || []).length > 0 && (
              <div style={{ marginTop: 12, background: "#FBF9F4", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 11px" }}>
                <div className="sub" style={{ marginTop: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>Service issue calls</div>
                {j.contactLog.slice().reverse().map((e, i) => (
                  <div key={e.id || i} className="sub" style={{ marginTop: 3 }}>
                    {String(e.at || "").slice(0, 16).replace("T", " ")} · <b>{e.by || "?"}</b> — {CALL_HOW_LABEL[e.how] || e.how}{e.phone ? ` · ${e.phone}` : ""}
                  </div>
                ))}
              </div>
            )}
            {(j.bookedBy || (j.changes || []).length > 0) && (
              <div style={{ marginTop: 12, background: "#FBF9F4", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 11px" }}>
                <div className="sub" style={{ marginTop: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>Booking history</div>
                {j.bookedBy && (
                  <div className="sub" style={{ marginTop: 3, display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span className="mono" style={{ minWidth: 108 }}>{j.bookedDate || ""}</span>
                    <span><b style={{ color: "var(--muted)" }}>{j.bookedBy}</b> booked this service</span>
                  </div>
                )}
                {(j.changes || []).map((e, i) => (
                  <div key={e.id || i} className="sub" style={{ marginTop: 3, display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span className="mono" style={{ minWidth: 108 }}>{e.date}{e.time ? " " + e.time : ""}</span>
                    <span><b style={{ color: "var(--muted)" }}>{e.by || "?"}</b> — {e.note}</span>
                  </div>
                ))}
              </div>
            )}
            {!ro && exists && (
              <div className="btnrow" style={{ marginTop: 12 }}>
                <button className="btn warn mini" onClick={() => { dropJob(j.id); onClose(); }}>Delete this job</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const PlaceSheet = ({ j, onClose }) => {
    const [d, setD] = useState(j.date || fmtDate(day));
    const [t, setT] = useState(j.techId || "");
    const chosen = parseDate(d);
    const away = chosen ? techs.filter((x) => offOnDate(x.staffId, chosen, timeOff)).map((x) => x.id) : [];
    const busy = chosen ? stopsOn(chosen).length + jobsOn(chosen).length : 0;
    if (!canAdjust) {
      return (
        <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
          <div className="sheet" style={{ maxWidth: 460 }}>
            <div className="bar">
              <div>
                <strong style={{ fontSize: 16 }}>Place this job</strong>
                <div className="sub">{clientName(j.clientId)} &middot; {JOB_LABEL[j.kind]}</div>
              </div>
              <button className="btn ghost" onClick={onClose}>Close</button>
            </div>
            <div className="pad">
              <div className="notice">Only a manager or the owner can place or move calls on the board.</div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
        <div className="sheet" style={{ maxWidth: 460 }}>
          <div className="bar">
            <div>
              <strong style={{ fontSize: 16 }}>Place this job</strong>
              <div className="sub">{clientName(j.clientId)} &middot; {JOB_LABEL[j.kind]}</div>
            </div>
            <button className="btn ghost" onClick={onClose}>Cancel</button>
          </div>
          <div className="pad">
            <div className="fields">
              <div><label>Day</label><input type="date" value={d} onChange={(e) => setD(e.target.value)} /></div>
              <div>
                <label>Technician</label>
                <select value={t} onChange={(e) => setT(e.target.value)}>
                  <option value="">Decide later</option>
                  {techs.map((x) => (
                    <option key={x.id} value={x.id}>{x.name}{away.includes(x.id) ? " — off that day" : ""}</option>
                  ))}
                </select>
              </div>
            </div>
            {chosen && (
              <div className="sub" style={{ marginTop: 10 }}>
                {busy} already booked that day, {capOn(chosen)} fills it
                {away.length > 0 ? ` \u00b7 ${away.length} tech${away.length === 1 ? "" : "s"} off` : ""}
              </div>
            )}
            {t && away.includes(t) && (
              <div className="notice" style={{ marginTop: 10 }}>
                That tech has approved time off that day. You can still book it &mdash; it shows on their greyed column.
              </div>
            )}
            <div className="btnrow" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => { saveJob({ ...j, date: d, techId: t, order: "" }); onClose(); }}>Put it on the board</button>
              {j.date && <button className="btn ghost" onClick={() => { saveJob({ ...j, date: "", techId: "", order: "" }); onClose(); }}>Back to bin</button>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const [routeFor, setRouteFor] = useState(null);
  return (
    <div className="sched-board">
      {adding && <JobSheet j={adding} onClose={() => setAdding(null)} />}
      {placing && <PlaceSheet j={placing} onClose={() => setPlacing(null)} />}
      {catalogOpen && <ServiceCatalogSheet services={services} serviceTypes={serviceTypes} clients={clients} onClose={() => setCatalogOpen(false)} />}
      {issueCall && (
        <ServiceIssueCallSheet client={issueCall.c} job={issueCall.j || null} meName={meName}
          onLog={(note) => onLogComm && onLogComm(issueCall.c.id, note, "service")}
          onJobLog={(how, phone) => {
            const j = issueCall.j;
            if (!j) return;
            const live = (jobs || []).find((x) => x.id === j.id) || j;
            const next = { ...live, contactLog: [...(live.contactLog || []), { id: cid(), at: new Date().toISOString(), by: meName, how, phone: phone || "" }] };
            onSaveJobs && onSaveJobs((jobs || []).map((x) => x.id === next.id ? next : x));
            if (adding && adding.id === next.id) setAdding(next);
          }}
          onClose={() => setIssueCall(null)} />
      )}
      {routeFor && (
        <RouteSheet tech={routeFor.tech} day={day} stops={routeFor.stops} shopAddress={shopAddress} onGeo={onGeo}
          onClose={() => setRouteFor(null)} />
      )}
      <div className="sched-pin">
      <div className="panel bar" style={{ marginBottom: 12, borderBottom: 0, flexWrap: "wrap", gap: 10 }}>
        <div className="seg" style={{ margin: 0 }}>
          {[["ops", "Overview"], ["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"], ["tools", "Tools"]].map(([v, l]) => (
            <button key={v} type="button" data-on={mode === v ? 1 : 0} onClick={() => setMode(v)}>{l}</button>
          ))}
        </div>
        {mode === "day" && (
          <>
            <div>
              <strong style={{ fontSize: 15 }}>{day.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</strong>
              <div className="sub">
                {dayStops.length + dayJobs.length} call{dayStops.length + dayJobs.length === 1 ? "" : "s"} booked · {BAND_WORD[band]}
                {offCount(day) > 0 ? ` · ${offCount(day)} off` : ""}
              </div>
            </div>
            <div className="btnrow">
              <button className="btn ghost" onClick={() => setTechEdit(true)}>Technicians</button>
              <button className="btn ghost" onClick={() => shiftDay(-1)}>&lsaquo;</button>
              {fmtDate(day) !== today && (
                <button className="btn ghost" onClick={() => { const t = new Date(); setDay(new Date(t.getFullYear(), t.getMonth(), t.getDate())); }}>Today</button>
              )}
              <button className="btn ghost" onClick={() => shiftDay(1)}>&rsaquo;</button>
              <label className="sub" style={{ marginTop: 0, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                title={canAdjust ? undefined : "Only a manager or the owner can rearrange call order"}>
                Auto stage
                <select
                  aria-label="Auto stage by distance from the shop"
                  defaultValue=""
                  disabled={!canAdjust}
                  style={{ width: "auto", minWidth: 148, padding: "6px 8px" }}
                  onChange={(e) => {
                    const v = e.target.value;
                    e.target.value = "";
                    if (!canAdjust) return;
                    if (v === "furthest" || v === "closest") autoStageDay(v);
                  }}
                >
                  <option value="">{canAdjust ? "Distance…" : "Manager only"}</option>
                  <option value="furthest">Furthest → closest</option>
                  <option value="closest">Closest → furthest</option>
                </select>
              </label>
            </div>
          </>
        )}
        {mode === "week" && (
          <>
            <div>
              <strong style={{ fontSize: 15 }}>{fmtShort(cols[0].dt)} &ndash; {fmtShort(cols[cols.length - 1].dt)}</strong>
              <div className="sub">{thisWeek ? "This week" : "Week of " + fmtDate(weekStart)}</div>
            </div>
            <div className="btnrow">
              <button className="btn ghost" onClick={() => shiftWeek(-1)}>&lsaquo; Previous</button>
              {!thisWeek && <button className="btn ghost" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>}
              <button className="btn ghost" onClick={() => shiftWeek(1)}>Next &rsaquo;</button>
            </div>
          </>
        )}
        {mode === "month" && (
          <>
            <div>
              <strong style={{ fontSize: 15 }}>{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</strong>
              <div className="sub">{capOn(month)} calls fills a full day &mdash; {techs.length} techs at {sch.perTech} each, less anyone off</div>
            </div>
            <div className="btnrow">
              <span className="sub" style={{ marginTop: 0 }}>Calls per tech</span>
              <input type="number" min="1" max="30" value={sch.perTech} style={{ width: 66, padding: "6px 8px" }}
                onChange={(e) => onSaveSchedule({ ...sch, perTech: Math.max(1, Number(e.target.value) || 1) })} />
              <button className="btn ghost" onClick={() => shiftMonth(-1)}>&lsaquo;</button>
              <button className="btn ghost" onClick={() => shiftMonth(1)}>&rsaquo;</button>
            </div>
          </>
        )}
      </div>

      <div className="binwrap sched-bin" data-open={binOpen ? 1 : 0} data-over={overRow === "bin" ? 1 : 0}
        {...dropProps("bin", (j) => saveJob({ ...j, date: "", techId: "", order: "" }))}>
        <div className="binhead">
          <button type="button" className="bintoggle" onClick={() => setBinOpen((v) => !v)}>
            <span className="chev" data-open={binOpen ? 1 : 0}>&rsaquo;</span>
            Holding bin
            <span className="bincount" data-zero={bin.length ? 0 : 1}>{bin.length}</span>
          </button>
          <div className="sub" style={{ marginTop: 0, flex: 1 }}>
            {canAdjust ? "Booked work waiting for a day and a tech" : "View only — only a manager or the owner can rearrange, add, or remove calls"}
          </div>
          <button className="btn ghost" style={{ fontSize: 15.6, padding: "10px 17px", fontWeight: 700 }} onClick={() => setCatalogOpen(true)}><ListIcon size={17} /> Service catalog</button>
          {canAdjust ? (
            <button className="btn" style={{ fontSize: 15.6, padding: "10px 17px", fontWeight: 700 }} data-cobrowse="schedule.book" onClick={() => (onBookService ? onBookService() : setAdding(blankJob()))}><CalendarIcon size={17} /> Book a service call</button>
          ) : (
            <button className="btn" style={{ fontSize: 15.6, padding: "10px 17px", fontWeight: 700 }} disabled title="Only a manager or the owner can book a service call onto the schedule"><CalendarIcon size={17} /> Book a service call</button>
          )}
        </div>
        {binOpen && (
          bin.length === 0
            ? <div className="empty" style={{ padding: "14px 10px" }}>Bin is empty — everything booked has a day.</div>
            : <div className="binrow">{bin.map((j) => <JobCard key={j.id} j={j} inBin />)}</div>
        )}
      </div>
      </div>
      <div className="sched-board-scroll" ref={boardScrollRef}>

      {(() => {
        // Confirmations queue: calls that just got a day + truck, waiting on the customer being told.
        const toConfirm = allJobs.filter((j) => j.confirmPending && !j.done && !j.cancelled);
        if (!toConfirm.length) return null;
        const markConfirmed = (j, how) => {
          onSaveJobs(allJobs.map((x) => (x.id === j.id
            ? { ...x, confirmPending: false, confirmSent: { at: new Date().toISOString(), by: meName, how } } : x)));
        };
        return (
          <div className="binwrap" style={{ position: "static", boxShadow: "none", borderLeft: "4px solid #12A650", marginTop: -2 }}>
            <div className="binhead">
              <b style={{ fontSize: 13.5 }}>Confirm with the customer</b>
              <span className="bincount">{toConfirm.length}</span>
              <span className="sub" style={{ marginTop: 0, flex: 1 }}>Scheduled — the customer hasn't been told yet. One tap sends it their preferred way.</span>
            </div>
            {toConfirm.map((j) => {
              const c = clients.find((x) => x.id === j.clientId);
              if (!c) return null;
              const pref = servicePrefFor(c);
              const t = techs.find((x) => x.id === j.techId) || {};
              const msg = confirmMsgFor(c, j, pref.contact);
              return (
                <div key={j.id} style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", padding: "7px 2px", borderTop: "1px solid var(--line)" }}>
                  <button className="link" style={{ fontWeight: 700 }} onClick={() => onPick(j.clientId)}>{c.name}</button>
                  <span className="doc" style={{ background: "#17356E" }}>{j.serviceType || JOB_LABEL[j.kind] || "Service"}</span>
                  <span className="doc" style={{ background: "#0887C8" }}>{j.date}{t.vehicle || t.name ? " · " + (t.vehicle || t.name) : ""}</span>
                  {pref.channel === "phone"
                    ? <span className="doc" style={{ background: "#B7791F" }}>CALL REQUIRED — their preference</span>
                    : <span className="doc" style={{ background: "#5A6E86" }}>prefers {pref.channel}</span>}
                  <span className="btnrow" style={{ marginLeft: "auto", flexWrap: "nowrap" }}>
                    {pref.channel === "email" && pref.contact.email && onCompose && (
                      <button className="btn mini" onClick={() => {
                        onCompose({ kind: "mail", client: c, to: pref.contact.email, emailCat: "service", subject: "Pool Master — your service is confirmed", body: msg });
                        onLogComm && onLogComm(c.id, `Service confirmation sent by email — ${j.serviceType || "service"} on ${j.date}`, "service");
                        markConfirmed(j, "email");
                      }}>Send email</button>
                    )}
                    {pref.channel === "text" && pref.contact.phone && onCompose && (
                      <button className="btn mini" onClick={() => {
                        onCompose({ kind: "text", client: c, to: pref.contact.phone, body: msg });
                        onLogComm && onLogComm(c.id, `Service confirmation sent by text — ${j.serviceType || "service"} on ${j.date}`, "service");
                        markConfirmed(j, "text");
                      }}>Send text</button>
                    )}
                    {pref.channel === "phone" && (
                      <>
                        {pref.contact.phone && <a className="btn mini" style={{ textDecoration: "none" }} href={"tel:" + String(pref.contact.phone).replace(/[^0-9+]/g, "")}
                          onClick={() => { onLogComm && onLogComm(c.id, `Confirmation call placed — ${j.serviceType || "service"} on ${j.date}`, "service"); }}>Call {pref.contact.phone}</a>}
                        <button className="btn ghost mini" onClick={() => {
                          onLogComm && onLogComm(c.id, `Service schedule confirmed by phone — ${j.serviceType || "service"} on ${j.date}`, "service");
                          markConfirmed(j, "phone");
                        }}>Confirmed ✓</button>
                      </>
                    )}
                    <button className="btn ghost mini" title="No confirmation needed" onClick={() => markConfirmed(j, "skipped")}>Skip</button>
                  </span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        // Closed guided work orders → the review-before-billing queue. Nothing
        // invoices until the office looks at it and says so.
        const toBill = allJobs.filter((j) => j.wf && j.wf.completedAt && !j.wf.billing);
        if (!toBill.length || !onWoBilling) return null;
        return (
          <div className="binwrap" style={{ position: "static", boxShadow: "none", borderLeft: "4px solid #0B6FBE", marginTop: -2 }}>
            <div className="binhead">
              <b style={{ fontSize: 13.5 }}>Closed work orders — review before billing</b>
              <span className="bincount">{toBill.length}</span>
              <span className="sub" style={{ marginTop: 0, flex: 1 }}>Confirm what invoices; return visits ask whether they bill at all.</span>
            </div>
            {toBill.map((j) => {
              const c = clients.find((x) => x.id === j.clientId);
              const jt = jobTypes.find((t) => t.id === (j.wf || {}).jobTypeId);
              const dur = jt ? wfDuration(jt, j.wf.results || {}) : null;
              const chems = (((j.wf.results || {}).chems || {}).items || []);
              const truck = chems.filter((x) => x.src === "truck");
              const deferredNote = jt && wfDeferred(jt, j.wf.results || {}).length > 0;
              return (
                <div key={j.id} className="visit" style={{ margin: "8px 10px" }}>
                  <div className="vhead" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <b>{j.workOrder || "WO"}</b>
                    <button className="link" onClick={() => onPick && onPick(j.clientId)}>{c ? c.name : "Customer"}</button>
                    <span className="doc" style={{ background: "#0B6FBE" }}>{(jt || {}).name || "Guided job"}</span>
                    {dur != null && <span className="doc" style={{ background: "#5A6E86" }}>{dur} min on site</span>}
                    {(() => {
                      const g = ((j.wf.results || {}).arrive || {}).geo;
                      return g ? <a className="doc" style={{ background: "#0E7A3C", textDecoration: "none" }} target="_blank" rel="noreferrer"
                        href={`https://maps.google.com/?q=${g.lat},${g.lng}`} title="Where the arrival photo was taken — every step of this work order carries the same GPS stamp">📍 GPS verified</a> : null;
                    })()}
                    {j.parentJobId && <span className="doc" style={{ background: "#B36B00" }}>Return visit — bill?</span>}
                    {deferredNote && <span className="doc" style={{ background: "#B36B00" }}>startup deferred → follow-up spawned</span>}
                    <span style={{ flex: 1 }} />
                    {(j.wf || {}).jobTypeId === "site_quote" && onBuildQuote && (
                      <button className="btn mini" onClick={() => onBuildQuote(j.clientId)}>Open quote builder</button>
                    )}
                    {(() => {
                      const canBill = confirmBillingCanProceed(j);
                      return (
                        <>
                          <button
                            className="btn mini"
                            data-cobrowse="wo.confirmBilling"
                            disabled={!canBill}
                            title={!canBill ? CONFIRM_BILLING_EMPTY_MSG : undefined}
                            onClick={() => {
                              if (!confirmBillingCanProceed(j)) {
                                return;
                              }
                              onWoBilling(j.id, "bill");
                            }}
                          >Confirm for billing</button>
                          {!canBill && (
                            <span className="sub" style={{ color: "#B36B00", flexBasis: "100%" }}>{CONFIRM_BILLING_EMPTY_MSG}</span>
                          )}
                        </>
                      );
                    })()}
                    <button className="btn ghost mini" onClick={() => onWoBilling(j.id, "nocharge")}>No charge</button>
                  </div>
                  {truck.length > 0 && <div className="sub" style={{ marginTop: 4 }}>Billable materials (truck): {truck.map((x) => `${x.desc}${(Number(x.qty) || 1) > 1 ? ` ×${x.qty}` : ""}`).join("; ")}</div>}
                  {chems.some((x) => x.src === "customer") && <div className="sub">Customer's own chemicals used: {chems.filter((x) => x.src === "customer").map((x) => x.desc).join("; ")} — not billed.</div>}
                  {(j.wf.results || {}).pins_note && <div className="sub" style={{ color: "#B36B00" }}>Pins note: {(j.wf.results || {}).pins_note.note}</div>}
                  {(j.wf.closeNotes || "").trim() && <div className="sub" style={{ marginTop: 4 }}>Close notes: {j.wf.closeNotes}</div>}
                  {(j.wf.followReason || "").trim() && <div className="sub">Follow-up: {j.wf.followReason}</div>}
                </div>
              );
            })}
          </div>
        );
      })()}

      {mode === "ops" && (() => {
        const todayD = new Date();
        const days7 = Array.from({ length: 7 }, (_, i) => new Date(todayD.getFullYear(), todayD.getMonth(), todayD.getDate() + i));
        const todayStops = stopsOn(todayD);
        const todayJobs = jobsOn(todayD);
        const doneToday = todayJobs.filter((j) => j.done).length;
        const offToday = techs.filter((t) => offOnDate(t.staffId, todayD, timeOff));
        const unassignedToday = todayStops.filter((c) => !c.techId || !techs.some((t) => t.id === c.techId)).length
          + todayJobs.filter((j) => !j.techId || !techs.some((t) => t.id === j.techId)).length;
        const binJobs = allJobs.filter((j) => (!j.date || !j.techId || !techs.some((t) => t.id === j.techId)) && !j.done && !j.cancelled);
        const binAgeDays = (j) => Math.max(0, Math.round((todayD - new Date(j.at || todayD)) / 86400000));
        const oldBin = binJobs.filter((j) => binAgeDays(j) >= 7);
        const noSvc = route.filter((c) => (c.billing || {}).noService).length;
        const alerts = [];
        if (unassignedToday > 0) alerts.push(`${unassignedToday} of today's calls have no truck assigned — they're waiting in the holding bin; drag them onto a truck.`);
        if (oldBin.length > 0) alerts.push(`${oldBin.length} job${oldBin.length === 1 ? " has" : "s have"} sat in the holding bin for a week or more — oldest ${Math.max(...oldBin.map(binAgeDays))} days.`);
        days7.forEach((d) => { if (loadOn(d) > capOn(d)) alerts.push(`${d.toLocaleDateString(undefined, { weekday: "long" })} is over capacity (${loadOn(d)} booked, room for ${capOn(d)}).`); });
        if (offToday.length) alerts.push(`Off today: ${offToday.map((t) => t.name).join(", ")}.`);
        if (noSvc > 0) alerts.push(`${noSvc} route customer${noSvc === 1 ? " is" : "s are"} flagged Do-not-service — they are skipped until lifted.`);
        const fuOver = (followups || []).filter((f) => !f.done && f.dueDate && f.dueDate < fmtDate(todayD));
        if (fuOver.length) alerts.push(`${fuOver.length} follow-up${fuOver.length === 1 ? " is" : "s are"} past due — open the Follow-ups page and clear them.`);
        const repWait = repairsAwaitingContact(repairs);
        if (repWait.length) alerts.push(`${repWait.length} completed repair${repWait.length === 1 ? "" : "s"} — the customer hasn't been reached yet. Open In-House Repair to log the pickup call.`);
        const toolFlags = (tools || []).filter((t) => t.flag && !t.retired);
        if (toolFlags.length) alerts.push(`${toolFlags.length} tool${toolFlags.length === 1 ? " is" : "s are"} flagged for management review (${toolFlags.map((t) => `${t.tag || t.name} — ${t.flag.responsible || "responsible unknown"}`).join("; ")}) — open the Tools tab.`);
        const ac = (businessProfile || {}).autoComms || {};
        const setAc = (k, v) => onSaveProfile && onSaveProfile({ ...businessProfile, autoComms: { reports: false, reminders: false, reviews: false, reviewUrl: "", ...ac, [k]: v } });
        return (
          <div>
            {isOwner && onSaveProfile && (
              <div className="panel" style={{ padding: 12, marginBottom: 12 }}>
                <b style={{ fontSize: 13 }}>Automatic customer messages</b>
                <span className="sub" style={{ display: "inline", marginLeft: 8 }}>ship OFF — flip them on at go-live, once the test customers are scrubbed.</span>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
                  <label className="chk" style={{ marginTop: 0 }}>
                    <input type="checkbox" checked={!!ac.reports} onChange={(e) => setAc("reports", e.target.checked)} />
                    After-service report emails itself when a stop or work order finishes
                  </label>
                  <label className="chk" style={{ marginTop: 0 }}>
                    <input type="checkbox" checked={!!ac.reminders} onChange={(e) => setAc("reminders", e.target.checked)} />
                    Day-before appointment reminder texts
                  </label>
                  <label className="chk" style={{ marginTop: 0 }}>
                    <input type="checkbox" checked={!!ac.reviews} onChange={(e) => setAc("reviews", e.target.checked)} />
                    Google review ask the day after a visit
                  </label>
                  <label className="sub" style={{ marginTop: 0, flex: 1, minWidth: 240 }}>Google review link
                    <input value={ac.reviewUrl || ""} placeholder="https://g.page/r/…/review" style={{ marginLeft: 6, width: "70%" }} onChange={(e) => setAc("reviewUrl", e.target.value)} />
                  </label>
                </div>
              </div>
            )}
            <div className="counts" style={{ marginBottom: 14 }}>
              <div className="count-cell"><div className="n">{todayStops.length + todayJobs.length}</div><div className="l">Calls today</div></div>
              <div className="count-cell"><div className="n">{doneToday}</div><div className="l">Done today</div></div>
              <div className="count-cell"><div className="n" style={binJobs.length ? { color: "var(--sun)" } : undefined}>{binJobs.length}</div><div className="l">In the bin</div></div>
              <div className="count-cell"><div className="n">{techs.length - offToday.length}/{techs.length}</div><div className="l">Trucks out</div></div>
            </div>

            {alerts.length > 0 ? alerts.map((a, i) => (
              <div key={i} className="notice" style={{ marginBottom: 8, borderLeft: "4px solid var(--sun)" }}>{a}</div>
            )) : <div className="notice" style={{ marginBottom: 8, borderLeft: "4px solid #12A650" }}>All clear — nothing needs attention.</div>}

            <div className="sechead">Today by crew</div>
            <div style={{ overflowX: "auto" }}>
              <table className="salest">
                <thead><tr><th>Truck</th><th style={{ textAlign: "right" }}>Stops</th><th style={{ textAlign: "right" }}>Jobs</th><th style={{ textAlign: "right" }}>Load</th><th>Status</th></tr></thead>
                <tbody>
                  {techs.map((t) => {
                    const st = todayStops.filter((c) => c.techId === t.id).length;
                    const jb = todayJobs.filter((j) => j.techId === t.id);
                    const load = st + jb.reduce((a, j) => a + (Number(j.weight) || 1), 0);
                    const off = offOnDate(t.staffId, todayD, timeOff);
                    return (
                      <tr key={t.id} style={off ? { opacity: 0.5 } : undefined}>
                        <td><b>{t.vehicle || t.name}</b>{t.vehicle ? <span className="sub" style={{ marginTop: 0, marginLeft: 5, display: "inline" }}>({t.name})</span> : null}</td>
                        <td className="mono" style={{ textAlign: "right" }}>{st}</td>
                        <td className="mono" style={{ textAlign: "right" }}>{jb.length}</td>
                        <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{load}/{per}</td>
                        <td>{off ? <span className="doc" style={{ background: "#5A6E86" }}>OFF</span>
                          : load > per ? <span className="doc" style={{ background: "#B31212" }}>Over</span>
                          : load === 0 ? <span className="sub" style={{ marginTop: 0 }}>Free</span>
                          : <span className="doc" style={{ background: "#12A650" }}>OK</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="sechead">Next 7 days — capacity</div>
            {days7.map((d) => {
              const load = loadOn(d), cap = capOn(d);
              const pct = Math.min(100, Math.round((load / Math.max(1, cap)) * 100));
              const colr = load > cap ? "#B31212" : pct >= Math.round((sch.amberAt || 0.7) * 100) ? "#B7791F" : "#12A650";
              return (
                <div key={+d} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
                  <span style={{ width: 110, fontWeight: 700, fontSize: 12.5 }}>{d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                  <div style={{ flex: 1, height: 10, background: "var(--card)", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ width: pct + "%", height: "100%", background: colr, borderRadius: 6 }} />
                  </div>
                  <span className="mono" style={{ width: 64, textAlign: "right", fontSize: 12.5, fontWeight: 700 }}>{load}/{cap}</span>
                </div>
              );
            })}

            {binJobs.length > 0 && (<>
              <div className="sechead">Holding bin — waiting for a day</div>
              {binJobs.slice(0, 8).map((j) => (
                <div key={j.id} className="sub" style={{ marginTop: 3, display: "flex", gap: 10 }}>
                  <span className="mono" style={{ minWidth: 70 }}>{binAgeDays(j)}d old</span>
                  <span style={{ flex: 1 }}><b>{clientName(j.clientId)}</b> · {j.serviceType || JOB_LABEL[j.kind] || "Job"}{j.what ? ` — ${j.what}` : ""}</span>
                </div>
              ))}
            </>)}
          </div>
        );
      })()}

      {mode === "tools" && (
        <ToolsPanel tools={tools} staff={staff} meId={meId} meName={meName} isMgr onSave={onToolsSave} />
      )}

      {mode === "day" && (
        <div className="techrows">
          {techs.map((t, i) => {
            const mine = dayStops.filter((c) => c.techId === t.id);
            const load = mine.reduce((a, c) => a + effectiveCx(c), 0);
            const myJobs = sortJobsByOrder(dayJobs.filter((j) => j.techId === t.id));
            const off = offOnDate(t.staffId, day, timeOff);
            const full = !off && mine.length + myJobs.length >= per;
            return (
              <div key={t.id} className="techrow" data-full={full ? 1 : 0} data-off={off ? 1 : 0}
                data-over={overRow === t.id ? 1 : 0}
                {...(!off ? dropProps(t.id, (j) => saveJob({ ...j, date: fmtDate(day), techId: t.id, order: "" })) : {})}>
                <div className="rowhead">
                  <div className="tchead">
                    <span className="tnum">{i + 1}</span>
                    <span className="tnm">{t.vehicle || t.name}{t.vehicle ? <span style={{ fontWeight: 600, opacity: 0.72, fontSize: 10.5 }}> ({t.name})</span> : null}</span>
                  </div>
                  <div className="rowmeta">
                    <span className="tcnt">{off ? "OFF" : (mine.length + myJobs.length) + " call" + (mine.length + myJobs.length === 1 ? "" : "s") + (load ? " \u00b7 cx " + load : "") + (full ? " \u00b7 FULL" : "")}</span>
                    {mine.length + myJobs.length > 0 && !off && (
                      <button type="button" title="Best driving order + Google Maps"
                        style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 14, padding: "0 2px" }}
                        onClick={() => setRouteFor({ tech: t, stops: [...mine, ...myJobs.map((j) => clients.find((c) => c.id === j.clientId)).filter(Boolean)] })}>🗺</button>
                    )}
                  </div>
                  {overRow === t.id && <span className="droptag">{full ? "Full — drop to overbook" : "Drop to assign"}</span>}
                  {off && (
                    <div className="offtag" style={{ marginTop: 6 }}>
                      Time off{off.from !== off.to ? ` \u00b7 ${off.from} to ${off.to}` : ""}
                      {mine.length > 0 && <b>{mine.length} call{mine.length === 1 ? "" : "s"} still here — move them</b>}
                    </div>
                  )}
                </div>
                <div className="rowstops">
                  {mine.length === 0 && myJobs.length === 0
                    ? <div className="empty" style={{ padding: "12px 8px" }}>{off ? "Nothing booked" : (canAdjust ? "Open — drag a job here" : "Open")}</div>
                    : <>
                        {mine.map((c) => <StopRow key={c.id} c={c} />)}
                        {myJobs.map((j) => <JobCard key={j.id} j={j} />)}
                      </>}
                </div>
              </div>
            );
          })}
          {unassigned.length > 0 && (
            <div className="techrow" data-warn="1">
              <div className="rowhead">
                <div className="tchead">
                  <span className="tnum">!</span>
                  <span className="tnm">Route stops without a truck</span>
                </div>
                <div className="rowmeta"><span className="tcnt">{unassigned.length}</span></div>
              </div>
              <div className="rowstops">
                {unassigned.map((c) => <StopRow key={c.id} c={c} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {mode === "week" && (
        <div className="weekgrid">
          {cols.map(({ d, dt }) => {
            const stops = stopsOn(dt);
            const djobs = jobsOn(dt).filter((j) => !j.done && !j.cancelled);
            const total = stops.length + djobs.length;
            const b = dayLoadBand(loadOn(dt), capOn(dt), sch.amberAt);
            const isToday = fmtDate(dt) === today;
            return (
              <div key={d} className="panel daycol" data-over={overRow === "wk:" + d ? 1 : 0}
                style={{ ...(isToday ? { borderTopColor: "var(--sun)" } : {}), ...(overRow === "wk:" + d ? { outline: "2px dashed #0B6FBE", outlineOffset: -2 } : {}) }}
                {...dropProps("wk:" + d, (j) => {
                  const nd = fmtDate(dt);
                  // Dropped on a day: keep the truck if it has one; otherwise open
                  // the Place sheet with the day pre-filled so the truck gets picked
                  // right away instead of the job sliding back into the bin.
                  if (j.techId) saveJob({ ...j, date: nd, order: "" });
                  else setPlacing({ ...j, date: nd });
                })}>
                <h3>
                  <span className="band" data-b={b} />
                  {d} {fmtShort(dt)}
                  <span className="count"> &middot; {total} call{total === 1 ? "" : "s"}</span>
                </h3>
                {djobs.map((j) => <JobCard key={j.id} j={j} />)}
                {total === 0
                  ? <div className="empty" style={{ padding: "16px 12px" }}>Open day.</div>
                  : stops.map((c) => <StopRow key={c.id} c={c} showTech />)}
              </div>
            );
          })}
        </div>
      )}

      {mode === "month" && (
        <div className="panel" style={{ padding: 12 }}>
          <div className="monthgrid">
            {DAYS.map((d) => <div key={d} className="mhead">{d}</div>)}
            {monthCells.map(({ dt, inMonth }, i) => {
              const n = stopsOn(dt).length + jobsOn(dt).filter((j) => !j.done && !j.cancelled).length;
              const b = dayLoadBand(loadOn(dt), capOn(dt), sch.amberAt);
              const away = offCount(dt);
              return (
                <button key={i} className="calcell" data-b={n ? b : "none"} data-out={inMonth ? 0 : 1}
                  data-today={fmtDate(dt) === today ? 1 : 0}
                  onClick={() => { setDay(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())); setMode("day"); }}>
                  <span className="dnum">{dt.getDate()}{away > 0 && <em className="awaydot" title={`${away} off`}>{away}</em>}</span>
                  <span className="dcount">{n || ""}</span>
                </button>
              );
            })}
          </div>
          <div className="monthkey">
            {["green", "amber", "red"].map((b) => (
              <span key={b} className="keyitem"><span className="band" data-b={b} />{BAND_WORD[b]}</span>
            ))}
          </div>
        </div>
      )}

      {route.length === 0 && (
        <div className="panel empty" style={{ marginTop: 12 }}>
          No clients are on a service day yet. Open a client and set a service day to put them on the route.
        </div>
      )}
      </div>

      {techEdit && (
        <TechSheet techs={techs} clients={clients} staff={staff}
          onSave={(next) => { onSaveTechs(next); setTechEdit(false); }} onClose={() => setTechEdit(false)} />
      )}

      {areaAsk && (
        <AreaGateSheet info={areaAsk} isOwner={isOwner} meName={meName}
          onApprove={(j) => saveJob(j, true)} onClose={() => setAreaAsk(null)} />
      )}
    </div>
  );
}

// The service-perimeter gate popup, shared by every booking path: "ask" band
// needs the office's OK; "never" (beyond the km limit) needs the owner.
function AreaGateSheet({ info, isOwner, meName, onApprove, onClose }) {
  const never = info.band.band === "never";
  const stamped = (label) => ({ ...info.j, areaOkBy: label, areaOkAt: new Date().toISOString() });
  return (
    <div className="overlay">
      <div className="sheet" style={{ maxWidth: 480 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>{never ? "🚫 Outside the service limit" : "📍 Outside the service zones"}</strong>
            <div className="sub">{info.cl ? info.cl.name : ""} · {info.band.km} km from the shop</div>
          </div>
        </div>
        <div className="pad">
          {never ? (<>
            <div className="notice" style={{ marginBottom: 12, borderColor: "#E8B4B4", background: "#FDF2F2", color: "#8A2C2C" }}>
              This address is <b>{info.band.km} km</b> away — beyond the absolute {SERVICE_LIMIT_KM} km service limit.
              {isOwner ? " Only an owner approval can book it." : " It cannot be booked for service — only the owner can approve an exception."}
              {" "}Retail and special orders are still fine.
            </div>
            <div className="btnrow" style={{ flexWrap: "wrap" }}>
              {isOwner && (
                <button className="btn" style={{ background: "#B31212" }}
                  onClick={() => onApprove(stamped(`${meName || "owner"} (owner approval, ${info.band.km} km)`))}>
                  Owner approval — book it anyway
                </button>
              )}
              <button className="btn ghost" onClick={onClose}>{isOwner ? "Cancel — don't book" : "Understood"}</button>
            </div>
          </>) : (<>
            <div className="notice" style={{ marginBottom: 12, borderColor: "#F2C063", background: "#FDF6E7" }}>
              This address is outside Zones 1 and 2 ({info.band.km} km from the shop) but within the {SERVICE_LIMIT_KM} km limit.
              <b> Check with service whether we're allowed to book this area</b> before going ahead.
            </div>
            <div className="btnrow" style={{ flexWrap: "wrap" }}>
              <button className="btn" onClick={() => onApprove(stamped(meName || "staff"))}>
                Service says OK — book it
              </button>
              <button className="btn ghost" onClick={onClose}>Cancel — don't book</button>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

function TechSheet({ techs, clients, staff, onSave, onClose }) {
  const [list, setList] = useState(techs.map((t) => ({ ...t })));
  const rename = (id, name) => setList((L) => L.map((t) => (t.id === id ? { ...t, name } : t)));
  const setVeh = (id, vehicle) => setList((L) => L.map((t) => (t.id === id ? { ...t, vehicle } : t)));
  const link = (id, staffId) => setList((L) => L.map((t) => (t.id === id ? { ...t, staffId } : t)));
  const add = () => setList((L) => [...L, { id: cid(), name: "Technician " + (L.length + 1) }]);
  const drop = (id) => setList((L) => L.filter((t) => t.id !== id));
  const used = (id) => clients.filter((c) => c.techId === id).length;

  return (
    <div className="overlay" onClick={(e) => { if (e.target.className === "overlay") onClose(); }}>
      <div className="sheet" style={{ maxWidth: 520 }}>
        <div className="bar">
          <div>
            <strong style={{ fontSize: 16 }}>Technicians</strong>
            <div className="sub">One column on the daily board for each. Link an employee and their approved time off greys the column.</div>
          </div>
          <div className="btnrow">
            <button className="btn ghost" onClick={add}>Add</button>
            <button className="btn" onClick={() => onSave(list)}>Save</button>
          </div>
        </div>
        <div className="pad">
          {list.map((t, i) => (
            <div key={t.id} className="ctop" style={{ marginBottom: 8, flexWrap: "wrap" }}>
              <span className="lvlrank">{i + 1}</span>
              <input value={t.name} style={{ maxWidth: 140 }} onChange={(e) => rename(t.id, e.target.value)} />
              <input value={t.vehicle || ""} placeholder="Vehicle" style={{ maxWidth: 120 }} onChange={(e) => setVeh(t.id, e.target.value)} />
              <select value={t.staffId || ""} style={{ maxWidth: 180 }} onChange={(e) => link(t.id, e.target.value)}>
                <option value="">No employee linked</option>
                {(staff || []).filter((x) => x.active !== false).map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
              <span className="sub" style={{ marginTop: 0, whiteSpace: "nowrap" }}>{used(t.id)} assigned</span>
              {used(t.id) === 0 && list.length > 1 && (
                <button className="btn warn mini" onClick={() => drop(t.id)}>Remove</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Office destinations for desktop .mainnav and the phone Menu drawer (one source of truth). */
// Display grouping for the office menu (Apple pass 2) — headings only, no gates here.
const NAV_GROUPS = [
  { id: "daily", label: "Every day" },
  { id: "shop", label: "Shop" },
  { id: "business", label: "Business" },
  { id: "assist", label: "Assistants" },
];
function officeNavItems({ can, isOwner, isMgr, isOwnerRole, view, activeSpecialCount = 0, followupOpenCount = 0, repairOpenCount = 0 }) {
  const items = [];
  const add = (item) => {
    items.push({
      ...item,
      kind: item.kind || "view",
      on: !!(item.view && view === item.view),
    });
  };
  add({ id: "clients", group: "daily", label: "Customers", view: "clients", cobrowse: "nav.customers", icon: "customers" });
  add({ id: "week", group: "daily", label: "Service Schedule", view: "week", cobrowse: "nav.schedule", icon: "calendar" });
  if (can("sales") || can("pricing")) add({ id: "retail", group: "daily", label: "Retail Sales", view: "retail", cobrowse: "nav.retail", icon: "retail" });
  if (can("sales") || can("pricing")) add({ id: "promo", group: "business", label: "Promotions", view: "promo", icon: "special" });
  if (can("edit")) add({ id: "special", group: "shop", label: activeSpecialCount > 0 ? `Special orders (${activeSpecialCount})` : "Special orders", view: "special", cobrowse: "nav.special", icon: "special" });
  if (can("edit")) add({ id: "followups", group: "daily", label: followupOpenCount > 0 ? `Follow-ups (${followupOpenCount})` : "Follow-ups", view: "followups", cobrowse: "nav.followups", icon: "tick" });
  if (can("edit")) add({ id: "quotes", group: "daily", label: "Quotes", kind: "quotes", title: "Every quote on file", icon: "quote" });
  if (can("edit")) add({ id: "warrantyref", group: "shop", label: "Warranty Lookup", view: "warrantyref", icon: "quote" });
  if (can("edit")) add({ id: "repairs", group: "shop", label: repairOpenCount > 0 ? `In-House Repair (${repairOpenCount})` : "In-House Repair", view: "repairs", cobrowse: "nav.repairs", icon: "wrench" });
  if (isMgr) add({ id: "fleet", group: "shop", label: "Fleet Management", view: "fleet", cobrowse: "nav.fleet", icon: "fleet" });
  if (can("pricing")) add({ id: "inventory", group: "shop", label: "Inventory", view: "inventory", cobrowse: "nav.inventory", icon: "retail" });
  if (can("sales")) add({ id: "sales", group: "business", label: "Sales Dashboard", view: "sales", cobrowse: "nav.sales", icon: "dashboard" });
  if (isOwner || can("margins")) add({ id: "ar", group: "business", label: "A/R", view: "ar", cobrowse: "nav.ar", icon: "retail" });
  if (isMgr) add({ id: "ai", group: "assist", label: "AI Phone", view: "ai", cobrowse: "nav.aiphone", icon: "phone" });
  add({ id: "assist", group: "assist", label: "Ask the Poolmaster", view: "assist", cobrowse: "nav.assist", icon: "ask" });
  if (isOwnerRole) add({ id: "staff", group: "business", label: "Employee Profiles", kind: "staff", cobrowse: "nav.staff", icon: "staff" });
  return items;
}



// CSS-contract: Service Schedule Daily board — vehicles + calls as vertical tiles.
function verticalTilesContract() {
  const rows = CSS.match(/\.pd \.techrows\s*\{([^}]+)\}/);
  const row = CSS.match(/\.pd \.techrow\s*\{([^}]+)\}/);
  const stops = CSS.match(/\.pd \.techrow \.rowstops\s*\{([^}]+)\}/);
  const rowsBody = rows ? rows[1] : "";
  const rowBody = row ? row[1] : "";
  const stopsBody = stops ? stops[1] : "";
  return {
    trucksStacked: /flex-direction:\s*column/.test(rowsBody),
    vehicleTileColumn: /flex-direction:\s*column/.test(rowBody),
    callsStacked: /flex-direction:\s*column/.test(stopsBody),
    noSidewaysTechcols: !/\.pd \.techcols\s*\{/.test(CSS),
    noMultiColRowstops: !/grid-template-columns:\s*repeat\(auto-fill/.test(stopsBody),
    stickyBinIntact: /\.pd \.binwrap\.sched-bin\s*\{/.test(CSS) && /\.pd \.sched-board-scroll/.test(CSS) && !/\.pd \.binwrap\.sched-bin\s*\{[^}]*z-index:\s*4[0-9]/.test(CSS),
    zoomOnHtml: /html\s*\{[^}]*\bzoom\s*:\s*1\.08/.test(CSS),
    phoneUsable: /@media \(max-width:\s*820px\)/.test(CSS),
  };
}

// CSS-contract for the schedule holding bin pin (pinned without covering rows).
function holdingBinStickyContract() {
  const rule = CSS.match(/\.pd \.binwrap\.sched-bin\s*\{([^}]+)\}/);
  const body = rule ? rule[1] : "";
  const generic = (CSS.match(/\.pd \.binwrap\s*\{([^}]+)\}/) || [])[1] || "";
  const pd = CSS.match(/\.pd\s*\{([^}]+)\}/);
  const pdBody = pd ? pd[1] : "";
  const board = (CSS.match(/\.pd \.sched-board\s*\{([^}]+)\}/) || [])[1] || "";
  const pin = (CSS.match(/\.pd \.sched-pin\s*\{([^}]+)\}/) || [])[1] || "";
  const scroll = (CSS.match(/\.pd \.sched-board-scroll\s*\{([^}]+)\}/) || [])[1] || "";
  const zMatch = body.match(/z-index:\s*(\d+)/);
  const zIndex = zMatch ? Number(zMatch[1]) : 0;
  const sticky = /position:\s*sticky/.test(body);
  const nestedScroll = /display:\s*flex/.test(board) && /overflow-y:\s*auto/.test(scroll) && /\.pd \.sched-pin/.test(CSS);
  const overlaysRows = sticky && zIndex >= 20;
  return {
    className: "sched-bin",
    hasRule: !!rule,
    position: sticky ? "sticky" : (/position:\s*relative/.test(body) ? "relative" : ""),
    top: /top:\s*8px/.test(body) ? "8px" : "",
    zIndex,
    nestedScroll,
    overlaysRows,
    pinWithoutCover: nestedScroll && !overlaysRows,
    solidBg: /background:\s*var\(--card\)/.test(body) || /background:\s*var\(--card\)/.test(generic),
    maxHeight: /max-height\s*:/.test(body),
    overflowY: /overflow-y\s*:\s*auto/.test(body),
    genericNotSticky: !/position\s*:\s*sticky/.test(generic),
    zoomNotOnPd: !/\bzoom\s*:/.test(pdBody),
    zoomOnHtml: /html\s*\{[^}]*\bzoom\s*:\s*1\.08/.test(CSS),
    phoneCap: /\.pd \.binwrap\.sched-bin\s*\{\s*max-height:\s*42vh/.test(CSS),
    pinHasBg: /background:\s*var\(--plaster\)/.test(pin),
  };
}

// Named exports for component tests.
export { geocodeTries, checklistFor, stopGate, dailyChecklistFor, dailyExtrasFor, leaveGate, DAILY_CHECKLIST, TAB_FEEDER_ITEM, needsFilledFeeder, pruneVisitPhotos, AddressAutocomplete, EditForm, accountBalances, applyTransfer, arAge, arFollowReason, AR_GRACE_DAYS, TERM_DAYS, bulkRoute, bulkOnHand, spOf, posTotals, autoFlags, hstRateFor, hstFactorFor, managerByPin, withStoreCredit, needsConfirm, servicePrefFor, confirmMsgFor, telHref, serviceIssueNote, CALL_HOW_LABEL, galToL, lToGal, stripServiceFields, determineServiceZone, serviceBand, SERVICE_LIMIT_KM, repairIntakeRoute, withRepairStatus, repairKpis, repairReminderDue, followupOverdue, sortFollowups, visibleFollowup, followupPinnedTo, followupReminders, contactFirst, promoConsent, promoAudience, softubLikely, campaignDue, repairWarrantyMatches, repairWarrantyMonthsFor, repairClosingGate, isRoboticRepair, withDisposalClose, closingAnswersFromForm, repairPickupPayCheck, repairPickupGate, repairUnitLabel, isPressureSideRepair, isHoseRepair, pressurePayOpCheck, repairPressurePayGate, repairAmountCharged, repairHistory, warrantyYearOf, afterServiceReport, serviceEmailContact, toolCheckout, toolCheckin, toolTransfer, toolFlag, toolClearFlag, toolHeldFor, warrantyTermStatus, deductibleFor, deductibleOutstanding, saleSerialGaps, addMonthsIso, partHasWarranty, serialList, partsAfterTruckUse, repairStageGate, repairTimeline, repairLastUpdated, repairStale, repairEtaOverdue, businessDaysBetween, REPAIR_STALE_BUSINESS_DAYS, REPAIR_QUEUE_STAGE, BENCH_PARK_STAGES, REPAIR_FORM_STATUSES, applyRepairFormChoice, repairFormChoice, repairFormChoiceLabel, rentalLateDays, rentalLiveStatus, rentalFee, rentalTotals, rentalNotices, sortRepairQueue, holdRepair, releaseRepair, migrate, applyServiceRatesCsv, quoteListRows, quoteDisplayStatus, quoteListAmount, quoteSender, QUOTE_VALID_DAYS, QUOTE_STATUS, serviceCatalogRows, filterServiceCatalog, catalogNamesMatch, mergeDefaultTerms, currentTermsText, defaultTermIds, resolveTerms, DEFAULT_TERMS, TERM_TAGS, repairsAwaitingContact, canFlagGiftedAccount, applyGiftedAccountFlag, visitGiftedDefault, applyGiftedVisitPricing, canAdjustSchedule, canAdjustCallOrder, guidedCloseGate, guidedCloseDraft, applyGuidedClose, guidedCloseFollowup, SERVICE_FOLLOW_REASONS, SERVICE_FOLLOW_NONE, officeNavItems, holdingBinStickyContract, verticalTilesContract, autoStageJobsByDistance, SHOP_POINT, parseRepairTicketFromLocation, repairQrPayload, repairQrImg, stripRepairTicketFromLocation, consumeRepairTicketFromLocation, RepairQrImg, assistantExtensionLines, addAssistantExtensionLine, markInvoiceMailed, invoiceMailStamp, cashChangeDue, posCartCanComplete, confirmBillingCanProceed, confirmBillingBillableLines, CONFIRM_BILLING_EMPTY_MSG, lookupStoredClientZone, lookupServiceZone, zoneLookupLabel, warehouseStockLocations, warehouseTransferLocations, WAREHOUSE_QTY_HELP, chemCheckGate, chemCheckDraft, CHEM_CHECK_CHOICES, CHEM_WHY_NEW, CHEM_CHECK_REMINDER, promoFailCount, promoSendCounts, promoRowFailed, catalogDescPoints, CATALOG_DESC_PLACEHOLDER, capServiceDays, canPickServiceDay, waterTestSaveGate, showDayFilter, serviceCloseGate, printVisitInvoice, WATER_TEST_REMINDER_NOTE, makeWaterTestReminder, SALT_SYSTEM_TYPES, saltSystemTypeOptions, needsSaltSystemType, VOLUME_PRESETS, volumePresetOptions, gallonsFromVolumePreset, volumeCalcGallons, addEquipmentOfKind, equipmentTimerFields, equipmentTimerChangeNote, COMPLAINT_VALIDITY, COMPLAINT_SOURCE, blankComplaint, storeComplaint, complaintCount, VIDEO_DURATION_MIN, VIDEO_DURATION_MAX, videoDurationGate, PO_VENDOR_MINIMUMS, poVendorMinimum, poMinimumGap, suggestItemsToMinimum, poLineTotal, specialOrderToPoItem, upsertSpecialOrderPo, specialOrderEmailGate, specialOrderEmailPayload, SMS_SENDER_LABEL, smsSenderIdentity, ensureSmsBrand, searchClientsForEmail, displayTitleCase, STRUCTURES, 
wfResolve, wfCurrent, wfComplete, wfDeferred, wfDuration, wfPrereqBlock, diyVerdict, lowWaterFactor, DEFAULT_JOB_TYPES };
